// ============================================================
// AUTOPILOT DIGITAL — CLIENT DASHBOARD
// Version: v18 — Session 18: GlitchRobotCard error card added; FAL_KEY_INVALID wired into ImageGenerationPanel, META_KEY_INVALID wired into ConnectionsPanel, CLAUDE_KEY_INVALID ready for future panels (April 12 2026)
// DO NOT change colors/fonts/layout without Roger's approval.
//
// DESIGN SPEC (LOCKED):
//   Accent:      #31DCBA (teal)
//   BG Base:     #0d0f14
//   BG Surface:  #141720
//   BG Card:     #1a1d27
//   Font:        Barlow / Barlow Condensed (Google Fonts)
//   Layout:      Collapsible left sidebar + workspace + collapsible dashboard bar (bottom)
//
// LAYOUT:
//   - Left sidebar: collapsible. When collapsed shows logo + expand arrow below it.
//   - Main workspace: opens panel content when nav item clicked. Empty by default.
//   - Bottom dashboard bar: stat cards + Recent Posts + Pending Approval. Open by default, collapsible.
//
// SECTIONS (12):
//   1.  Home (workspace panel — currently empty placeholder)
//   2.  Onboarding
//   3.  Brand Hub
//   4.  Content Calendar
//   5.  Content Queue
//   6.  Photo Library
//   7.  Image Generation (never call "AI Images" — avoid AI terminology with clients)
//   8.  Post Performance
//   9.  Stories (entry point → Post Generator with IG/FB Story pre-selected)
//   10. Account & Perks
//   11. Connections (Instagram + Facebook — either optional, expandable for future platforms)
//   12. Ads Application (Ads Bank + Ad Builder + analytics + renewals — linked to calendar)
//
// BUILD ORDER:
//   Session 1:  Onboarding panel ✓
//   Session 2:  Brand Hub panel ✓
//   Session 3:  Content Calendar panel ✓
//   Session 4:  Content Queue panel ✓
//   Session 5:  Photo Library panel ✓
//   Session 6:  Image Generation panel ✓
//   Session 7:  Post Performance panel ✓
//   Session 8:  Account & Perks panel ✓
//   Session 9:  Tooltip system ✓
//   Session 10: Connections + Ads Application panels ✓
//   Session 11: PWA setup or skip → Mobile layout
//   Session 12: Mobile layout (bottom nav bar for phones)
//   Session 13: Wire into real React app (Meta OAuth, Meta Ads API, FAL, Claude, Stripe)
//
// CONNECTIONS PANEL NOTES:
//   - Instagram and Facebook are each independent — either or both can be connected
//   - Mock state for now — real Meta OAuth wires in Session 13
//   - Expandable: future platforms (Google, TikTok, etc.) slot in here
//
// ADS PORTAL NOTES:
//   - Ads Bank: separate creative library, images routable from Image Generation
//   - Ad Builder: platform (IG/FB/both), budget, duration, audience, creative selector
//   - Pricing: mock CPM ranges — real data from Meta Ads API in Session 13
//   - Renewals: active/scheduled/ended ad status cards
//   - Analytics: impressions, reach, clicks, spend, CTR
//   - Calendar: ads appear as distinct event type (different colour/icon)
//
// PWA TARGET: Desktop + mobile responsive
// ============================================================

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { supabase } from './lib/supabaseClient';
import { uploadLogo } from './lib/uploadLogo';
import { ensureRenderableImage } from './lib/heic';
import { prepareScreenshots, screenshotResultLine, MAX_SCREENSHOTS } from './lib/screenshotImages';
import { useClientId, initialAuthType, PASSWORD_SETUP_TYPES, readAuthTypeFromHash } from './hooks/useClientId';
import { useViewingAs, setViewingAs, clearViewingAs } from './hooks/useViewingAs';
import usePwaInstall from './hooks/usePwaInstall';
import { authedFetch, authHeader, clientScopedFetch } from './lib/authedFetch';
import { postingDayWarning, removedPostingDays, postsOnRemovedDays, removedDaysWarning }
  from './lib/postingDays';
import { LoginScreen } from './components/auth/LoginScreen';
import { SetPasswordScreen } from './components/auth/SetPasswordScreen';
import PhotoEditorPanel from './panels/PhotoEditorPanel';
import VideoEditorPanel from './panels/VideoEditorPanel';
import ChatWidget from './components/ChatWidget';
import GoogleBusinessOptions, { useGoogleBizConnected, GoogleGlyph } from './components/GoogleBusinessOptions';
import GoogleLivePosts from './components/GoogleLivePosts';
import ReviewsPanel from './components/ReviewsPanel';
import GoogleStats from './components/GoogleStats';
import GoogleProfilePanel from './components/GoogleProfilePanel';
import { StuckClientsCard } from './components/StuckClientsCard';
import { defaultGbpSettings, editableGbpSettings, gbpSettingsProblem, gbpSettingsForSave, gbpNoteFor, gbpTrimNote } from './lib/gbp';
import { VIDEO_FONTS, resolveFont } from './utils/videoFonts';
import {
  overlayMetrics, alignOffset, customTextScale, customTextWidth, blockFrame, handlePoint,
  RESIZE_HANDLES, screenDeltaToLocal, resizeOverlayBlock, handleCursor,
} from './utils/overlayGeometry';
import { pickHashtags } from './lib/hashtags';

// Maps a raw `brand_profile` row (snake_case Supabase columns) to the camelCase
// shape the app's generate-post callers expect. Mirrors the mapping the carousel/
// video builder does inline (~5589-5608) so every path that loads a brand profile
// produces an identically-shaped object. Kept at module scope to avoid the
// per-component casing drift that previously dropped hashtagSets and most brand
// fields on the Build-tab and Regenerate paths.
function mapBrandProfileRow(data) {
  if (!data) return {};
  const toArr = (v) =>
    !v ? [] : Array.isArray(v) ? v
      : typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean)
      : [];
  return {
    bizName:                 data.brand_name || '',
    tagline:                 data.brand_tagline || '',
    category:                data.brand_industry || '',
    tones:                   toArr(data.brand_tone),
    emojiPref:               data.emoji_pref || 'Minimal',
    primaryColour:           data.primary_color || '',
    secondaryColour:         data.secondary_color || '',
    accentColour:            data.accent_color || '',
    logoUrl:                 data.logo_url || '',
    visualStyles:            toArr(data.visual_styles),
    lightingMood:            toArr(data.image_mood),
    imgBrandKeywords:        toArr(data.image_keywords),
    falStyleKeywords:        data.image_style || '',
    negativePromptAdditions: data.image_negative_prompt || '',
    keyProducts:             toArr(data.key_products),
    upcomingPromos:          data.upcoming_promos || '',
    avoidTopics:             toArr(data.avoid_topics),
    hashtagSets:             toArr(data.hashtag_sets),
  };
}

// Single source of truth for which timezone we render dates in. CalendarPanel
// already honors `localStorage.ap_timezone` — this helper makes HomePanel,
// PhotoLibrary, PostPerformance, and the bottom dashboard bar render in the
// same zone so the same scheduled post never shows on different days across
// panels. Falls back to the browser's resolved timezone.
function getDisplayTimeZone() {
  try {
    return localStorage.getItem('ap_timezone')
      || Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

// ── Wall-clock ↔ UTC in the CLIENT's zone ────────────────────────────────────
//
// The Scheduler's <input type="datetime-local"> has no timezone: it yields
// '2026-09-23T17:45', a wall-clock reading. Every write site turned that into
// an instant with `new Date(dtLocal).toISOString()`, which resolves it in the
// BROWSER's zone. Meanwhile the panel renders every timestamp with
// `toLocaleString({ timeZone })`. So an admin in Toronto scheduling for a
// Vancouver client typed 17:45, stored 14:45 Vancouver, and was then shown
// 14:45 — the display and the write disagreed by the offset between them, and
// nothing in the UI said so.
//
// These two convert against an explicit zone instead, which is the same
// arithmetic the backend's schedule-slot.js already does for the approve path.
// Ported rather than shared because CRA will not import from outside src/.
//
// The client's zone comes from brand_profile.timezone, so both halves of the
// round trip and the server's day-boundary maths now agree on one clock.

// The calendar/clock fields of `date` as seen in `tz`.
function wallClockInZone(date, tz) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(date);
  const get = (t) => Number(parts.find(p => p.type === t).value);
  return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}

// How far `tz` is ahead of UTC at instant `ms`, in ms.
function zoneOffsetAt(ms, tz) {
  const w = wallClockInZone(new Date(ms), tz);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute) - Math.floor(ms / 60000) * 60000;
}

// 'YYYY-MM-DDTHH:MM' read in `tz` → the UTC instant, as an ISO string.
//
// Two passes because the offset depends on the instant being solved for — the
// same reason and the same resolution as schedule-slot.js: at the autumn
// fall-back this returns the first (daylight) occurrence, and in the spring
// gap the instant an hour later on the wall clock.
export function localInputToIso(dtLocal, tz) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(dtLocal || ''));
  if (!m || !tz) return dtLocal ? new Date(dtLocal).toISOString() : null;
  const [, y, mo, d, h, mi] = m.map(Number);
  const local = Date.UTC(y, mo - 1, d, h, mi);
  // Structure copied from zonedTimeToUtc in schedule-slot.js rather than
  // condensed. A shorter form that picked min/max off an equality test looks
  // equivalent and is not: on the fall-back day an unambiguous 04:00 resolves
  // through a first pass that is still on daylight time, and only "take the
  // second pass when its own offset agrees with itself" lands on 04:00 rather
  // than 03:00. Keep these two in step.
  const first = local - zoneOffsetAt(local, tz);
  const secondOffset = zoneOffsetAt(first, tz);
  const second = local - secondOffset;
  let chosen;
  if (second === first) chosen = first;
  else if (zoneOffsetAt(second, tz) === secondOffset) chosen = second;
  else chosen = Math.max(first, second);
  return new Date(chosen).toISOString();
}

// The reverse, for prefilling the same inputs. Must round-trip with the above,
// or opening the reschedule picker would silently shift a post's time.
export function isoToLocalInputInZone(iso, tz) {
  if (!iso) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const w = wallClockInZone(new Date(iso), tz);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}T${pad(w.hour)}:${pad(w.minute)}`;
}

// A 'YYYY-MM-DD' + 'HH:MM' pair (the chip modal's two inputs) → a UTC instant.
export function localDateTimeToIso(date, time, tz) {
  if (!date || !time) return null;
  return localInputToIso(`${date}T${time.slice(0, 5)}`, tz);
}

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Barlow+Condensed:wght@600;700&family=Inter:wght@700&display=swap');
  :root {
    --accent:        #31DCBA;
    --accent-dim:    rgba(49,220,186,0.12);
    --accent-border: rgba(49,220,186,0.3);
    --bg-base:       #0d0f14;
    --bg-surface:    #141720;
    --bg-card:       #1a1d27;
    --bg-card2:      #1f2330;
    --border:        rgba(255,255,255,0.07);
    --text-primary:  #f0f2f8;
    --text-secondary:#8b90a4;
    --text-muted:    #555a6e;
    --font:          'Barlow', sans-serif;
    --font-head:     'Barlow Condensed', sans-serif;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }

  .ap-root { display:flex; height:100vh; background:var(--bg-base); font-family:var(--font); color:var(--text-primary); font-size:13px; }

  /* SIDEBAR */
  .ap-sidebar { width:220px; min-width:220px; height:100vh; background:var(--bg-surface); border-right:1px solid var(--border); display:flex; flex-direction:column; transition:width 0.25s; overflow:hidden; }
  .ap-sidebar.collapsed { width:52px; min-width:52px; }

  .ap-logo-area { display:flex; align-items:center; padding:14px 12px; border-bottom:1px solid var(--border); position:relative; min-height:62px; gap:10px; flex-wrap:nowrap; }
  .ap-sidebar.collapsed .ap-logo-area { flex-direction:column; align-items:center; justify-content:center; padding:10px 0; gap:6px; border-bottom:none; }

  .ap-logo-icon { width:46px; height:46px; flex-shrink:0; object-fit:contain; border-radius:8px; }
  .ap-logo-text { font-family:var(--font-head); font-size:15px; font-weight:700; line-height:1.2; white-space:nowrap; margin-right:10px; }
  .ap-logo-text span { color:var(--accent); }

  .ap-cbtn { background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.12); color:var(--text-secondary); cursor:pointer; font-size:13px; padding:3px 8px; line-height:1.4; border-radius:4px; flex-shrink:0; margin-left:auto; white-space:nowrap; }
  .ap-sidebar.collapsed .ap-cbtn { margin-left:0; width:34px; text-align:center; padding:4px 0; }
  .ap-cbtn:hover { background:rgba(255,255,255,0.13); color:var(--text-primary); }

  .ap-divider { border-bottom:1px solid var(--border); }
  .ap-client { display:flex; align-items:center; gap:9px; padding:12px; border-bottom:1px solid var(--border); }
  .ap-avatar { width:30px; height:30px; border-radius:50%; background:var(--accent-dim); border:1px solid var(--accent-border); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:var(--accent); flex-shrink:0; }
  .ap-cname { font-size:12px; font-weight:600; white-space:nowrap; }
  .ap-cplan { font-size:10px; color:var(--text-secondary); white-space:nowrap; }

  .ap-nav { flex:1; overflow-y:auto; padding:8px 0; }
  .ap-ns { font-size:10px; font-weight:700; letter-spacing:0.08em; color:var(--text-muted); padding:10px 12px 4px; text-transform:uppercase; white-space:nowrap; }
  .ap-ni { display:flex; align-items:center; gap:9px; padding:8px 12px; cursor:pointer; color:var(--text-secondary); font-size:12px; font-weight:500; transition:background 0.15s, color 0.15s; white-space:nowrap; }
  .ap-ni:hover { background:rgba(255,255,255,0.04); color:var(--text-primary); }
  .ap-ni.active { background:var(--accent-dim); color:var(--accent); border-right:2px solid var(--accent); }
  .ap-badge { background:var(--accent); color:var(--bg-base); font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; margin-left:auto; }
  .ap-soon { background:rgba(49,220,186,0.15); color:var(--accent); font-size:10px; font-weight:600; padding:1px 6px; border-radius:10px; margin-left:auto; }

  .ap-sfooter { border-top:1px solid var(--border); padding:8px 0; flex-shrink:0; }

  /* PLAN SWITCHER */
  .ap-plan-switch { margin:0 10px 8px; border-radius:8px; background:var(--bg-card); border:1px solid var(--border); overflow:hidden; }
  .ap-plan-switch-label { font-size:9px; font-weight:700; letter-spacing:0.08em; color:var(--text-muted); text-transform:uppercase; padding:7px 10px 4px; }
  .ap-plan-opts { display:flex; gap:3px; padding:0 4px 4px; }
  .ap-plan-opt { flex:1; padding:5px 4px; text-align:center; font-size:10px; font-weight:600; border-radius:5px; cursor:pointer; border:none; font-family:var(--font); transition:all 0.15s; background:transparent; color:var(--text-muted); }
  .ap-plan-opt.active { background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); }
  .ap-plan-opt:hover:not(.active) { color:var(--text-secondary); background:rgba(255,255,255,0.04); }

  /* MAIN */
  .ap-right { flex:1; display:flex; flex-direction:column; overflow:hidden; min-width:0; }
  .ap-topbar { display:flex; align-items:center; gap:10px; padding:12px 20px; background:var(--bg-surface); border-bottom:1px solid var(--border); flex-shrink:0; }
  .ap-topbar-title { font-family:var(--font-head); font-size:17px; font-weight:700; letter-spacing:0.03em; }
  .ap-sdot { width:7px; height:7px; border-radius:50%; background:var(--accent); margin-left:auto; flex-shrink:0; }
  .ap-slabel { font-size:11px; color:var(--accent); white-space:nowrap; }

  /* WORKSPACE */
  .ap-workspace { flex:1; overflow-y:auto; padding:20px; }
  .ap-workspace-empty { display:flex; align-items:center; justify-content:center; height:100%; color:var(--text-muted); font-size:12px; }

  /* DASHBOARD BAR (bottom) */
  .ap-dashboard-bar { background:var(--bg-surface); border-top:1px solid var(--border); flex-shrink:0; }
  .ap-db-handle { display:flex; align-items:center; padding:8px 20px; cursor:pointer; user-select:none; position:relative; }
  .ap-db-label { font-family:var(--font-head); font-size:12px; font-weight:700; letter-spacing:0.06em; color:var(--text-secondary); text-transform:uppercase; }
  .ap-db-hint { position:absolute; left:50%; transform:translateX(-50%); font-family:var(--font-head); font-size:12px; font-weight:700; color:var(--accent); }
  .ap-db-arrow { margin-left:auto; color:var(--text-muted); font-size:12px; display:inline-block; transition:transform 0.25s; }
  .ap-db-inner { padding:10px 20px 14px; }
  .ap-db-inner.hidden { display:none; }

  /* STAT CARDS */
  .ap-stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:10px; }
  .ap-stat-card { background:var(--bg-card); border:1px solid var(--border); border-radius:9px; padding:11px 13px; }
  .ap-stat-label { font-size:9px; color:var(--text-secondary); font-weight:600; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px; }
  .ap-stat-value { font-family:var(--font-head); font-size:22px; font-weight:700; color:var(--text-primary); line-height:1; }
  .ap-stat-trend { font-size:11px; color:var(--accent); font-weight:600; margin-left:4px; font-family:var(--font); }
  .ap-stat-sub { font-size:10px; color:var(--text-muted); margin-top:3px; }

  /* TWO COL CARDS */
  .ap-two-col { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .ap-card { background:var(--bg-card); border:1px solid var(--border); border-radius:9px; padding:12px; }
  .ap-card-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
  .ap-card-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.03em; }
  .ap-card-action { font-size:11px; color:var(--accent); cursor:pointer; }
  .ap-post-item { display:flex; align-items:center; gap:8px; padding:6px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
  .ap-post-item:last-child { border-bottom:none; }
  .ap-thumb { width:28px; height:28px; background:var(--bg-card2); border-radius:5px; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
  .ap-post-caption { font-size:11px; color:var(--text-primary); font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:130px; }
  .ap-post-meta { font-size:10px; color:var(--text-muted); }
  .ap-pill { font-size:9px; font-weight:600; padding:2px 6px; border-radius:8px; margin-left:auto; flex-shrink:0; }
  .s-posted { background:rgba(49,220,186,0.12); color:var(--accent); }
  .s-scheduled { background:rgba(99,153,250,0.12); color:#6399fa; }

  /* ============================================================
     MOBILE LAYOUT (≤768px)
     Bottom nav bar replaces left sidebar.
     Sidebar hidden. Dashboard bar hidden on mobile.
     ============================================================ */

  /* Bottom nav bar */
  .ap-mobile-nav {
    display: none;
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 1000;
    background: var(--bg-surface);
    border-top: 1px solid var(--border);
    /* Safe area for iPhone home bar */
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .ap-mobile-nav-inner {
    display: flex;
    align-items: stretch;
    height: 56px;
  }
  .ap-mobile-nav-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    cursor: pointer;
    color: var(--text-muted);
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: var(--font);
    position: relative;
    transition: color 0.15s;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
  }
  .ap-mobile-nav-item.active { color: var(--accent); }
  .ap-mobile-nav-item:active { background: rgba(255,255,255,0.04); }
  .ap-mobile-nav-item svg { flex-shrink: 0; }
  .ap-mobile-nav-badge {
    position: absolute;
    top: 6px; right: calc(50% - 14px);
    background: var(--accent);
    color: var(--bg-base);
    font-size: 9px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 8px;
    min-width: 16px;
    text-align: center;
    line-height: 1.4;
  }
  /* "More" overlay drawer */
  .ap-mobile-more-overlay {
    display: none;
    position: fixed;
    inset: 0;
    z-index: 999;
  }
  .ap-mobile-more-overlay.open { display: block; }
  .ap-mobile-more-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.55);
  }
  .ap-mobile-more-sheet {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    background: var(--bg-surface);
    border-top: 1px solid var(--border);
    border-radius: 16px 16px 0 0;
    padding: 12px 0 calc(72px + env(safe-area-inset-bottom, 0px)) 0;
  }
  .ap-mobile-more-handle {
    width: 36px; height: 4px;
    background: var(--border);
    border-radius: 2px;
    margin: 0 auto 12px;
  }
  .ap-mobile-more-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--text-muted);
    text-transform: uppercase;
    padding: 0 16px 8px;
  }
  .ap-mobile-more-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    cursor: pointer;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 500;
    transition: background 0.12s;
    -webkit-tap-highlight-color: transparent;
  }
  .ap-mobile-more-item:active { background: rgba(255,255,255,0.04); }
  .ap-mobile-more-item.active { color: var(--accent); background: var(--accent-dim); }
  .ap-mobile-more-item svg { flex-shrink: 0; }
  .ap-mobile-more-label { flex: 1; }
  .ap-mobile-more-badge {
    background: var(--accent);
    color: var(--bg-base);
    font-size: 10px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 10px;
  }
  .ap-mobile-more-soon {
    background: rgba(49,220,186,0.15);
    color: var(--accent);
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
  }

  /* Mobile client header strip (replaces sidebar client row) */
  .ap-mobile-header-client {
    display: none;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    flex-shrink: 0;
  }
  .ap-mobile-header-plan {
    font-size: 10px;
    color: var(--text-muted);
    margin-left: auto;
    white-space: nowrap;
  }
  .ap-mobile-plan-toggle {
    font-size: 10px;
    color: var(--accent);
    background: var(--accent-dim);
    border: 1px solid var(--accent-border);
    border-radius: 5px;
    padding: 3px 8px;
    cursor: pointer;
    font-family: var(--font);
    font-weight: 600;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    /* Show mobile nav, hide sidebar */
    .ap-mobile-nav { display: block; }
    .ap-mobile-header-client { display: flex; }
    .ap-sidebar { display: none !important; }

    /* Root becomes a column, no sidebar */
    .ap-root { flex-direction: column; height: 100dvh; }

    /* Top bar: tighter on mobile */
    .ap-topbar {
      padding: 10px 16px;
      height: 48px;
    }
    .ap-topbar-title { font-size: 15px; }

    /* Mobile client info row sits between topbar and workspace */
    .ap-mobile-header-client {
      height: 36px;
      background: var(--bg-surface);
      border-bottom: 1px solid var(--border);
      align-items: center;
    }

    /* Workspace fills remaining space, leaves room for bottom nav */
    .ap-workspace {
      padding: 14px;
      padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px) + 14px);
    }

    /* Hide bottom dashboard bar — too cluttered on mobile */
    .ap-dashboard-bar { display: none !important; }

    /* Right side fills full width */
    .ap-right { width: 100%; height: 100dvh; }

    /* ── Panel tweaks ── */

    /* Onboarding: stack steps compactly */
    .ob-steps { overflow-x: auto; padding-bottom: 4px; }
    .ob-step-label { font-size: 9px; }
    .ob-card { padding: 18px 14px; }
    .ob-pillars { grid-template-columns: 1fr; }

    /* Brand Hub: hide preview sidebar, stack tabs */
    .bh-wrap { flex-direction: column; gap: 12px; }
    .bh-preview { display: none; }
    .bh-tabs { overflow-x: auto; }
    .bh-two-col { grid-template-columns: 1fr; }

    /* Calendar: make day cells smaller */
    .cal-day { min-height: 44px; padding: 4px 3px; }
    .cal-day-num { font-size: 11px; width: 18px; height: 18px; }
    .cal-dots { gap: 2px; }
    .cal-dot { width: 16px; height: 16px; }
    .cal-legend { display: none; }
    .cal-header { gap: 8px; }

    /* Post Approval: full-width cards */
    .aq-wrap { max-width: 100%; }

    /* Photo Library: 2-col grid on mobile */
    .pl-grid { grid-template-columns: repeat(2, 1fr) !important; }

    /* Image generation: single col */
    .ig-wrap { flex-direction: column !important; }
    .ig-sidebar { width: 100% !important; }

    /* Carousel: stack two-column layout */
    .carousel-two-col { flex-direction: column !important; }
    .carousel-left { flex: none !important; width: 100% !important; position: static !important; }

    /* Post Performance: stack charts */
    .pp-grid { grid-template-columns: 1fr !important; }
    .pp-two-col { grid-template-columns: 1fr !important; }

    /* Account: stack cards (the actual class is .acct-grid; old rule
       targeted a non-existent .acct-two-col so cards overflowed on mobile) */
    .acct-grid { grid-template-columns: 1fr !important; }

    /* Stat grid in dashboard bar → 2 col (hidden anyway, but safe) */
    .ap-stat-grid { grid-template-columns: repeat(2, 1fr); }
    .ap-two-col { grid-template-columns: 1fr; }

    /* Ads Application tabs: scrollable */
    .ads-tabs { overflow-x: auto; }

    /* Connections: single col */
    .conn-grid { grid-template-columns: 1fr !important; }
  }

  /* ONBOARDING PANEL */
  .ob-wrap { max-width:620px; margin:0 auto; padding:8px 0 40px; }

  /* Step indicator */
  .ob-steps { display:flex; align-items:center; margin-bottom:36px; }
  .ob-step { display:flex; flex-direction:column; align-items:center; gap:5px; position:relative; z-index:1; }
  .ob-step-circle { width:32px; height:32px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:var(--font-head); font-size:13px; font-weight:700; border:2px solid var(--border); background:var(--bg-card); color:var(--text-muted); transition:all 0.2s; flex-shrink:0; }
  .ob-step.done .ob-step-circle { background:var(--accent); border-color:var(--accent); color:var(--bg-base); }
  .ob-step.active .ob-step-circle { border-color:var(--accent); color:var(--accent); background:var(--accent-dim); }
  .ob-step-label { font-size:10px; color:var(--text-muted); white-space:nowrap; font-weight:500; }
  .ob-step.active .ob-step-label { color:var(--accent); }
  .ob-step.done .ob-step-label { color:var(--text-secondary); }
  .ob-connector { flex:1; height:2px; background:var(--border); margin:0 4px; margin-bottom:18px; transition:background 0.2s; }
  .ob-connector.done { background:var(--accent); }

  /* Card */
  .ob-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:28px; }
  .ob-card-title { font-family:var(--font-head); font-size:20px; font-weight:700; letter-spacing:0.02em; margin-bottom:6px; }
  .ob-card-sub { font-size:12px; color:var(--text-secondary); margin-bottom:24px; line-height:1.6; }

  /* Buttons */
  .ob-btn-row { display:flex; align-items:center; gap:10px; margin-top:28px; }
  .ob-btn { padding:9px 22px; border-radius:7px; font-family:var(--font); font-size:13px; font-weight:600; cursor:pointer; border:none; transition:all 0.15s; }
  .ob-btn-primary { background:var(--accent); color:var(--bg-base); }
  .ob-btn-primary:hover:not(:disabled) { background:#28c4a8; }
  /* A DISABLED BUTTON MUST LOOK DISABLED. There was no :disabled rule at all,
     so a button that refused to act kept full accent fill and a pointer
     cursor — indistinguishable from a working one, which is exactly how a
     blocked Save read as "the button does nothing".
     Three call sites are affected and all three improve or stay the same:
     Brand Hub's Save (the reason this exists), the onboarding logo Change
     button while a logo uploads (previously looked live mid-upload), and the
     onboarding Next buttons, which already set opacity/cursor inline — inline
     styles win, so those look exactly as they did. */
  .ob-btn:disabled { opacity:0.45; cursor:not-allowed; filter:grayscale(0.4); }
  .ob-btn:disabled:hover { background:var(--accent); }
  .ob-btn-secondary:disabled:hover { background:transparent; }
  .ob-btn-secondary { background:transparent; color:var(--text-secondary); border:1px solid var(--border); }
  .ob-btn-secondary:hover { color:var(--text-primary); border-color:rgba(255,255,255,0.2); }
  .ob-btn-skip { background:transparent; border:none; color:var(--text-muted); font-size:12px; cursor:pointer; margin-left:auto; }
  .ob-btn-skip:hover { color:var(--text-secondary); }

  /* Step 1 — Welcome */
  .ob-welcome-icon { width:56px; height:56px; border-radius:14px; background:var(--accent-dim); border:1px solid var(--accent-border); display:flex; align-items:center; justify-content:center; margin-bottom:20px; }

  /* Step 2 — Invite */
  .ob-input { width:100%; background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; padding:10px 13px; font-family:var(--font); font-size:13px; color:var(--text-primary); outline:none; transition:border-color 0.15s; }
  .ob-input:focus { border-color:var(--accent-border); }
  .ob-input::placeholder { color:var(--text-muted); }
  .ob-label { font-size:11px; font-weight:600; color:var(--text-secondary); text-transform:uppercase; letter-spacing:0.06em; margin-bottom:6px; }
  .ob-field { margin-bottom:16px; }
  .ob-invited { display:flex; align-items:center; gap:10px; background:var(--accent-dim); border:1px solid var(--accent-border); border-radius:8px; padding:10px 14px; margin-top:14px; font-size:12px; color:var(--accent); }

  /* Step 3 — Pillars */
  .ob-pillars { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .ob-pillar { background:var(--bg-surface); border:1px solid var(--border); border-radius:9px; padding:13px; cursor:pointer; transition:all 0.15s; position:relative; }
  .ob-pillar.active { border-color:var(--accent-border); background:var(--accent-dim); }
  .ob-pillar-name { font-size:12px; font-weight:600; color:var(--text-primary); margin-bottom:3px; display:flex; align-items:center; gap:7px; }
  .ob-pillar-desc { font-size:11px; color:var(--text-muted); line-height:1.5; }
  .ob-pillar.active .ob-pillar-desc { color:var(--text-secondary); }
  .ob-pillar-check { width:16px; height:16px; border-radius:50%; border:2px solid var(--border); flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:9px; margin-left:auto; }
  .ob-pillar.active .ob-pillar-check { background:var(--accent); border-color:var(--accent); color:var(--bg-base); }
  .ob-pillar-edit { position:absolute; top:8px; right:8px; background:none; border:none; color:var(--text-muted); font-size:11px; cursor:pointer; opacity:0; transition:opacity 0.15s; padding:2px 5px; }
  .ob-pillar:hover .ob-pillar-edit { opacity:1; }
  .ob-pillar.active .ob-pillar-edit { color:var(--accent); opacity:0.7; }
  .ob-edit-input { width:100%; background:var(--bg-card); border:1px solid var(--accent-border); border-radius:5px; padding:5px 8px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; margin-bottom:4px; }

  /* Step 4 — Brand */
  .ob-brand-row { display:flex; align-items:center; gap:14px; padding:13px; background:var(--bg-surface); border:1px solid var(--border); border-radius:9px; margin-bottom:10px; }
  .ob-brand-swatch { width:36px; height:36px; border-radius:7px; flex-shrink:0; border:1px solid rgba(255,255,255,0.1); }
  .ob-brand-info { flex:1; min-width:0; }
  .ob-brand-label { font-size:11px; color:var(--text-muted); margin-bottom:2px; }
  .ob-brand-val { font-size:13px; font-weight:600; color:var(--text-primary); }
  .ob-brand-tag { font-size:10px; background:var(--accent-dim); color:var(--accent); padding:2px 8px; border-radius:6px; border:1px solid var(--accent-border); white-space:nowrap; }
  .ob-upload-zone { border:1px dashed var(--border); border-radius:9px; padding:20px; text-align:center; color:var(--text-muted); font-size:12px; cursor:pointer; transition:all 0.15s; margin-top:6px; }
  .ob-upload-zone:hover { border-color:var(--accent-border); color:var(--text-secondary); background:var(--accent-dim); }

  /* Step 5 — Instagram */
  .ob-ig-box { display:flex; flex-direction:column; align-items:center; padding:30px 20px; gap:14px; }
  .ob-ig-icon { width:64px; height:64px; border-radius:18px; background:linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888); display:flex; align-items:center; justify-content:center; }
  .ob-ig-connected { display:flex; align-items:center; gap:8px; background:rgba(49,220,186,0.12); border:1px solid var(--accent-border); border-radius:8px; padding:10px 18px; font-size:13px; color:var(--accent); font-weight:600; }

  /* Step 6 — Approve */
  .ob-post-preview { background:var(--bg-surface); border:1px solid var(--border); border-radius:10px; overflow:hidden; margin-bottom:16px; }
  .ob-post-img { width:100%; height:180px; background:linear-gradient(135deg,var(--bg-card2) 0%,#1e2535 100%); display:flex; align-items:center; justify-content:center; font-size:36px; }
  .ob-post-body { padding:14px; }
  .ob-post-caption { font-size:12px; color:var(--text-primary); line-height:1.6; margin-bottom:8px; }
  .ob-post-meta { font-size:11px; color:var(--text-muted); }
  .ob-post-tags { font-size:11px; color:#6399fa; margin-top:5px; }

  /* Complete screen */
  .ob-complete { display:flex; flex-direction:column; align-items:center; text-align:center; padding:20px 0; gap:12px; }
  .ob-complete-icon { width:64px; height:64px; border-radius:50%; background:var(--accent-dim); border:2px solid var(--accent); display:flex; align-items:center; justify-content:center; font-size:26px; }

  /* BRAND HUB PANEL */
  .bh-wrap { display:flex; gap:20px; height:100%; min-height:0; }
  .bh-main { flex:1; min-width:0; display:flex; flex-direction:column; overflow:hidden; }
  .bh-card-wrap { flex:1; min-height:0; overflow-y:auto; }
  .bh-preview { width:220px; flex-shrink:0; }
  .bh-tabs { display:flex; gap:2px; margin-bottom:14px; background:var(--bg-surface); border:1px solid var(--border); border-radius:9px; padding:4px; }
  .bh-tab { flex:1; padding:7px 2px; text-align:center; font-size:10px; font-weight:600; color:var(--text-muted); cursor:pointer; border-radius:6px; transition:all 0.15s; white-space:nowrap; }
  .bh-tab.active { background:var(--bg-card); color:var(--accent); }
  .bh-tab:hover:not(.active) { color:var(--text-secondary); }
  .bh-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:22px; padding-bottom:32px; flex:1; overflow-y:auto; }
  .bh-section-title { font-family:var(--font-head); font-size:15px; font-weight:700; letter-spacing:0.02em; margin-bottom:4px; }
  .bh-section-sub { font-size:11px; color:var(--text-muted); margin-bottom:20px; line-height:1.5; }
  .bh-field { margin-bottom:15px; }
  .bh-label { font-size:10px; font-weight:700; color:var(--text-secondary); text-transform:uppercase; letter-spacing:0.07em; margin-bottom:5px; }
  .bh-input { width:100%; background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; padding:9px 12px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; transition:border-color 0.15s; }
  .bh-input:focus { border-color:var(--accent-border); }
  .bh-input::placeholder { color:var(--text-muted); }
  .bh-textarea { width:100%; background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; padding:9px 12px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; resize:vertical; min-height:68px; line-height:1.6; transition:border-color 0.15s; }
  .bh-textarea:focus { border-color:var(--accent-border); }
  .bh-textarea::placeholder { color:var(--text-muted); }
  .bh-two-col { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .bh-chips { display:flex; flex-wrap:wrap; gap:7px; }
  .bh-chip { padding:5px 12px; border-radius:20px; font-size:11px; font-weight:600; border:1px solid var(--border); color:var(--text-secondary); cursor:pointer; transition:all 0.15s; background:var(--bg-surface); }
  .bh-chip.active { background:var(--accent-dim); border-color:var(--accent-border); color:var(--accent); }
  .bh-chip:hover:not(.active) { border-color:rgba(255,255,255,0.18); color:var(--text-primary); }
  /* An off-list value kept from a saved row (see CustomChips). */
  .bh-chip-custom { border-style:dashed; }
  /* 2x / day. Deliberately NOT styled like .bh-chip.active when owned: an
     active frequency chip means "this is the setting you picked", and this
     chip is never a setting. Dashed border reads as "not one of the above". */
  .bh-chip-2x { border-style:dashed; border-color:var(--accent-border); color:var(--accent); font-family:var(--font); }
  .bh-chip-2x:hover:not(.owned):not(:disabled) { background:var(--accent-dim); border-style:solid; }
  .bh-chip-2x:disabled { opacity:0.6; cursor:wait; }
  .bh-chip-2x.owned { border-style:solid; background:var(--accent-dim); border-color:var(--accent-border); color:var(--accent); cursor:default; }
  /* Posting days. Sized to sit under the frequency chips without competing
     with them: same surface and border, square rather than pill, so the row
     reads as a set of boxes to tick rather than a second set of options to
     choose one of. */
  .bh-days { display:flex; flex-wrap:wrap; gap:6px; }
  .bh-day { display:flex; align-items:center; gap:6px; padding:5px 10px; border-radius:7px; font-size:11px; font-weight:600; border:1px solid var(--border); color:var(--text-secondary); cursor:pointer; background:var(--bg-surface); transition:all 0.15s; user-select:none; }
  .bh-day:hover:not(.on) { border-color:rgba(255,255,255,0.18); color:var(--text-primary); }
  .bh-day.on { background:var(--accent-dim); border-color:var(--accent-border); color:var(--accent); }
  .bh-day input { accent-color:var(--accent); margin:0; cursor:pointer; }
  /* Amber and bold, not --text-secondary grey. It is the one thing standing
     between the client and a save, so it has to read as something to act on
     rather than as a caption. */
  .bh-days-note { margin-top:7px; font-size:11.5px; font-weight:600; color:#ffb020; }
  .bh-colour-row { display:flex; align-items:center; gap:10px; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; padding:10px 12px; margin-bottom:8px; }
  .bh-swatch { width:32px; height:32px; border-radius:7px; border:1px solid rgba(255,255,255,0.12); flex-shrink:0; }
  .bh-colour-val { font-size:12px; font-weight:600; color:var(--text-primary); }
  .bh-colour-val.unset { font-weight:500; color:var(--text-muted); }
  .bh-colour-name { font-size:10px; color:var(--text-muted); }
  /* A brand colour can be "not set" (''), which an <input type="color"> cannot
     hold, so the input sits invisibly over a swatch that shows stripes until
     a colour is picked. */
  .bh-colour-swatch { position:relative; width:32px; height:32px; border-radius:7px; border:1px solid rgba(255,255,255,0.12); flex-shrink:0; overflow:hidden; cursor:pointer; }
  .bh-colour-swatch.unset, .bh-prev-swatch.unset { background:repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 4px, transparent 4px 8px); border-style:dashed; border-color:rgba(255,255,255,0.22); }
  .bh-colour-input { position:absolute; inset:0; width:100%; height:100%; opacity:0; border:0; padding:0; cursor:pointer; }
  .bh-colour-clear { margin-left:auto; background:none; border:1px solid var(--border); border-radius:6px; padding:3px 9px; font-family:var(--font); font-size:10px; color:var(--text-muted); cursor:pointer; }
  .bh-colour-clear:hover { color:var(--text-primary); border-color:rgba(255,255,255,0.25); }
  .bh-tags { display:flex; flex-wrap:wrap; gap:6px; background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; padding:7px 10px; min-height:42px; cursor:text; transition:border-color 0.15s; }
  .bh-tags:focus-within { border-color:var(--accent-border); }
  .bh-tag { display:flex; align-items:center; gap:4px; background:var(--bg-card2); border:1px solid var(--border); border-radius:5px; padding:2px 8px; font-size:11px; color:var(--text-secondary); }
  .bh-tag-remove { cursor:pointer; color:var(--text-muted); font-size:11px; }
  .bh-tag-remove:hover { color:var(--text-primary); }
  .bh-tag-input { background:none; border:none; outline:none; font-family:var(--font); font-size:11px; color:var(--text-primary); min-width:80px; }
  .bh-tag-input::placeholder { color:var(--text-muted); }
  .bh-pillar-row { display:flex; align-items:center; gap:10px; padding:9px 12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; margin-bottom:7px; }
  .bh-pillar-dot { width:8px; height:8px; border-radius:50%; background:var(--accent); flex-shrink:0; }
  .bh-pillar-dot.off { background:var(--text-muted); }
  .bh-pillar-name { font-size:12px; font-weight:600; color:var(--text-primary); flex:1; }
  .bh-pillar-status { font-size:10px; color:var(--text-muted); }
  .bh-upload { border:1px dashed var(--border); border-radius:9px; padding:16px; text-align:center; color:var(--text-muted); font-size:12px; cursor:pointer; transition:all 0.15s; }
  .bh-upload:hover { border-color:var(--accent-border); color:var(--text-secondary); background:var(--accent-dim); }
  .bh-save-bar { display:flex; align-items:center; justify-content:space-between; margin-top:12px; padding:11px 16px; background:var(--bg-surface); border:1px solid var(--border); border-radius:9px; gap:12px; }
  .bh-save-status { font-size:11px; color:var(--text-muted); flex-shrink:0; white-space:nowrap; }
  .bh-save-status.saved { color:var(--accent); }
  /* Both of these were applied in the markup and styled nowhere. */
  .bh-save-status.error { color:#ff4d4d; font-weight:700; }
  .bh-save-status.warn  { color:#ffb020; font-weight:700; }
  .bh-prev-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:16px; position:sticky; top:0; }
  .bh-prev-title { font-family:var(--font-head); font-size:10px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:var(--text-muted); margin-bottom:12px; }
  .bh-prev-logo { width:44px; height:44px; border-radius:10px; background:#1a3a8f; display:flex; align-items:center; justify-content:center; margin-bottom:8px; }
  .bh-prev-biz { font-family:var(--font-head); font-size:13px; font-weight:700; margin-bottom:2px; }
  .bh-prev-tag { font-size:11px; color:var(--text-secondary); margin-bottom:12px; font-style:italic; }
  .bh-prev-divider { border-bottom:1px solid var(--border); margin-bottom:10px; }
  .bh-prev-row { display:flex; align-items:flex-start; gap:7px; margin-bottom:8px; }
  .bh-prev-label { font-size:10px; color:var(--text-muted); min-width:48px; padding-top:1px; }
  .bh-prev-val { font-size:11px; color:var(--text-primary); font-weight:500; line-height:1.5; }
  .bh-prev-colours { display:flex; gap:5px; margin-bottom:10px; }
  .bh-prev-swatch { height:18px; border-radius:4px; flex:1; border:1px solid rgba(255,255,255,0.08); }
  .bh-prev-tones { display:flex; flex-wrap:wrap; gap:4px; }
  .bh-prev-tone { font-size:10px; background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); padding:2px 7px; border-radius:10px; }

  /* CONTENT CALENDAR PANEL */
  .cal-wrap { display:flex; flex-direction:column; height:100%; gap:16px; }
  .cal-header { display:flex; align-items:center; gap:14px; flex-shrink:0; }
  .cal-month { font-family:var(--font-head); font-size:20px; font-weight:700; letter-spacing:0.03em; flex:1; }
  .cal-nav-btn { background:var(--bg-card); border:1px solid var(--border); color:var(--text-secondary); font-size:13px; font-weight:600; padding:6px 14px; border-radius:7px; cursor:pointer; transition:all 0.15s; font-family:var(--font); }
  .cal-nav-btn:hover { background:var(--bg-card2); color:var(--text-primary); border-color:rgba(255,255,255,0.15); }
  .cal-legend { display:flex; align-items:center; gap:14px; margin-left:auto; }
  .cal-legend-item { display:flex; align-items:center; gap:5px; font-size:11px; color:var(--text-muted); }
  .cal-legend-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }

  .cal-grid-wrap { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; overflow:hidden; flex-shrink:0; }
  .cal-dow-row { display:grid; grid-template-columns:repeat(7,1fr); border-bottom:1px solid var(--border); }
  .cal-dow { padding:8px 0; text-align:center; font-size:10px; font-weight:700; color:#31DCBA; text-transform:uppercase; letter-spacing:0.07em; }
  .cal-days { display:grid; grid-template-columns:repeat(7,1fr); }
  .cal-day { min-height:72px; padding:7px 8px; border-right:1px solid var(--border); border-bottom:1px solid var(--border); cursor:pointer; transition:background 0.12s; position:relative; }
  .cal-day:nth-child(7n) { border-right:none; }
  .cal-day:nth-last-child(-n+7):nth-child(7n+1),
  .cal-day:nth-last-child(-n+7):nth-child(7n+2),
  .cal-day:nth-last-child(-n+7):nth-child(7n+3),
  .cal-day:nth-last-child(-n+7):nth-child(7n+4),
  .cal-day:nth-last-child(-n+7):nth-child(7n+5),
  .cal-day:nth-last-child(-n+7):nth-child(7n+6),
  .cal-day:nth-last-child(1)  { border-bottom:none; }
  .cal-day:hover:not(.cal-day-other) { background:rgba(255,255,255,0.03); }
  .cal-day.selected { background:var(--accent-dim); }
  .cal-day-other { opacity:0.3; cursor:default; }
  .cal-day-other:hover { background:none; }
  .cal-day-num { font-size:12px; font-weight:600; color:var(--text-secondary); line-height:1; margin-bottom:5px; display:inline-flex; width:22px; height:22px; align-items:center; justify-content:center; border-radius:50%; }
  .cal-day-num.today { background:var(--accent); color:var(--bg-base); font-weight:700; }
  .cal-day.selected .cal-day-num:not(.today) { color:var(--accent); }
  .cal-dots { display:flex; flex-wrap:wrap; gap:3px; margin-top:2px; }
  .cal-dot { width:16px; height:16px; border-radius:50%; flex-shrink:0; }
  .cal-dot-scheduled { background:var(--accent); }
  .cal-dot-posted    { background:rgba(49,220,186,0.35); }
  .cal-dot-pending   { background:#f5a623; }

  .cal-detail { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:16px 18px; flex-shrink:0; }
  .cal-detail-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
  .cal-detail-title { font-family:var(--font-head); font-size:14px; font-weight:700; letter-spacing:0.02em; }
  .cal-detail-close { background:none; border:none; color:var(--text-muted); font-size:16px; cursor:pointer; padding:0 4px; line-height:1; }
  .cal-detail-close:hover { color:var(--text-primary); }
  .cal-post-row { display:flex; align-items:center; gap:10px; padding:8px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
  .cal-post-row:last-child { border-bottom:none; }
  .cal-post-thumb { width:32px; height:32px; background:var(--bg-card2); border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
  .cal-post-time { font-size:11px; font-weight:700; color:var(--text-secondary); white-space:nowrap; min-width:46px; }
  .cal-post-caption { font-size:12px; color:var(--text-primary); font-weight:500; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .cal-post-type { font-size:10px; color:var(--text-muted); white-space:nowrap; }
  .cal-post-actions { display:flex; align-items:center; gap:4px; flex-shrink:0; margin-left:2px; }
  .cal-post-confirm { display:flex; align-items:center; gap:6px; flex-shrink:0; margin-left:2px; }
  .cal-post-confirm-text { font-size:11px; color:var(--text-secondary); white-space:nowrap; }
  .cal-post-btn { font-size:11px; font-family:var(--font-head); letter-spacing:0.02em; border-radius:5px; padding:3px 8px; cursor:pointer; border:1px solid transparent; transition:background 0.12s, color 0.12s; line-height:1.4; }
  .cal-post-btn-ghost { background:rgba(255,255,255,0.06); color:var(--text-secondary); border-color:rgba(255,255,255,0.1); }
  .cal-post-btn-ghost:hover { background:rgba(255,255,255,0.1); color:var(--text-primary); }
  .cal-post-btn-danger { background:rgba(239,68,68,0.15); color:#f87171; border-color:rgba(239,68,68,0.3); }
  .cal-post-btn-danger:hover { background:rgba(239,68,68,0.28); }
  .cal-post-btn-delete { background:none; color:rgba(239,68,68,0.45); border:none; padding:3px 5px; font-size:13px; }
  .cal-post-btn-delete:hover { color:#f87171; background:rgba(239,68,68,0.1); border-radius:4px; }
  .cal-pill { font-size:9px; font-weight:700; padding:2px 7px; border-radius:8px; white-space:nowrap; margin-left:4px; }
  .cal-pill-scheduled { background:rgba(49,220,186,0.12); color:var(--accent); }
  .cal-pill-posted    { background:rgba(49,220,186,0.07); color:rgba(49,220,186,0.6); }
  .cal-pill-pending   { background:rgba(245,166,35,0.12); color:#f5a623; }
  .cal-pill-both { background:rgba(49,220,186,0.10); color:#31DCBA; border:1px solid rgba(49,220,186,0.18); }
  .cal-pill-ig   { background:rgba(193,80,180,0.10); color:#d47ed4; border:1px solid rgba(193,80,180,0.18); }
  .cal-pill-fb   { background:rgba(66,103,178,0.12); color:#7a9fd4; border:1px solid rgba(66,103,178,0.20); }
  .cal-pill-story { background:var(--accent); color:#0d0f14; border:1px solid var(--accent); }

  .cal-unscheduled { display:flex; flex-direction:column; gap:10px; flex-shrink:0; }
  .cal-unscheduled-heading { font-family:var(--font-head); font-size:14px; font-weight:700; letter-spacing:0.02em; }
  .cal-unscheduled-empty { font-size:13px; color:var(--text-muted); text-align:center; padding:18px 0; margin:0; }
  .cal-unscheduled-loading { font-size:13px; color:var(--text-muted); margin:0; padding:8px 0; }
  .cal-upost-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; padding:12px 14px; display:flex; flex-direction:column; gap:6px; }
  .cal-upost-caption { font-size:13px; color:var(--text-primary); font-weight:500; }
  .cal-upost-date { font-size:11px; color:var(--text-muted); }

  .cal-sched-section { display:flex; flex-direction:column; gap:8px; margin-top:10px; padding-top:10px; border-top:1px solid rgba(255,255,255,0.06); }
  .cal-sched-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .cal-sched-label { font-size:10px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.07em; min-width:42px; flex-shrink:0; }
  .cal-sched-input { background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; padding:6px 10px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; transition:border-color 0.15s; color-scheme:dark; }
  .cal-sched-input:focus { border-color:var(--accent-border); }
  .cal-sched-quick { background:var(--bg-surface); border:1px solid var(--border); border-radius:6px; padding:5px 10px; font-family:var(--font); font-size:11px; font-weight:600; color:var(--text-secondary); cursor:pointer; transition:all 0.15s; }
  .cal-sched-quick:hover { border-color:rgba(255,255,255,0.15); color:var(--text-primary); }
  .cal-sched-quick.active { border-color:var(--accent-border); color:var(--accent); background:var(--accent-dim); }
  .cal-swatch-row { display:flex; align-items:center; gap:6px; }
  .cal-swatch { width:18px; height:18px; border-radius:50%; cursor:pointer; border:2px solid transparent; transition:all 0.15s; flex-shrink:0; }
  .cal-swatch:hover { transform:scale(1.15); }
  .cal-swatch.selected { border-color:#fff; box-shadow:0 0 0 1px rgba(255,255,255,0.3); }
  .cal-swatch.brand { box-shadow:0 0 0 1px var(--accent-border); }
  .cal-swatch.brand.selected { border-color:#fff; box-shadow:0 0 0 2px var(--accent); }
  .cal-sched-cta { width:100%; padding:8px; border-radius:7px; background:var(--accent); color:var(--bg-base); font-family:var(--font-head); font-size:12px; font-weight:700; letter-spacing:0.04em; border:none; cursor:pointer; transition:all 0.15s; margin-top:2px; }
  .cal-sched-cta:hover:not(:disabled) { background:#28c4a8; }
  .cal-sched-cta:disabled { opacity:0.4; cursor:default; }
  .cal-sched-confirmed { font-size:13px; font-weight:700; color:var(--accent); text-align:center; padding:12px 0; }
  .cal-sched-failed { font-size:12px; font-weight:600; color:#ff5252; text-align:center; padding:12px 0; }
  .cal-sched-blocked { font-size:12px; font-weight:600; color:var(--accent); margin-top:4px; }

  /* SCHEDULER GRID — thumbnail cards w/ hover controls + DnD */
  .sched-grid-wrap { margin-top:24px; display:flex; flex-direction:column; }
  .sched-grid-heading { font-family:var(--font-head); font-size:13px; letter-spacing:0.12em; text-transform:uppercase; color:var(--text-secondary); margin-bottom:12px; }
  .sched-grid { display:grid; grid-template-columns:repeat(2, minmax(0, 1fr)); gap:12px; }
  @media (min-width: 900px) { .sched-grid { grid-template-columns:repeat(3, minmax(0, 1fr)); } }
  /* touch-action:manipulation keeps the browser's own vertical page scroll and
     the chip row's horizontal scroll — an engaged drag stops scrolling with a
     non-passive touchmove instead (see usePointerReorder). The callout and
     user-select rules kill iOS's long-press selection bubble. */
  .sched-card { background:rgba(13,15,20,0.85); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); border:1px solid rgba(49,220,186,0.12); border-radius:12px; overflow:hidden; cursor:grab; transition:border-color 0.2s, transform 0.18s, box-shadow 0.2s; position:relative; touch-action:manipulation; -webkit-touch-callout:none; -webkit-user-select:none; user-select:none; }
  .sched-card:hover { border-color:rgba(49,220,186,0.4); box-shadow:0 6px 24px rgba(49,220,186,0.08); }
  .sched-card.dragging { opacity:0.4; transform:scale(0.97); cursor:grabbing; }
  .sched-card.drop-target { border-color:#31DCBA; box-shadow:0 0 0 2px rgba(49,220,186,0.3); }
  /* Native DnD drew its own drag image; pointer events do not, so the ghost is
     a real element parked on the pointer. pointer-events:none keeps it out of
     the elementFromPoint hit test that finds the drop target. */
  .sched-drag-ghost { position:fixed; left:0; top:0; z-index:1200; pointer-events:none; width:112px; border-radius:10px; overflow:hidden; background:rgba(13,15,20,0.96); border:1px solid #31DCBA; box-shadow:0 14px 38px rgba(0,0,0,0.6); opacity:0.94; transform:translate3d(-9999px,-9999px,0); }
  .sched-drag-ghost img { display:block; width:100%; aspect-ratio:1 / 1; object-fit:cover; }
  .sched-drag-ghost-fallback { width:100%; aspect-ratio:1 / 1; background:#31DCBA; }
  .sched-drag-ghost-label { font-family:var(--font-head); font-size:10px; letter-spacing:0.05em; color:#31DCBA; padding:5px 7px; text-align:center; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .sched-drag-ghost-chip { width:64px; }
  .sched-card-thumb { position:relative; width:100%; aspect-ratio:1 / 1; overflow:hidden; background:#0d0f14; }
  .sched-card-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
  .sched-card-thumb-fallback { width:100%; height:100%; }
  .sched-card-overlay { position:absolute; inset:0; background:rgba(13,15,20,0.72); display:flex; align-items:center; justify-content:center; gap:14px; opacity:0; transition:opacity 0.18s; backdrop-filter:blur(2px); }
  .sched-card:hover .sched-card-overlay { opacity:1; }
  .sched-card-overlay-btn { width:38px; height:38px; border-radius:50%; background:rgba(49,220,186,0.16); border:1px solid rgba(49,220,186,0.45); color:#31DCBA; font-size:15px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.15s, transform 0.15s; }
  .sched-card-overlay-btn:hover { background:rgba(49,220,186,0.32); transform:scale(1.08); }
  .sched-card-overlay-danger { color:#ff7a7a; border-color:rgba(255,107,107,0.45); background:rgba(255,107,107,0.12); }
  .sched-card-overlay-danger:hover { background:rgba(255,107,107,0.26); }
  .sched-card-meta { padding:10px 12px 12px; }
  .sched-card-time { font-family:var(--font-head); font-size:11px; letter-spacing:0.06em; color:#31DCBA; margin-bottom:5px; font-weight:700; }
  .sched-card-caption { font-size:12px; color:var(--text-secondary); line-height:1.4; margin-bottom:7px; min-height:1.4em; overflow:hidden; }
  .sched-card-platforms { display:flex; gap:4px; }
  .sched-card-pf { font-family:var(--font-head); font-size:9px; letter-spacing:0.08em; padding:2px 7px; border-radius:4px; background:rgba(49,220,186,0.12); color:#31DCBA; border:1px solid rgba(49,220,186,0.25); }
  .sched-card-pf.story { background:var(--accent); color:#0d0f14; border:1px solid var(--accent); }
  .sched-card-platform-badge { position:absolute; bottom:7px; left:7px; background:rgba(13,15,20,0.82); backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px); border-radius:5px; padding:2px 7px; font-family:var(--font-head); font-size:9px; font-weight:700; letter-spacing:0.06em; color:#fff; z-index:1; pointer-events:none; }
  .sched-card-dot-badge { position:absolute; bottom:8px; right:8px; width:13px; height:13px; border-radius:50%; z-index:1; border:2px solid rgba(0,0,0,0.55); pointer-events:none; box-shadow:0 1px 4px rgba(0,0,0,0.5); }
  .sched-card-period { font-family:var(--font-head); font-size:9px; letter-spacing:0.08em; text-transform:uppercase; color:var(--text-muted); margin-bottom:3px; }
  .sched-card-reschedule { padding:8px 12px; background:rgba(49,220,186,0.08); border-top:1px solid rgba(49,220,186,0.22); display:flex; flex-direction:column; gap:8px; }
  .sched-card-reschedule-actions { display:flex; justify-content:flex-end; gap:6px; }
  /* Why a post could not be moved — the daily cap's sentence, or the generic
     failure line. Shares one class across the reschedule picker, the chip
     picker and the chip modal so all three read the same. */
  .sched-limit-error { color:#FF8A80; font-size:11.5px; line-height:1.45; width:100%; }
  /* Growth Boost bought but not yet configured. Accent rather than red: this is
     an unused entitlement, not an error. */
  .sched-boost-banner { color:var(--accent); background:rgba(49,220,186,0.08); border:1px solid var(--accent-border);
    border-radius:8px; padding:9px 12px; font-size:12.5px; font-weight:600; line-height:1.45; margin-bottom:10px; }
  .sched-card-dtinput { background:rgba(255,255,255,0.05); border:1px solid rgba(49,220,186,0.3); color:#fff; padding:5px 7px; border-radius:5px; font-family:var(--font); font-size:11px; flex:1 1 140px; min-width:0; outline:none; color-scheme:dark; }
  .sched-card-dtinput:focus { border-color:#31DCBA; }
  .sched-card-confirm { background:#31DCBA; color:#0d0f14; border:none; padding:5px 11px; border-radius:5px; font-family:var(--font-head); font-size:10px; letter-spacing:0.06em; cursor:pointer; font-weight:700; }
  .sched-card-confirm:hover { background:#28c4a8; }
  .sched-card-cancel { background:transparent; color:var(--text-muted); border:1px solid rgba(255,255,255,0.1); padding:5px 8px; border-radius:5px; cursor:pointer; font-size:10px; }
  .sched-chips-heading { font-family:var(--font-head); font-size:13px; letter-spacing:0.12em; text-transform:uppercase; color:var(--text-secondary); margin:24px 0 10px; }
  .sched-chips-row { display:flex; gap:10px; overflow-x:auto; padding:2px 2px 10px; scroll-snap-type:x proximity; }
  .sched-chips-row::-webkit-scrollbar { height:6px; }
  .sched-chips-row::-webkit-scrollbar-thumb { background:rgba(49,220,186,0.25); border-radius:3px; }
  .sched-chips-row::-webkit-scrollbar-track { background:transparent; }
  .sched-chip { position:relative; flex:0 0 auto; display:flex; align-items:center; gap:8px; padding:7px; background:rgba(13,15,20,0.85); backdrop-filter:blur(10px); -webkit-backdrop-filter:blur(10px); border:1px solid rgba(49,220,186,0.14); border-radius:10px; scroll-snap-align:start; transition:border-color 0.2s; }
  .sched-chip:hover { border-color:rgba(49,220,186,0.35); }
  .sched-chip-delete { position:absolute; top:-7px; right:-7px; width:18px; height:18px; border-radius:50%; background:#c0392b; border:none; color:#fff; font-size:10px; line-height:1; cursor:pointer; display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity 0.15s; padding:0; }
  .sched-chip:hover .sched-chip-delete { opacity:1; }
  .sched-chip-thumb-wrap { position:relative; width:54px; height:54px; flex-shrink:0; }
  .sched-chip-thumb { width:54px; height:54px; object-fit:cover; border-radius:6px; display:block; }
  .sched-chip-thumb-empty { background:rgba(49,220,186,0.1); }
  .sched-chip-thumb-hint { position:absolute; bottom:0; left:0; right:0; background:rgba(0,0,0,0.48); color:rgba(255,255,255,0.72); font-size:9px; font-family:var(--font-head); letter-spacing:0.03em; text-align:center; padding:3px 0; border-radius:0 0 6px 6px; pointer-events:none; }
  .sched-chip-add { background:transparent; border:1px dashed rgba(49,220,186,0.5); color:#31DCBA; padding:6px 11px; border-radius:6px; font-family:var(--font-head); font-size:10px; letter-spacing:0.06em; cursor:pointer; white-space:nowrap; font-weight:700; transition:all 0.15s; }
  .sched-chip-add:hover { background:rgba(49,220,186,0.14); border-color:#31DCBA; border-style:solid; }
  .sched-chip-picker { display:flex; gap:5px; align-items:center; flex-wrap:wrap; max-width:260px; }

  /* HOME PANEL */
  .hp-wrap { display:flex; flex-direction:column; gap:16px; padding-bottom:24px; }

  /* GETTING STARTED — Home checklist (Self-Serve only). Mobile-first: every
     control is at least 44px tall and nothing depends on hover. */
  .gs-card { background:var(--bg-card); border:1px solid var(--accent-border); border-radius:12px; padding:16px; display:flex; flex-direction:column; gap:12px; }
  .gs-head { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
  .gs-title { font-size:15px; font-weight:700; color:var(--text-primary); flex:1; min-width:0; }
  .gs-progress-text { font-size:12px; color:var(--text-secondary); }
  .gs-bar { height:6px; border-radius:3px; background:var(--accent-dim); overflow:hidden; }
  .gs-bar-fill { height:100%; background:var(--accent); border-radius:3px; transition:width 0.3s; }
  .gs-toggle { min-height:44px; min-width:44px; padding:0 12px; background:transparent; border:1px solid var(--border); border-radius:8px; color:var(--text-secondary); font-family:var(--font); font-size:12px; font-weight:600; cursor:pointer; }
  .gs-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
  .gs-step { display:flex; align-items:center; gap:12px; padding:10px 12px; border:1px solid var(--border); border-radius:10px; flex-wrap:wrap; }
  .gs-step.next { border-color:var(--accent); background:var(--accent-dim); }
  .gs-step.done { opacity:0.6; }
  .gs-num { width:26px; height:26px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; border:1px solid var(--border); color:var(--text-secondary); }
  .gs-step.done .gs-num { background:var(--accent); border-color:var(--accent); color:var(--bg-base); }
  .gs-step.next .gs-num { border-color:var(--accent); color:var(--accent); }
  .gs-body { flex:1; min-width:180px; display:flex; flex-direction:column; gap:2px; }
  .gs-step-title { font-size:13px; font-weight:700; color:var(--text-primary); display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .gs-step.done .gs-step-title { text-decoration:line-through; color:var(--text-secondary); }
  .gs-next-tag { font-size:10px; font-weight:700; letter-spacing:0.05em; text-transform:uppercase; color:var(--accent); background:var(--bg-card); border:1px solid var(--accent-border); border-radius:6px; padding:1px 6px; }
  .gs-line { font-size:12px; color:var(--text-secondary); line-height:1.5; }
  .gs-muted { font-size:11px; color:var(--text-muted); line-height:1.5; }
  .gs-btn { min-height:44px; padding:0 16px; flex-shrink:0; }
  .gs-done-row { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
  .gs-first-post-choices { display:flex; flex-direction:column; gap:10px; margin:4px 0 12px; }
  .gs-first-post-choices .ob-btn { min-height:48px; width:100%; font-size:14px; }
  .gs-sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  .hp-banner { background:var(--bg-card); border:1px solid var(--accent-border); border-radius:12px; padding:18px 22px; display:flex; align-items:center; gap:16px; }
  .hp-banner-dot { width:10px; height:10px; border-radius:50%; background:var(--accent); flex-shrink:0; box-shadow:0 0 8px var(--accent); }
  .hp-banner-main { flex:1; min-width:0; }
  .hp-banner-title { font-family:var(--font-head); font-size:16px; font-weight:700; letter-spacing:0.02em; margin-bottom:3px; }
  .hp-banner-sub { font-size:12px; color:var(--text-secondary); }
  .hp-banner-next { text-align:right; flex-shrink:0; }
  .hp-banner-next-label { font-size:10px; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.07em; margin-bottom:3px; }
  .hp-banner-next-val { font-family:var(--font-head); font-size:14px; font-weight:700; color:var(--accent); }
  .hp-banner-next-sub { font-size:11px; color:var(--text-muted); margin-top:1px; }

  /* INSTALL APP — only rendered once the browser says install is possible */
  .hp-install-row { display:flex; align-items:center; gap:10px; margin-top:9px; }
  .hp-install-btn { display:inline-flex; align-items:center; gap:6px; padding:5px 11px; background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); border-radius:7px; font-family:var(--font-head); font-size:11px; font-weight:700; letter-spacing:0.06em; text-transform:uppercase; cursor:pointer; transition:background 0.15s, border-color 0.15s; }
  .hp-install-btn:hover { background:rgba(49,220,186,0.2); border-color:var(--accent); }
  .hp-install-dismiss { background:none; border:none; color:var(--text-muted); font-family:var(--font); font-size:11px; cursor:pointer; padding:4px 2px; transition:color 0.15s; }
  .hp-install-dismiss:hover { color:var(--text-secondary); }

  .hp-ios-overlay { position:fixed; inset:0; z-index:1200; background:rgba(0,0,0,0.72); backdrop-filter:blur(5px); -webkit-backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; padding:16px; }
  .hp-ios-modal { background:#0d0f14; border:1px solid var(--accent-border); border-radius:16px; width:100%; max-width:360px; padding:22px 24px 20px; box-shadow:0 24px 80px rgba(0,0,0,0.7); }
  .hp-ios-title { font-family:var(--font-head); font-size:17px; font-weight:700; letter-spacing:0.01em; color:var(--text-primary); margin-bottom:8px; }
  .hp-ios-lead { font-size:12.5px; color:var(--text-secondary); line-height:1.6; margin-bottom:16px; }
  .hp-ios-steps { display:flex; flex-direction:column; gap:12px; margin-bottom:18px; }
  .hp-ios-step { display:flex; align-items:center; gap:11px; font-size:12.5px; color:var(--text-secondary); line-height:1.45; }
  .hp-ios-step strong { color:var(--text-primary); font-weight:700; }
  .hp-ios-step-icon { width:34px; height:34px; border-radius:9px; background:var(--bg-card2); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; flex-shrink:0; color:var(--accent); }
  .hp-ios-close { width:100%; padding:11px; border-radius:10px; background:var(--accent); color:var(--bg-base); border:none; font-family:var(--font-head); font-size:12.5px; font-weight:700; letter-spacing:0.04em; cursor:pointer; transition:background 0.15s; }
  .hp-ios-close:hover { background:#28c4a8; }

  .hp-upcoming { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:16px 18px; }
  .hp-upcoming-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.02em; margin-bottom:12px; }
  .hp-week-row { display:flex; align-items:center; gap:12px; padding:7px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
  .hp-week-row:last-child { border-bottom:none; }
  .hp-week-date { font-size:11px; font-weight:700; color:var(--text-secondary); min-width:60px; }
  .hp-week-date.today { color:var(--accent); }
  .hp-week-emoji { font-size:14px; width:22px; text-align:center; flex-shrink:0; }
  .hp-week-caption { font-size:12px; color:var(--text-primary); flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .hp-week-time { font-size:11px; color:var(--text-muted); white-space:nowrap; }
  .hp-week-type { font-size:10px; color:var(--text-muted); white-space:nowrap; }
  .hp-week-pill { font-size:9px; font-weight:700; padding:2px 7px; border-radius:8px; white-space:nowrap; }
  @keyframes hp-shimmer { 0%,100%{opacity:0.35} 50%{opacity:0.65} }
  .hp-skel { border-radius:4px; background:var(--bg-card2); animation:hp-shimmer 1.4s ease-in-out infinite; }

  /* CONTENT QUEUE PANEL */
  .cq-wrap { display:flex; flex-direction:column; gap:16px; padding-bottom:32px; max-width:720px; }
  .cq-quota-banner { display:flex; align-items:center; gap:10px; padding:11px 16px; background:rgba(245,166,35,0.12); border:1px solid rgba(245,166,35,0.35); border-radius:9px; color:#f5a623; font-size:13px; font-weight:500; }
  .cq-header { display:flex; align-items:center; justify-content:space-between; }
  .cq-title { font-family:var(--font-head); font-size:20px; font-weight:700; letter-spacing:0.02em; }
  .cq-usage-meter { font-size:12px; color:var(--text-muted); }
  .cq-usage-meter strong { color:var(--text-secondary); font-weight:600; }
  .cq-topic-row { display:flex; gap:10px; }
  .cq-topic-input { flex:1; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; padding:10px 14px; color:var(--text-primary); font-size:13px; outline:none; }
  .cq-topic-input:focus { border-color:var(--accent-border); }
  .cq-topic-input:disabled { opacity:0.5; }
  .cq-generate-btn, .ig-generate-btn { display:flex; align-items:center; gap:8px; padding:10px 20px; background:var(--accent); color:var(--bg-base); border:none; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap; transition:opacity .15s; }
  .cq-generate-btn:disabled, .ig-generate-btn:disabled { opacity:0.55; cursor:not-allowed; }
  .cq-spinner { width:13px; height:13px; border:2px solid rgba(13,15,20,.3); border-top-color:var(--bg-base); border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
  .cq-error { font-size:12px; color:#f87171; padding:8px 12px; background:rgba(248,113,113,0.08); border-radius:7px; border:1px solid rgba(248,113,113,0.2); }
  .cq-toast { display:flex; align-items:center; gap:8px; padding:11px 16px; background:rgba(49,220,186,0.1); border:1px solid var(--accent-border); border-radius:9px; color:var(--accent); font-size:13px; font-weight:500; }
  .cq-tpl-label { font-size:11px; color:var(--text-muted); margin-bottom:2px; }
  .cq-tpl-row { display:flex; gap:8px; overflow-x:auto; padding:2px 2px 6px; }
  .cq-tpl-row::-webkit-scrollbar { height:6px; }
  .cq-tpl-row::-webkit-scrollbar-thumb { background:rgba(49,220,186,0.25); border-radius:3px; }
  .cq-tpl-row::-webkit-scrollbar-track { background:transparent; }
  .cq-tpl-chip { flex:0 0 auto; white-space:nowrap; padding:6px 13px; border-radius:20px; font-size:11px; font-weight:600; cursor:pointer; transition:all .15s; background:rgba(13,15,20,0.85); border:1px solid var(--border); color:var(--text-secondary); }
  .cq-tpl-chip:hover:not(.active) { border-color:rgba(255,255,255,0.18); color:var(--text-primary); }
  .cq-tpl-chip.active { border-color:#31DCBA; color:#31DCBA; background:rgba(49,220,186,0.08); }
  .cq-tpl-chip:disabled { opacity:0.5; cursor:not-allowed; }

  /* POST APPROVAL QUEUE PANEL */
  .aq-wrap { display:flex; flex-direction:column; gap:16px; padding-bottom:32px; max-width:760px; }
  .aq-header { display:flex; align-items:center; gap:12px; }
  .aq-title { font-family:var(--font-head); font-size:20px; font-weight:700; letter-spacing:0.02em; }
  .aq-count-pill { background:var(--accent); color:var(--bg-base); font-size:11px; font-weight:700; padding:3px 9px; border-radius:12px; }
  .aq-count-pill.zero { background:rgba(255,255,255,0.08); color:var(--text-muted); }
  .aq-all-done { display:flex; flex-direction:column; align-items:center; gap:10px; padding:48px 0; color:var(--text-muted); }
  .aq-all-done-icon { font-size:36px; }
  .aq-all-done-title { font-family:var(--font-head); font-size:16px; font-weight:700; color:var(--text-secondary); }
  .aq-all-done-sub { font-size:12px; }

  /* Post card */
  .aq-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; overflow:hidden; transition:opacity 0.3s, border-color 0.2s; }
  .aq-card.approved { opacity:0.55; border-color:rgba(49,220,186,0.2); }
  .aq-card.edit_requested { opacity:0.55; border-color:rgba(245,166,35,0.2); }
  .aq-card-inner { display:flex; gap:0; }
  .aq-img { width:140px; min-width:140px; background:linear-gradient(135deg,var(--bg-card2) 0%,#1e2535 100%); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; padding:16px 0; flex-shrink:0; position:relative; }
  .aq-img-emoji { font-size:34px; }
  .aq-type-badge { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.07em; padding:2px 8px; border-radius:8px; background:rgba(255,255,255,0.07); color:var(--text-muted); }
  .aq-body { flex:1; padding:16px 18px; display:flex; flex-direction:column; gap:10px; min-width:0; }
  .aq-caption { font-size:13px; color:var(--text-primary); line-height:1.65; }
  .aq-meta-row { display:flex; align-items:center; gap:14px; }
  .aq-meta-item { display:flex; align-items:center; gap:5px; font-size:11px; color:var(--text-muted); }
  .aq-meta-icon { opacity:0.6; }
  .aq-actions { display:flex; align-items:center; gap:8px; margin-top:auto; padding-top:4px; }
  .aq-btn { padding:7px 18px; border-radius:7px; font-family:var(--font); font-size:12px; font-weight:600; cursor:pointer; border:none; transition:all 0.15s; }
  .aq-btn-approve { background:var(--accent); color:var(--bg-base); }
  .aq-btn-approve:hover { background:#28c4a8; }
  .aq-btn-approve:disabled { opacity:0.4; cursor:default; }
  .aq-btn-edit { background:transparent; color:var(--text-secondary); border:1px solid var(--border); }
  .aq-btn-edit:hover { border-color:rgba(245,166,35,0.5); color:#f5a623; }
  .aq-btn-edit:disabled { opacity:0.4; cursor:default; }

  /* Swipe approval mode — opt-in card stack. Presentation only: every commit
     path below calls the same handlers the list-view card calls. */
  .aq-mode-row { display:flex; align-items:center; gap:8px; margin-bottom:12px; flex-wrap:wrap; }
  .aq-mode-btn { display:inline-flex; align-items:center; gap:6px; background:transparent; border:1px solid var(--border); color:var(--text-secondary); font-family:var(--font); font-size:12px; font-weight:600; padding:7px 14px; border-radius:7px; cursor:pointer; transition:all 0.15s; }
  .aq-mode-btn:hover { border-color:var(--accent-border); color:var(--text-primary); }
  .aq-mode-btn.active { background:var(--accent); border-color:var(--accent); color:var(--bg-base); }
  .aq-swipe-stage { position:relative; padding:4px 0 2px; }
  .aq-swipe-deck { position:relative; }
  .aq-swipe-peek { position:absolute; left:0; right:0; top:0; z-index:0; pointer-events:none; opacity:0.5; }
  .aq-swipe-live { position:relative; z-index:2; touch-action:pan-y; }
  .aq-swipe-live.dragging { user-select:none; cursor:grabbing; }
  .aq-swipe-badge { position:absolute; top:16px; z-index:3; pointer-events:none; font-size:11px; font-weight:800; letter-spacing:0.09em; text-transform:uppercase; padding:6px 12px; border-radius:7px; border:2px solid; }
  .aq-swipe-badge.approve { right:16px; color:var(--accent); border-color:var(--accent); background:rgba(49,220,186,0.15); }
  .aq-swipe-badge.edit { left:16px; color:#f5a623; border-color:#f5a623; background:rgba(245,166,35,0.15); }
  .aq-swipe-foot { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:14px; flex-wrap:wrap; }
  .aq-swipe-count { font-size:11px; color:var(--text-muted); font-weight:600; }
  .aq-swipe-nav { display:flex; gap:6px; }
  .aq-swipe-navbtn { background:transparent; border:1px solid var(--border); color:var(--text-secondary); font-family:var(--font); font-size:11px; font-weight:600; padding:5px 12px; border-radius:6px; cursor:pointer; transition:all 0.15s; }
  .aq-swipe-navbtn:hover:not(:disabled) { border-color:var(--accent-border); color:var(--text-primary); }
  .aq-swipe-navbtn:disabled { opacity:0.35; cursor:default; }
  .aq-swipe-hint { font-size:11px; color:var(--text-muted); text-align:center; margin-top:10px; line-height:1.6; }
  .aq-swipe-blocked { font-size:11px; color:#f5a623; text-align:center; margin-top:8px; line-height:1.5; }
  .aq-swipe-undo { display:flex; align-items:center; gap:8px; background:rgba(49,220,186,0.09); border:1px solid rgba(49,220,186,0.22); border-radius:8px; padding:9px 13px; margin-top:12px; font-size:12px; color:var(--text-secondary); flex-wrap:wrap; }
  .aq-swipe-undo.failed { background:rgba(245,166,35,0.09); border-color:rgba(245,166,35,0.26); color:#f5a623; }

  /* Status banners */
  .aq-status-approved { display:flex; align-items:center; gap:7px; background:rgba(49,220,186,0.1); border-top:1px solid rgba(49,220,186,0.15); padding:9px 18px; font-size:12px; font-weight:600; color:var(--accent); }
  .aq-status-edit { display:flex; align-items:center; gap:7px; background:rgba(245,166,35,0.08); border-top:1px solid rgba(245,166,35,0.15); padding:9px 18px; font-size:12px; font-weight:600; color:#f5a623; }
  .aq-status-note { font-size:11px; font-weight:400; color:var(--text-secondary); margin-left:4px; }
  .aq-undo-btn { margin-left:auto; background:transparent; border:1px solid rgba(49,220,186,0.3); color:var(--accent); font-family:var(--font); font-size:11px; font-weight:600; padding:3px 10px; border-radius:6px; cursor:pointer; transition:all 0.15s; }
  .aq-undo-btn:hover { background:rgba(49,220,186,0.12); border-color:var(--accent); }

  /* Caption edit form */
  .aq-caption-edit-wrap { display:flex; flex-direction:column; gap:8px; }
  .aq-caption-textarea { width:100%; background:var(--bg-surface); border:1px solid var(--accent-border); border-radius:7px; padding:9px 12px; font-family:var(--font); font-size:13px; color:var(--text-primary); outline:none; resize:none; line-height:1.65; transition:border-color 0.15s; }
  .aq-caption-textarea:focus { border-color:var(--accent); }
  .aq-caption-edit-row { display:flex; align-items:center; gap:8px; }
  .aq-btn-caption-save { background:var(--accent); color:var(--bg-base); padding:6px 14px; border-radius:7px; font-family:var(--font); font-size:12px; font-weight:700; cursor:pointer; border:none; transition:all 0.15s; }
  .aq-btn-caption-save:hover { background:#28c4a8; }
  .aq-btn-caption-cancel { background:transparent; border:none; font-family:var(--font); font-size:12px; color:var(--text-muted); cursor:pointer; }
  .aq-btn-caption-cancel:hover { color:var(--text-secondary); }
  .aq-btn-caption-edit { background:transparent; border:none; font-family:var(--font); font-size:12px; color:var(--text-muted); cursor:pointer; padding:0; text-decoration:underline; text-underline-offset:2px; }
  .aq-btn-caption-edit:hover { color:var(--text-secondary); }

  /* Caption saved flash */
  .aq-saved-flash { font-size:11px; font-weight:600; color:var(--accent); opacity:1; transition:opacity 0.4s; }
  .aq-saved-flash.fade { opacity:0; }

  /* Edit request form */
  .aq-edit-form { border-top:1px solid var(--border); padding:14px 18px; background:rgba(245,166,35,0.04); display:flex; flex-direction:column; gap:9px; }
  .aq-edit-label { font-size:11px; font-weight:600; color:#f5a623; text-transform:uppercase; letter-spacing:0.06em; }
  .aq-edit-textarea { width:100%; background:var(--bg-surface); border:1px solid rgba(245,166,35,0.3); border-radius:7px; padding:9px 12px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; resize:none; min-height:64px; line-height:1.6; transition:border-color 0.15s; }
  .aq-edit-textarea:focus { border-color:rgba(245,166,35,0.6); }
  .aq-edit-textarea::placeholder { color:var(--text-muted); }
  .aq-edit-row { display:flex; align-items:center; gap:8px; }
  .aq-btn-submit-edit { background:#f5a623; color:#0d0f14; padding:7px 16px; border-radius:7px; font-family:var(--font); font-size:12px; font-weight:700; cursor:pointer; border:none; transition:all 0.15s; }
  .aq-btn-submit-edit:hover { background:#e09510; }
  .aq-btn-submit-edit:disabled { opacity:0.35; cursor:default; }
  .aq-btn-cancel-edit { background:transparent; border:none; font-family:var(--font); font-size:12px; color:var(--text-muted); cursor:pointer; }
  .aq-btn-cancel-edit:hover { color:var(--text-secondary); }

  /* UNIFIED QUEUE — tabs, chips, new card elements */
  .aq-tab-bar { display:flex; gap:2px; border-bottom:1px solid var(--border); margin-bottom:16px; }
  .aq-tab-btn { background:transparent; border:none; border-bottom:2px solid transparent; padding:9px 18px; font-family:var(--font); font-size:13px; font-weight:600; color:var(--text-muted); cursor:pointer; transition:color .15s, border-color .15s; margin-bottom:-1px; }
  .aq-tab-btn:hover { color:var(--text-secondary); }
  .aq-tab-btn.active { color:var(--accent); border-bottom-color:var(--accent); }
  .aq-banner { font-size:12px; color:var(--text-muted); padding:10px 14px; background:rgba(49,220,186,0.05); border:1px solid rgba(49,220,186,0.12); border-radius:8px; margin-bottom:14px; }
  .aq-loading { font-size:13px; color:var(--text-muted); padding:32px 0; text-align:center; }
  .aq-img-thumb { width:140px; min-width:140px; background:var(--bg-card2); display:flex; align-items:center; justify-content:center; overflow:hidden; flex-shrink:0; }
  .aq-thumb-img { width:100%; height:100%; object-fit:cover; display:block; min-height:160px; }
  .aq-thumb-placeholder { font-size:32px; }
  .aq-hashtags { font-size:11px; color:var(--text-muted); line-height:1.55; }
  .aq-platforms { display:flex; gap:5px; flex-wrap:wrap; }
  .aq-chip { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; padding:3px 9px; border-radius:20px; background:rgba(49,220,186,0.1); border:1px solid rgba(49,220,186,0.22); color:var(--accent); }
  .aq-chip.inactive { background:rgba(255,255,255,0.04); border-color:rgba(255,255,255,0.08); color:var(--text-muted); opacity:0.5; }
  .aq-story-badge { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.06em; padding:3px 9px; border-radius:20px; background:var(--accent); border:1px solid var(--accent); color:#0d0f14; }
  .aq-filter-bar { display:flex; gap:6px; margin-bottom:14px; }
  .aq-filter-btn { background:transparent; border:1px solid var(--border); border-radius:20px; padding:5px 14px; font-family:var(--font); font-size:12px; font-weight:600; color:var(--text-muted); cursor:pointer; transition:all .15s; }
  .aq-filter-btn:hover { color:var(--text-secondary); border-color:rgba(255,255,255,0.16); }
  .aq-filter-btn.active { color:#0d0f14; background:var(--accent); border-color:var(--accent); }
  .aq-btn-reject { background:transparent; color:#f87171; border:1px solid rgba(248,113,113,0.3); }
  .aq-btn-reject:hover { background:rgba(248,113,113,0.08); border-color:rgba(248,113,113,0.5); }
  .aq-btn-regen { background:transparent; color:var(--text-secondary); border:1px solid var(--border); }
  .aq-btn-regen:hover { border-color:rgba(49,220,186,0.4); color:var(--accent); }
  .aq-btn-regen:disabled { opacity:0.4; cursor:default; }
  .aq-edit-textarea { width:100%; background:rgba(0,0,0,0.3); border:1px solid rgba(255,255,255,0.1); border-radius:8px; font-size:13px; padding:10px; resize:vertical; margin-bottom:6px; color:var(--text-primary); font-family:var(--font); outline:none; line-height:1.5; }
  .aq-edit-hashtags { width:100%; background:rgba(0,0,0,0.3); border:1px solid rgba(255,255,255,0.1); border-radius:8px; font-size:12px; padding:10px; margin-bottom:6px; color:#31DCBA; font-family:var(--font); outline:none; }
  .aq-build-wrap { display:flex; flex-direction:column; gap:14px; }
  .aq-usage-row { font-size:12px; color:var(--text-muted); }
  .aq-usage-row strong { color:var(--text-secondary); font-weight:600; }

  /* PHOTO LIBRARY PANEL */
  .pl-wrap { display:flex; flex-direction:column; height:100%; }
  .pl-toolbar { display:flex; align-items:center; gap:12px; margin-bottom:16px; flex-shrink:0; }
  .pl-toolbar-title { font-family:var(--font-head); font-size:18px; font-weight:700; letter-spacing:0.02em; }
  .pl-toolbar-count { font-size:12px; color:var(--text-muted); padding:3px 9px; background:var(--bg-card); border:1px solid var(--border); border-radius:20px; }
  .pl-toolbar-right { margin-left:auto; display:flex; align-items:center; gap:8px; }
  .pl-upload-btn { display:flex; align-items:center; gap:7px; background:var(--accent); color:var(--bg-base); border:none; border-radius:7px; padding:8px 16px; font-family:var(--font); font-size:12px; font-weight:700; cursor:pointer; transition:background 0.15s; }
  .pl-upload-btn:hover { background:#28c4a8; }

  .pl-dropzone { border:2px dashed var(--border); border-radius:12px; padding:48px 20px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; cursor:pointer; transition:all 0.2s; background:var(--bg-card); flex:1; }
  .pl-dropzone:hover, .pl-dropzone.dragging { border-color:var(--accent); background:var(--accent-dim); }
  .pl-dropzone-icon { width:52px; height:52px; border-radius:14px; background:var(--accent-dim); border:1px solid var(--accent-border); display:flex; align-items:center; justify-content:center; margin-bottom:4px; }
  .pl-dropzone-title { font-family:var(--font-head); font-size:16px; font-weight:700; color:var(--text-primary); }
  .pl-dropzone-sub { font-size:12px; color:var(--text-muted); text-align:center; line-height:1.6; }
  .pl-dropzone-btn { background:var(--accent); color:var(--bg-base); border:none; border-radius:7px; padding:9px 22px; font-family:var(--font); font-size:12px; font-weight:700; cursor:pointer; margin-top:6px; transition:background 0.15s; }
  .pl-dropzone-btn:hover { background:#28c4a8; }

  .pl-drop-banner { display:flex; align-items:center; gap:10px; background:var(--bg-card); border:1px dashed var(--border); border-radius:9px; padding:10px 16px; margin-bottom:14px; cursor:pointer; transition:all 0.2s; flex-shrink:0; }
  .pl-drop-banner:hover, .pl-drop-banner.dragging { border-color:var(--accent); background:var(--accent-dim); }
  .pl-drop-banner-text { font-size:12px; color:var(--text-muted); }
  .pl-drop-banner-text strong { color:var(--text-secondary); font-weight:600; }
  .pl-drop-banner-hint { font-size:11px; color:var(--text-muted); margin-left:auto; }

  /* grid-auto-rows:max-content — each row is as tall as its card. Without it
     the rows were squeezed to fit .pl-wrap's height (the cards' overflow:hidden
     makes their minimum height 0), so on a phone every card was cut short and
     the Reuse / Use in Post / Delete buttons were hidden below its edge — worst
     in the installed app, where the home-bar inset leaves even less height.
     Rows now keep their full height and the grid scrolls instead. */
  .pl-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(155px, 1fr)); grid-auto-rows:max-content; gap:12px; overflow-y:auto; padding-bottom:8px; align-content:start; }
  .pl-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; overflow:hidden; position:relative; transition:border-color 0.15s, transform 0.15s; }
  .pl-card:hover { border-color:rgba(255,255,255,0.16); transform:translateY(-2px); }
  .pl-card-img-wrap { width:100%; aspect-ratio:4/5; overflow:hidden; position:relative; }
  .pl-card-img { width:100%; height:100%; object-fit:cover; display:block; }
  .pl-card-placeholder { width:100%; height:100%; display:flex; align-items:center; justify-content:center; font-size:32px; }
  .pl-card-info { padding:8px 10px 9px; border-top:1px solid var(--border); }
  .pl-card-filename { font-size:11px; font-weight:600; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:2px; }
  .pl-card-date { font-size:10px; color:var(--text-muted); }

  .pl-card-overlay { position:absolute; inset:0; background:rgba(13,15,20,0.78); display:flex; flex-direction:column; align-items:center; justify-content:flex-end; gap:7px; opacity:0; transition:opacity 0.18s; padding:12px; }
  .pl-card:hover .pl-card-overlay { opacity:1; }
  .pl-overlay-btn { width:100%; padding:8px 10px; border-radius:7px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; border:none; transition:all 0.15s; text-align:center; }
  .pl-overlay-use { background:var(--accent); color:var(--bg-base); }
  .pl-overlay-use:hover { background:#28c4a8; }
  .pl-overlay-delete { background:rgba(239,68,68,0.14); color:#f87171; border:1px solid rgba(239,68,68,0.28); }
  .pl-overlay-delete:hover { background:rgba(239,68,68,0.26); border-color:rgba(239,68,68,0.5); }
  .pl-overlay-reuse { background:rgba(49,220,186,0.14); color:var(--accent); border:1px solid rgba(49,220,186,0.36); }
  .pl-overlay-reuse:hover { background:rgba(49,220,186,0.28); border-color:rgba(49,220,186,0.6); }
  /* Touch devices have no hover: keep the action buttons always visible with a
     bottom gradient (instead of the full scrim) so the photo stays visible. */
  @media (hover: none) {
    .pl-card-overlay { opacity:1; background:linear-gradient(to bottom, rgba(13,15,20,0) 0%, rgba(13,15,20,0) 32%, rgba(13,15,20,0.55) 62%, rgba(13,15,20,0.92) 100%); }
  }
  .pl-card.pl-card-used { cursor:default; }
  .pl-card.pl-card-used:active { cursor:default; }

  .pl-toast { position:fixed; bottom:30px; left:50%; transform:translateX(-50%) translateY(10px); background:var(--bg-card2); border:1px solid var(--accent-border); color:var(--accent); font-size:12px; font-weight:600; padding:10px 20px; border-radius:9px; display:flex; align-items:center; gap:8px; z-index:9999; transition:opacity 0.25s, transform 0.25s; box-shadow:0 6px 28px rgba(0,0,0,0.5); pointer-events:none; opacity:0; }
  .pl-toast.visible { opacity:1; transform:translateX(-50%) translateY(0); }
  @keyframes spin { to { transform: rotate(360deg); } }

  .pl-info-banner { display:flex; align-items:center; gap:10px; background:rgba(49,220,186,0.06); border:1px solid rgba(49,220,186,0.18); border-radius:9px; padding:10px 14px; margin-bottom:14px; flex-shrink:0; }
  .pl-info-banner-text { font-size:11px; color:var(--text-secondary); line-height:1.5; flex:1; }
  .pl-info-banner-text strong { color:var(--accent); font-weight:700; }
  .pl-info-dismiss { background:none; border:none; color:var(--text-muted); font-size:15px; cursor:pointer; line-height:1; padding:0 2px; flex-shrink:0; transition:color 0.15s; }
  .pl-info-dismiss:hover { color:var(--text-primary); }

  .pl-card.dragging-card { opacity:0.4; border:1px dashed var(--accent); transform:scale(0.97); }
  .pl-card.drag-over { border-color:var(--accent); box-shadow:0 0 0 2px var(--accent-border); transform:translateY(-3px); }
  .pl-card { cursor:grab; }
  .pl-card:active { cursor:grabbing; }

  /* IMAGE GENERATION PANEL */
  .ig-wrap { display:flex; gap:20px; align-items:flex-start; }
  .ig-left { width:300px; flex-shrink:0; display:flex; flex-direction:column; gap:14px; }
  .ig-right { flex:1; min-width:0; display:flex; flex-direction:column; gap:14px; }

  /* Carousel tab: escape the 300px ig-left and span full panel width */
  .ig-wrap.is-carousel { display:block; }
  .ig-wrap.is-carousel .ig-left { width:100%; }
  .ig-wrap.is-carousel .ig-right { display:none; }

  /* Carousel — two-column desktop layout (stacks on mobile via media query above) */
  .carousel-two-col { display:flex; gap:24px; align-items:flex-start; }
  .carousel-left { flex:0 0 420px; position:sticky; top:1rem; align-self:flex-start; }
  .carousel-right { flex:1; min-width:0; display:flex; flex-direction:column; }

  .ig-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:18px; }
  .ig-card-title { font-family:var(--font-head); font-size:14px; font-weight:700; letter-spacing:0.03em; margin-bottom:12px; }

  .ig-label { font-size:10px; font-weight:700; color:var(--text-secondary); text-transform:uppercase; letter-spacing:0.07em; margin-bottom:6px; }
  .ig-textarea { width:100%; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; padding:10px 12px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; resize:none; min-height:90px; line-height:1.6; transition:border-color 0.15s, box-shadow 0.2s; }
  .ig-textarea:focus { border-color:var(--accent-border); }
  .ig-textarea::placeholder { color:var(--text-muted); }
  .ig-textarea-pulse { border-color:rgba(49,220,186,0.5); animation: ig-prompt-pulse 1.9s ease-in-out infinite; }
  .ig-textarea-pulse:focus { animation: none; border-color:var(--accent); box-shadow:0 0 0 3px rgba(49,220,186,0.18); }
  @keyframes ig-prompt-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(49,220,186,0.0), 0 0 8px 0 rgba(49,220,186,0.18); border-color:rgba(49,220,186,0.35); }
    50%      { box-shadow: 0 0 0 4px rgba(49,220,186,0.18), 0 0 18px 2px rgba(49,220,186,0.45); border-color:rgba(49,220,186,0.85); }
  }

  .ig-templates { display:flex; flex-direction:column; gap:6px; }
  .ig-template { padding:9px 12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; font-size:11px; color:var(--text-secondary); cursor:pointer; transition:all 0.15s; line-height:1.5; }
  .ig-template:hover { border-color:var(--accent-border); color:var(--text-primary); background:var(--accent-dim); }
  .ig-template.selected { border-color:var(--accent-border); background:var(--accent-dim); color:var(--accent); }
  .ig-template-label { font-size:10px; font-weight:700; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px; }
  .ig-template.selected .ig-template-label { color:var(--accent); }

  .ig-style-grid { display:grid; grid-template-columns:1fr 1fr; gap:7px; }
  .ig-style-opt { padding:9px 10px; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; cursor:pointer; transition:all 0.15s; text-align:center; }
  .ig-style-opt:hover { border-color:rgba(255,255,255,0.18); }
  .ig-style-opt.selected { border-color:var(--accent-border); background:var(--accent-dim); }
  .ig-style-name { font-size:11px; font-weight:600; color:var(--text-secondary); }
  .ig-style-opt.selected .ig-style-name { color:var(--accent); }
  .ig-style-icon { font-size:18px; margin-bottom:4px; }

  .ig-gen-btn { width:100%; padding:11px; background:var(--accent); color:var(--bg-base); border:none; border-radius:8px; font-family:var(--font); font-size:13px; font-weight:700; cursor:pointer; transition:all 0.15s; display:flex; align-items:center; justify-content:center; gap:8px; }
  .ig-gen-btn:hover { background:#28c4a8; }
  .ig-gen-btn:disabled { opacity:0.45; cursor:not-allowed; }
  .ig-gen-btn.generating { background:rgba(49,220,186,0.2); color:var(--accent); border:1px solid var(--accent-border); }

  /* Pending section */
  .ig-section-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
  .ig-section-title { font-family:var(--font-head); font-size:15px; font-weight:700; letter-spacing:0.02em; }
  .ig-section-badge { background:rgba(245,166,35,0.18); color:#f5a623; font-size:10px; font-weight:700; padding:2px 8px; border-radius:10px; border:1px solid rgba(245,166,35,0.3); }
  .ig-section-badge.approved { background:var(--accent-dim); color:var(--accent); border-color:var(--accent-border); }

  .ig-pending-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(240px, 1fr)); gap:14px; }
  .ig-pcard { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; transition:border-color 0.15s; }
  .ig-pcard.pending-card { border-color:rgba(245,166,35,0.3); }
  .ig-pcard.approved-card { border-color:var(--accent-border); }

  .ig-pcard-img { width:100%; aspect-ratio:4/5; background:linear-gradient(135deg,var(--bg-card2) 0%,#1e2535 100%); display:flex; align-items:center; justify-content:center; font-size:48px; position:relative; overflow:hidden; }
  .ig-pcard-img img { width:100%; height:100%; object-fit:cover; display:block; }
  .ig-pcard-status-badge { position:absolute; top:10px; left:10px; font-size:10px; font-weight:700; padding:3px 9px; border-radius:8px; }
  .ig-pcard-status-badge.pending { background:rgba(245,166,35,0.9); color:#0d0f14; }
  .ig-pcard-status-badge.approved { background:rgba(49,220,186,0.9); color:#0d0f14; }

  .ig-pcard-body { padding:12px 14px; }
  .ig-pcard-prompt { font-size:11px; color:var(--text-secondary); line-height:1.5; margin-bottom:4px; overflow:hidden; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; }
  .ig-pcard-meta { font-size:10px; color:var(--text-muted); margin-bottom:10px; }

  /* Admin approve/reject row */
  .ig-action-row { display:flex; gap:7px; }
  .ig-btn-approve { flex:1; padding:8px; background:var(--accent); color:var(--bg-base); border:none; border-radius:7px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; transition:all 0.15s; }
  .ig-btn-approve:hover { background:#28c4a8; }
  .ig-btn-reject { flex:1; padding:8px; background:rgba(239,68,68,0.12); color:#f87171; border:1px solid rgba(239,68,68,0.25); border-radius:7px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; transition:all 0.15s; }
  .ig-btn-reject:hover { background:rgba(239,68,68,0.22); border-color:rgba(239,68,68,0.45); }

  /* Client waiting badge */
  .ig-awaiting { display:flex; align-items:center; gap:6px; padding:8px 12px; background:rgba(245,166,35,0.08); border:1px solid rgba(245,166,35,0.2); border-radius:7px; font-size:11px; color:#f5a623; font-weight:600; }

  /* Admin toggle */
  .ig-admin-toggle { display:flex; align-items:center; gap:10px; padding:9px 14px; background:var(--bg-card); border:1px solid var(--border); border-radius:9px; margin-bottom:4px; }
  .ig-admin-toggle-label { font-size:11px; color:var(--text-secondary); flex:1; }
  /* Photo Library: arrived from Getting started "Turn on auto-post" with the switch off. */
  .ig-admin-toggle.pl-autopost-glow { border-color:rgba(49,220,186,0.7); background:rgba(49,220,186,0.08); animation: pl-autopost-pulse 1.6s ease-in-out infinite; }
  @keyframes pl-autopost-pulse {
    0%, 100% { box-shadow: 0 0 0 2px rgba(49,220,186,0.35), 0 0 10px 0 rgba(49,220,186,0.25); }
    50%      { box-shadow: 0 0 0 5px rgba(49,220,186,0.22), 0 0 22px 4px rgba(49,220,186,0.55); }
  }
  .pl-autopost-hint { font-size:11px; font-weight:700; color:var(--accent); white-space:nowrap; }
  .pl-autopost-note { font-size:11px; color:var(--text-muted); line-height:1.5; margin:-8px 0 14px; text-align:right; }
  @media (max-width: 768px) { .pl-autopost-note { text-align:left; } }
  @media (prefers-reduced-motion: reduce) {
    .ig-admin-toggle.pl-autopost-glow { animation:none; box-shadow: 0 0 0 3px rgba(49,220,186,0.35), 0 0 16px 2px rgba(49,220,186,0.45); }
  }
  .ig-toggle-track { width:34px; height:18px; border-radius:9px; background:var(--bg-surface); border:1px solid var(--border); position:relative; cursor:pointer; transition:background 0.2s; flex-shrink:0; }
  .ig-toggle-track.on { background:rgba(49,220,186,0.25); border-color:var(--accent-border); }
  .ig-toggle-thumb { width:12px; height:12px; border-radius:50%; background:var(--text-muted); position:absolute; top:2px; left:2px; transition:all 0.2s; }
  .ig-toggle-track.on .ig-toggle-thumb { left:18px; background:var(--accent); }
  .ig-view-label { font-size:10px; font-weight:700; padding:2px 8px; border-radius:8px; }
  .ig-view-label.admin { background:rgba(49,220,186,0.15); color:var(--accent); }
  .ig-view-label.client { background:rgba(255,255,255,0.07); color:var(--text-muted); }

  /* History */
  .ig-history-list { display:flex; flex-direction:column; gap:8px; }
  .ig-history-row { display:flex; align-items:center; gap:10px; padding:9px 12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:8px; }
  .ig-history-thumb { width:36px; height:36px; border-radius:6px; background:linear-gradient(135deg,var(--bg-card2),#1e2535); display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; overflow:hidden; }
  .ig-history-thumb img { width:100%; height:100%; object-fit:cover; }
  .ig-history-info { flex:1; min-width:0; }
  .ig-history-prompt { font-size:11px; color:var(--text-primary); font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .ig-history-meta { font-size:10px; color:var(--text-muted); margin-top:2px; }
  .ig-history-pill { font-size:9px; font-weight:700; padding:2px 7px; border-radius:8px; flex-shrink:0; }
  .ig-history-pill.approved { background:var(--accent-dim); color:var(--accent); }
  .ig-history-pill.rejected { background:rgba(239,68,68,0.1); color:#f87171; }
  .ig-history-pill.pending { background:rgba(245,166,35,0.15); color:#f5a623; }

  /* Generating spinner */
  @keyframes ig-spin { to { transform:rotate(360deg); } }
  .ig-spinner { width:16px; height:16px; border:2px solid rgba(49,220,186,0.3); border-top-color:var(--accent); border-radius:50%; animation:ig-spin 0.7s linear infinite; flex-shrink:0; }

  /* Batch selector */
  .ig-batch-opts { display:flex; gap:6px; }
  .ig-batch-opt { flex:1; padding:7px 4px; text-align:center; background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; font-family:var(--font); font-size:11px; font-weight:700; color:var(--text-muted); cursor:pointer; transition:all 0.15s; }
  .ig-batch-opt:hover { color:var(--text-secondary); border-color:rgba(255,255,255,0.18); }
  .ig-batch-opt.selected { background:var(--accent-dim); border-color:var(--accent-border); color:var(--accent); }
  .ig-batch-note { font-size:10px; color:var(--text-muted); margin-top:5px; line-height:1.5; }
  @keyframes ig-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  .ig-gen-placeholder { width:100%; aspect-ratio:4/5; background:var(--bg-surface); border-radius:10px; border:1px dashed var(--accent-border); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; }
  .ig-gen-pulse { width:40px; height:40px; border-radius:50%; background:var(--accent-dim); border:2px solid var(--accent-border); display:flex; align-items:center; justify-content:center; animation:ig-pulse 1.5s ease-in-out infinite; }

  /* ── POST PERFORMANCE PANEL ── */
  .pp-wrap { display:flex; flex-direction:column; gap:20px; }
  .pp-header { display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
  .pp-title { font-family:var(--font-head); font-size:18px; font-weight:700; letter-spacing:0.02em; }
  .pp-range-tabs { display:flex; gap:4px; background:var(--bg-card); border:1px solid var(--border); border-radius:8px; padding:3px; }
  .pp-range-tab { padding:5px 12px; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; border:none; font-family:var(--font); background:transparent; color:var(--text-muted); transition:all 0.15s; }
  .pp-range-tab.active { background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); }
  .pp-range-tab:hover:not(.active) { color:var(--text-secondary); }

  .pp-stat-row { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
  .pp-stat-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; padding:14px 16px; }
  .pp-stat-label { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.08em; color:var(--text-secondary); margin-bottom:6px; }
  .pp-stat-value { font-family:var(--font-head); font-size:26px; font-weight:700; line-height:1; color:var(--text-primary); }
  .pp-stat-trend { font-size:11px; font-weight:600; margin-left:5px; font-family:var(--font); }
  .pp-stat-trend.up { color:var(--accent); }
  .pp-stat-trend.down { color:#f87171; }
  .pp-stat-sub { font-size:10px; color:var(--text-muted); margin-top:4px; }

  .pp-body { display:grid; grid-template-columns:1fr 340px; gap:16px; align-items:start; }

  .pp-chart-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; padding:16px 18px; }
  .pp-chart-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
  .pp-chart-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.03em; }
  .pp-chart-legend { display:flex; gap:12px; }
  .pp-legend-item { display:flex; align-items:center; gap:5px; font-size:10px; color:var(--text-secondary); }
  .pp-legend-dot { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
  .pp-chart-area { position:relative; }
  .pp-y-labels { position:absolute; left:0; top:0; bottom:24px; display:flex; flex-direction:column; justify-content:space-between; pointer-events:none; }
  .pp-y-label { font-size:9px; color:var(--text-muted); text-align:right; width:28px; }
  .pp-chart-svg-wrap { margin-left:34px; }
  .pp-x-labels { display:flex; justify-content:space-between; margin-left:34px; margin-top:4px; }
  .pp-x-label { font-size:9px; color:var(--text-muted); text-align:center; flex:1; }

  .pp-right-col { display:flex; flex-direction:column; gap:14px; }

  .pp-type-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; padding:14px 16px; }
  .pp-type-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.03em; margin-bottom:12px; }
  .pp-type-row { display:flex; align-items:center; gap:10px; margin-bottom:9px; }
  .pp-type-row:last-child { margin-bottom:0; }
  .pp-type-label { font-size:11px; font-weight:600; color:var(--text-secondary); width:72px; flex-shrink:0; }
  .pp-type-bar-track { flex:1; height:8px; background:var(--bg-surface); border-radius:4px; overflow:hidden; }
  .pp-type-bar-fill { height:100%; border-radius:4px; transition:width 0.4s ease; }
  .pp-type-pct { font-size:11px; font-weight:700; color:var(--text-primary); width:32px; text-align:right; flex-shrink:0; }

  .pp-top-posts-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; padding:14px 16px; }
  .pp-tp-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
  .pp-tp-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.03em; }
  .pp-tp-count { font-size:10px; color:var(--text-muted); background:var(--bg-surface); border:1px solid var(--border); border-radius:20px; padding:2px 9px; }

  .pp-table { width:100%; border-collapse:collapse; }
  .pp-table th { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.07em; color:var(--text-muted); text-align:left; padding:0 8px 8px 0; border-bottom:1px solid var(--border); white-space:nowrap; }
  .pp-table th:last-child { padding-right:0; }
  .pp-table td { padding:10px 8px 10px 0; border-bottom:1px solid rgba(255,255,255,0.04); font-size:11px; vertical-align:middle; }
  .pp-table td:last-child { padding-right:0; }
  .pp-table tr:last-child td { border-bottom:none; }
  .pp-post-thumb { width:32px; height:32px; border-radius:6px; background:var(--bg-card2); display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .pp-post-caption { font-size:11px; color:var(--text-primary); font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:160px; }
  .pp-post-date { font-size:10px; color:var(--text-muted); }
  .pp-metric { font-size:12px; font-weight:600; color:var(--text-primary); }
  .pp-pill-type { font-size:9px; font-weight:600; padding:2px 7px; border-radius:8px; white-space:nowrap; }
  .pp-pill-single   { background:rgba(99,153,250,0.14); color:#6399fa; }
  .pp-pill-carousel { background:rgba(165,119,250,0.14); color:#a577fa; }
  .pp-pill-story    { background:rgba(245,166,35,0.14); color:#f5a623; }

  .pp-lock-overlay { position:relative; }
  .pp-lock-blur { filter:blur(5px); pointer-events:none; user-select:none; opacity:0.4; }
  .pp-lock-badge { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; }
  .pp-lock-icon { width:44px; height:44px; border-radius:12px; background:var(--bg-card2); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; font-size:20px; }
  .pp-lock-text { font-size:12px; font-weight:600; color:var(--text-secondary); text-align:center; line-height:1.5; }
  .pp-lock-btn { background:var(--accent); color:var(--bg-base); border:none; border-radius:7px; padding:8px 18px; font-family:var(--font); font-size:12px; font-weight:700; cursor:pointer; transition:background 0.15s; }
  .pp-lock-btn:hover { background:#28c4a8; }

  /* ── ACCOUNT & PERKS PANEL ── */
  .acct-wrap { display:flex; flex-direction:column; gap:20px; max-width:860px; }

  .acct-header { display:flex; align-items:center; gap:16px; background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:20px 22px; }
  .acct-avatar-lg { width:54px; height:54px; border-radius:14px; background:var(--accent-dim); border:1px solid var(--accent-border); display:flex; align-items:center; justify-content:center; font-family:var(--font-head); font-size:20px; font-weight:700; color:var(--accent); flex-shrink:0; }
  .acct-header-info { flex:1; min-width:0; }
  .acct-biz-name { font-family:var(--font-head); font-size:20px; font-weight:700; letter-spacing:0.02em; line-height:1.2; }
  .acct-biz-sub { font-size:12px; color:var(--text-secondary); margin-top:3px; }
  .acct-plan-badge { display:inline-flex; align-items:center; gap:6px; padding:5px 13px; border-radius:20px; font-size:12px; font-weight:700; font-family:var(--font-head); letter-spacing:0.04em; flex-shrink:0; }
  .acct-plan-badge.managed { background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); }
  .acct-plan-badge.self-serve { background:rgba(99,153,250,0.12); color:#6399fa; border:1px solid rgba(99,153,250,0.3); }
  .acct-plan-dot { width:7px; height:7px; border-radius:50%; background:currentColor; flex-shrink:0; }

  .acct-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

  .acct-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:18px 20px; }
  .acct-card-title { font-family:var(--font-head); font-size:14px; font-weight:700; letter-spacing:0.03em; margin-bottom:14px; display:flex; align-items:center; justify-content:space-between; }
  .acct-card-action { font-size:11px; color:var(--accent); cursor:pointer; font-family:var(--font); font-weight:600; }
  .acct-card-action:hover { opacity:0.8; }

  /* Plan details */
  .acct-plan-price { display:flex; align-items:baseline; gap:4px; margin-bottom:4px; }
  .acct-price-num { font-family:var(--font-head); font-size:32px; font-weight:700; color:var(--text-primary); line-height:1; }
  .acct-price-period { font-size:12px; color:var(--text-muted); }
  .acct-price-setup { font-size:11px; color:var(--text-muted); margin-bottom:14px; }
  .acct-feature-list { display:flex; flex-direction:column; gap:7px; margin-bottom:16px; }
  .acct-feature { display:flex; align-items:center; gap:8px; font-size:12px; color:var(--text-secondary); }
  .acct-feature-check { width:16px; height:16px; border-radius:50%; background:var(--accent-dim); border:1px solid var(--accent-border); display:flex; align-items:center; justify-content:center; font-size:9px; color:var(--accent); flex-shrink:0; }
  .acct-feature-x { width:16px; height:16px; border-radius:50%; background:rgba(255,255,255,0.05); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; font-size:9px; color:var(--text-muted); flex-shrink:0; }

  .acct-upgrade-box { background:rgba(49,220,186,0.07); border:1px solid var(--accent-border); border-radius:9px; padding:12px 14px; display:flex; align-items:center; gap:12px; }
  .acct-upgrade-text { flex:1; min-width:0; font-size:12px; color:var(--text-secondary); line-height:1.5; }
  .acct-upgrade-text strong { color:var(--accent); font-weight:700; }
  .acct-upgrade-btn { background:var(--accent); color:var(--bg-base); border:none; border-radius:7px; padding:8px 14px; font-family:var(--font); font-size:12px; font-weight:700; cursor:pointer; white-space:nowrap; transition:background 0.15s; flex-shrink:0; }
  .acct-upgrade-btn:hover { background:#28c4a8; }

  /* Billing */
  .acct-billing-row { display:flex; align-items:center; gap:10px; padding:10px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
  .acct-billing-row:last-child { border-bottom:none; padding-bottom:0; }
  .acct-billing-label { font-size:11px; color:var(--text-muted); width:110px; flex-shrink:0; }
  .acct-billing-value { font-size:12px; color:var(--text-primary); font-weight:600; flex:1; }
  .acct-billing-tag { font-size:10px; font-weight:700; padding:2px 8px; border-radius:8px; }
  .acct-billing-tag.active { background:var(--accent-dim); color:var(--accent); }
  .acct-billing-tag.pending { background:rgba(245,166,35,0.15); color:#f5a623; }
  .acct-manage-link { font-size:11px; color:var(--accent); cursor:pointer; font-weight:600; }
  .acct-manage-link:hover { opacity:0.8; }

  /* Team */
  .acct-team-member { display:flex; align-items:center; gap:10px; padding:9px 0; border-bottom:1px solid rgba(255,255,255,0.05); }
  .acct-team-member:last-child { border-bottom:none; padding-bottom:0; }
  .acct-team-avatar { width:32px; height:32px; border-radius:50%; background:var(--bg-surface); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; color:var(--text-secondary); flex-shrink:0; }
  .acct-team-avatar.you { background:var(--accent-dim); border-color:var(--accent-border); color:var(--accent); }
  .acct-team-name { font-size:12px; font-weight:600; color:var(--text-primary); }
  .acct-team-email { font-size:10px; color:var(--text-muted); margin-top:1px; }
  .acct-team-role { font-size:10px; font-weight:600; padding:2px 7px; border-radius:8px; background:rgba(255,255,255,0.06); color:var(--text-secondary); margin-left:auto; flex-shrink:0; }
  .acct-team-status { font-size:10px; font-weight:700; padding:2px 7px; border-radius:8px; flex-shrink:0; }
  .acct-team-status.active { background:var(--accent-dim); color:var(--accent); }
  .acct-team-status.pending { background:rgba(245,166,35,0.15); color:#f5a623; }
  .acct-add-member { display:flex; align-items:center; gap:7px; padding:9px 12px; background:rgba(255,255,255,0.03); border:1px dashed var(--border); border-radius:8px; cursor:pointer; font-size:12px; color:var(--text-muted); margin-top:10px; transition:all 0.15s; }
  .acct-add-member:hover { border-color:var(--accent-border); color:var(--accent); background:var(--accent-dim); }
  .acct-add-icon { width:22px; height:22px; border-radius:50%; border:1px solid currentColor; display:flex; align-items:center; justify-content:center; font-size:14px; line-height:1; flex-shrink:0; }

  /* Perks */
  .acct-perks-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .acct-perk { background:var(--bg-surface); border:1px solid var(--border); border-radius:10px; padding:13px 15px; display:flex; flex-direction:column; gap:6px; transition:border-color 0.15s; }
  .acct-perk.active-perk { border-color:var(--accent-border); background:rgba(49,220,186,0.05); }
  .acct-perk.soon-perk { opacity:0.6; }
  .acct-perk-top { display:flex; align-items:center; justify-content:space-between; }
  .acct-perk-icon { font-size:18px; }
  .acct-perk-pill { font-size:9px; font-weight:700; padding:2px 7px; border-radius:8px; }
  .acct-perk-pill.included { background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); }
  .acct-perk-pill.add-on { background:rgba(255,255,255,0.07); color:var(--text-secondary); border:1px solid var(--border); }
  .acct-perk-pill.soon { background:rgba(99,153,250,0.12); color:#6399fa; border:1px solid rgba(99,153,250,0.25); }
  .acct-perk-name { font-size:12px; font-weight:700; color:var(--text-primary); }
  .acct-perk-desc { font-size:11px; color:var(--text-muted); line-height:1.5; }
  .acct-perk-action { margin-top:4px; }
  .acct-perk-btn { background:var(--accent); color:var(--bg-base); border:none; border-radius:6px; padding:6px 12px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; transition:background 0.15s; }
  .acct-perk-btn:hover { background:#28c4a8; }

  /* ── TOOLTIP ── */
  .ap-tip-wrap { position:relative; display:inline-flex; align-items:center; }
  .ap-tip-icon { display:inline-flex; align-items:center; justify-content:center; width:16px; height:16px; border-radius:50%; background:rgba(49,220,186,0.15); border:1px solid rgba(49,220,186,0.4); color:var(--accent); font-size:10px; font-weight:700; cursor:default; flex-shrink:0; line-height:1; font-family:var(--font); transition:background 0.15s,border-color 0.15s; user-select:none; }
  .ap-tip-wrap:hover .ap-tip-icon { background:rgba(49,220,186,0.28); border-color:var(--accent); }
  .ap-tip-bubble { position:absolute; bottom:calc(100% + 7px); left:50%; transform:translateX(-50%); background:#1f2330; border:1px solid rgba(49,220,186,0.25); border-radius:8px; padding:8px 11px; font-size:11px; color:var(--text-secondary); line-height:1.55; white-space:normal; width:220px; pointer-events:none; opacity:0; z-index:999; box-shadow:0 6px 22px rgba(0,0,0,0.45); transition:opacity 0.15s; font-family:var(--font); font-weight:400; }
  .ap-tip-bubble::after { content:''; position:absolute; top:100%; left:50%; transform:translateX(-50%); border:5px solid transparent; border-top-color:#1f2330; }
  .ap-tip-bubble.tip-below { bottom:auto; top:calc(100% + 7px); }
  .ap-tip-bubble.tip-below::after { top:auto; bottom:100%; border-top-color:transparent; border-bottom-color:#1f2330; }
  .ap-tip-wrap:hover .ap-tip-bubble,
  .ap-tip-wrap.tip-open .ap-tip-bubble,
  .ap-tip-wrap:focus-visible .ap-tip-bubble { opacity:1; }
  .ap-tip-wrap { outline:none; }
  .ap-tip-wrap:focus-visible .ap-tip-icon { border-color:var(--accent); box-shadow:0 0 0 2px rgba(49,220,186,0.25); }
  .ap-tip-wrap.tip-open .ap-tip-icon { background:rgba(49,220,186,0.28); border-color:var(--accent); }

  /* ── CONNECTIONS PANEL ── */
  .cx-wrap { max-width:700px; display:flex; flex-direction:column; gap:24px; padding-bottom:32px; }
  .cx-page-header { display:flex; flex-direction:column; gap:4px; }
  .cx-page-title { font-family:var(--font-head); font-size:20px; font-weight:700; letter-spacing:0.02em; }
  .cx-page-sub { font-size:12px; color:var(--text-secondary); line-height:1.6; }

  .cx-platform-card { background:var(--bg-card); border:1px solid var(--border); border-radius:14px; overflow:hidden; transition:border-color 0.2s; }
  .cx-platform-card.connected { border-color:var(--accent-border); }

  .cx-platform-header { display:flex; align-items:center; gap:14px; padding:18px 20px; }
  .cx-platform-icon { width:46px; height:46px; border-radius:12px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .cx-platform-icon.ig { background:linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888); }
  .cx-platform-icon.fb { background:#1877f2; }
  .cx-platform-icon svg { width:24px; height:24px; }
  .cx-platform-info { flex:1; min-width:0; }
  .cx-platform-name { font-family:var(--font-head); font-size:15px; font-weight:700; letter-spacing:0.02em; margin-bottom:2px; }
  .cx-platform-desc { font-size:11px; color:var(--text-muted); line-height:1.5; }
  .cx-status-badge { display:flex; align-items:center; gap:6px; font-size:11px; font-weight:700; padding:5px 12px; border-radius:20px; flex-shrink:0; }
  .cx-status-badge.connected { background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); }
  .cx-status-badge.disconnected { background:rgba(255,255,255,0.05); color:var(--text-muted); border:1px solid var(--border); }
  .cx-status-dot { width:6px; height:6px; border-radius:50%; background:currentColor; flex-shrink:0; }

  .cx-platform-body { border-top:1px solid var(--border); padding:16px 20px; display:flex; flex-direction:column; gap:12px; }
  .cx-connected-info { display:flex; align-items:center; gap:12px; padding:12px 14px; background:var(--accent-dim); border:1px solid var(--accent-border); border-radius:10px; }
  .cx-connected-avatar { width:36px; height:36px; border-radius:50%; background:rgba(49,220,186,0.25); border:2px solid var(--accent); display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:700; color:var(--accent); flex-shrink:0; font-family:var(--font-head); }
  .cx-connected-detail { flex:1; min-width:0; }
  .cx-connected-name { font-size:13px; font-weight:700; color:var(--text-primary); }
  .cx-connected-handle { font-size:11px; color:var(--accent); font-weight:600; }
  .cx-connected-meta { font-size:10px; color:var(--text-secondary); margin-top:2px; }

  .cx-permissions { display:grid; grid-template-columns:1fr 1fr; gap:7px; }
  .cx-permission { display:flex; align-items:center; gap:7px; font-size:11px; color:var(--text-secondary); }
  .cx-perm-check { width:16px; height:16px; border-radius:50%; background:var(--accent-dim); border:1px solid var(--accent-border); display:flex; align-items:center; justify-content:center; font-size:8px; color:var(--accent); flex-shrink:0; }
  .cx-perm-check.off { background:rgba(255,255,255,0.04); border-color:var(--border); color:var(--text-muted); }

  .cx-connect-btn { display:flex; align-items:center; justify-content:center; gap:8px; width:100%; padding:11px; border-radius:9px; font-family:var(--font); font-size:13px; font-weight:700; cursor:pointer; border:none; transition:all 0.15s; }
  .cx-connect-btn.ig-btn { background:linear-gradient(135deg,#e6683c,#dc2743,#cc2366); color:#fff; }
  .cx-connect-btn.ig-btn:hover { opacity:0.9; }
  .cx-connect-btn.fb-btn { background:#1877f2; color:#fff; }
  .cx-connect-btn.fb-btn:hover { background:#1465d8; }
  .cx-disconnect-btn { background:transparent; border:1px solid rgba(239,68,68,0.3); color:#f87171; border-radius:7px; padding:7px 16px; font-family:var(--font); font-size:11px; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .cx-disconnect-btn:hover { background:rgba(239,68,68,0.1); border-color:rgba(239,68,68,0.55); }

  .cx-connect-actions { display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
  .cx-connect-note { font-size:10px; color:var(--text-muted); line-height:1.5; flex:1; }

  .cx-coming-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
  .cx-coming-card { background:var(--bg-surface); border:1px solid var(--border); border-radius:10px; padding:14px; display:flex; align-items:center; gap:10px; opacity:0.55; }
  .cx-coming-icon { width:32px; height:32px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .cx-coming-name { font-size:12px; font-weight:600; color:var(--text-secondary); }
  .cx-coming-soon-tag { font-size:9px; font-weight:700; padding:2px 7px; border-radius:8px; background:rgba(99,153,250,0.12); color:#6399fa; margin-left:auto; flex-shrink:0; }
  .cx-section-label { font-family:var(--font-head); font-size:12px; font-weight:700; letter-spacing:0.06em; color:var(--text-muted); text-transform:uppercase; margin-bottom:10px; }

  /* ── ADS PORTAL PANEL ── */
  .ads-wrap { display:flex; flex-direction:column; gap:20px; padding-bottom:32px; }
  .ads-tabs { display:flex; gap:3px; background:var(--bg-surface); border:1px solid var(--border); border-radius:10px; padding:4px; align-self:flex-start; }
  .ads-tab { padding:7px 18px; border-radius:7px; font-size:12px; font-weight:600; cursor:pointer; border:none; font-family:var(--font); background:transparent; color:var(--text-muted); transition:all 0.15s; white-space:nowrap; }
  .ads-tab.active { background:var(--bg-card); color:var(--accent); box-shadow:0 1px 6px rgba(0,0,0,0.3); }
  .ads-tab:hover:not(.active) { color:var(--text-secondary); }

  /* Ads Overview */
  .ads-stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; }
  .ads-stat { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; padding:13px 15px; }
  .ads-stat-label { font-size:10px; color:var(--text-muted); font-weight:600; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:5px; }
  .ads-stat-val { font-family:var(--font-head); font-size:22px; font-weight:700; color:var(--text-primary); line-height:1; }
  .ads-stat-trend { font-size:11px; color:var(--accent); font-weight:600; margin-left:4px; }
  .ads-stat-sub { font-size:10px; color:var(--text-muted); margin-top:3px; }

  /* Ad cards */
  .ads-card-list { display:flex; flex-direction:column; gap:12px; }
  .ads-ad-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; overflow:hidden; transition:border-color 0.15s; }
  .ads-ad-card.active-ad { border-color:var(--accent-border); }
  .ads-ad-card.ended-ad { opacity:0.6; }
  .ads-ad-inner { display:flex; gap:0; }
  .ads-ad-creative { width:90px; min-width:90px; background:linear-gradient(135deg,var(--bg-card2) 0%,#1e2535 100%); display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; padding:14px 0; font-size:28px; position:relative; }
  .ads-ad-status-dot { position:absolute; top:8px; right:8px; width:8px; height:8px; border-radius:50%; }
  .ads-ad-status-dot.active { background:var(--accent); box-shadow:0 0 6px var(--accent); }
  .ads-ad-status-dot.scheduled { background:#6399fa; }
  .ads-ad-status-dot.ended { background:var(--text-muted); }
  .ads-ad-body { flex:1; padding:14px 16px; display:flex; flex-direction:column; gap:8px; min-width:0; }
  .ads-ad-name { font-size:13px; font-weight:700; color:var(--text-primary); }
  .ads-ad-meta-row { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
  .ads-ad-meta { font-size:11px; color:var(--text-muted); display:flex; align-items:center; gap:4px; }
  .ads-ad-pill { font-size:9px; font-weight:700; padding:2px 8px; border-radius:8px; margin-left:auto; flex-shrink:0; }
  .ads-ad-pill.active { background:var(--accent-dim); color:var(--accent); border:1px solid var(--accent-border); }
  .ads-ad-pill.scheduled { background:rgba(99,153,250,0.12); color:#6399fa; border:1px solid rgba(99,153,250,0.25); }
  .ads-ad-pill.ended { background:rgba(255,255,255,0.05); color:var(--text-muted); border:1px solid var(--border); }
  .ads-ad-metrics { display:flex; gap:18px; padding:10px 16px; background:rgba(255,255,255,0.025); border-top:1px solid var(--border); }
  .ads-metric { display:flex; flex-direction:column; gap:2px; }
  .ads-metric-label { font-size:9px; color:var(--text-muted); font-weight:600; text-transform:uppercase; letter-spacing:0.05em; }
  .ads-metric-val { font-size:13px; font-weight:700; color:var(--text-primary); font-family:var(--font-head); }
  .ads-metric-sub { font-size:10px; color:var(--text-secondary); }
  .ads-renew-btn { background:var(--accent); color:var(--bg-base); border:none; border-radius:6px; padding:6px 14px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; transition:all 0.15s; }
  .ads-renew-btn:hover { background:#28c4a8; }
  .ads-edit-btn { background:transparent; border:1px solid var(--border); color:var(--text-secondary); border-radius:6px; padding:6px 14px; font-family:var(--font); font-size:11px; font-weight:600; cursor:pointer; transition:all 0.15s; }
  .ads-edit-btn:hover { border-color:rgba(255,255,255,0.2); color:var(--text-primary); }

  /* Ad Builder */
  .ads-builder-wrap { display:grid; grid-template-columns:1fr 340px; gap:20px; align-items:start; }
  .ads-builder-form { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:20px; display:flex; flex-direction:column; gap:18px; }
  .ads-builder-section { display:flex; flex-direction:column; gap:10px; }
  .ads-builder-section-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.03em; color:var(--text-secondary); text-transform:uppercase; letter-spacing:0.07em; font-size:10px; padding-bottom:6px; border-bottom:1px solid var(--border); }
  .ads-field { display:flex; flex-direction:column; gap:5px; }
  .ads-label { font-size:11px; font-weight:600; color:var(--text-secondary); text-transform:uppercase; letter-spacing:0.06em; }
  .ads-input { width:100%; background:var(--bg-surface); border:1px solid var(--border); border-radius:7px; padding:9px 12px; font-family:var(--font); font-size:13px; color:var(--text-primary); outline:none; transition:border-color 0.15s; }
  .ads-input:focus { border-color:var(--accent-border); }
  .ads-input::placeholder { color:var(--text-muted); }
  .ads-select { appearance:none; cursor:pointer; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b90a4' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat:no-repeat; background-position:right 10px center; padding-right:30px; }

  .ads-platform-toggle { display:flex; gap:8px; }
  .ads-platform-opt { flex:1; display:flex; align-items:center; justify-content:center; gap:7px; padding:10px; background:var(--bg-surface); border:1px solid var(--border); border-radius:9px; cursor:pointer; font-size:12px; font-weight:600; color:var(--text-muted); transition:all 0.15s; user-select:none; }
  .ads-platform-opt.selected-ig { border-color:rgba(220,39,67,0.5); background:rgba(220,39,67,0.08); color:#f87171; }
  .ads-platform-opt.selected-fb { border-color:rgba(24,119,242,0.5); background:rgba(24,119,242,0.08); color:#6399fa; }
  .ads-platform-opt:hover:not(.selected-ig):not(.selected-fb) { color:var(--text-secondary); border-color:rgba(255,255,255,0.14); }

  .ads-budget-row { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .ads-budget-hint { font-size:10px; color:var(--text-muted); margin-top:4px; line-height:1.5; }
  .ads-budget-hint strong { color:var(--text-secondary); }

  .ads-audience-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }

  /* Creative picker */
  .ads-creative-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(90px, 1fr)); gap:8px; }
  .ads-creative-thumb { aspect-ratio:4/5; background:var(--bg-surface); border:2px solid var(--border); border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:22px; cursor:pointer; transition:all 0.15s; position:relative; }
  .ads-creative-thumb:hover { border-color:rgba(255,255,255,0.2); }
  .ads-creative-thumb.selected { border-color:var(--accent); background:var(--accent-dim); }
  .ads-creative-thumb .ads-creative-check { position:absolute; top:4px; right:4px; width:16px; height:16px; border-radius:50%; background:var(--accent); color:var(--bg-base); display:flex; align-items:center; justify-content:center; font-size:9px; font-weight:700; }
  .ads-creative-empty { font-size:11px; color:var(--text-muted); text-align:center; padding:20px; line-height:1.6; }

  /* Ad preview card */
  .ads-preview-card { background:var(--bg-card); border:1px solid var(--border); border-radius:12px; padding:16px; position:sticky; top:0; }
  .ads-preview-title { font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.03em; margin-bottom:14px; }
  .ads-preview-phone { background:var(--bg-surface); border:1px solid var(--border); border-radius:12px; overflow:hidden; }
  .ads-preview-phone-bar { background:var(--bg-card2); padding:8px 12px; display:flex; align-items:center; gap:6px; font-size:10px; color:var(--text-muted); }
  .ads-preview-phone-bar-dot { width:5px; height:5px; border-radius:50%; background:var(--text-muted); opacity:0.5; }
  .ads-preview-img { width:100%; aspect-ratio:4/5; background:linear-gradient(135deg,var(--bg-card2),#1e2535); display:flex; align-items:center; justify-content:center; font-size:40px; }
  .ads-preview-caption { padding:10px 12px; display:flex; flex-direction:column; gap:3px; }
  .ads-preview-caption-label { font-size:9px; font-weight:700; color:var(--accent); text-transform:uppercase; letter-spacing:0.06em; }
  .ads-preview-caption-text { font-size:11px; color:var(--text-secondary); line-height:1.5; }
  .ads-preview-cta { margin:4px 12px 12px; background:var(--accent); color:var(--bg-base); border-radius:6px; padding:8px; text-align:center; font-size:11px; font-weight:700; }

  .ads-est-box { background:rgba(49,220,186,0.06); border:1px solid var(--accent-border); border-radius:9px; padding:12px 14px; display:flex; flex-direction:column; gap:7px; margin-top:10px; }
  .ads-est-row { display:flex; align-items:center; justify-content:space-between; font-size:11px; }
  .ads-est-label { color:var(--text-muted); }
  .ads-est-val { color:var(--text-primary); font-weight:600; }
  .ads-est-note { font-size:10px; color:var(--text-muted); line-height:1.5; padding-top:6px; border-top:1px solid rgba(49,220,186,0.15); }

  .ads-submit-btn { width:100%; padding:13px; background:var(--accent); color:var(--bg-base); border:none; border-radius:9px; font-family:var(--font); font-size:14px; font-weight:700; cursor:pointer; transition:all 0.15s; margin-top:4px; }
  .ads-submit-btn:hover { background:#28c4a8; }
  .ads-submit-btn:disabled { opacity:0.4; cursor:default; }

  /* Ads Bank */
  .ads-bank-toolbar { display:flex; align-items:center; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
  .ads-bank-title { font-family:var(--font-head); font-size:18px; font-weight:700; letter-spacing:0.02em; }
  .ads-bank-count { font-size:12px; color:var(--text-muted); padding:3px 9px; background:var(--bg-card); border:1px solid var(--border); border-radius:20px; }
  .ads-bank-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(150px, 1fr)); gap:12px; }
  .ads-bank-card { background:var(--bg-card); border:1px solid var(--border); border-radius:10px; overflow:hidden; transition:border-color 0.15s, transform 0.15s; position:relative; }
  .ads-bank-card:hover { border-color:rgba(255,255,255,0.16); transform:translateY(-2px); }
  .ads-bank-img { width:100%; aspect-ratio:4/5; background:linear-gradient(135deg,var(--bg-card2),#1e2535); display:flex; align-items:center; justify-content:center; font-size:32px; }
  .ads-bank-info { padding:8px 10px 9px; border-top:1px solid var(--border); }
  .ads-bank-name { font-size:11px; font-weight:600; color:var(--text-primary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-bottom:2px; }
  .ads-bank-meta { font-size:10px; color:var(--text-muted); }
  .ads-bank-overlay { position:absolute; inset:0; background:rgba(13,15,20,0.78); display:flex; flex-direction:column; align-items:center; justify-content:flex-end; gap:6px; opacity:0; transition:opacity 0.18s; padding:10px; }
  .ads-bank-card:hover .ads-bank-overlay { opacity:1; }
  .ads-bank-use-btn { width:100%; padding:7px; background:var(--accent); color:var(--bg-base); border:none; border-radius:6px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; }
  .ads-bank-del-btn { width:100%; padding:6px; background:rgba(239,68,68,0.14); color:#f87171; border:1px solid rgba(239,68,68,0.28); border-radius:6px; font-family:var(--font); font-size:11px; font-weight:700; cursor:pointer; }
  .ads-bank-empty { display:flex; flex-direction:column; align-items:center; gap:10px; padding:60px 20px; color:var(--text-muted); text-align:center; }
  .ads-bank-empty-icon { font-size:36px; }
  .ads-bank-empty-title { font-family:var(--font-head); font-size:16px; font-weight:700; color:var(--text-secondary); }
  .ads-bank-empty-sub { font-size:12px; line-height:1.6; max-width:260px; }

  /* Submitted success */
  .ads-submitted { display:flex; flex-direction:column; align-items:center; gap:12px; padding:48px 20px; text-align:center; }
  .ads-submitted-icon { width:64px; height:64px; border-radius:50%; background:var(--accent-dim); border:2px solid var(--accent); display:flex; align-items:center; justify-content:center; font-size:26px; }
  .ads-submitted-title { font-family:var(--font-head); font-size:20px; font-weight:700; }
  .ads-submitted-sub { font-size:12px; color:var(--text-secondary); max-width:360px; line-height:1.7; }

  /* ── GLITCH ROBOT ERROR CARD ── */
  .gr-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    padding: 48px 36px;
    background: rgba(26,29,39,0.85);
    backdrop-filter: blur(14px);
    border: 1px solid rgba(49,220,186,0.25);
    border-radius: 16px;
    box-shadow: 0 0 40px rgba(49,220,186,0.07), inset 0 0 24px rgba(49,220,186,0.03);
    text-align: center;
    max-width: 420px;
    margin: 48px auto;
  }
  .gr-robot-wrap { animation: gr-float 3.2s ease-in-out infinite; position: relative; }
  .gr-msg { font-size: 13px; color: var(--text-secondary); line-height: 1.7; max-width: 300px; }
  .gr-code { font-size: 10px; font-family: monospace; color: rgba(49,220,186,0.35); letter-spacing: 0.1em; margin-top: -4px; }

  @keyframes gr-float  { 0%,100%{transform:translateY(0)}   50%{transform:translateY(-9px)} }
  @keyframes gr-glitch { 0%{transform:translateY(-4px) skewX(-2deg) translateX(0)} 50%{transform:translateY(-6px) skewX(3deg) translateX(3px)} 100%{transform:translateY(-4px) skewX(-1deg) translateX(-1px)} }
  @keyframes gr-blink  { 0%,90%,100%{opacity:1} 95%{opacity:0} }
  @keyframes gr-spark  { 0%,100%{opacity:0;transform:scale(.6) rotate(-10deg)} 40%,60%{opacity:1;transform:scale(1.15) rotate(8deg)} }
  @keyframes gr-spark2 { 0%,100%{opacity:0;transform:scale(.5) rotate(12deg)}  30%,55%{opacity:1;transform:scale(1)    rotate(-6deg)} }
.login-screen {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0a0a0a;
  }
  .login-card {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(49,220,186,0.2);
    border-radius: 16px;
    padding: 48px 40px;
    width: 100%;
    max-width: 400px;
    text-align: center;
  }
  .login-card h2 {
    color: #31DCBA;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 28px;
    margin-bottom: 8px;
  }
  .login-card p {
    color: rgba(255,255,255,0.5);
    font-size: 14px;
    margin-bottom: 32px;
  }
  .login-input {
    width: 100%;
    padding: 12px 16px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 8px;
    color: #fff;
    font-size: 16px;
    margin-bottom: 16px;
    box-sizing: border-box;
  }
  .login-input:focus {
    outline: none;
    border-color: #31DCBA;
  }
  .login-btn {
    width: 100%;
    padding: 12px;
    background: #31DCBA;
    color: #0a0a0a;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 18px;
    font-weight: 700;
    border: none;
    border-radius: 8px;
    cursor: pointer;
  }
  .login-error {
    color: #ff6b6b;
    font-size: 13px;
    margin-top: 12px;
  }

  /* UNSCHEDULED QUEUE — chip DnD + modal */
  .sched-chip { cursor:grab; touch-action:manipulation; -webkit-touch-callout:none; -webkit-user-select:none; user-select:none; }
  .sched-chip.chip-dragging { opacity:0.35; transform:scale(0.95); cursor:grabbing; }
  .sched-chip.chip-drop-target { border-color:#31DCBA; box-shadow:0 0 0 2px rgba(49,220,186,0.25); }
  .sched-chips-hint { font-size:12px; color:var(--text-muted); margin:0 0 10px; line-height:1.5; }
  .sched-chips-standalone { margin-top:24px; }

  .chip-modal-overlay { position:fixed; inset:0; z-index:1000; background:rgba(0,0,0,0.72); backdrop-filter:blur(5px); -webkit-backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; padding:16px; }
  .chip-modal { background:#0d0f14; border:1px solid rgba(49,220,186,0.22); border-radius:16px; width:100%; max-width:420px; max-height:90vh; overflow-y:auto; position:relative; display:flex; flex-direction:column; box-shadow:0 24px 80px rgba(0,0,0,0.7); }
  .chip-modal::-webkit-scrollbar { width:4px; }
  .chip-modal::-webkit-scrollbar-thumb { background:rgba(49,220,186,0.2); border-radius:2px; }
  .chip-modal-close { position:absolute; top:12px; right:12px; width:30px; height:30px; border-radius:50%; background:rgba(255,255,255,0.07); border:1px solid rgba(255,255,255,0.12); color:var(--text-muted); font-size:14px; cursor:pointer; display:flex; align-items:center; justify-content:center; z-index:2; transition:all 0.15s; }
  .chip-modal-close:hover { background:rgba(255,255,255,0.14); color:#fff; }
  .chip-modal-img { width:100%; max-height:220px; object-fit:cover; border-radius:12px 12px 0 0; display:block; }
  .chip-modal-img-fallback { width:100%; height:140px; background:rgba(49,220,186,0.08); border-radius:12px 12px 0 0; }
  .chip-modal-body { padding:16px 18px 22px; display:flex; flex-direction:column; gap:14px; }
  .chip-modal-field { display:flex; flex-direction:column; gap:5px; }
  .chip-modal-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:0.08em; color:var(--text-muted); }
  .chip-modal-textarea { width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:10px 12px; font-family:var(--font); font-size:13px; color:var(--text-primary); resize:vertical; min-height:80px; outline:none; color-scheme:dark; box-sizing:border-box; transition:border-color 0.15s; }
  .chip-modal-textarea:focus { border-color:rgba(49,220,186,0.4); }
  .chip-modal-input { width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:8px 12px; font-family:var(--font); font-size:13px; color:var(--text-primary); outline:none; color-scheme:dark; box-sizing:border-box; transition:border-color 0.15s; }
  .chip-modal-input:focus { border-color:rgba(49,220,186,0.4); }
  .chip-modal-pills { display:flex; gap:8px; flex-wrap:wrap; }
  .chip-modal-pill { padding:6px 16px; border-radius:20px; font-family:var(--font-head); font-size:11px; font-weight:700; letter-spacing:0.05em; cursor:pointer; border:1.5px solid rgba(49,220,186,0.25); background:transparent; color:var(--text-muted); transition:all 0.15s; }
  .chip-modal-pill.active { background:#31DCBA; color:#0d0f14; border-color:#31DCBA; }
  .chip-modal-pill:hover:not(.active) { border-color:rgba(49,220,186,0.5); color:var(--text-secondary); }
  .chip-modal-time-row { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
  .chip-modal-time-btn { padding:6px 12px; border-radius:7px; background:var(--bg-surface,rgba(255,255,255,0.04)); border:1px solid rgba(255,255,255,0.1); font-family:var(--font); font-size:12px; font-weight:600; color:var(--text-secondary); cursor:pointer; transition:all 0.15s; }
  .chip-modal-time-btn.active { border-color:rgba(49,220,186,0.5); color:#31DCBA; background:rgba(49,220,186,0.08); }
  .chip-modal-time-input { background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:6px 10px; font-family:var(--font); font-size:12px; color:var(--text-primary); outline:none; color-scheme:dark; transition:border-color 0.15s; flex:1; min-width:80px; }
  .chip-modal-time-input:focus { border-color:rgba(49,220,186,0.4); }
  .chip-modal-save { width:100%; padding:12px; border-radius:10px; background:#31DCBA; color:#0d0f14; font-family:var(--font-head); font-size:13px; font-weight:700; letter-spacing:0.05em; border:none; cursor:pointer; transition:all 0.18s; margin-top:2px; }

  /* CONFIRM MODAL — one-time disclaimers (auto-approve) */
  .ap-confirm-overlay { position:fixed; inset:0; z-index:1200; background:rgba(0,0,0,0.72); backdrop-filter:blur(5px); -webkit-backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; padding:16px; }
  .ap-confirm { background:#0d0f14; border:1px solid rgba(255,176,32,0.3); border-radius:16px; width:100%; max-width:460px; max-height:90vh; overflow-y:auto; padding:22px 24px 20px; box-shadow:0 24px 80px rgba(0,0,0,0.7); }
  .ap-confirm-title { font-family:var(--font-head); font-size:17px; font-weight:700; letter-spacing:0.01em; color:var(--text-primary); margin-bottom:8px; }
  .ap-confirm-lead { font-size:12.5px; color:var(--text-secondary); line-height:1.6; margin-bottom:14px; }
  .ap-confirm-list { display:flex; flex-direction:column; gap:10px; margin-bottom:20px; }
  .ap-confirm-item { display:flex; gap:9px; align-items:flex-start; font-size:12px; color:var(--text-secondary); line-height:1.55; }
  .ap-confirm-item strong { color:var(--text-primary); font-weight:700; }
  .ap-confirm-dot { width:5px; height:5px; border-radius:50%; background:#FFB020; flex-shrink:0; margin-top:6px; }
  .ap-confirm-actions { display:flex; gap:10px; }
  .ap-confirm-btn { flex:1; padding:11px; border-radius:10px; font-family:var(--font-head); font-size:12.5px; font-weight:700; letter-spacing:0.04em; cursor:pointer; transition:all 0.18s; }
  .ap-confirm-btn-go { background:#31DCBA; color:#0d0f14; border:none; }
  .ap-confirm-btn-go:hover { background:#28c4a8; box-shadow:0 4px 16px rgba(49,220,186,0.25); }
  .ap-confirm-btn-go:disabled { opacity:0.4; cursor:not-allowed; box-shadow:none; background:#31DCBA; }
  .ap-confirm-agree { display:flex; gap:10px; align-items:flex-start; min-height:44px; padding:10px 12px; margin-bottom:8px; border:1px solid var(--border); border-radius:10px; font-size:12px; color:var(--text-primary); line-height:1.55; cursor:pointer; }
  .ap-confirm-agree input { width:18px; height:18px; margin:1px 0 0; flex-shrink:0; accent-color:#31DCBA; cursor:pointer; }
  .ap-confirm-terms { font-size:11px; color:var(--text-muted); margin-bottom:16px; }
  .ap-confirm-terms a { color:var(--text-secondary); text-decoration:underline; }
  .ap-confirm-btn-cancel { background:transparent; color:var(--text-secondary); border:1px solid var(--border); }
  .ap-confirm-btn-cancel:hover { color:var(--text-primary); border-color:rgba(255,255,255,0.28); }
  .chip-modal-save:hover { background:#28c4a8; box-shadow:0 4px 16px rgba(49,220,186,0.25); }
`;



// --- NAV CONFIG ---
const NAV = [
  { section: 'MAIN' },
  { id: 'home',        label: 'Home',             icon: 'home' },
  { id: 'onboarding',  label: 'Onboarding',       icon: 'check-circle' },
  { id: 'brand',       label: 'Brand Hub',        icon: 'star' },
  { section: 'CONTENT' },
  { id: 'calendar',    label: 'Scheduler',         icon: 'calendar' },
  { id: 'approval',    label: 'Content Queue',    icon: 'file-check' },
  { id: 'photos',      label: 'Photo Library',    icon: 'image' },
  { id: 'editor',      label: 'Photo Editor',     icon: 'wand' },
  { id: 'video',       label: 'Video Editor',     icon: 'video' },
  { id: 'imagegen',    label: 'Post Generator', icon: 'sparkle' },
  { section: 'GROWTH' },
  { id: 'ads',         label: 'Ads Application',  icon: 'megaphone' },
  // Shown only while Google Business is connected (see the nav render).
  { id: 'reviews',     label: 'Reviews',          icon: 'message' },
  // Also only while Google Business is connected: hours, phone, photos.
  { id: 'gbprofile',   label: 'Google Profile',   icon: 'store' },
  { section: 'ANALYTICS' },
  { id: 'performance', label: 'Performance',      icon: 'line-chart' },
  { section: 'SETTINGS' },
  { id: 'connections', label: 'Connections', icon: 'link' },
  { id: 'admin', label: 'Admin', icon: 'settings' },
];
const FOOTER_NAV = [
  { id: 'account', label: 'Account & Perks', icon: 'user' },
];

const TITLES = {
  home: 'Home', onboarding: 'Onboarding', brand: 'Brand Hub',
  calendar: 'Content Calendar', approval: 'Content Queue',
  photos: 'Photo Library', editor: 'Photo Editor', video: 'Video Editor', imagegen: 'Post Generator',
  stories: 'Stories', performance: 'Post Performance', account: 'Account & Perks',
  connections: 'Connections', ads: 'Ads Application', admin: 'Admin',
  reviews: 'Google Reviews', gbprofile: 'Google Profile',
};

// ============================================================
// PANEL COMPONENTS — build one per session
// ============================================================

// ── Glitch Robot Error Card ──────────────────────────────────
// Shown inside a panel when the backend returns a named API key error.
// errorCode: 'FAL_KEY_INVALID' | 'CLAUDE_KEY_INVALID' | 'META_KEY_INVALID'
function GlitchRobotCard({ errorCode }) {
  const [glitching, setGlitching] = React.useState(false);

  // Random glitch twitch every 4-8s
  React.useEffect(() => {
    let timer;
    let cancelled = false;
    function scheduleGlitch() {
      if (cancelled) return;
      const delay = 4000 + Math.random() * 4000;
      timer = setTimeout(() => {
        if (cancelled) return;
        setGlitching(true);
        timer = setTimeout(() => {
          if (cancelled) return;
          setGlitching(false);
          scheduleGlitch();
        }, 320);
      }, delay);
    }
    scheduleGlitch();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const T = '#31DCBA';
  const glow = `drop-shadow(0 0 6px ${T}) drop-shadow(0 0 14px rgba(49,220,186,0.45))`;

  return (
    <div className="gr-card">
      {/* Neon robot SVG */}
      <div className={`gr-robot-wrap${glitching ? ' glitch' : ''}`} style={{ filter: glow }}>
        <svg width="88" height="96" viewBox="0 0 88 96" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Antenna base */}
          <line x1="44" y1="8" x2="44" y2="18" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
          {/* Antenna tip — sparks */}
          <circle cx="44" cy="5" r="3.5" stroke={T} strokeWidth="2"/>
          {/* Spark left */}
          <g style={{ transformOrigin:'44px 5px', animation:'gr-spark 1.8s ease-in-out infinite' }}>
            <line x1="37" y1="2" x2="34" y2="-1" stroke={T} strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="36" y1="5" x2="32" y2="5"  stroke={T} strokeWidth="1.8" strokeLinecap="round"/>
          </g>
          {/* Spark right */}
          <g style={{ transformOrigin:'44px 5px', animation:'gr-spark2 1.8s ease-in-out infinite 0.4s' }}>
            <line x1="51" y1="2" x2="54" y2="-1" stroke={T} strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="52" y1="5" x2="56" y2="5"  stroke={T} strokeWidth="1.8" strokeLinecap="round"/>
          </g>
          {/* Head */}
          <rect x="20" y="18" width="48" height="34" rx="7" stroke={T} strokeWidth="2.5"/>
          {/* Eyes */}
          <rect x="27" y="27" width="12" height="10" rx="3" stroke={T} strokeWidth="2"
            style={{ animation:'gr-blink 3.5s ease-in-out infinite' }}/>
          <rect x="49" y="27" width="12" height="10" rx="3" stroke={T} strokeWidth="2"
            style={{ animation:'gr-blink 3.5s ease-in-out infinite 0.15s' }}/>
          {/* Eye pupils */}
          <circle cx="33" cy="32" r="2.5" fill={T} style={{ animation:'gr-blink 3.5s ease-in-out infinite' }}/>
          <circle cx="55" cy="32" r="2.5" fill={T} style={{ animation:'gr-blink 3.5s ease-in-out infinite 0.15s' }}/>
          {/* Mouth — flat line = concern */}
          <line x1="32" y1="44" x2="56" y2="44" stroke={T} strokeWidth="2" strokeLinecap="round"/>
          {/* Neck */}
          <line x1="36" y1="52" x2="36" y2="58" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="52" y1="52" x2="52" y2="58" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
          {/* Body */}
          <rect x="16" y="58" width="56" height="30" rx="8" stroke={T} strokeWidth="2.5"/>
          {/* Chest panel */}
          <rect x="28" y="65" width="32" height="16" rx="4" stroke={T} strokeWidth="1.5" strokeDasharray="3 2"/>
          {/* Chest light */}
          <circle cx="44" cy="73" r="3.5" fill={T} style={{ opacity: 0.7 }}/>
          {/* Arms */}
          <line x1="16" y1="64" x2="6"  y2="72" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="72" y1="64" x2="82" y2="72" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
          {/* Hands */}
          <circle cx="5"  cy="75" r="3" stroke={T} strokeWidth="2"/>
          <circle cx="83" cy="75" r="3" stroke={T} strokeWidth="2"/>
          {/* Legs */}
          <line x1="32" y1="88" x2="28" y2="96" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
          <line x1="56" y1="88" x2="60" y2="96" stroke={T} strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Message */}
      <div className="gr-msg">
        Something glitched on our end — we're already on it. Sorry for the blip! 🛠️
      </div>

      {/* Error code */}
      {errorCode && <div className="gr-code">{errorCode}</div>}
    </div>
  );
}

function StoriesPanel({ onStartStory }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', color:'var(--text-muted)', gap:14, padding:24, textAlign:'center' }}>
      {/* Vertical 9:16 frame to echo how a Story looks */}
      <div style={{
        width:96, aspectRatio:'9 / 16', borderRadius:12,
        background:'linear-gradient(135deg, var(--accent-dim), #1e2535)',
        border:'1px solid var(--accent-border)',
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:32,
      }}>📱</div>
      <div style={{ fontFamily:'var(--font-head)', fontSize:18, fontWeight:700, color:'var(--text-primary)' }}>Instagram &amp; Facebook Stories</div>
      <div style={{ fontSize:13, lineHeight:1.5, maxWidth:340 }}>
        Create a vertical 9:16 Story and publish it straight to Instagram and Facebook. We'll open the Post Generator with both Story destinations pre-selected.
      </div>
      <button
        onClick={() => onStartStory?.()}
        style={{
          marginTop:4, padding:'11px 22px', border:'none', borderRadius:9,
          background:'var(--accent)', color:'#0d0f14', fontFamily:'var(--font-head)',
          fontSize:13, fontWeight:700, letterSpacing:'0.03em', cursor:'pointer',
        }}
      >✦ Create a Story</button>
    </div>
  );
}

// ============================================================
// SHARED TOOLTIP COMPONENT
// Usage: <Tip text="Your tooltip text here" />
//        <Tip text="..." below /> for downward-facing bubble
// ============================================================
function Tip({ text, below }) {
  // Hover alone leaves the bubble unreachable on touch, so the icon is also a
  // button: tap toggles, blur closes. stopPropagation keeps a Tip that sits
  // inside a clickable row (e.g. the auto-approve switch) from firing the row.
  const [open, setOpen] = useState(false);
  return (
    <span
      className={`ap-tip-wrap${open ? ' tip-open' : ''}`}
      style={{ marginLeft: 5 }}
      role="button"
      aria-label="More information"
      aria-expanded={open}
      tabIndex={0}
      onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }
        else if (e.key === 'Escape') setOpen(false);
      }}
      onBlur={() => setOpen(false)}
    >
      <span className="ap-tip-icon">?</span>
      <span className={`ap-tip-bubble${below ? ' tip-below' : ''}`}>{text}</span>
    </span>
  );
}

// Phase 7: computeNextSlot() DELETED from here.
//
// It read `posts` in the browser under RLS to find the next free publishing
// slot, and was module-scoped because two panels needed to hand out the same
// slots. Phase 5 migrated the first of those (PostApprovalPanel) onto
// POST /api/posts/:id/approve, whose computeNextSlotFor is a character-for-
// character copy of this function; the Post Generator's two callers were the
// only ones left, and they move onto the same endpoint below. Nothing calls it
// any more, so it goes rather than lingering as live dead code 4,200 lines from
// its last caller.
//
// The bug it existed to prevent is unchanged and now enforced server-side: the
// publish-approved cron filters on
// `.not('scheduled_at','is',null).lte('scheduled_at', now())`, so approving
// without a scheduled_at drops the post from the queue without it ever
// publishing (Bug #11). The endpoint always sets one.
//
// It also fixes something this version could not. Running in the browser it
// read the SIGNED-IN admin's posts, so approving while viewing as a client
// derived the slot from the admin's schedule rather than the client's — the
// same defect Phase 5 recorded for the approval queue, now closed for the
// generator too.

// Approve a post and schedule it into the client's next free slot.
//
// caption/hashtags are optional and exist for the Post Generator's result
// cards, which stay editable until the moment Approve is pressed: sending them
// here writes the text and the schedule in ONE statement. PostApprovalPanel
// omits both and behaves exactly as it did in Phase 5.
async function approvePost(postId, edits) {
  const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(postId)}/approve`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(edits || {}),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body.scheduled_at;
}

// Upload a transient composer input (a generation base image, a carousel slide,
// a composited slide) and get back its public URL.
//
// Deliberately NOT /api/brand-photos: that route inserts a brand_photos row,
// and these files are scratch. Routing them there would fill the client's Photo
// Library with images they never chose to save.
async function scratchUpload(file, purpose) {
  const form = new FormData();
  form.append('file', file);
  form.append('purpose', purpose);
  const res = await clientScopedFetch(`${BACKEND}/api/scratch-upload`, { method: 'POST', body: form });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body.url;
}

// Mark library photos as spent. One call, one statement server-side.
async function markPhotosUsed(ids) {
  if (!ids || ids.length === 0) return;
  const res = await clientScopedFetch(`${BACKEND}/api/brand-photos/mark-used`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ ids }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
}
// Phase 5: the two generic post-mutation calls every panel now shares.
//
// Neither sends a client identifier. requireOwnerOrAdmin resolves the target
// from req.body.clientId BEFORE req.query.client, so a body-borne id would
// silently beat an admin's viewing-as override; the backend also strips them at
// the route boundary. Scope comes from the resolved session, full stop.
//
// This is also what retires QueueCard's `if (post.client_id)` conditional. That
// guard failed OPEN — a falsy client_id meant an update filtered by id alone,
// i.e. a possible cross-client write. Here the scope is not optional.
// opts.ifStatus: apply only while the row still has that status (one statement
// server-side). Fails with err.code 'status_changed' when it no longer does.
async function patchPost(postId, patch, opts = {}) {
  const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(postId)}`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(opts.ifStatus ? { patch, if_status: opts.ifStatus } : { patch }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    if (body.code) err.code = body.code;
    throw err;
  }
  return body.post;
}

// Phase 6 companion to patchPost. Same contract: no client identifier, scope
// resolved server-side. Retires four id-only filters in PhotoLibrary and the
// editor as a side effect of the route being client-scoped.
async function patchBrandPhoto(photoId, patch) {
  const res = await clientScopedFetch(`${BACKEND}/api/brand-photos/${encodeURIComponent(photoId)}`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ patch }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
}

async function deletePost(postId) {
  const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(postId)}`, {
    method: 'DELETE',
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
}

// ============================================================
// ONBOARDING PANEL — Session 1
// ============================================================

// The content pillars a client starts with, in brand_profile.brand_pillars'
// shape: { id, name, active }. Shared by Onboarding and Brand Hub — both show
// these when nothing is saved, so the two can never offer different defaults.
export const DEFAULT_PILLARS = [
  { id: '1', name: 'Behind the Scenes', active: true },
  { id: '2', name: 'Product Spotlight', active: true },
  { id: '3', name: 'Community & Local', active: true },
  { id: '4', name: 'Tips & Value', active: true },
  { id: '5', name: 'Seasonal & Promos', active: true },
  { id: '6', name: 'Customer Stories', active: false },
];

export const ONBOARDING_STEP_LABELS = ['Welcome', 'Pillars', 'Brand', 'Social', 'Brand Hub'];

// "https://www.yourbusiness.com/" → "yourbusiness.com", for button labels.
const displayWebsite = (url) => String(url || '').trim()
  .replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');

// onOpenBrandHub(intent?) — leave Onboarding for Brand Hub. intent is a
// one-shot autofill request ({ mode: 'website', url } | { mode: 'screenshots' })
// that Brand Hub consumes once; no intent just navigates.
// isSelfServe / onCreatePost — the finish screen points a Self-Serve client
// at their first post; Managed keeps the plain Brand Hub hand-off.
function OnboardingPanel({ onOpenBrandHub = () => {}, isSelfServe = false, onCreatePost = () => {} }) {
  const { clientId } = useClientId();
  const { viewingAsClientId } = useViewingAs();
  const [step, setStep] = useState(1);
  const [igConnected, setIgConnected] = useState(false);
  const [fbConnected, setFbConnected] = useState(false);
  // Which platform's popup is open ('ig' | 'fb' | null), and the last error
  // for each. One at a time: both use the same named popup window.
  const [metaConnecting, setMetaConnecting] = useState(null);
  const [metaError, setMetaError] = useState({ ig: null, fb: null });
  const metaTimersRef = useRef({});
  const [website, setWebsite] = useState('');
  const [savedWebsite, setSavedWebsite] = useState('');
  // Brand step colours — brand_profile.primary_color / secondary_color, the
  // same two Brand Hub's Visual tab edits. '' is "not set", as in Brand Hub:
  // nothing is filled in for a client who never picked one. coloursLoaded
  // gates the save the way pillarsLoaded does.
  const [primaryColour, setPrimaryColour] = useState('');
  const [secondaryColour, setSecondaryColour] = useState('');
  const [savedColours, setSavedColours] = useState({ primary: '', secondary: '' });
  const [coloursLoaded, setColoursLoaded] = useState(false);
  const [brandSaving, setBrandSaving] = useState(false);
  const [brandError, setBrandError] = useState('');
  const [editingPillar, setEditingPillar] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const logoInputRef = useRef(null);
  // Brand Hub step: a website typed here when none was saved in the Brand step.
  const [fillUrl, setFillUrl] = useState('');

  // Pillars are brand_profile.brand_pillars — the same list Brand Hub edits.
  // savedPillars is what the server holds, so an untouched list skips the
  // save. pillarsLoaded stays false until the read lands: a failed read must
  // not let edits to the defaults overwrite a list we never saw.
  const [pillars, setPillars] = useState(DEFAULT_PILLARS);
  const [savedPillars, setSavedPillars] = useState(DEFAULT_PILLARS);
  const [pillarsLoaded, setPillarsLoaded] = useState(false);
  const [pillarsSaving, setPillarsSaving] = useState(false);
  const [pillarsError, setPillarsError] = useState('');

  const TOTAL = ONBOARDING_STEP_LABELS.length;
  const goNext = () => setStep(s => Math.min(s + 1, TOTAL + 1));
  const goBack = () => setStep(s => Math.max(s - 1, 1));

  const togglePillar = (id) => {
    setPillars(ps => ps.map(p => p.id === id ? { ...p, active: !p.active } : p));
  };
  const renamePillar = (id, val) => {
    setPillars(ps => ps.map(p => p.id === id ? { ...p, name: val } : p));
  };

  // Phase 2: was a direct supabase.from('brand_profile') read. RLS scopes that
  // to the signed-in user, so it returned nothing for an admin viewing a
  // client; clientScopedFetch carries the viewing-as override instead.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/brand-profile`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d?.profile?.logo_url) setLogoUrl(d.profile.logo_url); })
      .catch(() => { /* header art is best-effort, same as the query it replaced */ });
    return () => { cancelled = true; };
  }, [clientId]);

  // igConnected used to be set by the Instagram button itself, which made
  // this step lie — clicking it marked IG "connected" without ever starting
  // OAuth. This reads the real connection state instead.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/meta/status`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d) { setIgConnected(!!d.instagram); setFbConnected(!!d.facebook); } })
      .catch(() => { /* best-effort, same as the brand-profile fetch above */ });
    return () => { cancelled = true; };
  }, [clientId]);

  // Website and pillars prefill. /api/brand-profile does not serve either;
  // the Brand Hub read does, so this uses the same route Brand Hub loads from,
  // with Brand Hub's fallback: no saved pillars → DEFAULT_PILLARS. Keyed on
  // viewingAsClientId too, and reset first, so an admin switching client view
  // never sees (or saves over) the previous client's values.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setWebsite(''); setSavedWebsite('');
    setPrimaryColour(''); setSecondaryColour('');
    setSavedColours({ primary: '', secondary: '' });
    setColoursLoaded(false); setBrandError('');
    setPillars(DEFAULT_PILLARS); setSavedPillars(DEFAULT_PILLARS);
    setPillarsLoaded(false); setPillarsError('');
    clientScopedFetch(`${BACKEND}/api/brand-hub`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        // { ok, profile: null } is a client with no row yet: a successful,
        // empty load. A body without `profile` is a failed one.
        if (cancelled || !d || !('profile' in d)) return;
        const w = d.profile?.brand_website || '';
        if (w) { setWebsite(w); setSavedWebsite(w); }
        const primary = d.profile?.primary_color || '';
        const secondary = d.profile?.secondary_color || '';
        setPrimaryColour(primary); setSecondaryColour(secondary);
        setSavedColours({ primary, secondary });
        setColoursLoaded(true);
        const p =d.profile?.brand_pillars?.length ? d.profile.brand_pillars : DEFAULT_PILLARS;
        setPillars(p); setSavedPillars(p);
        setPillarsLoaded(true);
      })
      .catch(() => { /* optional fields — defaults are the fallback */ });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId]);

  // Stop any connect timers if the panel unmounts mid-flow.
  useEffect(() => () => {
    const t = metaTimersRef.current;
    if (t.poll) clearInterval(t.poll);
    if (t.safety) clearTimeout(t.safety);
    if (t.close) clearInterval(t.close);
  }, []);

  // Connect in place, so the client never leaves Onboarding. Same flow as
  // ConnectionsPanel.handleConnect — popup opened synchronously on about:blank
  // while the click gesture is live, then pointed at metaOAuthUrl once
  // mintOAuthFlowToken returns — with /api/meta/status polled for the result.
  function connectMeta(platform) {
    const dbPlatform = platform === 'ig' ? 'instagram' : 'facebook';
    const setConnected = platform === 'ig' ? setIgConnected : setFbConnected;
    const t = metaTimersRef.current;
    const stopTimers = () => {
      if (t.poll) clearInterval(t.poll);
      if (t.safety) clearTimeout(t.safety);
      if (t.close) clearInterval(t.close);
      t.poll = t.safety = t.close = null;
    };
    const fail = (msg) => {
      stopTimers();
      setMetaConnecting(null);
      setMetaError(e => ({ ...e, [platform]: msg }));
    };
    const readStatus = () => clientScopedFetch(`${BACKEND}/api/meta/status`)
      .then(r => (r.ok ? r.json() : null));

    stopTimers();
    setMetaError(e => ({ ...e, [platform]: null }));
    setMetaConnecting(platform);

    const popup = window.open('about:blank', 'MetaOAuth', 'width=540,height=680,scrollbars=yes,resizable=yes,status=yes'); // synchronous — keep it that way
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      fail('Popup blocked. Please allow popups for this site and try again.');
      return;
    }

    mintOAuthFlowToken(dbPlatform)
      .then((flowToken) => { popup.location.href = metaOAuthUrl(platform, flowToken); })
      .catch((err) => {
        try { popup.close(); } catch (_) { /* already gone */ }
        fail(err.message || 'Could not start the connection. Please try again.');
      });

    const succeed = (data) => {
      stopTimers();
      setMetaConnecting(null);
      setIgConnected(!!data.instagram);
      setFbConnected(!!data.facebook);
      setConnected(true);
    };

    t.poll = setInterval(() => {
      readStatus().then(d => { if (d && d[dbPlatform]) succeed(d); }).catch(() => { /* keep polling */ });
    }, 2000);
    t.safety = setTimeout(() => fail('Connection timed out. Please try again.'), 3 * 60 * 1000);
    t.close = setInterval(() => {
      if (!popup.closed) return;
      clearInterval(t.close); t.close = null;
      // Final check — the client may have finished OAuth before closing it.
      readStatus()
        .then(d => { if (d && d[dbPlatform]) succeed(d); else fail('Connection cancelled.'); })
        .catch(() => fail('Connection cancelled.'));
    }, 600);
  }

  // The Brand step's fields — website and the two colours — in one POST to
  // /api/brand-hub, the route Brand Hub saves through. It writes only the
  // allowlisted keys it is given (an UPDATE of those columns), so only what
  // changed is sent and nothing else on brand_profile is touched. Nothing
  // changed → no request. A cleared colour is sent as '' (not set), as Brand
  // Hub sends it.
  const saveBrandAndNext = async () => {
    const next = website.trim();
    const changes = {};
    if (next !== savedWebsite) changes.brand_website = next;
    if (primaryColour !== savedColours.primary) changes.primary_color = primaryColour;
    if (secondaryColour !== savedColours.secondary) changes.secondary_color = secondaryColour;
    if (!Object.keys(changes).length) { goNext(); return; }
    // Colours we never loaded could be real ones: don't overwrite them blind.
    if (('primary_color' in changes || 'secondary_color' in changes) && !coloursLoaded) {
      setBrandError('We couldn’t load your saved colours, so these changes weren’t saved. Check your connection and refresh to try again.');
      return;
    }
    setBrandSaving(true);
    setBrandError('');
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-hub`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ profile: changes }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setSavedWebsite(next);
      setWebsite(next);
      setSavedColours({ primary: primaryColour, secondary: secondaryColour });
      goNext();
    } catch (err) {
      setBrandError('Could not save your brand: ' + err.message);
    } finally {
      setBrandSaving(false);
    }
  };

  // Same partial write as the website: brand_pillars alone, so nothing else
  // on brand_profile is touched. Skipped when unchanged; stays on the step
  // with the error shown when it fails.
  const savePillarsAndNext = async () => {
    if (JSON.stringify(pillars) === JSON.stringify(savedPillars)) { goNext(); return; }
    if (!pillarsLoaded) {
      setPillarsError('We couldn’t load your saved pillars, so these changes weren’t saved. Check your connection and refresh to try again.');
      return;
    }
    setPillarsSaving(true);
    setPillarsError('');
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-hub`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ profile: { brand_pillars: pillars } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setSavedPillars(pillars);
      goNext();
    } catch (err) {
      setPillarsError('Could not save your pillars: ' + err.message);
    } finally {
      setPillarsSaving(false);
    }
  };

  // Brand Hub step. The URL the website button will read: the one saved in
  // the Brand step, else whatever was typed here.
  const fillFromUrl = (savedWebsite || fillUrl).trim();

  // The button's input and the drop zone both upload through here.
  const uploadLogoFile = async (file) => {
    setLogoUploading(true);
    setLogoError('');
    const { publicUrl, error, stage } = await uploadLogo({ file, clientId });
    if (error) {
      if (stage === 'db') setLogoError('Saved to storage but failed to update profile: ' + error.message);
      else setLogoError('Upload failed: ' + error.message);
    } else {
      setLogoUrl(publicUrl);
    }
    setLogoUploading(false);
  };
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadLogoFile(file);
  };
  const logoDrop = useFileDropZone({
    disabled: logoUploading,
    onFiles: (files) => {
      const { file, error } = singleImageFromDrop(files);
      if (error) { setLogoError(error); return; }
      if (file) uploadLogoFile(file);
    },
  });

  const StepIndicator = () => (
    <div className="ob-steps">
      {ONBOARDING_STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const status = step > n ? 'done' : step === n ? 'active' : '';
        return (
          <React.Fragment key={n}>
            {i > 0 && <div className={`ob-connector${step > n ? ' done' : ''}`} />}
            <div className={`ob-step ${status}`}>
              <div className="ob-step-circle">
                {step > n ? '✓' : n}
              </div>
              <div className="ob-step-label">{label}</div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  // COMPLETE
  if (step === TOTAL + 1) return (
    <div className="ob-wrap">
      <StepIndicator />
      <div className="ob-card">
        <div className="ob-complete">
          <div className="ob-complete-icon">✓</div>
          <div className="ob-card-title" style={{marginBottom:0}}>You're set up!</div>
          {isSelfServe ? <>
            <div style={{fontSize:13,color:'var(--text-secondary)',maxWidth:380,lineHeight:1.7}}>
              Next, create your first post — your Getting started checklist on Home will walk you through the rest.
            </div>
            <div style={{display:'flex',gap:10,marginTop:8,flexWrap:'wrap',justifyContent:'center'}}>
              <button className="ob-btn ob-btn-primary" style={{minHeight:44}} onClick={() => onCreatePost()}>Create your first post →</button>
              <button className="ob-btn ob-btn-secondary" style={{minHeight:44}} onClick={() => onOpenBrandHub()}>Go to Brand Hub</button>
            </div>
          </> : <>
            <div style={{fontSize:13,color:'var(--text-secondary)',maxWidth:380,lineHeight:1.7}}>
              Your Brand Hub is ready to review.
            </div>
            <div style={{display:'flex',gap:10,marginTop:8,flexWrap:'wrap',justifyContent:'center'}}>
              <button className="ob-btn ob-btn-primary" style={{minHeight:44}} onClick={() => onOpenBrandHub()}>Go to Brand Hub</button>
              <button className="ob-btn ob-btn-secondary" style={{minHeight:44}} onClick={() => setStep(1)}>Review Setup</button>
            </div>
          </>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="ob-wrap">
      <StepIndicator />
      <div className="ob-card">

        {/* STEP 1 — WELCOME */}
        {step === 1 && <>
          <div className="ob-welcome-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14v2.92z"/>
            </svg>
          </div>
          <div className="ob-card-title">Welcome to Autopilot Digital</div>
          <div className="ob-card-sub">
            Let's get your account set up. This takes about 3 minutes.<br/>
            Confirm a few things and we'll help you fill in your Brand Hub.
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {[
              'Choose your content pillars',
              'Add your logo and website',
              'Connect Instagram and Facebook',
              'Fill in your Brand Hub',
            ].map((item, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,fontSize:12,color:'var(--text-secondary)'}}>
                <div style={{width:20,height:20,borderRadius:'50%',background:'var(--accent-dim)',border:'1px solid var(--accent-border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'var(--accent)',flexShrink:0}}>{i+1}</div>
                {item}
              </div>
            ))}
          </div>
          <div className="ob-btn-row">
            <button className="ob-btn ob-btn-primary" onClick={goNext}>Get Started →</button>
          </div>
        </>}

        {/* STEP 2 — CONTENT PILLARS (brand_profile.brand_pillars, shared with Brand Hub) */}
        {step === 2 && <>
          <div className="ob-card-title">Your Content Pillars<Tip text="Pillars are the recurring themes of your content. Each post is built around one pillar to keep your feed varied and on-strategy." below /></div>
          <div className="ob-card-sub">
            These are the content themes we'll rotate through each week. Tap any that don't fit to turn them off, or tap the pencil to rename them. You can change these any time in Brand Hub.
          </div>
          <div className="ob-pillars">
            {pillars.map(p => (
              <div key={p.id} className={`ob-pillar${p.active ? ' active' : ''}`}
                onClick={() => editingPillar !== p.id && togglePillar(p.id)}>
                {editingPillar === p.id ? (
                  <div onClick={e => e.stopPropagation()}>
                    <input className="ob-edit-input" value={p.name} autoFocus
                      onChange={e => renamePillar(p.id, e.target.value)}
                      onBlur={() => setEditingPillar(null)}
                      onKeyDown={e => e.key === 'Enter' && setEditingPillar(null)} />
                  </div>
                ) : (
                  <>
                    <div className="ob-pillar-name">
                      {p.name}
                      <div className="ob-pillar-check">{p.active ? '✓' : ''}</div>
                    </div>
                    <button className="ob-pillar-edit"
                      aria-label={`Edit ${p.name}`}
                      title={`Edit ${p.name}`}
                      onClick={e => { e.stopPropagation(); setEditingPillar(p.id); }}>✎</button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{marginTop:12,fontSize:11,color:'var(--text-muted)'}}>
            {pillars.filter(p=>p.active).length} of {pillars.length} pillars active
          </div>
          {pillarsError && <div role="alert" style={{fontSize:11,color:'#ff6b6b',marginTop:8}}>{pillarsError}</div>}
          <div className="ob-btn-row">
            <button className="ob-btn ob-btn-secondary" onClick={goBack}>← Back</button>
            <button className="ob-btn ob-btn-primary" onClick={savePillarsAndNext} disabled={pillarsSaving}>
              {pillarsSaving ? 'Saving…' : 'Looks good →'}
            </button>
          </div>
        </>}

        {/* STEP 3 — BRAND */}
        {step === 3 && <>
          <div className="ob-card-title">Your Brand</div>
          <div className="ob-card-sub">
            Pick your brand colours, add your logo and website, then hit confirm. You can update these any time in Brand Hub.
          </div>
          {/* Brand Hub's own colour fields, so "not set" looks and saves the same. */}
          <div className="ob-label">Brand colours <span style={{color:'var(--text-muted)'}}>(optional)</span></div>
          <BrandColourField label="Primary"          value={primaryColour}   onChange={setPrimaryColour} />
          <BrandColourField label="Secondary / Dark" value={secondaryColour} onChange={setSecondaryColour} />
          <div className="ob-brand-row">
            <div style={{width:36,height:36,borderRadius:7,background:'var(--bg-card2)',overflow:'hidden',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>
              {logoUrl
                ? <img src={logoUrl} alt="Logo" style={{width:'100%',height:'100%',objectFit:'contain'}} />
                : '✈'}
            </div>
            <div className="ob-brand-info">
              <div className="ob-brand-label">Logo</div>
              <div className="ob-brand-val">{logoUrl ? 'Logo uploaded' : 'No logo uploaded yet'}</div>
            </div>
            {logoUrl && <span className="ob-brand-tag">Confirmed</span>}
          </div>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/svg+xml"
            style={{display:'none'}}
            onChange={handleLogoUpload}
          />
          {logoError && <div style={{fontSize:11,color:'#ff6b6b',marginTop:4}}>{logoError}</div>}
          <div data-testid="onboarding-logo-dropzone"
            className={`pl-dropzone${logoDrop.dragging ? ' dragging' : ''}`}
            {...logoDrop.handlers}
            style={{flex:'none',padding:'12px 14px',gap:6,marginTop:6,cursor:'default',alignItems:'stretch'}}>
          {logoUrl
            ? <div style={{display:'flex',alignItems:'center',gap:10}}>
                <img src={logoUrl} alt="Logo preview" style={{height:48,maxWidth:160,objectFit:'contain',borderRadius:6,border:'1px solid rgba(255,255,255,0.12)'}} />
                <button
                  className="ob-btn ob-btn-secondary"
                  style={{fontSize:11,padding:'5px 12px'}}
                  disabled={logoUploading}
                  onClick={() => logoInputRef.current.click()}>
                  {logoUploading ? 'Uploading…' : 'Change'}
                </button>
              </div>
            : <div
                className="ob-upload-zone"
                style={logoUploading ? {opacity:0.5,pointerEvents:'none',margin:0} : {margin:0}}
                onClick={() => logoInputRef.current.click()}>
                {logoUploading ? 'Uploading…' : '+ Upload logo (PNG or SVG)'}
              </div>
          }
          <div className="pl-dropzone-sub">or drag and drop your logo here</div>
          </div>
          <div className="ob-field" style={{marginTop:16}}>
            <div className="ob-label">What's your website? <span style={{color:'var(--text-muted)'}}>(optional)</span></div>
            <input className="ob-input" type="url" inputMode="url" placeholder="https://yourbusiness.com"
              value={website} onChange={e => setWebsite(e.target.value)} />
          </div>
          {brandError && <div role="alert" style={{fontSize:11,color:'#ff6b6b',marginTop:4}}>{brandError}</div>}
          <div className="ob-btn-row">
            <button className="ob-btn ob-btn-secondary" onClick={goBack}>← Back</button>
            <button className="ob-btn ob-btn-primary" onClick={saveBrandAndNext} disabled={brandSaving}>
              {brandSaving ? 'Saving…' : 'Confirm Brand →'}
            </button>
          </div>
        </>}

        {/* STEP 4 — INSTAGRAM + FACEBOOK */}
        {step === 4 && <>
          <div className="ob-card-title">Connect Instagram and Facebook</div>
          <div className="ob-card-sub">
            Connect your accounts so we can publish posts directly on your behalf. You stay in control — nothing goes out without your approval first.
          </div>
          {[
            { key:'ig', name:'Instagram', connected:igConnected,
              copy:'We ask permission to publish your posts, see your basic profile, and show how your posts perform. We never read your DMs.',
              icon: (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              ) },
            { key:'fb', name:'Facebook', connected:fbConnected,
              copy:'We ask permission to see your Pages, publish your posts, and show how your posts perform. We never read your messages.',
              icon: (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
              </svg>
              ) },
          ].map(p => (
            <div key={p.key} className="ob-ig-box" style={{marginBottom:12}}>
              <div className="ob-ig-icon">{p.icon}</div>
              {!p.connected ? (
                <>
                  <div style={{fontSize:12,color:'var(--text-secondary)',textAlign:'center',maxWidth:340,lineHeight:1.6}}>
                    {p.copy}
                  </div>
                  <button className="ob-btn ob-btn-primary" style={{padding:'11px 32px',fontSize:14}}
                    disabled={!!metaConnecting}
                    onClick={() => connectMeta(p.key)}>
                    {metaConnecting === p.key ? 'Waiting for ' + p.name + '…' : 'Connect ' + p.name}
                  </button>
                  {metaError[p.key] && <div style={{fontSize:11,color:'#ff6b6b'}}>{metaError[p.key]}</div>}
                </>
              ) : (
                <div className="ob-ig-connected">
                  <span>✓</span>
                  <span>{p.name} connected</span>
                </div>
              )}
            </div>
          ))}
          <div className="ob-btn-row">
            <button className="ob-btn ob-btn-secondary" onClick={goBack}>← Back</button>
            <button className="ob-btn ob-btn-primary" onClick={goNext}
              disabled={!(igConnected || fbConnected)} style={{opacity: (igConnected || fbConnected) ? 1 : 0.4, cursor: (igConnected || fbConnected) ? 'pointer' : 'not-allowed'}}>
              Next →
            </button>
            {!(igConnected && fbConnected) && <button className="ob-btn-skip" onClick={goNext}>Skip for now</button>}
          </div>
        </>}

        {/* STEP 5 — FILL YOUR BRAND HUB. Both buttons hand off to Brand Hub's own
            autofill (apply, review, Save); nothing is filled or saved here. */}
        {step === 5 && <>
          <div className="ob-card-title">Fill your Brand Hub</div>
          <div className="ob-card-sub">
            We can read your website and screenshots of your Instagram or Facebook to fill in your Brand Hub. You review everything before it's saved.
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {!savedWebsite && (
              <div className="ob-field" style={{marginBottom:0}}>
                <div className="ob-label">Your website</div>
                <input className="ob-input" type="url" inputMode="url" placeholder="https://yourbusiness.com"
                  aria-label="Your website"
                  value={fillUrl} onChange={e => setFillUrl(e.target.value)} />
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>
                  Or add it in the Brand step.
                </div>
              </div>
            )}
            <button className="ob-btn ob-btn-primary" style={{minHeight:48,width:'100%',fontSize:14}}
              disabled={!fillFromUrl}
              onClick={() => onOpenBrandHub({ mode: 'website', url: fillFromUrl })}>
              🌐 {fillFromUrl ? `Fill from ${displayWebsite(fillFromUrl)}` : 'Fill from my website'}
            </button>
            <button className="ob-btn ob-btn-secondary" style={{minHeight:48,width:'100%',fontSize:14}}
              onClick={() => onOpenBrandHub({ mode: 'screenshots' })}>
              📸 Fill from Instagram/Facebook screenshots
            </button>
          </div>
          <div className="ob-btn-row">
            <button className="ob-btn ob-btn-secondary" onClick={goBack}>← Back</button>
            <button className="ob-btn ob-btn-primary" onClick={goNext}>Finish setup →</button>
            <button className="ob-btn-skip" onClick={goNext}>Skip for now</button>
          </div>
        </>}

      </div>
    </div>
  );
}

// ============================================================
// BRAND HUB PANEL — Session 2
// ============================================================
function TagInput({ tags, setTags, placeholder }) {
  const [val, setVal] = useState('');
  const add = () => {
    const t = val.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setVal('');
  };
  return (
    <div className="bh-tags" onClick={e => e.currentTarget.querySelector('input').focus()}>
      {tags.map(t => (
        <span key={t} className="bh-tag">
          {t}
          <span
            className="bh-tag-remove"
            role="button"
            tabIndex={0}
            aria-label={`Remove ${t}`}
            title={`Remove ${t}`}
            onClick={() => setTags(tags.filter(x => x !== t))}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTags(tags.filter(x => x !== t)); } }}
          >×</span>
        </span>
      ))}
      <input className="bh-tag-input" value={val} placeholder={placeholder}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }} />
    </div>
  );
}

// ── Posting days ─────────────────────────────────────────────────────────────
//
// brand_profile.posting_days is the set of weekdays a client publishes on, and
// posting_frequency is the chip beside it. They are ONE setting stored in two
// columns, and the backend refuses to store them disagreeing (posting-days.js,
// live since 2026-09-27). So the day row below is the thing the client edits
// and the frequency button follows it — never the other way round, which is
// how the two would drift.
//
// 0 = Sunday … 6 = Saturday, the numbering the column uses, which is
// JavaScript's Date#getUTCDay and what slotsForDay reads. The ROW is Monday
// first because that is how a week reads to a person; Sunday is 0 and sits
// last on screen. Those two orderings are deliberately independent — the
// number is for the database, the position is for the eye.
const POSTING_DAY_ROW = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 0, label: 'Sun' },
];

// Which days each frequency button ticks when it is tapped. Weekday-first and
// spread out rather than consecutive: a client posting three times a week
// means Mon/Wed/Fri, not Mon/Tue/Wed.
//
// ALL SEVEN COUNTS, not just 3/4/5/7. The first four are unchanged — they are
// what clients have been saving — and 1, 2 and 6 are new. Those three counts
// previously had no button, which meant unticking a single day from Daily left
// six ticked, no chip selected, and Save silently refusing: 10 of 11 live
// clients load on seven days, so the most obvious first action in this whole
// panel was the one that dead-ended.
const FREQ_DEFAULT_DAYS = {
  '1x / week': [1],
  '2x / week': [1, 4],
  '3x / week': [1, 3, 5],
  '4x / week': [1, 2, 4, 5],
  '5x / week': [1, 2, 3, 4, 5],
  '6x / week': [1, 2, 3, 4, 5, 6],
  'Daily':     [1, 2, 3, 4, 5, 6, 0],
};

// …and back the other way: how many ticked days selects which button.
//
// TOTAL OVER 1-7 NOW. It used to hold only 3, 4, 5 and 7, and the gaps were
// deliberate — the argument was that inventing chips would be a product
// decision smuggled into a lookup table. That was the wrong call: the gaps did
// not stop anyone choosing six days, they just stopped anyone SAVING six days,
// silently, with a disabled button and a grey note nobody could see. Every
// count a client can tick now has a name.
//
// Seven is 'Daily' rather than '7x / week' because that is the word the app has
// always used for it, it is the value already stored for three live clients,
// and it is the spelling the backend validator maps to seven days.
const FREQ_FOR_DAY_COUNT = {
  1: '1x / week',
  2: '2x / week',
  3: '3x / week',
  4: '4x / week',
  5: '5x / week',
  6: '6x / week',
  7: 'Daily',
};

// The only count with no name, because it is not a cadence — it is a client
// who has unticked everything and has not finished choosing yet.
const POSTING_DAYS_NOTE = 'Pick at least one posting day.';

// The ticked days in row order, so the array reads Mon-first everywhere it is
// logged, inspected or sent. Order is not meaningful to the column — the
// migration says so explicitly — but an arbitrary order is harder to read.
const inRowOrder = (days) => POSTING_DAY_ROW.map(d => d.n).filter(n => days.includes(n));

// ── Applying a brand brief to Brand Hub's form ─────────────────────────────
// One path for both producers of a brief: the discovery-call transcript
// (Build Brief, overwrites) and the website autofill (onlyEmpty: fills a field
// only if the form has nothing in it). `fields` maps each brief key to the
// form's current value and its setter, in schema order; `list` marks fields
// whose brief value counts only when it is a non-empty array — the same test
// the per-field `if (d.x?.length)` lines used, so Build Brief is unchanged.
// Returns the keys actually filled.
//
// onlyEmpty also skips any field flagged autofill: false (ignored in
// overwrite mode): posting frequency and best times (frequency drives the
// posting days, so a website guess must not move them), and emoji usage and
// photo preference (a website can't state them, so any value is a guess).
export const briefFieldIsEmpty = (v) =>
  v == null || (Array.isArray(v) ? v.length === 0 : (typeof v === 'string' ? v.trim() === '' : false));

// Y in "We filled X of Y fields": the schema's fields (as the backend sends
// them) minus those autofill may never set (autofill: false).
export function autofillTotal(schemaFields, fields) {
  return schemaFields.filter(k => fields[k]?.autofill !== false).length;
}

// Popup lines for the social sources the autofill read. The backend decides
// (admin-only for now); a source it did not even try comes back
// 'not_available' and gets no line at all.
export function autofillSocialLines(social) {
  const out = [];
  for (const [key, label] of [['instagram', 'Instagram'], ['facebook', 'Facebook']]) {
    const s = social?.[key];
    if (!s?.status || s.status === 'not_available') continue;
    const who = key === 'instagram' ? (s.handle ? ` @${s.handle}` : '') : (s.name ? ` ${s.name}` : '');
    if (s.status === 'ok') out.push({ key, ok: true, text: `✓ Read ${label}${who} (${s.postsRead || 0} posts)` });
    else if (s.status === 'not_connected') out.push({ key, ok: false, text: `${label} not connected — connect it in Connections` });
    else out.push({ key, ok: false, text: `Couldn’t read ${label}${s.error ? `: ${s.error}` : ''}` });
  }
  return out;
}

// "~3.5 posts/week, mostly Mon, Wed, mornings" — informational only, never
// filled into Posting Frequency / Best Times. null when there is nothing to say.
const HABIT_BUCKET = { morning: 'mornings', afternoon: 'afternoons', evening: 'evenings', night: 'nights' };
export function autofillHabitsText(h) {
  if (!h) return null;
  const parts = [];
  if (typeof h.postsPerWeek === 'number') parts.push(`~${h.postsPerWeek} posts/week`);
  if (Array.isArray(h.topDays) && h.topDays.length) parts.push(`mostly ${h.topDays.join(', ')}`);
  if (HABIT_BUCKET[h.timeOfDay]) parts.push(HABIT_BUCKET[h.timeOfDay]);
  return parts.length ? parts.join(', ') : null;
}

// A chip field's values (array or saved comma string) mapped onto the chips'
// exact spelling: trimmed, matched case-insensitively, duplicates removed
// case-insensitively in first-seen order. The chips match by ===, so a saved
// "professional" beside "Professional" lit nothing and could never be
// unticked — and kept the field non-empty, so autofill skipped it.
//
// Off-list values are dropped by default — that is for AI / autofill answers,
// which may only pick chips. Loading a saved row passes { keepUnknown: true }:
// saved rows hold real custom values ("Teal Accent") that feed image prompts,
// so they are kept (first spelling wins) and rendered as custom chips.
export function normaliseChips(values, allowed, { keepUnknown = false } = {}) {
  const list = Array.isArray(values) ? values : (typeof values === 'string' ? values.split(',') : []);
  const byKey = new Map(allowed.map(a => [a.toLowerCase(), a]));
  const out = [];
  const seen = new Set();
  for (const v of list) {
    if (typeof v !== 'string') continue;
    const trimmed = v.trim();
    const key = trimmed.toLowerCase();
    if (!key || seen.has(key)) continue;
    const chip = byKey.get(key) || (keepUnknown ? trimmed : null);
    if (!chip) continue;
    seen.add(key);
    out.push(chip);
  }
  return out;
}

// The off-list values a chip row must still show (see normaliseChips): each
// one renders after the standard chips, lit, dashed, and unticking it removes
// it for good — nothing in state is invisible.
function CustomChips({ values, allowed, onRemove }) {
  return values.filter(v => !allowed.includes(v)).map(v => (
    <div key={`custom:${v}`} className="bh-chip active bh-chip-custom" title="Custom — saved earlier"
      onClick={() => onRemove(v)}>
      {v}
    </div>
  ));
}

// `norm`, when a field has one, is applied to the brief's value before the
// present check and the set — so a chip field whose answer is all off-list
// words counts as not given: not filled, not tagged, not counted.
export function applyBrief(d, fields, { onlyEmpty = false } = {}) {
  const filled = [];
  if (!d || typeof d !== 'object') return filled;
  for (const [key, f] of Object.entries(fields)) {
    const v = f.norm ? f.norm(d[key]) : d[key];
    const present = f.list ? !!v?.length : !!v;
    if (!present) continue;
    if (onlyEmpty && f.autofill === false) continue;
    if (onlyEmpty && !briefFieldIsEmpty(f.value)) continue;
    f.set(v);
    filled.push(key);
  }
  return filled;
}

// A logo drop → { file } or { error }. One image only; anything else is turned
// away here with a message, before it reaches uploadLogo. Which image types the
// logo bucket takes is still the backend's call (same errors as the button).
export function singleImageFromDrop(fileList) {
  const files = Array.from(fileList || []);
  if (!files.length) return {};
  if (files.length > 1) return { error: 'Drop one logo at a time.' };
  if (!(files[0].type || '').startsWith('image/')) return { error: 'Logos must be an image file (PNG, SVG, JPG or WEBP).' };
  return { file: files[0] };
}

// A file dropped anywhere that isn't a drop zone would otherwise make the
// browser open it in this tab and leave the dashboard. Every drop zone
// preventDefaults its own dragover; anything still not prevented when the event
// reaches `target` (the window) is outside a zone, so it gets "none" as the drop
// effect and its drop is swallowed. Only drags carrying files are touched —
// the Photo Library and Scheduler card reordering drag no files and are left
// alone. Returns the cleanup.
export function blockStrayFileDrops(target) {
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragOver = (e) => {
    if (!hasFiles(e) || e.defaultPrevented) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none';
  };
  const onDrop = (e) => { if (hasFiles(e)) e.preventDefault(); };
  target.addEventListener('dragover', onDragOver);
  target.addEventListener('drop', onDrop);
  return () => {
    target.removeEventListener('dragover', onDragOver);
    target.removeEventListener('drop', onDrop);
  };
}

// Drag state + handlers for a .pl-dropzone, the same behaviour as Brand Hub's
// "Fill from screenshots" zone: highlight while dragging over, ignore children's
// dragleave, and do nothing (no highlight, no drop) while disabled. The zone
// always preventDefaults, so a drop on it never opens the file in the tab.
function useFileDropZone({ disabled, onFiles }) {
  const [dragging, setDragging] = useState(false);
  return {
    dragging,
    handlers: {
      onDragOver: (e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
        if (!disabled) setDragging(true);
      },
      onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); },
      onDrop: (e) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled) onFiles(e.dataTransfer?.files);
      },
    },
  };
}

// One brand colour in the Visual tab. '' is "not set": the swatch is striped
// and clicking it opens the picker; once set, "clear" puts it back to ''. The
// picker itself needs a valid colour, so it opens on black while unset — that
// stand-in is never stored.
function BrandColourField({ label, value, onChange }) {
  return (
    <div className="bh-colour-row">
      <label className={`bh-colour-swatch${value ? '' : ' unset'}`} style={value ? { background: value } : undefined}
        title={value || 'Not set — pick a colour'}>
        <input type="color" className="bh-colour-input" aria-label={`${label} colour`}
          value={value || '#000000'} onChange={e => onChange(e.target.value)} />
      </label>
      <div>
        <div className={`bh-colour-val${value ? '' : ' unset'}`}>{value || 'Not set — pick a colour'}</div>
        <div className="bh-colour-name">{label}</div>
      </div>
      {value && (
        <button type="button" className="bh-colour-clear" aria-label={`Clear ${label} colour`} onClick={() => onChange('')}>clear</button>
      )}
    </div>
  );
}

// Where a fresh Brand Hub starts. Each load resets to these and a field the
// profile leaves empty falls back to them, so the two cannot drift apart.
// The colours start '' (not set): they used to start as Autopilot Digital's
// own colours, which a client who never picked any then saved on their first
// Save, and the server put them into every image prompt.
const BRAND_HUB_INITIAL = {
  emojiPref:       'Minimal',
  primaryColour:   '',
  secondaryColour: '',
  accentColour:    '',
  photoPref:       'Mix of real + generated',
  brandLogoPos:    'bottom-right',
  contentMix:      { educational: 15, product: 20, lifestyle: 20, promo: 15, socialProof: 15, behindScenes: 15 },
  postingGoal:     'Brand Awareness',
};

// Brand Hub's ✨ Autofill section: '1' = collapsed, '0' = open. Nothing else is stored.
const AUTOFILL_COLLAPSED_KEY = 'bh_autofill_collapsed';

// "Create your first post" — shown by Brand Hub after a successful Save for a
// Self-Serve client with no posts yet. Escape and the backdrop both mean
// Later. Focus starts on the first choice.
export function FirstPostPrompt({ onUseOwnPhotos, onCreateWithAI, onLater }) {
  const firstRef = useRef(null);
  useEffect(() => { if (firstRef.current) firstRef.current.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onLater(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onLater]);
  return (
    <div className="ap-confirm-overlay" onClick={onLater}
      role="dialog" aria-modal="true" aria-labelledby="bh-first-post-title">
      <div className="ap-confirm" onClick={e => e.stopPropagation()}>
        <div className="ap-confirm-title" id="bh-first-post-title">
          ✓ Brand Hub saved! Next: create your first post.
        </div>
        <div className="gs-first-post-choices">
          <button ref={firstRef} type="button" className="ob-btn ob-btn-primary" onClick={onUseOwnPhotos}>
            📷 Use my own photos
          </button>
          <div className="gs-muted" style={{ marginTop: -4 }}>Upload a photo, then tap “Use in Post” on it to make a post.</div>
          <button type="button" className="ob-btn ob-btn-secondary" onClick={onCreateWithAI}>
            ✨ Create one with AI
          </button>
          <div className="gs-muted" style={{ marginTop: -4 }}>No photos yet? AI can make your first image.</div>
        </div>
        <div className="ap-confirm-actions">
          <button type="button" className="ap-confirm-btn ap-confirm-btn-cancel" style={{ minHeight: 44 }} onClick={onLater}>Later</button>
        </div>
      </div>
    </div>
  );
}

export function BrandHubPanel({ isSelfServe, isAdmin = false, clientId, stripeCustomerId, hasTwoX, imgBrandKeywords, setImgBrandKeywords, imgAvoidTerms, setImgAvoidTerms, lightingMood, setLightingMood, onBizNameChange, onLogoChange, autofillIntent = null, onAutofillIntentConsumed, onNavigate }) {
  // Phase 4: the panel's reads and writes now carry the admin act-as-client
  // override, and the load effect must re-run when that context changes.
  const { viewingAsClientId } = useViewingAs();
  const [tab, setTab] = useState(0);
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  // "Create your first post" pop-up — see maybePromptFirstPost.
  const [firstPostPrompt, setFirstPostPrompt] = useState(false);
  const firstPostDismissKey = `ap_first_post_prompt_dismissed_${viewingAsClientId || clientId}`;
  // The client whose profile is on screen, set only once its read succeeds.
  // Save is off unless it matches the client being viewed.
  const [loadedFor, setLoadedFor] = useState(null);
  const loadedForViewed = loadedFor !== null && loadedFor === (viewingAsClientId || clientId);

  // Build Brief from Call
  const [briefTranscript, setBriefTranscript] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState('');
  const [briefSuccess, setBriefSuccess] = useState(false);
  // Fill from my website — offered to every client; the backend gate is
  // WEBSITE_READER_ENABLED_FOR_ALL.
  // aiFilled: brief key → JSON of the value the autofill put there. A field
  // shows "AI-filled — check me" while its value still equals that, so any
  // edit clears the tag without touching every input's onChange. Save clears
  // them all. autofillRan turns on the "still empty" highlight.
  const [aiFilled, setAiFilled] = useState({});
  const [autofillRan, setAutofillRan] = useState(false);
  const [autofillLoading, setAutofillLoading] = useState(false);
  const [autofillResult, setAutofillResult] = useState(null); // popup contents | null
  const [autofillAskUrl, setAutofillAskUrl] = useState(false);
  const [autofillUrlInput, setAutofillUrlInput] = useState('');
  // Fill from screenshots — the client's own IG/FB screenshots, resized here
  // and sent once; the backend gate is SCREENSHOT_AUTOFILL_ENABLED_FOR_ALL.
  // The files stay in memory only until they are sent or cleared.
  const [shotFiles, setShotFiles] = useState([]);
  const [shotNote, setShotNote] = useState('');
  const [shotLoading, setShotLoading] = useState(false);
  const [shotDragging, setShotDragging] = useState(false);
  // The ✨ Autofill section around both — open the first time, collapsed
  // after a successful run, remembered per browser. Storage failures just
  // mean the default (open).
  const [autofillOpen, setAutofillOpenState] = useState(() => {
    try { return localStorage.getItem(AUTOFILL_COLLAPSED_KEY) !== '1'; } catch { return true; }
  });
  const setAutofillOpen = (open) => {
    setAutofillOpenState(open);
    try { localStorage.setItem(AUTOFILL_COLLAPSED_KEY, open ? '0' : '1'); } catch { /* default next time */ }
  };
  const autofillSectionRef = useRef(null);
  const shotInputRef = useRef(null);
  // The autofill intent last acted on — guards against running one twice.
  const consumedIntentRef = useRef(null);
  // Build Brief gate
  const [briefUnlocked, setBriefUnlocked] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefPwVisible, setBriefPwVisible] = useState(false);
  const [briefPwInput, setBriefPwInput] = useState('');
  const [briefPwError, setBriefPwError] = useState('');
  const [briefPwLoading, setBriefPwLoading] = useState(false);

  // Business Basics
  const [bizName, setBizName] = useState('');
  const [tagline, setTagline] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');
  const [website, setWebsite] = useState('');
  const [audience, setAudience] = useState('');

  // Voice & Tone
  const TONES = ['Friendly','Warm','Professional','Bold','Playful','Minimal','Inspirational','Witty','Calm','Authentic'];
  const [tones, setTones] = useState([]);
  const [wordsUse, setWordsUse] = useState([]);
  const [wordsAvoid, setWordsAvoid] = useState([]);
  const [emojiPref, setEmojiPref] = useState(BRAND_HUB_INITIAL.emojiPref);
  const [voiceNotes, setVoiceNotes] = useState('');

  // Visual Identity
  const [primaryColour, setPrimaryColour] = useState(BRAND_HUB_INITIAL.primaryColour);
  const [secondaryColour, setSecondaryColour] = useState(BRAND_HUB_INITIAL.secondaryColour);
  const [accentColour, setAccentColour] = useState(BRAND_HUB_INITIAL.accentColour);
  const VISUAL_STYLES = ['Warm','Moody','Bright','Minimal','Bold','Earthy','Luxury','Raw','Soft','Dramatic'];
  const [visualStyles, setVisualStyles] = useState([]);
  const PHOTO_PREFS = ['Real photos only','Mix of real + generated','Generated ok'];
  const [photoPref, setPhotoPref] = useState(BRAND_HUB_INITIAL.photoPref);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const logoFileInputRef = useRef(null);
  const [brandLogoPos, setBrandLogoPos] = useState(BRAND_HUB_INITIAL.brandLogoPos);

  // Content Strategy
  const [pillars, setPillars] = useState(DEFAULT_PILLARS);
  const [newPillarInput, setNewPillarInput] = useState('');
  // Ordered by how often a client posts, so the row reads as a scale.
  const FREQ = ['1x / week','2x / week','3x / week','4x / week','5x / week','6x / week','Daily'];
  // Daily, not '4x / week'. This is what a profile with NO saved
  // posting_frequency falls back to, and the old default meant such a client
  // was shown "4x / week" having never chosen it — a setting invented by a
  // useState call. NULL frequency has always MEANT every day everywhere else
  // (the backend's daysPerWeekForFrequency reads it that way, and NULL
  // posting_days is every day), so Daily is the honest default and the only
  // one that agrees with what the scheduler actually does.
  const [freq, setFreq] = useState('Daily');

  // The ticked days, or null for "never configured".
  //
  // NULL IS NOT THE EMPTY ARRAY, and the difference decides whether Save is
  // blocked. null means nothing has ever been picked — the state an
  // unrecognised legacy frequency loads into, since it has no default days —
  // and it saves as posting_days: null, which the column reads as every day.
  // [] means the client unticked their way down to nothing, which is a state
  // no button matches and which must be fixed before saving. Collapsing the
  // two would either block a save nobody broke or silently turn "no days" into
  // "every day".
  const [postingDays, setPostingDays] = useState(FREQ_DEFAULT_DAYS['Daily']);
  const tickedDays = postingDays || [];

  // What posting_days actually holds in the database, so the save can work out
  // which days are being TAKEN AWAY — `postingDays` above is what is ticked
  // right now and cannot answer that on its own.
  //
  // `undefined` until the profile loads, and that is load-bearing rather than
  // lazy: null already means "every day", so it cannot also mean "not read
  // yet". removedPostingDays treats undefined as "nothing known to be removed"
  // and stays silent, which is the right answer for a panel that has not seen
  // the stored value.
  const [savedPostingDays, setSavedPostingDays] = useState(undefined);

  // The pending confirm: { message, count, proceed } or null.
  const [removedDaysPrompt, setRemovedDaysPrompt] = useState(null);

  // Save is blocked ONLY when the client has unticked every day.
  //
  // This used to be "no button matches the count", which caught 1, 2 and 6 as
  // well — see FREQ_FOR_DAY_COUNT for why that was wrong. Every count from 1
  // to 7 is now a cadence with a name, so the single remaining unsaveable
  // state is zero: not a cadence, just someone mid-edit. Writing it would mean
  // posting_days: null on the wire, which the column reads as EVERY day — the
  // exact opposite of an empty row, which is why it cannot simply be allowed
  // through.
  //
  // null still means "not configured" and is still not blocked; only a real
  // empty array is. See the comment on postingDays.
  const postingDaysBlocked = Array.isArray(postingDays) && postingDays.length === 0;

  // THE PAIR CHANGES THROUGH THESE TWO FUNCTIONS AND NOWHERE ELSE. Two pieces
  // of state that must agree is how a screen starts lying about what it will
  // save, so every edit goes through one of them.
  //
  // Tapping a frequency button ticks that button's days.
  const pickFrequency = (f) => {
    setFreq(f);
    // An unrecognised legacy chip (freqOptions' extra) has no default days, so
    // the row is left exactly as it was rather than guessed at.
    if (FREQ_DEFAULT_DAYS[f]) setPostingDays(FREQ_DEFAULT_DAYS[f]);
  };

  // Ticking or unticking a day selects the button that matches the new count,
  // or clears the selection when no button does. A SWAP KEEPS THE BUTTON: the
  // count is what selects it, so trading Wednesday for Thursday on 3x / week
  // leaves 3x / week selected, which is the whole point of driving the button
  // off the count rather than off which days were picked.
  const togglePostingDay = (n) => {
    const next = inRowOrder(
      tickedDays.includes(n) ? tickedDays.filter(d => d !== n) : [...tickedDays, n]
    );
    setPostingDays(next);
    setFreq(FREQ_FOR_DAY_COUNT[next.length] || '');
  };
  /* "2x Growth Boost" was a chip in this list until it was retired. It is an
     add-on, not a posting frequency: selecting it bought nothing, set nothing
     and changed nothing — posting_frequency is stored but never read by
     generation or scheduling, so the chip only ever looked like a setting.
     Profiles saved while it existed still hold that string, so render it as an
     extra chip rather than dropping it. Removing an option must not silently
     rewrite someone's saved value, and a chip row with nothing selected reads
     as a bug. It disappears for that profile the moment they pick anything
     else. */
  // The `!freq` guard is new: freq is '' while the day row matches no button,
  // and without it that empty string became an extra, blank, unlabelled chip.
  const freqOptions = (!freq || FREQ.includes(freq)) ? FREQ : [...FREQ, freq];

  // Legacy posting_frequency values that were never a frequency.
  //
  // The comment above kept "2x Growth Boost" renderable as an extra chip so
  // that removing the option would not silently rewrite a saved value or leave
  // the row with nothing selected. That reasoning held while there was no real
  // 2x control. There is one now, immediately to the right of these chips, and
  // a dead "2x Growth Boost" chip sitting beside a live "2x / day" chip is the
  // confusion that decision was trying to avoid, not an instance of avoiding it.
  //
  // So these display as Daily — which is what an account on one of them has
  // actually been getting, since posting_frequency is stored and never read by
  // generation or scheduling. Nothing is rewritten in the database by looking:
  // this is display only, and the value is not saved unless the user presses
  // Save, at which point Daily is what they can see and therefore what they
  // mean. The entitlement itself lives in two_x_daily and is untouched.
  const LEGACY_2X_FREQUENCIES = ['2x Daily', '2x Growth Boost'];
  const normaliseFreq = (v) => (LEGACY_2X_FREQUENCIES.includes(v) ? 'Daily' : v);

  // The 2x / day chip's checkout. Same call the Scheduler's upsell button
  // makes, differing only in returnPanel so Stripe sends the customer back
  // here. Deliberately NOT a variant of setFreq: this chip buys an add-on and
  // writes nothing to posting_frequency.
  const [twoXCheckoutLoading, setTwoXCheckoutLoading] = useState(false);
  const handleTwoXChipClick = async () => {
    // Owned, or a checkout already in flight. Either way there is nothing to
    // buy — this is the guard that makes a second purchase impossible rather
    // than merely unlikely.
    if (hasTwoX || twoXCheckoutLoading) return;
    setTwoXCheckoutLoading(true);
    try {
      const res = await authedFetch('/api/create-2x-checkout', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ returnPanel: 'brand' }),
      });
      const json = await res.json();
      if (json.url) window.location.href = json.url;
      else setTwoXCheckoutLoading(false);
    } catch {
      setTwoXCheckoutLoading(false);
    }
  };
  const [hashtagSets, setHashtagSets] = useState([]);
  const [bestTimes, setBestTimes] = useState('');

  // Business Context
  const [keyProducts, setKeyProducts] = useState([]);
  const [upcomingPromos, setUpcomingPromos] = useState('');
  const [avoidTopics, setAvoidTopics] = useState([]);
  const [competitors, setCompetitors] = useState('');

  // Image Generation — state is lifted to AutopilotDashboard and received as props
  const LIGHTING_MOODS = ['Natural Light','Golden Hour','Moody & Dark','Bright & Airy','Warm & Soft','Cool & Clean','Dramatic','Flat Lay'];

  // Content Mix (post-type ratios)
  const [contentMix, setContentMix] = useState(BRAND_HUB_INITIAL.contentMix);
  const setMixVal = (key, val) => setContentMix(m => ({ ...m, [key]: Number(val) }));
  const contentMixTotal = Object.values(contentMix).reduce((a, b) => a + b, 0);

  // Active Promotions
  const [activePromotions, setActivePromotions] = useState([]);
  const addPromotion = () => setActivePromotions(p => [...p, { id: Date.now(), title: '', description: '', startDate: '', endDate: '', maxFrequency: 'As needed' }]);
  const removePromotion = id => setActivePromotions(p => p.filter(x => x.id !== id));
  const updatePromotion = (id, key, val) => setActivePromotions(p => p.map(x => x.id === id ? { ...x, [key]: val } : x));

  // Posting Goal
  const POSTING_GOALS = ['Brand Awareness', 'Drive Traffic', 'Build Community', 'Generate Leads', 'Increase Sales'];
  const [postingGoal, setPostingGoal] = useState(BRAND_HUB_INITIAL.postingGoal);

  // Preferred Post Times

  // FAL Style Keywords
  const [falStyleKeywords, setFalStyleKeywords] = useState('');

  // Negative Prompt Additions
  const [negativePromptAdditions, setNegativePromptAdditions] = useState('');

  // Check Build Brief admin unlock on mount
  useEffect(() => {
    const expiry = localStorage.getItem('ap_admin_unlocked_expiry');
    if (expiry && Date.now() < parseInt(expiry)) {
      setBriefUnlocked(true);
    } else {
      localStorage.removeItem('ap_admin_unlocked');
      localStorage.removeItem('ap_admin_unlocked_expiry');
    }
  }, []);

  const handleBriefUnlock = async () => {
    setBriefPwLoading(true);
    setBriefPwError('');
    try {
      const res = await authedFetch(`${BACKEND}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: briefPwInput }),
      });
      if (!res.ok) {
        setBriefPwError('Incorrect password.');
        setBriefPwLoading(false);
        return;
      }
      localStorage.setItem('ap_admin_unlocked', '1');
      localStorage.setItem('ap_admin_unlocked_expiry', Date.now() + 5 * 24 * 60 * 60 * 1000);
      setBriefUnlocked(true);
      setBriefPwVisible(false);
      setBriefOpen(true);
    } catch {
      setBriefPwError('Could not reach backend.');
    }
    setBriefPwLoading(false);
  };

  const handleBriefToggle = () => {
    if (!briefUnlocked) {
      setBriefPwVisible(v => !v);
    } else {
      setBriefOpen(v => !v);
    }
  };

  const toArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
    return [];
  };

  // Load saved Brand Hub data on mount.
  //
  // Phase 4: was supabase.from('brand_profile').select('*'). RLS scoped that to
  // the signed-in user, so it returned the ADMIN's brand while viewing a client.
  // The '*' was deliberate — it auto-loaded columns added to the schema later —
  // and that property is given up here in exchange for an explicit server-side
  // allowlist, since brand_profile also holds stripe_customer_id and plan. The
  // setters below stay optional-chaining-guarded, so a column the endpoint does
  // not return is still inert.
  //
  // Deps include viewingAsClientId for the Phase 3 reason: clientId is the
  // signed-in user and never changes when an admin enters or leaves a client
  // view, so keyed on [clientId] alone this would show the wrong brand.
  //
  // The panel is never remounted on a View As switch either, so every field is
  // put back where a fresh panel starts BEFORE the read, each field falls back
  // to that same start value when the profile leaves it empty, and Save stays
  // off until this client's read has landed. A slow or failed read for client
  // B can then neither show nor save client A's values.
  const resetBrandFields = () => {
    setBizName(''); setTagline(''); setCategory(''); setLocation(''); setWebsite(''); setAudience('');
    setTones([]); setWordsUse([]); setWordsAvoid([]); setEmojiPref(BRAND_HUB_INITIAL.emojiPref); setVoiceNotes('');
    setPrimaryColour(BRAND_HUB_INITIAL.primaryColour);
    setSecondaryColour(BRAND_HUB_INITIAL.secondaryColour);
    setAccentColour(BRAND_HUB_INITIAL.accentColour);
    setVisualStyles([]); setPhotoPref(BRAND_HUB_INITIAL.photoPref);
    setLogoUrl(''); setLogoError(''); setBrandLogoPos(BRAND_HUB_INITIAL.brandLogoPos);
    setPillars(DEFAULT_PILLARS);
    setFreq('Daily'); setPostingDays(FREQ_DEFAULT_DAYS['Daily']); setSavedPostingDays(undefined); setRemovedDaysPrompt(null); setFirstPostPrompt(false);
    setHashtagSets([]); setBestTimes(''); setKeyProducts([]); setUpcomingPromos(''); setAvoidTopics([]); setCompetitors('');
    setContentMix(BRAND_HUB_INITIAL.contentMix); setActivePromotions([]); setPostingGoal(BRAND_HUB_INITIAL.postingGoal);
    setFalStyleKeywords(''); setNegativePromptAdditions('');
    // The image modifiers live in the shell and outlive a client switch.
    setImgBrandKeywords([]); setImgAvoidTerms([]); setLightingMood([]);
    // AI-filled tags are keyed by value; one client's must not tag another's.
    setAiFilled({}); setAutofillRan(false); setAutofillResult(null); setAutofillAskUrl(false); setAutofillUrlInput('');
    // Chosen screenshots belong to the client they were picked for.
    setShotFiles([]); setShotNote('');
  };

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    const loadingFor = viewingAsClientId || clientId;
    resetBrandFields();
    setLoadedFor(null);
    clientScopedFetch(`${BACKEND}/api/brand-hub`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        // { ok, profile: null } is a client with no row yet: a successful,
        // empty load. A body without `profile` is a failed one.
        if (cancelled || !d || !('profile' in d)) return;
        const data = d.profile || {};
        setBizName(data.brand_name || '');
        if (onBizNameChange) onBizNameChange(data.brand_name || '');
        setTagline(data.brand_tagline || '');
        setWebsite(data.brand_website || '');
        setCategory(data.brand_industry || '');
        setTones(normaliseChips(data.brand_tone, TONES, { keepUnknown: true }));
        setLogoUrl(data.logo_url || '');
        if (onLogoChange) onLogoChange(data.logo_url || '');
        setBrandLogoPos(data.logo_position || BRAND_HUB_INITIAL.brandLogoPos);
        setPrimaryColour(data.primary_color || BRAND_HUB_INITIAL.primaryColour);
        setSecondaryColour(data.secondary_color || BRAND_HUB_INITIAL.secondaryColour);
        setAccentColour(data.accent_color || BRAND_HUB_INITIAL.accentColour);
        setFalStyleKeywords(data.image_style || '');
        setLightingMood(normaliseChips(data.image_mood, LIGHTING_MOODS, { keepUnknown: true }));
        setImgBrandKeywords(toArray(data.image_keywords));
        setImgAvoidTerms(toArray(data.image_avoid_terms));
        setNegativePromptAdditions(data.image_negative_prompt || '');
        setVoiceNotes(data.brand_voice_notes || '');
        setPillars(data.brand_pillars?.length ? data.brand_pillars : DEFAULT_PILLARS);
        setLocation(data.location || '');
        setAudience(data.target_audience || '');
        setWordsUse(toArray(data.words_use));
        setWordsAvoid(toArray(data.words_avoid));
        setVisualStyles(normaliseChips(data.visual_styles, VISUAL_STYLES, { keepUnknown: true }));
        setHashtagSets(toArray(data.hashtag_sets));
        setBestTimes(data.best_times || '');
        setKeyProducts(toArray(data.key_products));
        setUpcomingPromos(data.upcoming_promos || '');
        setAvoidTopics(toArray(data.avoid_topics));
        setCompetitors(data.competitors || '');
        // Optional schema fields — absent columns fall back like empty ones.
        setEmojiPref(data.emoji_pref || BRAND_HUB_INITIAL.emojiPref);
        setPhotoPref(data.photo_pref || BRAND_HUB_INITIAL.photoPref);
        // POSTING FREQUENCY AND POSTING DAYS LOAD TOGETHER, because they are
        // one setting and loading them apart is how the screen would open
        // showing a pair the backend would refuse to save.
        //
        //   posting_days set      -> show it, and let the count pick the button.
        //                            The backend guarantees the two agree, so
        //                            the count IS the saved frequency; reading
        //                            the button off the days rather than off
        //                            the string means a row that somehow
        //                            disagrees shows the days, which are what
        //                            the scheduler obeys.
        //   posting_days NULL     -> tick the saved frequency's defaults, so a
        //                            client who has never seen this row opens on
        //                            the days their chip implies rather than on
        //                            an empty row.
        //   both NULL             -> Daily, every day ticked. NULL means every
        //                            day to the column and to the scheduler, so
        //                            this is what they are already getting.
        //   unrecognised frequency-> nothing ticked (null), because there are no
        //                            defaults to tick and inventing some would
        //                            write days nobody chose. Save still works;
        //                            it sends posting_days: null.
        const savedFreq = data.posting_frequency ? normaliseFreq(data.posting_frequency) : null;
        const savedDays = Array.isArray(data.posting_days) ? inRowOrder(data.posting_days) : null;
        // The stored value, before any ticking. NULL stays null here — it
        // means every day, and that is exactly what a later save needs to
        // compare against to see which days a partial set takes away.
        setSavedPostingDays(savedDays && savedDays.length ? savedDays : null);
        if (savedDays && savedDays.length) {
          setPostingDays(savedDays);
          setFreq(FREQ_FOR_DAY_COUNT[savedDays.length] || savedFreq || '');
        } else {
          const defaults = FREQ_DEFAULT_DAYS[savedFreq || 'Daily'] || null;
          setPostingDays(defaults);
          setFreq(savedFreq || 'Daily');
        }
        setPostingGoal(data.posting_goal || BRAND_HUB_INITIAL.postingGoal);
        setContentMix(data.content_mix && typeof data.content_mix === 'object' ? data.content_mix : BRAND_HUB_INITIAL.contentMix);
        setActivePromotions(Array.isArray(data.active_promotions) ? data.active_promotions : []);
        setLoadedFor(loadingFor);

        // The Growth Boost return used to be handled HERE, by writing
        // two_x_daily straight back through /api/brand-hub on seeing
        // ?2xdaily=success in the URL. That granted a paid entitlement on the
        // strength of a query string: pasting the param onto the dashboard URL
        // unlocked Growth Boost for nothing, and the allowlist let it through
        // because two_x_daily was a Brand Hub column.
        //
        // The whole flow now lives in the root shell, which hands the Stripe
        // session id to POST /api/2x/confirm and lets the SERVER decide. It is
        // in the shell rather than in a panel because the return lands on
        // either Brand Hub or the Scheduler depending on where the upsell was
        // pressed, and only the shell is mounted for both. This panel keeps
        // only its own tidy-up of the query string.
        const params = new URLSearchParams(window.location.search);
        if (params.get('2xdaily') === 'success') {
          const cleaned = window.location.search
            .replace(/[?&]2xdaily=success/, '')
            .replace(/[?&]panel=[^&]*/, '')
            .replace(/^&/, '?');
          window.history.replaceState({}, '', window.location.pathname + cleaned);
        }
      })
      .catch(() => { /* panel renders its defaults, same as the read it replaced */ });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId]);

  // Look for future scheduled posts on days this save is taking away.
  //
  // READ-ONLY, AND ONLY WHEN A DAY IS ACTUALLY BEING REMOVED. GET /api/calendar
  // already returns this client's scheduled posts with their scheduled_at, is
  // behind requireOwnerOrAdmin, and goes out over clientScopedFetch — so under
  // an admin's viewing-as it is the VIEWED client's posts, and no new endpoint
  // is needed for a question an existing read already answers. It is fetched
  // here rather than on mount so a client who never touches the day row never
  // pays for it.
  //
  // A FAILED LOOKUP LETS THE SAVE THROUGH. This is an advisory heads-up about
  // posts that will publish either way; refusing to save a brand profile
  // because a calendar read timed out would be a worse outcome than not
  // mentioning it.
  const findPostsOnRemovedDays = async () => {
    const removed = removedPostingDays(
      savedPostingDays,
      freq === 'Daily' ? null : tickedDays,
    );
    const none = { conflicts: [], timezone: null };
    if (removed.length === 0) return none;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/calendar`);
      if (!res.ok) return none;
      const body = await res.json().catch(() => ({}));
      const scheduled = Array.isArray(body?.scheduled) ? body.scheduled : [];
      // The zone rides back with the same response the posts do, so the
      // weekday a post is bucketed under and the date the warning prints are
      // resolved against one clock rather than two.
      const timezone = body?.settings?.timezone || 'America/Vancouver';
      return { conflicts: postsOnRemovedDays(scheduled, removed, timezone, new Date()), timezone };
    } catch (err) {
      console.warn('[BrandHub] could not check posts on removed days:', err?.message || err);
      return none;
    }
  };

  // The Save button. Asks first when days with posts on them are going away,
  // then runs the ordinary save either way — see saveBrandHub, which is
  // unchanged from what this used to be.
  const handleSave = async () => {
    if (!loadedForViewed) return;           // this client's profile has not loaded
    if (postingDaysBlocked) return;
    if (removedDaysPrompt) return;          // a question is already on screen

    const { conflicts, timezone } = await findPostsOnRemovedDays();
    const warning = removedDaysWarning(conflicts, timezone);
    if (!warning) return saveBrandHub();

    // Cancel is the default: closing the modal any other way leaves the
    // profile unsaved and the day row exactly as the client left it, so
    // nothing is written on a stray click or an Escape.
    setRemovedDaysPrompt({ ...warning, proceed: () => { setRemovedDaysPrompt(null); saveBrandHub(); } });
  };

  const saveBrandHub = async () => {
    // The day row is in a state no frequency button matches. Refusing here as
    // well as disabling the button is not belt-and-braces for its own sake:
    // the button is in the save bar and the day row is on the Content tab, so
    // the two can be looked at separately, and a save that silently posted a
    // pair the backend would 400 is the failure this screen exists to prevent.
    if (postingDaysBlocked) return;
    // Also checked here because the removed-days prompt calls this directly.
    // (A client switch closes that prompt: resetBrandFields clears it.)
    if (!loadedForViewed) return;

    setSaveState('saving');
    const toStr = (v) => Array.isArray(v) ? v.filter(Boolean).join(', ') : (v ?? '');
    const toArr = (v) => Array.isArray(v) ? v.filter(Boolean) : (typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : []);

    // Known-existing schema columns (from the Week-1 brand_profile overhaul).
    const corePayload = {
      brand_name:             bizName,
      brand_tagline:          tagline,
      brand_website:          website,
      brand_industry:         category,
      brand_tone:             toStr(tones),
      logo_url:               logoUrl,
      primary_color:          primaryColour,
      secondary_color:        secondaryColour,
      accent_color:           accentColour,
      image_style:            falStyleKeywords,
      image_mood:             toStr(lightingMood),
      image_keywords:         toStr(imgBrandKeywords),
      image_avoid_terms:      toStr(imgAvoidTerms),
      image_negative_prompt:  negativePromptAdditions,
      brand_voice_notes:      voiceNotes,
      brand_pillars:          pillars,
      location:               location,
      target_audience:        audience,
      words_use:              toArr(wordsUse),
      words_avoid:            toArr(wordsAvoid),
      visual_styles:          toArr(visualStyles),
      hashtag_sets:           hashtagSets,
      best_times:             bestTimes,
      key_products:           toArr(keyProducts),
      upcoming_promos:        upcomingPromos,
      avoid_topics:           toArr(avoidTopics),
      competitors:            competitors,
    };

    // Optional schema columns — these aren't guaranteed to exist yet.
    // If they do exist, we save them; if they don't, we drop them silently
    // so the core save still succeeds. Add migrations for these to
    // brand_profile if you want them persisted: emoji_pref TEXT, photo_pref TEXT,
    // posting_frequency TEXT, posting_goal TEXT,
    // content_mix JSONB, active_promotions JSONB, logo_position TEXT.
    const optionalPayload = {
      emoji_pref:         emojiPref,
      photo_pref:         photoPref,
      // ALWAYS SENT TOGETHER. The backend compares the pair before it writes
      // and 400s if they disagree; sending one without the other would make it
      // compare against whatever is stored, which is not what the client is
      // looking at. Sending both means the screen is always the authority.
      posting_frequency:  freq,
      // NULL IS THE ONLY SPELLING OF EVERY DAY. Daily could be sent as the
      // seven-element array and the backend would accept it, but then the
      // column would hold two values meaning the same thing and every reader
      // would have to handle both — the exact drift the migration's comment
      // refuses a default for. Unconfigured (null) goes the same way.
      posting_days:       (freq === 'Daily' || !tickedDays.length) ? null : tickedDays,
      posting_goal:       postingGoal,
      content_mix:        contentMix,
      active_promotions:  activePromotions,
      logo_position:      brandLogoPos,
    };

    // Phase 4: this was a try-then-retry PAIR of direct Supabase updates — the
    // second firing only on a missing-column error from the first. Both moved
    // together to /api/brand-hub as ONE call, with the retry server-side. The
    // trigger regex is preserved verbatim there; it is not two operations the
    // browser can drive independently, and splitting it across two round trips
    // would let a save land twice.
    //
    // NO clientId IN THE BODY, deliberately. requireOwnerOrAdmin reads
    // req.body.clientId BEFORE req.query.client, so one here would beat the
    // viewing-as override and write a client's 26-column brand identity onto
    // the admin's own row while the UI showed the client's.
    let failed = false;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-hub`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ profile: { ...corePayload, ...optionalPayload } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      // The server ran the degraded second pass: optional columns did not
      // persist. Surfaced at the same volume the old client-side warning was.
      if (body.degraded) {
        console.warn('[BrandHub] Optional column missing — core fields saved only. Add the columns listed in the source comment to persist them.');
      }
    } catch (err) {
      console.error('[BrandHub] Save failed:', err.message);
      failed = true;
    }

    if (failed) {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return;
    }
    setSaveState('saved');
    // Saved means checked: the AI-filled tags and empty highlights go.
    setAiFilled({});
    setAutofillRan(false);
    // The baseline moves with the save. Without this a client who confirmed
    // once and pressed Save again — to change something else entirely — would
    // be asked the same question about the same days a second time.
    setSavedPostingDays(freq === 'Daily' || !tickedDays.length ? null : tickedDays);
    if (onBizNameChange) onBizNameChange(bizName);
    setTimeout(() => setSaveState('idle'), 3000);
    maybePromptFirstPost();
  };

  // After a successful Save only — never on autofill, which fills fields but
  // saves nothing. Self-Serve (see gettingStartedApplies), no posts yet per the
  // server, and not put off with Later this session. Any failure to ask the
  // server means no pop-up: a nudge is not worth a guess.
  const maybePromptFirstPost = async () => {
    if (!isSelfServe && !viewingAsClientId) return;
    if (safeGet('sessionStorage', firstPostDismissKey) === '1') return;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/getting-started`);
      if (!res.ok) return;
      const d = await res.json();
      if (!gettingStartedApplies({ isSelfServe, viewingAsClientId, plan: d?.plan })) return;
      if (d?.counts?.posts === 0) setFirstPostPrompt(true);
    } catch (_) { /* no answer, no pop-up */ }
  };

  const firstPostLater = React.useCallback(() => {
    safeSet('sessionStorage', firstPostDismissKey, '1');
    setFirstPostPrompt(false);
  }, [firstPostDismissKey]);
  const firstPostGo = (panel) => {
    setFirstPostPrompt(false);
    if (onNavigate) onNavigate(panel);
  };

  // Phase 4: was three direct Supabase calls written inline in this panel's
  // JSX (storage list, storage remove, brand_profile update). Extracted into a
  // named handler first — an inline arrow in the tree cannot be migrated or
  // tested — then routed through DELETE /api/brand-hub/logo, which performs the
  // same three steps server-side under requireOwnerOrAdmin.
  //
  // The optimistic local clear stays ahead of the request, as before: the
  // preview should disappear on click. A failed delete logs rather than
  // reverting, which is the behaviour this replaced.
  // The Visual tab's logo upload — the hidden file input and the drop zone
  // around it both come through here.
  const uploadBrandLogo = async (file) => {
    setLogoUploading(true);
    setLogoError('');
    try {
      const { publicUrl, error, stage } = await uploadLogo({ file, clientId });
      if (error) {
        console.error('[Logo] upload failed:', stage, error.message);
        if (stage === 'db') setLogoError('Saved to storage but profile update failed: ' + error.message);
        else setLogoError('Upload failed: ' + error.message);
        return;
      }
      setLogoUrl(publicUrl);
      if (onLogoChange) onLogoChange(publicUrl);
    } catch (err) {
      console.error('[Logo] unexpected upload error:', err);
      setLogoError('Upload failed: ' + (err?.message || 'unknown error'));
    } finally {
      setLogoUploading(false);
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
    }
  };
  const logoDrop = useFileDropZone({
    disabled: logoUploading,
    onFiles: (files) => {
      const { file, error } = singleImageFromDrop(files);
      if (error) { setLogoError(error); return; }
      if (file) uploadBrandLogo(file);
    },
  });

  const handleRemoveLogo = async () => {
    setLogoUrl('');
    if (onLogoChange) onLogoChange('');
    if (!clientId) return;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-hub/logo`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      console.error('[Logo] remove failed:', err.message);
    }
  };

  // Brief key → form value + setter, in BRAND_BRIEF_SCHEMA order. `snap` is
  // what the state actually holds after set (frequency and chips are
  // normalised). The chip fields' `norm` maps the AI's words onto the chips
  // ("friendly" lights Friendly) and drops the rest before applyBrief decides
  // whether the brief gave that field anything.
  const chipField = (value, set, allowed) => {
    const norm = (v) => normaliseChips(v, allowed);
    return { value, set: (v) => set(norm(v)), norm, snap: norm, list: true };
  };
  const briefFields = () => ({
    businessName:       { value: bizName,          set: setBizName },
    category:           { value: category,         set: setCategory },
    tagline:            { value: tagline,          set: setTagline },
    location:           { value: location,         set: setLocation },
    website:            { value: website,          set: setWebsite },
    targetAudience:     { value: audience,         set: setAudience },
    toneOfVoice:        chipField(tones,           setTones,            TONES),
    wordsToUse:         { value: wordsUse,         set: setWordsUse,          list: true },
    wordsToNeverUse:    { value: wordsAvoid,       set: setWordsAvoid,        list: true },
    emojiUsage:         { value: emojiPref,        set: setEmojiPref,         autofill: false },
    visualStyle:        chipField(visualStyles,    setVisualStyles,     VISUAL_STYLES),
    photoPreference:    { value: photoPref,        set: setPhotoPref,         autofill: false },
    // normaliseFreq and pickFrequency, not a bare setFreq. The AI is prompted
    // for one of the four chips but answers with free text, so a legacy 2x
    // string has to be folded the same way a saved one is — it went straight
    // into state before, which is how "2x Daily" could come back from a
    // transcript and sit in the row as an unrecognised chip. pickFrequency
    // then re-ticks that frequency's days, so the brief cannot leave the
    // chip saying one thing and the day row another.
    postingFrequency:   { value: freq,             set: (v) => pickFrequency(normaliseFreq(v)), snap: normaliseFreq, autofill: false },
    bestPostingTimes:   { value: bestTimes,        set: setBestTimes,         autofill: false },
    hashtagPool:        { value: hashtagSets,      set: setHashtagSets,       list: true },
    keyProducts:        { value: keyProducts,      set: setKeyProducts,       list: true },
    upcomingPromotions: { value: upcomingPromos,   set: setUpcomingPromos },
    topicsToAvoid:      { value: avoidTopics,      set: setAvoidTopics,       list: true },
    competitors:        { value: competitors,      set: setCompetitors },
    brandKeywords:      { value: imgBrandKeywords, set: setImgBrandKeywords,  list: true },
    // Autofilled and counted again now that Avoid in Images is saved and loaded
    // (brand_profile.image_avoid_terms) — it was autofill:false while it was lost
    // on reload. The backend reads it from the row into every image route's
    // negative prompt.
    avoidInImages:      { value: imgAvoidTerms,    set: setImgAvoidTerms,     list: true },
    lightingMood:       chipField(lightingMood,    setLightingMood,     LIGHTING_MOODS),
  });

  // Tag shown next to a field label: AI-filled until edited or saved, or a
  // "still empty" highlight once an autofill has run.
  const fieldTag = (key) => {
    const f = briefFields()[key];
    if (!f) return null;
    if (key in aiFilled && JSON.stringify(f.value) === aiFilled[key]) {
      return <span style={{marginLeft:8,fontSize:10,fontWeight:600,color:'#fbbf24',background:'rgba(251,191,36,0.12)',border:'1px solid rgba(251,191,36,0.35)',borderRadius:4,padding:'1px 6px'}}>AI-filled — check me</span>;
    }
    if (autofillRan && briefFieldIsEmpty(f.value)) {
      return <span style={{marginLeft:8,fontSize:10,fontWeight:600,color:'#f87171',background:'rgba(248,113,113,0.10)',border:'1px solid rgba(248,113,113,0.35)',borderRadius:4,padding:'1px 6px'}}>Still empty</span>;
    }
    return null;
  };

  const handleWebsiteAutofill = async (typedUrl) => {
    // An empty website still goes to the server: it may have connected
    // Instagram/Facebook to read instead. If it has nothing, it answers
    // 400 no_website and we ask for the website as before.
    const url = (typedUrl ?? website).trim();
    setAutofillAskUrl(false);
    if (typedUrl) setWebsite(url);
    setAutofillLoading(true);
    try {
      const r = await clientScopedFetch(`${BACKEND}/api/brand-autofill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // includeSocial is only a request — the backend decides who gets it.
        body: JSON.stringify(url ? { url, includeSocial: true } : { includeSocial: true }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (body.code === 'no_website') { setAutofillAskUrl(true); return; }
        setAutofillResult({ error: body.error || `Couldn't fill from your website (HTTP ${r.status}).`, website: body.website || null, social: body.social || null });
        return;
      }
      setAutofillResult({
        website: body.website,
        social: body.social || null,
        habits: body.habits || null,
        ...applyAutofillBrief(body),
      });
      if (body.brief) setAutofillOpen(false);
    } catch {
      setAutofillResult({ error: 'Network error — check connection.' });
    } finally {
      setAutofillLoading(false);
    }
  };

  // The one apply step for both autofills (website, screenshots): fill empty
  // fields only, tag them "AI-filled — check me", nothing saved until Save.
  // → { filled, total } for the popup.
  const applyAutofillBrief = (body) => {
    const fields = briefFields();
    const filled = applyBrief(body.brief, fields, { onlyEmpty: true });
    if (filled.length) {
      setAiFilled(prev => {
        const next = { ...prev };
        for (const k of filled) {
          const v = body.brief[k];
          next[k] = JSON.stringify(fields[k].snap ? fields[k].snap(v) : v);
        }
        return next;
      });
    }
    setAutofillRan(true);
    return {
      filled: filled.length,
      total: autofillTotal(Array.isArray(body.schemaFields) ? body.schemaFields : Object.keys(fields), fields),
    };
  };

  // Both the file input and the drop zone land here. Images only — a drop can
  // carry anything, and "All files" in a picker can too. HEIC often arrives
  // with an empty MIME type, so the extension counts as well.
  const pickScreenshots = (fileList) => {
    const given = Array.from(fileList || []);
    const picked = given.filter(f => (f.type || '').startsWith('image/') || /\.(heic|heif)$/i.test(f.name || ''));
    const rejected = given.length - picked.length;
    const rejectedNote = rejected
      ? `${rejected === 1 ? '1 file isn’t an image and was' : `${rejected} files aren’t images and were`} skipped.`
      : '';
    if (!picked.length) {
      if (rejectedNote) setShotNote(rejectedNote);
      return;
    }
    const all = [...shotFiles, ...picked];
    setShotFiles(all.slice(0, MAX_SCREENSHOTS));
    setShotNote([rejectedNote, all.length > MAX_SCREENSHOTS ? `Only the first ${MAX_SCREENSHOTS} screenshots are used.` : ''].filter(Boolean).join(' '));
  };

  // Drop zone around the screenshot picker — same drag behavior as the Photo
  // Library's .pl-dropzone, inert while the picker itself is disabled.
  const shotPickDisabled = shotLoading || shotFiles.length >= MAX_SCREENSHOTS;
  const onShotDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = shotPickDisabled ? 'none' : 'copy';
    if (!shotPickDisabled) setShotDragging(true);
  };
  // Moving over a child (the label, the button) fires dragleave on the zone.
  const onShotDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShotDragging(false); };
  const onShotDrop = (e) => {
    e.preventDefault();
    setShotDragging(false);
    if (shotPickDisabled) return;
    pickScreenshots(e.dataTransfer?.files);
  };

  const clearScreenshots = () => { setShotFiles([]); setShotNote(''); };

  const handleScreenshotAutofill = async () => {
    if (!shotFiles.length || shotLoading) return;
    setShotLoading(true);
    setShotNote('');
    try {
      const { images, skipped } = await prepareScreenshots(shotFiles);
      const skippedNote = skipped.length
        ? `${skipped.length === 1 ? '1 image' : `${skipped.length} images`} couldn’t be opened and ${skipped.length === 1 ? 'was' : 'were'} skipped.`
        : '';
      if (!images.length) {
        setShotNote(`${skippedNote} Try taking the screenshots again.`);
        return;
      }
      setShotNote(skippedNote);
      const r = await clientScopedFetch(`${BACKEND}/api/brand-autofill/screenshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setAutofillResult({ title: 'Fill from screenshots', error: body.error || `Couldn't fill from your screenshots (HTTP ${r.status}).` });
        return;
      }
      // Read once — drop the files now that the server has them.
      setShotFiles([]);
      const line = screenshotResultLine(body.screenshots);
      if (!body.brief) {
        setAutofillResult({ title: 'Fill from screenshots', line,
          note: 'We couldn’t find a bio or post captions in these screenshots. Try your profile page, plus posts with the caption fully opened (tap “more”).' });
        return;
      }
      setAutofillResult({ title: 'Fill from screenshots', line, ...applyAutofillBrief(body) });
      setAutofillOpen(false);
    } catch {
      setAutofillResult({ title: 'Fill from screenshots', error: 'Network error — check connection.' });
    } finally {
      setShotLoading(false);
    }
  };

  // A one-shot autofill request from Onboarding's last step. It waits for
  // this client's profile to land (autofill fills empty fields only, and the
  // load resets every field — screenshots picked before it would be wiped),
  // then runs once through the same handlers the buttons below use. The shell
  // drops the intent on consume, so later visits and a refresh start plain.
  useEffect(() => {
    if (!autofillIntent || !loadedForViewed) return;
    if (consumedIntentRef.current === autofillIntent) return;
    consumedIntentRef.current = autofillIntent;
    if (onAutofillIntentConsumed) onAutofillIntentConsumed();
    setAutofillOpen(true);
    autofillSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    if (autofillIntent.mode === 'website') {
      handleWebsiteAutofill(autofillIntent.url || undefined);
    } else if (autofillIntent.mode === 'screenshots' && navigator.userActivation?.isActive) {
      // A file picker needs a live tap. Only try while Onboarding's tap still
      // counts (browsers that report it); otherwise the section is just open
      // and in view, one tap from the picker.
      shotInputRef.current?.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autofillIntent, loadedForViewed]);

  const handleBuildBrief = async () => {
    setBriefLoading(true);
    setBriefError('');
    try {
      const r = await authedFetch('https://autopilot-backend-pn9r.onrender.com/api/brand-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: briefTranscript }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 400) { setBriefError(body.error || 'Bad request.'); }
        else { setBriefError("Claude couldn't parse the transcript. Try again or shorten it."); }
        setBriefLoading(false);
        return;
      }
      const d = await r.json();
      applyBrief(d, briefFields());
      setBriefTranscript('');
      setBriefSuccess(true);
      setTimeout(() => setBriefSuccess(false), 5000);
    } catch {
      setBriefError('Network error — check connection.');
    }
    setBriefLoading(false);
  };

  const TABS = ['Basics','Voice','Visual','Content','Context','Post Gen'];

  const PreviewCard = () => (
    <div className="bh-prev-card">
      <div className="bh-prev-title">Brand Preview</div>
      <div className="bh-prev-logo" style={logoUrl ? { background:'transparent', padding:0 } : {}}>
        {logoUrl
          ? <img src={logoUrl} alt="Brand logo" style={{ width:'100%', height:'100%', objectFit:'contain', borderRadius:10 }} />
          : <svg width="26" height="26" viewBox="0 0 34 34"><path d="M6 26 L16 7 L18 12 L22 9 L20 15 L26 13 L21 18 L24 17 L19 24 L16 20 L13 23 L14 19 L10 21 L13 16 Z" fill="white"/></svg>
        }
      </div>
      <div className="bh-prev-biz">{bizName || 'Business Name'}</div>
      <div className="bh-prev-tag">{tagline || 'Tagline goes here'}</div>
      <div className="bh-prev-colours">
        {[primaryColour, secondaryColour, accentColour].map((c, i) => (
          <div key={i} data-testid="bh-prev-swatch" className={`bh-prev-swatch${c ? '' : ' unset'}`}
            style={c ? {background:c} : undefined} title={c || 'Not set'} />
        ))}
      </div>
      <div className="bh-prev-divider" />
      <div className="bh-prev-row">
        <span className="bh-prev-label">Category</span>
        <span className="bh-prev-val">{category || '—'}</span>
      </div>
      <div className="bh-prev-row">
        <span className="bh-prev-label">Location</span>
        <span className="bh-prev-val">{location || '—'}</span>
      </div>
      <div className="bh-prev-row">
        <span className="bh-prev-label">Posting</span>
        <span className="bh-prev-val">{freq || '—'}</span>
      </div>
      <div className="bh-prev-row">
        <span className="bh-prev-label">Photos</span>
        <span className="bh-prev-val">{photoPref}</span>
      </div>
      {tones.length > 0 && <>
        <div className="bh-prev-divider" />
        <div className="bh-prev-tones">
          {tones.map(t => <span key={t} className="bh-prev-tone">{t}</span>)}
        </div>
      </>}
      {(lightingMood.length > 0 || imgBrandKeywords.length > 0) && <>
        <div className="bh-prev-divider" />
        <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.08em',color:'var(--text-muted)',textTransform:'uppercase',marginBottom:5}}>Post Gen</div>
        {lightingMood.length > 0 && (
          <div className="bh-prev-row">
            <span className="bh-prev-label">Mood</span>
            <span className="bh-prev-val" style={{fontSize:10}}>{lightingMood.join(', ')}</span>
          </div>
        )}
        {imgBrandKeywords.length > 0 && (
          <div className="bh-prev-row">
            <span className="bh-prev-label">Keywords</span>
            <span className="bh-prev-val" style={{fontSize:10}}>{imgBrandKeywords.slice(0,3).join(', ')}{imgBrandKeywords.length > 3 ? ` +${imgBrandKeywords.length-3}` : ''}</span>
          </div>
        )}
        {imgAvoidTerms.length > 0 && (
          <div className="bh-prev-row">
            <span className="bh-prev-label">Avoid</span>
            <span className="bh-prev-val" style={{fontSize:10,color:'#f87171'}}>{imgAvoidTerms.slice(0,3).join(', ')}{imgAvoidTerms.length > 3 ? ` +${imgAvoidTerms.length-3}` : ''}</span>
          </div>
        )}
      </>}
    </div>
  );

  return (
    <div className="bh-wrap">
      <div className="bh-main">

        {!isSelfServe && (
          <>
            <button
              onClick={handleBriefToggle}
              style={{display:'flex',alignItems:'center',gap:8,width:'100%',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:9,padding:'9px 14px',color:'var(--text)',fontSize:13,fontWeight:600,cursor:'pointer',marginBottom:8,textAlign:'left'}}
            >
              <span style={{flex:1}}>🎙️ Build Brief from Call</span>
              <span style={{fontSize:11,color:'var(--text-secondary)',display:'inline-block',transform:briefOpen ? 'rotate(180deg)' : 'rotate(0deg)',transition:'transform 0.2s'}}>▾</span>
            </button>

            {!briefUnlocked && briefPwVisible && (
              <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:9,padding:'12px 14px',marginBottom:8,display:'flex',flexDirection:'column',gap:8}}>
                <div style={{display:'flex',gap:8}}>
                  <input
                    type="password"
                    placeholder="Admin password"
                    value={briefPwInput}
                    onChange={e => setBriefPwInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleBriefUnlock()}
                    style={{flex:1,background:'rgba(255,255,255,0.04)',border:'1px solid var(--border)',borderRadius:6,padding:'7px 10px',color:'var(--text)',fontSize:13,fontFamily:'inherit'}}
                  />
                  <button
                    onClick={handleBriefUnlock}
                    disabled={briefPwLoading}
                    style={{background:'var(--accent)',color:'#000',border:'none',borderRadius:6,padding:'7px 14px',fontWeight:700,fontSize:13,cursor:briefPwLoading ? 'not-allowed' : 'pointer',opacity:briefPwLoading ? 0.6 : 1}}
                  >
                    {briefPwLoading ? '…' : 'Unlock'}
                  </button>
                </div>
                {briefPwError && <div style={{fontSize:12,color:'#f87171'}}>{briefPwError}</div>}
              </div>
            )}

            {briefUnlocked && briefOpen && (
              <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:16}}>
                <div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:10}}>Paste a Fireflies transcript. Claude will auto-fill every field below.</div>
                <textarea
                  value={briefTranscript}
                  onChange={e => setBriefTranscript(e.target.value)}
                  placeholder="Paste transcript here..."
                  style={{width:'100%',minHeight:100,maxHeight:250,overflowY:'auto',background:'rgba(255,255,255,0.04)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px',color:'var(--text)',fontSize:13,resize:'vertical',boxSizing:'border-box',fontFamily:'inherit'}}
                />
                <div style={{display:'flex',alignItems:'center',gap:12,marginTop:10,flexWrap:'wrap'}}>
                  <button
                    onClick={handleBuildBrief}
                    disabled={briefTranscript.length < 100 || briefLoading}
                    style={{background:'var(--accent)',color:'#000',border:'none',borderRadius:8,padding:'8px 18px',fontWeight:700,fontSize:13,cursor:briefTranscript.length < 100 || briefLoading ? 'not-allowed' : 'pointer',opacity:briefTranscript.length < 100 || briefLoading ? 0.55 : 1,display:'flex',alignItems:'center',gap:8,transition:'opacity 0.15s'}}
                  >
                    {briefLoading && <span style={{width:12,height:12,border:'2px solid rgba(0,0,0,0.3)',borderTopColor:'#000',borderRadius:'50%',display:'inline-block',animation:'spin 0.7s linear infinite'}} />}
                    {briefLoading ? 'Building brief…' : 'Build Brief from Call'}
                  </button>
                  {briefSuccess && (
                    <span style={{fontSize:12,color:'#4ade80',fontWeight:600,animation:'fadeOut 5s forwards'}}>
                      Brief built. Review below and save.
                    </span>
                  )}
                </div>
                {briefError && <div style={{fontSize:12,color:'#f87171',marginTop:8}}>{briefError}</div>}
              </div>
            )}
          </>
        )}

        <div ref={autofillSectionRef} style={{marginBottom:autofillOpen ? 0 : 8}}>
        <button
          type="button"
          onClick={() => setAutofillOpen(!autofillOpen)}
          aria-expanded={autofillOpen}
          aria-controls="bh-autofill-body"
          style={{width:'100%',minHeight:44,display:'flex',alignItems:'center',gap:10,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:9,padding:'8px 14px',marginBottom:autofillOpen ? 8 : 0,color:'var(--text)',fontFamily:'inherit',textAlign:'left',cursor:'pointer'}}
        >
          <span style={{flex:1,minWidth:0}}>
            <span style={{fontSize:13,fontWeight:700}}>✨ Autofill</span>
            <span style={{fontSize:11,fontWeight:400,color:'var(--text-secondary)',marginLeft:8}}>Fill empty fields from your website or Instagram/Facebook screenshots</span>
          </span>
          <span aria-hidden="true" style={{fontSize:11,color:'var(--text-secondary)',display:'inline-block',transform:autofillOpen ? 'rotate(180deg)' : 'rotate(0deg)',transition:'transform 0.2s'}}>▾</span>
        </button>
        <div id="bh-autofill-body" hidden={!autofillOpen}>
        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:9,padding:'9px 14px',marginBottom:8,display:'flex',flexDirection:'column',gap:8}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{flex:1,fontSize:13,fontWeight:600}}>🌐 Fill from my website <span style={{fontSize:11,fontWeight:400,color:'var(--text-secondary)'}}>— fills empty fields only; nothing saves until you press Save</span></span>
            <button
              onClick={() => handleWebsiteAutofill()}
              disabled={autofillLoading}
              style={{background:'var(--accent)',color:'#000',border:'none',borderRadius:6,padding:'7px 14px',fontWeight:700,fontSize:13,cursor:autofillLoading ? 'not-allowed' : 'pointer',opacity:autofillLoading ? 0.6 : 1}}
            >
              {autofillLoading ? 'Reading website…' : 'Fill from my website'}
            </button>
          </div>
          {autofillAskUrl && (
            <div style={{display:'flex',gap:8}}>
              <input
                type="url"
                placeholder="What's your website? e.g. https://yourbusiness.com"
                value={autofillUrlInput}
                onChange={e => setAutofillUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && autofillUrlInput.trim() && handleWebsiteAutofill(autofillUrlInput)}
                style={{flex:1,background:'rgba(255,255,255,0.04)',border:'1px solid var(--border)',borderRadius:6,padding:'7px 10px',color:'var(--text)',fontSize:13,fontFamily:'inherit'}}
              />
              <button
                onClick={() => handleWebsiteAutofill(autofillUrlInput)}
                disabled={!autofillUrlInput.trim() || autofillLoading}
                style={{background:'var(--accent)',color:'#000',border:'none',borderRadius:6,padding:'7px 14px',fontWeight:700,fontSize:13,cursor:'pointer',opacity:!autofillUrlInput.trim() || autofillLoading ? 0.55 : 1}}
              >
                Read
              </button>
            </div>
          )}
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:9,padding:'9px 14px',marginBottom:8,display:'flex',flexDirection:'column',gap:8}}>
          <span style={{fontSize:13,fontWeight:600}}>📸 Fill from screenshots <span style={{fontSize:11,fontWeight:400,color:'var(--text-secondary)'}}>— fills empty fields only; nothing saves until you press Save</span></span>
          <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.45}}>
            Screenshot your Instagram or Facebook profile page, plus up to 5 posts with the caption fully opened (tap “more”).
            <br />Screenshots are read once to suggest details and aren’t saved.
          </div>
          <div data-testid="screenshot-dropzone"
            className={`pl-dropzone${shotDragging ? ' dragging' : ''}`}
            onDragOver={onShotDragOver}
            onDragLeave={onShotDragLeave}
            onDrop={onShotDrop}
            style={{flex:'none',padding:'14px 16px',gap:8,cursor:'default'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,flexWrap:'wrap'}}>
            {/* A real <label> around the input: one tap opens the photo library on phones. */}
            <label style={{position:'relative',display:'inline-flex',alignItems:'center',justifyContent:'center',minHeight:44,padding:'0 16px',border:'1px solid var(--border)',borderRadius:8,background:'rgba(255,255,255,0.04)',fontSize:14,fontWeight:600,cursor: shotPickDisabled ? 'not-allowed' : 'pointer',opacity: shotPickDisabled ? 0.55 : 1}}>
              {shotFiles.length ? 'Add screenshots' : 'Choose screenshots'}
              <input ref={shotInputRef} type="file" accept="image/*" multiple aria-label="Choose screenshots"
                disabled={shotPickDisabled}
                onChange={e => { pickScreenshots(e.target.files); e.target.value = ''; }}
                style={{position:'absolute',width:1,height:1,opacity:0,overflow:'hidden'}} />
            </label>
            <button
              onClick={handleScreenshotAutofill}
              disabled={!shotFiles.length || shotLoading}
              style={{minHeight:44,background:'var(--accent)',color:'#000',border:'none',borderRadius:8,padding:'0 16px',fontWeight:700,fontSize:14,cursor:!shotFiles.length || shotLoading ? 'not-allowed' : 'pointer',opacity:!shotFiles.length || shotLoading ? 0.55 : 1}}
            >
              {shotLoading ? 'Reading screenshots…' : 'Fill from screenshots'}
            </button>
          </div>
          <div className="pl-dropzone-sub">or drag and drop screenshots here</div>
          </div>
          {shotFiles.length > 0 && (
            <div style={{display:'flex',alignItems:'center',gap:10,fontSize:13}}>
              <span>{shotFiles.length} of {MAX_SCREENSHOTS} screenshots chosen</span>
              <button onClick={clearScreenshots} disabled={shotLoading}
                style={{minHeight:44,background:'none',border:'none',color:'var(--accent)',fontSize:13,fontWeight:600,padding:'0 6px',cursor:'pointer'}}>
                Clear
              </button>
            </div>
          )}
          {shotNote && <div role="status" style={{fontSize:12,color:'#fbbf24'}}>{shotNote}</div>}
        </div>
        </div>
        </div>

        {autofillResult && (
          <div role="dialog" aria-modal="true" aria-label={autofillResult.title || 'Fill from my website'}
            onClick={() => setAutofillResult(null)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16}}>
            <div onClick={e => e.stopPropagation()}
              style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:20,width:'100%',maxWidth:420,display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontSize:15,fontWeight:700}}>{autofillResult.title || 'Fill from my website'}</div>
              {autofillResult.line && (
                <div style={{fontSize:13,fontWeight:600,color:'#4ade80'}}>{autofillResult.line}</div>
              )}
              {autofillResult.website?.status === 'none' && (
                <div style={{fontSize:13,color:'var(--text-secondary)'}}>No website given</div>
              )}
              {autofillResult.website && autofillResult.website.status !== 'none' && (
                <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
                  <span style={{fontWeight:700,color: autofillResult.website.status === 'ok' ? '#4ade80' : '#f87171'}}>
                    {autofillResult.website.status === 'ok' ? '✓ Read' : autofillResult.website.status === 'blocked' ? '✕ Blocked' : '✕ Couldn’t read'}
                  </span>
                  <span style={{color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{autofillResult.website.url}</span>
                </div>
              )}
              {autofillResult.website?.status && autofillResult.website.status !== 'ok' && autofillResult.website.error && (
                <div style={{fontSize:12,color:'var(--text-secondary)'}}>{autofillResult.website.error}</div>
              )}
              {autofillSocialLines(autofillResult.social).map(l => (
                <div key={l.key} style={{fontSize:13,color: l.ok ? '#4ade80' : 'var(--text-secondary)'}}>{l.text}</div>
              ))}
              {autofillHabitsText(autofillResult.habits) && (
                <div style={{fontSize:12,color:'var(--text-secondary)'}}>Your current habits (not filled in): {autofillHabitsText(autofillResult.habits)}</div>
              )}
              {autofillResult.error
                ? <div style={{fontSize:13,color:'#f87171'}}>{autofillResult.error}</div>
                : autofillResult.note
                ? <div style={{fontSize:13}}>{autofillResult.note}</div>
                : <div style={{fontSize:13}}>We filled <strong>{autofillResult.filled}</strong> of <strong>{autofillResult.total}</strong> fields. Filled fields are tagged “AI-filled — check me”; fields still empty are highlighted. Nothing is saved until you press Save.</div>}
              <div style={{display:'flex',justifyContent:'flex-end'}}>
                <button onClick={() => setAutofillResult(null)}
                  style={{background:'var(--accent)',color:'#000',border:'none',borderRadius:6,padding:'7px 16px',fontWeight:700,fontSize:13,cursor:'pointer'}}>OK</button>
              </div>
            </div>
          </div>
        )}

        <div className="bh-tabs">
          {TABS.map((t, i) => (
            <div key={t} className={`bh-tab${tab === i ? ' active' : ''}`} onClick={() => setTab(i)}>{t}</div>
          ))}
        </div>

        <div className="bh-card">

          {/* TAB 0 — BUSINESS BASICS */}
          {tab === 0 && <>
            <div className="bh-section-title">Business Basics</div>
            <div className="bh-section-sub">Core information about the business. This is used in captions, hashtags, and post context.</div>
            <div className="bh-two-col">
              <div className="bh-field">
                <div className="bh-label">Business Name{fieldTag('businessName')}</div>
                <input className="bh-input" value={bizName} onChange={e => setBizName(e.target.value)} placeholder="e.g. Your Business Name" />
              </div>
              <div className="bh-field">
                <div className="bh-label">Category{fieldTag('category')}</div>
                <select className="bh-input" value={category} onChange={e => setCategory(e.target.value)} style={{cursor:'pointer'}}>
                  {['Cafe / Coffee Shop','Restaurant / Food','Hair Salon / Barbershop','Spa / Wellness','Gym / Fitness','Retail Boutique','Trades / Home Services','Professional Services','Other'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Tagline{fieldTag('tagline')}</div>
              <input className="bh-input" value={tagline} onChange={e => setTagline(e.target.value)} placeholder="A short phrase that captures the brand" />
            </div>
            <div className="bh-two-col">
              <div className="bh-field">
                <div className="bh-label">Location{fieldTag('location')}</div>
                <input className="bh-input" value={location} onChange={e => setLocation(e.target.value)} placeholder="City, Province" />
              </div>
              <div className="bh-field">
                <div className="bh-label">Website{fieldTag('website')}</div>
                <input className="bh-input" value={website} onChange={e => setWebsite(e.target.value)} placeholder="yoursite.ca" />
              </div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Target Audience{fieldTag('targetAudience')}<Tip text="The more specific this is, the better every caption performs. Age, lifestyle, and buying habits all help." /></div>
              <textarea className="bh-textarea" value={audience} onChange={e => setAudience(e.target.value)} placeholder="Who is the ideal customer? Age, lifestyle, habits..." />
            </div>
          </>}

          {/* TAB 1 — VOICE & TONE */}
          {tab === 1 && <>
            <div className="bh-section-title">Voice & Tone</div>
            <div className="bh-section-sub">How the brand speaks. This shapes every caption we write.</div>
            <div className="bh-field">
              <div className="bh-label">Tone of Voice — select all that apply{fieldTag('toneOfVoice')}<Tip text="These words shape how every caption sounds. Select 2–4 that feel most like your brand." /></div>
              <div className="bh-chips">
                {TONES.map(t => (
                  <div key={t} className={`bh-chip${tones.includes(t) ? ' active' : ''}`}
                    onClick={() => setTones(ts => ts.includes(t) ? ts.filter(x=>x!==t) : [...ts,t])}>
                    {t}
                  </div>
                ))}
                <CustomChips values={tones} allowed={TONES} onRemove={v => setTones(ts => ts.filter(x => x !== v))} />
              </div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Words & phrases to use{fieldTag('wordsToUse')}</div>
              <TagInput tags={wordsUse} setTags={setWordsUse} placeholder="Type and press Enter..." />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>Words that feel on-brand. Press Enter after each one.</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Words &amp; phrases to never use{fieldTag('wordsToNeverUse')}<Tip text="These are filtered out of every caption automatically — no manual checking needed." /></div>
              <TagInput tags={wordsAvoid} setTags={setWordsAvoid} placeholder="Type and press Enter..." />
            </div>
            <div className="bh-field">
              <div className="bh-label">Emoji usage{fieldTag('emojiUsage')}</div>
              <div className="bh-chips">
                {['None','Minimal','Moderate','Frequent'].map(e => (
                  <div key={e} className={`bh-chip${emojiPref===e?' active':''}`} onClick={() => setEmojiPref(e)}>{e}</div>
                ))}
              </div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Voice Notes</div>
              <textarea
                className="bh-textarea"
                value={voiceNotes}
                onChange={e => setVoiceNotes(e.target.value)}
                placeholder="e.g. Write like you're texting a friend. Always reference the local community. Never use the word 'leverage'."
                style={{minHeight:80}}
              />
            </div>
          </>}

          {/* TAB 2 — VISUAL IDENTITY */}
          {tab === 2 && <>
            <div className="bh-section-title">Visual Identity</div>
            <div className="bh-section-sub">Colours, logo, and visual style. Used to guide image generation and keep posts on-brand.</div>
            <div className="bh-field">
              <div className="bh-label">Brand Colours</div>
              <BrandColourField label="Primary"          value={primaryColour}   onChange={setPrimaryColour} />
              <BrandColourField label="Secondary / Dark" value={secondaryColour} onChange={setSecondaryColour} />
              <BrandColourField label="Accent"           value={accentColour}    onChange={setAccentColour} />
            </div>
            <div className="bh-field">
              <div className="bh-label">Logo</div>
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => { const file = e.target.files[0]; if (file) uploadBrandLogo(file); }}
              />
              <div data-testid="brandhub-logo-dropzone"
                className={`pl-dropzone${logoDrop.dragging ? ' dragging' : ''}`}
                {...logoDrop.handlers}
                style={{ flex:'none', padding:'12px 14px', gap:6, cursor:'default', alignItems:'stretch' }}>
              {logoUrl
                ? <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <img src={logoUrl} alt="Logo preview" style={{ height:48, maxWidth:120, objectFit:'contain', borderRadius:6, border:'1px solid rgba(255,255,255,0.12)' }} />
                    <button
                      disabled={logoUploading}
                      onClick={() => logoFileInputRef.current?.click()}
                      style={{ fontSize:11, color:'var(--text-secondary)', background:'none', border:'1px solid var(--border)', borderRadius:6, padding:'4px 10px', cursor:'pointer' }}>
                      {logoUploading ? 'Uploading…' : 'Change'}
                    </button>
                    <button
                      disabled={logoUploading}
                      onClick={handleRemoveLogo}
                      style={{ fontSize:11, color:'var(--text-secondary)', background:'none', border:'none', cursor:'pointer', padding:0 }}>
                      Remove
                    </button>
                  </div>
                : <div
                    className="bh-upload"
                    style={logoUploading ? { opacity:0.5, pointerEvents:'none' } : {}}
                    onClick={() => logoFileInputRef.current?.click()}>
                    {logoUploading ? 'Uploading…' : '+ Upload logo (PNG or SVG recommended)'}
                  </div>
              }
              <div className="pl-dropzone-sub">or drag and drop your logo here</div>
              </div>
              {logoError &&<div style={{ fontSize:11, color:'#ff6b6b', marginTop:6 }}>{logoError}</div>}
            </div>
            <div className="bh-field">
              <div className="bh-label">Logo Position<Tip text="Default corner for logo watermarks on generated posts. Each post can still be overridden in the generator." /></div>
              <LogoPositionPicker value={brandLogoPos} onChange={setBrandLogoPos} />
            </div>
            <div className="bh-field">
              <div className="bh-label">Visual Style — select all that apply{fieldTag('visualStyle')}<Tip text="These keywords feed directly into image generation prompts to keep photos on-brand." /></div>
              <div className="bh-chips">
                {VISUAL_STYLES.map(s => (
                  <div key={s} className={`bh-chip${visualStyles.includes(s)?' active':''}`}
                    onClick={() => setVisualStyles(vs => vs.includes(s) ? vs.filter(x=>x!==s) : [...vs,s])}>
                    {s}
                  </div>
                ))}
                <CustomChips values={visualStyles} allowed={VISUAL_STYLES} onRemove={v => setVisualStyles(vs => vs.filter(x => x !== v))} />
              </div>
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:6}}>These keywords feed directly into image generation prompts.</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Photo Preference{fieldTag('photoPreference')}</div>
              <div className="bh-chips">
                {PHOTO_PREFS.map(p => (
                  <div key={p} className={`bh-chip${photoPref===p?' active':''}`} onClick={()=>setPhotoPref(p)}>{p}</div>
                ))}
              </div>
            </div>
          </>}

          {/* TAB 3 — CONTENT STRATEGY */}
          {tab === 3 && <>
            <div className="bh-section-title">Content Strategy</div>
            <div className="bh-section-sub">Posting rhythm, pillars, and hashtags.</div>
            <div className="bh-field">
              <div className="bh-label">Posting Goal</div>
              <select className="bh-input" value={postingGoal} onChange={e => setPostingGoal(e.target.value)} style={{cursor:'pointer'}}>
                {POSTING_GOALS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div className="bh-field">
              <div className="bh-label">Posting Frequency{fieldTag('postingFrequency')}</div>
              <div className="bh-chips">
                {freqOptions.map(f => (
                  <div
                    key={f}
                    className={`bh-chip${freq===f?' active':''}`}
                    onClick={() => pickFrequency(f)}
                  >{f}</div>
                ))}
                {/* 2x / day is a PURCHASE, not a frequency, and sits in this row
                    because that is where someone looks when they want to post
                    more often — not because it is one of the options. It never
                    calls setFreq and is never part of freqOptions, so it cannot
                    become a saved posting_frequency the way the retired
                    "2x Growth Boost" chip did.

                    hasTwoX comes from the shell's /api/brand-profile read over
                    clientScopedFetch, so under an admin's viewing-as it is the
                    VIEWED client's entitlement. That is deliberately unlike the
                    admin 2x banner, which uses authedFetch and describes the
                    admin's own row (known bug, noted at its call site). */}
                {hasTwoX ? (
                  <div
                    className="bh-chip bh-chip-2x owned"
                    aria-label="2x per day — Growth Boost active"
                  >
                    2x / day · Active
                  </div>
                ) : (
                  <button
                    type="button"
                    className="bh-chip bh-chip-2x"
                    onClick={handleTwoXChipClick}
                    disabled={twoXCheckoutLoading}
                    aria-label="Add 2x per day with Growth Boost, $19 per month"
                  >
                    {twoXCheckoutLoading ? 'Redirecting…' : '2x / day · $19/mo'}
                  </button>
                )}
              </div>
            </div>
            {/* POSTING DAYS. This row is the thing the client edits; the
                frequency buttons above follow it. Deliberately under them
                rather than beside: tapping a frequency fills this row in, so
                the reading order is cause then effect.

                Checkboxes rather than another chip row, even though they would
                look more consistent. A chip means "one of these", and these are
                "any of these" — and a chip row directly under a chip row, where
                one selects and the other multi-selects, is the sort of thing
                that only makes sense to whoever built it. */}
            <div className="bh-field">
              <div className="bh-label">Posting Days<Tip text="The days your posts go out. Pick the days you want and the frequency above follows." /></div>
              <div className="bh-days" role="group" aria-label="Posting days">
                {POSTING_DAY_ROW.map(({ n, label }) => {
                  const on = tickedDays.includes(n);
                  return (
                    <label key={n} className={`bh-day${on ? ' on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => togglePostingDay(n)}
                        aria-label={label}
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
              {postingDaysBlocked && (
                <div className="bh-days-note" role="alert">⚠ {POSTING_DAYS_NOTE}</div>
              )}
            </div>
            {/* "Preferred Post Times" (morning_slot / evening_slot) lived here as
                two free-text 12-hour fields. They were written and read back by
                this form and consulted by nothing — no scheduling code ever
                looked at them — so they went with their columns in
                20260925120000_second_posting_slot.sql. Posting times, both
                slots, are the Calendar panel's. */}
            <div className="bh-field">
              <div className="bh-label">Content Mix<Tip text="Set the percentage split across content types. Total should equal 100%." /></div>
              <div style={{display:'flex',flexDirection:'column',gap:10,marginTop:4}}>
                {[
                  { key:'educational',  label:'Educational'       },
                  { key:'product',      label:'Product'           },
                  { key:'lifestyle',    label:'Lifestyle'         },
                  { key:'promo',        label:'Promotional'       },
                  { key:'socialProof',  label:'Social Proof'      },
                  { key:'behindScenes', label:'Behind the Scenes' },
                ].map(({ key, label }) => (
                  <div key={key} style={{display:'flex',alignItems:'center',gap:12,minWidth:0}}>
                    <span style={{fontSize:12,color:'var(--text-secondary)',flex:'0 0 110px',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={label}>{label}</span>
                    <input
                      type="range" min={0} max={100} value={contentMix[key]}
                      onChange={e => setMixVal(key, e.target.value)}
                      style={{flex:1,minWidth:0,accentColor:'var(--accent)',cursor:'pointer'}}
                    />
                    <span style={{fontSize:12,color:'var(--text)',flex:'0 0 34px',textAlign:'right',fontVariantNumeric:'tabular-nums'}}>{contentMix[key]}%</span>
                  </div>
                ))}
              </div>
              <div style={{fontSize:11,marginTop:8,color: contentMixTotal === 100 ? 'var(--accent)' : '#f5a623', fontWeight:600}}>
                Total: {contentMixTotal}%{contentMixTotal !== 100 ? ' — adjust sliders to reach 100%' : ' ✓'}
              </div>
              <div style={{fontSize:11,marginTop:6,color:'var(--text-muted)'}}>Applies to both image generation and caption writing</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Content Pillars</div>
              {pillars.map(p => (
                <div key={p.id} className="bh-pillar-row" style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
                  <button
                    onClick={() => setPillars(ps => ps.map(x => x.id === p.id ? { ...x, active: !x.active } : x))}
                    style={{flexShrink:0,width:32,height:18,borderRadius:9,border:'none',cursor:'pointer',padding:0,background:p.active ? 'var(--accent)' : 'rgba(255,255,255,0.12)',position:'relative',transition:'background 0.15s'}}
                    title={p.active ? 'Active — click to disable' : 'Off — click to enable'}
                  >
                    <span style={{position:'absolute',top:2,left:p.active?14:2,width:14,height:14,borderRadius:'50%',background:'#fff',transition:'left 0.15s',display:'block'}} />
                  </button>
                  <div className="bh-pillar-name" style={{flex:1,fontSize:13,color:p.active?'var(--text)':'var(--text-muted)'}}>{p.name}</div>
                  <button
                    onClick={() => setPillars(ps => ps.filter(x => x.id !== p.id))}
                    style={{fontSize:14,lineHeight:1,color:'var(--text-muted)',background:'none',border:'none',cursor:'pointer',padding:'0 2px',flexShrink:0}}
                    title="Remove pillar"
                    aria-label={`Remove pillar ${p.name}`}
                  >×</button>
                </div>
              ))}
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <input
                  className="bh-input"
                  value={newPillarInput}
                  onChange={e => setNewPillarInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newPillarInput.trim()) {
                      setPillars(ps => [...ps, { id: Date.now().toString(), name: newPillarInput.trim(), active: true }]);
                      setNewPillarInput('');
                    }
                  }}
                  placeholder="New pillar name..."
                  style={{flex:1,marginBottom:0}}
                />
                <button
                  onClick={() => {
                    if (!newPillarInput.trim()) return;
                    setPillars(ps => [...ps, { id: Date.now().toString(), name: newPillarInput.trim(), active: true }]);
                    setNewPillarInput('');
                  }}
                  style={{padding:'0 14px',background:'var(--accent)',color:'#000',border:'none',borderRadius:7,fontSize:12,fontWeight:700,cursor:'pointer',flexShrink:0}}
                >Add</button>
              </div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Hashtag Pool{fieldTag('hashtagPool')}<Tip text="Up to 30 hashtags rotate across posts automatically so you never repeat the same set twice." /></div>
              <TagInput tags={hashtagSets} setTags={setHashtagSets} placeholder="#addhashtag" />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>These rotate across posts automatically. Add up to 30.</div>
            </div>

          </>}

          {/* TAB 4 — BUSINESS CONTEXT */}
          {tab === 4 && <>
            <div className="bh-section-title">Business Context</div>
            <div className="bh-section-sub">What to feature, what's coming up, and what to avoid. The more detail here, the better every post gets.</div>
            <div className="bh-field">
              <div className="bh-label">Key Products & Services to Feature{fieldTag('keyProducts')}</div>
              <TagInput tags={keyProducts} setTags={setKeyProducts} placeholder="Add product or service..." />
            </div>
            <div className="bh-field">
              <div className="bh-label">Upcoming Notes{fieldTag('upcomingPromotions')}</div>
              <textarea className="bh-textarea" value={upcomingPromos} onChange={e=>setUpcomingPromos(e.target.value)} placeholder="Any launches, seasonal events, sales, or milestones coming up..." />
            </div>
            <div className="bh-field">
              <div className="bh-label">Active Promotions<Tip text="Structured promotions the AI can reference and schedule. Set a max frequency to avoid overposting." /></div>
              {activePromotions.length === 0 && (
                <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:8}}>No active promotions yet.</div>
              )}
              {activePromotions.map((promo, idx) => (
                <div key={promo.id} style={{background:'rgba(255,255,255,0.03)',border:'1px solid var(--border)',borderRadius:9,padding:'12px 14px',marginBottom:10,display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Promotion {idx + 1}</span>
                    <button onClick={() => removePromotion(promo.id)} style={{fontSize:11,color:'#f87171',background:'none',border:'none',cursor:'pointer',padding:0}}>Remove</button>
                  </div>
                  <input
                    className="bh-input"
                    value={promo.title}
                    onChange={e => updatePromotion(promo.id, 'title', e.target.value)}
                    placeholder="Promotion title (e.g. Spring Patio Launch)"
                    style={{marginBottom:0}}
                  />
                  <textarea
                    className="bh-textarea"
                    value={promo.description}
                    onChange={e => updatePromotion(promo.id, 'description', e.target.value)}
                    placeholder="Short description of the promotion..."
                    style={{minHeight:60,marginBottom:0}}
                  />
                  <div className="bh-two-col" style={{gap:10}}>
                    <div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,fontWeight:600}}>Start Date</div>
                      <input
                        type="date"
                        className="bh-input"
                        value={promo.startDate}
                        onChange={e => updatePromotion(promo.id, 'startDate', e.target.value)}
                        style={{marginBottom:0,colorScheme:'dark'}}
                      />
                    </div>
                    <div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,fontWeight:600}}>End Date</div>
                      <input
                        type="date"
                        className="bh-input"
                        value={promo.endDate}
                        onChange={e => updatePromotion(promo.id, 'endDate', e.target.value)}
                        style={{marginBottom:0,colorScheme:'dark'}}
                      />
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,fontWeight:600}}>Max Post Frequency</div>
                    <select
                      className="bh-input"
                      value={promo.maxFrequency}
                      onChange={e => updatePromotion(promo.id, 'maxFrequency', e.target.value)}
                      style={{cursor:'pointer',marginBottom:0}}
                    >
                      {['As needed','Once','2x / week','3x / week','Daily'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
              ))}
              <button
                onClick={addPromotion}
                style={{width:'100%',padding:'8px 0',background:'transparent',border:'1px dashed var(--border)',borderRadius:8,color:'var(--text-secondary)',fontSize:12,cursor:'pointer',fontWeight:600,transition:'border-color 0.15s,color 0.15s'}}
                onMouseEnter={e=>{e.target.style.borderColor='var(--accent)';e.target.style.color='var(--accent)'}}
                onMouseLeave={e=>{e.target.style.borderColor='var(--border)';e.target.style.color='var(--text-secondary)'}}
              >
                + Add Promotion
              </button>
            </div>
            <div className="bh-field">
              <div className="bh-label">Topics to Always Avoid{fieldTag('topicsToAvoid')}</div>
              <TagInput tags={avoidTopics} setTags={setAvoidTopics} placeholder="Add topic to avoid..." />
            </div>
            <div className="bh-field">
              <div className="bh-label">Competitors to be Aware Of{fieldTag('competitors')}</div>
              <input className="bh-input" value={competitors} onChange={e=>setCompetitors(e.target.value)} placeholder="Who are the main competitors in the area?" />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>We won't reference these — just helps us stay clearly differentiated.</div>
            </div>
          </>}

          {/* TAB 5 — IMAGE GENERATION */}
          {tab === 5 && <>
            <div className="bh-section-title">Post Generator</div>
            <div className="bh-section-sub">These settings feed directly into every generated image — keeping photos on-brand without you having to type a single prompt.</div>
            <div className="bh-field">
              <div className="bh-label">Brand Keywords{fieldTag('brandKeywords')}<Tip text="Visual words added to every image prompt automatically. Keep them descriptive and sensory — think textures, colours, and atmosphere. e.g. 'rustic', 'warm tones', 'cozy'." /></div>
              <TagInput tags={imgBrandKeywords} setTags={setImgBrandKeywords} placeholder="Type and press Enter..." />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>These are included in every image prompt. Add 3–8 visual keywords.</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">FAL Style Keywords<Tip text="Style keywords sent directly to the FAL image model. Use FAL-specific descriptors like 'cinematic', 'f/1.8 bokeh', 'hyperrealistic', 'editorial'." /></div>
              <input
                className="bh-input"
                value={falStyleKeywords}
                onChange={e => setFalStyleKeywords(e.target.value)}
                placeholder="e.g. cinematic, f/1.8 bokeh, hyperrealistic, editorial"
              />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>Comma-separated. Appended to every FAL generation request.</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Avoid in Images{fieldTag('avoidInImages')}<Tip text="Anything that should never appear in a generated photo. Passed as a negative prompt to the image model." /></div>
              <TagInput tags={imgAvoidTerms} setTags={setImgAvoidTerms} placeholder="Type and press Enter..." />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>e.g. people, text, logos, competitor colours. Press Enter after each.</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Negative Prompt Additions<Tip text="Free-text negative prompt appended to every generation. Use for things that can't be expressed as single tag keywords." /></div>
              <textarea
                className="bh-textarea"
                value={negativePromptAdditions}
                onChange={e => setNegativePromptAdditions(e.target.value)}
                placeholder="e.g. blurry, low quality, oversaturated, cartoon, illustration, stock photo look..."
                style={{minHeight:70}}
              />
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:4}}>Added verbatim to the negative prompt on every generation.</div>
            </div>
            <div className="bh-field">
              <div className="bh-label">Lighting &amp; Mood{fieldTag('lightingMood')}<Tip text="Sets the overall feel of generated images. Select 1–2 that best match your brand's visual style. This is combined with your Brand Keywords and Visual Style settings." /></div>
              <div className="bh-chips">
                {LIGHTING_MOODS.map(m => (
                  <div key={m} className={`bh-chip${lightingMood.includes(m)?' active':''}`}
                    onClick={() => setLightingMood(lm => lm.includes(m) ? lm.filter(x=>x!==m) : [...lm,m])}>
                    {m}
                  </div>
                ))}
                <CustomChips values={lightingMood} allowed={LIGHTING_MOODS} onRemove={v => setLightingMood(lm => lm.filter(x => x !== v))} />
              </div>
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:6}}>Select 1–2 that match your brand. Combined with Visual Style for the full image prompt.</div>
            </div>
            <div style={{marginTop:8,marginBottom:20,padding:'12px 14px',background:'var(--accent-dim)',border:'1px solid var(--accent-border)',borderRadius:9}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--accent)',marginBottom:6,fontFamily:'var(--font-head)',letterSpacing:'0.04em'}}>PROMPT PREVIEW</div>
              <div style={{fontSize:11,color:'var(--text-secondary)',lineHeight:1.7}}>
                <span style={{color:'var(--text-muted)'}}>Style: </span>{visualStyles.join(', ') || '—'}
                {lightingMood.length > 0 && <><span style={{color:'var(--text-muted)'}}> · Mood: </span>{lightingMood.join(', ')}</>}
                {imgBrandKeywords.length > 0 && <><span style={{color:'var(--text-muted)'}}> · Keywords: </span>{imgBrandKeywords.join(', ')}</>}
                {falStyleKeywords && <><span style={{color:'var(--text-muted)'}}> · FAL: </span>{falStyleKeywords}</>}
                {(imgAvoidTerms.length > 0 || negativePromptAdditions) && <><span style={{color:'var(--text-muted)'}}> · Avoid: </span>{[...imgAvoidTerms, negativePromptAdditions].filter(Boolean).join(', ')}</>}
              </div>
            </div>
          </>}

        </div>

        {/* Days with posts on them are going away. A modal rather than
            window.confirm — unlike the Scheduler's drag warning, which is a
            fast repeated action, this is a once-in-a-while decision about
            content that is already booked, and it names posts and dates that
            deserve to be readable. The ap-confirm primitive already carries
            the amber treatment the new warn styles use. */}
        {removedDaysPrompt && (
          <div
            className="ap-confirm-overlay"
            onClick={() => setRemovedDaysPrompt(null)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bh-removed-days-title"
          >
            <div className="ap-confirm" onClick={e => e.stopPropagation()}>
              <div className="ap-confirm-title" id="bh-removed-days-title">
                Posts are already scheduled on those days
              </div>
              <div className="bh-days-note" style={{ marginTop: 0, marginBottom: 12 }}>
                ⚠ {removedDaysPrompt.message}
              </div>
              <div className="ap-confirm-list">
                <div className="ap-confirm-item">
                  <span className="ap-confirm-dot" />
                  <span>
                    <strong>Nothing moves.</strong> These posts keep the date and time they
                    already have, and publish as normal. Posting days only affect where NEW
                    posts get placed.
                  </span>
                </div>
                <div className="ap-confirm-item">
                  <span className="ap-confirm-dot" />
                  <span>
                    <strong>To change them,</strong> reschedule or delete them in the Calendar.
                  </span>
                </div>
              </div>
              <div className="ap-confirm-actions">
                <button
                  className="ap-confirm-btn ap-confirm-btn-cancel"
                  onClick={() => setRemovedDaysPrompt(null)}
                >Cancel</button>
                <button
                  className="ap-confirm-btn ap-confirm-btn-go"
                  onClick={removedDaysPrompt.proceed}
                >Save anyway</button>
              </div>
            </div>
          </div>
        )}

        {/* Never stacked on another modal: if the autofill result popup is up,
            this waits until it is closed. */}
        {firstPostPrompt && !autofillResult && !removedDaysPrompt && (
          <FirstPostPrompt
            onUseOwnPhotos={() => firstPostGo('photos')}
            onCreateWithAI={() => firstPostGo('imagegen')}
            onLater={firstPostLater}
          />
        )}

        <div className="bh-save-bar">
          {/* The note is repeated here, not just under the day row. The row is
              on the Content tab and this bar is on every tab, so without it a
              client on Basics sees a dead Save button and no reason for it. */}
          {/* THE BLOCKED STATE IS AMBER, A FAILED SAVE IS RED, and until now both
              rendered in muted grey — `.bh-save-status.error` was applied and
              had no CSS rule at all, so "Pick …" and "✗ Error saving" looked
              exactly like the idle "Unsaved changes" they replaced. That is
              most of why a blocked Save read as a button that simply did
              nothing. Both classes are real styles now. */}
          <span className={`bh-save-status${postingDaysBlocked ? ' warn' : saveState === 'saved' ? ' saved' : saveState === 'error' ? ' error' : ''}`}>
            {postingDaysBlocked ? `⚠ ${POSTING_DAYS_NOTE}`
              : saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Changes saved' : saveState === 'error' ? '✗ Error saving' : 'Unsaved changes'}
          </span>
          <button className="ob-btn ob-btn-primary" onClick={handleSave} disabled={saveState === 'saving' || postingDaysBlocked || !loadedForViewed} style={{padding:'8px 20px',fontSize:12}}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? '✓ Saved' : saveState === 'error' ? '✗ Try again' : 'Save Brand Hub'}
          </button>
        </div>
      </div>

      <div className="bh-preview">
        <PreviewCard />
      </div>
    </div>
  );
}

// ============================================================
// HOME PANEL
// ============================================================
function InstallIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 2v8" />
      <path d="M4.5 7L8 10.5 11.5 7" />
      <path d="M2.5 13.5h11" />
    </svg>
  );
}

// Redrawn rather than described in words: "the Share icon" is not something
// people can pick out of the Safari toolbar from a text label alone.
function ShareIcon() {
  return (
    <svg width="17" height="20" viewBox="0 0 16 20" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 12.5V2" />
      <path d="M4.5 5.5L8 2l3.5 3.5" />
      <path d="M4.5 8.5H2.75A1.25 1.25 0 0 0 1.5 9.75v7A1.25 1.25 0 0 0 2.75 18h10.5a1.25 1.25 0 0 0 1.25-1.25v-7A1.25 1.25 0 0 0 13.25 8.5H11.5" />
    </svg>
  );
}

function AddToHomeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="14" height="14" rx="3.5" />
      <path d="M9 5.75v6.5M5.75 9h6.5" />
    </svg>
  );
}

/**
 * iOS has no programmatic install path — Safari deliberately never fires
 * `beforeinstallprompt` — so the button opens directions instead of a dialog.
 */
function IosInstallModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="hp-ios-overlay" onClick={onClose} role="presentation">
      <div className="hp-ios-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Install Autopilot">
        <div className="hp-ios-title">Install Autopilot</div>
        <div className="hp-ios-lead">
          Add the dashboard to your Home Screen and it opens like an app — full screen, no address bar.
        </div>
        <div className="hp-ios-steps">
          <div className="hp-ios-step">
            <div className="hp-ios-step-icon"><ShareIcon /></div>
            <div>Tap the <strong>Share</strong> icon in the Safari toolbar.</div>
          </div>
          <div className="hp-ios-step">
            <div className="hp-ios-step-icon"><AddToHomeIcon /></div>
            <div>Choose <strong>Add to Home Screen</strong>, then tap <strong>Add</strong>.</div>
          </div>
        </div>
        <button className="hp-ios-close" onClick={onClose} type="button">Got it</button>
      </div>
    </div>
  );
}

/**
 * "Install App" for the Welcome back banner. Renders nothing at all until the
 * browser has confirmed an install path exists — a captured
 * beforeinstallprompt, or iOS. See hooks/usePwaInstall.js.
 */
export function InstallPrompt() {
  const { canInstall, install, dismiss, showIosHelp, closeIosHelp } = usePwaInstall();

  if (!canInstall) return null;

  return (
    <>
      <div className="hp-install-row">
        <button className="hp-install-btn" onClick={install} type="button">
          <InstallIcon />
          Install App
        </button>
        <button className="hp-install-dismiss" onClick={dismiss} type="button">
          Not now
        </button>
      </div>
      {showIosHelp && <IosInstallModal onClose={closeIosHelp} />}
    </>
  );
}

// ============================================================
// GETTING STARTED — Self-Serve only
// ============================================================
// Walks a new Self-Serve client to their first post. Managed clients never see
// any of it: Roger runs those accounts.
//
// WHO IT APPLIES TO. Normally the shell's isSelfServe, the same signal every
// plan-specific bit of the dashboard uses. EXCEPT while an admin is viewing as
// a client: the shell's plan comes from /api/customer for the SIGNED-IN user
// (no viewing-as override), so it would describe the admin, not the client on
// screen. In that case the viewed client's stored brand_profile.plan — which
// /api/getting-started reports — decides, and only 'self_serve' counts, so an
// unclassified (null) plan shows nothing.
export function gettingStartedApplies({ isSelfServe, viewingAsClientId, plan }) {
  if (viewingAsClientId) return plan === 'self_serve';
  return Boolean(isSelfServe);
}

// Every storage touch is wrapped: private mode and locked-down browsers throw
// on access, and a checklist must never take Home down with it.
const safeGet = (store, key) => { try { return window[store].getItem(key); } catch (_) { return null; } };
const safeSet = (store, key, val) => { try { window[store].setItem(key, val); } catch (_) { /* best-effort */ } };

export const GETTING_STARTED_STEPS = [
  { key: 'brandHub',  title: 'Set up your Brand Hub',          line: 'Tell us about your business so posts sound like you.', cta: 'Open Brand Hub',     panel: 'brand' },
  { key: 'social',    title: 'Connect Instagram or Facebook',  line: 'So we can post for you.',                               cta: 'Connect',            panel: 'connections' },
  { key: 'photos',    title: 'Add your photos',                line: 'Upload photos of your work, products or space.',        cta: 'Open Photo Library', panel: 'photos',
    muted: 'No photos yet? Skip this — you can create one with AI in the next step.' },
  { key: 'firstPost', title: 'Create your first post',         line: 'Turn a photo into a post, or let AI make one.',         cta: 'Open Post Generator', panel: 'imagegen' },
  { key: 'approved',  title: 'Review and approve it',          line: 'Check the caption and approve it to schedule.',         cta: 'Review posts',       panel: 'approval' },
  { key: 'autopilot', title: 'Turn on Autopilot',              line: 'Auto-post from your Photo Library so your feed never goes quiet.', cta: 'Turn on auto-post', panel: 'photos', focus: 'autopost' },
];

// Index of the step to highlight as "Next", or -1 when there is none. Photos
// is optional: once a post exists the client evidently skipped it and used AI,
// so it stops blocking "Next" — but it is still shown unticked, because the
// tick only ever means the server found a photo.
export function gettingStartedNextIndex(steps, counts) {
  const skippedPhotos = Boolean(steps?.firstPost) || (counts?.posts ?? 0) > 0;
  return GETTING_STARTED_STEPS.findIndex(s =>
    !steps?.[s.key] && !(s.key === 'photos' && skippedPhotos));
}

export function GettingStartedCard({ clientId, isSelfServe, onNavigate = () => {} }) {
  const { viewingAsClientId } = useViewingAs();
  const viewedId = viewingAsClientId || clientId;
  // null until a good answer lands; a failed read stays null and the card is
  // never drawn — no answer means no ticks, never guessed ones.
  const [data, setData] = useState(null);
  const collapseKey = `ap_getting_started_collapsed_${viewedId}`;
  const dismissKey  = `ap_getting_started_dismissed_${viewedId}`;
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Asked only when it could matter: a client the shell knows is Self-Serve,
  // or an admin viewing as someone (whose plan only this endpoint can tell).
  const mightApply = Boolean(isSelfServe) || Boolean(viewingAsClientId);

  useEffect(() => {
    setData(null);
    setCollapsed(safeGet('localStorage', collapseKey) === '1');
    setDismissed(safeGet('localStorage', dismissKey) === '1');
    if (!clientId || !mightApply) return undefined;
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/getting-started`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d?.steps && d?.counts) setData(d); })
      .catch(() => { /* hidden on failure, by design */ });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId, mightApply, collapseKey, dismissKey]);

  if (!data || dismissed) return null;
  if (!gettingStartedApplies({ isSelfServe, viewingAsClientId, plan: data.plan })) return null;

  const doneCount = GETTING_STARTED_STEPS.filter(s => data.steps[s.key]).length;
  const total = GETTING_STARTED_STEPS.length;
  const allDone = doneCount === total;
  const nextIdx = gettingStartedNextIndex(data.steps, data.counts);
  const pct = Math.round((doneCount / total) * 100);

  const progress = (
    <div className="gs-bar" role="progressbar" aria-label="Getting started progress"
      aria-valuemin={0} aria-valuemax={total} aria-valuenow={doneCount}>
      <div className="gs-bar-fill" style={{ width: `${pct}%` }} />
    </div>
  );

  if (allDone) return (
    <section className="gs-card" aria-label="Getting started">
      <div className="gs-done-row">
        <div className="gs-title">🎉 You're on Autopilot</div>
        <span className="gs-progress-text">{doneCount} of {total} done</span>
        <button type="button" className="ob-btn ob-btn-secondary gs-btn"
          onClick={() => { safeSet('localStorage', dismissKey, '1'); setDismissed(true); }}>
          Dismiss
        </button>
      </div>
      {progress}
    </section>
  );

  const toggleCollapsed = () => {
    const next = !collapsed;
    safeSet('localStorage', collapseKey, next ? '1' : '0');
    setCollapsed(next);
  };

  return (
    <section className="gs-card" aria-label="Getting started">
      <div className="gs-head">
        <div className="gs-title">Getting started</div>
        <span className="gs-progress-text">{doneCount} of {total} done</span>
        <button type="button" className="gs-toggle" onClick={toggleCollapsed}
          aria-expanded={!collapsed} aria-controls="gs-steps">
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>
      {progress}
      {!collapsed && (
        <ol className="gs-list" id="gs-steps">
          {GETTING_STARTED_STEPS.map((s, i) => {
            const done = Boolean(data.steps[s.key]);
            const isNext = i === nextIdx;
            return (
              <li key={s.key} className={`gs-step${done ? ' done' : ''}${isNext ? ' next' : ''}`}
                data-step={s.key} aria-current={isNext ? 'step' : undefined}>
                <div className="gs-num" aria-hidden="true">{done ? '✓' : i + 1}</div>
                <div className="gs-body">
                  <div className="gs-step-title">
                    {s.title}
                    {done && <span className="gs-sr-only"> (done)</span>}
                    {isNext && <span className="gs-next-tag">Next</span>}
                  </div>
                  <div className="gs-line">{s.line}</div>
                  {s.muted && !done && <div className="gs-muted">{s.muted}</div>}
                </div>
                <button type="button"
                  className={`ob-btn ${isNext ? 'ob-btn-primary' : 'ob-btn-secondary'} gs-btn`}
                  onClick={() => onNavigate(s.panel, s.focus ? { focus: s.focus } : undefined)}>
                  {s.cta}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function HomePanel({ clientId, clientName, onStatsLoaded, hasTwoX, isSelfServe = false, onNavigate }) {
  // Phase 3 (addendum item 2): the summary fetch below keyed only on
  // [clientId], which useClientId always resolves to the SIGNED-IN user — so it
  // never re-ran when an admin entered or left a client view, and exiting via
  // "back to admin" while sitting on Home left the just-viewed client's numbers
  // on screen until the panel happened to remount. Not reachable on entry ("View
  // as" only lives on the Admin tab, where Home is unmounted), which is why it
  // read as an exit-only bug. Same fix, same reason, as the root shell's reads.
  const { viewingAsClientId } = useViewingAs();
  const [loading, setLoading] = useState(true);

  // The 2x / day chip in the engagement card. Same endpoint and same
  // returnPanel the button it replaced used — 'home' needs nothing from the
  // backend, which interpolates returnPanel straight into ?panel= with no
  // allowlist, and 'home' is the dashboard's default panel anyway.
  const [twoXCheckoutLoading, setTwoXCheckoutLoading] = useState(false);
  const handleTwoXChipClick = async () => {
    if (hasTwoX || twoXCheckoutLoading) return;
    setTwoXCheckoutLoading(true);
    try {
      // clientId/customerId are not sent: the endpoint derives both from this
      // token. (Carried over from the button this replaced.)
      const res = await authedFetch('/api/create-2x-checkout', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ returnPanel: 'home' }),
      });
      const json = await res.json();
      if (json.url) window.location.href = json.url;
      else setTwoXCheckoutLoading(false);
    } catch (e) {
      console.error('2x checkout error', e);
      setTwoXCheckoutLoading(false);
    }
  };

  const [postsThisMonth, setPostsThisMonth] = useState(null);
  const [pendingCount, setPendingCount] = useState(null);
  const [nextPost, setNextPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [postingActive, setPostingActive] = useState(false);
  const [avgEngagement, setAvgEngagement] = useState(null);

  // Phase 2: these were eight parallel supabase.from(...) reads. They were
  // always one logical fetch — a single Promise.all feeding a single render —
  // so they moved to one backend endpoint rather than eight.
  //
  // monthStart/monthEnd are still computed HERE and passed along. `new Date(y,
  // m, 1)` resolves in the VIEWER's local timezone; recomputing it on Render
  // would use UTC and shift "posts this month" for anyone west of UTC on the
  // first or last day of a month. now/+7d are absolute instants and are left to
  // the server.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();

      let d = null;
      try {
        const qs = `monthStart=${encodeURIComponent(monthStart)}&monthEnd=${encodeURIComponent(monthEnd)}`;
        const res = await clientScopedFetch(`${BACKEND}/api/home-summary?${qs}`);
        if (res.ok) d = await res.json();
        else console.error('[Home] summary failed:', res.status);
      } catch (err) {
        console.error('[Home] summary failed:', err.message);
      }
      if (cancelled) return;

      // A failed fetch leaves every card in its empty state rather than showing
      // another client's numbers or stale ones.
      setPostsThisMonth(d?.postsThisMonth ?? 0);
      setPendingCount(d?.pendingCount ?? 0);
      setNextPost(d?.nextPost ?? null);
      setRecentPosts(d?.recentPosts ?? []);
      setPendingList(d?.pendingList ?? []);
      setUpcoming(d?.upcoming ?? []);
      setPostingActive(Boolean(d?.postingActive));
      setAvgEngagement(d?.avgEngagement ?? null);
      setLoading(false);
      if (onStatsLoaded) onStatsLoaded({
        postsThisMonth: d?.postsThisMonth ?? 0,
        avgEngagement:  d?.avgEngagement ?? null,
        nextPost:       d?.nextPost ?? null,
        recentPosts:    d?.recentPosts ?? [],
        pendingList:    d?.pendingList ?? [],
      });
    };
    fetchData();
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId]);

  const tz = getDisplayTimeZone();
  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    if (d.toDateString() === new Date(now.getTime() + 86400000).toDateString()) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
  };
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz }) : '';
  const trunc = (s, n) => s && s.length > n ? s.slice(0, n) + '…' : (s || '');
  const fmtPlatform = (platforms) => {
    if (!platforms || platforms.length === 0) return '';
    const p = platforms[0];
    return p.charAt(0).toUpperCase() + p.slice(1);
  };
  const pillStyle = (s) => {
    if (s === 'pending') return { background:'rgba(245,166,35,0.12)', color:'#f5a623' };
    return { background:'rgba(49,220,186,0.12)', color:'var(--accent)' };
  };
  const isToday = (iso) => iso && new Date(iso).toDateString() === new Date().toDateString();

  const displayName = clientName || 'your account';

  const nextPostVal = nextPost
    ? `${fmtDate(nextPost.scheduled_at)} · ${fmtTime(nextPost.scheduled_at)}`
    : 'None scheduled';
  const nextPostSub = nextPost
    ? `${nextPost.post_type || 'Post'}${nextPost.platforms?.[0] ? ' · ' + fmtPlatform(nextPost.platforms) : ''}`
    : '';

  if (loading) {
    const Skel = ({ w, h = 14 }) => (
      <div className="hp-skel" style={{ width: w || '100%', height: h }} />
    );
    return (
      <div className="hp-wrap">
        <GettingStartedCard clientId={clientId} isSelfServe={isSelfServe} onNavigate={onNavigate} />
        <div className="hp-banner" style={{ gap: 16 }}>
          <div className="hp-banner-dot" style={{ opacity: 0.3 }} />
          <div className="hp-banner-main" style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <Skel w="55%" h={16} />
            <Skel w="70%" h={11} />
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
            <Skel w={80} h={10} />
            <Skel w={120} h={14} />
            <Skel w={90} h={10} />
          </div>
        </div>
        <div className="ap-stat-grid">
          {[0,1,2,3].map(i => (
            <div key={i} className="ap-stat-card" style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <Skel w="60%" h={11} />
              <Skel w="40%" h={24} />
              <Skel w="70%" h={10} />
            </div>
          ))}
        </div>
        <div className="ap-two-col">
          {[0,1].map(i => (
            <div key={i} className="ap-card" style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <Skel w="50%" h={13} />
              {[0,1,2].map(j => <Skel key={j} h={36} />)}
            </div>
          ))}
        </div>
        <div className="hp-upcoming" style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <Skel w="40%" h={13} />
          {[0,1,2,4].map(i => <Skel key={i} h={28} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="hp-wrap">
      <GettingStartedCard clientId={clientId} isSelfServe={isSelfServe} onNavigate={onNavigate} />
      <div className="hp-banner">
        <div className="hp-banner-dot" style={postingActive ? {} : { background: 'var(--text-muted)', boxShadow: 'none' }} />
        <div className="hp-banner-main">
          <div className="hp-banner-title">Welcome back, {displayName}.</div>
          <div className="hp-banner-sub">
            {postingActive
              ? 'Your Autopilot is active — posts are going out on schedule.'
              : 'No scheduled posts yet — your queue is empty.'}
          </div>
          <InstallPrompt />
        </div>
        <div className="hp-banner-next">
          <div className="hp-banner-next-label">Next Post</div>
          <div className="hp-banner-next-val">{nextPostVal}</div>
          {nextPostSub && <div className="hp-banner-next-sub">{nextPostSub}</div>}
        </div>
      </div>
      <div className="ap-stat-grid">
        <div className="ap-stat-card">
          <div className="ap-stat-label">Posts This Month<Tip text="Total posts published this calendar month across all connected accounts." /></div>
          <div className="ap-stat-value">{postsThisMonth}</div>
          <div className="ap-stat-sub">{postsThisMonth === 0 ? 'None published yet' : 'This calendar month'}</div>
        </div>
        <div className="ap-stat-card">
          <div className="ap-stat-label">Avg. Engagement<Tip text="Average engagement rate across all posts with available insights data." /></div>
          {avgEngagement !== null
            ? <>
                <div className="ap-stat-value">{avgEngagement.toFixed(1)}%</div>
                <div className="ap-stat-sub">Across tracked posts</div>
              </>
            : <>
                <div className="ap-stat-value" style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  No engagement data yet — this will populate as your posts go live.
                </div>
                <div className="ap-stat-sub" style={{ marginTop: 8, fontStyle: 'normal', color: 'var(--text-muted)' }}>
                  2x Growth Boost doubles your monthly image allowance — 48 Generated Images Per Month instead of 24.
                </div>
                {/* The third and last 2x / day chip, replacing the
                    "Add 2x Growth Boost — $19/mo →" button that stood here.
                    Same spot, same endpoint, same returnPanel.

                    NOTE it inherits this card's condition: the whole block
                    renders only while avgEngagement is null, so on Home the
                    chip — Active state included — is visible only to accounts
                    with no engagement data yet. That is what the button did
                    too, and keeping it in the same spot means keeping that. */}
                <div style={{ marginTop: 10 }}>
                  {hasTwoX ? (
                    <div
                      className="bh-chip bh-chip-2x owned"
                      aria-label="2x per day — Growth Boost active"
                    >
                      2x / day · Active
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="bh-chip bh-chip-2x"
                      onClick={handleTwoXChipClick}
                      disabled={twoXCheckoutLoading}
                      aria-label="Add 2x per day with Growth Boost, $19 per month"
                    >
                      {twoXCheckoutLoading ? 'Redirecting…' : '2x / day · $19/mo'}
                    </button>
                  )}
                </div>
              </>
          }
        </div>
        <div className="ap-stat-card">
          <div className="ap-stat-label">Pending Approval<Tip text="Posts your team has created that need your review before they go live." /></div>
          <div className="ap-stat-value">{pendingCount}</div>
          <div className="ap-stat-sub">{pendingCount === 0 ? 'All reviewed' : 'Needs your review'}</div>
        </div>
        <div className="ap-stat-card">
          <div className="ap-stat-label">Next Post</div>
          {nextPost
            ? <>
                <div className="ap-stat-value" style={{fontSize:17}}>{fmtDate(nextPost.scheduled_at)}</div>
                <div className="ap-stat-sub">{fmtTime(nextPost.scheduled_at)}{nextPost.post_type ? ` — ${nextPost.post_type}` : ''}</div>
              </>
            : <>
                <div className="ap-stat-value" style={{fontSize:17}}>—</div>
                <div className="ap-stat-sub">No upcoming posts</div>
              </>
          }
        </div>
      </div>
      <div className="ap-two-col">
        <div className="ap-card">
          <div className="ap-card-header"><span className="ap-card-title">Recent Posts</span><span className="ap-card-action">View all →</span></div>
          {recentPosts.length === 0
            ? <div style={{color:'var(--text-muted)',fontSize:12,padding:'8px 0'}}>No published posts yet.</div>
            : recentPosts.map(p => (
                <div key={p.id} className="ap-post-item">
                  <div className="ap-thumb">{p.post_type?.[0] || '📄'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="ap-post-caption">{trunc(p.caption, 40)}</div>
                    <div className="ap-post-meta">{fmtDate(p.scheduled_at)}{p.platforms?.[0] ? ' · ' + fmtPlatform(p.platforms) : ''}</div>
                  </div>
                  <span className={`ap-pill s-${p.status}`}>{p.status.charAt(0).toUpperCase() + p.status.slice(1)}</span>
                </div>
              ))
          }
        </div>
        <div className="ap-card">
          <div className="ap-card-header"><span className="ap-card-title">Pending Approval</span><span className="ap-card-action">Review →</span></div>
          {pendingList.length === 0
            ? <div style={{color:'var(--text-muted)',fontSize:12,padding:'8px 0'}}>Nothing pending — you're all caught up.</div>
            : pendingList.map(p => (
                <div key={p.id} className="ap-post-item">
                  <div className="ap-thumb">{p.post_type?.[0] || '📄'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="ap-post-caption">{trunc(p.caption, 40)}</div>
                    <div className="ap-post-meta">{fmtDate(p.scheduled_at)}{p.topic ? ' · ' + p.topic : ''}</div>
                  </div>
                </div>
              ))
          }
        </div>
      </div>
      <div className="hp-upcoming">
        <div className="hp-upcoming-title">Upcoming — Next 7 Days</div>
        {upcoming.length === 0
          ? <div style={{color:'var(--text-muted)',fontSize:12,padding:'8px 0'}}>No upcoming posts in the next 7 days.</div>
          : upcoming.map((item, i) => (
              <div key={item.id || i} className="hp-week-row">
                <div className={`hp-week-date${isToday(item.scheduled_at) ? ' today' : ''}`}>{fmtDate(item.scheduled_at)}</div>
                <div className="hp-week-caption">{trunc(item.caption, 40)}</div>
                <div className="hp-week-time">{fmtTime(item.scheduled_at)}</div>
                <div className="hp-week-type">{item.post_type || ''}</div>
                <span className="hp-week-pill" style={pillStyle(item.status)}>{item.status.charAt(0).toUpperCase() + item.status.slice(1)}</span>
              </div>
            ))
        }
      </div>
    </div>
  );
}

// ============================================================
// CONTENT CALENDAR PANEL — Session 3
// ============================================================

// ── Pointer-driven reorder for the Scheduler ────────────────────────────────
// The Scheduler used native HTML5 drag-and-drop (`draggable` + onDragStart /
// onDrop). WebKit never synthesises `dragstart` from a touch on an ordinary
// draggable element, so on iPad and iPhone — Safari and the installed PWA
// alike — pressing a card ran no drag code at all, and iOS fell back to its own
// long-press behaviour: the selection callout that reads as "a small bubble",
// and no reorder. Desktop mouse worked because desktop WebKit/Blink do fire it.
//
// Pointer events replace it for every input, mouse, touch and pencil alike.
// Same rationale as SwipeApprovalStack below: this needs one hook and the repo
// deliberately carries no gesture library.
//
// HOW A DRAG STARTS, and why the inputs differ:
//   • Mouse — REORDER_MOUSE_ENGAGE_PX of travel, so a press that never moves is
//     still an ordinary click on the card's own buttons.
//   • Touch / pencil — a REORDER_LONG_PRESS_MS press that has NOT moved. Moving
//     first means the finger is scrolling, so the press is abandoned outright
//     and the page scrolls as before. Scroll and drag are therefore mutually
//     exclusive by construction, which is what keeps vertical page scroll and
//     the chip row's horizontal scroll intact.
//
// HOW SCROLLING IS HELD OFF once a touch drag HAS engaged: the browser latches
// `touch-action` when a gesture begins, so flipping it mid-gesture is too late.
// An engaged drag instead attaches a non-passive `touchmove` listener that
// preventDefaults — React's own touchmove listener is passive and could not.
// Nothing is attached until a drag engages, so plain scrolling never pays for
// it, and every exit path detaches it.
//
// THE DROP is not reimplemented. This resolves the element under the pointer
// and hands its id to the existing handleInsertDrop / handleChipDrop, which
// read the source from the same dragId / chipDragId state the old dragstart
// set. That cross-event state dependency is exactly the one native DnD already
// relied on (dragstart set it, a later drop read it), so the commit path — and
// the API call behind it — is untouched.
const REORDER_LONG_PRESS_MS   = 300;  // touch/pencil: hold this long to pick up
const REORDER_TOUCH_SLOP_PX   = 10;   // movement before then = a scroll, not a drag
const REORDER_MOUSE_ENGAGE_PX = 4;    // mouse: travel before a drag engages
// Native DnD auto-scrolled the page near the viewport edge for free; pointer
// events do not, and on touch an engaged drag blocks scrolling outright, so
// without this the only reachable drop targets are the ones already on screen.
// The scheduled grid is two columns deep, so a month of posts is well past a
// viewport.
const EDGE_SCROLL_ZONE_PX = 72;   // how close to an edge starts scrolling
const EDGE_SCROLL_MAX_PPF = 18;   // px per frame once the pointer is AT the edge

// A press that lands on a control belongs to that control, not to the drag.
const REORDER_INTERACTIVE = 'button, a, input, textarea, select, label';

// Named export purely as a test seam, matching SwipeApprovalStack / QueueCard:
// nothing in the app imports it.
export function usePointerReorder({ attr, setSourceId, setOverId, onCommit, resolveId }) {
  const drag       = useRef(null);    // the live gesture, or null between drags
  const ghostRef   = useRef(null);    // the floating ghost's DOM node
  const overRef    = useRef(null);    // last hovered target id, read at drop
  const draggedRef = useRef(false);   // eats the click that follows a drag
  const [draggingId, setDraggingId] = useState(null);

  // Re-pointed every render so a commit runs against the newest closure, the
  // way the old onDrop prop did.
  const commitRef  = useRef(onCommit);  commitRef.current  = onCommit;
  const resolveRef = useRef(resolveId); resolveRef.current = resolveId;

  // Held in a ref so add/removeEventListener see one identity for its lifetime.
  const blockScroll = useRef((e) => { e.preventDefault(); });

  const positionGhost = (x, y) => {
    const node = ghostRef.current;
    if (node) node.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
  };

  // The ghost only exists a render after the drag engages, so its opening
  // position is applied here rather than from the move that created it.
  useLayoutEffect(() => {
    if (draggingId != null && drag.current) positionGhost(drag.current.x, drag.current.y);
  }, [draggingId]);

  const stopScrollBlock = () => document.removeEventListener('touchmove', blockScroll.current);

  // Which card is under this point, resolved back through the list rather than
  // used as the raw attribute string: ids may be numbers and the drop handlers
  // compare with ===. Shared by the move handler and the auto-scroll loop,
  // which has to re-ask as the cards slide under a stationary pointer.
  const updateTarget = (x, y) => {
    const d = drag.current;
    if (!d) return;
    // The pointer is captured, so e.target stays the source card for the whole
    // gesture — the target has to come from geometry. The ghost is
    // pointer-events:none, so it is never what comes back.
    const el  = document.elementFromPoint(x, y);
    const hit = el && el.closest ? el.closest(`[${attr}]`) : null;
    const id  = hit ? resolveRef.current(hit.getAttribute(attr)) : null;
    if (id !== overRef.current) {
      overRef.current = id;
      setOverId(id != null && id !== d.sourceId ? id : null);
    }
  };

  // ── Edge auto-scroll ──────────────────────────────────────────────────────
  // Runs for the whole of an engaged drag rather than starting and stopping
  // with the edge zone: the pointer can sit still inside the zone, firing no
  // events at all, and the page still has to keep moving. One cheap frame
  // callback is a fair price for not having to reason about that.
  const rafRef = useRef(0);

  const stopAutoScroll = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
  };

  const autoScrollStep = () => {
    rafRef.current = 0;
    const d = drag.current;
    if (!d || !d.engaged) return;          // the drag ended: let the loop die
    const vh = window.innerHeight || 0;
    let delta = 0;
    if (d.y < EDGE_SCROLL_ZONE_PX) {
      // Ratio is clamped because the pointer can leave the viewport entirely.
      const near = Math.min(1, (EDGE_SCROLL_ZONE_PX - d.y) / EDGE_SCROLL_ZONE_PX);
      delta = -EDGE_SCROLL_MAX_PPF * near;
    } else if (d.y > vh - EDGE_SCROLL_ZONE_PX) {
      const near = Math.min(1, (d.y - (vh - EDGE_SCROLL_ZONE_PX)) / EDGE_SCROLL_ZONE_PX);
      delta = EDGE_SCROLL_MAX_PPF * near;
    }
    if (delta) {
      window.scrollBy(0, delta);
      // The pointer has not moved, but the cards under it have.
      updateTarget(d.x, d.y);
    }
    rafRef.current = requestAnimationFrame(autoScrollStep);
  };

  const clearPressTimer = () => {
    if (drag.current && drag.current.timer) {
      clearTimeout(drag.current.timer);
      drag.current.timer = null;
    }
  };

  // EVERY exit runs through here — commit, cancel, release off-target, a lost
  // pointer — so a drag can never be left stuck with the page unscrollable.
  const reset = () => {
    clearPressTimer();
    stopScrollBlock();
    stopAutoScroll();
    drag.current    = null;
    overRef.current = null;
    setDraggingId(null);
    setSourceId(null);
    setOverId(null);
  };

  // Unmounting mid-drag must not leave the scroll block behind. Deliberately
  // no setState here: the component is going away.
  useEffect(() => () => {
    stopScrollBlock();
    stopAutoScroll();
    if (drag.current && drag.current.timer) clearTimeout(drag.current.timer);
  }, []);

  const engage = () => {
    const d = drag.current;
    if (!d || d.engaged) return;
    d.engaged = true;
    d.timer   = null;
    draggedRef.current = true;
    setSourceId(d.sourceId);
    setDraggingId(d.sourceId);
    // Capture keeps move/up on this node even when the pointer leaves it.
    try { d.node.setPointerCapture(d.pointerId); } catch { /* not fatal */ }
    document.addEventListener('touchmove', blockScroll.current, { passive: false });
    rafRef.current = requestAnimationFrame(autoScrollStep);
  };

  const handlePointerDown = (e, sourceId) => {
    if (drag.current) return;                                // one gesture at a time
    if (e.pointerType === 'mouse' && e.button !== 0) return; // left button only
    if (e.target && e.target.closest && e.target.closest(REORDER_INTERACTIVE)) return;
    draggedRef.current = false;   // clears a flag a click never came to consume
    drag.current = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      sourceId,
      node: e.currentTarget,
      x: e.clientX, y: e.clientY,
      startX: e.clientX, startY: e.clientY,
      engaged: false,
      timer: null,
    };
    if (e.pointerType !== 'mouse') {
      drag.current.timer = setTimeout(engage, REORDER_LONG_PRESS_MS);
    }
  };

  const handlePointerMove = (e) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    d.x = e.clientX;
    d.y = e.clientY;

    if (!d.engaged) {
      const dist = Math.hypot(e.clientX - d.startX, e.clientY - d.startY);
      if (d.pointerType === 'mouse') {
        if (dist < REORDER_MOUSE_ENGAGE_PX) return;
        engage();
        // Deliberately falls through to the hit test below rather than
        // returning: the move that engages a mouse drag already carries a
        // position, and returning here left overRef null, so a quick
        // press-move-release dropped on nothing and reordered nothing.
      } else {
        // A touch that moves before the press matures is a scroll. Drop the
        // gesture entirely and leave it to the browser.
        if (dist > REORDER_TOUCH_SLOP_PX) { clearPressTimer(); drag.current = null; }
        return;   // a touch drag only ever engages from the press timer
      }
    }

    positionGhost(e.clientX, e.clientY);
    updateTarget(e.clientX, e.clientY);
  };

  const handlePointerUp = (e) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const engaged = d.engaged;
    const target  = overRef.current;
    const source  = d.sourceId;
    try { d.node.releasePointerCapture(d.pointerId); } catch { /* not fatal */ }
    reset();
    // reset() nulls the state, but the commit reads the source off the render
    // that engage() produced, so this still sees it — as onDrop always did.
    if (engaged && target != null && target !== source) commitRef.current(target);
  };

  const handlePointerCancel = (e) => {
    const d = drag.current;
    if (d && e.pointerId !== d.pointerId) return;
    if (d) { try { d.node.releasePointerCapture(d.pointerId); } catch { /* not fatal */ } }
    reset();
  };

  return {
    draggingId,
    ghostRef,
    // True exactly once, for the click WebKit fires after a completed drag.
    consumeDragClick: () => {
      if (!draggedRef.current) return false;
      draggedRef.current = false;
      return true;
    },
    handlers: (id) => ({
      onPointerDown:   (e) => handlePointerDown(e, id),
      onPointerMove:   handlePointerMove,
      onPointerUp:     handlePointerUp,
      onPointerCancel: handlePointerCancel,
    }),
  };
}

export function CalendarPanel({ hasDoubleSlot, clientId, stripeCustomerId, calendarRefreshKey }) {
  // Whether Google can be offered as a destination in the Scheduler's modals,
  // and whether the "Live on Google" list is shown.
  const gbpConnected = useGoogleBizConnected(clientId);
  // Phase 5: reads and writes now carry the act-as-client override, and the
  // load effects must re-run when that context changes (Phase 3 reasoning).
  const { viewingAsClientId } = useViewingAs();
  const today = new Date();
  const [viewDate, setViewDate] = useState({ year: today.getFullYear(), month: today.getMonth() }); // 0-indexed month
  const [selectedDay, setSelectedDay] = useState(null);
  const [unscheduledPosts, setUnscheduledPosts] = useState([]);
  const [accentColour, setAccentColour] = useState('#31DCBA');
  const [scheduledPosts, setScheduledPosts] = useState([]);
  // Timezone: the CLIENT's, from brand_profile.timezone, delivered by
  // GET /api/calendar alongside the posting times and applied in
  // applySettings below.
  //
  // It used to be the browser's (or an ap_timezone localStorage override), and
  // that was wrong in both directions. An admin viewing as a client saw the
  // client's posts on the admin's calendar days, and — worse — the reschedule
  // and chip-modal pickers WROTE in the browser's zone while the panel
  // displayed in this one, so the time that came back was not the time that
  // was typed. Now one zone drives display, both writes, and the server's
  // day-boundary check for the daily cap.
  //
  // Seeded with the same default the backend uses (schedule-slot.js's
  // DEFAULT_TIMEZONE) rather than the browser's, so the first paint before
  // /api/calendar resolves agrees with the server instead of disagreeing with
  // it. ap_timezone stays as an override for the module-level display helper;
  // it deliberately does not apply here, where a client's real zone is known.
  const [timezone, setTimezone] = useState('America/Vancouver');
  // brand_profile.posting_days for the client this panel is showing. null is
  // every day, which is every client's state until they pick — and the state
  // this starts in, so nothing warns before the settings have loaded.
  //
  // Named apart from BrandHubPanel's `postingDays`, which is a different thing:
  // that one is the editable day picker, this one is a read-only copy of what
  // is saved, used only to decide whether a move deserves a question.
  const [clientPostingDays, setClientPostingDays] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [chipScheduleId, setChipScheduleId] = useState(null);
  const [chipScheduleValue, setChipScheduleValue] = useState('');

  // Why a post could not be moved, keyed by post id, plus one slot for the chip
  // modal (which edits a post that is not on the schedule yet).
  //
  // The daily cap answers a 409 with a sentence the client can act on —
  // "You already have 1 post going out that day (limit 1)." — and the panel
  // shows it verbatim rather than formatting its own copy, so the wording lives
  // in one place (daily-limit-gate.js). Anything else that fails gets the
  // generic line: a network blip must not read as a cap.
  const [scheduleErrors, setScheduleErrors] = useState({});
  const CHIP_MODAL_ERROR_KEY = '__chipModal';
  // The drag has no single post to hang a message on — a refused batch is
  // refused whole — so it gets its own slot, rendered above the grid.
  const DRAG_ERROR_KEY = '__drag';
  const messageFor = (err) => (err?.code === 'daily_limit_reached' && err.message)
    ? err.message
    : "Couldn't save — nothing was changed. Try again.";
  const setScheduleError = (key, err) =>
    setScheduleErrors(prev => ({ ...prev, [key]: messageFor(err) }));
  const clearScheduleError = (key) =>
    setScheduleErrors(prev => { const { [key]: _drop, ...rest } = prev; return rest; });
  const [chipDragId, setChipDragId] = useState(null);
  const [chipDragOverId, setChipDragOverId] = useState(null);
  // Pointer-driven reorder, replacing the native HTML5 DnD that iOS never fired
  // (see usePointerReorder). The commits are wrapped in arrows so these can sit
  // with the rest of the state while handleInsertDrop / handleChipDrop are
  // still declared further down.
  const cardDrag = usePointerReorder({
    attr: 'data-sched-card-id',
    setSourceId: setDragId,
    setOverId: setDragOverId,
    onCommit: (targetId) => handleInsertDrop(targetId),
    resolveId: (raw) => {
      const hit = scheduledPosts.find(p => String(p.id) === raw);
      return hit ? hit.id : null;
    },
  });
  const chipDrag = usePointerReorder({
    attr: 'data-sched-chip-id',
    setSourceId: setChipDragId,
    setOverId: setChipDragOverId,
    onCommit: (targetId) => handleChipDrop(targetId),
    resolveId: (raw) => {
      const hit = unscheduledPosts.find(p => String(p.id) === raw);
      return hit ? hit.id : null;
    },
  });
  const [chipModalPostId, setChipModalPostId] = useState(null);
  const [chipModalFields, setChipModalFields] = useState({});
  const [storyEditPostId, setStoryEditPostId] = useState(null);
  const [storyEditFields, setStoryEditFields] = useState({ is_ig_story: false, is_fb_story: false });
  const [storyEditSaving, setStoryEditSaving] = useState(false);
  // Edit (scheduled posts): caption, hashtags, feed platforms and the text
  // overlay, saved as one write that leaves status and scheduled_at alone, so
  // the post stays scheduled. editPost is the row as it was when opened.
  const [editPost, setEditPost]               = useState(null);
  const [editFields, setEditFields]           = useState(null);
  const [editOverlayOpen, setEditOverlayOpen] = useState(false);
  const [editTextSel, setEditTextSel]         = useState(0);   // selected text block
  const [editSaving, setEditSaving]           = useState(false);
  const [editError, setEditError]             = useState('');
  const [weekdaySlot, setWeekdaySlot] = useState('morning');
  const [weekdayTime, setWeekdayTime] = useState('09:00');
  const [weekendSlot, setWeekendSlot] = useState('morning');
  const [weekendTime, setWeekendTime] = useState('09:00');
  // Growth Boost's second slot. '' means not configured, which is what every
  // 2x client starts as: the column is nullable with no default precisely so
  // nobody gets a second daily post at a time they never chose. Approve stays
  // at one a day until one of these is saved.
  const [weekdayTime2, setWeekdayTime2] = useState('');
  const [weekendTime2, setWeekendTime2] = useState('');
  // True just after returning from Growth Boost checkout: highlights the second
  // time inputs and asks for one, instead of dropping the client back on a
  // Scheduler that looks no different from before they paid.
  const [promptSecondTime, setPromptSecondTime] = useState(false);

  const { year, month } = viewDate;

  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DOW = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();

  const prevMonth = () => setViewDate(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  const nextMonth = () => setViewDate(v => v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 });

  // Build grid: 6 rows × 7 cols
  const cells = [];
  for (let i = 0; i < firstDow; i++) {
    cells.push({ day: daysInPrev - firstDow + 1 + i, other: true, key: `prev-${i}` });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, other: false, key: `cur-${d}` });
  }
  const remaining = 42 - cells.length;
  for (let i = 1; i <= remaining; i++) {
    cells.push({ day: i, other: true, key: `next-${i}` });
  }

  const dateKey = (d) => {
    const m = String(month + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${year}-${m}-${dd}`;
  };

  const isToday = (d) => !!(year === today.getFullYear() && month === today.getMonth() && d === today.getDate());
  const isSelected = (d) => selectedDay === d;

  const scheduledByDate = {};
  scheduledPosts.forEach(p => {
    if (!p.scheduled_at) return;
    const key = new Date(p.scheduled_at).toLocaleDateString('en-CA', { timeZone: timezone });
    if (!scheduledByDate[key]) scheduledByDate[key] = [];
    scheduledByDate[key].push(p);
  });

  const handleDayClick = (cell) => {
    if (cell.other) return;
    const key = dateKey(cell.day);
    if (!scheduledByDate[key]?.length) { setSelectedDay(null); return; }
    setSelectedDay(prev => prev === cell.day ? null : cell.day);
  };

  const selectedKey = selectedDay ? dateKey(selectedDay) : null;
  const selectedPosts = selectedKey ? (scheduledByDate[selectedKey] || []) : [];

  const selectedDateLabel = selectedDay
    ? `${MONTH_NAMES[month]} ${selectedDay}, ${year}`
    : '';

  // Phase 5: the panel's three reads (unscheduled posts, scheduled posts, brand
  // posting-time settings) were three direct Supabase queries and are now one
  // /api/calendar round trip. They are kept as three named functions because
  // the panel calls them independently after writes; each now refreshes from
  // the same shared fetch rather than issuing its own query.
  const loadCalendar = React.useCallback(async () => {
    if (!clientId) return null;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/calendar`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error('[Calendar] load failed:', err?.message || err);
      return null;
    }
  }, [clientId]);

  const fetchApprovedPosts = async () => {
    const d = await loadCalendar();
    if (d) setUnscheduledPosts(d.unscheduled ?? []);
  };

  const deriveSlot = (t) => t === '09:00' ? 'morning' : t === '18:00' ? 'evening' : '';

  const applySettings = (row) => {
    if (!row) return;
    // Before accent/times: everything below renders and writes against it.
    if (row.timezone) setTimezone(row.timezone);
    // Assigned unconditionally, like the *_2 pair below: NULL is the meaningful
    // "every day" state and has to be able to overwrite a stale value. It
    // arrives over clientScopedFetch, so under an admin's viewing-as these are
    // the VIEWED client's days — the same channel timezone comes down, which is
    // what makes the warning describe the right person's calendar.
    setClientPostingDays(row.posting_days ?? null);
    if (row.accent_color) setAccentColour(row.accent_color);
    if (row.weekday_time) { setWeekdayTime(row.weekday_time); setWeekdaySlot(deriveSlot(row.weekday_time)); }
    if (row.weekend_time) { setWeekendTime(row.weekend_time); setWeekendSlot(deriveSlot(row.weekend_time)); }
    // Assigned unconditionally, unlike the pair above: null is the meaningful
    // "not configured" state and has to be able to overwrite a stale value,
    // where a blank weekday_time genuinely should keep the 09:00 default.
    setWeekdayTime2(row.weekday_time_2 || '');
    setWeekendTime2(row.weekend_time_2 || '');
  };

  const fetchBrandSettings = async () => {
    const d = await loadCalendar();
    if (d) applySettings(d.settings);
  };

  const [postingTimesSaveState, setPostingTimesSaveState] = useState('idle');
  const [growthBoostLoading, setGrowthBoostLoading] = useState(false);
  const handleGrowthBoostCheckout = async () => {
    // hasDoubleSlot is belt and braces: the owned chip renders as a div with
    // no handler, so this is already unreachable. It is here so the guard
    // survives someone later giving that branch an onClick.
    if (hasDoubleSlot || growthBoostLoading) return;
    setGrowthBoostLoading(true);
    try {
      const res = await authedFetch('/api/create-2x-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnPanel: 'calendar' }),
      });
      const json = await res.json();
      if (json.url) window.location.href = json.url;
      else setGrowthBoostLoading(false);
    } catch {
      setGrowthBoostLoading(false);
    }
  };

  const handleSavePostingTimes = async () => {
    if (!clientId) return;
    setPostingTimesSaveState('saving');
    // No clientId in the body: requireOwnerOrAdmin reads the body before the
    // query string, so one here would beat the viewing-as override.
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/calendar/settings`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        // The second pair goes up even when blank, so clearing one persists.
        // The server accepts it whatever the plan (see CALENDAR_SETTINGS_COLUMNS);
        // it is only ever ACTED on when two_x_daily is true.
        body:    JSON.stringify({ settings: {
          weekday_time:   weekdayTime,
          weekend_time:   weekendTime,
          weekday_time_2: hasDoubleSlot ? (weekdayTime2 || null) : undefined,
          weekend_time_2: hasDoubleSlot ? (weekendTime2 || null) : undefined,
        } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      console.error('[PostingTimes] persist failed:', err.message);
      setPostingTimesSaveState('error');
      return;
    }
    setPostingTimesSaveState('saved');
    // The prompt has served its purpose once a second time exists.
    if (weekdayTime2 || weekendTime2) setPromptSecondTime(false);
    setTimeout(() => setPostingTimesSaveState('idle'), 2000);
  };

  const fetchScheduledPosts = async () => {
    const d = await loadCalendar();
    if (d) setScheduledPosts(d.scheduled ?? []);
  };

  // Just back from Growth Boost checkout.
  //
  // create-2x-checkout sends success_url=?panel=calendar&2xdaily=success when
  // the upsell was pressed here, so this panel is already what the client lands
  // on. Without the prompt they land on a Scheduler that looks exactly as it
  // did before they paid — the entitlement is live but nothing visibly changed,
  // because a second slot with no time in it schedules nothing.
  //
  // The param is stripped so a refresh does not re-open the prompt. BrandHubPanel
  // strips it too, for its own return path; it is not mounted when panel=calendar,
  // so both need to.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('2xdaily') !== 'success') return;
    setPromptSecondTime(true);
    const cleaned = window.location.search
      .replace(/[?&]2xdaily=[^&]*/, '')
      .replace(/^&/, '?');
    window.history.replaceState({}, '', window.location.pathname + (cleaned === '?' ? '' : cleaned));
  }, []);

  // One fetch on mount rather than three: the three named refreshers above each
  // call /api/calendar, so calling all three here would issue the same request
  // three times. They stay available for the targeted refreshes after writes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await loadCalendar();
      if (cancelled || !d) return;
      setUnscheduledPosts(d.unscheduled ?? []);
      setScheduledPosts(d.scheduled ?? []);
      applySettings(d.settings);
    })();
    return () => { cancelled = true; };
  }, [calendarRefreshKey, clientId, viewingAsClientId, loadCalendar]);

  // Re-fetch every 2 minutes so posts published by pg_cron drop off the grid
  // without requiring a page reload.
  useEffect(() => {
    if (!clientId) return;
    const interval = setInterval(fetchScheduledPosts, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [clientId]);

  // (Dead-code purge: the `Unscheduled Posts` section was removed in Week 2.
  // Its handlers — `handleSchedule`, `setCardField`, `pillClass`, `pillLabel`,
  // and the `scheduleState` / `confirmedIds` / `failedIds` / `blockedIds` state —
  // were also removed here. Live scheduling now happens via the chip + modal
  // flow below: `handleScheduleFromChip`, `handleChipModalSave`,
  // `handleReschedulePost`, `handleInsertDrop`.)

  const handleDeletePost = async (postId) => {
    let error = null;
    try { await deletePost(postId); } catch (err) { error = err; }
    if (!error) {
      setScheduledPosts(prev => prev.filter(p => p.id !== postId));
      setConfirmDeleteId(null);
    }
  };

  const handleUnschedulePost = async (postId) => {
    let error = null;
    try { await patchPost(postId, { status: 'pending', scheduled_at: null }); }
    catch (err) { error = err; }
    if (!error) {
      const post = scheduledPosts.find(p => p.id === postId);
      setScheduledPosts(prev => prev.filter(p => p.id !== postId));
      if (post) {
        setUnscheduledPosts(prev => [...prev, { ...post, status: 'pending', scheduled_at: null }]);
      }
    }
  };

  const formatScheduledLabel = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dow = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: timezone });
    const mo  = d.toLocaleDateString('en-US', { month: 'short', timeZone: timezone });
    const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: timezone });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: timezone });
    return `${dow} ${mo} ${day} · ${time}`;
  };

  // Prefill for the reschedule picker. Reads the stored instant on the CLIENT's
  // wall clock (it used to use the browser's getFullYear/getHours), so opening
  // the picker shows the same time the card above it shows, and round-trips
  // through localInputToIso without moving the post.
  const isoToLocalInput = (iso) => isoToLocalInputInZone(iso, timezone);

  // Ask before putting a post on a day this client does not post on.
  //
  // TRUE MEANS GO AHEAD — no warning was needed, or the client said yes. The
  // scheduler never produces one of these days by itself (computeNextSlotFor
  // skips them), so this fires only when a PERSON has aimed at one, and the
  // answer to a person aiming at something is a question, not a refusal.
  // posting_days says where posts go by default; it is not a rule about where
  // they may be put. The server stays permissive for the same reason, and the
  // daily cap — which IS a rule — still refuses on its own terms afterwards.
  //
  // window.confirm, like the delete-chip confirm above: this is a two-option
  // question with a destructive-ish default, and the panel has no modal
  // primitive that adds anything to that.
  const confirmPostingDay = (iso) => {
    const warning = postingDayWarning(iso, timezone, clientPostingDays);
    if (!warning) return true;
    return window.confirm(warning);
  };

  const handleReschedulePost = async (postId, dtLocal) => {
    if (!dtLocal) return;
    // The picker's value is a wall-clock reading with no zone. Resolved in the
    // client's zone, not the browser's — see localInputToIso.
    const iso = localInputToIso(dtLocal, timezone);
    // Cancel leaves the post exactly where it was: nothing has been written and
    // nothing optimistic has been applied at this point.
    if (!confirmPostingDay(iso)) return false;
    let error = null;
    try { await patchPost(postId, { scheduled_at: iso }); } catch (err) { error = err; }
    if (error) { setScheduleError(postId, error); return false; }
    clearScheduleError(postId);
    setScheduledPosts(prev => prev.map(p => p.id === postId ? { ...p, scheduled_at: iso } : p));
    return true;
  };

  const handleInsertDrop = async (targetId) => {
    const sourceId = dragId;
    setDragId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;

    // 1. Full list sorted ascending by scheduled_at (same order the grid renders).
    const sorted = [...scheduledPosts].sort((a, b) => {
      const atA = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Infinity;
      const atB = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Infinity;
      return atA - atB;
    });
    const dragged = sorted.find(p => p.id === sourceId);
    const target  = sorted.find(p => p.id === targetId);
    if (!dragged || !target) return;

    // The ordered time slots stay fixed; posts get reassigned to them in the new order.
    const slots = sorted.map(p => p.scheduled_at);

    // 2. Remove dragged from its current index. 3. Insert it at the drop index.
    const reordered = sorted.filter(p => p.id !== sourceId);
    const insertAt = reordered.findIndex(p => p.id === targetId);
    reordered.splice(insertAt, 0, dragged);

    // 4. Redistribute timestamps in order; collect only rows that actually changed.
    const changed = [];
    reordered.forEach((p, i) => {
      if (p.scheduled_at !== slots[i]) changed.push({ id: p.id, scheduled_at: slots[i] });
    });
    if (changed.length === 0) return;
    const changedById = new Map(changed.map(c => [c.id, c.scheduled_at]));

    // ASKED ABOUT THE CARD THE PERSON GRABBED, not about every row the reorder
    // shifts.
    //
    // A reorder is a PERMUTATION: `slots` is the existing timestamps and the
    // posts are dealt back into them, so the set of occupied days is identical
    // before and after. No day gains a post that did not already have one, and
    // warning per changed row would fire two or three boxes for one gesture —
    // for a rearrangement that did not put content anywhere new.
    //
    // What the client did do is put THIS post on that day, so that is the one
    // question worth asking. Cancel abandons the whole reorder before any
    // optimistic update or write, so every card stays where it was.
    const draggedTo = changedById.get(sourceId);
    if (draggedTo && !confirmPostingDay(draggedTo)) return;

    // Optimistic update.
    const prevPosts = scheduledPosts;
    setScheduledPosts(prev => prev.map(p =>
      changedById.has(p.id) ? { ...p, scheduled_at: changedById.get(p.id) } : p
    ));

    // 5. Batch persist all affected rows.
    // Phase 5: was a Promise.all fan of N single-row updates, one request per
    // changed row. Now one /api/posts/schedule-batch call. The endpoint is
    // deliberately NOT atomic — it runs the same N updates server-side, so the
    // partial-failure behaviour is unchanged and this rollback still applies.
    // What moved is where the client scope comes from: the server's resolved
    // client, not a clientId the browser attached to each row.
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/posts/schedule-batch`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ updates: changed }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) {
        const first = (body.results || []).find(r => !r.ok);
        const err = new Error(first?.error || body.error || `HTTP ${res.status}`);
        // Carried so messageFor can tell the cap's sentence from a generic
        // failure. A 409 has no `results` — the batch is refused whole — so
        // body.error is the message, and body.code is what marks it as the cap.
        if (body.code) err.code = body.code;
        throw err;
      }
      clearScheduleError(DRAG_ERROR_KEY);
    } catch (err) {
      console.error('[SchedulerInsert] persist failed:', err.message);
      // The rollback was already here and is what makes this safe to surface:
      // the grid returns to the order it had, and the message says why it
      // sprang back. Without it a refused drag just silently undid itself.
      setScheduledPosts(prevPosts);
      setScheduleError(DRAG_ERROR_KEY, err);
    }
  };

  const handleScheduleFromChip = async (postId, dtLocal) => {
    if (!dtLocal) return;
    // Same zone fix as handleReschedulePost: this picker is the same
    // datetime-local control and had the same browser-zone conversion.
    const iso = localInputToIso(dtLocal, timezone);
    // Cancel leaves the post unscheduled, in the chip rail where it was.
    if (!confirmPostingDay(iso)) return;
    let error = null;
    try {
      await patchPost(postId, { scheduled_at: iso, status: 'scheduled', approved_at: new Date().toISOString() });
    } catch (err) { error = err; }
    if (error) { setScheduleError(postId, error); return; }
    clearScheduleError(postId);
    const post = unscheduledPosts.find(p => p.id === postId);
    setUnscheduledPosts(prev => prev.filter(p => p.id !== postId));
    if (post) setScheduledPosts(prev => [...prev, { ...post, scheduled_at: iso, status: 'scheduled' }]);
    setChipScheduleId(null);
    setChipScheduleValue('');
  };

  const openChipModal = (post) => {
    const hashtags = Array.isArray(post.hashtags)
      ? post.hashtags.join(' ')
      : (post.hashtags || '');
    const platforms = Array.isArray(post.platforms)
      ? post.platforms.map(p => p.toLowerCase())
      : [];
    setChipModalFields({
      caption: post.caption || '',
      hashtags,
      platforms,
      date: '',
      time: '',
      color: post.dot_color || accentColour,
    });
    clearScheduleError(CHIP_MODAL_ERROR_KEY);
    setChipModalPostId(post.id);
  };

  const openStoryEdit = (post) => {
    setStoryEditPostId(post.id);
    setStoryEditFields({ is_ig_story: !!post.is_ig_story, is_fb_story: !!post.is_fb_story });
  };

  const handleStoryEditSave = async () => {
    if (!storyEditPostId) return;
    setStoryEditSaving(true);
    const { is_ig_story, is_fb_story } = storyEditFields;
    try { await patchPost(storyEditPostId, { is_ig_story, is_fb_story }); }
    catch (err) { console.error('[StoryEdit] save failed:', err.message); }
    setScheduledPosts(prev => prev.map(p => p.id === storyEditPostId ? { ...p, is_ig_story, is_fb_story } : p));
    setStoryEditPostId(null);
    setStoryEditSaving(false);
  };

  // Text can only be baked into a single still image: video posts publish
  // video_url and carousels publish media_items, so image_url isn't what goes out.
  const canEditOverlay = (post) => !!post?.image_url && (!post.media_type || post.media_type === 'image');
  // Edit depends on columns only the updated GET /api/calendar returns. A row
  // from an older backend lacks the KEYS (a new backend returns them, null or
  // not), and saving from it would drop typed hashtags, skip the still-scheduled
  // guard and lose the overlay settings — so the modal refuses to save instead.
  const editSupported = (post) => !!post && 'hashtags' in post && 'media_type' in post && 'text_overlay' in post;
  // The saved text blocks — a pre-blocks row's single object reads as one
  // (overlayBlocksOf) — or null for none.
  const savedOverlayOf = (post) => {
    const blocks = overlayBlocksOf(post?.text_overlay).filter(b => b.text);
    return blocks.length ? blocks : null;
  };
  // Text this app baked BEFORE overlays were tracked has no clean original to
  // re-render from: the image Edit would draw on already contains it. Every
  // such image is a scratch upload named 'carousel-composited-<ts>-…' (Carousel
  // slides and Post Generator text alike), so the base's filename gives it
  // away — whether the base is image_url itself or a saved base from an earlier
  // edit of one of these. Text that came with the image some other way (the
  // Photo Editor, AI lettering, an uploaded design) can't be detected.
  const hasUntrackedText = (post) =>
    /\/carousel-composited-\d+-/.test(post?.overlay_base_image_url || post?.image_url || '');

  const openEdit = (post) => {
    setEditPost(post);
    setEditFields({
      caption:   post.caption || '',
      hashtags:  Array.isArray(post.hashtags) ? post.hashtags.join(' ') : (post.hashtags || ''),
      platforms: Array.isArray(post.platforms) ? post.platforms.map(p => String(p).toLowerCase()) : [],
      overlay:   (savedOverlayOf(post) || [{}]).map(b => ({ ...DEFAULT_TEXT_OVERLAY, ...b })),
      gbp:       editableGbpSettings(post.gbp_settings),
    });
    setEditOverlayOpen(false);
    setEditTextSel(0);
    setEditError('');
  };
  const closeEdit = () => { if (!editSaving) { setEditPost(null); setEditFields(null); } };

  // Save = the confirm step, same as Post Generator's Apply: if the text
  // changed, bake it onto the ORIGINAL image (overlay_base_image_url, so text
  // never stacks), then write everything in one PATCH conditioned on the row
  // still being 'scheduled'. If the cron has claimed it meanwhile, nothing is
  // written and the user is told.
  const handleEditSave = async () => {
    const post = editPost;
    if (!post || !editFields || editSaving || !editSupported(post)) return;
    const { caption, hashtags, platforms, overlay, gbp } = editFields;
    if (platforms.length === 0 && !post.is_ig_story && !post.is_fb_story) {
      setEditError('Pick at least one place to post, or use Unschedule to take it off the schedule.');
      return;
    }
    // Google options are only checked and saved while Google is a destination
    // and the post is not a video (which Google skips anyway).
    const gbpEditable = platforms.includes('google_business') && (post.media_type || 'image') !== 'video';
    if (gbpEditable) {
      const problem = gbpSettingsProblem(gbp);
      if (problem) { setEditError(problem); return; }
    }
    const tags = hashtags.split(/\s+/).filter(Boolean).map(t => t.startsWith('#') ? t : `#${t}`);
    const updates = {
      caption,
      // Keep the row's own format: generated posts store a string, uploaded
      // ones an array. The publisher interpolates whichever it finds.
      hashtags: Array.isArray(post.hashtags) ? tags : tags.join(' '),
      platforms,
      ...(gbpEditable ? { gbp_settings: gbpSettingsForSave(gbp) } : {}),
    };
    const nextOverlay = liveOverlayBlocks(overlay);
    const overlayChanged = canEditOverlay(post)
      && textOverlayKey(nextOverlay) !== textOverlayKey(savedOverlayOf(post));
    setEditSaving(true);
    setEditError('');
    try {
      if (overlayChanged) {
        const base = post.overlay_base_image_url || post.image_url;
        updates.image_url              = nextOverlay ? await applyTextOverlay(base, nextOverlay, 0) : base;
        updates.overlay_base_image_url = nextOverlay ? base : null;
        updates.text_overlay           = nextOverlay;
      }
      await patchPost(post.id, updates, { ifStatus: 'scheduled' });
      setScheduledPosts(prev => prev.map(p => p.id === post.id ? { ...p, ...updates } : p));
      setEditPost(null);
      setEditFields(null);
    } catch (err) {
      console.error('[SchedulerEdit] save failed:', err?.message || err);
      if (err?.code === 'status_changed') {
        setEditError("This post has already started publishing or was taken off the schedule, so the edit wasn't saved.");
        fetchScheduledPosts();
      } else if (err?.code === 'gbp_settings_invalid') {
        setEditError(err.message);
      } else {
        setEditError("Couldn't save — nothing was changed. Try again.");
      }
    } finally {
      setEditSaving(false);
    }
  };

  const handleChipModalSave = async () => {
    const { caption, hashtags, platforms, date, time, color } = chipModalFields;
    const newHashtags = hashtags.split(/\s+/).filter(Boolean).map(t => t.startsWith('#') ? t : `#${t}`);
    const updates = { caption, hashtags: newHashtags, platforms, dot_color: color };
    let scheduled_at = null;
    if (date && time) {
      // Was `new Date(\`${date}T${time}:00\`)`, which resolves in the browser's
      // zone. The modal's own date input already bounds itself with
      // `min={... timeZone: timezone}`, so the two halves of this control were
      // reading different clocks.
      scheduled_at = localDateTimeToIso(date, time, timezone);
      // Asked before anything is written. Cancel keeps the modal open with the
      // client's edits intact, so they can pick a different day rather than
      // losing the caption they just wrote.
      if (!confirmPostingDay(scheduled_at)) return;
      updates.scheduled_at = scheduled_at;
      updates.status = 'scheduled';
      updates.approved_at = new Date().toISOString();
    }
    // This used to log the failure and then fall through to the optimistic
    // update and setChipModalPostId(null) — a refused save closed the modal and
    // showed the post as scheduled anyway. With a cap in front of it that turns
    // a 409 into a post the client believes is queued and which does not exist,
    // so the failure now keeps the modal open and says why.
    try {
      await patchPost(chipModalPostId, updates);
    } catch (err) {
      console.error('[ChipModal] save failed:', err.message);
      setScheduleError(CHIP_MODAL_ERROR_KEY, err);
      return;
    }
    clearScheduleError(CHIP_MODAL_ERROR_KEY);
    const post = unscheduledPosts.find(p => p.id === chipModalPostId);
    if (scheduled_at && post) {
      setUnscheduledPosts(prev => prev.filter(p => p.id !== chipModalPostId));
      setScheduledPosts(prev => [...prev, { ...post, ...updates, scheduled_at }]);
    } else {
      setUnscheduledPosts(prev => prev.map(p =>
        p.id === chipModalPostId ? { ...p, caption, hashtags: newHashtags, platforms, dot_color: color } : p
      ));
    }
    setChipModalPostId(null);
  };

  const handleChipDrop = (targetId) => {
    const sourceId = chipDragId;
    setChipDragId(null);
    setChipDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    setUnscheduledPosts(prev => {
      const arr = [...prev];
      const srcIdx = arr.findIndex(p => p.id === sourceId);
      const tgtIdx = arr.findIndex(p => p.id === targetId);
      if (srcIdx === -1 || tgtIdx === -1) return prev;
      const [moved] = arr.splice(srcIdx, 1);
      arr.splice(tgtIdx, 0, moved);
      return arr;
    });
  };

  const handleDeleteChip = async (e, postId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this post?')) return;
    let error = null;
    try { await deletePost(postId); } catch (err) { error = err; }
    if (!error) setUnscheduledPosts(prev => prev.filter(p => p.id !== postId));
  };

  const swatches = [accentColour, ...SWATCH_PRESETS.filter(c => c.toLowerCase() !== accentColour.toLowerCase())];

  return (
    <div className="cal-wrap">
      {/* Header */}
      <div className="cal-header">
        <button className="cal-nav-btn" onClick={prevMonth}>← Prev</button>
        <div className="cal-month">{MONTH_NAMES[month]} {year}</div>
        <button className="cal-nav-btn" onClick={nextMonth}>Next →</button>
        <div className="cal-legend">
          <div className="cal-legend-item"><span>⏱</span> Scheduled</div>
          <Tip text="Dots use your chosen post colour. ⏱ = scheduled and ready to go. Published posts drop off the calendar automatically." below />
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="cal-grid-wrap">
        <div className="cal-dow-row">
          {DOW.map(d => <div key={d} className="cal-dow">{d}</div>)}
        </div>
        <div className="cal-days">
          {cells.map(cell => {
            const key = cell.other ? null : dateKey(cell.day);
            const dots = key ? (scheduledByDate[key] || []) : [];
            const selected = !cell.other && isSelected(cell.day);
            const todayFlag = !cell.other && isToday(cell.day);
            return (
              <div
                key={cell.key}
                className={`cal-day${cell.other ? ' cal-day-other' : ''}${selected ? ' selected' : ''}`}
                onClick={() => handleDayClick(cell)}
              >
                <div className={`cal-day-num${todayFlag ? ' today' : ''}`}>{cell.day}</div>
                {dots.length > 0 && (
                  <div className="cal-dots">
                    {dots.map((p, i) => (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:2}}>
                        <div className="cal-dot" style={{ background: p.dot_color || 'var(--accent)' }} />
                        <span style={{fontSize:16,lineHeight:1,opacity:0.9}}>
                          {p.status === 'published' ? '✓' : '⏱'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Posts already out on Google, which can still be edited or deleted there. */}
      {gbpConnected && <GoogleLivePosts clientId={clientId} />}

      {/* Growth Boost is paid for but doing nothing: the cap allows two a day,
          and approve will only ever fill one, because there is no second time
          to put a post at.

          OUTSIDE the Scheduled Posts block on purpose. It lived in there for a
          moment, which hid it behind `scheduledPosts.length > 0` — so the one
          client who most needs it, a new Growth Boost account with an empty
          queue, was the one client who could never see it.

          Shown until ONE of the two times is set, not until both: a client who
          wants a second post on weekdays only is configured, and telling them
          to "set your second time" would be false. */}
      {hasDoubleSlot && !weekdayTime2 && !weekendTime2 && (
        <div className="sched-boost-banner" role="status">
          Growth Boost is active. Set your second time to start posting twice a day.
        </div>
      )}

      {/* POSTING TIME */}
      <div className="sched-grid-wrap">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div className="sched-grid-heading" style={{ margin: 0 }}>Posting Time</div>
          {/* 2x / day. This replaced an "Add 2x Growth Boost — $19/mo →"
              button that stood in exactly this spot and did exactly this — same
              endpoint, same redirect. It is the same chip as the one in Brand
              Hub's posting frequency row, in the place a client already looks
              when they want to post more often, so the two read as one feature
              rather than two upsells.

              The button it replaced simply VANISHED once Growth Boost was
              owned, so the Scheduler never said you had it; the banner above
              only appears while no second time is set, and says nothing once
              one is. The Active state fills that silence.

              hasDoubleSlot is the shell's hasTwoX, read over clientScopedFetch,
              so under viewing-as this is the VIEWED client's entitlement. */}
          {hasDoubleSlot ? (
            <div
              className="bh-chip bh-chip-2x owned"
              aria-label="2x per day — Growth Boost active"
            >
              2x / day · Active
            </div>
          ) : (
            <button
              type="button"
              className="bh-chip bh-chip-2x"
              onClick={handleGrowthBoostCheckout}
              disabled={growthBoostLoading}
              aria-label="Add 2x per day with Growth Boost, $19 per month"
            >
              {growthBoostLoading ? 'Redirecting…' : '2x / day · $19/mo'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="chip-modal-label" style={{ minWidth: 134, flexShrink: 0 }}>Weekdays (Mon–Fri)</span>
            <div className="chip-modal-time-row">
              <button
                className={`chip-modal-time-btn${weekdaySlot === 'morning' ? ' active' : ''}`}
                onClick={() => { setWeekdaySlot('morning'); setWeekdayTime('09:00'); }}
              >Morning</button>
              <button
                className={`chip-modal-time-btn${weekdaySlot === 'evening' ? ' active' : ''}`}
                onClick={() => { setWeekdaySlot('evening'); setWeekdayTime('18:00'); }}
              >Evening</button>
              <input
                type="time"
                className="chip-modal-time-input"
                value={weekdayTime}
                onChange={(e) => { setWeekdayTime(e.target.value); setWeekdaySlot(''); }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="chip-modal-label" style={{ minWidth: 134, flexShrink: 0 }}>Weekends (Sat–Sun)</span>
            <div className="chip-modal-time-row">
              <button
                className={`chip-modal-time-btn${weekendSlot === 'morning' ? ' active' : ''}`}
                onClick={() => { setWeekendSlot('morning'); setWeekendTime('09:00'); }}
              >Morning</button>
              <button
                className={`chip-modal-time-btn${weekendSlot === 'evening' ? ' active' : ''}`}
                onClick={() => { setWeekendSlot('evening'); setWeekendTime('18:00'); }}
              >Evening</button>
              <input
                type="time"
                className="chip-modal-time-input"
                value={weekendTime}
                onChange={(e) => { setWeekendTime(e.target.value); setWeekendSlot(''); }}
              />
            </div>
          </div>
          {/* Second slot — Growth Boost only. Hidden entirely without it, so a
              standard client sees exactly the panel they saw before. */}
          {hasDoubleSlot && (
            <>
              <div
                style={{
                  display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4,
                  paddingTop: 12, borderTop: '1px solid var(--border)',
                  ...(promptSecondTime ? {
                    border: '1px solid var(--accent)', borderRadius: 8,
                    padding: 12, background: 'rgba(49,220,186,0.06)',
                  } : {}),
                }}
              >
                {promptSecondTime && (
                  <div style={{ color: 'var(--accent)', fontSize: 12.5, fontWeight: 700 }} role="status">
                    Pick your second posting time
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="chip-modal-label" style={{ minWidth: 134, flexShrink: 0 }}>Second post — weekdays</span>
                  <div className="chip-modal-time-row">
                    <input
                      type="time"
                      aria-label="Second posting time, weekdays"
                      className="chip-modal-time-input"
                      value={weekdayTime2}
                      onChange={(e) => setWeekdayTime2(e.target.value)}
                    />
                    {weekdayTime2 && (
                      <button className="chip-modal-time-btn" onClick={() => setWeekdayTime2('')}>Clear</button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className="chip-modal-label" style={{ minWidth: 134, flexShrink: 0 }}>Second post — weekends</span>
                  <div className="chip-modal-time-row">
                    <input
                      type="time"
                      aria-label="Second posting time, weekends"
                      className="chip-modal-time-input"
                      value={weekendTime2}
                      onChange={(e) => setWeekendTime2(e.target.value)}
                    />
                    {weekendTime2 && (
                      <button className="chip-modal-time-btn" onClick={() => setWeekendTime2('')}>Clear</button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <button
              className="chip-modal-save"
              onClick={handleSavePostingTimes}
              disabled={postingTimesSaveState === 'saving'}
            >
              {postingTimesSaveState === 'saving' ? 'Saving…'
                : postingTimesSaveState === 'saved' ? 'Saved ✓'
                : postingTimesSaveState === 'error' ? 'Retry'
                : 'Save'}
            </button>
            {postingTimesSaveState === 'error' && (
              <span style={{ color: '#FF5252', fontSize: 13 }}>Save failed — check console.</span>
            )}
          </div>
        </div>
      </div>

      {/* Scheduled Posts Thumbnail Grid (drag-and-drop reorder) */}
      {scheduledPosts.length > 0 && (
        <div className="sched-grid-wrap">
          <div className="sched-grid-heading">Scheduled Posts</div>
          {/* A refused drag has already sprung back by the time this renders —
              handleInsertDrop rolls the grid to its previous order — so this is
              the only thing telling the client why. */}
          {scheduleErrors[DRAG_ERROR_KEY] && (
            <div className="sched-limit-error" role="alert" style={{ marginBottom: 10 }}>
              {scheduleErrors[DRAG_ERROR_KEY]}
            </div>
          )}
          <div className="sched-grid">
            {[...scheduledPosts]
              .sort((a, b) => {
                const atA = a.scheduled_at ? new Date(a.scheduled_at).getTime() : Infinity;
                const atB = b.scheduled_at ? new Date(b.scheduled_at).getTime() : Infinity;
                return atA - atB;
              })
              .map(post => {
                const platformsArr = Array.isArray(post.platforms) ? post.platforms : [];
                const norm = platformsArr.map(p => String(p).toLowerCase().trim());
                const hasIG = norm.some(p => p.startsWith('insta'));
                const hasFB = norm.some(p => p.startsWith('face'));
                const platformBadge = (hasIG && hasFB) ? 'Both' : hasIG ? 'IG' : hasFB ? 'FB' : '';
                const period = post.scheduled_at
                  ? (parseInt(new Date(post.scheduled_at).toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: timezone })) < 12 ? 'Morning' : 'Evening')
                  : null;
                const captionPreview = (post.caption || '').slice(0, 60) + ((post.caption || '').length > 60 ? '…' : '');
                const isDragging = dragId === post.id;
                const isOver = dragOverId === post.id && dragId !== post.id;
                const isRescheduling = rescheduleId === post.id;
                return (
                  <div
                    key={post.id}
                    className={`sched-card${isDragging ? ' dragging' : ''}${isOver ? ' drop-target' : ''}`}
                    data-sched-card-id={post.id}
                    {...cardDrag.handlers(post.id)}
                  >
                    <div className="sched-card-thumb">
                      {post.image_url
                        ? <img src={post.image_url} alt="" draggable={false} />
                        : <div className="sched-card-thumb-fallback" style={{ background: post.dot_color || '#31DCBA' }} />
                      }
                      {platformBadge && (
                        <div className="sched-card-platform-badge">{platformBadge}</div>
                      )}
                      {post.dot_color && (
                        <div className="sched-card-dot-badge" style={{ background: post.dot_color }} />
                      )}
                      <div className="sched-card-overlay">
                        <button
                            className="sched-card-overlay-btn"
                            title="Reschedule"
                            aria-label="Reschedule this post"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRescheduleId(post.id);
                              setRescheduleValue(isoToLocalInput(post.scheduled_at));
                            }}
                          >📅</button>
                        <button
                          className="sched-card-overlay-btn"
                          title="Edit caption, platforms and text"
                          aria-label="Edit this post"
                          onClick={(e) => { e.stopPropagation(); openEdit(post); }}
                        >✎</button>
                        <button
                          className="sched-card-overlay-btn"
                          title="Edit story destinations"
                          aria-label="Edit story destinations"
                          onClick={(e) => { e.stopPropagation(); openStoryEdit(post); }}
                          style={(post.is_ig_story || post.is_fb_story) ? { background:'rgba(49,220,186,0.28)', borderColor:'#31DCBA' } : undefined}
                        >✦</button>
                        <button
                          className="sched-card-overlay-btn sched-card-overlay-danger"
                          title="Unschedule"
                          aria-label="Unschedule this post"
                          onClick={(e) => { e.stopPropagation(); handleUnschedulePost(post.id); }}
                        >🗑</button>
                      </div>
                    </div>
                    <div className="sched-card-meta">
                      {period && (
                        <div className="sched-card-period">{period}</div>
                      )}
                      <div className="sched-card-time">{formatScheduledLabel(post.scheduled_at)}</div>
                      <div className="sched-card-caption">{captionPreview}</div>
                      <div className="sched-card-platforms">
                        {platformsArr.map((p, i) => {
                          const lower = String(p).toLowerCase();
                          const label = lower.startsWith('insta') ? 'IG' : lower.startsWith('face') ? 'FB' : String(p).slice(0,2).toUpperCase();
                          return <span key={`${post.id}-${i}`} className="sched-card-pf">{label}</span>;
                        })}
                        {post.is_ig_story && <span className="sched-card-pf story">IG Story</span>}
                        {post.is_fb_story && <span className="sched-card-pf story">FB Story</span>}
                      </div>
                    </div>
                    {isRescheduling && (
                      <div className="sched-card-reschedule" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="datetime-local"
                          className="sched-card-dtinput"
                          value={rescheduleValue}
                          onChange={(e) => setRescheduleValue(e.target.value)}
                        />
                        <div className="sched-card-reschedule-actions">
                          <button
                            className="sched-card-confirm"
                            // The picker no longer closes itself on Save. It
                            // used to close before the request resolved, so a
                            // refusal had nowhere to appear; now the handler
                            // closes it on success and leaves it open, with the
                            // reason, on a 409.
                            onClick={async () => {
                              const moved = await handleReschedulePost(post.id, rescheduleValue);
                              if (moved) setRescheduleId(null);
                            }}
                          >Save</button>
                          <button
                            className="sched-card-cancel"
                            onClick={() => { clearScheduleError(post.id); setRescheduleId(null); }}
                            aria-label="Cancel reschedule"
                            title="Cancel"
                          >✕</button>
                        </div>
                        {scheduleErrors[post.id] && (
                          <div className="sched-limit-error" role="alert">{scheduleErrors[post.id]}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

        </div>
      )}

      {/* Day Detail Drawer */}
      {selectedDay && selectedPosts.length > 0 && (
        <div className="cal-detail">
          <div className="cal-detail-header">
            <div className="cal-detail-title">{selectedDateLabel}</div>
            <button className="cal-detail-close" onClick={() => setSelectedDay(null)} aria-label="Close day details" title="Close">✕</button>
          </div>
          {selectedPosts.map(post => {
  const timeLabel = post.scheduled_at
    ? new Date(post.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: timezone })
    : '';

  // Defensive parsing: handle real array, stringified array, CSV
  let platformsArr = [];
  const raw = post.platforms;
  if (Array.isArray(raw)) {
    platformsArr = raw;
  } else if (typeof raw === 'string') {
    try { platformsArr = JSON.parse(raw); }
    catch { platformsArr = raw.split(',').map(s => s.trim()); }
  }
  const normalized = platformsArr
    .filter(Boolean)
    .map(p => String(p).toLowerCase().trim().replace(/['"{}[\]]/g, ''));
  const hasIG = normalized.includes('instagram');
  const hasFB = normalized.includes('facebook');
  let platformLabel = '';
  let platformClass = '';
  if (hasIG && hasFB) { platformLabel = 'Both'; platformClass = 'cal-pill-both'; }
  else if (hasIG)    { platformLabel = 'Instagram'; platformClass = 'cal-pill-ig'; }
  else if (hasFB)    { platformLabel = 'Facebook'; platformClass = 'cal-pill-fb'; }

  return (
    <div key={post.id} className="cal-post-row">
      {post.image_url
        ? <img src={post.image_url} className="cal-post-thumb" style={{ objectFit: 'cover', borderRadius: 6 }} alt="" />
        : <div className="cal-post-thumb" style={{ background: post.dot_color || 'var(--accent-dim)' }} />
      }
      <div className="cal-post-time">{timeLabel}</div>
      <div className="cal-post-caption">{post.caption}</div>
      {platformLabel && (
        <span className={`cal-pill ${platformClass}`}>{platformLabel}</span>
      )}
      {post.is_ig_story && <span className="cal-pill cal-pill-story">IG Story</span>}
      {post.is_fb_story && <span className="cal-pill cal-pill-story">FB Story</span>}
      {post.status === 'published'
        ? <span className="cal-pill cal-pill-posted">✓ Posted</span>
        : post.status === 'scheduled'
          ? <span className="cal-pill cal-pill-scheduled">⏱ Scheduled</span>
          : post.status === 'publishing'
            // Sent, not yet confirmed. TikTok is called out by name because it
            // is the one that routinely takes minutes — it transcodes after
            // accepting the post — and because "Pending" next to a post that has
            // already gone to TikTok reads as "nothing has happened yet".
            ? (post.tiktok_publish_id
                ? <span className="cal-pill cal-pill-pending" title="TikTok has the post and is still processing it. This can take a few minutes.">⏳ TikTok is still processing</span>
                : <span className="cal-pill cal-pill-pending" title="Sent to the platform — waiting for it to finish processing.">⏳ Publishing…</span>)
            : <span className="cal-pill cal-pill-pending">⏳ Pending</span>
      }
      {post.status === 'scheduled' && (
        confirmDeleteId === post.id ? (
          <span className="cal-post-confirm">
            <span className="cal-post-confirm-text">Delete?</span>
            <button className="cal-post-btn cal-post-btn-danger" onClick={() => handleDeletePost(post.id)}>Yes</button>
            <button className="cal-post-btn cal-post-btn-ghost" onClick={() => setConfirmDeleteId(null)}>No</button>
          </span>
        ) : (
          <span className="cal-post-actions">
            <button className="cal-post-btn cal-post-btn-ghost" onClick={() => openEdit(post)} title="Edit caption, platforms and text">✎ Edit</button>
            <button className="cal-post-btn cal-post-btn-ghost" onClick={() => openStoryEdit(post)} title="Edit story destinations">✦ Stories</button>
            <button className="cal-post-btn cal-post-btn-ghost" onClick={() => handleUnschedulePost(post.id)} title="Send back to approved">↩ Unschedule</button>
            <button className="cal-post-btn cal-post-btn-delete" onClick={() => setConfirmDeleteId(post.id)} title="Delete this post" aria-label="Delete this post">✕</button>
          </span>
        )
      )}
    </div>
  );
})}
        </div>
      )}

      {/* Unscheduled Queue — standalone, always visible */}
      {unscheduledPosts.filter(p => !p.scheduled_at).length > 0 && (
        <div className="sched-chips-standalone">
          <div className="sched-chips-heading">Unscheduled Queue</div>
          <p className="sched-chips-hint">Posts here are auto-scheduled in order — drag to reprioritize, or tap a card to set a specific time.</p>
          <div className="sched-chips-row">
            {unscheduledPosts.filter(p => !p.scheduled_at).map(post => {
              const opening = chipScheduleId === post.id;
              const isChipDragging = chipDragId === post.id;
              const isChipOver = chipDragOverId === post.id && chipDragId !== post.id;
              return (
                <div
                  key={post.id}
                  className={`sched-chip${isChipDragging ? ' chip-dragging' : ''}${isChipOver ? ' chip-drop-target' : ''}`}
                  data-sched-chip-id={post.id}
                  {...chipDrag.handlers(post.id)}
                  onClick={() => {
                    // A drag ends on the chip it started on, which would
                    // otherwise open the modal every time someone reorders.
                    if (chipDrag.consumeDragClick()) return;
                    if (!opening) openChipModal(post);
                  }}
                >
                  <button className="sched-chip-delete" onClick={(e) => handleDeleteChip(e, post.id)} aria-label="Delete this draft" title="Delete">✕</button>
                  <div className="sched-chip-thumb-wrap">
                    {post.image_url
                      ? <img src={post.image_url} alt="" className="sched-chip-thumb" draggable={false} />
                      : <div className="sched-chip-thumb sched-chip-thumb-empty" />
                    }
                    <span className="sched-chip-thumb-hint">Tap to edit</span>
                  </div>
                  {opening ? (
                    <div className="sched-chip-picker" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="datetime-local"
                        className="sched-card-dtinput"
                        value={chipScheduleValue}
                        onChange={(e) => setChipScheduleValue(e.target.value)}
                      />
                      <button
                        className="sched-card-confirm"
                        onClick={() => handleScheduleFromChip(post.id, chipScheduleValue)}
                      >Schedule</button>
                      <button
                        className="sched-card-cancel"
                        onClick={() => { clearScheduleError(post.id); setChipScheduleId(null); setChipScheduleValue(''); }}
                        aria-label="Cancel scheduling"
                        title="Cancel"
                      >✕</button>
                      {scheduleErrors[post.id] && (
                        <div className="sched-limit-error" role="alert">{scheduleErrors[post.id]}</div>
                      )}
                    </div>
                  ) : (
                    <button
                      className="sched-chip-add"
                      onClick={(e) => {
                        e.stopPropagation();
                        setChipScheduleId(post.id);
                        const tmrw = new Date(Date.now() + 24*60*60*1000);
                        setChipScheduleValue(isoToLocalInput(tmrw.toISOString()));
                      }}
                    >+ Schedule</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Post Edit Modal */}
      {/* Story destination edit modal */}
      {storyEditPostId && (() => {
        const editPost = scheduledPosts.find(p => p.id === storyEditPostId);
        const toggle = (key) => setStoryEditFields(prev => ({ ...prev, [key]: !prev[key] }));
        return (
          <div className="chip-modal-overlay" onClick={() => setStoryEditPostId(null)}>
            <div className="chip-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 340 }}>
              <button className="chip-modal-close" onClick={() => setStoryEditPostId(null)} aria-label="Close" title="Close">✕</button>
              {editPost?.image_url
                ? <img src={editPost.image_url} alt="" className="chip-modal-img" />
                : <div className="chip-modal-img-fallback" />
              }
              <div className="chip-modal-body">
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Story Destinations</span>
                  <div className="chip-modal-pills">
                    {[
                      { key: 'is_ig_story', label: 'IG Story' },
                      { key: 'is_fb_story', label: 'FB Story' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        className={`chip-modal-pill${storyEditFields[opt.key] ? ' active' : ''}`}
                        onClick={() => toggle(opt.key)}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
                {!storyEditFields.is_ig_story && !storyEditFields.is_fb_story && (
                  <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>
                    Deselecting both removes all story destinations from this post.
                  </div>
                )}
                <button className="chip-modal-save" onClick={handleStoryEditSave} disabled={storyEditSaving}>
                  {storyEditSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Edit modal (scheduled posts). Feed platforms only — story
          destinations stay on the ✦ Stories control, which owns
          is_ig_story / is_fb_story. TikTok can't be added or removed here, same
          as Content Queue: it needs privacy settings and a consent this
          modal has no room for. */}
      {editPost && editFields && (() => {
        const post = editPost;
        const ef = editFields;
        const supported = editSupported(post);
        const overlayOk = supported && canEditOverlay(post);
        const base = post.overlay_base_image_url || post.image_url;
        const previewOverlay = liveOverlayBlocks(ef.overlay);
        // Old baked text: new text stacks on it, and clearing can't remove it.
        // Warned in the overlay section and again, specifically, above Save
        // once the text has actually been changed.
        const oldText = overlayOk && hasUntrackedText(post);
        const overlayChanged = overlayOk && textOverlayKey(previewOverlay) !== textOverlayKey(savedOverlayOf(post));
        const stackWarning = oldText && overlayChanged;
        const hasTikTok = ef.platforms.includes('tiktok');
        const togglePlatform = (p) => setEditFields(prev => ({
          ...prev,
          platforms: prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p],
        }));
        const stories = [post.is_ig_story && 'IG Story', post.is_fb_story && 'FB Story'].filter(Boolean);
        return (
          <div className="chip-modal-overlay" onClick={closeEdit}>
            <div className="chip-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <button className="chip-modal-close" onClick={closeEdit} aria-label="Close" title="Close">✕</button>
              {/* Whole image, letterboxed, so text placement is judged against
                  the full frame; the live preview sits on the ORIGINAL image. */}
              <div style={{ position:'relative', width:'100%', height:300, background:'#000', borderRadius:'12px 12px 0 0', overflow:'hidden', flexShrink:0 }}>
                {overlayOk
                  ? <TextOverlayPreviewImage src={base} alt="" overlay={trimOverlayBlocks(ef.overlay)}
                      selected={editTextSel} onSelect={setEditTextSel}
                      onMove={editSaving ? undefined : (i, x, y) => setEditFields(prev => ({
                        ...prev, overlay: prev.overlay.map((b, j) => (j === i ? { ...b, x, y } : b)),
                      }))}
                      onResize={editSaving ? undefined : (i, fields) => setEditFields(prev => ({
                        ...prev, overlay: prev.overlay.map((b, j) => (j === i ? { ...b, ...fields } : b)),
                      }))}
                      style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }} />
                  : post.image_url
                    ? <img src={post.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }} />
                    : <div className="chip-modal-img-fallback" style={{ height:'100%' }} />}
              </div>
              <div className="chip-modal-body">
                <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.4 }}>
                  Stays scheduled for <span style={{ color:'var(--text-secondary)', fontWeight:600 }}>{formatScheduledLabel(post.scheduled_at)}</span>. Use 📅 to change the time.
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Caption</span>
                  <textarea
                    className="chip-modal-textarea"
                    value={ef.caption}
                    onChange={(e) => setEditFields(prev => ({ ...prev, caption: e.target.value }))}
                  />
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Hashtags</span>
                  <input
                    type="text"
                    className="chip-modal-input"
                    value={ef.hashtags}
                    placeholder="#tag1 #tag2"
                    onChange={(e) => setEditFields(prev => ({ ...prev, hashtags: e.target.value }))}
                  />
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Where it posts</span>
                  <div className="chip-modal-pills">
                    {[
                      ['instagram', 'Instagram'], ['facebook', 'Facebook'],
                      // Google shows once connected, or when the post already
                      // targets it (so it can be switched off either way).
                      ...((gbpConnected || ef.platforms.includes('google_business')) ? [['google_business', 'Google']] : []),
                    ].map(([p, label]) => (
                      <button
                        key={p}
                        type="button"
                        className={`chip-modal-pill${ef.platforms.includes(p) ? ' active' : ''}`}
                        onClick={() => togglePlatform(p)}
                      >{label}</button>
                    ))}
                    {hasTikTok && (
                      <span className="chip-modal-pill active" style={{ cursor:'default', opacity:0.7 }} title="TikTok can't be changed from here">TikTok</span>
                    )}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>
                    Feed posts. {stories.length ? `Also posting to ${stories.join(' and ')}` : 'No story destinations'} — change stories with ✦.
                  </div>
                </div>
                {ef.platforms.includes('google_business') && (
                  <GoogleBusinessOptions
                    value={ef.gbp}
                    onChange={next => setEditFields(prev => ({ ...prev, gbp: next }))}
                    isVideo={post.media_type === 'video'}
                    caption={ef.caption}
                  />
                )}
                {overlayOk ? (
                  <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }}>
                    <button
                      type="button"
                      onClick={() => setEditOverlayOpen(o => !o)}
                      aria-expanded={editOverlayOpen}
                      style={{
                        width:'100%', display:'flex', alignItems:'center', gap:8, padding:'9px 12px',
                        background:'transparent', border:'none', cursor:'pointer', textAlign:'left',
                        fontFamily:'var(--font)', fontSize:13, color: previewOverlay ? '#31DCBA' : 'var(--text-muted)',
                      }}
                    >
                      <span style={{ fontSize:10, width:10 }}>{editOverlayOpen ? '▾' : '▸'}</span>
                      <span style={{ flex:1 }}>Text overlay</span>
                      {oldText && (
                        <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:8, background:'rgba(245,166,35,0.15)', color:'#f5a623' }}>⚠ Has old text</span>
                      )}
                      {previewOverlay && (
                        <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:8, background:'rgba(49,220,186,0.15)', color:'#31DCBA' }}>On</span>
                      )}
                    </button>
                    {editOverlayOpen && (
                      <div style={{ padding:'0 12px 12px' }}>
                        {oldText ? (
                          <div style={{ fontSize:11, color:'#f5a623', background:'rgba(245,166,35,0.08)', border:'1px solid rgba(245,166,35,0.25)', borderRadius:7, padding:'8px 10px', marginBottom:10, lineHeight:1.5 }}>
                            <strong>This image already has text baked into it</strong>, from before text could be edited, and the version without text wasn't kept. Anything you type here is drawn <strong>on top of</strong> the old text — it can't replace or fix it, and clearing this field won't remove it.
                          </div>
                        ) : (
                          <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:8, lineHeight:1.4 }}>
                            Previewing live on the image above. It's added when you save; clear the text to remove it.
                          </div>
                        )}
                        <TextOverlayControls
                          value={ef.overlay}
                          onChange={update => setEditFields(prev => ({ ...prev, overlay: update(prev.overlay) }))}
                          selected={editTextSel}
                          onSelect={setEditTextSel}
                          placeholder="Text to show on the image"
                          imageUrl={base}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>
                    Text overlay isn't available for {post.media_type === 'video' ? 'video' : post.media_type === 'carousel' ? 'carousel' : 'this'} posts.
                  </div>
                )}
                {!supported && (
                  <div className="cq-error">
                    Editing isn't available until the latest server update is live. Nothing has been changed — close this and try again later.
                  </div>
                )}
                {stackWarning && (
                  <div style={{ fontSize:12, color:'#f5a623', background:'rgba(245,166,35,0.1)', border:'1px solid rgba(245,166,35,0.4)', borderRadius:8, padding:'10px 12px', lineHeight:1.5 }}>
                    <strong>⚠ Your text won't replace the old text.</strong>{' '}
                    {previewOverlay
                      ? 'Saving draws it on top of the text already in the image — both will show, as in the preview above. If you were fixing a typo, this will make it worse, not fix it.'
                      : 'Saving removes only the text you added in a later edit. The original baked-in text stays in the image.'}
                  </div>
                )}
                {editError && <div className="cq-error">{editError}</div>}
                <button className="chip-modal-save" onClick={handleEditSave} disabled={editSaving || !supported}>
                  {editSaving ? 'Saving…' : stackWarning && previewOverlay ? 'Save anyway — text stacks on top' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {chipModalPostId && (() => {
        const mf = chipModalFields;
        const modalPost = unscheduledPosts.find(p => p.id === chipModalPostId);
        const isPlatformActive = (p) => mf.platforms && mf.platforms.includes(p);
        const isBothActive = isPlatformActive('instagram') && isPlatformActive('facebook');
        const togglePlatform = (p) => {
          const current = mf.platforms || [];
          const next = current.includes(p) ? current.filter(x => x !== p) : [...current, p];
          setChipModalFields(prev => ({ ...prev, platforms: next }));
        };
        // Both only switches Instagram + Facebook. It used to replace the whole
        // list, which silently dropped any other destination (TikTok, Google).
        const toggleBoth = () => {
          const others = (mf.platforms || []).filter(p => p !== 'instagram' && p !== 'facebook');
          const next = isBothActive ? others : [...others, 'instagram', 'facebook'];
          setChipModalFields(prev => ({ ...prev, platforms: next }));
        };
        return (
          <div className="chip-modal-overlay" onClick={() => { clearScheduleError(CHIP_MODAL_ERROR_KEY); setChipModalPostId(null); }}>
            <div className="chip-modal" onClick={(e) => e.stopPropagation()}>
              <button className="chip-modal-close" onClick={() => { clearScheduleError(CHIP_MODAL_ERROR_KEY); setChipModalPostId(null); }} aria-label="Close modal" title="Close">✕</button>
              {modalPost?.image_url
                ? <img src={modalPost.image_url} alt="" className="chip-modal-img" />
                : <div className="chip-modal-img-fallback" />
              }
              <div className="chip-modal-body">
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Caption</span>
                  <textarea
                    className="chip-modal-textarea"
                    value={mf.caption}
                    onChange={(e) => setChipModalFields(prev => ({ ...prev, caption: e.target.value }))}
                  />
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Hashtags</span>
                  <input
                    type="text"
                    className="chip-modal-input"
                    value={mf.hashtags}
                    placeholder="#tag1 #tag2"
                    onChange={(e) => setChipModalFields(prev => ({ ...prev, hashtags: e.target.value }))}
                  />
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Platform</span>
                  <div className="chip-modal-pills">
                    <button
                      className={`chip-modal-pill${isPlatformActive('instagram') && !isBothActive ? ' active' : ''}`}
                      onClick={() => togglePlatform('instagram')}
                    >Instagram</button>
                    <button
                      className={`chip-modal-pill${isPlatformActive('facebook') && !isBothActive ? ' active' : ''}`}
                      onClick={() => togglePlatform('facebook')}
                    >Facebook</button>
                    <button
                      className={`chip-modal-pill${isBothActive ? ' active' : ''}`}
                      onClick={toggleBoth}
                    >Both</button>
                    {(gbpConnected || isPlatformActive('google_business')) && (
                      <button
                        className={`chip-modal-pill${isPlatformActive('google_business') ? ' active' : ''}`}
                        onClick={() => togglePlatform('google_business')}
                      >Google</button>
                    )}
                  </div>
                  {isPlatformActive('google_business') && modalPost?.media_type === 'video' && (
                    <div style={{ fontSize:11, color:'#f5a623', lineHeight:1.5 }}>{gbpNoteFor({ ...modalPost, platforms: mf.platforms })}</div>
                  )}
                  {isPlatformActive('google_business') && modalPost?.media_type !== 'video' && gbpTrimNote(mf.caption) && (
                    <div style={{ fontSize:11, color:'#f5a623', lineHeight:1.5 }} data-testid="gbp-trim-note">{gbpTrimNote(mf.caption)}.</div>
                  )}
                  {isPlatformActive('google_business') && modalPost?.media_type !== 'video' && (
                    <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>
                      Posts to Google as a Standard post. Schedule it, then use Edit to add a button, offer or event.
                    </div>
                  )}
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Date</span>
                  <input
                    type="date"
                    className="chip-modal-input"
                    value={mf.date}
                    min={new Date().toLocaleDateString('en-CA', { timeZone: timezone })}
                    onChange={(e) => setChipModalFields(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Time</span>
                  <div className="chip-modal-time-row">
                    <button
                      className={`chip-modal-time-btn${mf.time === '09:00' ? ' active' : ''}`}
                      onClick={() => setChipModalFields(prev => ({ ...prev, time: '09:00' }))}
                    >Morning</button>
                    <button
                      className={`chip-modal-time-btn${mf.time === '18:00' ? ' active' : ''}`}
                      onClick={() => setChipModalFields(prev => ({ ...prev, time: '18:00' }))}
                    >Evening</button>
                    <input
                      type="time"
                      className="chip-modal-time-input"
                      value={mf.time}
                      onChange={(e) => setChipModalFields(prev => ({ ...prev, time: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="chip-modal-field">
                  <span className="chip-modal-label">Colour</span>
                  <div className="cal-swatch-row">
                    {swatches.map((swatch, i) => (
                      <div
                        key={swatch}
                        className={`cal-swatch${mf.color === swatch ? ' selected' : ''}${i === 0 ? ' brand' : ''}`}
                        style={{ background: swatch }}
                        onClick={() => setChipModalFields(prev => ({ ...prev, color: swatch }))}
                      />
                    ))}
                  </div>
                </div>
                {scheduleErrors[CHIP_MODAL_ERROR_KEY] && (
                  <div className="sched-limit-error" role="alert">{scheduleErrors[CHIP_MODAL_ERROR_KEY]}</div>
                )}
                <button className="chip-modal-save" onClick={handleChipModalSave}>Save</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Drag ghosts. Native DnD drew its own drag image from the source
          element; pointer events draw nothing, so the thing under the finger
          has to be a real element. Positioned imperatively from the move
          handler — see usePointerReorder — so a drag does not re-render the
          whole panel on every pointermove. */}
      {cardDrag.draggingId != null && (() => {
        const ghostPost = scheduledPosts.find(p => p.id === cardDrag.draggingId);
        if (!ghostPost) return null;
        return (
          <div ref={cardDrag.ghostRef} className="sched-drag-ghost" aria-hidden="true">
            {ghostPost.image_url
              ? <img src={ghostPost.image_url} alt="" />
              : <div className="sched-drag-ghost-fallback" style={{ background: ghostPost.dot_color || '#31DCBA' }} />
            }
            <div className="sched-drag-ghost-label">{formatScheduledLabel(ghostPost.scheduled_at)}</div>
          </div>
        );
      })()}
      {chipDrag.draggingId != null && (() => {
        const ghostPost = unscheduledPosts.find(p => p.id === chipDrag.draggingId);
        if (!ghostPost) return null;
        return (
          <div ref={chipDrag.ghostRef} className="sched-drag-ghost sched-drag-ghost-chip" aria-hidden="true">
            {ghostPost.image_url
              ? <img src={ghostPost.image_url} alt="" />
              : <div className="sched-drag-ghost-fallback" style={{ background: ghostPost.dot_color || '#31DCBA' }} />
            }
          </div>
        );
      })()}
    </div>
  );
}

// ============================================================
// POST APPROVAL QUEUE PANEL — Session 4
// ============================================================
// ============================================================
// POST APPROVAL PANEL — unified queue (managed + self-serve)
// ============================================================
// TikTok's Content Sharing Guidelines: the commercial-content disclosure
// defaults off, and once it is ON the creator must pick at least one of "Your
// brand" (brand_organic) or "Branded content" (branded_content) — publishing
// stays disabled until they do. Declared here so the approval queue, the
// results grid and the composer all gate on one rule rather than three copies.
export function ttDisclosureIncomplete(tt) {
  if (!tt || !tt.commercial_content) return false;
  return !tt.brand_organic && !tt.branded_content;
}

const PLATFORM_LABELS  = { instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', google_business: 'Google' };
const SWATCH_PRESETS   = ['#F5A623', '#6399FA', '#E040FB', '#FF5252', '#4CAF50'];
const ALL_PLATFORMS   = ['instagram', 'facebook', 'tiktok'];

export function QueueCard({ post, onApprove, onReject, onRegen, regening, onPlatformToggle, onSave, processing, approveError, ttConsented, onTtConsentChange, editSignal, onEditingChange }) {
  const platforms = Array.isArray(post.platforms) ? post.platforms.map(p => p.toLowerCase()) : [];
  const hashtags  = Array.isArray(post.hashtags) ? post.hashtags.join(' ') : (post.hashtags || '');

  // TikTok requires an explicit Music Usage Confirmation consent before the post
  // goes out. Approving schedules the post for the cron publisher, so the gate
  // lives on Approve here (this card has no Publish Now button).
  const isTikTokDest      = platforms.includes('tiktok');
  const isBrandedTt       = !!post.tiktok_settings?.branded_content;
  const ttConsentBlocked  = isTikTokDest && !ttConsented;
  // Second TikTok gate, same mechanism as the consent one: disclosure declared
  // but neither commercial-content option picked.
  const ttDisclosureBlocked = isTikTokDest && ttDisclosureIncomplete(post.tiktok_settings);
  const ttBlocked         = ttConsentBlocked || ttDisclosureBlocked;
  const ttBlockedReason   = ttDisclosureBlocked
    ? 'Select "Your brand" or "Branded content" — the commercial content disclosure is on'
    : 'Confirm the TikTok Music Usage Confirmation to approve';

  const [editing, setEditing]             = useState(false);
  const [draftCaption, setDraftCaption]   = useState(post.caption);
  const [draftHashtags, setDraftHashtags] = useState(hashtags);
  const [saving, setSaving]               = useState(false);

  // Both optional, and both no-ops when the card is rendered by the list view
  // (which passes neither). They exist so swipe mode can drive THIS editor
  // rather than grow a second one: a left swipe bumps editSignal, and the card
  // reports the resulting state back so the stack can suspend dragging while
  // someone is typing in it.
  useEffect(() => { if (editSignal) setEditing(true); }, [editSignal]);
  useEffect(() => { if (onEditingChange) onEditingChange(editing); }, [editing, onEditingChange]);

  const handleSave = async () => {
    setSaving(true);
    const newHashtags = draftHashtags.split(/\s+/).filter(Boolean).map(t => t.startsWith('#') ? t : `#${t}`);
    // Phase 5: was an update filtered by id, with client_id added only `if
    // (post.client_id)`. That conditional guarded a case that cannot occur —
    // PostApproval loads these rows with .eq('client_id', clientId), so the
    // field is always set — and it failed OPEN: a falsy client_id meant an
    // update scoped by id alone, i.e. a possible cross-client write. The route
    // takes no client identifier at all, so the scope is no longer optional.
    try {
      await patchPost(post.id, { caption: draftCaption, hashtags: newHashtags });
    } catch (err) {
      console.error('[QueueCard] save failed:', err.message);
    }
    onSave(post.id, draftCaption, newHashtags);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="aq-card" style={{ marginBottom: 12 }}>
      <div className="aq-card-inner">
        <div className="aq-img-thumb">
          {post.image_url
            ? <img src={post.image_url} alt="" className="aq-thumb-img" />
            : <div className="aq-thumb-placeholder">📷</div>
          }
        </div>
        <div className="aq-body">
          {editing ? (
            <>
              <textarea
                className="aq-edit-textarea"
                rows={4}
                value={draftCaption}
                onChange={e => setDraftCaption(e.target.value)}
              />
              <input
                className="aq-edit-hashtags"
                value={draftHashtags}
                onChange={e => setDraftHashtags(e.target.value)}
              />
            </>
          ) : (
            <>
              <div className="aq-caption">{draftCaption}</div>
              {draftHashtags && <div className="aq-hashtags">{draftHashtags}</div>}
            </>
          )}
          <div className="aq-platforms">
            {/* Google is listed only for a post that targets it (or did, and
                still carries its Google options) — this card cannot tell
                whether Google is connected, so it never offers it fresh. */}
            {[...ALL_PLATFORMS, ...((platforms.includes('google_business') || post.gbp_settings) ? ['google_business'] : [])].map(p => {
              // An empty platforms array means the server-side default of IG + FB
              // (see generate-post), so only those two light up in that case —
              // TikTok is only ever active when listed explicitly on the row.
              const active = platforms.length === 0 ? p !== 'tiktok' : platforms.includes(p);
              // TikTok stays non-toggleable in this compact card on purpose: there
              // is no room here for the privacy / duet / stitch settings a fresh
              // TikTok destination would require. Making it toggleable is separate work.
              const clickable = p !== 'tiktok';
              return (
                <span
                  key={p}
                  className={`aq-chip${active ? '' : ' inactive'}`}
                  style={clickable ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                  onClick={clickable ? () => onPlatformToggle(post.id, p, platforms) : undefined}
                >
                  {PLATFORM_LABELS[p]}
                </span>
              );
            })}
            {post.is_ig_story && <span className="aq-story-badge">IG Story</span>}
            {post.is_fb_story && <span className="aq-story-badge">FB Story</span>}
          </div>
          {platforms.includes('google_business') && (post.media_type || 'image') !== 'video' && gbpTrimNote(post.caption) && (
            <div style={{ fontSize:11, color:'#f5a623', margin:'6px 0 2px', lineHeight:1.5 }} data-testid="gbp-trim-note">{gbpTrimNote(post.caption)}.</div>
          )}
          {gbpNoteFor(post) && (
            <div style={{ fontSize:11, color:'#f5a623', margin:'6px 0 2px', lineHeight:1.5 }}>{gbpNoteFor(post)}</div>
          )}
          {isTikTokDest && (
            <label style={{ display:'flex', gap:8, alignItems:'flex-start', margin:'8px 0 4px', fontSize:11, color:'var(--text-secondary)', lineHeight:1.5, cursor:'pointer' }}>
              <input
                type="checkbox"
                checked={!!ttConsented}
                onChange={e => onTtConsentChange(post.id, e.target.checked)}
                style={{ marginTop:2, flexShrink:0 }}
              />
              <span>
                {isBrandedTt ? (
                  <>By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Branded Content Policy</a> and <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Music Usage Confirmation</a></>
                ) : (
                  <>By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Music Usage Confirmation</a></>
                )}
              </span>
            </label>
          )}
          {ttDisclosureBlocked && (
            <div style={{ fontSize:11, color:'#f5a623', margin:'4px 0', lineHeight:1.5 }}>
              This post declares commercial content — pick "Your brand" or "Branded content" in the TikTok settings before approving.
            </div>
          )}
          <div className="aq-actions">
            {editing ? (
              <>
                <button className="aq-btn aq-btn-regen" onClick={handleSave} disabled={saving}>
                  {saving ? '…' : 'Save'}
                </button>
                <button className="aq-btn aq-btn-regen" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="aq-btn aq-btn-approve"
                  onClick={() => onApprove(post.id)}
                  disabled={processing || ttBlocked}
                  style={ttBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  title={ttBlocked ? ttBlockedReason : undefined}
                >
                  {processing ? '…' : '✓ Approve'}
                </button>
                {approveError && <span style={{ fontSize:11, color:'#f5a623', marginLeft:6 }}>Failed — try again</span>}
                <button className="aq-btn aq-btn-regen" onClick={() => setEditing(true)}>
                  ✎ Edit
                </button>
                <button className="aq-btn aq-btn-regen" onClick={() => onRegen(post)} disabled={regening}>
                  {regening ? '…' : '↺ Regen'}
                </button>
                <button className="aq-btn aq-btn-reject" onClick={() => onReject(post.id)}>
                  Reject
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const POST_TEMPLATES = [
  { id: 'promo',     label: '🏷️ Promo',            topic: 'Promote our current special offer or sale' },
  { id: 'tips',      label: '💡 Tips',             topic: 'Share 3 helpful tips related to our business' },
  { id: 'product',   label: '⭐ Highlight',         topic: 'Showcase one of our key products or services' },
  { id: 'bts',       label: '🎬 Behind the Scenes', topic: 'Give a behind-the-scenes look at how we work' },
  { id: 'seasonal',  label: '📅 Seasonal',          topic: 'Create a post tied to the current season or upcoming holiday' },
  { id: 'story',     label: '💬 Our Story',         topic: 'Tell the story of who we are and why we do what we do' },
];

const CAROUSEL_PRESETS = [
  {
    id: 'tips',
    label: '💡 Tips & Tricks',
    aspectRatio: '4:5',
    slideCount: 5,
    hookPrompt: 'Bold hook slide: promise the reader 5 useful tips',
    defaultText: { position: 'bottom', size: 'M', align: 'left', style: 'Backed', color: '#FFFFFF' }
  },
  {
    id: 'product',
    label: '⭐ Product Showcase',
    aspectRatio: '1:1',
    slideCount: 4,
    hookPrompt: 'Eye-catching cover highlighting our standout product',
    defaultText: { position: 'bottom', size: 'L', align: 'center', style: 'Solid', color: '#FFFFFF' }
  },
  {
    id: 'quote',
    label: '💬 Quote Stack',
    aspectRatio: '4:5',
    slideCount: 3,
    hookPrompt: 'Striking opening quote or bold brand statement',
    defaultText: { position: 'middle', size: 'L', align: 'center', style: 'Outline', color: '#FFFFFF' }
  },
  {
    id: 'bts',
    label: '🎬 Behind the Scenes',
    aspectRatio: '4:5',
    slideCount: 4,
    hookPrompt: 'Curious hook: invite the viewer behind the curtain',
    defaultText: { position: 'top', size: 'S', align: 'left', style: 'Backed', color: '#FFFFFF' }
  },
  {
    id: 'promo',
    label: '🏷️ Promo / Sale',
    aspectRatio: '1:1',
    slideCount: 3,
    hookPrompt: 'Urgent cover slide announcing the offer or sale',
    defaultText: { position: 'bottom', size: 'L', align: 'center', style: 'Solid', color: '#FFFFFF' }
  },
];

// ── Swipe approval mode ─────────────────────────────────────────────────────
// An opt-in, mobile-first presentation of the SAME queue the list view renders.
// It is presentation and nothing else: every commit path calls the handlers the
// list-view card already calls (onApprove → POST /api/posts/:id/approve; a left
// swipe opens QueueCard's own inline editor, whose Save is the existing
// patchPost). No new endpoint, no second approve path to keep in sync.
//
// Hand-rolled pointer events rather than a gesture dependency: this needs ~40
// lines and the repo carries no such library. Pointer events cover mouse and
// touch in one handler set, and `touch-action: pan-y` on the live card leaves
// vertical scrolling to the browser so the stack never fights the page.
//
// ACCIDENTAL-APPROVAL GUARDS, in the order they apply:
//   1. A gesture is not horizontal until it travels SWIPE_ENGAGE_PX and beats
//      its own vertical component — a thumb scrolling the page never engages.
//   2. An engaged drag does not COMMIT until SWIPE_COMMIT_PX. Short of that the
//      card springs back and nothing is called.
//   3. Approve / ✎ Edit stay visible as buttons on every card, so the gesture
//      is never the only way to act.
//   4. Approve is undoable for UNDO_WINDOW_MS (see handleUndoApprove).
// TikTok's consent and disclosure gates are evaluated here exactly as QueueCard
// evaluates them, so a swipe cannot bypass a gate the button enforces.
const SWIPE_ENGAGE_PX = 12;   // travel before a drag counts as horizontal at all
const SWIPE_COMMIT_PX = 96;   // travel before a swipe commits
const SWIPE_FLYOUT_MS = 170;
// How long an approve stays undoable. Comfortably inside the window in which
// the post is not yet a cron candidate (see handleUndoApprove).
const UNDO_WINDOW_MS = 10000;

// Named export purely as a test seam, matching TikTokSettingsPanel / QueueCard
// above: the default export is unchanged and nothing in the app imports this.
export function SwipeApprovalStack({
  posts, onApprove, onReject, onRegen, regeningIds, onPlatformToggle, onSave,
  processingIds, approveErrors, tiktokConsentIds, onTtConsentChange,
}) {
  const [index, setIndex]           = useState(0);
  const [dx, setDx]                 = useState(0);
  const [dragging, setDragging]     = useState(false);
  const [flyOut, setFlyOut]         = useState(false);
  const [editSignal, setEditSignal] = useState(0);
  const [cardEditing, setCardEditing] = useState(false);
  const [blocked, setBlocked]       = useState('');
  const drag    = useRef(null);
  const alive   = useRef(true);
  const flyTimer = useRef(null);
  // alive.current is SET here, not just cleared in the cleanup. index.js renders
  // the app inside <React.StrictMode>, which in development runs mount effects
  // twice — setup, cleanup, setup. A cleanup-only effect therefore left the ref
  // false for the component's entire life in dev, and commitApprove's fly-out
  // timer bailed at `if (!alive.current) return` before ever calling onApprove:
  // no approve request, and flyOut stuck true so the stack locked after one
  // swipe. Production was unaffected, which is exactly what made it easy to
  // miss. Cleanup behaviour is otherwise unchanged.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; clearTimeout(flyTimer.current); };
  }, []);

  // Approving removes the post from the parent's list, so the card at this
  // index becomes the next one on its own — the index only moves for Skip/Back.
  const safeIndex = posts.length ? Math.min(index, posts.length - 1) : 0;
  const post = posts[safeIndex];

  // Reset per-card UI whenever the card under the cursor changes.
  useEffect(() => { setDx(0); setBlocked(''); setCardEditing(false); }, [post?.id]);

  const onEditingChange = React.useCallback((v) => setCardEditing(v), []);

  if (!post) return null;

  // Mirrors QueueCard's gates exactly (see its isTikTokDest block).
  const platforms = Array.isArray(post.platforms) ? post.platforms.map(p => String(p).toLowerCase()) : [];
  const isTikTokDest = platforms.includes('tiktok');
  const ttConsentBlocked    = isTikTokDest && !tiktokConsentIds.has(post.id);
  const ttDisclosureBlocked = isTikTokDest && ttDisclosureIncomplete(post.tiktok_settings);
  const ttBlocked = ttConsentBlocked || ttDisclosureBlocked;
  const ttBlockedReason = ttDisclosureBlocked
    ? 'Select "Your brand" or "Branded content" in the TikTok settings before approving.'
    : 'Confirm the TikTok Music Usage Confirmation on the card before approving.';
  const processing = processingIds.has(post.id);
  // Dragging is suspended while the inline editor is open (someone is typing in
  // it) and while an approve is in flight.
  const locked = cardEditing || processing || flyOut;

  const commitApprove = () => {
    if (ttBlocked) { setDx(0); setBlocked(ttBlockedReason); return; }
    setBlocked('');
    setFlyOut(true);
    setDx(window.innerWidth || 600);
    flyTimer.current = setTimeout(() => {
      if (!alive.current) return;
      setFlyOut(false);
      setDx(0);
      onApprove(post.id);   // the list view's handler, unchanged
    }, SWIPE_FLYOUT_MS);
  };

  const commitEdit = () => {
    setDx(0);
    setBlocked('');
    setEditSignal(n => n + 1);   // QueueCard opens its own inline editor
  };

  // A press that lands on a control is that control's, not the stack's.
  const INTERACTIVE = 'button, a, input, textarea, select, label';

  const handlePointerDown = (e) => {
    if (locked) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target?.closest?.(INTERACTIVE)) return;
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, engaged: false };
  };

  const handlePointerMove = (e) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    const mx = e.clientX - d.x;
    const my = e.clientY - d.y;
    if (!d.engaged) {
      if (Math.abs(mx) < SWIPE_ENGAGE_PX) return;
      // Vertical intent wins outright — release the gesture back to the page.
      if (Math.abs(my) > Math.abs(mx)) { drag.current = null; return; }
      d.engaged = true;
      setDragging(true);
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not fatal */ }
    }
    d.dx = mx;
    setDx(mx);
  };

  const handlePointerUp = (e) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not fatal */ }
    setDragging(false);
    if (!d.engaged) { setDx(0); return; }
    // Read the travel off the ref, not off state: this is the value the gesture
    // actually ended on, with no dependence on a render having landed first.
    if (d.dx >= SWIPE_COMMIT_PX)       commitApprove();
    else if (d.dx <= -SWIPE_COMMIT_PX) commitEdit();
    else setDx(0);                      // short of the threshold: spring back
  };

  const handlePointerCancel = () => {
    drag.current = null;
    setDragging(false);
    setDx(0);
  };

  // Intent is shown only once the drag would actually commit, so the badge is a
  // readout of the threshold rather than a hint that one exists.
  const willApprove = dx >= SWIPE_COMMIT_PX;
  const willEdit    = dx <= -SWIPE_COMMIT_PX;
  const lean = Math.max(-1, Math.min(1, dx / 240));

  const cardProps = {
    post,
    onApprove,
    onReject,
    onRegen,
    regening: regeningIds.has(post.id),
    onPlatformToggle,
    onSave,
    processing,
    approveError: !!approveErrors[post.id],
    ttConsented: tiktokConsentIds.has(post.id),
    onTtConsentChange,
  };

  return (
    <div className="aq-swipe-stage">
      <div className="aq-swipe-deck">
        {/* One peeked card behind the live one, purely to show there is a stack.
            Inert: pointer-events are off and it is hidden from assistive tech. */}
        {posts[safeIndex + 1] && (
          <div className="aq-swipe-peek" aria-hidden="true" style={{ transform: 'scale(0.965) translateY(10px)' }}>
            <QueueCard
              {...cardProps}
              post={posts[safeIndex + 1]}
              regening={false}
              processing={false}
              approveError={false}
              ttConsented={tiktokConsentIds.has(posts[safeIndex + 1].id)}
            />
          </div>
        )}

        <div
          className={`aq-swipe-live${dragging ? ' dragging' : ''}`}
          style={{
            transform: `translateX(${dx}px) rotate(${lean * 4}deg)`,
            transition: dragging ? 'none' : `transform ${flyOut ? SWIPE_FLYOUT_MS : 200}ms ease-out`,
            opacity: flyOut ? 0 : 1,
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
        >
          {willApprove && <div className="aq-swipe-badge approve">✓ Approve</div>}
          {willEdit    && <div className="aq-swipe-badge edit">✎ Edit</div>}
          <QueueCard {...cardProps} editSignal={editSignal} onEditingChange={onEditingChange} />
        </div>
      </div>

      <div className="aq-swipe-foot">
        <span className="aq-swipe-count">{safeIndex + 1} of {posts.length}</span>
        <div className="aq-swipe-nav">
          <button
            type="button"
            className="aq-swipe-navbtn"
            onClick={() => setIndex(i => Math.max(0, Math.min(i, posts.length - 1) - 1))}
            disabled={safeIndex === 0}
          >
            ← Back
          </button>
          <button
            type="button"
            className="aq-swipe-navbtn"
            onClick={() => setIndex(i => Math.min(posts.length - 1, Math.min(i, posts.length - 1) + 1))}
            disabled={safeIndex >= posts.length - 1}
          >
            Skip →
          </button>
        </div>
      </div>

      {blocked
        ? <div className="aq-swipe-blocked">{blocked}</div>
        : <div className="aq-swipe-hint">
            Swipe right to approve · swipe left to edit · or use the buttons on the card.
          </div>
      }
    </div>
  );
}

// AI images come from the plan's monthly allowance first, then from paid
// images (the $19 top-up / $5 refill), which carry over month to month. A
// cap_reached from the backend therefore means BOTH are empty. Own-photo posts
// never use either.
export const aiImageLimitMessage = (data) =>
  `AI image limit reached — ${data?.used ?? '?'}/${data?.cap ?? '?'} used this month and no paid images left`;

// "12 / 24 images · 30 paid": this month out of the cap, plus paid images left.
// Never negative, and the monthly figure never reads above the cap.
export function quotaPillText(quota) {
  const cap  = quota?.cap;
  const used = Math.max(0, Math.min(Number(quota?.images_used) || 0, cap ?? Infinity));
  const paid = Math.max(0, Number(quota?.paid_images_remaining) || 0);
  return `${used} / ${cap} images${paid > 0 ? ` · ${paid} paid` : ''}`;
}

export function PostApprovalPanel({ isSelfServe, planStatus, onRetryPlan, clientId, stripeCustomerId, onPendingChange, onApprove }) {
  // Phase 5: reads and writes carry the act-as-client override; the load effect
  // must re-run when that context changes (Phase 3 reasoning).
  const { viewingAsClientId } = useViewingAs();
  const [tab, setTab] = useState('approve');
  const [contentFilter, setContentFilter] = useState('all'); // 'all' | 'feed' | 'stories'
  // Opt-in. The list stays the default on every load — this is deliberately not
  // persisted, so nobody is ever dropped into a gesture UI they did not pick
  // this session.
  const [swipeMode, setSwipeMode] = useState(false);

  // ── Approve tab ──
  const [posts, setPosts]         = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [regeningIds, setRegeningIds]   = useState(new Set());
  const [processingIds, setProcessingIds] = useState(new Set());
  const [approveErrors, setApproveErrors] = useState({});
  // Undo-approve, swipe mode only (see handleUndoApprove for why it is safe
  // against the publish cron, and why it is scoped to this mode).
  const [undoInfo, setUndoInfo] = useState(null);
  const undoTimer = useRef(null);
  useEffect(() => () => clearTimeout(undoTimer.current), []);
  // Per-post TikTok Music Usage Confirmation consent (set of post ids). Required
  // before a TikTok-targeted post can be approved (TikTok Direct Post audit).
  // This queue is a separate component from the Post Generator's result cards,
  // so it carries its own consent state rather than sharing that one.
  const [tiktokConsentIds, setTiktokConsentIds] = useState(new Set());
  // The tick is persisted, not just held in this Set. It used to live only here,
  // which meant the consent TikTok requires existed solely as a disabled button
  // in one browser tab: the server could not tell a consented post from an
  // unconsented one, and both publish paths would publish either. The post row
  // now carries the record (posts.tiktok_consent_at, stamped server-side), and
  // the publish paths refuse a TikTok post without it.
  //
  // Optimistic, then reconciled: the checkbox responds immediately and is put
  // back if the write fails, because a tick that silently did not persist would
  // be the same bug in a new place.
  const toggleTiktokConsent = async (postId, checked) => {
    setTiktokConsentIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(postId); else next.delete(postId);
      return next;
    });
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(postId)}/tiktok-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consented: checked }),
      });
      if (!res.ok) throw new Error(`consent save failed (${res.status})`);
    } catch (err) {
      console.error('[Approval] TikTok consent save failed:', err?.message || err);
      setTiktokConsentIds(prev => {
        const next = new Set(prev);
        if (checked) next.delete(postId); else next.add(postId);
        return next;
      });
    }
  };

  const fetchPosts = React.useCallback(async () => {
    if (!clientId) return;
    setLoadingPosts(true);
    let data = null;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/approval-queue`);
      if (res.ok) data = (await res.json())?.posts;
    } catch (err) {
      console.error('[Approval] load failed:', err.message);
    }
    const loaded = data || [];
    setPosts(loaded);
    // Seed the consent Set from what the server already has on file, so a
    // reload doesn't present a post whose consent IS recorded as though it
    // still needed ticking.
    setTiktokConsentIds(new Set(loaded.filter(p => p.tiktok_consent_at).map(p => p.id)));
    if (onPendingChange) onPendingChange(loaded.length);
    setLoadingPosts(false);
  }, [clientId, viewingAsClientId, onPendingChange]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleApprove = async (id) => {
    if (processingIds.has(id)) return;
    // TikTok Direct Post audit: approving sets status='scheduled', after which
    // the publish-approved cron posts to TikTok with no further user interaction.
    // Consent must therefore be captured before approve, not just before a manual
    // publish. The button is disabled too; this re-check exists because the path
    // is compliance-critical and must not depend on UI state alone.
    const target = posts.find(p => p.id === id);
    const targetsTikTok = Array.isArray(target?.platforms)
      && target.platforms.some(p => String(p).toLowerCase() === 'tiktok');
    if (targetsTikTok && !tiktokConsentIds.has(id)) return;
    setProcessingIds(s => new Set([...s, id]));
    try {
      // Bug #11 fix: must set scheduled_at on approve, otherwise the post is
      // dropped from the queue but never picked up by publish-approved.
      // Phase 5: the slot is computed SERVER-SIDE now. computeNextSlot ran in
      // the browser and read `posts` under RLS, so approving while viewing as a
      // client derived the next slot from the ADMIN's schedule rather than the
      // client's. That is fixed as a consequence of the routing, not as
      // separate work. The 404 branch replaces the old empty-array check: the
      // endpoint returns one on a row the resolved client does not own, which
      // is the same condition RLS used to express by matching nothing.
      const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(id)}/approve`, {
        method: 'POST',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || 'approve_no_rows');
      setPosts(ps => { const next = ps.filter(p => p.id !== id); if (onPendingChange) onPendingChange(next.length); return next; });
      // Keep the row itself, not just its id: undo restores the card without a
      // refetch. Only in swipe mode — the list view's behaviour is untouched.
      if (swipeMode && target) {
        clearTimeout(undoTimer.current);
        setUndoInfo({ post: target, error: '' });
        undoTimer.current = setTimeout(() => setUndoInfo(null), UNDO_WINDOW_MS);
      }
      if (onApprove) onApprove();
    } catch (err) {
      console.error('[Approve] failed:', err?.message || err);
      setProcessingIds(s => { const n = new Set(s); n.delete(id); return n; });
      setApproveErrors(e => ({ ...e, [id]: true }));
      setTimeout(() => setApproveErrors(e => { const { [id]: _dropped, ...rest } = e; return rest; }), 4000);
    }
  };

  // UNDO-APPROVE — safe against publish-approved-posts, for two independent
  // reasons. Checked before writing this, not assumed:
  //
  // 1. TIMING. Approve does not schedule a post for "now". The slot comes from
  //    computeNextSlotFor (server.js), which returns either tomorrow 09:00 or
  //    24h after the latest already-scheduled post — always a future instant.
  //    The cron (POST /api/publish-approved) only considers rows where
  //    status='scheduled' AND scheduled_at <= NOW(), so a just-approved post is
  //    not a candidate for hours at minimum.
  //
  // 2. ATOMICITY, which is what actually makes it safe rather than merely
  //    likely-safe. The revert goes through patchPost's if_status precondition,
  //    so the backend issues ONE statement filtered on status='scheduled'. The
  //    cron claims rows with the mirror-image statement (UPDATE … SET
  //    status='publishing' WHERE status='scheduled'), so exactly one of the two
  //    can match a given row. If the cron has already claimed it the row reads
  //    'publishing', this update matches nothing, and the route answers 409
  //    status_changed — surfaced below rather than swallowed. There is no
  //    window in which an undo can retract a post that is in flight or already
  //    out the door.
  //
  // Scoped to swipe mode on purpose: a gesture can be made by accident, so the
  // mode that adds the gesture is the mode that owes the user a way back. The
  // list view, where approving takes a deliberate button press, is unchanged.
  //
  // KNOWN GAP: approve and reject log an admin action when an admin is acting
  // as a client (logAdminAction in their routes); this revert goes through the
  // generic PATCH, which logs nothing, so an admin's undo leaves no audit row.
  // Closing that means a route of its own — backend work, out of scope here.
  const handleUndoApprove = async () => {
    const info = undoInfo;
    if (!info) return;
    clearTimeout(undoTimer.current);
    try {
      await patchPost(
        info.post.id,
        { status: 'pending', scheduled_at: null, approved_at: null },
        { ifStatus: 'scheduled' },
      );
    } catch (err) {
      setUndoInfo({
        ...info,
        error: err?.code === 'status_changed'
          ? 'Too late — this post is already publishing. It stays approved.'
          : 'Undo failed. The post is still approved.',
      });
      return;
    }
    setUndoInfo(null);
    // handleApprove adds the id to processingIds and only removes it on the
    // FAILURE path — correct while a successful approve meant the id could
    // never return, which undo makes false. Without this the restored card
    // arrives still marked in-flight: the swipe stack's `locked` stays true so
    // it cannot be dragged, QueueCard renders Approve disabled as '…', and
    // handleApprove's own `if (processingIds.has(id)) return` refuses it a
    // second time. Edit/Regen/Reject keep working — none of them reads
    // `processing` — which is what made the card look half-stuck rather than
    // plainly broken.
    setProcessingIds(s => { const n = new Set(s); n.delete(info.post.id); return n; });
    // Restored at the head of the queue: it is the card just acted on, so it is
    // the one the user is looking for.
    setPosts(ps => {
      const restored = { ...info.post, status: 'pending', scheduled_at: null, approved_at: null };
      const next = [restored, ...ps.filter(p => p.id !== restored.id)];
      if (onPendingChange) onPendingChange(next.length);
      return next;
    });
    // The post left the calendar on approve; tell the shell to re-read it.
    if (onApprove) onApprove();
  };

  const handleReject = async (id) => {
    // Its own route, not the generic PATCH: reject is one of the four actions
    // the audit trail records, and the endpoint should not have to infer that
    // from a status value the Post Generator also writes on render failures.
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(id)}/reject`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('[Reject] failed:', err.message);
    }
    setPosts(ps => { const next = ps.filter(p => p.id !== id); if (onPendingChange) onPendingChange(next.length); return next; });
  };

  const handleRegen = async (post) => {
    setRegeningIds(s => new Set([...s, post.id]));
    try {
      if (post.source_photo_id) {
        // The route scopes this by client_id. The panel filtered by id alone —
        // a pre-existing gap of the class batched into the pre-client-#2
        // security audit. It closes here as a side effect of every route in
        // this pattern being client-scoped, not as a fix worked in this phase.
        await clientScopedFetch(`${BACKEND}/api/brand-photos/${encodeURIComponent(post.source_photo_id)}`, {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ patch: { used: false } }),
        });
      }
      // Reuses Phase 4's endpoint: its allowlist already covers every column
      // mapBrandProfileRow reads, so no new read route is needed here.
      const bhRes = await clientScopedFetch(`${BACKEND}/api/brand-hub`);
      const brandData = bhRes.ok ? (await bhRes.json())?.profile : null;
      const bh = mapBrandProfileRow(brandData);
      // PHASE 7 CARRYOVER. This function read the VIEWED client's brand hub on
      // the line above and then generated the post for the SIGNED-IN admin,
      // because authedFetch sends no override and `clientId` in the body beats
      // ?client= anyway. One function, two different clients. Phase 5 migrated
      // the read and left the generate behind; adding ignoreBodyClient to
      // /api/generate-post in this phase is what made the mismatch visible.
      await clientScopedFetch(`${BACKEND}/api/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: post.topic || 'showcase the brand', ...bh }),
      });
      await deletePost(post.id);
      await fetchPosts();
    } catch {
      await fetchPosts();
    }
    setRegeningIds(s => { const n = new Set(s); n.delete(post.id); return n; });
  };

  const handlePostSave = (postId, newCaption, newHashtags) => {
    setPosts(ps => ps.map(p => p.id === postId ? { ...p, caption: newCaption, hashtags: newHashtags } : p));
  };

  const handlePlatformToggle = async (postId, platform, currentPlatforms) => {
    const current = currentPlatforms.length === 0 ? ['instagram', 'facebook'] : [...currentPlatforms];
    const next = current.includes(platform) ? current.filter(p => p !== platform) : [...current, platform];
    if (next.length === 0) return;
    setPosts(ps => ps.map(p => p.id === postId ? { ...p, platforms: next } : p));
    try { await patchPost(postId, { platforms: next }); }
    catch (err) { console.error('[PlatformToggle] failed:', err.message); }
  };

  // ── Build tab (self-serve) ──
  const [brandHub, setBrandHub]   = useState(null);
  const [topic, setTopic]         = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [quota, setQuota]         = useState(null);
  const [buildError, setBuildError] = useState('');
  const [buildToast, setBuildToast] = useState(false);

  useEffect(() => {
    if (!isSelfServe || !clientId) return;
    clientScopedFetch(`${BACKEND}/api/brand-hub`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.profile) setBrandHub(mapBrandProfileRow(d.profile)); })
      .catch(() => {});
  }, [isSelfServe, clientId]);

  // Fetch usage on mount so the header pill + 75/90% banner reflect quota
  // on every tab, not just Build.
  useEffect(() => {
    if (!isSelfServe || !clientId) return;
    authedFetch(`${BACKEND}/api/usage/${encodeURIComponent(clientId)}${stripeCustomerId ? `?customerId=${encodeURIComponent(stripeCustomerId)}` : ''}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setQuota(prev => ({
          ...(prev || {}),
          images_used: data.images_used,
          cap: data.cap,
          paid_images_remaining: data.paid_images_remaining,
          show_quota_banner: data.show_quota_banner,
        }));
      })
      .catch(() => { /* silent — banner stays hidden */ });
  }, [isSelfServe, clientId, stripeCustomerId]);

  const handleTemplate = (t) => {
    if (selectedTemplate === t.id) { setSelectedTemplate(null); setTopic(''); }
    else { setSelectedTemplate(t.id); setTopic(t.topic); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setBuildError('');
    setBuildToast(false);
    try {
      const bh  = brandHub || {};
      // Same carryover as handleRegen above: the panel's other reads are
      // Phase 5-migrated, this generate was not.
      const res = await clientScopedFetch(`${BACKEND}/api/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId:              stripeCustomerId,
          topic:                   topic.trim() || 'showcase the brand',
          bizName:                 bh.bizName,
          tagline:                 bh.tagline,
          category:                bh.category,
          tones:                   bh.tones,
          emojiPref:               bh.emojiPref,
          primaryColour:           bh.primaryColour,
          secondaryColour:         bh.secondaryColour,
          accentColour:            bh.accentColour,
          logoUrl:                 bh.logoUrl,
          visualStyles:            bh.visualStyles,
          lightingMood:            bh.lightingMood,
          imgBrandKeywords:        bh.imgBrandKeywords,
          imgAvoidTerms:           bh.imgAvoidTerms,
          falStyleKeywords:        bh.falStyleKeywords,
          negativePromptAdditions: bh.negativePromptAdditions,
          keyProducts:             bh.keyProducts,
          upcomingPromos:          bh.upcomingPromos,
          activePromotions:        bh.activePromotions,
          avoidTopics:             bh.avoidTopics,
          hashtagSets:             bh.hashtagSets,
        }),
      });
      const data = await res.json();
      if (res.status === 403 && data.error === 'cap_reached') {
        setBuildError(`${aiImageLimitMessage(data)}. Top up, wait until next month, or upgrade.`);
        setGenerating(false);
        return;
      }
      if (!res.ok) {
        setBuildError(data.error || 'Generation failed — try again.');
        setGenerating(false);
        return;
      }
      // Merge: the usage in a generate response only carries the paid balance
      // when it was read (at the cap), so keep the last known one otherwise.
      setQuota(prev => ({ ...(prev || {}), ...data.usage }));
      setTopic('');
      setBuildToast(true);
      setTimeout(() => setBuildToast(false), 4000);
      await fetchPosts();
      setTab('approve');
    } catch {
      setBuildError('Network error — check connection.');
    }
    setGenerating(false);
  };

  const TABS = isSelfServe
    ? [{ id: 'approve', label: 'Approve' }, { id: 'build', label: 'Build' }, { id: 'archive', label: 'Archive' }]
    : [{ id: 'approve', label: 'Approve' }, { id: 'archive', label: 'Archive' }];

  // When the plan couldn't be confirmed we still render the conservative view
  // (approve + archive, no Build tab), but we must not assert the Managed copy
  // — telling a Self-Serve client "your team drops posts here" promises a
  // service they aren't paying for.
  const planUnknown = planStatus === 'failed';
  const banner = planUnknown
    ? "We couldn't load your plan details just now. Your subscription and billing are unaffected — this only limits what this page can show."
    : isSelfServe
      ? 'Approve posts to send them to your scheduler.'
      : 'Your team drops posts here for your approval.';

  return (
    <div className="aq-wrap">
      <div className="aq-header">
        <div className="aq-title">Content Queue</div>
        <div className={`aq-count-pill${posts.length === 0 ? ' zero' : ''}`}>
          {posts.length === 0 ? 'All reviewed' : `${posts.length} pending`}
        </div>
        {isSelfServe && quota?.cap != null && (
          <div className="aq-count-pill">
            {quotaPillText(quota)}
          </div>
        )}
      </div>

      {isSelfServe && quota?.show_quota_banner && (
        <div className="cq-quota-banner">
          {quota.paid_images_remaining > 0
            ? `⚠️ You're at 90% of your monthly image limit. After that, your ${quota.paid_images_remaining} paid images are used.`
            : "⚠️ You're at 90% of your monthly image limit. Upgrade or add 2x Growth Boost for 24 more images a month."}
        </div>
      )}

      <div className="aq-tab-bar">
        {TABS.map(t => (
          <button key={t.id} className={`aq-tab-btn${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Approve tab */}
      {tab === 'approve' && (
        <div>
          <div className="aq-banner" style={planUnknown ? {display:'flex', alignItems:'center', gap:12} : undefined}>
            <span style={planUnknown ? {flex:1, minWidth:0} : undefined}>{banner}</span>
            {planUnknown && onRetryPlan && (
              <button
                onClick={onRetryPlan}
                style={{
                  flexShrink:0, background:'transparent',
                  border:'1px solid var(--accent-border)', color:'var(--accent)',
                  borderRadius:6, padding:'5px 11px', fontFamily:'var(--font)',
                  fontSize:11, fontWeight:700, cursor:'pointer',
                }}
              >
                Retry
              </button>
            )}
          </div>
          <div className="aq-mode-row">
            <button
              type="button"
              className={`aq-mode-btn${swipeMode ? '' : ' active'}`}
              onClick={() => setSwipeMode(false)}
              aria-pressed={!swipeMode}
            >
              ☰ List
            </button>
            <button
              type="button"
              className={`aq-mode-btn${swipeMode ? ' active' : ''}`}
              onClick={() => setSwipeMode(true)}
              aria-pressed={swipeMode}
            >
              ⇄ Swipe
            </button>
          </div>
          <div className="aq-filter-bar">
            {[
              { id: 'all',     label: 'All' },
              { id: 'feed',    label: 'Feed' },
              { id: 'stories', label: 'Stories' },
            ].map(f => (
              <button
                key={f.id}
                className={`aq-filter-btn${contentFilter === f.id ? ' active' : ''}`}
                onClick={() => setContentFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          {loadingPosts && <div className="aq-loading">Loading posts…</div>}
          {(() => {
            const isStory = p => p.is_ig_story || p.is_fb_story;
            const isFeed  = p => Array.isArray(p.platforms) && p.platforms.length > 0;
            const visiblePosts = posts.filter(p =>
              contentFilter === 'stories' ? isStory(p)
              : contentFilter === 'feed'  ? isFeed(p)
              : true
            );
            return (<>
          {!loadingPosts && visiblePosts.length === 0 && (
            <div className="aq-all-done">
              <div className="aq-all-done-icon">✓</div>
              <div className="aq-all-done-title">{posts.length === 0 ? 'Queue is clear' : 'Nothing here'}</div>
              <div className="aq-all-done-sub">{posts.length === 0 ? 'All posts have been reviewed. New posts will appear here when ready.' : 'No posts match this filter.'}</div>
            </div>
          )}
          {swipeMode ? (
            <SwipeApprovalStack
              posts={visiblePosts}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegen={handleRegen}
              regeningIds={regeningIds}
              onPlatformToggle={handlePlatformToggle}
              onSave={handlePostSave}
              processingIds={processingIds}
              approveErrors={approveErrors}
              tiktokConsentIds={tiktokConsentIds}
              onTtConsentChange={toggleTiktokConsent}
            />
          ) : visiblePosts.map(post => (
            <QueueCard
              key={post.id}
              post={post}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegen={handleRegen}
              regening={regeningIds.has(post.id)}
              onPlatformToggle={handlePlatformToggle}
              onSave={handlePostSave}
              processing={processingIds.has(post.id)}
              approveError={!!approveErrors[post.id]}
              ttConsented={tiktokConsentIds.has(post.id)}
              onTtConsentChange={toggleTiktokConsent}
            />
          ))}
          {swipeMode && undoInfo && (
            <div className={`aq-swipe-undo${undoInfo.error ? ' failed' : ''}`}>
              {undoInfo.error ? (
                <>
                  <span>{undoInfo.error}</span>
                  <button type="button" className="aq-swipe-navbtn" style={{ marginLeft: 'auto' }} onClick={() => setUndoInfo(null)}>
                    Dismiss
                  </button>
                </>
              ) : (
                <>
                  <span>Approved &amp; scheduled.</span>
                  <button type="button" className="aq-undo-btn" onClick={handleUndoApprove}>
                    Undo
                  </button>
                </>
              )}
            </div>
          )}
            </>);
          })()}
        </div>
      )}

      {/* Build tab (self-serve only) */}
      {tab === 'build' && isSelfServe && (
        <div className="aq-build-wrap">
          <div>
            <div className="cq-tpl-label">Quick templates</div>
            <div className="cq-tpl-row">
              {POST_TEMPLATES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`cq-tpl-chip${selectedTemplate === t.id ? ' active' : ''}`}
                  onClick={() => handleTemplate(t)}
                  disabled={generating}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="cq-topic-row">
            <input
              className="cq-topic-input"
              placeholder="Post topic — optional (e.g. Spring menu launch)"
              value={topic}
              onChange={e => setTopic(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !generating && handleGenerate()}
              disabled={generating}
            />
            <button className="cq-generate-btn" onClick={handleGenerate} disabled={generating}>
              {generating ? <><span className="cq-spinner" /> Generating…</> : 'Generate Post'}
            </button>
          </div>
          {buildError && <div className="cq-error">{buildError}</div>}
          {buildToast && <div className="cq-toast">✓ Post generated — check the Approve tab</div>}
        </div>
      )}

      {/* Archive tab */}
      {tab === 'archive' && (
        <div className="aq-all-done">
          <div className="aq-all-done-icon">📁</div>
          <div className="aq-all-done-title">Archive</div>
          <div className="aq-all-done-sub">Published posts will appear here.</div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PHOTO LIBRARY PANEL — Session 5
// ============================================================

function formatPhotoDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', timeZone: getDisplayTimeZone() });
}

// focusAutoPost — one-shot from Home's Getting started "Turn on auto-post":
// once the library has loaded, bring the auto-post switch into view and focus
// it. The client still flips it themselves; nothing is turned on for them.
export function PhotoLibraryPanel({ onNavigate, setSelectedBrandPhoto, focusAutoPost = false, onFocusAutoPostConsumed } = {}) {
  const { clientId } = useClientId();
  // Phase 6: reads and writes carry the act-as-client override; the load effect
  // must re-run when that context changes (Phase 3 reasoning).
  const { viewingAsClientId } = useViewingAs();
  const [photos, setPhotos]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [fileDragging, setFileDragging] = useState(false);
  const [toast, setToast]           = useState({ visible: false, msg: '' });
  const [showInfo, setShowInfo]     = useState(true);
  const [dragId, setDragId]         = useState(null);   // card being dragged
  const [dragOverId, setDragOverId] = useState(null);   // card being hovered over
  // Auto-pull: brand_profile.auto_pull_photos + the alert slot the backend
  // cron writes when it can't pull (see /api/auto-generate-posts).
  const [autoPull, setAutoPull]           = useState(false);
  const [autoPullSaving, setAutoPullSaving] = useState(false);
  const [autoPullAlert, setAutoPullAlert] = useState(null);
  const fileInputRef                = React.useRef(null);
  const toastTimerRef               = React.useRef(null);
  const autoPullToggleRef           = React.useRef(null);
  // The switch glows when the panel was opened via focusAutoPost. focusAutoPost
  // itself is one-shot (consumed below), so the glow is latched here and only
  // cleared once auto-post is actually on; leaving the panel unmounts it.
  const [autoPostGlow, setAutoPostGlow] = useState(focusAutoPost);

  useEffect(() => {
    if (focusAutoPost) setAutoPostGlow(true);
  }, [focusAutoPost]);

  useEffect(() => {
    if (!focusAutoPost || loading) return;
    const el = autoPullToggleRef.current;
    if (el) {
      if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus();
    }
    if (onFocusAutoPostConsumed) onFocusAutoPostConsumed();
  }, [focusAutoPost, loading, onFocusAutoPostConsumed]);

  const showToast = (msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ visible: true, msg });
    toastTimerRef.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 2400);
  };

  // Phase 6: the panel's two reads (photos, auto-pull settings) were separate
  // direct queries and are now one /api/photo-library round trip. Mount issues
  // ONE request where it previously issued two.
  //
  // viewingAsClientId is in the deps for the Phase 3 reason: clientId is the
  // signed-in user and never changes when an admin enters or leaves a client
  // view, so keyed on [clientId] alone this would show the admin's library.
  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const d = await fetchLibrary();
      if (cancelled) return;
      applyLibrary(d);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, viewingAsClientId]);

  async function fetchLibrary() {
    if (!clientId) return null;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/photo-library`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error('[PhotoLibrary] load failed:', err?.message || err);
      return null;
    }
  }

  function applyLibrary(d) {
    if (!d) return;
    setPhotos((d.photos || []).map(p => ({ ...p, date: p.created_at?.slice(0, 10) })));
    // A null settings payload means the endpoint could not read those columns —
    // treated as "feature off", exactly as the direct read did.
    setAutoPull(!!d.settings?.auto_pull_photos);
    if (d.settings?.auto_pull_photos) setAutoPostGlow(false);
    setAutoPullAlert(d.settings?.auto_pull_alert_kind || null);
  }

  async function loadAutoPull() {
    applyLibrary(await fetchLibrary());
  }

  const toggleAutoPull = async () => {
    if (!clientId || autoPullSaving) return;
    const next = !autoPull;
    setAutoPull(next);           // optimistic
    setAutoPullSaving(true);
    let error = null;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/photo-library/settings`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ settings: { auto_pull_photos: next } }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) { error = err; }
    setAutoPullSaving(false);
    if (error) {
      setAutoPull(!next);        // roll back
      console.error('[PhotoLibrary] auto_pull_photos save failed:', error.message);
      showToast('Could not save that setting — try again');
      return;
    }
    if (next) setAutoPostGlow(false);
    showToast(next
      ? 'Auto-posting on — we\'ll top up your queue from this library'
      : 'Auto-posting off — posts are created only when you ask');
  };

  // Clears the client-facing "no unused photos" banner the moment the client
  // fixes it. The backend clears the same slot on its next successful pull;
  // without this the banner would linger for up to 6 hours after an upload.
  async function clearNoPhotosAlert() {
    if (!clientId) return;
    setAutoPullAlert(null);
    // The 'no_photos' guard lives in the endpoint, preserved verbatim: it is
    // what stops this clearing a DIFFERENT alert the cron raised meanwhile.
    try {
      await clientScopedFetch(`${BACKEND}/api/photo-library/clear-alert`, { method: 'POST' });
    } catch (err) {
      console.warn('[PhotoLibrary] clear auto-pull alert failed:', err.message);
    }
  }

  async function loadPhotos() {
    if (!clientId) return;
    setLoading(true);
    applyLibrary(await fetchLibrary());
    setLoading(false);
  }

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!clientId) { showToast('Still loading your workspace — try again in a moment'); return; }
    let uploads = Array.from(files);
    // The Photo Library is image-only (brand reference photos). Videos belong in
    // the Video Editor, which renders them into publishable posts. Detect video
    // drops/selections, point the user there, and drop them from the batch.
    const videoFiles = uploads.filter(f => (f.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v|avi)$/i.test(f.name || ''));
    if (videoFiles.length) {
      showToast('Videos go in the Video Editor, not the Photo Library — upload your clip there to turn it into a post.');
      uploads = uploads.filter(f => !videoFiles.includes(f));
      if (uploads.length === 0) return;
    }
    let count = 0;
    let firstError = null;
    for (const rawFile of uploads) {
      // iPhone HEIC/HEIF → JPEG so it renders in-browser after upload.
      // Phase 6: was upload -> getPublicUrl -> insert -> rollback, four calls
      // that had to move as ONE unit because of that rollback. They are now a
      // single POST /api/brand-photos per file, with the rollback server-side.
      //
      // Still one request per file, deliberately: the loop reports "N of M
      // uploaded" from per-file outcomes, and a batch endpoint would have had
      // to reimplement that partial-success accounting.
      const file = await ensureRenderableImage(rawFile);
      try {
        const form = new FormData();
        form.append('file', file, file.name);
        // No Content-Type header — the browser sets the multipart boundary.
        const res = await clientScopedFetch(`${BACKEND}/api/brand-photos`, { method: 'POST', body: form });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      } catch (err) {
        console.error('[PhotoLibrary] upload failed:', err);
        if (!firstError) firstError = err.message || 'upload failed';
        continue;
      }
      count++;
    }
    await loadPhotos();
    if (count > 0 && autoPullAlert === 'no_photos') await clearNoPhotosAlert();
    if (count === 0 && firstError) showToast(`Upload failed: ${firstError}`);
    else if (count < uploads.length) showToast(`${count} of ${uploads.length} uploaded — ${firstError || 'some failed'}`);
    else showToast(`${count} photo${count !== 1 ? 's' : ''} uploaded`);
  };

  // Phase 6: was a storage remove + a row delete, which had to move together.
  // The endpoint reads the row for its storage_path, so the panel no longer
  // needs to look it up. The row delete was also filtered by id ALONE — that
  // gap closes because the route is client-scoped, a side effect of the
  // routing rather than a fix worked here.
  const handleDelete = async (id) => {
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-photos/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
    } catch (err) {
      console.error('[PhotoLibrary] delete failed:', err.message);
      showToast('Could not remove that photo — try again');
      return;
    }
    setPhotos(prev => prev.filter(p => p.id !== id));
    showToast('Photo removed');
  };

  const handleUseInPost = async (photo) => {
    if (photo.used) {
      try {
        await patchBrandPhoto(photo.id, { used: false });
        setPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, used: false } : p));
      } catch (err) {
        console.error('[PhotoLibrary] clear used flag failed:', err.message);
      }
    }
    if (setSelectedBrandPhoto) {
      setSelectedBrandPhoto({
        id:           photo.id,
        url:          photo.url,
        filename:     photo.filename,
        storage_path: photo.storage_path,
      });
    }
    showToast(`"${photo.filename.replace(/\.[^.]+$/, '')}" attached to next post`);
    if (onNavigate) onNavigate('imagegen');
  };

  // Reuse: clear used flag without changing visual position. Photo stays
  // at the bottom of the unused stack — sort_order = max(unused) + 1.
  const handleReuse = async (photo) => {
    const maxUnusedSort = photos
      .filter(p => !p.used && p.id !== photo.id)
      .reduce((max, p) => Math.max(max, p.sort_order ?? 0), -1);
    const newSort = maxUnusedSort + 1;
    let error = null;
    try { await patchBrandPhoto(photo.id, { used: false, sort_order: newSort }); }
    catch (err) { error = err; }
    if (error) {
      console.error('[PhotoLibrary] reuse failed:', error.message);
      showToast('Could not reuse photo — try again');
      return;
    }
    await loadPhotos();
    if (autoPullAlert === 'no_photos') await clearNoPhotosAlert();
    showToast(`"${photo.filename.replace(/\.[^.]+$/, '')}" available again`);
  };

  // File drop handlers (uploading files from OS)
  const onFileDragOver  = (e) => { e.preventDefault(); setFileDragging(true); };
  const onFileDragLeave = ()  => setFileDragging(false);
  const onFileDrop      = (e) => {
    e.preventDefault();
    setFileDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // Card reorder drag handlers
  const onCardDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onCardDragEnter = (e, id) => { e.preventDefault(); if (id !== dragId) setDragOverId(id); };
  const onCardDragOver  = (e)     => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const onCardDragEnd   = ()      => { setDragId(null); setDragOverId(null); };
  const onCardDrop      = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setDragOverId(null); return; }
    // Drag-reorder only applies to unused photos — used photos are anchored
    // to the bottom of the grid by `order('used', asc)` in loadPhotos.
    const draggedPhoto = photos.find(p => p.id === dragId);
    const targetPhoto  = photos.find(p => p.id === targetId);
    if (!draggedPhoto || !targetPhoto || draggedPhoto.used || targetPhoto.used) {
      setDragId(null); setDragOverId(null); return;
    }
    let newOrder = null;
    setPhotos(prev => {
      const arr   = [...prev];
      const fromI = arr.findIndex(p => p.id === dragId);
      const toI   = arr.findIndex(p => p.id === targetId);
      if (fromI === -1 || toI === -1) return prev;
      const [moved] = arr.splice(fromI, 1);
      arr.splice(toI, 0, moved);
      newOrder = arr;
      return arr;
    });
    setDragId(null);
    setDragOverId(null);
    if (!newOrder) return;
    // Renumber unused photos and persist sort_order in parallel.
    // Phase 6: was a Promise.all fan of N single-row updates, each filtered by
    // id alone. Now one batch call — same resolution as Calendar's
    // handleInsertDrop in Phase 5, and for the same reason: the endpoint runs
    // the same N updates server-side, so the partial-failure behaviour is
    // unchanged while the client scope becomes the server's to enforce.
    const unused = newOrder.filter(p => !p.used);
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-photos/sort-batch`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ updates: unused.map((p, i) => ({ id: p.id, sort_order: i })) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) {
        const first = (body.results || []).find(r => !r.ok);
        throw new Error(first?.error || body.error || `HTTP ${res.status}`);
      }
      showToast('Order updated');
    } catch (err) {
      console.error('[PhotoLibrary] sort_order persist failed:', err.message);
      showToast('Order saved locally — refresh may revert');
    }
  };

  const isEmpty = photos.length === 0;
  const showAutoPostGlow = autoPostGlow && !autoPull && !loading;

  return (
    <div className="pl-wrap">
      {loading && <div style={{color:'#aaa',padding:'2rem',textAlign:'center'}}>Loading photos…</div>}
      {/* TOOLBAR */}
      <div className="pl-toolbar">
        <div className="pl-toolbar-title">Photo Library</div>
        {!isEmpty && <div className="pl-toolbar-count">{photos.length} photo{photos.length !== 1 ? 's' : ''}</div>}
        <div className="pl-toolbar-right">
          {/* AUTO-POST TOGGLE — brand_profile.auto_pull_photos */}
          <div
            ref={autoPullToggleRef}
            className={`ig-admin-toggle${showAutoPostGlow ? ' pl-autopost-glow' : ''}`}
            style={{ marginBottom: 0, padding: '7px 12px', gap: 8, cursor: autoPullSaving ? 'wait' : 'pointer' }}
            onClick={toggleAutoPull}
            role="switch"
            aria-checked={autoPull}
            aria-label="Auto-post from this library"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAutoPull(); } }}
          >
            <span className="ig-admin-toggle-label" style={{ flex: 'none', whiteSpace: 'nowrap' }}>
              Auto-post from library
            </span>
            <div className={`ig-toggle-track${autoPull ? ' on' : ''}`} style={{ opacity: autoPullSaving ? 0.5 : 1 }}>
              <div className="ig-toggle-thumb" />
            </div>
          </div>
          {showAutoPostGlow && <span className="pl-autopost-hint">← Turn this on</span>}
          <Tip text="When your upcoming posts run low, we'll automatically create a new one from the next unused photo here. Photos are used in order, and each is used once." below />
          <button className="pl-upload-btn" onClick={() => fileInputRef.current?.click()}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 1v10M4 5l4-4 4 4"/><path d="M1 13h14"/>
            </svg>
            Upload Photos
          </button>
          <Tip text="Uploaded photos go live in your library immediately and are ready to use in posts." below />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none', overflow: 'hidden' }}
            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
          />
        </div>
      </div>
      {/* Auto-post only creates posts; only auto-approve skips review. */}
      <p className="pl-autopost-note">
        Auto-post creates posts for you — they still wait in Review &amp; Approve unless auto-approve is turned on in Post Generator.
      </p>

      {/* AUTO-PULL STALLED BANNER — set by /api/auto-generate-posts when it
          ran, wanted to post, and found nothing left to post with. Deliberately
          not dismissible: auto-posting is silently stopped until it's resolved,
          which is exactly the state that must not fail quietly. */}
      {autoPullAlert === 'no_photos' && (
        <div
          className="pl-info-banner"
          style={{ borderColor: 'rgba(255,176,32,0.45)', background: 'rgba(255,176,32,0.08)' }}
          role="status"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#FFB020" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
            <path d="M8 1.5 15 14H1L8 1.5z"/><line x1="8" y1="6" x2="8" y2="9.5"/><circle cx="8" cy="11.5" r="0.5" fill="#FFB020" stroke="none"/>
          </svg>
          <span className="pl-info-banner-text">
            <strong>Auto-posting is paused — every photo here has been used.</strong> Upload new photos, or hit “Use again” on one you'd like to repost, and we'll pick straight back up.
          </span>
          <Tip text="We only auto-create a post when your upcoming queue runs low, and each photo is used once. When the library runs out, we stop rather than repost something without asking." />
        </div>
      )}

      {/* INSTRUCTION BANNER */}
      {showInfo && !isEmpty && (
        <div className="pl-info-banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
            <circle cx="8" cy="8" r="7"/><line x1="8" y1="7" x2="8" y2="11"/><circle cx="8" cy="5" r="0.5" fill="var(--accent)" stroke="none"/>
          </svg>
          <span className="pl-info-banner-text">
            <strong>Photos are used in order, left to right.</strong> Drag to rearrange. Uploaded photos are ready to use immediately. Generated images need approval before they enter the queue.
          </span>
          <Tip text="Posts created in the Post Generator arrive as Pending. Approve them from that panel and they'll appear here automatically." />
          <button className="pl-info-dismiss" onClick={() => setShowInfo(false)} title="Dismiss" aria-label="Dismiss this notice">×</button>
        </div>
      )}

      {/* EMPTY STATE — full drop zone */}
      {isEmpty && (
        <div
          className={`pl-dropzone${fileDragging ? ' dragging' : ''}`}
          onDragOver={onFileDragOver}
          onDragLeave={onFileDragLeave}
          onDrop={onFileDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="pl-dropzone-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="15" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="3,17 8,12 11,15 14,12 21,18"/>
            </svg>
          </div>
          <div className="pl-dropzone-title">Drop your photos here</div>
          <div className="pl-dropzone-sub">
            Drag and drop image files, or click to browse.<br/>
            JPG, PNG, WEBP supported.
          </div>
          <button className="pl-dropzone-btn" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>
            Browse Files
          </button>
        </div>
      )}

      {/* POPULATED STATE */}
      {!isEmpty && <>
        {/* Upload drop banner */}
        <div
          className={`pl-drop-banner${fileDragging ? ' dragging' : ''}`}
          onDragOver={onFileDragOver}
          onDragLeave={onFileDragLeave}
          onDrop={onFileDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--text-muted)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 1v10M4 5l4-4 4 4"/><path d="M1 13h14"/>
          </svg>
          <span className="pl-drop-banner-text"><strong>Drop to upload</strong> or click to browse</span>
          <span className="pl-drop-banner-hint">JPG · PNG · WEBP</span>
        </div>

        {/* Grid — draggable cards */}
        <div className="pl-grid">
          {photos.map((photo, idx) => (
            <div
              key={photo.id}
              className={`pl-card${dragId === photo.id ? ' dragging-card' : ''}${dragOverId === photo.id ? ' drag-over' : ''}${photo.used ? ' pl-card-used' : ''}`}
              draggable={!photo.used}
              onDragStart={photo.used ? undefined : e => onCardDragStart(e, photo.id)}
              onDragEnter={photo.used ? undefined : e => onCardDragEnter(e, photo.id)}
              onDragOver={photo.used ? undefined : onCardDragOver}
              onDragEnd={photo.used ? undefined : onCardDragEnd}
              onDrop={photo.used ? undefined : e => onCardDrop(e, photo.id)}
            >
              {/* Position badge — top-left = #1 */}
              <div style={{
                position:'absolute', top:7, left:7, zIndex:2,
                background:'rgba(13,15,20,0.72)', border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:5, padding:'2px 6px', fontSize:10, fontWeight:700,
                color: idx === 0 ? 'var(--accent)' : 'var(--text-muted)',
                fontFamily:'var(--font-head)', letterSpacing:'0.04em',
                display:'flex', alignItems:'center', gap:3,
              }}>#{idx + 1}{idx === 0 && <Tip text="Photos are used in order — this one is next. Drag cards to change the sequence." below />}</div>

              <div className="pl-card-img-wrap">
                <img src={photo.url} alt={photo.filename} style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'8px', opacity: photo.used ? 0.55 : 1 }} />
                {photo.used && (
                  <div style={{
                    position:'absolute', top:7, right:7, zIndex:2,
                    background:'rgba(13,15,20,0.82)', border:'1px solid rgba(255,255,255,0.18)',
                    borderRadius:5, padding:'2px 6px', fontSize:10, fontWeight:700,
                    color:'var(--text-muted)', fontFamily:'var(--font-head)', letterSpacing:'0.06em',
                    textTransform:'uppercase',
                  }}>Used</div>
                )}
                {/* Hover overlay */}
                <div className="pl-card-overlay">
                  {photo.used && (
                    <button className="pl-overlay-btn pl-overlay-reuse" onClick={() => handleReuse(photo)}>Reuse</button>
                  )}
                  <button className="pl-overlay-btn pl-overlay-use"    onClick={() => handleUseInPost(photo)}>Use in Post</button>
                  <button className="pl-overlay-btn pl-overlay-delete" onClick={() => handleDelete(photo.id)}>Delete</button>
                </div>
              </div>
              <div className="pl-card-info">
                <div className="pl-card-filename" title={photo.filename}>{photo.filename}</div>
                <div className="pl-card-date">{formatPhotoDate(photo.date)}</div>
              </div>
            </div>
          ))}
        </div>
      </>}

      {/* TOAST */}
      <div className={`pl-toast${toast.visible ? ' visible' : ''}`}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="8" cy="8" r="7"/><polyline points="5,8 7,10 11,6"/>
        </svg>
        {toast.msg}
      </div>
    </div>
  );
}

// ============================================================
// IMAGE GENERATION PANEL — Session 6
// ============================================================
const IG_TEMPLATES = [
  { label: 'Morning Vibe', text: 'A warm, sunlit coffee shop interior in the early morning, steam rising from a fresh espresso, cozy Kelowna aesthetic, golden hour light' },
  { label: 'Product Hero', text: 'A beautifully styled flat lay of specialty coffee beans and a ceramic cup on a dark wood surface, editorial food photography' },
  { label: 'Seasonal', text: 'Outdoor patio scene in autumn Kelowna, warm drinks, golden leaves, cozy atmosphere, lifestyle photography' },
  { label: 'Behind the Scenes', text: 'Barista crafting latte art, hands in focus, soft bokeh background, warm tones, authentic coffee culture' },
  { label: 'Minimal Brand', text: 'Clean minimal product shot of a coffee cup with brand colours, white background, studio lighting, professional' },
];

const IG_STYLES = [
  { id: 'warm',     label: 'Warm & Cozy',       icon: '🌅' },
  { id: 'airy',     label: 'Natural & Airy',    icon: '☁️' },
  { id: 'minimal',  label: 'Clean Minimal',     icon: '⬜' },
  { id: 'rustic',   label: 'Rustic & Textured', icon: '🪵' },
  { id: 'vibrant',  label: 'Vibrant & Bold',    icon: '🎨' },
  { id: 'editorial',label: 'Editorial',         icon: '📸' },
  { id: 'flatlay',  label: 'Flat Lay',          icon: '🔲' },
  { id: 'neon',     label: 'Night & Neon',      icon: '🌃' },
];

// ─────────────────────────────────────────────────────────────
// LogoPositionPicker — visual mini-canvas with 5 dot targets
// (four corners + centre). Dots only — no preview element.
// String values match what the canvas/image generator already
// expects.
// ─────────────────────────────────────────────────────────────
function LogoPositionPicker({ value, onChange }) {
  const POSITIONS = [
    { id: 'top-left',     label: 'Top left',     top: 6,  left: 6  },
    { id: 'top-right',    label: 'Top right',    top: 6,  left: 66 },
    { id: 'center',       label: 'Center',       top: 26, left: 36 },
    { id: 'bottom-left',  label: 'Bottom left',  top: 46, left: 6  },
    { id: 'bottom-right', label: 'Bottom right', top: 46, left: 66 },
  ];
  const selected = POSITIONS.find(p => p.id === value) || POSITIONS[4];

  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'flex-start', gap:8 }}>
      <div
        style={{
          position:'relative',
          width:72, height:52,
        }}
      >
        {POSITIONS.map(p => {
          const isSel = p.id === selected.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onChange(p.id)}
              title={p.label}
              aria-label={p.label}
              aria-pressed={isSel}
              style={{
                position:'absolute',
                top: p.top,
                left: p.left,
                transform:'translate(-50%, -50%)',
                width:12, height:12,
                padding:0,
                borderRadius:'50%',
                cursor:'pointer',
                background: isSel ? '#1D9E75' : 'transparent',
                border: isSel ? '1px solid #1D9E75' : '1px solid rgba(255,255,255,0.45)',
                boxShadow: isSel ? '0 0 0 3px rgba(29,158,117,0.25)' : 'none',
                transition:'background 0.15s, border-color 0.15s, box-shadow 0.15s',
              }}
            />
          );
        })}
      </div>
      <span style={{ fontSize:12, color:'rgba(255,255,255,0.75)', fontFamily:'var(--font)' }}>
        {selected.label}
      </span>
    </div>
  );
}

// ─── Client-side text overlay via HTML5 Canvas ──────────────────
// Moved out of ImageGenerationPanel verbatim so the Scheduler's Edit bakes
// text the same way. Carousel, Generate Post and Scheduler all call these.
const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload  = () => resolve(img);
  img.onerror = (e) => reject(e);
  img.src = url;
});

// The lines to draw at the current ctx.font. A typed line break always starts
// a new line (a blank line stays blank), and any line too wide for maxWidth
// wraps at spaces — the same as the preview's CSS `white-space: pre-line`.
// Each line is its words with their offsets in `text`, a blank line [].
const wrapCanvasText = (ctx, text, maxWidth) => {
  const lines = [];
  let offset = 0;
  for (const para of text.split('\n')) {
    let line = [];
    let lineText = '';
    for (const m of para.matchAll(/\S+/g)) {
      const word = { word: m[0], at: offset + m.index };
      const test = lineText ? `${lineText} ${m[0]}` : m[0];
      if (lineText && ctx.measureText(test).width > maxWidth) {
        lines.push(line);
        line = [word];
        lineText = m[0];
      } else {
        line.push(word);
        lineText = test;
      }
    }
    lines.push(line);
    offset += para.length + 1;
  }
  return lines;
};

// The accent: one word or phrase per overlay, marked by wrapping it in
// asterisks ("Book your *brows* today") and drawn in accentColor. Returns the
// text to draw — the pair of asterisks removed — and the accent's [start, end)
// in it, or null. Asterisks past the first pair, or a lone one, draw as typed.
const DEFAULT_ACCENT_COLOR = '#ffc857';
const parseAccent = (raw) => {
  const a = raw.indexOf('*');
  const b = a < 0 ? -1 : raw.indexOf('*', a + 1);
  if (b < 0) return { text: raw, accent: null };
  return {
    text:   raw.slice(0, a) + raw.slice(a + 1, b) + raw.slice(b + 1),
    accent: b > a + 1 ? [a, b - 1] : null,
  };
};

// One wrapped line (wrapCanvasText's words) as runs of plain and accent text,
// joined by single spaces.
const lineRuns = (words, accent) => {
  const runs = [];
  const inAccent = (i) => !!accent && i >= accent[0] && i < accent[1];
  words.forEach((w, wi) => {
    if (wi > 0) runs[runs.length - 1].text += ' ';
    for (let c = 0; c < w.word.length; c++) {
      const on = inAccent(w.at + c);
      const last = runs[runs.length - 1];
      if (last && last.accent === on) last.text += w.word[c];
      else runs.push({ text: w.word[c], accent: on });
    }
  });
  return runs;
};

const getContrastColor = (hex) => {
  const h = (hex || '#ffffff').replace('#', '');
  if (h.length !== 6) return '#000000';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.5 ? '#000000' : '#ffffff';
};

// The font picker's choices: Inter (the default, and every overlay saved
// before there was a picker) plus the Video Editor's curated set.
const OVERLAY_FONTS = [
  { id: 'default', label: 'Inter' },
  ...VIDEO_FONTS.filter(f => f.ttfUrl).map(f => ({ id: f.id, label: f.label })),
];

// The font an overlay is drawn in. The live preview (CSS) and the bake (canvas)
// both take the family, weight and fallback from here, so they can't drift
// apart. Inter is loaded by the @import at the top of CSS — before that it was
// named but never loaded, so every machine baked in its own fallback font.
// Picker fonts are bold where the font has a bold, else their one weight — so
// nothing is ever faux-bolded, which canvas and CSS don't do identically.
// They're registered under their own family name (`face`) because the Video
// Editor declares the same fonts without weights under the plain names.
const overlayFontOf = (overlay) => {
  const f = overlay?.textFont && overlay.textFont !== 'default' ? resolveFont(overlay.textFont) : null;
  if (!f || !f.ttfUrl) return { family: "'Inter', sans-serif", weight: 700 };
  const bold = !!(f.variable || f.boldTtfUrl);
  const name = `ap-overlay-${f.id}`;
  return {
    family: `'${name}', sans-serif`,
    weight: bold ? 700 : 400,
    face:   { name, url: f.boldTtfUrl || f.ttfUrl, weight: f.variable ? '100 900' : bold ? '700' : '400' },
  };
};

// Adds a picker font to document.fonts (once), where both CSS and canvas can
// use it. Safe to call on every render.
const overlayFaces = new Map();
const registerOverlayFont = (font) => {
  if (!font.face || overlayFaces.has(font.face.name)) return;
  const ff = new FontFace(font.face.name, `url("${font.face.url}")`, { weight: font.face.weight });
  overlayFaces.set(font.face.name, ff);
  document.fonts.add(ff);
};

// Resolves once the overlay's font is ready to draw `text`. Canvas never waits
// for a font the way CSS does: drawing before it has loaded silently uses the
// fallback, so the bake must await this. A font that can't load (offline, CDN
// down) is warned about and drawn in the fallback rather than failing the bake.
const loadOverlayFont = async (font, text) => {
  registerOverlayFont(font);
  try {
    const faces = await document.fonts.load(`${font.weight} 16px ${font.family}`, text || ' ');
    if (!faces.length) console.warn('[TextOverlay] font not available, using fallback:', font.family);
  } catch (err) {
    console.warn('[TextOverlay] font failed to load, using fallback:', font.family, err?.message || err);
  }
  // Drop a face that failed so the next attempt fetches it again.
  const ff = font.face && overlayFaces.get(font.face.name);
  if (ff && ff.status === 'error') {
    document.fonts.delete(ff);
    overlayFaces.delete(font.face.name);
  }
};

// Loads every picker font's "autopilot" glyphs once per app session (module-
// level cache, shared across every open TextOverlayControls — Carousel can
// have one per slide). The font picker gates its previews on this so a
// preview never silently renders in the browser's fallback font while the
// real face is still in flight — the same failure the Sep 11 build had for
// the single baked-in Inter font, now guarded for all 14 picker fonts.
let overlayFontsReadyPromise = null;
const ensureOverlayFontsReady = () => {
  if (!overlayFontsReadyPromise) {
    overlayFontsReadyPromise = Promise.all(
      OVERLAY_FONTS.map(f => loadOverlayFont(overlayFontOf({ textFont: f.id }), 'autopilot'))
    );
  }
  return overlayFontsReadyPromise;
};

// Freeform position (dragged in the preview): `x`/`y` are 0–1 fractions of
// the image's width/height, so they hold across image sizes. `x` is the
// text's alignment edge — its left edge when left-aligned, centre when
// centred, right edge when right-aligned — which is exactly where the preset
// buttons put it (0.1 / 0.5 / 0.9), and `y` is the block's vertical centre.
// null when the overlay has no x/y (every overlay saved before dragging
// existed): it then renders from the Top/Middle/Bottom preset, unchanged.
const overlayAnchor = (overlay) =>
  (Number.isFinite(overlay?.x) && Number.isFinite(overlay?.y) ? { x: overlay.x, y: overlay.y } : null);

// An overlay is a list of independent text blocks, each one the flat object
// ({ text, textPosition, …, x, y, rotation }) a whole overlay used to be.
// posts.text_overlay saved before blocks existed holds one plain object: it
// reads as a one-block list and renders exactly as it always has. New saves
// store the array. null/absent → no blocks.
const overlayBlocksOf = (v) =>
  (Array.isArray(v) ? v.filter(Boolean) : v && typeof v === 'object' ? [v] : []);
// The blocks as the live preview takes them: text trimmed, blocks with no text
// kept in place (they draw nothing), so block i is block i in the preview and
// in the controls alike.
const trimOverlayBlocks = (v) => overlayBlocksOf(v).map(b => ({ ...b, text: (b.text || '').trim() }));
// The blocks as they're baked and saved: only those with text, trimmed. null
// when none has any — "no overlay", as an empty text always was.
const liveOverlayBlocks = (v) => {
  const blocks = trimOverlayBlocks(v).filter(b => b.text);
  return blocks.length ? blocks : null;
};

// Overlay geometry (font size, wrap width, padding) is overlayMetrics, in
// utils/overlayGeometry: compositeTextOntoImage and TextOverlayPreviewImage
// both lay out from its numbers, custom handle sizes and widths included.

// Rotation in degrees, clockwise (a positive angle turns clockwise in both
// canvas and CSS). 0 when absent — every overlay saved before rotation — and
// a 0 overlay renders exactly as an unrotated one always has.
const overlayRotation = (overlay) => {
  const r = Number(overlay?.rotation);
  return Number.isFinite(r) ? r : 0;
};

// The text block as compositeTextOntoImage lays it out, measured on a
// scratch canvas: its wrapped lines (wrapCanvasText's words — the preview
// draws exactly these breaks), its widest line and its height, in image px.
// The font must be loaded for real widths (the preview re-measures once it is).
let measureCtx = null;
const measureOverlayBlock = (overlay, W, H) => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  const { text } = parseAccent((overlay.text || '').trim());
  const { fontSize, maxWidth, lineGap } = overlayMetrics(overlay, W, H);
  const font = overlayFontOf(overlay);
  measureCtx.font = `${font.weight} ${fontSize}px ${font.family}`;
  const lines = wrapCanvasText(measureCtx, text, maxWidth);
  const widest = lines.reduce((m, ln) => Math.max(m, measureCtx.measureText(ln.map(w => w.word).join(' ')).width), 0);
  return { lines, widest, blockH: lines.length * lineGap };
};

// Where the (unrotated) block sits, in image px: `ax` the x its lines align
// to, `top` its top edge, and (cx, cy) the centre of the box its text
// actually covers — the rotation pivot. A left-aligned block's centre is
// half its widest line right of its anchor; a right-aligned one's, half a
// line left; a centred one's, on it.
const overlayPlacement = (overlay, W, H, { widest, blockH }) => {
  const align  = overlay.textAlign || 'center';
  const anchor = overlayAnchor(overlay);
  const pos    = overlay.textPosition || 'bottom';
  const { sidePad, pad } = overlayMetrics(overlay, W, H);
  const ax  = anchor ? anchor.x * W
            : align === 'left' ? sidePad : align === 'right' ? W - sidePad : W / 2;
  const top = anchor ? anchor.y * H - blockH / 2
            : pos === 'top' ? pad : pos === 'middle' ? H / 2 - blockH / 2 : H - pad - blockH;
  return {
    ax, top,
    cx: ax + (align === 'left' ? widest / 2 : align === 'right' ? -widest / 2 : 0),
    cy: top + blockH / 2,
  };
};

// Bakes an overlay — a list of text blocks, or one block as a plain object
// (see overlayBlocksOf) — onto the image. Each block is drawn in array order,
// the last on top, in its own save()/restore() so nothing one block sets
// (font, shadow, transform) reaches the next: a block renders the same
// whatever else is on the image. A one-block overlay makes exactly the draw
// calls the single-block code always did.
const compositeTextOntoImage = async (imageUrl, overlay) => {
  const blocks = trimOverlayBlocks(overlay).filter(b => b.text);
  const [img] = await Promise.all([
    loadImage(imageUrl),
    ...blocks.map(b => loadOverlayFont(overlayFontOf(b), parseAccent(b.text).text)),
  ]);
  const W = img.naturalWidth, H = img.naturalHeight;
  const canvas  = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);
  blocks.forEach(block => {
    ctx.save();
    drawOverlayBlock(ctx, block, W, H);
    ctx.restore();
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) return reject(new Error('Canvas toBlob failed'));
      resolve(blob);
    }, 'image/jpeg', 0.92);
  });
};

// One text block onto a W×H canvas, its font already loaded.
export const drawOverlayBlock = (ctx, overlay, W, H) => {
  const { text, accent } = parseAccent(overlay.text.trim());
  const textAlign   = overlay.textAlign    || 'center';
  const textColor   = overlay.textColor    || '#ffffff';
  const accentColor = overlay.accentColor  || DEFAULT_ACCENT_COLOR;
  const textStyle   = overlay.textStyle    || 'solid';
  const position    = overlay.textPosition || 'bottom';
  const anchor      = overlayAnchor(overlay);
  const rotation    = overlayRotation(overlay);
  const font        = overlayFontOf(overlay);

  const { fontSize, sidePad, maxWidth, lineGap, pad, pillPadX, pillPadY } = overlayMetrics(overlay, W, H);
  ctx.font = `${font.weight} ${fontSize}px ${font.family}`;
  // Each line is placed from its measured width and drawn run by run (the
  // accent in its own colour), so runs are always drawn left-aligned.
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'alphabetic';

  const lines = wrapCanvasText(ctx, text, maxWidth).map(words => {
    const runs = lineRuns(words, accent);
    return { runs, width: ctx.measureText(runs.map(r => r.text).join('')).width };
  });
  const blockH = lines.length * lineGap;
  // Freeform: the context is translated to the anchor below, so the block is
  // laid out around (0, 0) — vertically centred on it, lines aligned to it.
  const blockTop = anchor                ? -blockH / 2
                 : position === 'top'    ? pad
                 : position === 'middle' ? H / 2 - blockH / 2
                 :                         H - pad - blockH;
  // Baselines placed the way a CSS line box places them (the preview is CSS):
  // the font's ascent+descent centred in the 1.2 line gap. Any font lands in
  // the same spot in both, not just ones whose metrics happen to suit a fixed
  // offset.
  const probe   = ctx.measureText(text);
  const ascent  = probe.fontBoundingBoxAscent  ?? fontSize * 0.8;
  const descent = probe.fontBoundingBoxDescent ?? fontSize * 0.2;
  const baselineOf = (i) => blockTop + i * lineGap + (lineGap + ascent - descent) / 2;
  const lineLeft = anchor
    ? (w) => (textAlign === 'left' ? 0 : textAlign === 'right' ? -w : -w / 2)
    : (w) => textAlign === 'left'  ? sidePad
           : textAlign === 'right' ? W - sidePad - w
           :                         W / 2 - w / 2;
  if (anchor) ctx.translate(anchor.x * W, anchor.y * H);
  // Rotated: turn the block about the centre of the box its text covers —
  // the widest line's extent across (lineLeft already accounts for the
  // alignment), the block's height down — in whichever frame the lines are
  // laid out in. Same pivot as overlayPlacement, which the preview uses.
  if (rotation) {
    const widest = lines.reduce((m, ln) => Math.max(m, ln.width), 0);
    const cx = lineLeft(widest) + widest / 2;
    const cy = blockTop + blockH / 2;
    ctx.translate(cx, cy);
    ctx.rotate(rotation * Math.PI / 180);
    ctx.translate(-cx, -cy);
  }
  // paint(text, x, y, colour) draws one run.
  const drawRuns = (paint) => lines.forEach((ln, i) => {
    let x = lineLeft(ln.width);
    ln.runs.forEach(r => {
      paint(r.text, x, baselineOf(i), r.accent ? accentColor : textColor);
      x += ctx.measureText(r.text).width;
    });
  });

  if (textStyle === 'backed') {
    // The pill covers the font's ascent+descent plus padding — what a CSS
    // inline background covers.
    const radius = fontSize * 0.18;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    lines.forEach((ln, i) => {
      if (!ln.runs.length) return;   // a blank line gets no pill, as in CSS
      const rectX = lineLeft(ln.width) - pillPadX;
      const rectY = baselineOf(i) - ascent - pillPadY;
      const rectW = ln.width + pillPadX * 2;
      const rectH = ascent + descent + pillPadY * 2;
      if (ctx.roundRect) {
        ctx.beginPath();
        ctx.roundRect(rectX, rectY, rectW, rectH, radius);
        ctx.fill();
      } else {
        ctx.fillRect(rectX, rectY, rectW, rectH);
      }
    });
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur  = 0;
    drawRuns((t, x, y, c) => { ctx.fillStyle = c; ctx.fillText(t, x, y); });
  } else if (textStyle === 'outline') {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur  = 0;
    ctx.lineJoin    = 'round';
    ctx.miterLimit  = 2;
    ctx.lineWidth   = Math.max(4, fontSize * 0.16);
    drawRuns((t, x, y, c) => {
      ctx.strokeStyle = getContrastColor(c);
      ctx.fillStyle   = c;
      ctx.strokeText(t, x, y);
      ctx.fillText(t, x, y);
    });
  } else {
    // Canvas shadow offsets ignore the transform, but the preview's CSS
    // text-shadow turns with its element — so turn the offset by hand, and a
    // rotated block drops its shadow "down" in its own frame in both.
    const drop = Math.round(fontSize * 0.06);
    const turn = rotation * Math.PI / 180;
    ctx.shadowColor   = 'rgba(0,0,0,0.85)';
    ctx.shadowBlur    = fontSize * 0.4;
    ctx.shadowOffsetX = rotation ? -drop * Math.sin(turn) : 0;
    ctx.shadowOffsetY = rotation ? drop * Math.cos(turn) : drop;
    drawRuns((t, x, y, c) => { ctx.fillStyle = c; ctx.fillText(t, x, y); });
  }
};

// A single post's text overlay (Generate Post's Apply, Scheduler's Edit): the
// same composite as a carousel slide, then a scratch upload. Reuses the
// 'carousel-composited' purpose because the backend allowlists purposes and it
// only picks the storage filename prefix; the post- filename keeps these
// distinguishable in the bucket. Throws on failure — callers leave the stored
// image untouched.
const applyTextOverlay = async (imageUrl, overlay, idx) => {
  const blob = await compositeTextOntoImage(imageUrl, overlay);
  const file = new File([blob], `post-${idx}.jpg`, { type: 'image/jpeg' });
  return scratchUpload(file, 'carousel-composited');
};

// ─────────────────────────────────────────────────────────────
// FontPicker — the overlay font control. Every option renders the word
// "autopilot" in its own font instead of a plain text label (the font name
// stays alongside it, in the UI's own font, so a cursive script is still
// identifiable). Custom-built rather than a native <select> with styled
// <option>s: Safari doesn't reliably honour font-family on <option>, and a
// plain <select> can't preview the CLOSED state in the chosen font either.
//
// Every preview — trigger and list — is gated on ensureOverlayFontsReady(),
// so nothing here ever paints an "autopilot" sample in the browser's fallback
// font before the real face lands; while a font is still loading (first
// open, cold cache) the list shows a loading line instead of guessing.
// ─────────────────────────────────────────────────────────────
function FontPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let live = true;
    ensureOverlayFontsReady().then(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const selected = OVERLAY_FONTS.find(f => f.id === value) || OVERLAY_FONTS[0];
  const styleOf = (id) => overlayFontOf({ textFont: id });
  const rowStyle = {
    display:'flex', alignItems:'center', justifyContent:'space-between', gap:8,
    width:'100%', boxSizing:'border-box', padding:'7px 9px', border:'none',
    borderBottom:'1px solid rgba(255,255,255,0.06)', background:'transparent',
    cursor:'pointer', textAlign:'left',
  };

  return (
    <div ref={rootRef} style={{ position:'relative', marginTop:6 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Font"
        style={{
          width:'100%', boxSizing:'border-box', display:'flex', alignItems:'center',
          justifyContent:'space-between', gap:8, background:'rgba(255,255,255,0.05)',
          border:'1px solid rgba(255,255,255,0.1)', borderRadius:6,
          padding:'6px 9px', cursor:'pointer',
        }}
      >
        <span style={{
          fontFamily: ready ? styleOf(selected.id).family : 'var(--font)',
          fontWeight: ready ? styleOf(selected.id).weight : 600,
          fontSize:15, color:'var(--text-primary)', overflow:'hidden',
          textOverflow:'ellipsis', whiteSpace:'nowrap',
        }}>
          autopilot
        </span>
        <span style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
          <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600 }}>{selected.label}</span>
          <span style={{ fontSize:9, color:'var(--text-muted)' }}>{open ? '▾' : '▸'}</span>
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Font"
          style={{
            marginTop:4, border:'1px solid rgba(255,255,255,0.12)', borderRadius:6,
            overflow:'hidden', maxHeight:230, overflowY:'auto', background:'#1a1d27',
          }}
        >
          {!ready ? (
            <div style={{ padding:'10px 9px', fontSize:11, color:'var(--text-muted)' }}>Loading fonts…</div>
          ) : OVERLAY_FONTS.map(f => {
            const active = f.id === selected.id;
            const fs = styleOf(f.id);
            return (
              <button
                key={f.id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onChange(f.id); setOpen(false); }}
                style={{
                  ...rowStyle,
                  background: active ? 'rgba(49,220,186,0.12)' : 'transparent',
                }}
              >
                <span style={{
                  fontSize:10, fontWeight:600, flexShrink:0, width:82,
                  color: active ? '#31DCBA' : 'var(--text-muted)',
                }}>
                  {f.label}
                </span>
                <span style={{
                  flex:1, textAlign:'right', fontFamily: fs.family, fontWeight: fs.weight,
                  fontSize:17, color:'var(--text-primary)', overflow:'hidden',
                  textOverflow:'ellipsis', whiteSpace:'nowrap',
                }}>
                  autopilot
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RotationControl — the overlay's rotation, two ways that stay in sync: a
// dial to drag (relative — the angle changes by how far you turn, so pressing
// doesn't jump it; Shift snaps to 15° steps, and it settles on 0 within 2°)
// and a degree box for exact values ("19"). Positive is clockwise. `degrees`
// comes from the overlay, so both follow any change made elsewhere; while
// the box is being typed in, it shows the typing rather than the value.
//
// The dial reports at most once per animation frame: the live preview is in
// another part of the panel, so every report re-renders the panel.
// ─────────────────────────────────────────────────────────────
function RotationControl({ degrees, onChange }) {
  const dialRef = useRef(null);
  const turnRef = useRef(null);       // { a0, d0 } while the dial is held
  const frameRef = useRef(null);
  const pendingRef = useRef(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); }, []);

  const report = (deg) => {
    pendingRef.current = deg;
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      onChange(pendingRef.current);
    });
  };
  const angleAt = (e) => {
    const r = dialRef.current.getBoundingClientRect();
    return Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2)) * 180 / Math.PI;
  };
  const onDown = (e) => {
    if (e.button > 0) return;
    // preventDefault (no text selection) also stops the degree box losing
    // focus, and a focused box shows its typing, not the dial's value — so
    // drop the draft and blur it here.
    e.preventDefault();
    setDraft(null);
    if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
    dialRef.current.setPointerCapture(e.pointerId);
    turnRef.current = { a0: angleAt(e), d0: degrees };
  };
  const onMoveDial = (e) => {
    const t = turnRef.current;
    if (!t) return;
    let d = t.d0 + angleAt(e) - t.a0;
    d = ((d + 180) % 360 + 360) % 360 - 180;       // keep it in [-180, 180)
    d = e.shiftKey ? Math.round(d / 15) * 15 : Math.round(d);
    if (Math.abs(d) <= 2) d = 0;
    report(d === -180 ? 180 : d);
  };
  const onUp = () => { turnRef.current = null; };

  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
      <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600 }}>Rotate</span>
      <div
        ref={dialRef}
        role="slider"
        aria-label="Rotation"
        aria-valuemin={-180}
        aria-valuemax={180}
        aria-valuenow={degrees}
        title="Drag to rotate (Shift: 15° steps)"
        onPointerDown={onDown}
        onPointerMove={onMoveDial}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        style={{
          position:'relative', width:30, height:30, flexShrink:0, borderRadius:'50%',
          border:'1px solid rgba(255,255,255,0.2)', background:'rgba(255,255,255,0.04)',
          cursor:'grab', touchAction:'none', userSelect:'none',
        }}
      >
        {/* The text's baseline, turned by the angle; the dot marks its end. */}
        <div style={{
          position:'absolute', left:4, right:4, top:14, height:2, borderRadius:1,
          background:'#31DCBA', transform:`rotate(${degrees}deg)`,
        }}>
          <div style={{ position:'absolute', right:-3, top:-2, width:6, height:6, borderRadius:'50%', background:'#31DCBA' }} />
        </div>
      </div>
      <input
        type="number"
        step="1"
        min="-180"
        max="180"
        aria-label="Rotation in degrees"
        value={draft ?? String(degrees)}
        onChange={e => {
          setDraft(e.target.value);
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(Math.round(Math.min(Math.max(n, -180), 180) * 10) / 10);
        }}
        onBlur={() => setDraft(null)}
        style={{
          width:58, boxSizing:'border-box', background:'rgba(255,255,255,0.05)',
          border:'1px solid rgba(255,255,255,0.1)', borderRadius:6,
          color:'var(--text-primary)', fontSize:12, padding:'4px 6px', fontFamily:'var(--font)',
        }}
      />
      <span style={{ fontSize:11, color:'var(--text-muted)', marginLeft:-4 }}>°</span>
      {degrees !== 0 && (
        <button type="button" onClick={() => { setDraft(null); onChange(0); }}
          style={{
            marginLeft:'auto', padding:'3px 8px', fontSize:10, fontWeight:600, borderRadius:4,
            border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.04)',
            color:'var(--text-muted)', cursor:'pointer',
          }}
        >Straighten</button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TextOverlayControls — the text-overlay controls used by every Carousel
// slide, the Generate Post tab and the Scheduler's Edit: a list of text
// blocks (see overlayBlocksOf), each with its own full set of controls
// (TextBlockControls), "+ Add text" to add one and × to remove one.
//
// `value` is the block list (a plain object reads as one block).
// `onChange(update)` takes a function from the current list to the next —
// every change is a functional update, so two in a row (setAlign sets textAlign
// and x) merge cleanly. The list always keeps at least one block; emptying
// its text is "no overlay", as it always was.
//
// `selected`/`onSelect`: the block picked in the live preview (clicking a
// block on the image selects it) or by working in its controls here. With
// two or more blocks the selected one's controls are highlighted and it's
// outlined on the image, so it's clear which block is being edited.
// `imageUrl` is the image the blocks' dragged x/y are fractions of.
// ─────────────────────────────────────────────────────────────
function TextOverlayControls({ value, onChange, placeholder, imageUrl, selected = 0, onSelect }) {
  const stored = overlayBlocksOf(value);
  const blocks = stored.length ? stored : [DEFAULT_TEXT_OVERLAY];
  const sel = Math.min(Math.max(selected, 0), blocks.length - 1);
  const multi = blocks.length > 1;
  const select = (i) => { if (onSelect && i !== selected) onSelect(i); };
  const update = (fn) => onChange(prev => {
    const list = overlayBlocksOf(prev);
    return fn(list.length ? list : [{ ...DEFAULT_TEXT_OVERLAY }]);
  });
  const fieldSetter = (i) => (field, v) => update(list => list.map((b, j) => (j === i ? { ...b, [field]: v } : b)));
  const addBlock = () => {
    update(list => [...list, { ...NEW_TEXT_BLOCK }]);
    if (onSelect) onSelect(blocks.length);
  };
  const removeBlock = (i) => {
    update(list => (list.length > 1 ? list.filter((_, j) => j !== i) : list));
    // The selection stays on the same block; removing the selected one moves
    // it to the block that takes its place (or the new last one).
    if (onSelect) onSelect(i < sel ? sel - 1 : Math.min(sel, blocks.length - 2));
  };
  return (
    <div>
      {blocks.map((block, i) => {
        const controls = (
          <TextBlockControls
            value={block}
            onChange={fieldSetter(i)}
            placeholder={i === 0 ? placeholder : 'More text to show on the image'}
            imageUrl={imageUrl}
          />
        );
        if (!multi) return <React.Fragment key={i}>{controls}</React.Fragment>;
        const on = i === sel;
        return (
          <div
            key={i}
            role="group"
            aria-label={`Text ${i + 1}`}
            onPointerDown={() => select(i)}
            onFocus={() => select(i)}
            style={{
              marginBottom:6, padding:'6px 8px 8px', borderRadius:8,
              border: `1px solid ${on ? 'rgba(49,220,186,0.6)' : 'rgba(255,255,255,0.08)'}`,
              background: on ? 'rgba(49,220,186,0.05)' : 'transparent',
            }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5 }}>
              <span style={{ fontSize:10, fontWeight:700, color: on ? '#31DCBA' : 'var(--text-muted)' }}>Text {i + 1}</span>
              {on && (
                <span style={{ fontSize:9, fontWeight:700, padding:'1px 6px', borderRadius:8, background:'rgba(49,220,186,0.15)', color:'#31DCBA' }}>Selected</span>
              )}
              <button
                type="button"
                // Not a selection: no pointerdown to the panel, and no focus
                // (which the panel would also take as one).
                onPointerDown={e => e.stopPropagation()}
                onMouseDown={e => e.preventDefault()}
                onClick={() => removeBlock(i)}
                aria-label={`Remove text ${i + 1}`}
                title={`Remove text ${i + 1}`}
                style={{
                  marginLeft:'auto', width:20, height:20, padding:0, lineHeight:'18px', fontSize:12,
                  borderRadius:4, border:'1px solid rgba(255,255,255,0.12)', background:'transparent',
                  color:'var(--text-muted)', cursor:'pointer',
                }}
              >×</button>
            </div>
            {controls}
          </div>
        );
      })}
      <button
        type="button"
        onClick={addBlock}
        title="Add another text — its own font, style, position and rotation"
        style={{
          width:'100%', marginTop: multi ? 0 : 8, padding:'5px 0', fontSize:11, fontWeight:600,
          borderRadius:6, border:'1px dashed rgba(49,220,186,0.45)', background:'transparent',
          color:'#31DCBA', cursor:'pointer', fontFamily:'var(--font)',
        }}
      >+ Add text</button>
      <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:4, lineHeight:1.4 }}>
        Wrap a word or phrase in *asterisks* to show it in the accent color.
        Drag the text on the image to place it anywhere; Top/Middle/Bottom
        snap it back. Drag a corner handle to resize the text, or a side
        handle to change how wide it wraps. Rotation turns it about its
        centre, clockwise for positive degrees.
        {multi && ' Each text moves and styles on its own — click one on the image to select it.'}
      </div>
    </div>
  );
}

// One text block's controls (text, font, position, size, alignment, style,
// colour, accent colour, rotation). `value` uses the carousel slide's field
// names so the same compositeTextOntoImage call renders any of them;
// `onChange(field, v)` sets one field. `imageUrl` is the image the block's
// dragged x/y are fractions of (see setAlign).
export function TextBlockControls({ value, onChange, placeholder, imageUrl }) {
  const optBtn = (active, extra) => ({
    flex:1, padding:'4px 0', fontSize:10, fontWeight:600,
    borderRadius:4, border:'1px solid rgba(255,255,255,0.15)',
    background: active ? 'rgba(49,220,186,0.2)' : 'rgba(255,255,255,0.04)',
    color:      active ? '#31DCBA' : 'var(--text-muted)',
    cursor:'pointer',
    ...extra,
  });
  const color  = value.textColor   || '#ffffff';
  const accent = value.accentColor || DEFAULT_ACCENT_COLOR;
  // Dragged on the image (see overlayAnchor). The field setters are all
  // functional updates, so setting two fields in a row merges cleanly.
  const dragged = !!overlayAnchor(value);
  const clearDrag = () => { if (dragged) { onChange('x', undefined); onChange('y', undefined); } };
  // Alignment on dragged text keeps the text where it is. The stored x is the
  // lines' alignment edge, so it moves by the gap between the old and new
  // edges of the widest line, measured on the image the x is a fraction of.
  // (It used to clear x/y, and the text jumped back to its preset.) Without
  // an image to measure, the x stays and the text shifts — but isn't lost.
  const setAlign = async (val) => {
    const from = value.textAlign || 'center';
    if (!dragged || val === from || !imageUrl) { onChange('textAlign', val); return; }
    try {
      const img = await loadImage(imageUrl);
      await loadOverlayFont(overlayFontOf(value), value.text);
      const W = img.naturalWidth;
      const { widest } = measureOverlayBlock(value, W, img.naturalHeight);
      const edge = (a) => (a === 'left' ? 0 : a === 'right' ? widest : widest / 2);
      // Not clamped to 0–1: text already hanging off the image can put its new
      // edge off it too, and clamping would make it jump.
      const x = value.x + (edge(val) - edge(from)) / W;
      onChange('textAlign', val);
      onChange('x', Math.round(x * 10000) / 10000);
    } catch (err) {
      console.warn('[TextOverlay] couldn\'t measure for realignment:', err?.message || err);
      onChange('textAlign', val);
    }
  };
  // 0 is stored as no rotation at all, like every overlay saved before it.
  const setRotation = (deg) => onChange('rotation', deg ? deg : undefined);
  // Set by the resize handles on the image (see utils/overlayGeometry).
  const customSize  = customTextScale(value) !== null;
  const customWidth = customTextWidth(value) !== null;
  const swatch = {
    width:28, height:24, padding:0, border:'1px solid rgba(255,255,255,0.15)',
    borderRadius:4, background:'transparent', cursor:'pointer',
  };
  return (
    <div>
      {/* A textarea so line breaks can be typed; the overlay keeps them. */}
      <textarea
        rows={2}
        placeholder={placeholder}
        value={value.text || ''}
        onChange={e => onChange('text', e.target.value)}
        style={{
          width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.05)',
          border:'1px solid rgba(255,255,255,0.1)', borderRadius:6,
          color:'var(--text-primary)', fontSize:12, padding:'7px 9px',
          fontFamily:'var(--font)', lineHeight:1.4, resize:'vertical', display:'block',
        }}
      />
      {/* Font — a rendered "autopilot" per option, not a text label. */}
      <FontPicker value={value.textFont || 'default'} onChange={id => onChange('textFont', id)} />
      {/* Text position. A dragged position (x/y) overrides these; pressing
          one snaps the text back to that preset. */}
      <div style={{ display:'flex', gap:4, marginTop:6 }}>
        {['top','middle','bottom'].map(pos => (
          <button key={pos} type="button" onClick={() => { onChange('textPosition', pos); clearDrag(); }}
            style={optBtn(!dragged && (value.textPosition || 'bottom') === pos, { textTransform:'capitalize' })}
          >{pos}</button>
        ))}
      </div>
      {/* Text size. Quick presets; a corner handle on the image sets a
          custom size (textScale), and then none of them is lit. Pressing one
          drops the custom size but keeps a custom width. */}
      <div style={{ display:'flex', gap:4, marginTop:4 }}>
        {[['s','S'],['m','M'],['l','L']].map(([val,label]) => (
          <button key={val} type="button"
            onClick={() => { onChange('textSize', val); if (value.textScale !== undefined) onChange('textScale', undefined); }}
            aria-pressed={customSize ? false : (value.textSize || 'm') === val}
            style={optBtn(!customSize && (value.textSize || 'm') === val)}
          >{label}</button>
        ))}
      </div>
      {(customSize || customWidth) && (
        <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:3, fontSize:10, color:'var(--text-muted)' }}>
          <span>{customSize && customWidth ? 'Custom size · custom width' : customSize ? 'Custom size' : 'Custom width'}</span>
          {customWidth && (
            <button type="button" onClick={() => onChange('textWidth', undefined)}
              style={{
                marginLeft:'auto', padding:'2px 7px', fontSize:10, fontWeight:600, borderRadius:4,
                border:'1px solid rgba(255,255,255,0.15)', background:'rgba(255,255,255,0.04)',
                color:'var(--text-muted)', cursor:'pointer',
              }}
            >Auto width</button>
          )}
        </div>
      )}
      {/* Text alignment */}
      <div style={{ display:'flex', gap:4, marginTop:4 }}>
        {[['left','⟵'],['center','≡'],['right','⟶']].map(([val,label]) => (
          <button key={val} type="button" onClick={() => setAlign(val)} title={`Align ${val}`}
            style={optBtn((value.textAlign || 'center') === val, { fontSize:11, fontWeight:700 })}
          >{label}</button>
        ))}
      </div>
      {/* Text style */}
      <div style={{ display:'flex', gap:4, marginTop:4 }}>
        {[['solid','Solid'],['backed','Backed'],['outline','Outline']].map(([val,label]) => (
          <button key={val} type="button" onClick={() => onChange('textStyle', val)}
            style={optBtn((value.textStyle || 'solid') === val)}
          >{label}</button>
        ))}
      </div>
      <RotationControl degrees={overlayRotation(value)} onChange={setRotation} />
      {/* Text color + accent color */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
        <input
          type="color"
          value={color}
          onChange={e => onChange('textColor', e.target.value)}
          style={swatch}
        />
        <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600, flex:1 }}>
          Color · {color.toUpperCase()}
        </span>
        <input
          type="color"
          value={accent}
          onChange={e => onChange('accentColor', e.target.value)}
          title="Accent color"
          style={swatch}
        />
        <span style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600, flex:1 }}>
          Accent · {accent.toUpperCase()}
        </span>
      </div>
    </div>
  );
}

// Carousel-slide field names and defaults; empty text means no overlay.
const DEFAULT_TEXT_OVERLAY = {
  text: '', textPosition: 'bottom', textSize: 'm', textAlign: 'center', textStyle: 'solid', textColor: '#ffffff',
  textFont: 'default', accentColor: DEFAULT_ACCENT_COLOR,
};

// Every field a text block has — a Carousel slide carries its first block's
// flat on the slide.
const SLIDE_TEXT_FIELDS = [
  'text', 'textPosition', 'textSize', 'textAlign', 'textStyle', 'textColor',
  'textFont', 'accentColor', 'x', 'y', 'rotation', 'textScale', 'textWidth',
];

// A block added with "+": centred on the image, unrotated, default font.
const NEW_TEXT_BLOCK = { ...DEFAULT_TEXT_OVERLAY, textPosition: 'middle' };

// Identity of an overlay setting, for "is what's baked into this image what the
// composer says now?". null (no text) keys to ''. Fields added later are keyed
// by their default when absent, so an overlay saved before they existed still
// matches the same settings reopened in the composer. A list keys block by
// block, so a one-block list keys exactly as that block always did as a plain
// object — an old post reopened and saved unchanged isn't re-baked.
export function textOverlayKey(v) {
  return overlayBlocksOf(v).map(textBlockKey).join('\u0002');
}
function textBlockKey(o) {
  const anchor = overlayAnchor(o);
  return [o.text, o.textPosition, o.textSize, o.textAlign, o.textStyle, o.textColor,
    o.textFont || 'default',
    // The accent colour only shows when the text has an accent run.
    parseAccent(o.text || '').accent ? (o.accentColor || DEFAULT_ACCENT_COLOR) : '',
    // A dragged position; '' for a preset one, as every older overlay is.
    anchor ? `${anchor.x},${anchor.y}` : '',
    // Rotation; '' for none, as every older overlay has.
    overlayRotation(o) ? String(overlayRotation(o)) : ''].join('')
    // A handle-set size/width. Appended only when there is one, so every
    // older block keys exactly as it always did.
    + (customTextScale(o) !== null || customTextWidth(o) !== null
      ? `${customTextScale(o) ?? ''}${customTextWidth(o) ?? ''}` : '');
}

// ─────────────────────────────────────────────────────────────
// TextOverlayPreviewImage — an <img> with a live CSS preview of what
// compositeTextOntoImage will bake onto it, used by Carousel, Generate Post
// and the Scheduler's Edit. The text is laid out at the image's natural size
// with the canvas's measurements and scaled to the rendered size — cards vary
// in width, and the point of the preview is that the text lands where it
// shows. Handles object-fit: contain (story frames) by placing the
// overlay on the letterboxed image area rather than the element box.
//
// `overlay` is a list of text blocks (a plain object reads as one; see
// overlayBlocksOf). Each block is its own layer, in array order — the last
// on top, as the canvas draws them — so a press lands on the topmost block
// under the pointer (the browser's own hit-testing, rotation included), and
// only that block moves.
//
// With `onMove(i, x, y)` a block can be dragged anywhere on the image: it
// follows the pointer here, locally, and only the drop point is reported
// (normalized, see overlayAnchor) — the panels holding the overlay are big,
// and re-rendering them at pointer rate would make the drag lag. A press
// that doesn't really move (≤3px) reports nothing, so a stray click on a
// preset-positioned overlay doesn't quietly turn it into a dragged one.
// Pressing a block also reports `onSelect(i)`. With two or more blocks the
// `selected` one is outlined — a preview-only mark, never baked.
//
// `bakedSrc` (Generate Post, a card whose text is already applied): shows
// that stored image, not the live preview, until the text is dragged — the
// text layer is still there, invisible, so the baked text can be grabbed.
// Once the drag moves, it switches to `src` (the image without text) plus
// the live text, which lands pixel-identical to the baked copy.
//
// A rotated block turns about the centre of the box its text covers
// (overlayPlacement), measured with the canvas's own wrap and widths — CSS
// can't shrink a wrapped block to its widest line to find that centre.
//
// Line breaks are the canvas's too: each block's lines come from
// wrapCanvasText and are drawn one per line, unwrapped, so a custom width
// can't break a line in the preview a sub-pixel away from where the bake does.
//
// With `onResize(i, fields)` the selected block shows resize handles (preview
// only, never baked): corners scale its font size, the left/right sides set its
// wrap width (utils/overlayGeometry has the math, rotation included). Like a
// drag, it follows the pointer locally and only the release is reported —
// the fields to merge into block i (textScale/textWidth and its new x/y).
// ─────────────────────────────────────────────────────────────
export function TextOverlayPreviewImage({ src, alt, style, overlay, onMove, onResize, bakedSrc, selected = 0, onSelect }) {
  const imgRef = useRef(null);
  const [box, setBox] = useState(null);
  const [drag, setDragState] = useState(null);
  // A handle drag: { i, handle, sx, sy, block, anchor, moved, patch }.
  const [resize, setResizeState] = useState(null);
  const resizeRef = useRef(null);
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const setResize = (r) => { resizeRef.current = r; setResizeState(r); };
  const resizing = !!resize;
  const blocks = trimOverlayBlocks(overlay);
  // Bumped once the blocks' fonts have loaded, so the canvas measurements
  // below (rotation pivot, drag start) are redone with the real fonts — CSS
  // swaps them in on its own, a measurement taken before they landed doesn't.
  const [, setFontTick] = useState(0);
  const fontIds = blocks.map(b => b.textFont || 'default').join(',');
  useEffect(() => {
    let live = true;
    Promise.all([...new Set(fontIds.split(','))].map(id => loadOverlayFont(overlayFontOf({ textFont: id }))))
      .then(() => { if (live) setFontTick(t => t + 1); });
    return () => { live = false; };
  }, [fontIds]);
  // Mirrors of state/props for the window-level pointer handlers below,
  // which are attached once per drag and would otherwise see stale values.
  const dragRef = useRef(null);
  const boxRef = useRef(null);
  const onMoveRef = useRef(onMove);
  boxRef.current = box;
  onMoveRef.current = onMove;
  const setDrag = (d) => { dragRef.current = d; setDragState(d); };
  const dragging = !!drag;

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (e) => {
      const d = dragRef.current, b = boxRef.current;
      if (!d || !b) return;
      const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
      setDrag({
        ...d,
        moved: d.moved || Math.abs(dx) > 3 || Math.abs(dy) > 3,
        // Anchor stays on the image (the text itself may hang off an edge) —
        // or where it started, if a realignment left it off (see setAlign),
        // so the first move doesn't snap it back.
        ax: Math.min(Math.max(d.ax0 + dx / b.scale, Math.min(0, d.ax0)), Math.max(b.nw, d.ax0)),
        ay: Math.min(Math.max(d.ay0 + dy / b.scale, Math.min(0, d.ay0)), Math.max(b.nh, d.ay0)),
      });
    };
    const up = () => {
      const d = dragRef.current, b = boxRef.current;
      setDrag(null);
      if (d && d.moved) {
        // The browser follows a drag with a click on whatever the press and
        // release have in common — released outside the Scheduler's Edit
        // modal, that's its backdrop, which closes the modal and drops the
        // edit. Swallow that one click; it fires before the timeout runs.
        const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', swallow, true);
        setTimeout(() => window.removeEventListener('click', swallow, true), 0);
      }
      if (d && d.moved && b && onMoveRef.current) {
        const round = (v) => Math.round(v * 10000) / 10000;
        onMoveRef.current(d.i, round(d.ax / b.nw), round(d.ay / b.nh));
      }
    };
    const cancel = () => setDrag(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [dragging]);

  // The handle drag, the same way: window listeners for its lifetime, the
  // live result kept here, only the release reported.
  useEffect(() => {
    if (!resizing) return undefined;
    const move = (e) => {
      const r = resizeRef.current, b = boxRef.current;
      if (!r || !b) return;
      const dx = e.clientX - r.sx, dy = e.clientY - r.sy;
      const { patch } = resizeOverlayBlock({
        block: r.block, handle: r.handle, anchor: r.anchor,
        local: screenDeltaToLocal(dx, dy, b.scale, overlayRotation(r.block)),
        W: b.nw, H: b.nh,
        measure: (bl) => measureOverlayBlock(bl, b.nw, b.nh),
      });
      setResize({ ...r, moved: r.moved || Math.abs(dx) > 2 || Math.abs(dy) > 2, patch });
    };
    const up = () => {
      const r = resizeRef.current;
      setResize(null);
      if (r && r.moved) {
        // As for a drag: swallow the click that follows a release outside
        // the Scheduler's Edit modal, which would close it.
        const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', swallow, true);
        setTimeout(() => window.removeEventListener('click', swallow, true), 0);
        if (r.patch && onResizeRef.current) onResizeRef.current(r.i, r.patch);
      }
    };
    const cancel = () => setResize(null);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
    };
  }, [resizing]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;
    const measure = () => {
      const nw = img.naturalWidth, nh = img.naturalHeight;
      const cw = img.clientWidth,  ch = img.clientHeight;
      if (!nw || !nh || !cw || !ch) return;
      const scale = Math.min(cw / nw, ch / nh);
      setBox({ left: (cw - nw * scale) / 2, top: (ch - nh * scale) / 2, nw, nh, scale });
    };
    measure();
    img.addEventListener('load', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(img);
    return () => { img.removeEventListener('load', measure); if (ro) ro.disconnect(); };
  }, [src]);

  // Showing the stored baked image instead of the live text (see bakedSrc).
  const showBaked = !!bakedSrc && !(drag && drag.moved) && !(resize && resize.moved);
  // Which block is selected only matters once there's more than one — or
  // when it can be resized, since its handles show which one that is.
  const sel = Math.min(Math.max(selected, 0), blocks.length - 1);
  const markSelected = blocks.length > 1 || !!onResize;

  let text = null;
  if (box && blocks.some(b => b.text)) {
    // Laid out at the image's NATURAL size with compositeTextOntoImage's own
    // numbers, then scaled down as one layer. Laying it out at card size
    // instead wraps differently: macOS switches to a wider optical face at
    // small font sizes, so the line breaks drift from the bake.
    const { nw, nh, scale } = box;

    // One block: its layer (`el`), and its selection outline if it's the
    // selected one.
    const renderBlock = (block, i) => {
      // Mid-drag, the dragged block renders at the pointer's position, not
      // the stored one. Every other block stays exactly where it is.
      // Mid-resize, likewise, with the size/width/position the handle gives.
      const eff = drag && drag.moved && drag.i === i ? { ...block, x: drag.ax / nw, y: drag.ay / nh }
                : resize && resize.patch && resize.i === i ? { ...block, ...resize.patch }
                : block;
      const { fontSize: f, maxWidth: maxW, pad, pillPadX: padX, pillPadY: padY } =
        overlayMetrics(eff, nw, nh);
      const font  = overlayFontOf(eff);
      registerOverlayFont(font);
      const color = eff.textColor || '#ffffff';
      const look  = eff.textStyle || 'solid';
      const align = eff.textAlign || 'center';
      const anchor = overlayAnchor(eff);
      const rotation = overlayRotation(eff);
      // The block as the canvas lays it out: where its lines anchor and the
      // centre of the box they cover (the rotation pivot).
      const measured = measureOverlayBlock(eff, nw, nh);
      const place = overlayPlacement(eff, nw, nh, measured);
      const turn = rotation
        ? { transform: `rotate(${rotation}deg)`, transformOrigin: `${place.cx}px ${place.cy}px` }
        : null;
      // The canvas centres each line's baseline in its line gap the way this
      // 1.2 line-height box does, so the block edges line up with no offset.
      const pos = eff.textPosition || 'bottom';
      const posStyle =
        anchor           ? { top: anchor.y * nh, transform: 'translateY(-50%)' } :
        pos === 'top'    ? { top: pad } :
        pos === 'middle' ? { top: nh / 2, transform: 'translateY(-50%)' } :
                           { top: nh - pad, transform: 'translateY(-100%)' };
      // The canvas hangs the pill padding outside its lines; CSS puts the
      // (cloned) padding inside each line, so widen the box by it.
      const hang = look === 'backed' ? padX : 0;
      // The lines are the canvas's (below), so the box only has to hold them:
      // the wrap width, or more if one word is wider than that (it overflows
      // the same way on the canvas, still aligned to the anchor).
      const boxW = measured.widest > maxW ? Math.ceil(measured.widest) + f : maxW;
      // The box's left edge: wherever puts the lines' alignment edge on
      // place.ax, as the canvas's lineLeft does — the side margin for a
      // preset position at the automatic width, as it always was.
      const boxLeft = place.ax - alignOffset(align, boxW) - hang;
      // Where the drag starts from, in natural px: the stored anchor, or — for
      // a preset position — the anchor that same spot would have (its lines'
      // alignment x, its vertical centre), so the text doesn't jump when a
      // preset overlay is first dragged. Rotation doesn't enter into it: the
      // anchor lives in the unrotated frame and the pivot moves with it.
      const startDrag = (e) => {
        if (!onMove || e.button > 0) return;
        e.preventDefault();
        e.stopPropagation();
        if (onSelect) onSelect(i);
        setDrag({ i, sx: e.clientX, sy: e.clientY, ax0: place.ax, ay0: place.cy, ax: place.ax, ay: place.cy, moved: false });
      };
      // Only the text itself is grabbable — its line spans, pill padding
      // included — not the whole wrap-width box around it.
      const grab = onMove ? {
        onPointerDown: startDrag,
        style: { pointerEvents: 'auto', cursor: drag ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none' },
      } : null;
      // Each line's box. The backed pill's padding is layout, so both layers
      // below carry it; only the pill layer paints the background.
      const pillBox = look === 'backed' ? {
        padding: `${padY}px ${padX}px`,
        borderRadius: f * 0.18,
        WebkitBoxDecorationBreak: 'clone',
        boxDecorationBreak: 'clone',
      } : {};
      const strokeFor = (c) => (look === 'outline' ? {
        WebkitTextStroke: `${Math.max(4, f * 0.16)}px ${getContrastColor(c)}`,
        paintOrder: 'stroke fill',
      } : {});
      // compositeTextOntoImage's accent parse, and its lines split into runs
      // of plain and accent text exactly as drawRuns paints them.
      const { accent } = parseAccent(block.text);
      const accentColor = block.accentColor || DEFAULT_ACCENT_COLOR;
      const lineRunsList = measured.lines.map(words => lineRuns(words, accent));
      // One copy of the text block. The canvas draws every pill before any
      // text, so a pill never covers the line above it (tall fonts overlap);
      // CSS would paint each line's pill over the previous line's text. So a
      // backed overlay is two identical layers: pills (text invisible), then
      // text (no background).
      const layer = (pills) => (
        <div aria-hidden={pills || undefined} style={{
          position: 'absolute', left: boxLeft, width: boxW + hang * 2,
          textAlign: align, color: pills ? 'transparent' : color,
          fontFamily: font.family,
          fontWeight: font.weight, fontSize: f, lineHeight: 1.2,
          textShadow: look === 'solid' ? `0 ${Math.round(f * 0.06)}px ${f * 0.4}px rgba(0,0,0,0.85)` : 'none',
          whiteSpace: 'pre', overflowWrap: 'normal', wordBreak: 'normal',
          ...posStyle,
        }}>
          {/* One span per line, broken where the canvas breaks it: a blank
              line is just the <br>, so it gets no pill (as on the canvas).
              Accent runs are spans inside. */}
          {lineRunsList.map((runs, li) => (
            <React.Fragment key={li}>
              {li > 0 && <br />}
              {runs.length > 0 && (
                <span
                  onPointerDown={!pills && grab ? grab.onPointerDown : undefined}
                  style={{
                    ...pillBox,
                    ...(pills ? { background: 'rgba(0,0,0,0.55)' } : strokeFor(color)),
                    ...(!pills && grab ? grab.style : null),
                  }}
                >
                  {runs.map((r, ri) => (r.accent && !pills
                    ? <span key={ri} style={{ color: accentColor, ...strokeFor(accentColor) }}>{r.text}</span>
                    : <React.Fragment key={ri}>{r.text}</React.Fragment>))}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      );
      // The block's own layer: everything it paints sits above every block
      // before it and below every block after it, as on the canvas. Rotated,
      // CSS text-shadow turns with it, as the canvas's hand-turned shadow
      // offset does. Invisible (but still grabbable) while the baked image
      // is shown.
      const el = (
        <div key={i} style={{
          position: 'absolute', left: 0, top: 0, width: nw, height: nh,
          opacity: showBaked ? 0 : 1, ...turn,
        }}>
          {look === 'backed' && layer(true)}
          {layer(false)}
        </div>
      );
      // The selection outline: the box the text covers (at a custom width,
      // the wrap width — see blockFrame), plus the pill's padding and a few
      // screen px, turned with the block. Drawn over all the blocks; the
      // outline itself never takes the pointer, so it can't change which
      // block a press lands on — only its handles do.
      let outline = null;
      if (markSelected && i === sel) {
        const m = (look === 'backed' ? Math.max(padX, padY) : 0) + 5 / scale;
        const frame = blockFrame(eff, measured, maxW);
        const ox = place.ax + frame.left - m, oy = place.cy + frame.top - m;
        const ow = frame.right - frame.left + m * 2, oh = frame.bottom - frame.top + m * 2;
        // Handles: a dot of a constant screen size on a bigger, invisible
        // touch target. Hidden while the block is being moved.
        const startResize = (handle) => (e) => {
          if (e.button > 0) return;
          e.preventDefault();
          e.stopPropagation();
          if (onSelect) onSelect(i);
          setResize({ i, handle, sx: e.clientX, sy: e.clientY, block: eff, anchor: { x: place.ax, y: place.cy }, moved: false, patch: null });
        };
        const hit = 28 / scale, dot = 10 / scale;
        const handles = onResize && !(drag && drag.moved) ? RESIZE_HANDLES.map(h => {
          const p = handlePoint({ left: 0, right: ow, top: 0, bottom: oh }, h);
          return (
            <div key={h} data-resize-handle={h}
              aria-label={h.length === 2 ? 'Resize text' : 'Change text width'}
              title={h.length === 2 ? 'Drag to resize the text' : 'Drag to change the text width'}
              onPointerDown={startResize(h)}
              style={{
                position: 'absolute', left: p.x - hit / 2, top: (h.length === 2 ? p.y : oh / 2) - hit / 2,
                width: hit, height: hit, pointerEvents: 'auto', touchAction: 'none',
                cursor: handleCursor(h, rotation), display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
              <div style={{
                width: h.length === 2 ? dot : dot * 0.6, height: h.length === 2 ? dot : dot * 1.6,
                borderRadius: dot / 2, background: '#fff', border: `${1.5 / scale}px solid #31DCBA`,
                boxShadow: `0 0 0 ${1 / scale}px rgba(0,0,0,0.35)`, boxSizing: 'border-box',
              }} />
            </div>
          );
        }) : null;
        outline = (
          <div key="selection" data-overlay-selection="" style={{
            position: 'absolute', left: 0, top: 0, width: nw, height: nh, pointerEvents: 'none', ...turn,
          }}>
            <div style={{
              position: 'absolute', left: ox, top: oy, width: ow, height: oh,
            }}>
              <div style={{
                position: 'absolute', inset: 0, boxSizing: 'border-box',
                border: `${1.5 / scale}px dashed #31DCBA`, borderRadius: 4 / scale,
                boxShadow: `0 0 0 ${1 / scale}px rgba(0,0,0,0.35)`,
              }} />
              {handles}
            </div>
          </div>
        );
      }
      return { el, outline };
    };

    const rendered = blocks.map((b, i) => (b.text ? renderBlock(b, i) : null)).filter(Boolean);
    text = (
      <div style={{
        position: 'absolute', left: box.left, top: box.top, width: nw, height: nh,
        transform: `scale(${scale})`, transformOrigin: '0 0',
        // Mid-drag (or mid-resize) the whole image area takes the pointer, so
        // the cursor stays put even when it outruns the text.
        pointerEvents: drag || resize ? 'auto' : 'none',
        cursor: drag ? 'grabbing' : resize ? handleCursor(resize.handle, overlayRotation(resize.block)) : undefined,
      }}>
        {rendered.map(r => r.el)}
        {rendered.map(r => r.outline)}
      </div>
    );
  }

  return (
    <>
      <img ref={imgRef} src={showBaked ? bakedSrc : src} alt={alt} style={style} />
      {text}
    </>
  );
}

// ============================================================
// TIKTOK SETTINGS PANEL — shown in the Post Generator when TikTok is a
// selected platform. Controlled component: `value` holds the tiktok_settings
// object lifted into ImageGenerationPanel; `onChange(partial)` merges updates.
// Fetches creator_info on mount to drive the privacy selector + interaction
// locks (TikTok audit UX requires showing the creator + honouring their
// per-account duet/stitch/comment availability).
// ============================================================
const TT_PRIVACY_OPTIONS = [
  { value: 'PUBLIC_TO_EVERYONE',     label: 'Public' },
  { value: 'MUTUAL_FOLLOW_FRIENDS',  label: 'Friends Only' },
  { value: 'SELF_ONLY',              label: 'Private' },
];

export function TtToggle({ on, disabled, onClick, label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, opacity: disabled ? 0.5 : 1 }}>
      <div
        onClick={disabled ? undefined : onClick}
        style={{ width:36, height:20, borderRadius:10, background: on ? '#31DCBA' : 'rgba(255,255,255,0.1)', cursor: disabled ? 'not-allowed' : 'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}
      >
        <div style={{ position:'absolute', top:3, left: on ? 18 : 3, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s' }} />
      </div>
      <span style={{ fontSize:13, color: on ? '#31DCBA' : 'var(--text-muted)', transition:'color 0.2s' }}>{label}</span>
    </div>
  );
}

// isVideo drives which interaction toggles apply. TikTok's guidelines exclude
// Duet and Stitch from photo posts, and publishToTikTok (the photo path) has
// always correctly sent neither — so on the photo tabs those two switches were
// controls that silently did nothing. Comments apply to both.
export function TikTokSettingsPanel({ clientId, value, onChange, previewImageUrl, isVideo = false }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
  const { viewingAsClientId } = useViewingAs();
  const [loading, setLoading]       = useState(true);
  const [info, setInfo]             = useState(null);   // creator_info payload
  const [loadError, setLoadError]   = useState(null);   // { code, message }
  const [showDisclosure, setShowDisclosure] = useState(false);

  // Fetch creator info once when the panel mounts (TikTok was just checked).
  //
  // PHASE 7 CARRYOVER, found while closing the Post Generator's. This panel has
  // no direct-Supabase call sites so it never appeared in the migration's
  // count, but ImageGenerationPanel renders it — and it sent `client_id` in the
  // body, which requireOwnerOrAdmin reads BEFORE ?client=. So the composer
  // showed the ADMIN's TikTok account, and its privacy options and disclosure
  // requirements, while an admin was viewing a client. The settings the
  // composer then wrote onto the post were chosen against the wrong account.
  //
  // The dep array gains viewingAsClientId for the usual reason: clientId is the
  // signed-in user and does not change when the viewed client does.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    clientScopedFetch(`${API_BASE}/api/tiktok/creator-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok || !data.ok) {
          setLoadError({ code: data.code || (r.status === 404 ? 'NO_TIKTOK_CONNECTION' : 'ERROR'), message: data.error || 'Could not load TikTok account.' });
          setLoading(false);
          return;
        }
        setInfo(data);
        // Privacy is intentionally left unset — the creator must pick a privacy
        // status manually (TikTok Direct Post audit requirement).
        // Honour per-account interaction availability (lock unavailable ones off).
        const locks = {};
        if (isVideo && data.duet_disabled)   locks.disable_duet = true;
        if (isVideo && data.stitch_disabled) locks.disable_stitch = true;
        if (data.comment_disabled)           locks.disable_comment = true;
        // audit_mode is the backend's TIKTOK_AUDIT_MODE. It is false since the
        // Direct Post audit was approved (2026-09-23); if it is ever true again
        // every post is forced private, and TikTok forbids private branded
        // content — so the branded toggle is unavailable. Clear anything a draft
        // carried in, otherwise the post fails the backend guard at publish.
        if (data.audit_mode && value.branded_content) locks.branded_content = false;
        if (Object.keys(locks).length) onChange(locks);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError({ code: 'NETWORK', message: 'Network error loading TikTok account.' });
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, viewingAsClientId]);

  const card = { background:'var(--bg-card2)', border:'1px solid var(--accent-border)', borderRadius:10, padding:14, marginBottom:16 };
  const sectionLabel = { fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 };

  if (loading) {
    return (
      <div style={card}>
        <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:12, color:'var(--text-muted)' }}>
          <div className="ig-spinner" /> Loading TikTok account…
        </div>
      </div>
    );
  }

  if (loadError) {
    const needsConnect = loadError.code === 'NO_TIKTOK_CONNECTION';
    return (
      <div style={{ ...card, borderColor:'rgba(245,166,35,0.3)' }}>
        <div style={{ fontSize:12, color:'#f5a623', lineHeight:1.6 }}>
          {needsConnect
            ? 'Connect your TikTok account in Connections first.'
            : `TikTok: ${loadError.message}`}
        </div>
      </div>
    );
  }

  const allowed = info?.privacy_level_options || [];
  const brandedOn = !!value.branded_content;
  // TIKTOK_AUDIT_MODE, surfaced by /api/tiktok/creator-info rather than
  // duplicated as a constant here. Off in production since the Direct Post
  // audit was approved; the panel follows the backend on every fetch.
  const auditMode = !!info?.audit_mode;

  // Branded content cannot be Private (TikTok rule). The option renders
  // disabled; this refuses it too in case that ever regresses.
  function setPrivacy(v) {
    if (brandedOn && v === 'SELF_ONLY') return;
    onChange({ privacy_level: v });
  }
  // The declaration itself (persisted), the chrome (view-only), and the rule
  // the publish gates read. Kept next to toggleBranded because turning the
  // declaration off has to clear what it revealed.
  const disclosureOn = !!value.commercial_content;
  const disclosureExpanded = showDisclosure || disclosureOn;
  const disclosureIncomplete = ttDisclosureIncomplete(value);

  function toggleDisclosure() {
    const next = !disclosureOn;
    if (next) {
      onChange({ commercial_content: true });
      setShowDisclosure(true);
      return;
    }
    // Clear both sub-options on the way out. Leaving them set would keep
    // sending brand_organic / brand_content_toggle for a post that no longer
    // declares commercial content at all.
    onChange({ commercial_content: false, brand_organic: false, branded_content: false });
  }

  function toggleBranded() {
    // Belt and braces — the switch renders disabled, so this is only reachable
    // if that ever regresses.
    if (auditMode) return;
    const next = !brandedOn;
    const patch = { branded_content: next };
    // Never pick a privacy level for the creator (TikTok's guidelines). This
    // used to switch Private to Public; now a Private choice is cleared and
    // they choose again from the options branded content allows. Turning
    // branded off picks nothing either; Private just becomes selectable again.
    if (next && value.privacy_level === 'SELF_ONLY') patch.privacy_level = '';
    onChange(patch);
  }

  return (
    <div style={card}>
      {/* Creator info block (mandatory TikTok audit UX) */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        {info?.creator_avatar_url
          ? <img src={info.creator_avatar_url} alt="" style={{ width:36, height:36, borderRadius:'50%', objectFit:'cover' }} />
          : <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,0.12)' }} />}
        <div style={{ minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{info?.creator_nickname || 'TikTok Creator'}</div>
          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{info?.creator_username ? `@${info.creator_username}` : ''}</div>
        </div>
      </div>

      {/* TikTok caption fields (separate from IG/FB caption) */}
      <div style={sectionLabel}>TikTok Caption</div>
      <input
        type="text"
        maxLength={90}
        value={value.caption_title || ''}
        onChange={e => onChange({ caption_title: e.target.value.slice(0, 90) })}
        placeholder="TikTok Title (defaults to the generated caption)"
        aria-label="TikTok Title"
        style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', borderRadius:7, border:'1px solid var(--border)', background:'var(--bg-surface)', color:'var(--text)', fontSize:12, marginBottom:4 }}
      />
      <div style={{ fontSize:10, color:'var(--text-muted)', textAlign:'right', marginBottom:10 }}>{(value.caption_title || '').length}/90</div>
      <textarea
        maxLength={4000}
        rows={3}
        value={value.caption_description || ''}
        onChange={e => onChange({ caption_description: e.target.value.slice(0, 4000) })}
        placeholder="TikTok Description / Hashtags (defaults to the generated caption + hashtags)"
        aria-label="TikTok Description / Hashtags"
        style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', borderRadius:7, border:'1px solid var(--border)', background:'var(--bg-surface)', color:'var(--text)', fontSize:12, resize:'vertical' }}
      />
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text-muted)', marginTop:4, marginBottom:14 }}>
        <span>Hashtags go inline e.g. #smallbusiness #kelowna</span>
        <span>{(value.caption_description || '').length}/4000</span>
      </div>

      {/* Privacy selector */}
      <div style={sectionLabel}>Privacy</div>
      <select
        value={value.privacy_level || ''}
        onChange={e => setPrivacy(e.target.value)}
        style={{ width:'100%', boxSizing:'border-box', padding:'8px 10px', borderRadius:7, border:'1px solid var(--border)', background:'var(--bg-surface)', color:'var(--text)', fontSize:12, marginBottom:6 }}
      >
        <option value="" disabled>-- Select privacy status --</option>
        {TT_PRIVACY_OPTIONS.map(o => {
          const isAllowed = !allowed.length || allowed.includes(o.value);
          const brandedBlocked = brandedOn && o.value === 'SELF_ONLY';
          return (
            <option key={o.value} value={o.value} disabled={!isAllowed || brandedBlocked}>
              {o.label}{!isAllowed ? ' (not available)' : ''}{brandedBlocked && isAllowed ? " (branded content can't be private)" : ''}
            </option>
          );
        })}
      </select>
      <div style={{ fontSize:10, color:'var(--text-muted)', lineHeight:1.5, marginBottom:14 }}>
        Posts are published with the privacy level selected here.
        {brandedOn && allowed.includes('SELF_ONLY') && <> Branded content can't be private.</>}
      </div>

      {/* Interaction toggles. Duet and Stitch do not exist for photo posts —
          TikTok's guidelines say to leave them out, and content/init/ has no
          field for them — so they render for video only. */}
      <div style={sectionLabel}>Interactions</div>
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
        {isVideo && (
          <>
            <TtToggle label="Allow Duet"   on={!value.disable_duet}   disabled={!!info?.duet_disabled}   onClick={() => onChange({ disable_duet: !value.disable_duet })} />
            <TtToggle label="Allow Stitch" on={!value.disable_stitch} disabled={!!info?.stitch_disabled} onClick={() => onChange({ disable_stitch: !value.disable_stitch })} />
          </>
        )}
        <TtToggle label="Allow Comments" on={!value.disable_comment} disabled={!!info?.comment_disabled} onClick={() => onChange({ disable_comment: !value.disable_comment })} />
      </div>

      {/* Commercial content disclosure.
          The header used to be the whole story: a chevron driving showDisclosure,
          which is view state only. There was no declaration to publish against
          and nothing stopped a post going out with the section open and neither
          option picked. The declaration is now value.commercial_content, a real
          TtToggle persisted into tiktok_settings, and ttDisclosureIncomplete()
          blocks Approve/Publish while it is on with neither option selected.
          showDisclosure survives as pure chrome — but an active declaration is
          never allowed to hide inside a collapsed section. */}
      <div
        onClick={() => setShowDisclosure(v => !v)}
        title="You need to indicate if your content promotes yourself, a third party, or both."
        style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', ...sectionLabel, marginBottom: disclosureExpanded ? 10 : 14 }}
      >
        <span>Commercial Content Disclosure</span>
        <span style={{ fontSize:12 }}>{disclosureExpanded ? '▲' : '▼'}</span>
      </div>
      {disclosureExpanded && (
        <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
          <TtToggle
            label="This post promotes a brand, product or third party"
            on={disclosureOn}
            onClick={toggleDisclosure}
          />
          {disclosureOn && (
            <>
              <TtToggle label="This post promotes a brand or product" on={!!value.brand_organic} onClick={() => onChange({ brand_organic: !value.brand_organic })} />
              <TtToggle label="This is paid partnership / branded content" on={brandedOn} disabled={auditMode} onClick={toggleBranded} />
              {auditMode && (
                <div style={{ fontSize:10, color:'#f5a623', lineHeight:1.5 }}>
                  Paid partnership is unavailable while this account is in TikTok's review mode. Branded content has to be public or friends-only, and review mode publishes everything privately. This unlocks once the Direct Post audit is approved.
                </div>
              )}
              {(value.brand_organic || brandedOn) && (
                <div style={{ fontSize:10, color:'var(--text-muted)', lineHeight:1.5 }}>
                  {brandedOn
                    ? 'Your photo/video will be labeled as "Paid partnership"'
                    : 'Your photo/video will be labeled as "Promotional content"'}
                </div>
              )}
              {disclosureIncomplete && (
                <div style={{ fontSize:10, color:'#f5a623', lineHeight:1.5 }}>
                  Select at least one option. You cannot publish with this disclosure on and neither chosen.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* AI-generated content disclosure.
          This was an unconditional badge claiming every post was disclosed to
          TikTok as AI-generated, matching a hardcoded is_aigc:true on the
          backend. Both were wrong for the client's own photos and video, so the
          declaration is now an explicit switch, off unless the creator sets it,
          and the badge only appears when the disclosure is actually being sent. */}
      <div style={sectionLabel}>AI-Generated Content</div>
      <div style={{ marginBottom: value.is_aigc ? 8 : 14 }}>
        <TtToggle
          label="This content was made with AI"
          on={!!value.is_aigc}
          onClick={() => onChange({ is_aigc: !value.is_aigc })}
        />
      </div>
      {value.is_aigc && (
        <div style={{ display:'inline-block', fontSize:11, color:'#31DCBA', background:'rgba(49,220,186,0.08)', border:'1px solid rgba(49,220,186,0.3)', borderRadius:6, padding:'4px 8px', marginBottom:14 }}>
          🤖 AI Generated — disclosed to TikTok
        </div>
      )}

      {/* Music notice */}
      <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:14 }}>🎵 Background music will be auto-added by TikTok</div>

      {/* 9:16 preview (CSS crop only) */}
      {previewImageUrl && (
        <div>
          <div style={sectionLabel}>TikTok Preview (9:16)</div>
          <div style={{ width:120, aspectRatio:'9 / 16', overflow:'hidden', borderRadius:8, border:'1px solid var(--border)', background:'#0d0f14' }}>
            <img src={previewImageUrl} alt="TikTok 9:16 preview" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function ImageGenerationPanel({ brandKeywords = [], avoidTerms = [], lightingMood = [], onNavigate, clientId, stripeCustomerId, isSelfServe, selectedBrandPhoto, setSelectedBrandPhoto, selectedVideoProject, setSelectedVideoProject, storyPreset = false, onStoryPresetConsumed }) {
  const { clientId: storedClientId } = useClientId();
  // Every load effect below keys on this as well as cid, for the Phase 3
  // reason: cid is the SIGNED-IN user and never changes when an admin switches
  // which client they are viewing. Without it the panel would keep rendering
  // the previous client's brand, logo variants and video renders — the exact
  // defect this migration exists to close, just moved one layer out.
  const { viewingAsClientId } = useViewingAs();
  const [activeTab, setActiveTab] = useState('generate');

  // Generate Post tab
  const [prompt, setPrompt]                 = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedStyles, setSelectedStyles] = useState(['warm']);
  const [generating, setGenerating]         = useState(false);
  const [showTemplates, setShowTemplates]   = useState(false);
  const [styleOff, setStyleOff]             = useState(false);
  const [genCount, setGenCount]             = useState(1);
  const [apiError, setApiError]             = useState(null);
  const [applyLogo, setApplyLogo]           = useState(true);
  const [logoPos, setLogoPos]               = useState('bottom-right');
  const [logoSize, setLogoSize]             = useState(20);
  // Text overlay — carousel-slide field names and defaults, so the Carousel's
  // compositeTextOntoImage bakes it in. One setting for every Generate Post
  // result: it previews live on their images and is only baked in by Apply
  // (handleApplyTextOverlay). A list of text blocks (overlayBlocksOf); no
  // block with text means no overlay; the accordion being collapsed does not.
  const [textOverlayOpen, setTextOverlayOpen] = useState(false);
  const [textOverlay, setTextOverlay]         = useState([DEFAULT_TEXT_OVERLAY]);
  const [textOverlaySel, setTextOverlaySel]   = useState(0);   // selected text block
  const [applyingOverlayIds, setApplyingOverlayIds] = useState(new Set());
  const [brandHub, setBrandHub]             = useState({});
  const [generatedResults, setGeneratedResults]       = useState([]);
  const [processingResultIds, setProcessingResultIds] = useState(new Set());
  const approveQueueRef                               = useRef(Promise.resolve());
  const [aspectRatio, setAspectRatio]                 = useState(storyPreset ? '9:16' : '1:1');
  const [brandPhotoBase, setBrandPhotoBase]           = useState(null);
  const brandPhotoFileRef                             = useRef(null);
  const [regeneratingResultIds, setRegeneratingResultIds] = useState(new Set());
  const [publishingResultIds, setPublishingResultIds] = useState(new Set());
  const [publishResultErrors, setPublishResultErrors] = useState({});
  // Result ids whose Publish Now hit the daily cap AND the server said this
  // caller may run it as an admin test past the cap (bypassAvailable). Only
  // the server's answer turns the "Publish anyway (test)" button on.
  const [publishBypassOffers, setPublishBypassOffers] = useState({});
  const [toast, setToast]                   = useState(null);
  const [capModalOpen, setCapModalOpen]     = useState(false);
  const [topupLoading, setTopupLoading]     = useState(false);
  // Per-result TikTok Music Usage Confirmation consent (id -> checked). Required
  // before a TikTok-targeted post can be published (TikTok Direct Post audit).
  const [tiktokConsentIds, setTiktokConsentIds] = useState(new Set());
  // Keyed by CARD id, persisted against the POST row id. The Set is what the
  // grid reads, but the consent itself belongs on the post — the publish paths
  // refuse a TikTok publish that has no posts.tiktok_consent_at, and a tick that
  // never left the browser is not a consent record. A card with no server row
  // yet has nothing to persist against; Publish Now already refuses those.
  const toggleResultTiktokConsent = async (item, checked) => {
    setTiktokConsentIds(prev => {
      const n = new Set(prev);
      if (checked) n.add(item.id); else n.delete(item.id);
      return n;
    });
    if (!item.postId) return;
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/posts/${encodeURIComponent(item.postId)}/tiktok-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consented: checked }),
      });
      if (!res.ok) throw new Error(`consent save failed (${res.status})`);
    } catch (err) {
      console.error('[PostGen] TikTok consent save failed:', err?.message || err);
      setTiktokConsentIds(prev => {
        const n = new Set(prev);
        if (checked) n.delete(item.id); else n.add(item.id);
        return n;
      });
    }
  };
  // Auto-approve: brand_profile.auto_approve + the one-time consent stamp.
  // Account-wide, not per tab — all four tabs here feed the same Review &
  // Approve queue, so the switch governs every one of them even though it is
  // rendered only above Generate Post. The tooltip and disclaimer say so.
  const [autoApprove, setAutoApprove]                       = useState(false);
  const [autoApproveSaving, setAutoApproveSaving]           = useState(false);
  const [autoApproveConsentedAt, setAutoApproveConsentedAt] = useState(null);
  const [autoApproveModalOpen, setAutoApproveModalOpen]     = useState(false);
  const [autoApproveAgreed, setAutoApproveAgreed]           = useState(false);
  const [platforms, setPlatforms]           = useState(storyPreset
    ? { ig: false, fb: false, igStory: true, fbStory: true, tt: false, gb: false }
    : { ig: true, fb: true, igStory: false, fbStory: false, tt: false, gb: false });
  // Carousel and Brand Photo keep their own platform selection, isolated from
  // the Build/Video shared `platforms` above. Both default to IG + FB on every
  // mount regardless of what Build/Video currently have selected.
  const [carouselPlatforms, setCarouselPlatforms]     = useState({ ig: true, fb: true, tt: false, gb: false });
  const [brandPhotoPlatforms, setBrandPhotoPlatforms] = useState({ ig: true, fb: true, igStory: false, fbStory: false, tt: false, gb: false });
  // When entered via the Stories view, the story preset is applied once at mount
  // (above). Clear the parent flag so a later direct visit to the Post Generator
  // starts in the normal feed mode.
  useEffect(() => {
    if (storyPreset) onStoryPresetConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [ttSettings, setTtSettings]         = useState({
    caption_title: '',
    caption_description: '',
    privacy_level: '',
    disable_duet: true,
    disable_comment: true,
    disable_stitch: true,
    // Commercial content disclosure — the declaration, off by default per
    // TikTok's guidelines. brand_organic / branded_content are only meaningful
    // while this is on, and are cleared when it goes off.
    commercial_content: false,
    brand_organic: false,
    branded_content: false,
    // AI-generated content disclosure. Opt-in and off by default: the backend
    // used to send is_aigc:true on every TikTok post, which labelled the
    // client's own phone footage as AI-made. Publishing reads this exact key
    // (tiktok_settings.is_aigc → publishToTikTokVideo's isAigc).
    is_aigc: false,
  });
  const updateTtSettings = (partial) => setTtSettings(prev => ({ ...prev, ...partial }));
  // TikTok's guidelines: privacy has no default. Called after every path that
  // creates a TikTok post, so the next post never starts with the last choice.
  // The created post keeps its own copy (the ttSettings payload / snapshot).
  const resetTtPrivacy = () => updateTtSettings({ privacy_level: '' });
  // Google Business post options. Shared across the tabs the same way
  // ttSettings is: one composer, one set of Google choices.
  const [gbpSettings, setGbpSettings] = useState(defaultGbpSettings);
  // What a post row stores in gbp_settings for this picker's selection.
  const gbpPayloadFor = (picker) => (picker && picker.gb ? gbpSettingsForSave(gbpSettings) : null);
  // Stops a submit BEFORE anything is generated or spent when the Google
  // options are incomplete. The server refuses the same things; this only
  // moves the refusal in front of the paid step.
  const gbpBlocked = (picker) => {
    if (!picker || !picker.gb) return false;
    const problem = gbpSettingsProblem(gbpSettings);
    if (problem) { showToast(problem); return true; }
    return false;
  };
  const [contentMixRatio, setContentMixRatio] = useState(50);
  const contentMixSaveTimer = useRef(null);

  // Photo Library picker (Post Generator)
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [libraryPhotos, setLibraryPhotos] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [pickerSelected, setPickerSelected] = useState([]); // ordered ids picked in modal
  const [pickerReuseConfirmId, setPickerReuseConfirmId] = useState(null); // photo id awaiting reuse confirmation
  const [selectedBrandPhotos, setSelectedBrandPhotos] = useState([]); // confirmed picks (multi)

  // Brand Photo tab
  const [brandLogoUrl, setBrandLogoUrl]             = useState('');
  const [brandPhotoLogoSize, setBrandPhotoLogoSize] = useState(25);
  const [brandPhotoLogoPos,  setBrandPhotoLogoPos]  = useState('bottom-right');
  const [generatingBrandPhoto, setGeneratingBrandPhoto] = useState(false);
  const [brandPhotoSelectedStyles, setBrandPhotoSelectedStyles] = useState(['cinematic']);
  const [brandPhotoStyleOff, setBrandPhotoStyleOff] = useState(false);
  const [brandPhotoBgPrompt, setBrandPhotoBgPrompt] = useState('');
  const [brandPhotoLogoVariants, setBrandPhotoLogoVariants] = useState([]);
  const [selectedBrandPhotoLogo, setSelectedBrandPhotoLogo] = useState('main');
  const brandPhotoLogoFileRef = useRef(null);

  // Carousel tab
  const [selectedPreset,          setSelectedPreset]          = useState(null);        // CAROUSEL_PRESETS id | null
  const [carouselMode,            setCarouselMode]            = useState('generate'); // 'generate' | 'upload'
  const [carouselAspect,          setCarouselAspect]          = useState('1:1');       // '1:1' | '4:5'
  const [carouselCount,           setCarouselCount]           = useState(3);           // 1..6
  const [carouselCoverLock,       setCarouselCoverLock]       = useState(false);
  const [carouselHookPrompt,      setCarouselHookPrompt]      = useState('');
  const [carouselBgPrompt,        setCarouselBgPrompt]        = useState('');
  const [carouselSlides,          setCarouselSlides]          = useState([]); // [{ id, imageUrl, source, text, caption }]
  const [carouselPreviewIdx,      setCarouselPreviewIdx]      = useState(0);
  const [carouselTextSel,         setCarouselTextSel]         = useState({}); // slide id → selected text block
  const [carouselGenerating,      setCarouselGenerating]      = useState(false);
  const [carouselRegenIds,        setCarouselRegenIds]        = useState(new Set());
  const [carouselBulkTextPrompt,  setCarouselBulkTextPrompt]  = useState('');
  const [carouselBulkTextLoading, setCarouselBulkTextLoading] = useState(false);
  const [carouselLogoOn,          setCarouselLogoOn]          = useState(true);
  const [carouselLogoPos,         setCarouselLogoPos]         = useState('bottom-right');
  const [carouselLogoSize,        setCarouselLogoSize]        = useState(20);
  const [carouselPostCaption,     setCarouselPostCaption]     = useState('');
  const [carouselPostHashtags,    setCarouselPostHashtags]    = useState('');
  const [carouselSubmitting,      setCarouselSubmitting]      = useState(false);
  const [carouselDragging,        setCarouselDragging]        = useState(false);
  const [carouselLibraryOpen,     setCarouselLibraryOpen]     = useState(false);
  const [carouselLibraryPhotos, setCarouselLibraryPhotos] = useState([]);
  const carouselFileInputRef = useRef(null);
  const carouselDragSrcRef   = useRef(null);

  // Video tab — pick a completed render and turn it into a video post
  const [videoProjects,        setVideoProjects]        = useState([]);
  const [videoLoading,         setVideoLoading]         = useState(false);
  const [selectedVideoId,      setSelectedVideoId]      = useState(null);
  const [videoCaption,         setVideoCaption]         = useState('');
  const [videoHashtags,        setVideoHashtags]        = useState('');
  const [videoPostId,          setVideoPostId]          = useState(null);
  const [videoCaptionLoading,  setVideoCaptionLoading]  = useState(false);
  const [videoSubmitting,      setVideoSubmitting]      = useState(false);

  const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' ? v.split(',').map(s => s.trim()).filter(Boolean) : [];

  const cid = clientId || storedClientId;
  // Google appears as a destination only once it is connected for the client
  // being worked on — unlike TikTok, it has no settings panel that explains a
  // missing connection, so an unconnected Google option would only fail later.
  const gbpConnected = useGoogleBizConnected(cid);
  // If Google stops being available (an admin switches to a client without
  // it), untick it everywhere so a hidden destination is never submitted.
  useEffect(() => {
    if (gbpConnected !== false) return;
    setPlatforms(p => (p.gb ? { ...p, gb: false } : p));
    setCarouselPlatforms(p => (p.gb ? { ...p, gb: false } : p));
    setBrandPhotoPlatforms(p => (p.gb ? { ...p, gb: false } : p));
  }, [gbpConnected]);

  // Phase 7: the separate auto-approve read is GONE. It existed because the
  // brand-hub select below was a .single() that would fail whole-hog if
  // auto_approve did not exist yet, taking the panel with it — so the two
  // columns were fetched apart to keep that failure contained.
  //
  // GET /api/brand-hub carries both concerns now: the four columns this panel
  // needs are in its read list, and the route's missing-column retry replaces
  // the isolation this separate query was buying. One request instead of two.

  // The consent timestamp is no longer generated here. The browser used to mint
  // it and send it as a column value; POST /api/auto-approve stamps it
  // server-side and returns what it wrote, and the column is absent from the
  // Brand Hub write allowlist so nothing else can set it. A consent record the
  // client supplies the value for is not a consent record.
  //
  // stampConsent therefore no longer needs to be passed: the route stamps on
  // the first enable and never re-stamps, which is the behaviour the two
  // callers were expressing between them.
  const saveAutoApprove = async (next) => {
    const prev = autoApprove;
    setAutoApprove(next);            // optimistic
    setAutoApproveSaving(true);
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/auto-approve`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ enabled: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      if (body.auto_approve_consented_at) setAutoApproveConsentedAt(body.auto_approve_consented_at);
      showToast(next
        ? 'Auto-approve on — posts publish without review, including any already waiting'
        : 'Auto-approve off — new posts wait in Review & Approve');
    } catch (err) {
      setAutoApprove(prev);          // roll back
      console.error('[AutoApprove] save failed:', err?.message || err);
      showToast('Could not save that setting — try again');
    } finally {
      setAutoApproveSaving(false);
    }
  };

  const requestToggleAutoApprove = () => {
    if (!cid || autoApproveSaving) return;
    // Turning OFF is immediate. Turning ON shows the disclaimer only if this
    // client has never confirmed it: auto_approve_consented_at is stamped once
    // and never cleared, so switching off and back on later just flips the
    // boolean.
    // The "I agree" box starts unticked every time the disclaimer opens.
    if (!autoApprove && !autoApproveConsentedAt) { setAutoApproveAgreed(false); setAutoApproveModalOpen(true); return; }
    saveAutoApprove(!autoApprove);
  };

  const confirmAutoApprove = async () => {
    setAutoApproveModalOpen(false);
    await saveAutoApprove(true);
  };

  // Auto-approve hand-off, called by every path in this panel that creates a
  // post. Resolves to the ISO slot the post was scheduled into, or null if it
  // stays pending for review.
  //
  // TikTok is excluded by design: handleApproveResult / handleApprove gate any
  // TikTok-targeted post behind a per-post Music Usage Confirmation, and
  // nothing captures that consent at creation time. Auto-approving one would
  // publish to TikTok with consent never given, so those keep going to Review
  // & Approve regardless of the switch.
  //
  // STILL SERIALIZED, and the reason is unchanged by the migration: slot
  // selection reads the latest scheduled post, so two concurrent callers would
  // be handed the same slot. Moving the read server-side shrinks that race
  // window but does not remove it — computeNextSlotFor and the update are two
  // statements there too — and batch generation (genCount > 1) creates posts in
  // parallel, so the queue stays.
  const maybeAutoSchedule = (postId, destPlatforms) => {
    if (!autoApprove || !postId || !cid) return Promise.resolve(null);
    if (destPlatforms?.tt) return Promise.resolve(null);
    approveQueueRef.current = approveQueueRef.current.then(async () => {
      try {
        // A 404 replaces the old empty-array check: the endpoint returns one on
        // a row the resolved client does not own, which is the same condition
        // RLS used to express by matching nothing. Leaving the post pending is
        // still the safe failure — it simply waits for review.
        return await approvePost(postId);
      } catch (err) {
        console.error('[AutoApprove] auto-schedule failed:', err?.message || err);
        return null;
      }
    });
    return approveQueueRef.current;
  };

  // "Tue 9:00 AM" — used in the auto-approved result card and its toast.
  const formatSlot = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      });
    } catch { return ''; }
  };

  // DEP ARRAY FIXED HERE, and it was wrong before this migration touched it.
  //
  // The body reads `cid` (= clientId || storedClientId) but the array named
  // `clientId` alone. When the prop was null and storedClientId resolved a tick
  // later, cid changed and this effect did not re-run — the panel kept an empty
  // brandHub and a default logo. There was no eslint-disable on it, so it was
  // an unacknowledged slip rather than a decision; the two effects either side
  // of it already used [cid] correctly.
  //
  // It went unnoticed because package.json builds with `CI= react-scripts
  // build`, which unsets CI so react-app's exhaustive-deps WARNINGS never
  // become errors.
  //
  // Fixing it is not optional cleanup: after this migration a stale cid is a
  // stale viewing-as context, and the request would go out against the wrong
  // client with nothing to catch it.
  useEffect(() => {
    if (!cid) return;
    let cancelled = false;
    (async () => {
      try {
        // allSettled, not all. These were two independent .then() chains before
        // the migration — the brand profile never depended on the logo listing
        // — and Promise.all would have reintroduced that coupling: a network
        // rejection on the variants request would discard the profile too and
        // leave the panel with an empty brandHub.
        const [profileRes, variantsRes] = await Promise.allSettled([
          clientScopedFetch(`${BACKEND}/api/brand-hub`),
          clientScopedFetch(`${BACKEND}/api/brand-hub/logo-variants`),
        ]);
        const readJson = async (settled) => (
          settled.status === 'fulfilled' ? settled.value.json().catch(() => ({})) : {}
        );
        const profileBody  = await readJson(profileRes);
        const variantsBody = await readJson(variantsRes);
        if (cancelled) return;
        if (profileRes.status === 'rejected') {
          console.warn('[PostGenerator] brand profile load failed:', profileRes.reason?.message || profileRes.reason);
        }
        if (variantsRes.status === 'rejected') {
          console.warn('[PostGenerator] logo variants load failed:', variantsRes.reason?.message || variantsRes.reason);
        }

        const data = profileBody?.profile;
        if (data) {
          setBrandLogoUrl(data.logo_url || '');
          setApplyLogo(data.apply_logo_ai ?? true);
          if (data.content_mix_ratio != null) setContentMixRatio(data.content_mix_ratio);
          // Phase 7: auto-approve state rides in on this same response, which
          // is what retired the panel's second brand_profile read.
          setAutoApprove(!!data.auto_approve);
          setAutoApproveConsentedAt(data.auto_approve_consented_at || null);
          setBrandHub({
            bizName:                 data.brand_name || '',
            tagline:                 data.brand_tagline || '',
            category:                data.brand_industry || '',
            tones:                   toArr(data.brand_tone),
            emojiPref:               data.emoji_pref || 'Minimal',
            primaryColour:           data.primary_color || '',
            secondaryColour:         data.secondary_color || '',
            accentColour:            data.accent_color || '',
            logoUrl:                 data.logo_url || '',
            visualStyles:            toArr(data.visual_styles),
            lightingMood:            toArr(data.image_mood),
            imgBrandKeywords:        toArr(data.image_keywords),
            falStyleKeywords:        data.image_style || '',
            negativePromptAdditions: data.image_negative_prompt || '',
            keyProducts:             toArr(data.key_products),
            upcomingPromos:          data.upcoming_promos || '',
            avoidTopics:             toArr(data.avoid_topics),
            hashtagSets:             toArr(data.hashtag_sets),
          });
        }
        if (Array.isArray(variantsBody?.variants)) setBrandPhotoLogoVariants(variantsBody.variants);
      } catch (err) {
        if (!cancelled) console.warn('[PostGenerator] brand load failed:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [cid, viewingAsClientId]);

  // Auto-populate carousel hashtags from brand profile when the carousel tab
  // opens: five drawn at random from ALL saved tags (lib/hashtags — the
  // backend's rules), not every tag, of which saving kept only the first five.
  useEffect(() => {
    if (activeTab !== 'carousel') return;
    if (carouselPostHashtags.trim()) return;
    const picked = pickHashtags(brandHub?.hashtagSets);
    if (picked.length === 0) return;
    setCarouselPostHashtags(picked.join(' '));
  }, [activeTab, brandHub]);

  // Load this client's completed video renders for the Video tab picker.
  const loadVideoProjects = async () => {
    if (!cid) return;
    setVideoLoading(true);
    try {
      // ?status=completed is the render_status filter, moved to the query
      // string. The route serves ProjectsList too, unfiltered.
      const res = await clientScopedFetch(`${BACKEND}/api/video/projects?status=completed`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setVideoProjects(body.projects || []);
    } catch (err) {
      console.error('[Video] load projects failed:', err?.message || err);
      setVideoProjects([]);
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'video') return;
    loadVideoProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, cid, viewingAsClientId]);

  // "Use in Post" bridge from the Video Editor — open the Video tab with the
  // project pre-selected, then clear the bridge so it fires only once.
  useEffect(() => {
    if (!selectedVideoProject) return;
    setActiveTab('video');
    setVideoProjects(prev =>
      prev.some(p => p.id === selectedVideoProject.id) ? prev : [selectedVideoProject, ...prev]
    );
    handleSelectVideoProject(selectedVideoProject);
    setSelectedVideoProject?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoProject]);

  // Image-only variations for a batch: they go to the backend as
  // image_variation and steer the photo, never the caption. Caption variety is
  // the backend's job (batch-variety.js), keyed off batch_index.
  const VARIATION_SEEDS = [
    'wide composition, environmental context',
    'tight close crop, different angle, detail focus',
    'overhead perspective, flat lay style',
    'lifestyle context, natural setting, candid feel',
    'dramatic framing, subject off-centre',
    'soft background bokeh, subject sharp',
    'golden ratio composition, balanced',
    'minimalist framing, negative space',
  ];

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  // Was hardcoded, unlike the module-level BACKEND and TikTokSettingsPanel's own
  // copy of this line. Harmless while every call here was direct-Supabase; not
  // harmless now that nine of this panel's calls go through it, because it
  // pinned the whole Post Generator to production regardless of
  // REACT_APP_BACKEND_URL — which is exactly the variable you would set to
  // exercise this phase against a local or staging backend.
  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

  const handlePostTopUp = async () => {
    if (topupLoading) return;
    setTopupLoading(true);
    try {
      const res = await clientScopedFetch(`${API_BASE}/api/create-post-topup-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: stripeCustomerId }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (err) {
      console.error('[PostTopUp] checkout failed:', err.message);
      showToast('Could not start checkout — please try again.');
      setTopupLoading(false);
    }
  };

  // realPercentage: 1.0 = all Real, 0.0 = all Generated
  const realPercentage = (100 - contentMixRatio) / 100;
  const requiredPhotoCount = Math.max(0, Math.ceil(genCount * realPercentage));

  const openPhotoPicker = async () => {
    setPhotoPickerOpen(true);
    // Seed picker selection from any already-confirmed picks
    setPickerSelected(selectedBrandPhotos.map(p => p.id));
    if (!cid) return;
    setLibraryLoading(true);
    try {
      // Phase 6's route, reused unchanged. Its orderBrandPhotos applies the
      // same used / sort_order / created_at ordering this query spelled out,
      // and its column set is a superset of the seven fields read here.
      const res = await clientScopedFetch(`${BACKEND}/api/photo-library`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setLibraryPhotos(body.photos || []);
    } catch (err) {
      console.error('[PhotoPicker] load failed:', err?.message || err);
    } finally {
      setLibraryLoading(false);
    }
  };

  const togglePickerPhoto = (photo) => {
    // If a used photo is already selected, allow toggling off without prompt.
    if (photo.used && !pickerSelected.includes(photo.id)) {
      setPickerReuseConfirmId(photo.id);
      return;
    }
    setPickerSelected(prev => {
      if (prev.includes(photo.id)) return prev.filter(id => id !== photo.id);
      if (prev.length >= requiredPhotoCount) return prev; // cap at required
      return [...prev, photo.id];
    });
  };

  const confirmPickerReuse = (photoId) => {
    setPickerSelected(prev => {
      if (prev.includes(photoId)) return prev;
      if (prev.length >= requiredPhotoCount) return prev;
      return [...prev, photoId];
    });
    setPickerReuseConfirmId(null);
  };

  const confirmPickerSelection = () => {
    const picked = pickerSelected
      .map(id => libraryPhotos.find(p => p.id === id))
      .filter(Boolean)
      .map(p => ({ id: p.id, url: p.url, filename: p.filename, storage_path: p.storage_path }));
    setSelectedBrandPhotos(picked);
    if (setSelectedBrandPhoto) setSelectedBrandPhoto(picked[0] || null);
    setPhotoPickerOpen(false);
  };

  const handleAutoPull = () => {
    const unused = libraryPhotos.filter(p => !p.used);
    const picks = unused.slice(0, requiredPhotoCount).map(p => ({
      id: p.id, url: p.url, filename: p.filename, storage_path: p.storage_path,
    }));
    setSelectedBrandPhotos(picks);
    if (setSelectedBrandPhoto) setSelectedBrandPhoto(picks[0] || null);
    setPhotoPickerOpen(false);
  };

  const pickLibraryPhoto = (photo) => {
    if (setSelectedBrandPhoto) {
      setSelectedBrandPhoto({
        id:           photo.id,
        url:          photo.url,
        filename:     photo.filename,
        storage_path: photo.storage_path,
      });
    }
    setPhotoPickerOpen(false);
  };

  const clearSelectedBrandPhoto = () => {
    if (setSelectedBrandPhoto) setSelectedBrandPhoto(null);
    setSelectedBrandPhotos([]);
  };

  const handleContentMixChange = (val) => {
    setContentMixRatio(val);
    if (contentMixSaveTimer.current) clearTimeout(contentMixSaveTimer.current);
    contentMixSaveTimer.current = setTimeout(async () => {
      if (!cid) return;
      try {
        // content_mix_ratio, NOT content_mix — brand_profile carries both, plus
        // content_style_mix, and only the integer one is this slider's.
        const res = await clientScopedFetch(`${BACKEND}/api/brand-hub`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ profile: { content_mix_ratio: val } }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      } catch (err) {
        console.error('[ContentMix] save failed:', err?.message || err);
      }
    }, 500);
  };

  const handleGenerate = async () => {
    // photosForGen: array of photos, indexed by post number (i). Multi-photo
    // selection from picker takes precedence over the legacy single photo.
    const photosForGen = selectedBrandPhotos.length > 0
      ? selectedBrandPhotos
      : (selectedBrandPhoto ? [selectedBrandPhoto] : []);
    if (realPercentage > 0 && photosForGen.length < requiredPhotoCount) {
      openPhotoPicker();
      return;
    }
    const hasAnyPhoto = photosForGen.length > 0;
    if (!hasAnyPhoto && !prompt.trim()) return;
    if (selectedStyles.length === 0) return;
    if (!platforms.ig && !platforms.fb && !platforms.igStory && !platforms.fbStory && !platforms.tt && !platforms.gb) return;
    if (platforms.tt && !ttSettings.privacy_level) { showToast('Select a TikTok privacy status'); return; }
    if (platforms.tt && ttDisclosureIncomplete(ttSettings)) { showToast('Select "Your brand" or "Branded content" for the TikTok commercial content disclosure'); return; }
    if (gbpBlocked(platforms)) return;
    setGenerating(true);
    const bh = brandHub;
    const platformList = [
      ...(platforms.ig ? ['instagram'] : []),
      ...(platforms.fb ? ['facebook'] : []),
      ...(platforms.tt ? ['tiktok'] : []),
      ...(platforms.gb ? ['google_business'] : []),
    ];
    const ttSettingsPayload = platforms.tt ? ttSettings : null;

    const variations = genCount > 1
      ? Array.from({ length: genCount }, (_, i) => VARIATION_SEEDS[i % VARIATION_SEEDS.length])
      : [null];
    // Where this batch starts in the backend's post-type / caption-angle
    // rotation, so back-to-back batches don't open the same way. Any integer
    // works; the backend adds batch_index to it.
    const batchOffset = Math.floor(Math.random() * 1000);

    try {
      // allSettled, not all: now that the backend counts every AI image, a
      // batch can cross the monthly cap partway. With Promise.all the first
      // cap_reached threw away the posts that WERE made — they sat in the
      // queue but never showed here.
      const settled = await Promise.allSettled(
        variations.map(async (seed, i) => {
          const photoForThisPost = photosForGen[i] || null;
          const hasPhoto = !!photoForThisPost;
          const topic = prompt.trim() || (hasPhoto ? `featuring ${photoForThisPost.filename.replace(/\.[^.]+$/, '')}` : '');
          const pickedStyle = selectedStyles[Math.floor(Math.random() * selectedStyles.length)];
          const styleLabel = IG_STYLES.find(s => s.id === pickedStyle)?.label || '';
          const res = await clientScopedFetch(`${API_BASE}/api/generate-post`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customerId:              stripeCustomerId,
              topic,
              bizName:                 bh.bizName,
              tagline:                 bh.tagline,
              category:                bh.category,
              tones:                   bh.tones,
              emojiPref:               bh.emojiPref || 'Minimal',
              primaryColour:           bh.primaryColour,
              secondaryColour:         bh.secondaryColour,
              accentColour:            bh.accentColour,
              logoUrl:                 bh.logoUrl,
              visualStyles:            bh.visualStyles,
              lightingMood:            bh.lightingMood,
              imgBrandKeywords:        bh.imgBrandKeywords,
              styles:                  selectedStyles,
              pickedStyle,
              platforms:               platformList,
              tiktok_settings:         ttSettingsPayload,
              gbp_settings:            gbpPayloadFor(platforms),
              falStyleKeywords:        styleOff ? '' : (bh.falStyleKeywords || styleLabel),
              negativePromptAdditions: bh.negativePromptAdditions,
              keyProducts:             bh.keyProducts,
              upcomingPromos:          bh.upcomingPromos,
              activePromotions:        [],
              avoidTopics:             bh.avoidTopics,
              hashtagSets:             bh.hashtagSets,
              apply_logo:              applyLogo,
              logo_position:           logoPos,
              logo_size:               logoSize,
              aspect_ratio:            aspectRatio,
              batch_mode:              genCount > 1,
              ...(genCount > 1 ? {
                batch_index:           i,
                batch_offset:          batchOffset,
                image_variation:       seed,
              } : {}),
              brand_photo_url:         hasPhoto ? photoForThisPost.url : null,
              source_photo_id:         hasPhoto ? photoForThisPost.id  : null,
              skip_image_gen:          hasPhoto,
            }),
          });
          const data = await res.json();
          if (data.errorCode === 'FAL_KEY_INVALID') { setApiError('FAL_KEY_INVALID'); throw new Error('FAL_KEY_INVALID'); }
          if (res.status === 403 && data.error === 'cap_reached') {
            setCapModalOpen(true);
            throw new Error('cap_reached');
          }
          if (!res.ok) throw new Error(data.error || 'Generation failed');
          // For a library photo, take what the backend returned. It does honour
          // brand_photo_url (runGeneratePost's useBrandPhoto path) and, with
          // apply_logo on, returns the photo WITH the logo composited. This used
          // to force the raw photo URL instead, which silently threw that logo
          // away. The raw URL is only a fallback if the row came back without one.
          const finalImageUrl = hasPhoto
            ? (data.post?.image_url || photoForThisPost.url)
            : (data.post?.image_url || null);
          if (data.post?.id) {
            // A PATCH, not an upsert. It was written as one defensively, but
            // the row always exists by here: /api/generate-post inserts it and
            // returns the id being written to. created_at and client_id drop
            // out with the upsert — the insert already set both, and client_id
            // is resolved server-side now.
            //
            // This overwrite is a WORKAROUND for /api/generate-post not
            // honouring the composer's media fields (see the comment above
            // finalImageUrl). Preserved as-is: fixing generation semantics
            // inside a routing migration would hide a behaviour change in a
            // mechanical diff. Logged as its own backlog item.
            try {
              await patchPost(data.post.id, {
                caption:         data.post.caption   || '',
                hashtags:        data.post.hashtags  || '',
                image_url:       finalImageUrl,
                platforms:       platformList,
                tiktok_settings: ttSettingsPayload,
                gbp_settings:    gbpPayloadFor(platforms),
                is_ig_story:     platforms.igStory,
                is_fb_story:     platforms.fbStory,
                status:          'pending',
                scheduled_at:    null,
                source_photo_id: hasPhoto ? photoForThisPost.id : null,
              });
            } catch (patchErr) {
              console.error('[Generate] post patch failed:', patchErr?.message || patchErr);
            }
          }
          const autoScheduledAt = await maybeAutoSchedule(data.post?.id, platforms);
          return {
            id:       crypto.randomUUID(),
            imageUrl: finalImageUrl,
            caption:  data.post?.caption    || '',
            hashtags: data.post?.hashtags   || '',
            postId:   data.post?.id,
            // Set when auto-approve scheduled this post at creation. The result
            // card then renders read-only: the row is already queued to publish,
            // so an editable caption here would silently not persist.
            autoScheduledAt,
            // Snapshot the composer state so the TikTok consent checkbox reflects
            // this item's own destinations/settings, not whatever the live
            // composer holds by the time it's reviewed.
            platforms:  { ...platforms },
            ttSettings: ttSettingsPayload,
            // What this post was generated FROM, so Regenerate makes a
            // variation on it. handleRegenerateResult has read item.topic since
            // the regen fix, but nothing stored it — so regen always fell back
            // to the composer prompt, which is cleared just below.
            topic,
            sourcePhoto: hasPhoto ? { id: photoForThisPost.id, url: photoForThisPost.url } : null,
            // The image before any text overlay, and the overlay baked into
            // imageUrl (null = none). Apply always composites from the base,
            // so re-applying never stacks text on text.
            baseImageUrl:   finalImageUrl,
            appliedOverlay: null,
          };
        })
      );
      const results = settled.filter(r => r.status === 'fulfilled').map(r => r.value);
      const errors  = settled.filter(r => r.status === 'rejected').map(r => r.reason);
      // Nothing made: same as before — the composer keeps its state.
      if (results.length === 0) throw errors[0];
      errors.forEach(e => console.error('Generate error:', e));
      setGeneratedResults(prev => [...results, ...prev]);
      if (platforms.tt) resetTtPrivacy();
      setPrompt('');
      setSelectedTemplate(null);
      if (setSelectedBrandPhoto) setSelectedBrandPhoto(null);
      setSelectedBrandPhotos([]);
      const made = results.length;
      const scheduledCount = results.filter(r => r.autoScheduledAt).length;
      const madeLabel = made === 1 ? 'Post' : `${made} posts`;
      // cap_reached already opened its modal; any other failure is named here.
      const otherFailures = errors.filter(e => e?.message !== 'cap_reached' && e?.message !== 'FAL_KEY_INVALID').length;
      showToast((scheduledCount > 0
        ? `${madeLabel} generated — ${scheduledCount === made ? 'scheduled to publish' : `${scheduledCount} scheduled to publish`}`
        : `${madeLabel} generated`) + (otherFailures ? ` — ${otherFailures} failed` : ''));
    } catch (err) {
      console.error('Generate error:', err);
      if (err.message !== 'FAL_KEY_INVALID' && err.message !== 'cap_reached') showToast('Generation failed — please try again');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateResult = (id, field, value) => {
    setGeneratedResults(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const handleApproveResult = (item) => {
    if (processingResultIds.has(item.id)) return;
    // TikTok Direct Post audit: approving schedules the post, and the cron
    // publishes it unattended — so consent must be captured here exactly as it
    // is for Publish Now. The button is disabled too, but this path is
    // compliance-critical, so it re-checks rather than trusting the UI alone.
    // Mirrors the destination resolution used in the result card's render.
    const itemPlatforms = item.platforms
      || ((item.previewKind === 'brand_photo' || item.post_type === 'brand_photo')
        ? brandPhotoPlatforms
        : platforms);
    if (itemPlatforms.tt && !tiktokConsentIds.has(item.id)) {
      showToast('Confirm the TikTok Music Usage Confirmation first');
      return;
    }
    // The card is previewing text that isn't in the stored image yet; approving
    // now would schedule something other than what's on screen.
    if (isOverlayPending(item) || applyingOverlayIds.has(item.id)) {
      showToast('Apply or clear the text overlay first');
      return;
    }
    setProcessingResultIds(prev => new Set([...prev, item.id]));
    approveQueueRef.current = approveQueueRef.current.then(async () => {
      try {
        // caption/hashtags travel WITH the approve so the card's last edits and
        // the schedule land in one statement. Splitting them into a PATCH and
        // an approve could half-succeed and schedule the text the user just
        // replaced.
        await approvePost(item.postId, { caption: item.caption, hashtags: item.hashtags });
        setGeneratedResults(prev => prev.filter(r => r.id !== item.id));
        showToast('✓ Post approved & scheduled');
      } catch (err) {
        console.error('[Approve] failed:', err?.message || err);
        setProcessingResultIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
        showToast('Approval failed — try again');
      }
    });
    return approveQueueRef.current;
  };

  // Publish Now — fire the post to the backend immediately (same publish
  // engine the cron uses) instead of waiting for its scheduled slot.
  const handlePublishNowResult = async (item, { bypassDailyLimit = false } = {}) => {
    if (
      publishingResultIds.has(item.id) ||
      processingResultIds.has(item.id) ||
      regeneratingResultIds.has(item.id) ||
      applyingOverlayIds.has(item.id)
    ) return;
    // Same reason as the approve gate: publish what the card shows.
    if (isOverlayPending(item)) {
      setPublishResultErrors(e => ({ ...e, [item.id]: 'Apply or clear the text overlay first.' }));
      return;
    }
    if (!item.postId) {
      setPublishResultErrors(e => ({ ...e, [item.id]: 'Cannot publish — this post is missing its server record.' }));
      return;
    }
    setPublishingResultIds(prev => new Set([...prev, item.id]));
    setPublishResultErrors(e => { const { [item.id]: _drop, ...rest } = e; return rest; });
    setPublishBypassOffers(o => { const { [item.id]: _drop, ...rest } = o; return rest; });
    try {
      const res = await clientScopedFetch(`${API_BASE}/api/publish-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postId:    item.postId,
          caption:   item.caption,
          hashtags:  item.hashtags,
          // Admin test only. The server ignores it for anyone who isn't an
          // admin publishing on a test account.
          ...(bypassDailyLimit ? { bypass_daily_limit: true } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        if (data.code === 'daily_limit_reached' && data.bypassAvailable === true) {
          setPublishBypassOffers(o => ({ ...o, [item.id]: true }));
        }
        throw new Error(data.error || `Publish failed (${res.status})`);
      }
      setGeneratedResults(prev => prev.filter(r => r.id !== item.id));
      // The backend says what is actually happening — which platform is still
      // processing, and that it can take a few minutes. That message was being
      // thrown away in favour of "going live shortly", which is both vaguer and,
      // for a TikTok post still transcoding, not necessarily true: TikTok can
      // still reject it. Only fall back when the server sent nothing.
      showToast(data.message || (data.pending ? '✓ Publishing — going live shortly' : '✓ Post published'));
    } catch (err) {
      console.error('[PublishNow] failed:', err?.message || err);
      setPublishResultErrors(e => ({ ...e, [item.id]: err?.message || 'Publish failed — please try again.' }));
    } finally {
      setPublishingResultIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
    }
  };

  // Video tab — selecting a render generates a caption (reusing the existing
  // /api/generate-post caption logic with skip_image_gen) and creates the
  // backing post row, patched to a video post (media_type/video_url/thumbnail).
  // Discard a previously-prepared-but-unsubmitted draft so switching videos or
  // regenerating doesn't leave orphan 'pending' rows behind.
  const discardVideoDraft = () => {
    if (!videoPostId) return;
    patchPost(videoPostId, { status: 'discarded' })
      .catch(err => console.error('[Video] discard prior draft failed:', err?.message || err));
  };

  // Selecting a video is a pure preview — highlight it and seed the caption
  // from Brand Hub (falling back to blank). Caption generation is deferred
  // to the explicit button below, which overwrites this unconditionally.
  const handleSelectVideoProject = (project) => {
    if (!project) return;
    if (project.id === selectedVideoId) return;
    discardVideoDraft();
    setSelectedVideoId(project.id);
    setVideoPostId(null);
    setVideoCaption(brandHub?.tagline || '');
    // Five at random from all saved tags, same as the carousel (lib/hashtags).
    setVideoHashtags(pickHashtags(brandHub?.hashtagSets).join(' '));
  };

  // Generate Caption — fires the existing /api/generate-post caption logic for
  // the selected video and creates the backing video post row.
  const handleGenerateVideoCaption = async () => {
    if (videoCaptionLoading) return;
    const project = videoProjects.find(p => p.id === selectedVideoId);
    if (!project) return;
    discardVideoDraft();
    setVideoPostId(null);
    setVideoCaptionLoading(true);
    const bh = brandHub;
    const platformList = [
      ...(platforms.ig ? ['instagram'] : []),
      ...(platforms.fb ? ['facebook'] : []),
      ...(platforms.tt ? ['tiktok'] : []),
      ...(platforms.gb ? ['google_business'] : []),
    ];
    try {
      const res = await clientScopedFetch(`${API_BASE}/api/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId:              stripeCustomerId,
          topic:                   project.title || 'showcase the brand',
          bizName:                 bh.bizName,
          tagline:                 bh.tagline,
          category:                bh.category,
          tones:                   bh.tones,
          emojiPref:               bh.emojiPref || 'Minimal',
          primaryColour:           bh.primaryColour,
          secondaryColour:         bh.secondaryColour,
          accentColour:            bh.accentColour,
          logoUrl:                 bh.logoUrl,
          visualStyles:            bh.visualStyles,
          lightingMood:            bh.lightingMood,
          imgBrandKeywords:        bh.imgBrandKeywords,
          platforms:               platformList.length ? platformList : ['instagram', 'facebook'],
          negativePromptAdditions: bh.negativePromptAdditions,
          keyProducts:             bh.keyProducts,
          upcomingPromos:          bh.upcomingPromos,
          activePromotions:        [],
          avoidTopics:             bh.avoidTopics,
          hashtagSets:             bh.hashtagSets,
          skip_image_gen:          true,
        }),
      });
      const data = await res.json();
      if (res.status === 403 && data.error === 'cap_reached') { setCapModalOpen(true); throw new Error('cap_reached'); }
      if (!res.ok) throw new Error(data.error || 'Caption generation failed');

      const caption  = data.post?.caption  || '';
      const hashtags = data.post?.hashtags || '';
      if (data.post?.id) {
        // Same shape as the Build tab's patch above, and the same standing:
        // the row exists, the overwrite is the generate-route workaround.
        try {
          await patchPost(data.post.id, {
            caption,
            hashtags,
            image_url:    project.thumbnail_url || null,
            video_url:    project.output_url || null,
            media_type:   'video',
            post_type:    'video',
            platforms:    platformList.length ? platformList : ['instagram', 'facebook'],
            is_ig_story:  false,
            is_fb_story:  false,
            status:       'pending',
            scheduled_at: null,
          });
        } catch (patchErr) {
          console.error('[Video] post patch failed:', patchErr?.message || patchErr);
        }
        setVideoPostId(data.post.id);
      }
      setVideoCaption(caption);
      setVideoHashtags(hashtags);
    } catch (err) {
      console.error('[Video] caption generation failed:', err?.message || err);
      if (err.message !== 'cap_reached') showToast('Caption generation failed — try again');
    } finally {
      setVideoCaptionLoading(false);
    }
  };

  // Push the prepared video post into the Review & Approve list, saving any
  // caption/hashtag/platform edits to its row first. From there the shared
  // result card handles Approve / Publish Now exactly like any other post.
  const handleAddVideoPost = async () => {
    if (!videoPostId || videoSubmitting) return;
    const project = videoProjects.find(p => p.id === selectedVideoId);
    const platformList = [
      ...(platforms.ig ? ['instagram'] : []),
      ...(platforms.fb ? ['facebook'] : []),
      ...(platforms.tt ? ['tiktok'] : []),
      ...(platforms.gb ? ['google_business'] : []),
    ];
    if (platformList.length === 0) { showToast('Select at least one platform'); return; }
    if (platforms.tt && !ttSettings.privacy_level) { showToast('Select a TikTok privacy status'); return; }
    if (platforms.tt && ttDisclosureIncomplete(ttSettings)) { showToast('Select "Your brand" or "Branded content" for the TikTok commercial content disclosure'); return; }
    setVideoSubmitting(true);
    try {
      const tiktok_settings = platforms.tt ? ttSettings : null;
      await patchPost(videoPostId, {
        caption: videoCaption, hashtags: videoHashtags, platforms: platformList, tiktok_settings,
      });
      const autoScheduledAt = await maybeAutoSchedule(videoPostId, platforms);
      setGeneratedResults(prev => [{
        id:        crypto.randomUUID(),
        post_type: 'video',
        postId:    videoPostId,
        autoScheduledAt,
        imageUrl:  project?.thumbnail_url || null,
        videoUrl:  project?.output_url || null,
        caption:   videoCaption,
        hashtags:  videoHashtags,
        // Snapshot composer state for a stable TikTok consent checkbox.
        platforms:  { ...platforms },
        ttSettings: tiktok_settings,
      }, ...prev]);
      // Reset the picker for the next selection.
      if (platforms.tt) resetTtPrivacy();
      setSelectedVideoId(null);
      setVideoPostId(null);
      setVideoCaption('');
      setVideoHashtags('');
      showToast(autoScheduledAt
        ? `Video post scheduled — publishes ${formatSlot(autoScheduledAt)}`
        : 'Video post added to Review & Approve');
    } catch (err) {
      console.error('[Video] add post failed:', err?.message || err);
      showToast('Could not add post — try again');
    } finally {
      setVideoSubmitting(false);
    }
  };

  const handleDiscardResult = async (item) => {
    const id = typeof item === 'object' ? item.id : item;
    const postId = typeof item === 'object' ? item.postId : null;
    setGeneratedResults(prev => prev.filter(r => r.id !== id));
    if (postId) {
      // scheduled_at is cleared alongside the status because this also backs
      // "Cancel post" on an auto-approved card: status='discarded' takes it out
      // of the publish cron's scan, and dropping the slot keeps a cancelled
      // post from lingering on the Calendar at a time it will never publish.
      // A no-op for posts that were still pending.
      //
      // 'discarded', not 'failed': this post was thrown away, never sent.
      // 'failed' made it read as a publishing failure (post 53f4b7e5, Oct 4).
      try {
        await patchPost(postId, { status: 'discarded', scheduled_at: null });
      } catch (err) {
        console.error('[Discard] mark discarded failed:', err?.message || err);
      }
    }
  };

  const handleRegenerateResult = async (item) => {
    if (regeneratingResultIds.has(item.id)) return;
    // The TikTok checks run BEFORE the old post is marked discarded below. They
    // used to run after it, and only on the non-brand-photo branch, so a
    // missing privacy choice threw away the post and then refused to replace
    // it. That became the common case once privacy resets after every post.
    const regenPlatforms = item.post_type === 'brand_photo' ? brandPhotoPlatforms : platforms;
    if (regenPlatforms.tt && !ttSettings.privacy_level) { showToast('Select a TikTok privacy status'); return; }
    if (regenPlatforms.tt && ttDisclosureIncomplete(ttSettings)) { showToast('Select "Your brand" or "Branded content" for the TikTok commercial content disclosure'); return; }
    setRegeneratingResultIds(prev => new Set([...prev, item.id]));
    const bh = brandHub;
    try {
      if (item.postId) {
        // NON-FATAL, matching what this was. The pre-migration call was
        // `await supabase.from('posts').update(...)` with no error check, so a
        // failure here never stopped the regeneration; patchPost throws, which
        // would have turned a silent miss into "Regeneration failed" with
        // nothing attempted.
        //
        // Worth knowing rather than hiding: if this misses, the old post stays
        // pending and the regenerated one is added alongside it — two live
        // rows. That was true before this phase too. Preserved rather than
        // changed here, on the same standard as the generate-post overwrite.
        try {
          // Replaced by the regenerated post — thrown away, not failed.
          await patchPost(item.postId, { status: 'discarded' });
        } catch (markErr) {
          console.error('[Regenerate] marking the previous post failed:', markErr?.message || markErr);
        }
      }

      let replacement;

      if (item.post_type === 'brand_photo') {
        const res = await clientScopedFetch(`${API_BASE}/api/generate-brand-photo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId:            stripeCustomerId,
            logoUrl:               selectedBrandPhotoLogo === 'main' ? brandLogoUrl : selectedBrandPhotoLogo,
            aspect_ratio:          aspectRatio,
            bg_prompt:             item.bg_prompt || '',
            styles:                brandPhotoStyleOff ? [] : brandPhotoSelectedStyles,
            logo_size:             brandPhotoLogoSize,
            logo_position:         brandPhotoLogoPos,
            hashtag_sets:          bh.hashtagSets || [],
            image_style:           bh.falStyleKeywords || '',
            image_mood:            bh.lightingMood || [],
            image_keywords:        bh.imgBrandKeywords || [],
            image_negative_prompt: bh.negativePromptAdditions || '',
          }),
        });
        const data = await res.json();
        if (res.status === 403 && data.error === 'cap_reached') {
          throw new Error(aiImageLimitMessage(data));
        }
        if (!res.ok) throw new Error(data.error || 'Regeneration failed');
        replacement = {
          id:        item.id,
          post_type: 'brand_photo',
          bg_prompt: item.bg_prompt,
          imageUrl:  data.imageUrl  || null,
          caption:   data.caption   || '',
          hashtags:  item.hashtags,
          postId:    data.postId,
        };
      } else {
        if (gbpBlocked(platforms)) throw new Error('gbp_settings_incomplete');
        const platformList = [
          ...(platforms.ig ? ['instagram'] : []),
          ...(platforms.fb ? ['facebook'] : []),
          ...(platforms.tt ? ['tiktok'] : []),
          ...(platforms.gb ? ['google_business'] : []),
        ];
        const ttSettingsPayload = platforms.tt ? ttSettings : null;
        const pickedStyle = selectedStyles[Math.floor(Math.random() * selectedStyles.length)] || 'warm';
        const styleLabel  = IG_STYLES.find(s => s.id === pickedStyle)?.label || '';
        const res = await clientScopedFetch(`${API_BASE}/api/generate-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId:              stripeCustomerId,
            topic:                   item.topic || prompt.trim() || 'showcase the brand',
            bizName:                 bh.bizName,
            tagline:                 bh.tagline,
            category:                bh.category,
            tones:                   bh.tones,
            emojiPref:               bh.emojiPref || 'Minimal',
            primaryColour:           bh.primaryColour,
            secondaryColour:         bh.secondaryColour,
            accentColour:            bh.accentColour,
            logoUrl:                 bh.logoUrl,
            visualStyles:            bh.visualStyles,
            lightingMood:            bh.lightingMood,
            imgBrandKeywords:        bh.imgBrandKeywords,
            styles:                  selectedStyles,
            pickedStyle,
            platforms:               platformList,
            tiktok_settings:         ttSettingsPayload,
            gbp_settings:            gbpPayloadFor(platforms),
            falStyleKeywords:        styleOff ? '' : (bh.falStyleKeywords || styleLabel),
            negativePromptAdditions: bh.negativePromptAdditions,
            keyProducts:             bh.keyProducts,
            upcomingPromos:          bh.upcomingPromos,
            activePromotions:        [],
            avoidTopics:             bh.avoidTopics,
            hashtagSets:             bh.hashtagSets,
            apply_logo:              applyLogo,
            // Position and size were missing here, so a regenerated image always
            // got the default bottom-right logo whatever the composer said. Same
            // source as apply_logo above: the composer's current settings.
            logo_position:           logoPos,
            logo_size:               logoSize,
            aspect_ratio:            aspectRatio,
            // A post made from a Photo Library photo regenerates on that same
            // photo (fresh caption) instead of swapping in an unrelated AI image.
            ...(item.sourcePhoto ? {
              brand_photo_url: item.sourcePhoto.url,
              source_photo_id: item.sourcePhoto.id,
              skip_image_gen:  true,
            } : {}),
          }),
        });
        const data = await res.json();
        if (res.status === 403 && data.error === 'cap_reached') {
          setCapModalOpen(true);
          throw new Error('cap_reached');
        }
        if (!res.ok) throw new Error(data.error || 'Regeneration failed');
        // A new image, so any text overlay has to be re-applied. The card
        // previews the composer's current text on it straight away.
        replacement = {
          id:             item.id,
          topic:          item.topic,
          sourcePhoto:    item.sourcePhoto || null,
          imageUrl:       data.post?.image_url || null,
          baseImageUrl:   data.post?.image_url || null,
          appliedOverlay: null,
          overlaySkip:    !!item.overlaySkip,
          caption:        data.post?.caption   || '',
          hashtags:       data.post?.hashtags  || '',
          postId:         data.post?.id,
        };
      }

      // The regenerated post is a brand-new row created backend-side, so the
      // story flags default to false — re-apply the composer's current selection.
      // Brand Photo has its own isolated picker; everything else uses the shared
      // Build/Video selection.
      const applyPlatforms = item.post_type === 'brand_photo' ? brandPhotoPlatforms : platforms;
      if (replacement.postId) {
        // Also non-fatal, and for a sharper reason than the mark above: the
        // replacement post ALREADY EXISTS by this line. Letting this throw
        // would skip the setGeneratedResults below, leaving the user looking at
        // the old card while the new row sits in the queue behind it.
        try {
          await patchPost(replacement.postId, {
            is_ig_story:     applyPlatforms.igStory,
            is_fb_story:     applyPlatforms.fbStory,
            tiktok_settings: applyPlatforms.tt ? ttSettings : null,
            gbp_settings:    gbpPayloadFor(applyPlatforms),
            // /api/generate-brand-photo is called above without platforms, so
            // the new row defaulted to Instagram + Facebook and quietly lost
            // TikTok and Google. The Build path sends its list on the generate
            // call itself; brand photos get theirs here.
            ...(item.post_type === 'brand_photo' ? { platforms: [
              ...(applyPlatforms.ig ? ['instagram'] : []),
              ...(applyPlatforms.fb ? ['facebook'] : []),
              ...(applyPlatforms.tt ? ['tiktok'] : []),
              ...(applyPlatforms.gb ? ['google_business'] : []),
            ] } : {}),
          });
        } catch (flagErr) {
          console.error('[Regenerate] applying story flags failed:', flagErr?.message || flagErr);
        }
      }

      setGeneratedResults(prev => prev.map(r => r.id === item.id ? replacement : r));
      // The replacement is a new post row, so it counts as a new TikTok post.
      if (replacement.postId && applyPlatforms.tt) resetTtPrivacy();
    } catch (err) {
      console.error('Regenerate error:', err.message);
      if (err.message !== 'cap_reached' && err.message !== 'gbp_settings_incomplete') showToast('Regeneration failed — try again');
    } finally {
      setRegeneratingResultIds(prev => { const n = new Set(prev); n.delete(item.id); return n; });
    }
  };

  const handleTemplateSelect = (t) => {
    setSelectedTemplate(t.label);
    setPrompt(t.text);
    setShowTemplates(false);
  };

  // The storage prefix is gone from here entirely. It was built from `cid` —
  // the signed-in admin — so an admin adding a logo variant while viewing a
  // client wrote it into their OWN logos/<admin>/variants/ folder while the
  // client's composer showed it. The server builds the path from the resolved
  // client now, and the local shadow of `cid` that made this easy to miss goes
  // with it.
  const handleUploadLogoVariant = async (file) => {
    // `cid` is kept purely as the "signed in yet" guard it is everywhere else
    // in this panel — the old local `const cid = clientId || storedClientId`
    // shadow here was building a storage path, and that is what moved server-
    // side. Without this the upload fires before the session resolves and the
    // user gets an error toast where they previously got a silent no-op.
    if (!cid || !file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await clientScopedFetch(`${BACKEND}/api/brand-hub/logo-variants`, {
        method: 'POST', body: form,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setBrandPhotoLogoVariants(prev => [...prev, { url: body.url, path: body.path }]);
      setSelectedBrandPhotoLogo(body.url);
    } catch (err) {
      console.error('[LogoVariant] upload failed:', err?.message || err);
      showToast('Logo upload failed');
    }
  };

  // The path still travels — it is how the object is named — but the route
  // refuses any path outside the RESOLVED client's variants/ prefix, so a path
  // for someone else's object cannot be deleted through here.
  const handleRemoveLogoVariant = async (variant) => {
    try {
      const res = await clientScopedFetch(`${BACKEND}/api/brand-hub/logo-variants`, {
        method:  'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ path: variant.path }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setBrandPhotoLogoVariants(prev => prev.filter(v => v.path !== variant.path));
      setSelectedBrandPhotoLogo(sel => sel === variant.url ? 'main' : sel);
    } catch (err) {
      console.error('[LogoVariant] delete failed:', err?.message || err);
      showToast('Could not remove that logo');
    }
  };

  const handleGenerateBrandPhoto = async () => {
    const platformList = [
      ...(brandPhotoPlatforms.ig ? ['instagram'] : []),
      ...(brandPhotoPlatforms.fb ? ['facebook'] : []),
      ...(brandPhotoPlatforms.tt ? ['tiktok'] : []),
      ...(brandPhotoPlatforms.gb ? ['google_business'] : []),
    ];
    if (platformList.length === 0 && !brandPhotoPlatforms.igStory && !brandPhotoPlatforms.fbStory) {
      showToast('Select at least one platform');
      return;
    }
    if (brandPhotoPlatforms.tt && !ttSettings.privacy_level) { showToast('Select a TikTok privacy status'); return; }
    if (brandPhotoPlatforms.tt && ttDisclosureIncomplete(ttSettings)) { showToast('Select "Your brand" or "Branded content" for the TikTok commercial content disclosure'); return; }
    if (gbpBlocked(brandPhotoPlatforms)) return;
    setGeneratingBrandPhoto(true);
    try {
      let baseImageUrl = null;
      if (brandPhotoBase) {
        baseImageUrl = await scratchUpload(brandPhotoBase, 'base');
      }
      const res = await clientScopedFetch(`${API_BASE}/api/generate-brand-photo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId:   stripeCustomerId,
          logoUrl:      selectedBrandPhotoLogo === 'main' ? brandLogoUrl : selectedBrandPhotoLogo,
          aspect_ratio: aspectRatio,
          base_image_url: baseImageUrl,
          bg_prompt:    brandPhotoBgPrompt,
          styles:       brandPhotoStyleOff ? [] : brandPhotoSelectedStyles,
          logo_size:    brandPhotoLogoSize,
          logo_position: brandPhotoLogoPos,
          hashtag_sets: brandHub.hashtagSets || [],
          platforms:    platformList,
          tiktok_settings: brandPhotoPlatforms.tt ? ttSettings : null,
          gbp_settings: gbpPayloadFor(brandPhotoPlatforms),
          is_ig_story:  brandPhotoPlatforms.igStory,
          is_fb_story:  brandPhotoPlatforms.fbStory,
        }),
      });
      const data = await res.json();
      if (res.status === 403 && data.error === 'cap_reached') {
        showToast(`${aiImageLimitMessage(data)} — top up or wait until next month`);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      const hashtagStr = Array.isArray(brandHub.hashtagSets) && brandHub.hashtagSets.length
        ? brandHub.hashtagSets.map(t => t.startsWith('#') ? t : `#${t}`).join(' ')
        : '';
      const autoScheduledAt = await maybeAutoSchedule(data.postId, brandPhotoPlatforms);
      setGeneratedResults(prev => [{
        autoScheduledAt,
        id:          crypto.randomUUID(),
        // post_type is what publish/regenerate logic keys off, so it must be set
        // at creation time — otherwise a first-time Regenerate misroutes through
        // the generate-post path instead of generate-brand-photo.
        post_type:   'brand_photo',
        // Display-only marker so the Review & Approve preview reads this item's
        // story-frame / caption styling from brandPhotoPlatforms.
        previewKind: 'brand_photo',
        imageUrl:    data.imageUrl,
        caption:     data.caption || '',
        hashtags:    hashtagStr,
        postId:      data.postId,
        // Snapshot composer state for a stable TikTok consent checkbox.
        platforms:   { ...brandPhotoPlatforms },
        ttSettings:  brandPhotoPlatforms.tt ? ttSettings : null,
      }, ...prev]);
      if (brandPhotoPlatforms.tt) resetTtPrivacy();
      setBrandPhotoBase(null);
      if (brandPhotoFileRef.current) brandPhotoFileRef.current.value = '';
      showToast(autoScheduledAt
        ? `✓ Brand photo generated — publishes ${formatSlot(autoScheduledAt)}`
        : (baseImageUrl ? '✓ Brand photo generated from your image' : '✓ Brand photo generated'));
    } catch {
      showToast('Generation failed — please try again');
    } finally {
      setGeneratingBrandPhoto(false);
    }
  };

  // ─── Carousel: shared FAL slide request ─────────────────────────
  const makeCarouselUid = () => `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const buildCarouselSlideBody = (slidePrompt) => {
    const bh = brandHub;
    const scene = (slidePrompt || '').trim();
    const composedPrompt = scene
      ? `Scene: ${scene}. Render this scene exactly as described, in a photorealistic, realistic photography style.`
      : '';
    return {
      customerId:           stripeCustomerId,
      prompt:               composedPrompt,
      aspect_ratio:         carouselAspect,
      image_style:          bh.falStyleKeywords || '',
      image_mood:           bh.lightingMood || [],
      image_keywords:       bh.imgBrandKeywords || [],
      image_negative_prompt: bh.negativePromptAdditions || '',
      logo_url:             carouselLogoOn ? (bh.logoUrl || brandLogoUrl || '') : '',
      logo_size:            carouselLogoSize,
      logo_position:        carouselLogoPos,
    };
  };

  const fetchCarouselSlide = async (slidePrompt) => {
    const res = await clientScopedFetch(`${API_BASE}/api/generate-carousel-slide`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(buildCarouselSlideBody(slidePrompt)),
    });
    const data = await res.json();
    if (data.errorCode === 'FAL_KEY_INVALID') {
      setApiError('FAL_KEY_INVALID');
      throw new Error('FAL_KEY_INVALID');
    }
    if (res.status === 403 && data.error === 'cap_reached') {
      const capErr = new Error(aiImageLimitMessage(data));
      capErr.isCapReached = true;
      throw capErr;
    }
    if (!res.ok || !data.imageUrl) throw new Error(data.error || 'Slide generation failed');
    return data.imageUrl;
  };

  const handleCarouselGenerate = async () => {
    if (!cid || carouselGenerating) return;
    const n = Math.max(1, Math.min(6, carouselCount));
    if (carouselMode !== 'generate') return;
    if (!carouselBgPrompt.trim()) {
      showToast('Add a background prompt first');
      return;
    }
    setCarouselGenerating(true);
    try {
      const indexes = Array.from({ length: n }, (_, i) => i);
      // allSettled, not all: each slide is one AI image against the monthly
      // limit, and the backend now counts parallel slides properly. When the
      // limit lands partway, the slides that WERE made (and counted) are kept
      // instead of being thrown away with a "failed" toast.
      const settled = await Promise.allSettled(indexes.map(async (i) => {
        const isCover = i === 0 && carouselCoverLock;
        const slidePrompt = (isCover && carouselHookPrompt.trim()) || carouselBgPrompt;
        const imageUrl = await fetchCarouselSlide(slidePrompt);
        return { id: makeCarouselUid(), imageUrl, source: 'generated', text: '', caption: '', photoId: null };
      }));
      const newSlides = settled.filter(r => r.status === 'fulfilled').map(r => r.value);
      const errors    = settled.filter(r => r.status === 'rejected').map(r => r.reason);
      if (newSlides.length === 0) throw errors[0];
      errors.forEach(e => console.error('[Carousel] slide failed:', e?.message));
      setCarouselSlides(newSlides);
      setCarouselPreviewIdx(newSlides.length - 1);
      const hitLimit = errors.some(e => e?.isCapReached);
      showToast(errors.length === 0
        ? `✓ Generated ${newSlides.length} slide${newSlides.length === 1 ? '' : 's'}`
        : `Made ${newSlides.length} of ${n} slides — ${hitLimit ? 'AI image limit reached (no paid images left)' : `${errors.length} failed`}`);
    } catch (err) {
      console.error('[Carousel] generate failed:', err.message);
      if (err.isCapReached) showToast(err.message);
      else if (err.message !== 'FAL_KEY_INVALID') showToast('Generation failed — please try again');
    } finally {
      setCarouselGenerating(false);
    }
  };

  const handleCarouselRegenSlide = async (idx) => {
    const slide = carouselSlides[idx];
    if (!slide || carouselRegenIds.has(slide.id)) return;
    if (slide.source !== 'generated') {
      showToast('Only generated slides can be regenerated');
      return;
    }
    setCarouselRegenIds(prev => new Set([...prev, slide.id]));
    try {
      const isCover = idx === 0 && carouselCoverLock;
      const slidePrompt = (isCover && carouselHookPrompt.trim()) || carouselBgPrompt;
      if (!slidePrompt) { showToast('Add a prompt first'); return; }
      const imageUrl = await fetchCarouselSlide(slidePrompt);
      setCarouselSlides(prev => prev.map((s, i) => i === idx ? { ...s, imageUrl } : s));
    } catch (err) {
      console.error('[Carousel] regen failed:', err.message);
      if (err.message !== 'FAL_KEY_INVALID') showToast('Regeneration failed');
    } finally {
      setCarouselRegenIds(prev => { const n = new Set(prev); n.delete(slide.id); return n; });
    }
  };

  const handleCarouselUpload = async (files) => {
    if (!cid || !files || files.length === 0) return;
    const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];
    const room = Math.max(0, 6 - carouselSlides.length);
    if (room === 0) { showToast('Carousel already has 6 slides'); return; }
    // Convert iPhone HEIC/HEIF → JPEG before the type filter, otherwise these
    // photos would be silently rejected as unsupported.
    const converted = await Promise.all(Array.from(files).map(ensureRenderableImage));
    const incoming = converted.filter(f => ACCEPTED.includes(f.type)).slice(0, room);
    if (incoming.length === 0) { showToast('Only JPG, PNG, or WEBP'); return; }
    const uploaded = [];
    for (let i = 0; i < incoming.length; i++) {
      const file = incoming[i];
      let slideUrl;
      try {
        slideUrl = await scratchUpload(file, 'carousel');
      } catch (err) {
        // Per-file skip preserved: the loop reports how many landed.
        console.error('[Carousel] upload failed:', err?.message || err);
        continue;
      }
      uploaded.push({ id: makeCarouselUid(), imageUrl: slideUrl, source: 'uploaded', text: '', caption: '', photoId: null });
    }
    if (uploaded.length === 0) { showToast('Upload failed — try again'); return; }
    setCarouselSlides(prev => {
      const merged = [...prev, ...uploaded].slice(0, 6);
      setCarouselPreviewIdx(merged.length - 1);
      return merged;
    });
    showToast(`✓ Uploaded ${uploaded.length} slide${uploaded.length === 1 ? '' : 's'}`);
  };

  const handleCarouselRemoveSlide = (idx) => {
    setCarouselSlides(prev => {
      const next = prev.filter((_, i) => i !== idx);
      setCarouselPreviewIdx(p => Math.max(0, Math.min(p, next.length - 1)));
      return next;
    });
  };

  const handleCarouselReorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    setCarouselSlides(prev => {
      if (fromIdx >= prev.length || toIdx >= prev.length) return prev;
      const next = prev.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      setCarouselPreviewIdx(p => {
        if (p === fromIdx) return toIdx;
        if (fromIdx < p && toIdx >= p) return p - 1;
        if (fromIdx > p && toIdx <= p) return p + 1;
        return p;
      });
      return next;
    });
  };

  // A slide's text blocks. The first block's fields live on the slide itself,
  // where the bulk text generator, the presets' style back-fill and the
  // library's defaults write them; any further blocks are in `moreText`.
  const slideTextBlocks = (s) => {
    const first = {};
    SLIDE_TEXT_FIELDS.forEach(k => { if (s[k] !== undefined) first[k] = s[k]; });
    return [first, ...(s.moreText || [])];
  };
  // Applies `update` (a function from a slide's block list to the next, as
  // TextOverlayControls' onChange gives) to slide idx.
  const updateSlideTextBlocks = (idx, update) => {
    setCarouselSlides(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const [first = {}, ...more] = update(slideTextBlocks(s));
      const next = { ...s };
      SLIDE_TEXT_FIELDS.forEach(k => { delete next[k]; });
      return { ...next, ...first, moreText: more };
    }));
  };

  const handleCarouselGenerateBulkText = async () => {
    if (!carouselBulkTextPrompt.trim()) { showToast('Enter a prompt for slide text'); return; }
    if (carouselSlides.length === 0)    { showToast('Generate or upload slides first'); return; }
    setCarouselBulkTextLoading(true);
    try {
      const bh = brandHub;
      const res = await clientScopedFetch(`${API_BASE}/api/generate-slide-texts`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: carouselBulkTextPrompt.trim(),
          n:      carouselSlides.length,
          brandContext: { tones: bh.tones || [], voiceNotes: '', audience: '' },
        }),
      });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data.texts)) throw new Error(data.error || 'Failed');
      setCarouselSlides(prev => prev.map((s, i) => ({ ...s, text: data.texts[i] ?? s.text })));
      showToast('✓ Slide text generated');
    } catch (err) {
      console.error('[Carousel] bulk text failed:', err.message);
      showToast('Text generation failed');
    } finally {
      setCarouselBulkTextLoading(false);
    }
  };

  const handleCarouselPreset = (preset) => {
    if (selectedPreset === preset.id) { setSelectedPreset(null); return; } // deselect, keep current settings
    setSelectedPreset(preset.id);
    setCarouselAspect(preset.aspectRatio);
    setCarouselCount(preset.slideCount);
    setCarouselHookPrompt(preset.hookPrompt);
  };

  // Back-fill the active preset's text defaults onto newly added slides (generate / upload / library),
  // normalized to the lowercase tokens the slide model + compositor expect. Leaves edited slides alone.
  useEffect(() => {
    if (!selectedPreset) return;
    const preset = CAROUSEL_PRESETS.find(p => p.id === selectedPreset);
    if (!preset) return;
    const d = preset.defaultText;
    const defaults = {
      textPosition: d.position,
      textSize:     d.size.toLowerCase(),   // 'M' -> 'm'
      textAlign:    d.align,
      textStyle:    d.style.toLowerCase(),  // 'Backed' -> 'backed'
      textColor:    d.color,
    };
    setCarouselSlides(prev => {
      let changed = false;
      const next = prev.map(s => {
        if (s.textPosition !== undefined) return s; // already styled or user-edited
        changed = true;
        return { ...s, ...defaults };
      });
      return changed ? next : prev;
    });
  }, [carouselSlides.length, selectedPreset]);

  const handleCarouselDiscard = () => {
    setCarouselSlides([]);
    setCarouselPreviewIdx(0);
    setCarouselBulkTextPrompt('');
    setCarouselPostCaption('');
    setCarouselPostHashtags('');
  };

  const uploadCompositedSlide = async (blob, idx) => {
    // A Blob has no filename; naming it here keeps the slide index in the
    // stored object the way the old path did.
    const file = new File([blob], `slide-${idx}.jpg`, { type: 'image/jpeg' });
    return scratchUpload(file, 'carousel-composited');
  };

  // The composer's overlay as it would be baked (null = no text), and which
  // result cards it governs: Generate Post results only — Brand Photo and Video
  // results carry a post_type and are left alone. Each card can opt out
  // (item.overlaySkip, its "Text" checkbox), which makes its target "no text".
  // A card is "pending" while its target differs from what's baked into its
  // stored image.
  const liveOverlay = liveOverlayBlocks(textOverlay);
  const isOverlayTarget  = (item) => !item.post_type && !!(item.baseImageUrl || item.imageUrl);
  const overlayTargetFor = (item) => (item.overlaySkip ? null : liveOverlay);
  // What a card previews: its target, but with blocks that have no text kept
  // in place, so a block's index on the card matches the controls'.
  const overlayPreviewFor = (item) => (overlayTargetFor(item) ? trimOverlayBlocks(textOverlay) : null);
  const moveOverlayBlock = (i, x, y) => setTextOverlay(bs => bs.map((b, j) => (j === i ? { ...b, x, y } : b)));
  const resizeOverlayBlockAt = (i, fields) => setTextOverlay(bs => bs.map((b, j) => (j === i ? { ...b, ...fields } : b)));
  const isOverlayPending = (item) => isOverlayTarget(item)
    && textOverlayKey(overlayTargetFor(item)) !== textOverlayKey(item.appliedOverlay);

  // Apply — the confirm step. Bakes each pending card's target overlay onto its
  // BASE image (or, for a cleared/skipped card, restores the base) and writes
  // it to the post row, along with the base and the settings so the Scheduler
  // can edit the text later without stacking. The card only switches to the
  // baked image once that write lands, so what's on screen is what publishes.
  // Works on posts auto-approve already scheduled too: it's an explicit edit.
  const handleApplyTextOverlay = async (items) => {
    const targets = items.filter(item =>
      isOverlayPending(item) &&
      !applyingOverlayIds.has(item.id) &&
      !regeneratingResultIds.has(item.id) &&
      !processingResultIds.has(item.id) &&
      !publishingResultIds.has(item.id));
    if (targets.length === 0) return;
    const ids = targets.map(t => t.id);
    setApplyingOverlayIds(prev => new Set([...prev, ...ids]));
    const outcomes = await Promise.all(targets.map(async (item, i) => {
      const overlay = overlayTargetFor(item);
      try {
        if (!item.postId) throw new Error('post has no server record');
        const base = item.baseImageUrl || item.imageUrl;
        const url  = overlay ? await applyTextOverlay(base, overlay, i) : base;
        await patchPost(item.postId, {
          image_url:              url,
          overlay_base_image_url: overlay ? base : null,
          text_overlay:           overlay,
        });
        setGeneratedResults(prev => prev.map(r => r.id === item.id
          ? { ...r, imageUrl: url, baseImageUrl: base, appliedOverlay: overlay }
          : r));
        return true;
      } catch (err) {
        console.error('[TextOverlay] apply failed for', item.postId, err?.message || err);
        return false;
      }
    }));
    setApplyingOverlayIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
    const failed = outcomes.filter(ok => !ok).length;
    const adding   = targets.filter(t => overlayTargetFor(t)).length;
    const verb     = adding === targets.length ? 'applied to' : adding === 0 ? 'removed from' : 'updated on';
    if (failed === 0) {
      showToast(`✓ Text ${verb} ${targets.length === 1 ? 'the post' : `${targets.length} posts`}`);
    } else {
      showToast(targets.length === 1
        ? "Couldn't update the text — try again"
        : `Text ${verb} ${targets.length - failed} of ${targets.length} posts — ${failed} failed, try again`);
    }
  };

  const handleCarouselSendToQueue = async () => {
    if (!cid || carouselSubmitting) return;
    if (carouselSlides.length === 0) { showToast('Add slides first'); return; }
    const platformList = [
      ...(carouselPlatforms.ig ? ['instagram'] : []),
      ...(carouselPlatforms.fb ? ['facebook'] : []),
      ...(carouselPlatforms.tt ? ['tiktok'] : []),
      ...(carouselPlatforms.gb ? ['google_business'] : []),
    ];
    if (platformList.length === 0) { showToast('Select at least one platform'); return; }
    if (carouselPlatforms.tt && !ttSettings.privacy_level) { showToast('Select a TikTok privacy status'); return; }
    if (carouselPlatforms.tt && ttDisclosureIncomplete(ttSettings)) { showToast('Select "Your brand" or "Branded content" for the TikTok commercial content disclosure'); return; }
    if (gbpBlocked(carouselPlatforms)) return;
    setCarouselSubmitting(true);
    try {
      let finalUrls = await Promise.all(carouselSlides.map(async (s, i) => {
        const blocks = liveOverlayBlocks(slideTextBlocks(s));
        if (!blocks) return s.imageUrl;
        try {
          const blob = await compositeTextOntoImage(s.imageUrl, blocks);
          return await uploadCompositedSlide(blob, i);
        } catch (err) {
          console.error('[Carousel] composite slide', i, 'failed:', err.message);
          return s.imageUrl;
        }
      }));

      const postCaption  = carouselPostCaption.trim();
      const postHashtags = carouselPostHashtags
        .split(/\s+/)
        .filter(Boolean)
        .map(t => (t.startsWith('#') ? t : `#${t}`));
      // Snapshot TikTok settings onto the post at creation time so the downstream
      // consent checkbox / publish path use this carousel's own settings.
      const ttSettingsPayload = carouselPlatforms.tt ? ttSettings : null;

      // Set when auto-approve schedules the carousel at creation, so the
      // closing toast can name the slot instead of pointing at a queue the
      // post never entered.
      let autoScheduledAt = null;

      // ONE PATH NOW. This used to fork on slide count: 2+ went to
      // /api/upload-post, exactly 1 was inserted straight from the browser,
      // and the two branches then repeated the same auto-schedule and
      // used-flag work verbatim. /api/upload-post accepts a single image as of
      // Phase 7, so the fork and the duplication both go.
      //
      // The only thing that still varies is the media type, which the route
      // uses to decide whether media_items is stored.
      const res = await clientScopedFetch(`${BACKEND}/api/upload-post`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mediaType:  finalUrls.length === 1 ? 'image' : 'carousel',
          mediaItems: finalUrls,
          caption:    postCaption,
          hashtags:   postHashtags,
          platforms:  platformList,
          tiktok_settings: ttSettingsPayload,
          gbp_settings: gbpPayloadFor(carouselPlatforms),
          status:     'pending',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Send to queue failed');
      autoScheduledAt = await maybeAutoSchedule(data.postId, carouselPlatforms);
      // NON-FATAL, deliberately. By this line the post exists and is possibly
      // already scheduled; letting a used-flag failure reach the catch below
      // would show "Send to queue failed" for a carousel that succeeded, and
      // skip the reset and the navigate. The pre-migration code ignored this
      // update's error entirely, so this preserves that outcome while at least
      // recording it.
      try {
        await markPhotosUsed(carouselSlides.filter(s => s.photoId).map(s => s.photoId));
      } catch (usedErr) {
        console.error('[Carousel] marking library photos used failed:', usedErr?.message || usedErr);
      }

      if (carouselPlatforms.tt) resetTtPrivacy();
      setCarouselSlides([]);
      setCarouselPreviewIdx(0);
      setCarouselHookPrompt('');
      setCarouselBgPrompt('');
      setCarouselBulkTextPrompt('');
      setCarouselPostCaption('');
      setCarouselPostHashtags('');
      showToast(autoScheduledAt
        ? `Carousel scheduled — publishes ${formatSlot(autoScheduledAt)}`
        : 'Carousel sent to Approve queue');
      if (onNavigate) onNavigate('approval');
    } catch (err) {
      console.error('[Carousel] send-to-queue failed:', err.message);
      showToast('Send to queue failed — try again');
    } finally {
      setCarouselSubmitting(false);
    }
  };

  if (apiError === 'FAL_KEY_INVALID') {
    return <GlitchRobotCard errorCode="FAL_KEY_INVALID" />;
  }

  return (
    <div className={`ig-wrap${activeTab === 'carousel' ? ' is-carousel' : ''}`}>

      {/* LEFT COLUMN — controls */}
      <div className="ig-left">

        {/* Tab bar */}
        <div style={{ display:'flex', gap:4, marginBottom:16, background:'var(--bg-card2)', borderRadius:8, padding:4 }}>
          {[{id:'generate', label:'Generate Post'}, {id:'brand-photo', label:'Brand Photo'}, {id:'carousel', label:'Carousel'}, {id:'video', label:'Video'}].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                flex:1, padding:'7px 8px', border:'none', borderRadius:6, fontFamily:'var(--font)',
                fontSize:12, fontWeight:600, cursor:'pointer', transition:'all 0.15s',
                background: activeTab === t.id ? 'var(--accent)' : 'transparent',
                color:      activeTab === t.id ? '#0d0f14' : 'var(--text-secondary)',
              }}
            >{t.label}</button>
          ))}
        </div>

        {/* AUTO-APPROVE SWITCH — brand_profile.auto_approve.
            Rendered only on Generate Post, but the setting is account-wide: all
            four tabs above create posts through the same pending → Review &
            Approve queue, so this governs every one of them. The tooltip and
            the disclaimer both say so, since the placement alone implies
            otherwise. */}
        {activeTab === 'generate' && (
          <div
            className="ig-admin-toggle"
            style={{ marginBottom: 16, cursor: autoApproveSaving ? 'wait' : 'pointer' }}
            onClick={requestToggleAutoApprove}
            role="switch"
            aria-checked={autoApprove}
            aria-label="Auto-approve posts"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); requestToggleAutoApprove(); } }}
          >
            <span className="ig-admin-toggle-label" style={{ flex: 'none', whiteSpace: 'nowrap' }}>
              Auto-approve posts
            </span>
            <Tip text="When on, new posts skip Review & Approve and publish to your accounts on their own. This applies to your whole account, not just this tab. TikTok posts still need your approval." below />
            <span style={{ flex: 1 }} />
            <div className={`ig-toggle-track${autoApprove ? ' on' : ''}`} style={{ opacity: autoApproveSaving ? 0.5 : 1 }}>
              <div className="ig-toggle-thumb" />
            </div>
          </div>
        )}

        {/* TAB: Video */}
        {activeTab === 'video' && (
          <div className="ig-card" style={{ flex:1 }}>
            <div className="ig-card-title">✦ Video Post</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12, lineHeight:1.4 }}>
              Turn a finished video render into a post. Pick a video, fine-tune the caption, choose where it publishes, then send it to Review &amp; Approve.
            </div>

            <div className="ig-label" style={{ marginBottom:8 }}>Your finished videos</div>
            {videoLoading ? (
              <div style={{ fontSize:12, color:'var(--text-muted)', padding:'20px 0', textAlign:'center' }}>Loading your videos…</div>
            ) : videoProjects.length === 0 ? (
              <div style={{
                fontSize:12, color:'var(--text-muted)', padding:'20px 12px', textAlign:'center',
                border:'1px dashed var(--border)', borderRadius:10, lineHeight:1.5,
              }}>
                No finished videos yet. Render a video in the Video Editor and it will appear here.
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:8, marginBottom:14 }}>
                {videoProjects.map(v => {
                  const selected = selectedVideoId === v.id;
                  const len = (v.trim_end != null && v.trim_start != null) ? Math.max(0, v.trim_end - v.trim_start) : null;
                  const dur = len != null ? `${Math.floor(len / 60)}:${String(Math.round(len % 60)).padStart(2, '0')}` : null;
                  return (
                    <div
                      key={v.id}
                      onClick={() => { if (!videoCaptionLoading) handleSelectVideoProject(v); }}
                      style={{
                        cursor: videoCaptionLoading ? 'wait' : 'pointer',
                        border:'2px solid', borderColor: selected ? 'var(--accent)' : 'var(--border)',
                        borderRadius:10, overflow:'hidden', background:'var(--bg-surface)', transition:'border-color 0.15s',
                      }}
                    >
                      <div style={{ position:'relative', width:'100%', aspectRatio:'16/9', background:'#000' }}>
                        {v.thumbnail_url
                          ? <img src={v.thumbnail_url} alt={v.title || 'Video'} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.6)', fontSize:18 }}>▶</div>
                        }
                        {dur && (
                          <span style={{ position:'absolute', bottom:5, right:5, fontSize:10, fontWeight:700, color:'#fff', background:'rgba(0,0,0,0.65)', padding:'1px 6px', borderRadius:5 }}>{dur}</span>
                        )}
                        {selected && (
                          <span style={{ position:'absolute', top:5, left:5, fontSize:10, fontWeight:700, color:'#0d0f14', background:'var(--accent)', padding:'1px 7px', borderRadius:5 }}>✓ Selected</span>
                        )}
                      </div>
                      <div style={{ padding:'7px 9px', fontSize:11, fontWeight:600, color:'var(--text-primary)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {v.title || 'Untitled Video'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedVideoId && (
              <>
                {/* Platform picker — Instagram / Facebook / TikTok */}
                <div className="ig-label" style={{ marginTop:4 }}>Platforms</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
                  {[
                    { key:'ig', label:'Instagram' },
                    { key:'fb', label:'Facebook'  },
                    { key:'tt', label:'TikTok'    },
                    ...(gbpConnected ? [{ key:'gb', label:'Google' }] : []),
                  ].map(opt => (
                    <div
                      key={opt.key}
                      onClick={() => setPlatforms(p => ({ ...p, [opt.key]: !p[opt.key] }))}
                      style={{
                        flex:'1 1 calc(33% - 6px)', display:'flex', alignItems:'center', justifyContent:'center',
                        padding:'9px 8px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
                        border:'1px solid', transition:'all 0.15s',
                        background:   platforms[opt.key] ? 'var(--accent-dim)' : 'var(--bg-surface)',
                        borderColor:  platforms[opt.key] ? 'var(--accent-border)' : 'var(--border)',
                        color:        platforms[opt.key] ? 'var(--accent)' : 'var(--text-muted)',
                      }}
                    >{opt.label}</div>
                  ))}
                </div>

                {/* TikTok Direct Post settings — required whenever TikTok is a target */}
                {platforms.tt && (
                  <TikTokSettingsPanel
                    clientId={cid}
                    value={ttSettings}
                    onChange={updateTtSettings}
                    isVideo
                    previewImageUrl={videoProjects.find(p => p.id === selectedVideoId)?.thumbnail_url || null}
                  />
                )}

                {/* Google can't take a video: say so instead of offering options */}
                {platforms.gb && gbpConnected && <GoogleBusinessOptions value={gbpSettings} onChange={setGbpSettings} isVideo />}

                {/* Caption — seeded from Brand Hub on selection, or after Generate Caption */}
                {(selectedVideoId || videoPostId || videoCaptionLoading) && (
                  <>
                    <div className="ig-label" style={{ marginBottom:4 }}>Caption</div>
                    {videoCaptionLoading ? (
                      <div style={{ fontSize:12, color:'var(--text-muted)', padding:'10px 0' }}>Writing a caption…</div>
                    ) : (
                      <>
                        <textarea
                          className="ig-textarea"
                          value={videoCaption}
                          onChange={e => setVideoCaption(e.target.value)}
                          placeholder="Caption…"
                          style={{ minHeight:80 }}
                        />
                        <input
                          className="aq-edit-hashtags"
                          value={videoHashtags}
                          onChange={e => setVideoHashtags(e.target.value)}
                          placeholder="Hashtags…"
                          style={{ width:'100%', margin:'8px 0 4px' }}
                        />
                      </>
                    )}
                  </>
                )}

                {/* Generate Caption (or regenerate once one exists) */}
                {!videoPostId ? (
                  <button
                    className="ig-generate-btn"
                    onClick={handleGenerateVideoCaption}
                    disabled={videoCaptionLoading || (!platforms.ig && !platforms.fb && !platforms.tt && !platforms.gb)}
                  >
                    {videoCaptionLoading ? 'Generating…' : '✦ Generate Caption'}
                  </button>
                ) : (
                  <>
                    <button
                      className="ig-generate-btn"
                      onClick={handleAddVideoPost}
                      disabled={videoCaptionLoading || videoSubmitting || (!platforms.ig && !platforms.fb && !platforms.tt && !platforms.gb)}
                    >
                      {videoSubmitting ? 'Adding…' : '✦ Add to Review & Approve'}
                    </button>
                    <button
                      onClick={handleGenerateVideoCaption}
                      disabled={videoCaptionLoading || videoSubmitting}
                      style={{
                        width:'100%', marginTop:8, background:'transparent', border:'1px solid var(--border)',
                        color:'var(--text-secondary)', borderRadius:8, padding:'8px 12px', fontSize:12,
                        fontWeight:600, cursor: videoCaptionLoading ? 'not-allowed' : 'pointer',
                      }}
                      title="Write a fresh caption"
                    >↺ Regenerate Caption</button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* TAB: Generate Post */}
        {activeTab === 'generate' && (
          <div className="ig-card" style={{ flex:1 }}>
            <div className="ig-card-title">✦ Generate Post</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12, lineHeight:1.4 }}>
              Create ready-to-post social media captions and images based on your brand voice and content pillars.
            </div>

            <div className="ig-label" style={{ marginBottom:4 }}>
              Topic or prompt
              <Tip text="Describe what this post should be about — a theme, product, angle, or the caption you want. Leave blank to let the AI choose based on your brand voice and content pillars." below />
              {selectedBrandPhoto && (
                <span style={{ marginLeft:6, fontSize:10, color:'var(--text-muted)', fontWeight:400 }}>
                  (optional — used for caption only)
                </span>
              )}
            </div>
            <textarea
              className={`ig-textarea${contentMixRatio > 0 ? ' ig-textarea-pulse' : ''}`}
              placeholder={selectedBrandPhoto
                ? "Optional — leave blank for a default caption, or describe the caption you want…"
                : "Describe what this post should be about…"}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
            />

            {/* Count picker */}
            <div className="ig-label" style={{ marginTop:12 }}>Posts to Generate<Tip text="Generate multiple caption+image variations at once." below /></div>
            <div className="ig-batch-opts" style={{ marginBottom:6 }}>
              {(isSelfServe ? [1, 4, 8, 14, 30] : [1, 4, 8, 14, 30, 60]).map(n => (
                <div key={n} className={`ig-batch-opt${genCount === n ? ' selected' : ''}`} onClick={() => setGenCount(n)}>{n}</div>
              ))}
            </div>
            {genCount > 1 && <div className="ig-batch-note">Each post gets a unique composition variation — caption and image.</div>}

            {/* Content Mix slider — high in the card so it's visible without scrolling */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:8, flexWrap:'wrap', minWidth:0 }}>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', whiteSpace:'nowrap', flexShrink:0, display:'inline-flex', alignItems:'center' }}>Content Mix<Tip text="Sets the balance between your real uploaded photos and AI-generated images for this post. Slide left for more of your own photos, right for more AI-generated visuals." /></span>
              <input
                type="range"
                min={0} max={100} step={5}
                value={contentMixRatio}
                onChange={e => handleContentMixChange(Number(e.target.value))}
                style={{ flex:'1 1 120px', minWidth:80, accentColor:'var(--accent)', cursor:'pointer' }}
              />
              <span style={{ fontSize:11, color:'var(--accent)', fontWeight:600, textAlign:'right', flexShrink:0 }}>
                {100 - contentMixRatio}% Real / {contentMixRatio}% Generated
              </span>
            </div>

            {/* Photo Library picker */}
            <div style={{ marginTop:8, marginBottom:6 }}>
              {selectedBrandPhotos.length > 1 ? (
                <div style={{
                  display:'flex', alignItems:'center', gap:10, padding:8,
                  background:'rgba(49,220,186,0.08)', border:'1px solid rgba(49,220,186,0.3)',
                  borderRadius:8,
                }}>
                  <div style={{ display:'flex', flexShrink:0 }}>
                    {selectedBrandPhotos.slice(0, 3).map((p, i) => (
                      <img
                        key={p.id}
                        src={p.url}
                        alt={p.filename}
                        style={{
                          width:36, height:36, objectFit:'cover', borderRadius:6,
                          border:'2px solid #0d0f14',
                          marginLeft: i === 0 ? 0 : -12,
                          zIndex: 3 - i,
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', letterSpacing:'0.04em', textTransform:'uppercase' }}>
                      {selectedBrandPhotos.length} Brand Photos Selected
                    </div>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>
                      {selectedBrandPhotos.length} real post{selectedBrandPhotos.length !== 1 ? 's' : ''} · {Math.max(0, genCount - selectedBrandPhotos.length)} AI-generated
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedBrandPhoto}
                    style={{
                      background:'transparent', border:'1px solid rgba(255,255,255,0.15)',
                      borderRadius:6, color:'var(--text-muted)', fontSize:11, padding:'5px 10px',
                      cursor:'pointer', flexShrink:0,
                    }}
                  >Remove</button>
                </div>
              ) : selectedBrandPhoto ? (
                <div style={{
                  display:'flex', alignItems:'center', gap:10, padding:8,
                  background:'rgba(49,220,186,0.08)', border:'1px solid rgba(49,220,186,0.3)',
                  borderRadius:8,
                }}>
                  <img
                    src={selectedBrandPhoto.url}
                    alt={selectedBrandPhoto.filename}
                    style={{ width:44, height:44, objectFit:'cover', borderRadius:6, flexShrink:0 }}
                  />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'var(--accent)', letterSpacing:'0.04em', textTransform:'uppercase' }}>
                      Brand Photo Selected
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {selectedBrandPhoto.filename}
                    </div>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:2 }}>
                      AI image generation will be skipped — your photo will be used as-is.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelectedBrandPhoto}
                    style={{
                      background:'transparent', border:'1px solid rgba(255,255,255,0.15)',
                      borderRadius:6, color:'var(--text-muted)', fontSize:11, padding:'5px 10px',
                      cursor:'pointer', flexShrink:0,
                    }}
                  >Remove</button>
                </div>
              ) : requiredPhotoCount > 0 ? (
                <button
                  type="button"
                  onClick={openPhotoPicker}
                  style={{
                    width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                    padding:'9px 12px', background:'rgba(255,255,255,0.04)',
                    border:'1px dashed rgba(49,220,186,0.4)', borderRadius:8,
                    color:'var(--accent)', fontSize:12, fontWeight:600, cursor:'pointer',
                    fontFamily:'var(--font)', transition:'all 0.15s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="14" height="11" rx="2"/><circle cx="5.5" cy="7.5" r="1.5"/><polyline points="1,12 5,8 8,11 10.5,8.5 15,13"/>
                  </svg>
                  Use Photo Library{requiredPhotoCount > 1 ? ` (${requiredPhotoCount} needed)` : ''}
                </button>
              ) : null}
            </div>

            <div
              style={{ fontSize:11, color:'var(--accent)', cursor:'pointer', marginBottom:12, marginTop:4, userSelect:'none' }}
              onClick={() => setShowTemplates(s => !s)}
            >
              {showTemplates ? '▾ Hide templates' : '▸ Use a preset template'}
            </div>

            {showTemplates && (
              <div style={{ marginBottom:14 }}>
                <div className="ig-templates">
                  {IG_TEMPLATES.map(t => (
                    <div
                      key={t.label}
                      className={`ig-template${selectedTemplate === t.label ? ' selected' : ''}`}
                      onClick={() => handleTemplateSelect(t)}
                    >
                      <div className="ig-template-label">{t.label}</div>
                      {t.text.slice(0, 72)}…
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Style picker */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div className="ig-label" style={{ marginBottom:0 }}>Visual Style<Tip text="Applies a style modifier to the generated image." below /></div>
              <div style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }} onClick={() => setStyleOff(o => !o)}>
                <div style={{ fontSize:10, color: styleOff ? 'var(--accent)' : 'var(--text-muted)', fontWeight:600 }}>
                  {styleOff ? 'Style off' : 'Style on'}
                </div>
                <div className={`ig-toggle-track${styleOff ? '' : ' on'}`} style={{ width:28, height:16 }}>
                  <div className="ig-toggle-thumb" style={{ width:10, height:10, top:2, left: styleOff ? 2 : 14 }} />
                </div>
              </div>
            </div>
            <div className="ig-style-grid" style={{ marginBottom:16, opacity: styleOff ? 0.3 : 1, pointerEvents: styleOff ? 'none' : 'auto', gridTemplateColumns:'1fr 1fr 1fr 1fr' }}>
              {IG_STYLES.map(s => (
                <div
                  key={s.id}
                  className={`ig-style-opt${selectedStyles.includes(s.id) ? ' selected' : ''}`}
                  onClick={() => setSelectedStyles(curr => {
                    if (curr.includes(s.id)) {
                      return curr.length > 1 ? curr.filter(x => x !== s.id) : curr;
                    }
                    return [...curr, s.id];
                  })}
                >
                  <div className="ig-style-icon" style={{ fontSize:14 }}>{s.icon}</div>
                  <div className="ig-style-name" style={{ fontSize:10 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Platform picker — feed + stories on each network */}
            <div className="ig-label">Platforms<Tip text="Pick where this post publishes — feed and/or stories, on Instagram and Facebook." below /></div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
              {[
                { key:'ig',      label:'Instagram', net:'ig' },
                { key:'fb',      label:'Facebook',  net:'fb' },
                { key:'igStory', label:'IG Story',  net:'ig' },
                { key:'fbStory', label:'FB Story',  net:'fb' },
                { key:'tt',      label:'TikTok',    net:'tt' },
                ...(gbpConnected ? [{ key:'gb',      label:'Google',    net:'gb' }] : []),
              ].map(opt => (
                <div
                  key={opt.key}
                  onClick={() => setPlatforms(p => ({ ...p, [opt.key]: !p[opt.key] }))}
                  style={{
                    flex:'1 1 calc(50% - 4px)', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                    padding:'9px 8px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
                    border:'1px solid', transition:'all 0.15s',
                    background:   platforms[opt.key] ? 'var(--accent-dim)' : 'var(--bg-surface)',
                    borderColor:  platforms[opt.key] ? 'var(--accent-border)' : 'var(--border)',
                    color:        platforms[opt.key] ? 'var(--accent)' : 'var(--text-muted)',
                  }}
                >
                  {opt.net === 'ig'
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    : opt.net === 'fb'
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    : opt.net === 'gb'
                    ? <GoogleGlyph />
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  }
                  {opt.label}
                </div>
              ))}
            </div>
            {!platforms.ig && !platforms.fb && !platforms.igStory && !platforms.fbStory && !platforms.tt && !platforms.gb && (
              <div style={{ fontSize:11, color:'#f5a623', padding:'6px 10px', background:'rgba(245,166,35,0.08)', borderRadius:7, border:'1px solid rgba(245,166,35,0.2)', marginBottom:14, marginTop:-8 }}>
                Select at least one destination
              </div>
            )}
            {(platforms.igStory || platforms.fbStory) && aspectRatio !== '9:16' && (
              <div style={{ fontSize:11, color:'#f5a623', padding:'6px 10px', background:'rgba(245,166,35,0.08)', borderRadius:7, border:'1px solid rgba(245,166,35,0.2)', marginBottom:14, marginTop:-8 }}>
                Stories display best at 9:16. Your image will show with color bars on the sides.
              </div>
            )}

            {/* TikTok settings — only when TikTok is a selected destination */}
            {platforms.tt && (
              <TikTokSettingsPanel
                clientId={cid}
                value={ttSettings}
                onChange={updateTtSettings}
                isVideo={false}
                previewImageUrl={selectedBrandPhoto?.image_url || selectedBrandPhoto?.url || selectedBrandPhoto?.signedUrl || (selectedBrandPhotos && selectedBrandPhotos[0] && (selectedBrandPhotos[0].image_url || selectedBrandPhotos[0].url)) || null}
              />
            )}

            {/* Google Business options — only when Google is a selected destination */}
            {platforms.gb && gbpConnected && (
              <GoogleBusinessOptions value={gbpSettings} onChange={setGbpSettings} />
            )}

            {/* Text overlay — accordion, collapsed by default. Nothing here
                touches Generate: the text previews live on the generated images
                in Review & Approve and is baked in only by Apply. The header
                pill shows state while collapsed, since collapsing it does not
                turn the overlay off. */}
            {(() => {
              const targets = generatedResults.filter(isOverlayTarget);
              const pending = targets.filter(isOverlayPending);
              const skipped = targets.filter(t => t.overlaySkip).length;
              const applying = targets.some(t => applyingOverlayIds.has(t.id));
              const plural = (n) => `${n} post${n === 1 ? '' : 's'}`;
              // Say exactly what Apply is about to do: add, remove, or both.
              const adds    = pending.filter(t => overlayTargetFor(t)).length;
              const removes = pending.length - adds;
              const applyLabel =
                adds && removes ? `Add text to ${plural(adds)}, remove from ${removes}`
                : adds          ? `Apply text to ${plural(adds)}`
                :                 `Remove text from ${plural(removes)}`;
              return (
                <div style={{ marginTop:14, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }}>
                  <button
                    type="button"
                    onClick={() => setTextOverlayOpen(o => !o)}
                    aria-expanded={textOverlayOpen}
                    style={{
                      width:'100%', display:'flex', alignItems:'center', gap:8, padding:'9px 12px',
                      background:'transparent', border:'none', cursor:'pointer', textAlign:'left',
                      fontFamily:'var(--font)', fontSize:13, color: liveOverlay ? '#31DCBA' : 'var(--text-muted)',
                    }}
                  >
                    <span style={{ fontSize:10, width:10 }}>{textOverlayOpen ? '▾' : '▸'}</span>
                    <span style={{ flex:1 }}>Text overlay</span>
                    {pending.length > 0 ? (
                      <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:8, background:'rgba(245,166,35,0.15)', color:'#f5a623' }}>{pending.length} to apply</span>
                    ) : liveOverlay && (
                      <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:8, background:'rgba(49,220,186,0.15)', color:'#31DCBA' }}>On</span>
                    )}
                  </button>
                  {textOverlayOpen && (
                    <div style={{ padding:'0 12px 12px' }}>
                      <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:8, lineHeight:1.4 }}>
                        {targets.length === 0
                          ? 'Generate first — the text then previews live on your generated images. Nothing is added until you press Apply.'
                          : `Previewing live on ${targets.length - skipped === 1 ? '1 post' : `${targets.length - skipped} posts`} in Review & Approve${skipped ? ` (${skipped} skipped)` : ''}. Untick "Text" on a post to skip it. Nothing is added until you press Apply.`}
                      </div>
                      <TextOverlayControls
                        value={textOverlay}
                        onChange={setTextOverlay}
                        selected={textOverlaySel}
                        onSelect={setTextOverlaySel}
                        placeholder="Text to show on the image"
                        // One setting for every card; realignment measures on
                        // the first card taking it (a batch shares its size).
                        imageUrl={(() => {
                          const t = targets.find(r => !r.overlaySkip) || targets[0];
                          return t ? (t.baseImageUrl || t.imageUrl) : undefined;
                        })()}
                      />
                      {targets.length > 0 && (
                        <button
                          type="button"
                          onClick={() => handleApplyTextOverlay(pending)}
                          disabled={pending.length === 0 || applying}
                          style={{
                            width:'100%', marginTop:10, padding:'8px 12px', borderRadius:7, border:'none',
                            fontFamily:'var(--font)', fontSize:12, fontWeight:700,
                            background: pending.length === 0 ? 'rgba(255,255,255,0.05)' : 'var(--accent)',
                            color:      pending.length === 0 ? 'var(--text-muted)' : '#0d0f14',
                            cursor: pending.length === 0 || applying ? 'default' : 'pointer',
                            opacity: applying ? 0.6 : 1,
                          }}
                        >
                          {applying ? 'Applying…'
                            : pending.length === 0 ? (liveOverlay ? '✓ Text applied' : 'No text to apply')
                            : applyLabel}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Logo toggle */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:14 }}>
              <div onClick={() => {
                const next = !applyLogo;
                setApplyLogo(next);
                if (cid) {
                  clientScopedFetch(`${BACKEND}/api/brand-hub`, {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ profile: { apply_logo_ai: next } }),
                  }).catch(err => console.error('[ApplyLogo] save failed:', err?.message || err));
                }
              }} style={{ width:36, height:20, borderRadius:10, background: applyLogo ? '#31DCBA' : 'rgba(255,255,255,0.1)', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:3, left: applyLogo ? 18 : 3, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s' }} />
              </div>
              <span style={{ fontSize:13, color: applyLogo ? '#31DCBA' : 'var(--text-muted)', transition:'color 0.2s' }}>Apply logo watermark</span>
            </div>

            {applyLogo && (
              <div style={{ marginTop:10, marginLeft:46, paddingRight:6, display:'flex', flexDirection:'column', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:12, color:'var(--text-muted)', minWidth:50 }}>Position</span>
                  <LogoPositionPicker value={logoPos} onChange={setLogoPos} />
                </div>
                <div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                    <div className="ig-label" style={{ marginBottom:0 }}>Logo Size</div>
                    <span style={{ fontSize:12, fontWeight:700, color:'#31DCBA' }}>{logoSize}%</span>
                  </div>
                  <input
                    type="range" min={10} max={100} step={1}
                    value={logoSize}
                    onChange={e => setLogoSize(Number(e.target.value))}
                    style={{ width:'100%', accentColor:'#31DCBA', cursor:'pointer' }}
                  />
                </div>
              </div>
            )}

            {/* Aspect Ratio */}
            <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:14, flexWrap:'wrap', minWidth:0 }}>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)', whiteSpace:'nowrap', flexShrink:0 }}>Aspect</span>
              <div style={{ display:'flex', gap:4, background:'var(--bg-card2)', borderRadius:7, padding:3, flex:'1 1 auto' }}>
                {['1:1', '4:5', '9:16'].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setAspectRatio(r)}
                    style={{
                      flex:1, padding:'5px 6px', border:'none', borderRadius:5,
                      fontFamily:'var(--font-head)', fontSize:11, letterSpacing:'0.04em',
                      cursor:'pointer', transition:'all 0.15s',
                      background: aspectRatio === r ? '#31DCBA' : 'transparent',
                      color:      aspectRatio === r ? '#0d0f14' : 'var(--text-secondary)',
                      fontWeight: aspectRatio === r ? 700 : 600,
                    }}
                  >{r}</button>
                ))}
              </div>
            </div>

            <button
              className={`ig-gen-btn${generating ? ' generating' : ''}`}
              onClick={handleGenerate}
              disabled={(!prompt.trim() && !selectedBrandPhoto && selectedBrandPhotos.length === 0) || generating || selectedStyles.length === 0 || (!platforms.ig && !platforms.fb && !platforms.igStory && !platforms.fbStory && !platforms.tt && !platforms.gb)}
              style={{ marginTop:14, boxShadow:'0 0 12px 3px #31DCBA' }}
            >
              {generating
                ? <><div className="ig-spinner" /> Generating {genCount === 1 ? 'post' : `${genCount} posts`}…</>
                : <>✦ Generate {genCount === 1 ? 'Post' : `${genCount} Posts`}</>
              }
            </button>
          </div>
        )}

        {/* TAB: Brand Photo */}
        {activeTab === 'brand-photo' && (
          <div className="ig-card" style={{ flex:1 }}>
            <div className="ig-card-title">✦ Brand Photo</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12, lineHeight:1.4 }}>
              Generate branded product or lifestyle photos using your brand colours, logo, and style keywords.
            </div>

            {/* Visual Style */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div className="ig-label" style={{ marginBottom:0 }}>Visual Style<Tip text="Applies a style modifier to the generated image." below /></div>
              <div style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }} onClick={() => setBrandPhotoStyleOff(o => !o)}>
                <div style={{ fontSize:10, color: brandPhotoStyleOff ? 'var(--accent)' : 'var(--text-muted)', fontWeight:600 }}>
                  {brandPhotoStyleOff ? 'Style off' : 'Style on'}
                </div>
                <div className={`ig-toggle-track${brandPhotoStyleOff ? '' : ' on'}`} style={{ width:28, height:16 }}>
                  <div className="ig-toggle-thumb" style={{ width:10, height:10, top:2, left: brandPhotoStyleOff ? 2 : 14 }} />
                </div>
              </div>
            </div>
            <div className="ig-style-grid" style={{ marginBottom:16, opacity: brandPhotoStyleOff ? 0.3 : 1, pointerEvents: brandPhotoStyleOff ? 'none' : 'auto', gridTemplateColumns:'1fr 1fr 1fr 1fr' }}>
              {IG_STYLES.map(s => (
                <div
                  key={s.id}
                  className={`ig-style-opt${brandPhotoSelectedStyles.includes(s.id) ? ' selected' : ''}`}
                  onClick={() => setBrandPhotoSelectedStyles(curr => {
                    if (curr.includes(s.id)) {
                      return curr.length > 1 ? curr.filter(x => x !== s.id) : curr;
                    }
                    return [...curr, s.id];
                  })}
                >
                  <div className="ig-style-icon" style={{ fontSize:14 }}>{s.icon}</div>
                  <div className="ig-style-name" style={{ fontSize:10 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Background Scene */}
            <div className="ig-label" style={{ marginBottom:8 }}>Background Scene</div>
            <textarea
              rows={3}
              placeholder="Describe the environment behind the product — e.g. 'rustic wooden café counter, soft morning light, shallow depth of field'"
              value={brandPhotoBgPrompt}
              onChange={e => setBrandPhotoBgPrompt(e.target.value)}
              style={{
                width:'100%', boxSizing:'border-box', marginBottom:18, resize:'vertical',
                background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:8, color:'var(--text-primary)', fontSize:12, padding:'10px 12px',
                fontFamily:'var(--font)', lineHeight:1.5,
              }}
            />

            {/* Logo Variant */}
            <div className="ig-label" style={{ marginBottom:8 }}>Logo Variant</div>
            <input
              ref={brandPhotoLogoFileRef}
              type="file"
              accept="image/*"
              style={{ display:'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadLogoVariant(f); e.target.value = ''; }}
            />
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:18 }}>
              {/* Main logo tile */}
              <div
                onClick={() => setSelectedBrandPhotoLogo('main')}
                style={{
                  width:64, height:64, borderRadius:8, cursor:'pointer', flexShrink:0, overflow:'hidden',
                  background:'rgba(255,255,255,0.05)', border:'2px solid',
                  borderColor: selectedBrandPhotoLogo === 'main' ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
                  transition:'border-color 0.15s',
                }}
              >
                {brandLogoUrl
                  ? <img src={brandLogoUrl} alt="Main logo" style={{ width:'100%', height:'100%', objectFit:'contain', padding:6, boxSizing:'border-box' }} />
                  : <span style={{ fontSize:10, color:'var(--text-muted)', textAlign:'center', lineHeight:1.3, padding:'0 4px' }}>No logo</span>
                }
                <span style={{ fontSize:9, fontWeight:700, color: selectedBrandPhotoLogo === 'main' ? 'var(--accent)' : 'var(--text-muted)', letterSpacing:'0.04em', marginTop:2 }}>MAIN</span>
              </div>

              {/* Uploaded variant tiles */}
              {brandPhotoLogoVariants.map(v => (
                <div
                  key={v.path}
                  onClick={() => setSelectedBrandPhotoLogo(v.url)}
                  style={{
                    width:64, height:64, borderRadius:8, cursor:'pointer', flexShrink:0,
                    background:'rgba(255,255,255,0.05)', border:'2px solid', position:'relative',
                    borderColor: selectedBrandPhotoLogo === v.url ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                    transition:'border-color 0.15s', overflow:'hidden',
                  }}
                >
                  <img src={v.url} alt="Logo variant" style={{ width:'100%', height:'100%', objectFit:'contain', padding:6, boxSizing:'border-box' }} />
                  <button
                    onClick={e => { e.stopPropagation(); handleRemoveLogoVariant(v); }}
                    aria-label="Remove logo variant"
                    title="Remove"
                    style={{
                      position:'absolute', top:2, right:2, width:16, height:16, borderRadius:'50%',
                      background:'rgba(0,0,0,0.7)', border:'none', color:'#fff', fontSize:9,
                      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                      lineHeight:1, padding:0,
                    }}
                  >×</button>
                </div>
              ))}

              {/* Add Variant tile */}
              <div
                onClick={() => brandPhotoLogoFileRef.current?.click()}
                style={{
                  width:64, height:64, borderRadius:8, cursor:'pointer', flexShrink:0,
                  background:'transparent', border:'1px dashed rgba(49,220,186,0.4)',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
                  transition:'border-color 0.15s',
                }}
              >
                <span style={{ fontSize:18, color:'#31DCBA', lineHeight:1 }}>+</span>
                <span style={{ fontSize:9, fontWeight:700, color:'#31DCBA', letterSpacing:'0.04em' }}>ADD</span>
              </div>
            </div>

            {/* Logo Position */}
            <div className="ig-label" style={{ marginBottom:8 }}>Logo Position</div>
            <div style={{ marginBottom:16 }}>
              <LogoPositionPicker value={brandPhotoLogoPos} onChange={setBrandPhotoLogoPos} />
            </div>

            {/* Logo Size */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <div className="ig-label" style={{ marginBottom:0 }}>Logo Size</div>
              <span style={{ fontSize:12, fontWeight:700, color:'#31DCBA' }}>{brandPhotoLogoSize}%</span>
            </div>
            <input
              type="range"
              min={10} max={100} step={1}
              value={brandPhotoLogoSize}
              onChange={e => setBrandPhotoLogoSize(Number(e.target.value))}
              style={{ width:'100%', accentColor:'#31DCBA', cursor:'pointer', marginBottom:18 }}
            />

            <div className="ig-label" style={{ marginBottom:8 }}>Base Photo (optional — uses your image instead of AI generation)</div>
            <input
              ref={brandPhotoFileRef}
              type="file"
              accept="image/*"
              // Convert iPhone HEIC/HEIF → JPEG so both the preview thumbnail
              // (createObjectURL) and the uploaded base image render correctly.
              onChange={async (e) => {
                const picked = e.target.files?.[0];
                setBrandPhotoBase(picked ? await ensureRenderableImage(picked) : null);
              }}
              style={{ display:'none' }}
            />
            {brandPhotoBase ? (
              <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16, padding:10, background:'rgba(49,220,186,0.06)', borderRadius:7, border:'1px solid rgba(49,220,186,0.25)' }}>
                <img
                  src={URL.createObjectURL(brandPhotoBase)}
                  alt="Base"
                  style={{ width:48, height:48, objectFit:'cover', borderRadius:5, flexShrink:0 }}
                />
                <div style={{ flex:1, minWidth:0, fontSize:11, color:'var(--text-secondary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{brandPhotoBase.name}</div>
                <button
                  onClick={() => { setBrandPhotoBase(null); if (brandPhotoFileRef.current) brandPhotoFileRef.current.value = ''; }}
                  style={{ background:'transparent', border:'1px solid rgba(255,255,255,0.1)', color:'var(--text-muted)', borderRadius:5, padding:'4px 8px', fontSize:10, cursor:'pointer' }}
                >Remove</button>
              </div>
            ) : (
              <button
                onClick={() => brandPhotoFileRef.current?.click()}
                style={{ marginBottom:16, padding:'10px 14px', background:'transparent', border:'1px dashed rgba(49,220,186,0.45)', color:'#31DCBA', borderRadius:7, fontFamily:'var(--font-head)', fontSize:11, letterSpacing:'0.06em', fontWeight:700, cursor:'pointer', textAlign:'left' }}
              >+ Upload Base Photo</button>
            )}

            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, fontWeight:600, color:'var(--text-secondary)' }}>Aspect</span>
              <div style={{ display:'flex', gap:4, background:'var(--bg-card2)', borderRadius:7, padding:3, flex:'1 1 auto' }}>
                {['1:1', '4:5', '9:16'].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setAspectRatio(r)}
                    style={{
                      flex:1, padding:'5px 6px', border:'none', borderRadius:5,
                      fontFamily:'var(--font-head)', fontSize:11, letterSpacing:'0.04em',
                      cursor:'pointer', transition:'all 0.15s',
                      background: aspectRatio === r ? '#31DCBA' : 'transparent',
                      color:      aspectRatio === r ? '#0d0f14' : 'var(--text-secondary)',
                      fontWeight: aspectRatio === r ? 700 : 600,
                    }}
                  >{r}</button>
                ))}
              </div>
            </div>

            {/* Platform picker — feed + stories on each network */}
            <div className="ig-label">Platforms</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
              {[
                { key:'ig',      label:'Instagram', net:'ig' },
                { key:'fb',      label:'Facebook',  net:'fb' },
                { key:'igStory', label:'IG Story',  net:'ig' },
                { key:'fbStory', label:'FB Story',  net:'fb' },
                { key:'tt',      label:'TikTok',    net:'tt' },
                ...(gbpConnected ? [{ key:'gb',      label:'Google',    net:'gb' }] : []),
              ].map(opt => (
                <div
                  key={opt.key}
                  onClick={() => setBrandPhotoPlatforms(p => ({ ...p, [opt.key]: !p[opt.key] }))}
                  style={{
                    flex:'1 1 calc(50% - 4px)', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                    padding:'9px 8px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
                    border:'1px solid', transition:'all 0.15s',
                    background:   brandPhotoPlatforms[opt.key] ? 'var(--accent-dim)' : 'var(--bg-surface)',
                    borderColor:  brandPhotoPlatforms[opt.key] ? 'var(--accent-border)' : 'var(--border)',
                    color:        brandPhotoPlatforms[opt.key] ? 'var(--accent)' : 'var(--text-muted)',
                  }}
                >
                  {opt.net === 'ig'
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    : opt.net === 'fb'
                    ? <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    : opt.net === 'gb'
                    ? <GoogleGlyph />
                    : <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                  }
                  {opt.label}
                </div>
              ))}
            </div>

            {/* TikTok Direct Post settings — required whenever TikTok is a target */}
            {brandPhotoPlatforms.tt && (
              <TikTokSettingsPanel
                clientId={cid}
                value={ttSettings}
                onChange={updateTtSettings}
                isVideo={false}
                previewImageUrl={selectedBrandPhoto?.url || null}
              />
            )}

            {brandPhotoPlatforms.gb && gbpConnected && (
              <GoogleBusinessOptions value={gbpSettings} onChange={setGbpSettings} />
            )}

            {!brandPhotoPlatforms.ig && !brandPhotoPlatforms.fb && !brandPhotoPlatforms.igStory && !brandPhotoPlatforms.fbStory && !brandPhotoPlatforms.tt && !brandPhotoPlatforms.gb && (
              <div style={{ fontSize:11, color:'#f5a623', padding:'6px 10px', background:'rgba(245,166,35,0.08)', borderRadius:7, border:'1px solid rgba(245,166,35,0.2)', marginBottom:14, marginTop:-8 }}>
                Select at least one destination
              </div>
            )}
            {(brandPhotoPlatforms.igStory || brandPhotoPlatforms.fbStory) && aspectRatio !== '9:16' && (
              <div style={{ fontSize:11, color:'#f5a623', padding:'6px 10px', background:'rgba(245,166,35,0.08)', borderRadius:7, border:'1px solid rgba(245,166,35,0.2)', marginBottom:14, marginTop:-8 }}>
                Stories display best at 9:16. Your image will show with color bars on the sides.
              </div>
            )}

            <button
              className={`ig-gen-btn${generatingBrandPhoto ? ' generating' : ''}`}
              onClick={handleGenerateBrandPhoto}
              disabled={generatingBrandPhoto || (!brandPhotoPlatforms.ig && !brandPhotoPlatforms.fb && !brandPhotoPlatforms.igStory && !brandPhotoPlatforms.fbStory && !brandPhotoPlatforms.tt && !brandPhotoPlatforms.gb)}
              style={{ marginTop:'auto', boxShadow:'0 0 12px 3px #31DCBA' }}
            >
              {generatingBrandPhoto
                ? <><div className="ig-spinner" /> Generating…</>
                : brandPhotoBase ? <>✦ Stylize My Photo</> : <>✦ Generate Brand Photo</>
              }
            </button>
          </div>
        )}

        {/* TAB: Carousel */}
        {activeTab === 'carousel' && (
          <div className="ig-card" style={{ flex:1 }}>
            <div className="ig-card-title">✦ Carousel</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:12, lineHeight:1.4 }}>
              Build multi-slide Instagram carousel posts. Generate slides with AI or upload your own — add slide text overlays and a post caption, then send to Approve.
            </div>

            <div className="carousel-two-col">
              <div className="carousel-left">
            {/* ─── Image preview (top of panel) ─── */}
            {carouselSlides.length > 0 ? (() => {
              const currentIdx   = Math.min(carouselPreviewIdx, carouselSlides.length - 1);
              const currentSlide = carouselSlides[currentIdx];
              return (
                <>
                  <div style={{ width:'100%', borderRadius:8, overflow:'hidden', marginBottom:10, position:'relative', flexShrink:0 }}>
                    {/* The shared preview, laid out from the bake's own numbers
                        — this used to be its own fixed-size approximation in
                        the dashboard font, which the baked slide didn't match. */}
                    <TextOverlayPreviewImage
                      src={currentSlide.imageUrl}
                      alt={`Slide ${currentIdx + 1}`}
                      overlay={trimOverlayBlocks(slideTextBlocks(currentSlide))}
                      selected={carouselTextSel[currentSlide.id] || 0}
                      onSelect={i => setCarouselTextSel(m => ({ ...m, [currentSlide.id]: i }))}
                      onMove={(i, x, y) => updateSlideTextBlocks(currentIdx, bs => bs.map((b, j) => (j === i ? { ...b, x, y } : b)))}
                      onResize={(i, fields) => updateSlideTextBlocks(currentIdx, bs => bs.map((b, j) => (j === i ? { ...b, ...fields } : b)))}
                      style={{ width:'100%', height:'auto', display:'block' }}
                    />
                    <div style={{
                      position:'absolute', bottom:8, right:8, padding:'3px 8px', borderRadius:10,
                      background:'rgba(49,220,186,0.9)', color:'#0d0f14', fontSize:11, fontWeight:700,
                    }}>
                      {currentIdx + 1}/{carouselSlides.length}
                    </div>
                  </div>

                  {/* Thumbnail strip */}
                  <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
                    {carouselSlides.map((s, idx) => {
                      const isSelected   = idx === currentIdx;
                      const isRegenning  = carouselRegenIds.has(s.id);
                      return (
                        <div
                          key={s.id}
                          draggable
                          onDragStart={() => { carouselDragSrcRef.current = idx; }}
                          onDragOver={e => { e.preventDefault(); }}
                          onDrop={e => {
                            e.preventDefault();
                            const from = carouselDragSrcRef.current;
                            carouselDragSrcRef.current = null;
                            if (from === null || from === undefined) return;
                            handleCarouselReorder(from, idx);
                          }}
                          onClick={() => setCarouselPreviewIdx(idx)}
                          style={{
                            width:100, height:100, borderRadius:8, overflow:'hidden', position:'relative',
                            cursor:'pointer', flexShrink:0, background:'var(--bg-card2)',
                            border:'2px solid', borderColor: isSelected ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                            transition:'border-color 0.15s',
                          }}
                        >
                          <img
                            src={s.imageUrl}
                            alt={`Thumb ${idx + 1}`}
                            style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}
                          />
                          {isRegenning && (
                            <div style={{
                              position:'absolute', inset:0, background:'rgba(13,15,20,0.65)',
                              display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                              <div className="ig-spinner" />
                            </div>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); handleCarouselRemoveSlide(idx); }}
                            aria-label="Remove slide"
                            title="Remove"
                            style={{
                              position:'absolute', top:4, right:4, width:18, height:18, borderRadius:'50%',
                              background:'rgba(0,0,0,0.7)', border:'none', color:'#fff', fontSize:10,
                              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                              lineHeight:1, padding:0,
                            }}
                          >×</button>
                          {s.source === 'generated' && (
                            <button
                              onClick={e => { e.stopPropagation(); handleCarouselRegenSlide(idx); }}
                              aria-label="Regenerate slide"
                              title="Regenerate"
                              disabled={isRegenning}
                              style={{
                                position:'absolute', bottom:4, right:4, width:18, height:18, borderRadius:'50%',
                                background:'rgba(0,0,0,0.7)', border:'none', color:'#fff', fontSize:10,
                                cursor: isRegenning ? 'not-allowed' : 'pointer',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                lineHeight:1, padding:0,
                              }}
                            >↻</button>
                          )}
                          <div style={{
                            position:'absolute', top:4, left:4, padding:'1px 6px', borderRadius:8,
                            background:'rgba(0,0,0,0.6)', color:'#fff', fontSize:10, fontWeight:700,
                          }}>{idx + 1}</div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })() : (
              <div style={{
                width:'100%', aspectRatio:'1 / 1', borderRadius:8,
                background:'rgba(255,255,255,0.03)', border:'1px dashed rgba(255,255,255,0.12)',
                display:'flex', alignItems:'center', justifyContent:'center',
                color:'var(--text-muted)', fontSize:11, padding:14, textAlign:'center',
              }}>
                Slides will appear here once you generate or upload them.
              </div>
            )}
              </div>

              <div className="carousel-right">
            {/* Mode toggle */}
            <div style={{ display:'flex', gap:4, marginBottom:14, background:'var(--bg-card2)', borderRadius:8, padding:4 }}>
              {[{ id:'generate', label:'Generate' }, { id:'upload', label:'Upload' }].map(m => (
                <button
                  key={m.id}
                  onClick={() => setCarouselMode(m.id)}
                  style={{
                    flex:1, padding:'6px 8px', border:'none', borderRadius:6, fontFamily:'var(--font)',
                    fontSize:11, fontWeight:600, cursor:'pointer', transition:'all 0.15s',
                    background: carouselMode === m.id ? 'var(--accent)' : 'transparent',
                    color:      carouselMode === m.id ? '#0d0f14' : 'var(--text-secondary)',
                  }}
                >{m.label}</button>
              ))}
            </div>
            <button
              onClick={async () => {
                if (!carouselLibraryOpen) {
                  // ORDERING CHANGES SLIGHTLY, and it is an improvement. This
                  // query ordered by used then sort_order with NO third key,
                  // while the shared route adds created_at DESC. sort_order
                  // defaults to 0 for every upload and is only backfilled on
                  // drag, so in a library nobody has reordered EVERY photo sits
                  // at 0 and the tiebreak alone decides the order — which this
                  // picker previously left undefined and every other reader of
                  // brand_photos already resolved newest-first.
                  try {
                    const res = await clientScopedFetch(`${BACKEND}/api/photo-library`);
                    const body = await res.json().catch(() => ({}));
                    if (res.ok && body.ok) setCarouselLibraryPhotos(body.photos || []);
                  } catch (err) {
                    console.error('[Carousel] library load failed:', err?.message || err);
                  }
                }
                setCarouselLibraryOpen(prev => !prev);
              }}
              style={{
                width:'100%', marginTop:6, fontSize:11, fontWeight:600,
                padding:'6px 12px', borderRadius:6,
                border:'1px solid rgba(255,255,255,0.15)',
                background: carouselLibraryOpen ? 'rgba(49,220,186,0.15)' : 'rgba(255,255,255,0.04)',
                color: carouselLibraryOpen ? '#31DCBA' : 'var(--text-muted)',
                cursor:'pointer',
              }}
            >📷 From Library</button>
            {carouselLibraryOpen && (
              <div style={{
                display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:6,
                marginTop:8, maxHeight:220, overflowY:'auto',
              }}>
                {carouselLibraryPhotos.filter(p => !p.used).map(photo => {
                  const alreadyAdded = carouselSlides.some(s => s.photoId === photo.id);
                  const atLimit = carouselSlides.length >= 6;
                  return (
                    <div
                      key={photo.id}
                      onClick={() => {
                        if (alreadyAdded || atLimit) return;
                        setCarouselSlides(prev => {
                          const next = [...prev, {
                            id: makeCarouselUid(),
                            imageUrl: photo.url,
                            source: 'library',
                            text: '',
                            caption: '',
                            textPosition: 'bottom',
                            textSize: 'm',
                            photoId: photo.id,
                          }].slice(0, 6);
                          setCarouselPreviewIdx(next.length - 1);
                          return next;
                        });
                      }}
                      style={{
                        position:'relative', aspectRatio:'1', borderRadius:6, overflow:'hidden',
                        cursor: alreadyAdded || atLimit ? 'default' : 'pointer',
                        opacity: alreadyAdded || atLimit ? 0.4 : 1,
                        border: alreadyAdded ? '2px solid #31DCBA' : '2px solid transparent',
                      }}
                    >
                      <img src={photo.url} alt={photo.filename}
                        style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quick presets */}
            <div className="cq-tpl-label">Quick presets</div>
            <div className="cq-tpl-row" style={{ marginBottom:14 }}>
              {CAROUSEL_PRESETS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  className={`cq-tpl-chip${selectedPreset === p.id ? ' active' : ''}`}
                  onClick={() => handleCarouselPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Aspect ratio */}
            <div className="ig-label" style={{ marginBottom:6 }}>Aspect Ratio</div>
            <div style={{ display:'flex', gap:6, marginBottom:14 }}>
              {['1:1','4:5'].map(a => (
                <button
                  key={a}
                  onClick={() => setCarouselAspect(a)}
                  style={{
                    padding:'5px 12px', borderRadius:5, border:'none', cursor:'pointer',
                    fontFamily:'var(--font)', fontSize:11, fontWeight:700, transition:'all 0.15s',
                    background: carouselAspect === a ? '#31DCBA' : 'rgba(255,255,255,0.05)',
                    color:      carouselAspect === a ? '#0d0f14' : 'var(--text-secondary)',
                  }}
                >{a}</button>
              ))}
            </div>

            {/* Slide count (generate mode only) */}
            {carouselMode === 'generate' && (
              <>
                <div className="ig-label" style={{ marginBottom:6 }}>Slides</div>
                <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
                  {[1,2,3,4,5,6].map(n => (
                    <button
                      key={n}
                      onClick={() => setCarouselCount(n)}
                      style={{
                        width:32, height:28, borderRadius:5, border:'none', cursor:'pointer',
                        fontFamily:'var(--font)', fontSize:11, fontWeight:700, transition:'all 0.15s',
                        background: carouselCount === n ? '#31DCBA' : 'rgba(255,255,255,0.05)',
                        color:      carouselCount === n ? '#0d0f14' : 'var(--text-secondary)',
                      }}
                    >{n}</button>
                  ))}
                </div>
              </>
            )}

            {/* Upload zone */}
            {carouselMode === 'upload' && (
              <>
                <input
                  ref={carouselFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  multiple
                  style={{ display:'none' }}
                  onChange={e => { handleCarouselUpload(e.target.files); e.target.value = ''; }}
                />
                <div
                  onClick={() => carouselFileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setCarouselDragging(true); }}
                  onDragLeave={() => setCarouselDragging(false)}
                  onDrop={e => { e.preventDefault(); setCarouselDragging(false); handleCarouselUpload(e.dataTransfer.files); }}
                  style={{
                    border: carouselDragging ? '1px dashed var(--accent)' : '1px dashed rgba(49,220,186,0.4)',
                    background: carouselDragging ? 'rgba(49,220,186,0.06)' : 'rgba(255,255,255,0.03)',
                    borderRadius:8, padding:18, marginBottom:14, textAlign:'center', cursor:'pointer',
                    transition:'all 0.15s',
                  }}
                >
                  <div style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:4 }}>
                    Drag & drop or click to upload
                  </div>
                  <div style={{ fontSize:10, color:'var(--text-muted)' }}>
                    JPG · PNG · WEBP — up to 6 slides
                  </div>
                </div>
              </>
            )}

            {/* ─── 3. Lock Cover Slide (generate mode only) ─── */}
            {carouselMode === 'generate' && (
              <>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: carouselCoverLock ? 6 : 14 }}>
                  <div className="ig-label" style={{ marginBottom:0 }}>Lock Cover Slide<Tip text="Locks the first slide so it stays as your cover when reordering. Drag to reorder the rest without affecting slide 1." below /></div>
                  <div style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }} onClick={() => setCarouselCoverLock(o => !o)}>
                    <div style={{ fontSize:10, color: carouselCoverLock ? 'var(--accent)' : 'var(--text-muted)', fontWeight:600 }}>
                      {carouselCoverLock ? 'On' : 'Off'}
                    </div>
                    <div className={`ig-toggle-track${carouselCoverLock ? ' on' : ''}`} style={{ width:28, height:16 }}>
                      <div className="ig-toggle-thumb" style={{ width:10, height:10, top:2, left: carouselCoverLock ? 14 : 2 }} />
                    </div>
                  </div>
                </div>
                {carouselCoverLock && (
                  <textarea
                    rows={2}
                    placeholder="Hook prompt — used for slide 1 only (e.g. 'bold dramatic close-up, eye-catching')"
                    value={carouselHookPrompt}
                    onChange={e => setCarouselHookPrompt(e.target.value)}
                    style={{
                      width:'100%', boxSizing:'border-box', marginBottom:14, resize:'vertical',
                      background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
                      borderRadius:8, color:'var(--text-primary)', fontSize:12, padding:'10px 12px',
                      fontFamily:'var(--font)', lineHeight:1.5,
                    }}
                  />
                )}

                {/* ─── 4. Background Scene ─── */}
                <div className="ig-label" style={{ marginBottom:6 }}>Background Scene</div>
                <textarea
                  rows={3}
                  placeholder="Describe the scene shared across all slides (or slides 2-N when cover lock is on)"
                  value={carouselBgPrompt}
                  onChange={e => setCarouselBgPrompt(e.target.value)}
                  style={{
                    width:'100%', boxSizing:'border-box', marginBottom:16, resize:'vertical',
                    background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
                    borderRadius:8, color:'var(--text-primary)', fontSize:12, padding:'10px 12px',
                    fontFamily:'var(--font)', lineHeight:1.5,
                  }}
                />
              </>
            )}

            {/* ─── 5. Logo controls ─── */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: carouselLogoOn ? 8 : 16 }}>
              <div className="ig-label" style={{ marginBottom:0 }}>Apply Logo Watermark</div>
              <div style={{ display:'flex', alignItems:'center', gap:7, cursor:'pointer' }} onClick={() => setCarouselLogoOn(o => !o)}>
                <div style={{ fontSize:10, color: carouselLogoOn ? 'var(--accent)' : 'var(--text-muted)', fontWeight:600 }}>
                  {carouselLogoOn ? 'On' : 'Off'}
                </div>
                <div className={`ig-toggle-track${carouselLogoOn ? ' on' : ''}`} style={{ width:28, height:16 }}>
                  <div className="ig-toggle-thumb" style={{ width:10, height:10, top:2, left: carouselLogoOn ? 14 : 2 }} />
                </div>
              </div>
            </div>
            {carouselLogoOn && (
              <>
                <div className="ig-label" style={{ marginBottom:6 }}>Logo Position</div>
                <div style={{ marginBottom:16 }}>
                  <LogoPositionPicker value={carouselLogoPos} onChange={setCarouselLogoPos} />
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                  <div className="ig-label" style={{ marginBottom:0 }}>Logo Size</div>
                  <span style={{ fontSize:12, fontWeight:700, color:'#31DCBA' }}>{carouselLogoSize}%</span>
                </div>
                <input
                  type="range" min={10} max={100} step={1}
                  value={carouselLogoSize}
                  onChange={e => setCarouselLogoSize(Number(e.target.value))}
                  style={{ width:'100%', accentColor:'#31DCBA', cursor:'pointer', marginBottom:16 }}
                />
              </>
            )}

            {/* ─── 6. Slide Text fields ─── */}
            {carouselSlides.length > 0 && (
              <div style={{ marginBottom:16, padding:12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }}>
                <div className="ig-label" style={{ marginBottom:6 }}>Slide Text</div>
                <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:8, lineHeight:1.4 }}>
                  Renders as an overlay on each slide image. Leave blank to keep the image as-is.
                </div>
                <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                  <input
                    type="text"
                    placeholder="e.g. 6 motivational quotes about resilience"
                    value={carouselBulkTextPrompt}
                    onChange={e => setCarouselBulkTextPrompt(e.target.value)}
                    style={{
                      flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
                      borderRadius:8, color:'var(--text-primary)', fontSize:12, padding:'8px 10px',
                      fontFamily:'var(--font)',
                    }}
                  />
                  <button
                    onClick={handleCarouselGenerateBulkText}
                    disabled={carouselBulkTextLoading || !carouselBulkTextPrompt.trim()}
                    style={{
                      padding:'8px 12px', borderRadius:6, border:'none', cursor:'pointer',
                      background:'var(--accent)', color:'#0d0f14', fontFamily:'var(--font)',
                      fontSize:11, fontWeight:700, transition:'all 0.15s',
                      opacity: (carouselBulkTextLoading || !carouselBulkTextPrompt.trim()) ? 0.5 : 1,
                    }}
                  >
                    {carouselBulkTextLoading ? '…' : 'Generate'}
                  </button>
                </div>

                {carouselSlides.map((s, idx) => (
                  <div key={s.id} style={{ marginBottom: idx === carouselSlides.length - 1 ? 0 : 8 }}>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:3, fontWeight:600 }}>Slide {idx + 1}</div>
                    <TextOverlayControls
                      value={slideTextBlocks(s)}
                      onChange={update => updateSlideTextBlocks(idx, update)}
                      selected={carouselTextSel[s.id] || 0}
                      onSelect={i => setCarouselTextSel(m => ({ ...m, [s.id]: i }))}
                      placeholder={`Slide text for slide ${idx + 1}`}
                      imageUrl={s.imageUrl}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* ─── 7. Post Caption ─── */}
            <div className="ig-label" style={{ marginBottom:6 }}>Post Caption</div>
            <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:6, lineHeight:1.4 }}>
              Single Instagram caption for the entire carousel post.
            </div>
            <textarea
              rows={4}
              placeholder="Write the caption for this carousel post…"
              value={carouselPostCaption}
              onChange={e => setCarouselPostCaption(e.target.value)}
              style={{
                width:'100%', boxSizing:'border-box', marginBottom:8, resize:'vertical',
                background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:8, color:'var(--text-primary)', fontSize:12, padding:'10px 12px',
                fontFamily:'var(--font)', lineHeight:1.5,
              }}
            />
            <input
              type="text"
              placeholder="#hashtag1 #hashtag2 #hashtag3"
              value={carouselPostHashtags}
              onChange={e => setCarouselPostHashtags(e.target.value)}
              style={{
                width:'100%', boxSizing:'border-box', marginBottom:16,
                background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.1)',
                borderRadius:8, color:'#31DCBA', fontSize:12, padding:'10px 12px',
                fontFamily:'var(--font)', outline:'none',
              }}
            />

            {/* ─── Platform picker — where this carousel publishes ─── */}
            <div className="ig-label" style={{ marginBottom:6 }}>Platforms</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
              {[
                { key:'ig', label:'Instagram' },
                { key:'fb', label:'Facebook'  },
                { key:'tt', label:'TikTok'    },
                ...(gbpConnected ? [{ key:'gb', label:'Google' }] : []),
              ].map(opt => (
                <div
                  key={opt.key}
                  onClick={() => setCarouselPlatforms(p => ({ ...p, [opt.key]: !p[opt.key] }))}
                  style={{
                    flex:'1 1 calc(33% - 6px)', display:'flex', alignItems:'center', justifyContent:'center',
                    padding:'9px 8px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
                    border:'1px solid', transition:'all 0.15s',
                    background:   carouselPlatforms[opt.key] ? 'var(--accent-dim)' : 'var(--bg-surface)',
                    borderColor:  carouselPlatforms[opt.key] ? 'var(--accent-border)' : 'var(--border)',
                    color:        carouselPlatforms[opt.key] ? 'var(--accent)' : 'var(--text-muted)',
                  }}
                >{opt.label}</div>
              ))}
            </div>

            {/* TikTok Direct Post settings — required whenever TikTok is a target */}
            {carouselPlatforms.tt && (
              <TikTokSettingsPanel
                clientId={cid}
                value={ttSettings}
                onChange={updateTtSettings}
                isVideo={false}
                previewImageUrl={carouselSlides[0]?.imageUrl || null}
              />
            )}

            {carouselPlatforms.gb && gbpConnected && (
              <GoogleBusinessOptions value={gbpSettings} onChange={setGbpSettings} caption={carouselPostCaption} />
            )}

            {!carouselPlatforms.ig && !carouselPlatforms.fb && !carouselPlatforms.tt && !carouselPlatforms.gb && (
              <div style={{ fontSize:11, color:'#f5a623', padding:'6px 10px', background:'rgba(245,166,35,0.08)', borderRadius:7, border:'1px solid rgba(245,166,35,0.2)', marginBottom:14, marginTop:-8 }}>
                Select at least one destination
              </div>
            )}

            {/* ─── 8. Generate Images button (generate mode only) ─── */}
            {carouselMode === 'generate' && (
              <button
                className={`ig-gen-btn${carouselGenerating ? ' generating' : ''}`}
                onClick={handleCarouselGenerate}
                disabled={carouselGenerating || !carouselBgPrompt.trim()}
                style={{ marginBottom:14, boxShadow:'0 0 12px 3px #31DCBA' }}
              >
                {carouselGenerating
                  ? <><div className="ig-spinner" /> Generating {carouselCount} slide{carouselCount === 1 ? '' : 's'}…</>
                  : <>✦ Generate Images</>
                }
              </button>
            )}

            {/* Approve / Regenerate / Discard — same trio as Generate Post */}
            {carouselSlides.length > 0 && (
              <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:'auto' }}>
                <button
                  className="aq-btn aq-btn-approve"
                  onClick={handleCarouselSendToQueue}
                  disabled={carouselSubmitting || carouselGenerating}
                  style={{ flex:1 }}
                >
                  {carouselSubmitting
                    ? <><div className="ig-spinner" /> Approving…</>
                    : <>✓ Approve</>
                  }
                </button>
                <button
                  onClick={handleCarouselGenerate}
                  disabled={carouselSubmitting || carouselGenerating || carouselMode !== 'generate' || !carouselBgPrompt.trim()}
                  title={carouselMode === 'generate' ? 'Regenerate all slides' : 'Switch to Generate mode to regenerate'}
                  style={{
                    background:'transparent',
                    border:'1px solid var(--border)',
                    color:'var(--text-secondary)',
                    borderRadius:7,
                    padding:'7px 12px',
                    fontSize:12,
                    fontWeight:600,
                    cursor: (carouselSubmitting || carouselGenerating || carouselMode !== 'generate' || !carouselBgPrompt.trim()) ? 'not-allowed' : 'pointer',
                    opacity: (carouselSubmitting || carouselGenerating || carouselMode !== 'generate' || !carouselBgPrompt.trim()) ? 0.5 : 1,
                  }}
                >
                  ↺ Regenerate
                </button>
                <button
                  className="aq-btn aq-btn-regen"
                  onClick={handleCarouselDiscard}
                  disabled={carouselSubmitting || carouselGenerating}
                  style={{ minWidth:80 }}
                >
                  Discard
                </button>
              </div>
            )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* RIGHT COLUMN — inline results */}
      <div className="ig-right">

        {/* Generating placeholder */}
        {(generating || generatingBrandPhoto) && (
          <div>
            <div className="ig-section-header">
              <div className="ig-section-title">Generating…</div>
            </div>
            <div className="ig-gen-placeholder">
              <div className="ig-gen-pulse"><div className="ig-spinner" /></div>
              <div style={{ fontSize:12, color:'var(--text-muted)' }}>
                {genCount > 1 ? `Creating ${genCount} posts` : 'Creating your post'}
              </div>
            </div>
          </div>
        )}

        {/* Inline result cards */}
        {generatedResults.length > 0 && (
          <div>
            <div className="ig-section-header">
              <div className="ig-section-title">Review &amp; Approve</div>
              <div className="ig-section-badge">{generatedResults.length} pending</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2, minmax(0, 1fr))', gap:12 }}>
              {generatedResults.map(item => {
                const isRegenerating = regeneratingResultIds.has(item.id);
                // Story-frame styling + caption visibility read from whichever
                // composer produced this item. Fresh items carry a `platforms`
                // snapshot stamped at creation time; fall back to the live
                // composer state for older/regenerated items that predate it.
                const itemPlatforms = item.platforms
                  || ((item.previewKind === 'brand_photo' || item.post_type === 'brand_photo')
                    ? brandPhotoPlatforms
                    : platforms);
                const isStoryDest = itemPlatforms.igStory || itemPlatforms.fbStory;
                // TikTok requires an explicit Music Usage Confirmation consent
                // before publishing. Branded content additionally references the
                // Branded Content Policy. Read the settings the item was created
                // with, not the live composer state.
                const itemTtSettings = item.ttSettings || item.tiktok_settings || ttSettings;
                const isTikTokDest = !!itemPlatforms.tt;
                const isBrandedTt = !!itemTtSettings?.branded_content;
                const ttConsented = tiktokConsentIds.has(item.id);
                const ttConsentBlocked = isTikTokDest && !ttConsented;
                // Disclosure gate — see ttDisclosureIncomplete.
                const ttDisclosureBlocked = isTikTokDest && ttDisclosureIncomplete(itemTtSettings);
                const ttBlocked = ttConsentBlocked || ttDisclosureBlocked;
                const ttBlockedReason = ttDisclosureBlocked
                  ? 'Select "Your brand" or "Branded content" — the commercial content disclosure is on'
                  : 'Confirm the TikTok Music Usage Confirmation to approve';
                // Text overlay: while pending, the card shows the BASE image with
                // the composer's text previewed live on it, and approve/publish
                // wait for Apply so they can't ship something else.
                const overlayTarget   = isOverlayTarget(item);
                const overlayPending  = isOverlayPending(item);
                const applyingOverlay = applyingOverlayIds.has(item.id);
                // Text already applied and unchanged: the card shows the stored
                // baked image, with the same text as an invisible drag handle
                // (TextOverlayPreviewImage's bakedSrc) so it can still be moved.
                const overlayGhost = overlayTarget && !overlayPending && !!overlayTargetFor(item)
                  && !!item.baseImageUrl && item.baseImageUrl !== item.imageUrl;
                const overlayBlocked  = overlayPending || applyingOverlay;
                const overlayBlockedReason = 'Apply or clear the text overlay first';
                const publishDisabled = publishingResultIds.has(item.id) || processingResultIds.has(item.id) || isRegenerating || ttBlocked || overlayBlocked;
                // Auto-approve already wrote this post to status='scheduled'.
                // The card stays as a receipt of what was made, but read-only:
                // caption edits here are only persisted by Approve, and
                // regenerating would swap the image out from under a post
                // that is already queued to publish.
                const autoScheduled = !!item.autoScheduledAt;
                return (
                <div key={item.id} className="ig-pcard pending-card" style={{ display:'flex', flexDirection:'column', padding:12, gap:0, minWidth:0 }}>
                  {/* Story destinations preview as a true vertical 9:16 frame */}
                  <div style={{ width:'100%', borderRadius:8, overflow:'hidden', marginBottom:10, position:'relative', flexShrink:0, ...(isStoryDest ? { aspectRatio:'9 / 16', background:'#0d0f14' } : {}) }}>
                    {overlayTarget
                      ? <TextOverlayPreviewImage
                          src={overlayPending || overlayGhost ? (item.baseImageUrl || item.imageUrl) : item.imageUrl}
                          alt="Generated"
                          overlay={overlayPending || overlayGhost ? overlayPreviewFor(item) : null}
                          bakedSrc={overlayGhost ? item.imageUrl : undefined}
                          // One text setting for every card, so dragging a
                          // block on any card moves it on all of them, and
                          // the selected block is the same on every card.
                          selected={textOverlaySel}
                          onSelect={setTextOverlaySel}
                          onMove={applyingOverlay ? undefined : moveOverlayBlock}
                          onResize={applyingOverlay ? undefined : resizeOverlayBlockAt}
                          style={{ width:'100%', height: isStoryDest ? '100%' : 'auto', objectFit: isStoryDest ? 'contain' : undefined, display:'block' }}
                        />
                      : item.imageUrl
                      ? <img src={item.imageUrl} alt="Generated" style={{ width:'100%', height: isStoryDest ? '100%' : 'auto', objectFit: isStoryDest ? 'contain' : undefined, display:'block' }} />
                      : <div style={{ width:'100%', aspectRatio: isStoryDest ? '9 / 16' : '1/1', background:'var(--bg-card2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:48 }}>🖼️</div>
                    }
                    {(isRegenerating || applyingOverlay) && (
                      <div style={{ position:'absolute', inset:0, background:'rgba(13,15,20,0.65)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(2px)' }}>
                        <div className="ig-spinner" />
                      </div>
                    )}
                  </div>
                  {/* Per-post text row: the "Text" checkbox is the skip/select
                      control, and the status says what Apply will do here. */}
                  {overlayTarget && (liveOverlay || item.appliedOverlay) && (() => {
                    const target = overlayTargetFor(item);
                    const status = overlayPending
                      ? (target ? 'Preview — not applied yet' : 'Removal — not applied yet')
                      : item.appliedOverlay ? '✓ Text applied' : 'Skipped';
                    return (
                      <div style={{
                        display:'flex', alignItems:'center', gap:8, marginBottom:10, padding:'5px 6px 5px 8px',
                        borderRadius:7, fontSize:11, lineHeight:1.3,
                        background: overlayPending ? 'rgba(245,166,35,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${overlayPending ? 'rgba(245,166,35,0.25)' : 'rgba(255,255,255,0.08)'}`,
                        color: overlayPending ? '#f5a623' : 'var(--text-muted)',
                      }}>
                        <label style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer', color:'var(--text-secondary)', fontWeight:600, flexShrink:0 }}>
                          <input
                            type="checkbox"
                            checked={!item.overlaySkip}
                            disabled={applyingOverlay}
                            onChange={e => handleUpdateResult(item.id, 'overlaySkip', !e.target.checked)}
                            style={{ margin:0 }}
                          />
                          Text
                        </label>
                        <span style={{ flex:1, minWidth:0 }}>{status}</span>
                        {overlayPending && (
                          <button
                            type="button"
                            onClick={() => handleApplyTextOverlay([item])}
                            disabled={applyingOverlay || isRegenerating || processingResultIds.has(item.id) || publishingResultIds.has(item.id)}
                            style={{
                              flexShrink:0, padding:'4px 10px', borderRadius:5, border:'none',
                              background:'#f5a623', color:'#0d0f14', fontFamily:'var(--font)',
                              fontSize:11, fontWeight:700, cursor: applyingOverlay ? 'default' : 'pointer',
                              opacity: applyingOverlay ? 0.6 : 1,
                            }}
                          >{applyingOverlay ? '…' : target ? 'Apply' : 'Remove'}</button>
                        )}
                      </div>
                    );
                  })()}
                  {/* Stories carry no caption — hide the caption/hashtags editors when only story destinations are selected */}
                  {!(!itemPlatforms.ig && !itemPlatforms.fb && (itemPlatforms.igStory || itemPlatforms.fbStory)) && autoScheduled && (
                    <div style={{ marginBottom:10 }}>
                      {item.caption && (
                        <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.5, whiteSpace:'pre-wrap', marginBottom:4 }}>
                          {item.caption}
                        </div>
                      )}
                      {item.hashtags && (
                        <div style={{ fontSize:11, color:'var(--text-muted)', lineHeight:1.5 }}>{item.hashtags}</div>
                      )}
                    </div>
                  )}
                  {!(!itemPlatforms.ig && !itemPlatforms.fb && (itemPlatforms.igStory || itemPlatforms.fbStory)) && !autoScheduled && (
                    <>
                      <textarea
                        className="aq-edit-textarea"
                        rows={3}
                        value={item.caption}
                        onChange={e => handleUpdateResult(item.id, 'caption', e.target.value)}
                        placeholder="Caption…"
                        style={{ width:'100%', marginBottom:6, resize:'vertical' }}
                      />
                      <input
                        className="aq-edit-hashtags"
                        value={item.hashtags}
                        onChange={e => handleUpdateResult(item.id, 'hashtags', e.target.value)}
                        placeholder="Hashtags…"
                        style={{ width:'100%', marginBottom:10 }}
                      />
                    </>
                  )}
                  {/* Generated captions first appear here, so this is where a
                      too-long one for Google is flagged (server trims to 1,500). */}
                  {itemPlatforms.gb && item.post_type !== 'video' && gbpTrimNote(item.caption) && (
                    <div style={{ fontSize:11, color:'#f5a623', marginBottom:8, lineHeight:1.5 }} data-testid="gbp-trim-note">
                      {gbpTrimNote(item.caption)}.
                    </div>
                  )}
                  {autoScheduled ? (
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <div style={{
                        flex:1, display:'flex', alignItems:'center', gap:7, padding:'8px 11px', borderRadius:8,
                        background:'rgba(49,220,186,0.08)', border:'1px solid var(--accent-border)',
                        fontSize:11.5, color:'var(--text-secondary)', lineHeight:1.4,
                      }}>
                        <span style={{ color:'var(--accent)', fontWeight:700 }}>✓</span>
                        <span>Auto-approved — publishes {formatSlot(item.autoScheduledAt)}</span>
                      </div>
                      <button
                        className="aq-btn aq-btn-regen"
                        onClick={() => handleDiscardResult(item)}
                        style={{ minWidth:90 }}
                        title="Pull this post back out of the schedule"
                      >
                        Cancel post
                      </button>
                    </div>
                  ) : (
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <button
                      className="aq-btn aq-btn-approve"
                      onClick={() => handleApproveResult(item)}
                      // Approving schedules the post for the cron publisher, so it
                      // needs the same TikTok consent gate as Publish Now.
                      disabled={processingResultIds.has(item.id) || isRegenerating || ttBlocked || overlayBlocked}
                      style={{ flex:1 }}
                      title={ttBlocked ? ttBlockedReason : overlayBlocked ? overlayBlockedReason : undefined}
                    >
                      {processingResultIds.has(item.id) ? '…' : '✓ Approve'}
                    </button>
                    {item.post_type !== 'video' && (
                    <button
                      onClick={() => handleRegenerateResult(item)}
                      disabled={isRegenerating || processingResultIds.has(item.id) || applyingOverlay}
                      style={{
                        background:'transparent',
                        border:'1px solid var(--border)',
                        color:'var(--text-secondary)',
                        borderRadius:7,
                        padding:'7px 12px',
                        fontSize:12,
                        fontWeight:600,
                        cursor: isRegenerating ? 'not-allowed' : 'pointer',
                        opacity: isRegenerating ? 0.5 : 1,
                      }}
                      title="Regenerate this post"
                    >
                      ↺ Regenerate
                    </button>
                    )}
                    <button
                      className="aq-btn aq-btn-regen"
                      onClick={() => handleDiscardResult(item)}
                      disabled={isRegenerating || applyingOverlay}
                      style={{ minWidth:80 }}
                    >
                      Discard
                    </button>
                  </div>
                  )}
                  {isTikTokDest && (
                    <label style={{ display:'flex', gap:8, alignItems:'flex-start', marginTop:10, fontSize:11, color:'var(--text-secondary)', lineHeight:1.5, cursor:'pointer' }}>
                      <input
                        type="checkbox"
                        checked={ttConsented}
                        onChange={e => toggleResultTiktokConsent(item, e.target.checked)}
                        style={{ marginTop:2, flexShrink:0 }}
                      />
                      <span>
                        {isBrandedTt ? (
                          <>By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Branded Content Policy</a> and <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Music Usage Confirmation</a></>
                        ) : (
                          <>By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>Music Usage Confirmation</a></>
                        )}
                      </span>
                    </label>
                  )}
                  <button
                    onClick={() => handlePublishNowResult(item)}
                    disabled={publishDisabled}
                    style={{
                      marginTop:8,
                      width:'100%',
                      // Greyed out and clearly non-interactive while the TikTok
                      // consent checkbox is required but unchecked; full green
                      // once it's actionable.
                      background: ttConsentBlocked ? 'var(--bg-surface)' : 'var(--accent)',
                      color: ttConsentBlocked ? 'var(--text-muted)' : 'var(--bg-base)',
                      border: ttConsentBlocked ? '1px solid var(--border)' : 'none',
                      borderRadius:7,
                      padding:'9px 12px',
                      fontSize:12,
                      fontWeight:700,
                      cursor: publishDisabled ? 'not-allowed' : 'pointer',
                      opacity: ttConsentBlocked ? 0.5 : (publishDisabled ? 0.6 : 1),
                    }}
                    title={ttConsentBlocked ? 'Confirm the TikTok Music Usage Confirmation to publish' : overlayBlocked ? overlayBlockedReason : 'Publish this post immediately'}
                  >
                    {publishingResultIds.has(item.id) ? 'Publishing…' : '⚡ Publish Now'}
                  </button>
                  {publishResultErrors[item.id] && (
                    <div className="cq-error" style={{ marginTop:8 }}>
                      {publishResultErrors[item.id]}
                    </div>
                  )}
                  {publishBypassOffers[item.id] && (
                    <button
                      type="button"
                      className="cal-post-btn cal-post-btn-ghost"
                      style={{ marginTop:6 }}
                      disabled={publishDisabled}
                      onClick={() => handlePublishNowResult(item, { bypassDailyLimit: true })}
                      title="Admin test account only — logged in the admin audit log"
                    >Publish anyway (admin test — skips the daily limit)</button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}

        {generatedResults.length === 0 && !generating && !generatingBrandPhoto && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', flex:1, color:'var(--text-muted)', fontSize:12, flexDirection:'column', gap:8, paddingTop:40 }}>
            <div style={{ fontSize:28 }}>✦</div>
            <div>Generate your first post using the panel on the left</div>
          </div>
        )}
      </div>

      {toast && <div className="pl-toast visible">{toast}</div>}

      {/* AUTO-APPROVE DISCLAIMER — shown only when auto_approve_consented_at is
          null, i.e. the first time this client ever turns the switch on.
          Confirming stamps the column, which is never cleared, so switching off
          and back on later skips this. */}
      {autoApproveModalOpen && (
        <div
          className="ap-confirm-overlay"
          onClick={() => setAutoApproveModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ap-auto-approve-title"
        >
          <div className="ap-confirm" onClick={e => e.stopPropagation()}>
            <div className="ap-confirm-title" id="ap-auto-approve-title">Turn on auto-approve?</div>
            {/* "From now on" was the whole lead until the server-side sweep
                shipped. It is no longer the whole truth: turning this on also
                approves anything ALREADY waiting in Review & Approve, which is
                a thing that happens to the client's queue the moment they press
                the button. A consent record has to describe what actually
                happens, or it is not consent to it. */}
            <div className="ap-confirm-lead">
              Posts we generate will publish without you seeing them first — including anything already waiting in Review &amp; Approve.
            </div>
            <div className="ap-confirm-list">
              <div className="ap-confirm-item">
                <span className="ap-confirm-dot" />
                <span><strong>This covers your whole account.</strong> Every post this dashboard creates — Generate Post, Brand Photo, Carousel and Video — not just the tab you're on.</span>
              </div>
              <div className="ap-confirm-item">
                <span className="ap-confirm-dot" />
                <span><strong>Posts go live on their own.</strong> They're scheduled into your next open slot (usually tomorrow morning) and published to your connected accounts. Anything currently waiting in Review &amp; Approve gets scheduled too, up to a week ahead.</span>
              </div>
              <div className="ap-confirm-item">
                <span className="ap-confirm-dot" />
                <span><strong>You're responsible for what gets published.</strong> AI writes these captions and images, and it can get facts, names, claims and prices wrong. Check your feed regularly.</span>
              </div>
              <div className="ap-confirm-item">
                <span className="ap-confirm-dot" />
                <span><strong>TikTok is the exception.</strong> TikTok requires you to confirm music usage on every post, so TikTok posts will still wait for your approval.</span>
              </div>
              <div className="ap-confirm-item">
                <span className="ap-confirm-dot" />
                <span><strong>You can turn this off any time.</strong> Posts already scheduled stay scheduled — cancel those from the Calendar.</span>
              </div>
            </div>
            <label className="ap-confirm-agree" htmlFor="ap-auto-approve-agree">
              <input
                id="ap-auto-approve-agree"
                type="checkbox"
                checked={autoApproveAgreed}
                onChange={e => setAutoApproveAgreed(e.target.checked)}
              />
              <span>I understand posts will publish without my review. I'm responsible for checking what goes out, and Autopilot Digital isn't liable for errors or problems in auto-approved posts.</span>
            </label>
            <div className="ap-confirm-terms">
              See our <a href="https://autopilotdigital.ca/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            </div>
            <div className="ap-confirm-actions">
              <button
                className="ap-confirm-btn ap-confirm-btn-cancel"
                onClick={() => setAutoApproveModalOpen(false)}
              >Cancel</button>
              <button
                className="ap-confirm-btn ap-confirm-btn-go"
                onClick={confirmAutoApprove}
                disabled={!autoApproveAgreed}
              >Turn on auto-approve</button>
            </div>
          </div>
        </div>
      )}

      {/* Monthly cap reached → post top-up modal */}
      {capModalOpen && (
        <div
          onClick={() => !topupLoading && setCapModalOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'rgba(8,10,14,0.72)', backdropFilter:'blur(6px)', WebkitBackdropFilter:'blur(6px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width:'100%', maxWidth:420,
              background:'rgba(20,24,32,0.96)', border:'1px solid rgba(49,220,186,0.22)',
              borderRadius:16, padding:'28px 26px 24px',
              boxShadow:'0 24px 60px rgba(0,0,0,0.5)',
              fontFamily:"'Barlow', var(--font-body, sans-serif)", color:'#e7ecf2',
            }}
          >
            <div style={{ fontFamily:"'Barlow', sans-serif", fontWeight:700, fontSize:19, letterSpacing:'-0.01em', marginBottom:8 }}>
              You've used this month's AI images
            </div>
            {/* The monthly cap is the AI IMAGE limit only. Posts from the
                client's own photos never count toward it and still work. */}
            <p style={{ fontSize:14, color:'#9aa4b2', lineHeight:1.5, margin:'0 0 22px' }}>
              Your plan's AI images for this month are used up, and you have no paid images left. Posts made from your own photos still work — there's no limit on those. Top up for 30 more AI images for $19 CAD. Paid images carry over to next month.
            </p>
            <button
              type="button"
              onClick={handlePostTopUp}
              disabled={topupLoading}
              style={{
                width:'100%', padding:'12px 16px', marginBottom:10,
                background:'#31DCBA', color:'#0d0f14',
                border:'none', borderRadius:9, fontWeight:700, fontSize:14,
                fontFamily:'var(--font-head)', letterSpacing:'0.02em',
                cursor: topupLoading ? 'not-allowed' : 'pointer', opacity: topupLoading ? 0.7 : 1,
              }}
            >
              {topupLoading ? 'Redirecting…' : 'Get 30 more AI images →'}
            </button>
            <button
              type="button"
              onClick={() => setCapModalOpen(false)}
              disabled={topupLoading}
              style={{
                width:'100%', padding:'11px 16px',
                background:'transparent', color:'#8b95a5',
                border:'1px solid rgba(255,255,255,0.12)', borderRadius:9, fontWeight:600, fontSize:13,
                fontFamily:'var(--font-head)', letterSpacing:'0.02em',
                cursor: topupLoading ? 'not-allowed' : 'pointer',
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Photo Library Picker Modal */}
      {photoPickerOpen && (
        <div
          onClick={() => setPhotoPickerOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'rgba(0,0,0,0.72)', backdropFilter:'blur(5px)', WebkitBackdropFilter:'blur(5px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background:'#0d0f14', border:'1px solid rgba(49,220,186,0.22)', borderRadius:16,
              width:'100%', maxWidth:640, maxHeight:'85vh', display:'flex', flexDirection:'column',
              boxShadow:'0 24px 80px rgba(0,0,0,0.7)',
            }}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
              <div>
                <div style={{ fontFamily:'var(--font-head)', fontSize:15, fontWeight:700, letterSpacing:'0.03em', color:'var(--text-primary)' }}>
                  Choose Brand Photos
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>
                  Need <span style={{ color:'var(--accent)', fontWeight:700 }}>{requiredPhotoCount}</span> photo{requiredPhotoCount !== 1 ? 's' : ''} — {genCount} post{genCount !== 1 ? 's' : ''} × {Math.round(realPercentage * 100)}% real
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPhotoPickerOpen(false)}
                style={{
                  width:30, height:30, borderRadius:'50%', background:'rgba(255,255,255,0.07)',
                  border:'1px solid rgba(255,255,255,0.12)', color:'var(--text-muted)', fontSize:14,
                  cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                }}
              >✕</button>
            </div>

            {/* Top action row: Auto-Pull Newest */}
            {!libraryLoading && libraryPhotos.some(p => !p.used) && requiredPhotoCount > 0 && (
              <div style={{ padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                <button
                  type="button"
                  onClick={handleAutoPull}
                  disabled={libraryPhotos.filter(p => !p.used).length < requiredPhotoCount}
                  style={{
                    width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                    padding:'11px 14px', background:'var(--accent)', color:'#0d0f14',
                    border:'none', borderRadius:9, fontWeight:700, fontSize:12,
                    fontFamily:'var(--font-head)', letterSpacing:'0.03em', cursor:'pointer',
                    opacity: libraryPhotos.filter(p => !p.used).length < requiredPhotoCount ? 0.4 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 8l4-4 4 4M6 4v10"/><path d="M14 12v2H2"/>
                  </svg>
                  Auto-Pull Newest {requiredPhotoCount}
                </button>
                <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:6, textAlign:'center' }}>
                  Pulls the top {requiredPhotoCount} unused photo{requiredPhotoCount !== 1 ? 's' : ''} from your library queue.
                </div>
              </div>
            )}

            <div style={{ padding:18, overflowY:'auto', flex:1 }}>
              {libraryLoading && (
                <div style={{ color:'var(--text-muted)', fontSize:13, padding:'24px 4px', textAlign:'center' }}>
                  Loading your photos…
                </div>
              )}
              {!libraryLoading && libraryPhotos.length === 0 && (
                <div style={{ color:'var(--text-muted)', fontSize:13, padding:'24px 4px', textAlign:'center', lineHeight:1.5 }}>
                  No brand photos uploaded yet.<br />
                  <span
                    onClick={() => { setPhotoPickerOpen(false); if (onNavigate) onNavigate('photos'); }}
                    style={{ color:'var(--accent)', cursor:'pointer', textDecoration:'underline' }}
                  >Upload photos in Photo Library →</span>
                </div>
              )}
              {!libraryLoading && libraryPhotos.length > 0 && (
                <>
                  <div style={{ fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:8 }}>
                    Or select manually
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:10 }}>
                    {libraryPhotos.map(p => {
                      const isSelected = pickerSelected.includes(p.id);
                      const selIdx = pickerSelected.indexOf(p.id);
                      const atCap = !isSelected && pickerSelected.length >= requiredPhotoCount;
                      const isDisabled = atCap;
                      const isConfirming = pickerReuseConfirmId === p.id;
                      return (
                        <div
                          key={p.id}
                          onClick={() => { if (!isDisabled && !isConfirming) togglePickerPhoto(p); }}
                          style={{
                            cursor: isConfirming ? 'default' : (isDisabled ? 'not-allowed' : 'pointer'),
                            borderRadius:8, overflow:'hidden', position:'relative',
                            border: isSelected ? '2px solid var(--accent)' : '1px solid rgba(255,255,255,0.1)',
                            background:'rgba(255,255,255,0.03)',
                            transition:'all 0.15s',
                            opacity: isDisabled ? 0.45 : (p.used && !isSelected ? 0.7 : 1),
                          }}
                        >
                          <div style={{ width:'100%', aspectRatio:'1/1', background:'#000', position:'relative' }}>
                            <img src={p.url} alt={p.filename} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                            {p.used && !isConfirming && (
                              <div style={{
                                position:'absolute', top:6, right:6,
                                background:'rgba(13,15,20,0.82)', border:'1px solid rgba(255,255,255,0.18)',
                                borderRadius:5, padding:'2px 6px', fontSize:9, fontWeight:700,
                                color:'var(--text-muted)', fontFamily:'var(--font-head)', letterSpacing:'0.06em',
                                textTransform:'uppercase',
                              }}>Used</div>
                            )}
                            {isSelected && (
                              <div style={{
                                position:'absolute', top:6, left:6,
                                width:22, height:22, borderRadius:'50%',
                                background:'var(--accent)', color:'#0d0f14',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:11, fontWeight:800, fontFamily:'var(--font-head)',
                                boxShadow:'0 2px 6px rgba(0,0,0,0.4)',
                              }}>{selIdx + 1}</div>
                            )}
                            {isConfirming && (
                              <div
                                onClick={e => e.stopPropagation()}
                                style={{
                                  position:'absolute', inset:0,
                                  background:'rgba(13,15,20,0.92)',
                                  display:'flex', flexDirection:'column',
                                  alignItems:'center', justifyContent:'center',
                                  padding:10, gap:8, cursor:'default',
                                }}
                              >
                                <div style={{
                                  fontSize:11, fontWeight:600, color:'var(--text-primary)',
                                  textAlign:'center', lineHeight:1.4,
                                }}>
                                  This photo has already been posted. Use again?
                                </div>
                                <div style={{ display:'flex', gap:6, width:'100%' }}>
                                  <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); confirmPickerReuse(p.id); }}
                                    style={{
                                      flex:1, padding:'6px 0', background:'var(--accent)', color:'#0d0f14',
                                      border:'none', borderRadius:6, fontWeight:700, fontSize:10,
                                      fontFamily:'var(--font-head)', letterSpacing:'0.04em', cursor:'pointer',
                                    }}
                                  >Confirm</button>
                                  <button
                                    type="button"
                                    onClick={e => { e.stopPropagation(); setPickerReuseConfirmId(null); }}
                                    style={{
                                      flex:1, padding:'6px 0', background:'rgba(255,255,255,0.08)', color:'var(--text-secondary)',
                                      border:'1px solid rgba(255,255,255,0.14)', borderRadius:6, fontWeight:700, fontSize:10,
                                      fontFamily:'var(--font-head)', letterSpacing:'0.04em', cursor:'pointer',
                                    }}
                                  >Cancel</button>
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ padding:'6px 8px', fontSize:10, color:'var(--text-muted)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                            {p.filename}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer with confirm button */}
            {!libraryLoading && libraryPhotos.length > 0 && requiredPhotoCount > 0 && (
              <div style={{ padding:'14px 20px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                <button
                  type="button"
                  onClick={confirmPickerSelection}
                  disabled={pickerSelected.length !== requiredPhotoCount}
                  style={{
                    width:'100%', padding:'11px 14px',
                    background: pickerSelected.length === requiredPhotoCount ? 'var(--accent)' : 'rgba(255,255,255,0.06)',
                    color: pickerSelected.length === requiredPhotoCount ? '#0d0f14' : 'var(--text-muted)',
                    border:'none', borderRadius:9, fontWeight:700, fontSize:12,
                    fontFamily:'var(--font-head)', letterSpacing:'0.03em',
                    cursor: pickerSelected.length === requiredPhotoCount ? 'pointer' : 'not-allowed',
                  }}
                >
                  {pickerSelected.length === requiredPhotoCount
                    ? `Confirm ${requiredPhotoCount} Photo${requiredPhotoCount !== 1 ? 's' : ''}`
                    : `Select ${requiredPhotoCount - pickerSelected.length} more`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// POST PERFORMANCE PANEL
// ============================================================
function lightenForDark(hex) {
  const m = /^#?([a-f\d]{6})$/i.exec(hex || '');
  if (!m) return hex || '#31DCBA';
  let r = parseInt(m[1].slice(0,2), 16);
  let g = parseInt(m[1].slice(2,4), 16);
  let b = parseInt(m[1].slice(4,6), 16);
  const lum = (rr, gg, bb) => 0.299*rr + 0.587*gg + 0.114*bb;
  let safety = 0;
  while (lum(r, g, b) < 100 && safety < 20) {
    r = Math.min(255, Math.round(r + (255 - r) * 0.15));
    g = Math.min(255, Math.round(g + (255 - g) * 0.15));
    b = Math.min(255, Math.round(b + (255 - b) * 0.15));
    safety++;
  }
  return '#' + [r,g,b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function PostPerformancePanel({ clientId, isSelfServe }) {
  // Google Business numbers sit at the top of this panel when Google is
  // connected for the client being viewed.
  const gbpConnected = useGoogleBizConnected(clientId);
  // Phase 4: reads move to /api/post-performance and must re-run when the
  // act-as-client context changes (Phase 3 reasoning).
  const { viewingAsClientId } = useViewingAs();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [accentColour, setAccentColour] = useState('#31DCBA');
  const [latestFetchedAt, setLatestFetchedAt] = useState(null);
  const [sortKey, setSortKey] = useState('published');
  const [sortDir, setSortDir] = useState('desc');
  const [loadError, setLoadError] = useState(null);   // { status, message }
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    // Phase 4: two parallel direct-Supabase reads became one backend round trip.
    //
    // The insights read was ALSO silently truncated: it ran with no range(), so
    // PostgREST's implicit 1000-row ceiling capped it, and two clients already
    // exceed that inside the 30-day window (2290 and 2494 rows, measured
    // 2026-09-08). Every figure below — posts published, average engagement,
    // total reach, total likes — was therefore computed from a partial window
    // with nothing saying so. The endpoint pages the query instead. Same bug
    // class Phase 2 fixed on the engagement average, and the same call:
    // replicating it would have meant writing .limit(1000) on purpose.
    // A non-2xx is a FAILURE TO REPORT, never an empty result. This previously
    // read `r.ok ? r.json() : null`, so a 401/403/500 collapsed to null, `rows`
    // stayed [], and the panel rendered the zero-rows empty state
    // — a broken endpoint and a genuinely quiet 30-day window were pixel
    // identical, and the only trace was a console line. The two states are now
    // distinct in the UI, and the error carries its status code.
    clientScopedFetch(`${BACKEND}/api/post-performance`)
      .then(async r => {
        if (!r.ok) {
          // Surface the backend's own message when it sent one — server.js
          // answers `{ error }` on 500 and on the 403 from requireOwnerOrAdmin.
          let detail = '';
          try { detail = (await r.json())?.error || ''; } catch { /* non-JSON body */ }
          const err = new Error(detail || `Request failed (HTTP ${r.status})`);
          err.status = r.status;
          throw err;
        }
        return r.json();
      })
      .then(d => {
      if (cancelled) return;
      setLoadError(null);
      const colour = d?.accentColor;
      if (colour) setAccentColour(colour);
      const data = d?.rows ?? [];
      setRows(data);
      const latest = data.reduce((acc, r) => {
        if (!r.fetched_at) return acc;
        return !acc || r.fetched_at > acc ? r.fetched_at : acc;
      }, null);
      setLatestFetchedAt(latest);
    }).catch(err => {
      if (cancelled) return;
      // Loud by design: this is the only signal when the endpoint is refusing.
      console.error(
        '[PostPerformance] load failed:',
        err?.status ? `HTTP ${err.status} — ${err.message}` : (err?.message || err),
        err,
      );
      setRows([]);
      setLoadError({ status: err?.status ?? null, message: err?.message || 'Could not reach the server' });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId, reloadToken]);

  const lightenedAccent = useMemo(() => lightenForDark(accentColour), [accentColour]);

  // One table row per post PER PLATFORM, carrying only the newest snapshot.
  //
  // `rows` is one row per METRICS SNAPSHOT — the cron refetches roughly every
  // 6 hours — so rendering it raw showed the same post four-plus times a day,
  // each looking identical. Grouping collapses that.
  //
  // The key is post_id + platform, not post_id alone: a post cross-posted to
  // Instagram and Facebook gets a SEPARATE post_insights row per platform
  // (server.js writes platform:'fb' and platform:'ig' independently), so
  // keying on post_id would silently drop one platform's numbers — a worse
  // bug than the duplicates, and invisible, since the surviving row still
  // looks right. Rows with no post_id fall back to their own id so they are
  // never merged into one another.
  const { tableRows, snapshotCounts } = useMemo(() => {
    const ts = (r) => {
      const t = r.fetched_at ? Date.parse(r.fetched_at) : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    const latest = new Map();
    const counts = new Map();
    for (const r of rows) {
      const key = r.post_id ? `${r.post_id}::${r.platform || ''}` : `row::${r.id}`;
      counts.set(key, (counts.get(key) || 0) + 1);
      const prev = latest.get(key);
      if (!prev || ts(r) > ts(prev)) latest.set(key, r);
    }
    return { tableRows: [...latest.values()], snapshotCounts: counts };
  }, [rows]);

  const groupKey = (r) => (r.post_id ? `${r.post_id}::${r.platform || ''}` : `row::${r.id}`);

  // Derived from tableRows, NOT from `rows`, and deliberately from the same
  // deduped list the table renders — these cards sit directly above the table
  // and have to agree with it.
  //
  // They previously reduced over every raw snapshot, so a post the cron had
  // refetched 120 times over the 30-day window contributed its reach and likes
  // 120 times. Total Reach and Total Likes were inflated by roughly the average
  // snapshot count; Avg Engagement had a subtler version of the same fault,
  // weighting each post by how long it had been collecting rather than
  // treating each post equally.
  //
  // Errors are filtered AFTER grouping, matching the table: a row whose newest
  // snapshot failed renders "—" there, so it must not contribute here either.
  const stats = useMemo(() => {
    const valid = tableRows.filter(r => r.fetch_status !== 'error');
    // Unchanged in meaning: grouping never drops a post_id, so this is the
    // same set it was before.
    const uniquePostIds = new Set(valid.map(r => r.post_id));
    const totalReach = valid.reduce((s, r) => s + (r.reach || 0), 0);
    const totalLikes = valid.reduce((s, r) => s + (r.likes || 0), 0);
    const avgEng = valid.length
      ? valid.reduce((s, r) => s + (Number(r.engagement_rate) || 0), 0) / valid.length * 100
      : 0;
    return {
      postsPublished: uniquePostIds.size,
      avgEngagement: avgEng,
      totalReach,
      totalLikes,
      hasValid: valid.length > 0,
    };
  }, [tableRows]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const get = (r) => {
      switch (sortKey) {
        case 'caption':    return (r.posts?.caption || '').toLowerCase();
        case 'platform':   return r.platform || '';
        case 'type':       return (r.posts?.is_ig_story || r.posts?.is_fb_story) ? 'Story' : 'Post';
        case 'published':  return r.posts?.scheduled_at || '';
        case 'likes':      return r.fetch_status === 'error' ? -Infinity : (r.likes || 0);
        case 'comments':   return r.fetch_status === 'error' ? -Infinity : (r.comments || 0);
        case 'reach':      return r.fetch_status === 'error' ? -Infinity : (r.reach || 0);
        case 'engagement': return r.fetch_status === 'error' ? -Infinity : (Number(r.engagement_rate) || 0);
        default:           return 0;
      }
    };
    return [...tableRows].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [tableRows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'caption' || key === 'platform' ? 'asc' : 'desc'); }
  };

  // The Google Business section does not depend on the IG/FB numbers below:
  // it shows above the loading, error and empty states too, so a Google-only
  // client (no IG/FB rows) or a failed IG/FB load still sees Google. Kept as
  // the FIRST child of the outer div in every state, so React keeps the same
  // GoogleStats instance (no refetch) when the panel moves between states.
  const googleSection = gbpConnected ? <GoogleStats clientId={clientId} /> : null;
  const withGoogle = (stateBox) => (
    <div style={{display:'flex',flexDirection:'column',gap:24,padding: googleSection ? '32px 32px 0' : 0}}>
      {googleSection}
      {stateBox}
    </div>
  );

  if (loading) {
    return withGoogle(
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:420,padding:32}}>
        <div style={{
          width:32,height:32,borderRadius:'50%',
          border:'2px solid rgba(255,255,255,0.1)',
          borderTopColor:lightenedAccent,
          animation:'ppp-spin 0.8s linear infinite',
        }}/>
        <style>{`@keyframes ppp-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Checked BEFORE the empty state: a failed load also leaves rows empty, and
  // falling through to the empty state is exactly the bug this branch removes.
  if (loadError) {
    const unauthorised = loadError.status === 401 || loadError.status === 403;
    return withGoogle(
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:420,padding:32}}>
        <div style={{
          display:'flex',flexDirection:'column',alignItems:'center',gap:16,
          background:'rgba(255,255,255,0.04)',backdropFilter:'blur(12px)',
          border:'1px solid rgba(245,166,35,0.3)',borderRadius:16,
          padding:'48px 56px',textAlign:'center',maxWidth:460,
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f5a623" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <path d="M12 9v4M12 17h.01"/>
          </svg>
          <div style={{fontFamily:'var(--font-head)',fontSize:18,fontWeight:700,color:'var(--text-primary)',letterSpacing:.5}}>
            Couldn&rsquo;t load performance data
          </div>
          <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>
            {unauthorised
              ? 'Your session may have expired. Sign out and back in, then try again.'
              : "The server didn't return your stats. This is a loading problem, not an empty account \u2014 your data is safe."}
          </div>
          <div style={{fontSize:11,color:'var(--text-muted)',fontFamily:'var(--font-mono, monospace)',opacity:.75}}>
            {loadError.status ? `HTTP ${loadError.status}` : 'Network error'}
            {loadError.message ? ` \u00b7 ${loadError.message}` : ''}
          </div>
          <button
            onClick={() => setReloadToken(t => t + 1)}
            style={{
              marginTop:4,padding:'8px 20px',borderRadius:8,cursor:'pointer',
              background:'transparent',border:'1px solid rgba(245,166,35,0.4)',
              color:'#f5a623',fontSize:13,fontFamily:'var(--font-head)',letterSpacing:.5,
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return withGoogle(
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:420,padding:32}}>
        <div style={{
          display:'flex',flexDirection:'column',alignItems:'center',gap:16,
          background:'rgba(255,255,255,0.04)',backdropFilter:'blur(12px)',
          border:'1px solid rgba(255,255,255,0.08)',borderRadius:16,
          padding:'48px 56px',textAlign:'center',maxWidth:420,
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={lightenedAccent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2"/>
            <path d="M8 21h8M12 17v4"/>
            <path d="M7 13V9M10 13V7M13 13v-3M16 13V5"/>
          </svg>
          <div style={{fontFamily:'var(--font-head)',fontSize:18,fontWeight:700,color:'var(--text-primary)',letterSpacing:.5}}>
            Data still populating
          </div>
          <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>
            Your post performance data will appear here once posts start publishing.
          </div>
        </div>
      </div>
    );
  }

  const fmtNum = (n) => n >= 1000 ? `${(n/1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
  const fmtPct = (n) => `${(n).toFixed(1)}%`;
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: getDisplayTimeZone() });
  };
  const truncate = (s, n) => {
    if (!s) return '';
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
  };
  const cellNum = (row, val, formatter = fmtNum) =>
    row.fetch_status === 'error' ? '—' : formatter(val || 0);

  const cardStyle = {
    background:'rgba(255,255,255,0.04)',backdropFilter:'blur(12px)',
    border:'1px solid rgba(255,255,255,0.08)',borderRadius:16,
    padding:24,display:'flex',flexDirection:'column',gap:8,
  };
  const statNumberStyle = {
    fontFamily:'var(--font-head)',fontSize:32,fontWeight:700,
    color:lightenedAccent,letterSpacing:.5,lineHeight:1,
  };
  const statLabelStyle = { fontSize:12,color:'var(--text-muted)',letterSpacing:.4,textTransform:'uppercase' };
  const thBase = {
    textAlign:'left',padding:'10px 12px',fontSize:11,fontWeight:600,
    color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:.5,
    userSelect:'none',whiteSpace:'nowrap',
    borderBottom:'1px solid rgba(255,255,255,0.08)',
  };
  const thSortable = { ...thBase, cursor:'pointer' };
  const tdStyle = { padding:'12px',fontSize:13,color:'var(--text-primary)',borderBottom:'1px solid rgba(255,255,255,0.06)',verticalAlign:'middle' };
  const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const platformPill = (p) => {
    const isIg = (p || '').toLowerCase().startsWith('i');
    return (
      <span style={{
        display:'inline-block',padding:'3px 10px',borderRadius:999,
        fontSize:10,fontWeight:700,color:'#fff',letterSpacing:.5,
        background: isIg
          ? 'linear-gradient(135deg,#e6683c,#dc2743,#cc2366)'
          : '#1877f2',
      }}>{isIg ? 'IG' : 'FB'}</span>
    );
  };

  // Stories and feed posts behave nothing alike — a story is gone in 24h and
  // its reach curve is flat by then — so the table shouldn't present them as
  // the same thing. Both flags live on the joined post, not the insights row.
  const typePill = (r) => {
    const isStory = !!(r.posts?.is_ig_story || r.posts?.is_fb_story);
    return (
      <span style={{
        display:'inline-block',padding:'2px 8px',borderRadius:5,
        fontSize:9,fontWeight:700,letterSpacing:.6,textTransform:'uppercase',
        color: isStory ? '#c9a3ff' : 'var(--text-secondary)',
        background: isStory ? 'rgba(160,110,255,0.14)' : 'rgba(255,255,255,0.05)',
        border: `1px solid ${isStory ? 'rgba(160,110,255,0.3)' : 'rgba(255,255,255,0.09)'}`,
      }}>{isStory ? 'Story' : 'Post'}</span>
    );
  };

  // A post the cron has only seen once is too new to read anything into —
  // its zeros mean "not measured yet", not "nobody engaged".
  const isCollecting = (r) => r.fetch_status !== 'error' && (snapshotCounts.get(groupKey(r)) || 0) <= 1;

  return (
    <div style={{padding:32,display:'flex',flexDirection:'column',gap:24}}>
      {googleSection}
      <style>{`@keyframes ppp-collect { 0%,100% { opacity:.3 } 50% { opacity:1 } }`}</style>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16}}>
        <div style={cardStyle}>
          <div style={statNumberStyle}>{stats.hasValid ? stats.postsPublished : '—'}</div>
          <div style={statLabelStyle}>Posts Published</div>
        </div>
        <div style={cardStyle}>
          <div style={statNumberStyle}>{stats.hasValid ? fmtPct(stats.avgEngagement) : '—'}</div>
          <div style={statLabelStyle}>Avg Engagement</div>
        </div>
        <div style={cardStyle}>
          <div style={statNumberStyle}>{stats.hasValid ? fmtNum(stats.totalReach) : '—'}</div>
          <div style={statLabelStyle}>Total Reach</div>
        </div>
        <div style={cardStyle}>
          <div style={statNumberStyle}>{stats.hasValid ? fmtNum(stats.totalLikes) : '—'}</div>
          <div style={statLabelStyle}>Total Likes</div>
        </div>
      </div>

      <div style={{
        display:'flex',alignItems:'flex-start',gap:10,
        padding:'11px 16px',borderRadius:10,
        background:'rgba(255,255,255,0.04)',
        border:'1px solid rgba(255,255,255,0.08)',
        fontSize:12,color:'var(--text-muted)',lineHeight:1.55,
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={lightenedAccent}
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
             style={{flexShrink:0,marginTop:1}} aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <div>
          Metrics refresh roughly every 6 hours. A new post can take a few days to
          fully reflect its performance — low or zero numbers on a recent post usually
          mean the data hasn&rsquo;t landed yet, not that the post underperformed.
        </div>
      </div>

      <div style={{...cardStyle,padding:0,overflow:'hidden'}}>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr>
                <th style={thBase}>Post</th>
                <th style={thSortable} onClick={() => toggleSort('caption')}>Caption{sortArrow('caption')}</th>
                <th style={thSortable} onClick={() => toggleSort('platform')}>Platform{sortArrow('platform')}</th>
                <th style={thSortable} onClick={() => toggleSort('type')}>Type{sortArrow('type')}</th>
                <th style={thSortable} onClick={() => toggleSort('published')}>Published{sortArrow('published')}</th>
                <th style={{...thSortable,textAlign:'right'}} onClick={() => toggleSort('likes')}>Likes{sortArrow('likes')}</th>
                <th style={{...thSortable,textAlign:'right'}} onClick={() => toggleSort('comments')}>Comments{sortArrow('comments')}</th>
                <th style={{...thSortable,textAlign:'right'}} onClick={() => toggleSort('reach')}>Reach{sortArrow('reach')}</th>
                <th style={{...thSortable,textAlign:'right'}} onClick={() => toggleSort('engagement')}>Engagement{sortArrow('engagement')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={groupKey(r)}>
                  <td style={tdStyle}>
                    {r.posts?.image_url
                      ? <img src={r.posts.image_url} alt="" style={{width:40,height:40,borderRadius:6,objectFit:'cover',display:'block'}} />
                      : <div style={{width:40,height:40,borderRadius:6,background:'rgba(255,255,255,0.05)'}} />
                    }
                  </td>
                  <td style={{...tdStyle,maxWidth:320}}>{truncate(r.posts?.caption, 60)}</td>
                  <td style={tdStyle}>{platformPill(r.platform)}</td>
                  <td style={tdStyle}>{typePill(r)}</td>
                  <td style={tdStyle}>
                    <div>{fmtDate(r.posts?.scheduled_at)}</div>
                    {isCollecting(r) && (
                      <div
                        title="Only one metrics snapshot so far — these numbers are still early."
                        style={{
                          display:'inline-flex',alignItems:'center',gap:5,marginTop:4,
                          fontSize:10,color:'var(--text-muted)',letterSpacing:.3,whiteSpace:'nowrap',
                        }}
                      >
                        <span style={{
                          width:5,height:5,borderRadius:'50%',background:lightenedAccent,
                          animation:'ppp-collect 1.8s ease-in-out infinite',
                        }} />
                        Still collecting
                      </div>
                    )}
                  </td>
                  <td style={{...tdStyle,textAlign:'right'}}>{cellNum(r, r.likes)}</td>
                  <td style={{...tdStyle,textAlign:'right'}}>{cellNum(r, r.comments)}</td>
                  <td style={{...tdStyle,textAlign:'right'}}>{cellNum(r, r.reach)}</td>
                  <td style={{...tdStyle,textAlign:'right',color: r.fetch_status === 'error' ? 'var(--text-primary)' : lightenedAccent,fontWeight:600}}>
                    {cellNum(r, Number(r.engagement_rate) * 100, fmtPct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{fontSize:11,color:'var(--text-muted)',textAlign:'right'}}>
        Last updated {timeAgo(latestFetchedAt)}
      </div>
    </div>
  );
}

// ============================================================
// ACCOUNT & PERKS PANEL — Session 9
// ============================================================
function AccountPanel({ isSelfServe, planStatus, onRetryPlan, stripeCustomerId, clientId }) {
  // Read only to decide whether the upgrade-form email prefill is safe; see
  // openUpgradeModal below.
  const { viewingAsClientId } = useViewingAs();
  const [addingMember, setAddingMember] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('Manager');
  const [members, setMembers] = useState([]);

  // Photography Partner application modal
  const PHOTOG_FIELDS = { fullName:'', email:'', phone:'', city:'', portfolio:'', bio:'', pricePoint:'' };
  const [photogOpen, setPhotogOpen] = useState(false);
  const [photogForm, setPhotogForm] = useState(PHOTOG_FIELDS);
  const [photogStatus, setPhotogStatus] = useState('idle'); // idle | loading | success | error

  const photogInputStyle = {
    width:'100%', boxSizing:'border-box', padding:'9px 11px',
    background:'rgba(10,13,18,0.7)', border:'1px solid rgba(255,255,255,0.1)',
    borderRadius:8, color:'#e7ecf2', fontSize:14,
    fontFamily:"'Barlow', var(--font-body, sans-serif)", outline:'none',
  };

  function openPhotogModal() {
    setPhotogForm(PHOTOG_FIELDS);
    setPhotogStatus('idle');
    setPhotogOpen(true);
  }

  async function handlePhotogSubmit(e) {
    e.preventDefault();
    setPhotogStatus('loading');
    try {
      const res = await authedFetch('https://autopilot-backend-pn9r.onrender.com/api/photography-application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(photogForm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error('Request failed');
      setPhotogStatus('success');
    } catch {
      setPhotogStatus('error');
    }
  }

  // Brand header state — pulled from brand_profile so the account header reflects the logged-in client
  const [bizName, setBizName] = useState('');
  const [bizLocation, setBizLocation] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  // Phase 2: moved off direct Supabase onto the backend read (see Onboarding).
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/brand-profile`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const data = d?.profile;
        if (cancelled || !data) return;
        if (data.brand_name)     setBizName(data.brand_name);
        if (data.brand_industry) setBizLocation(data.brand_industry);
        if (data.logo_url)       setLogoUrl(data.logo_url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  const initials = bizName.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();

  // Stripe billing state
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [refillLoading, setRefillLoading] = useState(false);

  useEffect(() => {
    if (!stripeCustomerId) return;
    setBillingLoading(true);
    authedFetch('/api/billing')
      .then(r => r.json())
      .then(data => { setBilling(data); setBillingLoading(false); })
      .catch(() => setBillingLoading(false));
  }, [stripeCustomerId]);

  async function handleManageBilling() {
    if (!stripeCustomerId) return;
    setPortalLoading(true);
    try {
      const res = await authedFetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank');
    } catch (e) { /* silent */ }
    setPortalLoading(false);
  }

  async function handleImageRefill() {
    if (!stripeCustomerId) return;
    setRefillLoading(true);
    try {
      const res = await authedFetch('/api/create-refill-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) { /* silent */ }
    setRefillLoading(false);
  }

  // ── Upgrade to Managed — an application, not a purchase ──
  // Nothing is charged here. The submission is reviewed manually and the
  // Stripe swap happens later from the Admin panel.
  const UPGRADE_FIELDS = { contactName:'', contactEmail:'', phone:'', currentChallenges:'', goals:'', notes:'' };
  const [upgradeOpen, setUpgradeOpen]     = useState(false);
  const [upgradeForm, setUpgradeForm]     = useState(UPGRADE_FIELDS);
  const [upgradeStatus, setUpgradeStatus] = useState('idle'); // idle | loading | success | error

  async function openUpgradeModal() {
    setUpgradeStatus('idle');
    // Prefill from the brand_profile row already fetched for the header, plus
    // the signed-in user's email — one less thing for the client to retype.
    //
    // DELIBERATELY BLANK WHILE VIEWING AS A CLIENT. auth.getUser() returns the
    // SIGNED-IN user, which under the admin act-as-client view is the admin,
    // not the client the form is about — so prefilling it would put the admin's
    // address on the client's upgrade application as the contact. There is no
    // override to apply here: this is a session call, not a table read, so
    // requireOwnerOrAdmin has nothing to act on. Reading the viewed client's
    // address instead would mean exposing auth.users emails through a new
    // endpoint, which is a bigger decision than a prefill warrants. An empty
    // field the admin fills in deliberately is the honest middle.
    let email = '';
    if (!viewingAsClientId) {
      try {
        const { data } = await supabase.auth.getUser();
        email = data?.user?.email || '';
      } catch { /* prefill is best-effort */ }
    }
    setUpgradeForm({ ...UPGRADE_FIELDS, contactEmail: email });
    setUpgradeOpen(true);
  }

  async function handleUpgradeSubmit(e) {
    e.preventDefault();
    setUpgradeStatus('loading');
    try {
      const res = await authedFetch(`${BACKEND}/api/plan-upgrade-submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id:          clientId,
          stripe_customer_id: stripeCustomerId,
          business_name:      bizName,
          contact_name:       upgradeForm.contactName,
          contact_email:      upgradeForm.contactEmail,
          phone:              upgradeForm.phone,
          current_challenges: upgradeForm.currentChallenges,
          goals:              upgradeForm.goals,
          notes:              upgradeForm.notes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error('Request failed');
      setUpgradeStatus('success');
    } catch {
      setUpgradeStatus('error');
    }
  }

  // ── Downgrade to Self-Serve — self-service, effective at period end ──
  const [downgradeOpen, setDowngradeOpen]       = useState(false);
  const [downgradeStatus, setDowngradeStatus]   = useState('idle'); // idle | loading | success | error
  const [keepManagedStatus, setKeepManagedStatus] = useState('idle'); // idle | loading | error
  // Locally-applied result so the card updates without a full billing refetch.
  const [downgradeOverride, setDowngradeOverride]   = useState(undefined);

  async function handleDowngradeConfirm() {
    if (!stripeCustomerId) return;
    setDowngradeStatus('loading');
    try {
      const res = await authedFetch(`${BACKEND}/api/schedule-downgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: stripeCustomerId, clientId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Request failed');
      setDowngradeOverride({
        effectiveDate: data.effectiveDate
          ? new Date(data.effectiveDate).toLocaleDateString('en-CA', { year:'numeric', month:'long', day:'numeric' })
          : null,
      });
      setDowngradeStatus('success');
    } catch {
      setDowngradeStatus('error');
    }
  }

  async function handleKeepManaged() {
    if (!stripeCustomerId) return;
    setKeepManagedStatus('loading');
    try {
      const res = await authedFetch(`${BACKEND}/api/cancel-scheduled-downgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: stripeCustomerId, clientId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Request failed');
      setDowngradeOverride(null);
      setKeepManagedStatus('idle');
    } catch {
      // The downgrade is still scheduled — the client must not walk away
      // believing they've cancelled it.
      setKeepManagedStatus('error');
    }
  }

  const billingStatus = billing?.status || '—';
  const billingPlan = billing?.planName || '—';
  const billingNextInvoice = billing?.nextInvoice || '—';
  const billingPayment = billing?.cardLast4
    ? `${billing.cardBrand ? billing.cardBrand.charAt(0).toUpperCase() + billing.cardBrand.slice(1) : 'Card'} ending ${billing.cardLast4}`
    : '—';
  const billingMemberSince = billing?.memberSince || '';
  // `undefined` = no local change yet, use whatever /api/billing reported.
  // `null` = we just cancelled one. An object = we just scheduled one.
  const scheduledDowngrade = downgradeOverride !== undefined
    ? downgradeOverride
    : (billing?.scheduledDowngrade || null);

  const managedFeatures = [
    { label:'Unlimited posts — fully managed', included:true },
    { label:'Full post approval workflow', included:true },
    { label:'Image generation — unlimited', included:true },
    { label:'Post performance analytics', included:true },
    { label:'Up to 3 team members', included:true },
    { label:'Priority support', included:true },
  ];
  const selfServeFeatures = [
    { label:'24 images/mo · $5 refill for 24 more', included:true },
    { label:'Self-serve post builder', included:true },
    { label:'Content calendar access', included:true },
    { label:'Post performance analytics', included:true },
    { label:'Brand Hub + Photo Library', included:true },
    { label:'Priority support', included:false },
  ];
  const features = isSelfServe ? selfServeFeatures : managedFeatures;
  // Everything in the Plan card — badge, price, setup line, feature list —
  // asserts one plan or the other. None of it may render until the plan is
  // confirmed, or a Self-Serve client whose lookup fails is told they pay $500.
  const planConfirmed = planStatus === 'resolved';

  const perks = [
    {
      icon:'🖼️',
      name:'Post Generator',
      desc: isSelfServe ? '24 generated images/mo included · Buy 24 more for $5' : 'Included with your plan',
      status: 'included',
      pill: 'Included',
      showBtn: isSelfServe,
      btnLabel:'Buy 24 more for $5',
      onBtn: handleImageRefill,
      btnLoading: refillLoading,
      btnLoadingLabel: 'Redirecting…',
    },
    {
      icon:'📸',
      name:'Product Photography',
      desc:'Professional-grade product shots for your menu items and merch.',
      subDesc:'Okanagan-based clients. Outside the area? Let us know if you have a local photographer — we\'d love to connect.',
      status:'soon',
      pill:'Coming Soon',
      showBtn:true,
      btnLabel:'Contact for More Info',
      onBtn: () => window.location.href = 'mailto:hello@autopilotdigital.ca?subject=Product%20Photography%20Inquiry',
      secondBtn: { label: 'Apply as a Photography Partner', onClick: openPhotogModal },
    },
    {
      icon:'✨',
      name:'Product Photo Enhancement',
      desc:'Your products, studio-quality. Perfect lighting, custom backgrounds, pro finish.',
      showBtn:true,
      btnLabel:'Contact for More Info',
      onBtn: () => window.location.href = 'mailto:hello@autopilotdigital.ca?subject=Product%20Photo%20Enhancement%20Inquiry',
    },
    {
      icon:'🎬',
      name:'Video Editor',
      desc:'Edit, trim, and enhance your videos directly in the dashboard.',
      status:'soon',
      pill:'Coming Soon',
      showBtn:true,
      btnLabel:'Coming Soon',
      btnDisabled:true,
    },
    {
      icon:'🌐',
      name:'Website Build',
      desc:'A clean, fast business site built and handed off to you',
      status:'add-on',
      pill:'Add-On',
      showBtn:true,
      btnLabel:'Get a Quote',
      onBtn: () => window.open('https://www.autopilotdigital.ca/#services', '_blank'),
    },
  ];

  function handleAddMember() {
    if (!newEmail.trim()) return;
    setMembers(m => [...m, {
      initials: newEmail.slice(0,2).toUpperCase(),
      name: newEmail.split('@')[0],
      email: newEmail,
      role: newRole,
      status:'pending',
      you:false,
    }]);
    setNewEmail('');
    setAddingMember(false);
  }

  return (
    <div className="acct-wrap">

      {/* HEADER */}
      <div className="acct-header">
        {logoUrl
          ? <img src={logoUrl} alt="Brand logo" className="acct-avatar-lg" style={{ objectFit:'contain', background:'var(--bg-card2)' }} />
          : <div className="acct-avatar-lg">{initials}</div>
        }
        <div className="acct-header-info">
          <div className="acct-biz-name">{bizName}</div>
          <div className="acct-biz-sub">
            {bizLocation}
            {bizLocation && billingMemberSince && ' · '}
            {billingMemberSince && `Member since ${billingMemberSince}`}
          </div>
        </div>
        {planConfirmed ? (
          <div className={`acct-plan-badge ${isSelfServe ? 'self-serve' : 'managed'}`}>
            <span className="acct-plan-dot" />
            {isSelfServe ? 'Self-Serve' : 'Managed'}
          </div>
        ) : (
          <div
            className="acct-plan-badge"
            style={{
              background:'rgba(255,255,255,0.05)', color:'var(--text-muted)',
              border:'1px solid rgba(255,255,255,0.12)',
            }}
          >
            <span className="acct-plan-dot" />
            {planStatus === 'loading' ? 'Checking plan…' : 'Plan unconfirmed'}
          </div>
        )}
      </div>

      {/* ROW 1: Plan + Billing */}
      <div className="acct-grid">

        {/* Plan Card */}
        <div className="acct-card">
          <div className="acct-card-title">
            Your Plan
          </div>
          {planConfirmed ? (
            <>
              <div className="acct-plan-price">
                <span className="acct-price-num">{isSelfServe ? '$79' : '$500'}</span>
                <span className="acct-price-period">/month</span>
              </div>
              {!isSelfServe && <div className="acct-price-setup">$500/mo · Setup fee $100 (reg. $300) — June &amp; July only</div>}
              <div className="acct-feature-list">
                {features.map((f,i) => (
                  <div className="acct-feature" key={i}>
                    <div className={f.included ? 'acct-feature-check' : 'acct-feature-x'}>
                      {f.included ? '✓' : '×'}
                    </div>
                    {f.label}
                  </div>
                ))}
              </div>
            </>
          ) : planStatus === 'loading' ? (
            <div style={{fontSize:12, color:'var(--text-muted)', padding:'12px 0'}}>
              Loading your plan…
            </div>
          ) : (
            <div style={{fontSize:12, color:'var(--text-secondary)', lineHeight:1.6, padding:'8px 0 4px'}}>
              We couldn't load your plan details just now. Your subscription and billing
              are unaffected — this only limits what this page can show.
              {onRetryPlan && (
                <div style={{marginTop:10}}>
                  <button
                    onClick={onRetryPlan}
                    style={{
                      background:'transparent', border:'1px solid var(--accent-border)',
                      color:'var(--accent)', borderRadius:6, padding:'5px 11px',
                      fontFamily:'var(--font)', fontSize:11, fontWeight:700, cursor:'pointer',
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Plan-change CTAs stay hidden until /api/customer has confirmed the
              plan against Stripe — never offer to change a plan we're guessing at. */}
          {planStatus === 'resolved' && isSelfServe && (
            <div className="acct-upgrade-box">
              <div className="acct-upgrade-text">
                <strong>Ready for more?</strong> Upgrade to Managed and we handle everything — approvals, generation, strategy.
              </div>
              <button className="acct-upgrade-btn" onClick={openUpgradeModal}>Upgrade →</button>
            </div>
          )}

          {planStatus === 'resolved' && !isSelfServe && (
            scheduledDowngrade ? (
              <div style={{
                marginTop:14, padding:'11px 13px', borderRadius:9,
                background:'rgba(245,166,35,0.08)', border:'1px solid rgba(245,166,35,0.25)',
                fontSize:12, color:'var(--text-secondary)', lineHeight:1.5,
              }}>
                Switching to Self-Serve on <strong style={{color:'#f5a623'}}>{scheduledDowngrade.effectiveDate}</strong>.
                You keep Managed until then.
                <div style={{marginTop:8}}>
                  <button
                    onClick={handleKeepManaged}
                    disabled={keepManagedStatus === 'loading'}
                    style={{
                      background:'transparent', border:'1px solid rgba(245,166,35,0.4)',
                      color:'#f5a623', borderRadius:7, padding:'6px 12px',
                      fontFamily:'var(--font)', fontSize:12, fontWeight:700,
                      cursor: keepManagedStatus === 'loading' ? 'default' : 'pointer',
                      opacity: keepManagedStatus === 'loading' ? 0.6 : 1,
                    }}
                  >
                    {keepManagedStatus === 'loading' ? 'Cancelling…' : 'Keep Managed'}
                  </button>
                </div>
                {keepManagedStatus === 'error' && (
                  <div style={{marginTop:8, fontSize:12, color:'#ff7b7b', lineHeight:1.5}}>
                    Couldn't cancel the switch — <strong>your downgrade is still scheduled</strong>.
                    Try again, or email hello@autopilotdigital.ca.
                  </div>
                )}
              </div>
            ) : (
              <div style={{marginTop:14, textAlign:'center'}}>
                <span
                  onClick={() => { setDowngradeStatus('idle'); setDowngradeOpen(true); }}
                  style={{
                    fontSize:11, color:'var(--text-muted)', cursor:'pointer',
                    textDecoration:'underline', textUnderlineOffset:3,
                  }}
                >
                  Switch to Self-Serve
                </span>
              </div>
            )
          )}
        </div>

        {/* Billing Card */}
        <div className="acct-card">
          <div className="acct-card-title">
            Billing
            <span
              className="acct-card-action"
              style={{cursor: stripeCustomerId ? 'pointer' : 'default', opacity: portalLoading ? 0.6 : 1}}
              onClick={handleManageBilling}
            >
              {portalLoading ? 'Opening…' : 'Manage Billing →'}
            </span>
          </div>
          {billingLoading ? (
            <div style={{fontSize:12,color:'var(--text-muted)',padding:'12px 0'}}>Loading billing info…</div>
          ) : (
            <>
              <div className="acct-billing-row">
                <span className="acct-billing-label">Status</span>
                <span className="acct-billing-value" style={{textTransform:'capitalize'}}>{billingStatus}</span>
                {billingStatus !== '—' && (
                  <span className={`acct-billing-tag ${billingStatus === 'active' ? 'active' : 'pending'}`}>
                    {billingStatus.charAt(0).toUpperCase() + billingStatus.slice(1)}
                  </span>
                )}
              </div>
              <div className="acct-billing-row">
                <span className="acct-billing-label">Plan</span>
                <span className="acct-billing-value">{billingPlan}</span>
              </div>
              <div className="acct-billing-row">
                <span className="acct-billing-label">Next Invoice</span>
                <span className="acct-billing-value">{billingNextInvoice}</span>
              </div>
              <div className="acct-billing-row">
                <span className="acct-billing-label">Payment</span>
                <span className="acct-billing-value">{billingPayment}</span>
              </div>
              <div className="acct-billing-row">
                <span className="acct-billing-label">Member Since</span>
                <span className="acct-billing-value">{billingMemberSince}</span>
              </div>
            </>
          )}
          <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid rgba(255,255,255,0.06)'}}>
            <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6}}>
              Questions about your invoice? Email us at{' '}
              <span style={{color:'var(--accent)',cursor:'pointer'}}>hello@autopilotdigital.ca</span>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 2: Team + Perks */}
      <div className="acct-grid">

        {/* Team Card */}
        <div className="acct-card">
          <div className="acct-card-title">Team Members</div>
          {members.length === 0 && !addingMember && (
            <div style={{fontSize:12,color:'var(--text-muted)',padding:'12px 0',textAlign:'center'}}>No team members yet.</div>
          )}
          {members.map((m, i) => (
            <div className="acct-team-member" key={i}>
              <div className={`acct-team-avatar${m.you ? ' you' : ''}`}>{m.initials}</div>
              <div style={{flex:1,minWidth:0}}>
                <div className="acct-team-name">{m.name}{m.you ? ' (You)' : ''}</div>
                <div className="acct-team-email">{m.email}</div>
              </div>
              <span className="acct-team-role">{m.role}</span>
              <span className={`acct-team-status ${m.status}`}>
                {m.status === 'active' ? 'Active' : 'Pending'}
              </span>
            </div>
          ))}

          {addingMember ? (
            <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:8}}>
              <input
                className="ob-input"
                placeholder="teammate@business.com"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                style={{fontSize:12}}
              />
              <div style={{display:'flex',gap:6}}>
                <select className="ob-input" value={newRole} onChange={e => setNewRole(e.target.value)}
                  style={{fontSize:12,cursor:'pointer',flex:1}}>
                  <option>Manager</option>
                  <option>Editor</option>
                  <option>Viewer</option>
                </select>
                <button className="ob-btn ob-btn-primary" style={{padding:'8px 14px',fontSize:12}} onClick={handleAddMember}>Send</button>
                <button className="ob-btn ob-btn-secondary" style={{padding:'8px 12px',fontSize:12}} onClick={() => setAddingMember(false)} aria-label="Cancel invite" title="Cancel">×</button>
              </div>
            </div>
          ) : (
            <div className="acct-add-member" onClick={() => setAddingMember(true)}>
              <div className="acct-add-icon">+</div>
              Invite a team member
            </div>
          )}
        </div>

        {/* Perks Card */}
        <div className="acct-card">
          <div className="acct-card-title">Perks & Add-Ons</div>
          <div className="acct-perks-grid">
            {perks.map((p, i) => (
              <div className={`acct-perk${p.status === 'included' ? ' active-perk' : p.status === 'soon' ? ' soon-perk' : ''}`} key={i}>
                <div className="acct-perk-top">
                  <span className="acct-perk-icon">{p.icon}</span>
                  <span className={`acct-perk-pill ${p.status === 'included' ? 'included' : p.status === 'soon' ? 'soon' : 'add-on'}`}>
                    {p.pill}
                  </span>
                </div>
                <div className="acct-perk-name">{p.name}</div>
                <div className="acct-perk-desc">{p.desc}</div>
                {p.subDesc && <div className="acct-perk-desc" style={{marginTop:3,opacity:0.75}}>{p.subDesc}</div>}
                {p.showBtn && (
                  <div className="acct-perk-action">
                    <button
                      className="acct-perk-btn"
                      onClick={p.btnDisabled || p.btnLoading ? undefined : p.onBtn}
                      disabled={p.btnDisabled || p.btnLoading}
                      style={{
                        opacity: p.btnLoading ? 0.6 : 1,
                        ...(p.btnDisabled ? { background:'var(--accent-dim)', color:'var(--accent)', border:'1px solid var(--accent-border)', cursor:'not-allowed' } : {}),
                      }}
                    >
                      {p.btnLoading ? p.btnLoadingLabel : p.btnLabel}
                    </button>
                    {p.secondBtn && (
                      <button
                        type="button"
                        onClick={p.secondBtn.onClick}
                        className="acct-perk-btn"
                        style={{ display:'block', width:'100%', textAlign:'center', marginTop:6, fontSize:11, opacity:0.75, cursor:'pointer', background:'transparent', border:'1px solid var(--accent-border)' }}
                      >
                        {p.secondBtn.label}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Photography Partner application modal */}
      {photogOpen && (
        <div
          onClick={() => setPhotogOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'rgba(8,10,14,0.72)', backdropFilter:'blur(6px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width:'100%', maxWidth:460, maxHeight:'90vh', overflowY:'auto',
              background:'rgba(20,24,32,0.96)', border:'1px solid rgba(49,220,186,0.22)',
              borderRadius:16, padding:'26px 26px 30px',
              boxShadow:'0 24px 60px rgba(0,0,0,0.5)',
              fontFamily:"'Barlow', var(--font-body, sans-serif)", color:'#e7ecf2',
            }}
          >
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:4 }}>
              <div style={{ fontFamily:"'Barlow', sans-serif", fontWeight:700, fontSize:19, letterSpacing:'-0.01em' }}>
                Apply as a Photography Partner
              </div>
              <button
                type="button"
                onClick={() => setPhotogOpen(false)}
                style={{ background:'transparent', border:'none', color:'#8b95a5', fontSize:22, lineHeight:1, cursor:'pointer', padding:0, marginLeft:12 }}
                aria-label="Close"
              >×</button>
            </div>

            {photogStatus === 'success' ? (
              <div style={{ padding:'28px 4px 8px', textAlign:'center' }}>
                <div style={{ fontSize:34, marginBottom:10 }}>✓</div>
                <div style={{ fontSize:15, fontWeight:600, color:'#31DCBA' }}>
                  Thanks! We'll be in touch soon.
                </div>
              </div>
            ) : (
              <form onSubmit={handlePhotogSubmit}>
                <p style={{ fontSize:13, color:'#9aa4b2', margin:'2px 0 18px' }}>
                  Tell us about yourself and your work — we review every application.
                </p>

                {[
                  { key:'fullName',   label:'Full Name',            type:'text',  required:true },
                  { key:'email',      label:'Email',                type:'email', required:true },
                  { key:'phone',      label:'Phone',                type:'tel',   required:false },
                  { key:'city',       label:'City / Area',          type:'text',  required:false },
                  { key:'portfolio',  label:'Portfolio Link',       type:'url',   required:false },
                ].map(f => (
                  <label key={f.key} style={{ display:'block', marginBottom:13 }}>
                    <span style={{ display:'block', fontSize:12, fontWeight:600, color:'#b6c0cf', marginBottom:5 }}>
                      {f.label}{f.required && <span style={{ color:'#31DCBA' }}> *</span>}
                    </span>
                    <input
                      type={f.type}
                      required={f.required}
                      value={photogForm[f.key]}
                      onChange={e => setPhotogForm(s => ({ ...s, [f.key]: e.target.value }))}
                      disabled={photogStatus === 'loading'}
                      style={photogInputStyle}
                    />
                  </label>
                ))}

                <label style={{ display:'block', marginBottom:13 }}>
                  <span style={{ display:'block', fontSize:12, fontWeight:600, color:'#b6c0cf', marginBottom:5 }}>Brief Bio</span>
                  <textarea
                    rows={3}
                    value={photogForm.bio}
                    onChange={e => setPhotogForm(s => ({ ...s, bio: e.target.value }))}
                    disabled={photogStatus === 'loading'}
                    style={{ ...photogInputStyle, resize:'vertical', minHeight:64 }}
                  />
                </label>

                <label style={{ display:'block', marginBottom:8 }}>
                  <span style={{ display:'block', fontSize:12, fontWeight:600, color:'#b6c0cf', marginBottom:5 }}>Expected Price Point</span>
                  <input
                    type="text"
                    placeholder="e.g. $150 per session"
                    value={photogForm.pricePoint}
                    onChange={e => setPhotogForm(s => ({ ...s, pricePoint: e.target.value }))}
                    disabled={photogStatus === 'loading'}
                    style={photogInputStyle}
                  />
                </label>

                {photogStatus === 'error' && (
                  <div style={{ fontSize:13, color:'#ff7b7b', margin:'10px 0 2px', textAlign:'center' }}>
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={photogStatus === 'loading'}
                  style={{
                    width:'100%', marginTop:16, padding:'12px 16px',
                    background: photogStatus === 'loading' ? 'rgba(49,220,186,0.5)' : '#31DCBA',
                    color:'#0d0f14', border:'none', borderRadius:10,
                    fontFamily:"'Barlow', sans-serif", fontWeight:700, fontSize:14,
                    cursor: photogStatus === 'loading' ? 'default' : 'pointer',
                  }}
                >
                  {photogStatus === 'loading' ? 'Submitting…' : 'Submit Application'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* UPGRADE TO MANAGED — application form. No card is charged here. */}
      {upgradeOpen && (
        <div
          onClick={() => setUpgradeOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'rgba(8,10,14,0.72)', backdropFilter:'blur(6px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width:'100%', maxWidth:460, maxHeight:'90vh', overflowY:'auto',
              background:'rgba(20,24,32,0.96)', border:'1px solid rgba(49,220,186,0.22)',
              borderRadius:16, padding:'26px 26px 30px',
              boxShadow:'0 24px 60px rgba(0,0,0,0.5)',
              fontFamily:"'Barlow', var(--font-body, sans-serif)", color:'#e7ecf2',
            }}
          >
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:4 }}>
              <div style={{ fontFamily:"'Barlow', sans-serif", fontWeight:700, fontSize:19, letterSpacing:'-0.01em' }}>
                Apply to upgrade to Managed
              </div>
              <button
                type="button"
                onClick={() => setUpgradeOpen(false)}
                style={{ background:'transparent', border:'none', color:'#8b95a5', fontSize:22, lineHeight:1, cursor:'pointer', padding:0, marginLeft:12 }}
                aria-label="Close"
              >×</button>
            </div>

            {upgradeStatus === 'success' ? (
              <div style={{ padding:'28px 4px 8px', textAlign:'center' }}>
                <div style={{ fontSize:34, marginBottom:10 }}>✓</div>
                <div style={{ fontSize:15, fontWeight:600, color:'#31DCBA', marginBottom:8 }}>
                  Application received.
                </div>
                <div style={{ fontSize:13, color:'#9aa4b2', lineHeight:1.6 }}>
                  We'll review it and get in touch before anything changes.
                  Your plan and billing are unchanged for now.
                </div>
              </div>
            ) : (
              <form onSubmit={handleUpgradeSubmit}>
                <p style={{ fontSize:13, color:'#9aa4b2', margin:'2px 0 16px', lineHeight:1.6 }}>
                  Managed is $500/mo — we handle approvals, generation and strategy for you.
                </p>
                <div style={{
                  fontSize:12, color:'#9aa4b2', lineHeight:1.6,
                  background:'rgba(49,220,186,0.07)', border:'1px solid rgba(49,220,186,0.2)',
                  borderRadius:9, padding:'10px 12px', marginBottom:18,
                }}>
                  <strong style={{color:'#31DCBA'}}>Nothing is charged now.</strong> This is an
                  application — we review every one and follow up before making any billing change.
                </div>

                {[
                  { key:'contactName',  label:'Your Name',  type:'text',  required:true  },
                  { key:'contactEmail', label:'Email',      type:'email', required:true  },
                  { key:'phone',        label:'Phone',      type:'tel',   required:false },
                ].map(f => (
                  <label key={f.key} style={{ display:'block', marginBottom:13 }}>
                    <span style={{ display:'block', fontSize:12, fontWeight:600, color:'#b6c0cf', marginBottom:5 }}>
                      {f.label}{f.required && <span style={{ color:'#31DCBA' }}> *</span>}
                    </span>
                    <input
                      type={f.type}
                      required={f.required}
                      value={upgradeForm[f.key]}
                      onChange={e => setUpgradeForm(s => ({ ...s, [f.key]: e.target.value }))}
                      disabled={upgradeStatus === 'loading'}
                      style={photogInputStyle}
                    />
                  </label>
                ))}

                {[
                  { key:'currentChallenges', label:"What's not working right now?" },
                  { key:'goals',             label:'What do you want Managed to achieve?' },
                  { key:'notes',             label:'Anything else we should know?' },
                ].map(f => (
                  <label key={f.key} style={{ display:'block', marginBottom:13 }}>
                    <span style={{ display:'block', fontSize:12, fontWeight:600, color:'#b6c0cf', marginBottom:5 }}>{f.label}</span>
                    <textarea
                      rows={3}
                      value={upgradeForm[f.key]}
                      onChange={e => setUpgradeForm(s => ({ ...s, [f.key]: e.target.value }))}
                      disabled={upgradeStatus === 'loading'}
                      style={{ ...photogInputStyle, resize:'vertical', minHeight:64 }}
                    />
                  </label>
                ))}

                {upgradeStatus === 'error' && (
                  <div style={{ fontSize:13, color:'#ff7b7b', margin:'10px 0 2px', textAlign:'center' }}>
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={upgradeStatus === 'loading'}
                  style={{
                    width:'100%', marginTop:16, padding:'12px 16px',
                    background: upgradeStatus === 'loading' ? 'rgba(49,220,186,0.5)' : '#31DCBA',
                    color:'#0d0f14', border:'none', borderRadius:10,
                    fontFamily:"'Barlow', sans-serif", fontWeight:700, fontSize:14,
                    cursor: upgradeStatus === 'loading' ? 'default' : 'pointer',
                  }}
                >
                  {upgradeStatus === 'loading' ? 'Submitting…' : 'Submit Application'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* DOWNGRADE TO SELF-SERVE — self-service, effective at period end. */}
      {downgradeOpen && (
        <div
          onClick={() => setDowngradeOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:1000,
            background:'rgba(8,10,14,0.72)', backdropFilter:'blur(6px)',
            display:'flex', alignItems:'center', justifyContent:'center', padding:20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width:'100%', maxWidth:460, maxHeight:'90vh', overflowY:'auto',
              background:'rgba(20,24,32,0.96)', border:'1px solid rgba(245,166,35,0.22)',
              borderRadius:16, padding:'26px 26px 30px',
              boxShadow:'0 24px 60px rgba(0,0,0,0.5)',
              fontFamily:"'Barlow', var(--font-body, sans-serif)", color:'#e7ecf2',
            }}
          >
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:4 }}>
              <div style={{ fontFamily:"'Barlow', sans-serif", fontWeight:700, fontSize:19, letterSpacing:'-0.01em' }}>
                Switch to Self-Serve
              </div>
              <button
                type="button"
                onClick={() => setDowngradeOpen(false)}
                style={{ background:'transparent', border:'none', color:'#8b95a5', fontSize:22, lineHeight:1, cursor:'pointer', padding:0, marginLeft:12 }}
                aria-label="Close"
              >×</button>
            </div>

            {downgradeStatus === 'success' ? (
              <div style={{ padding:'28px 4px 8px', textAlign:'center' }}>
                <div style={{ fontSize:34, marginBottom:10 }}>✓</div>
                <div style={{ fontSize:15, fontWeight:600, color:'#31DCBA', marginBottom:8 }}>
                  {downgradeOverride?.effectiveDate
                    ? `Scheduled for ${downgradeOverride.effectiveDate}.`
                    : 'Scheduled.'}
                </div>
                <div style={{ fontSize:13, color:'#9aa4b2', lineHeight:1.6, marginBottom:18 }}>
                  Nothing changes until then — you keep Managed for the rest of this
                  billing period. You can undo this any time before it takes effect.
                </div>
                <button
                  onClick={() => setDowngradeOpen(false)}
                  style={{
                    padding:'10px 20px', background:'transparent',
                    border:'1px solid rgba(255,255,255,0.15)', color:'#e7ecf2',
                    borderRadius:9, fontFamily:"'Barlow', sans-serif", fontWeight:600,
                    fontSize:13, cursor:'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <p style={{ fontSize:13, color:'#9aa4b2', margin:'2px 0 16px', lineHeight:1.6 }}>
                  Self-Serve is $79/mo. You build and approve your own posts.
                </p>

                <div style={{ fontSize:12, color:'#b6c0cf', lineHeight:1.7, marginBottom:16 }}>
                  <div style={{ fontWeight:700, color:'#e7ecf2', marginBottom:6 }}>What you'd give up:</div>
                  {[
                    'We stop writing, generating and scheduling posts for you',
                    'Image generation becomes capped at 24/mo (currently unlimited)',
                    'Strategy and priority support end',
                  ].map((t, i) => (
                    <div key={i} style={{ display:'flex', gap:8, marginBottom:3 }}>
                      <span style={{ color:'#f5a623' }}>×</span><span>{t}</span>
                    </div>
                  ))}
                </div>

                <div style={{
                  fontSize:12, color:'#9aa4b2', lineHeight:1.6,
                  background:'rgba(245,166,35,0.08)', border:'1px solid rgba(245,166,35,0.25)',
                  borderRadius:9, padding:'10px 12px', marginBottom:18,
                }}>
                  This takes effect at the <strong style={{color:'#f5a623'}}>end of your current
                  billing period</strong> — you keep Managed until then. There's no refund or
                  credit for the current period, and you won't be charged $500 again.
                </div>

                {downgradeStatus === 'error' && (
                  <div style={{ fontSize:13, color:'#ff7b7b', margin:'0 0 12px', textAlign:'center' }}>
                    Something went wrong. Your plan was not changed — please try again
                    or email hello@autopilotdigital.ca.
                  </div>
                )}

                <div style={{ display:'flex', gap:10 }}>
                  <button
                    onClick={() => setDowngradeOpen(false)}
                    disabled={downgradeStatus === 'loading'}
                    style={{
                      flex:1, padding:'12px 16px', background:'transparent',
                      border:'1px solid rgba(255,255,255,0.15)', color:'#e7ecf2',
                      borderRadius:10, fontFamily:"'Barlow', sans-serif", fontWeight:700,
                      fontSize:14, cursor: downgradeStatus === 'loading' ? 'default' : 'pointer',
                    }}
                  >
                    Stay on Managed
                  </button>
                  <button
                    onClick={handleDowngradeConfirm}
                    disabled={downgradeStatus === 'loading'}
                    style={{
                      flex:1, padding:'12px 16px',
                      background: downgradeStatus === 'loading' ? 'rgba(245,166,35,0.4)' : 'rgba(245,166,35,0.15)',
                      border:'1px solid rgba(245,166,35,0.5)', color:'#f5a623',
                      borderRadius:10, fontFamily:"'Barlow', sans-serif", fontWeight:700,
                      fontSize:14, cursor: downgradeStatus === 'loading' ? 'default' : 'pointer',
                    }}
                  >
                    {downgradeStatus === 'loading' ? 'Scheduling…' : 'Confirm switch'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}


// ============================================================
// CONNECTIONS PANEL — Session 13: Real Meta OAuth
// ============================================================
//
// HOW IT WORKS (production flow):
//   1. User clicks "Connect Instagram" or "Connect Facebook Page"
//   2. We open a popup to /api/auth/meta?platform=ig|fb
//      (your Express route builds the FB OAuth URL and redirects)
//   3. User authorises in the popup.
//   4. Your callback route (/api/auth/meta/callback) exchanges
//      the code for a long-lived token, then calls
//      window.opener.postMessage({ type:'META_OAUTH_SUCCESS', platform, account }, origin)
//      and closes itself.
//   5. The useEffect listener here catches that message, validates
//      the origin, and updates state.
//
// ENV VARS needed in your backend (not the client):
//   META_APP_ID, META_APP_SECRET, META_REDIRECT_URI
//
// SCOPES requested:
//   Instagram: instagram_basic, instagram_content_publish,
//              instagram_manage_insights, pages_read_engagement
//   Facebook:  pages_manage_posts, pages_read_engagement,
//              pages_manage_ads, ads_read, pages_show_list
//
// Origin check on the message listener was disabled for local dev (see
// `onMessage` below). When you re-enable it, validate against
// window.location.origin.
// ============================================================

// Build the OAuth popup URL — your backend handles the actual redirect.
//
// The URL carries a flowToken, never a clientId. It used to carry the raw
// client id, which meant anyone could open the connect URL with someone else's
// id and have their own Instagram linked into that client's dashboard. The id
// is now established by the authenticated mint call below and the popup only
// carries an opaque token standing for it.
function metaOAuthUrl(platform, flowToken) {
  const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
  const q = `?flowToken=${encodeURIComponent(flowToken)}`;
  if (platform === 'ig') return `${base}/auth/meta/instagram${q}`;
  if (platform === 'fb') return `${base}/auth/meta/facebook${q}`;
  return `${base}/auth/meta/${platform}${q}`;
}

function tiktokOAuthUrl(flowToken) {
  const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
  return `${base}/auth/tiktok?flowToken=${encodeURIComponent(flowToken)}`;
}

function googleBizOAuthUrl(flowToken) {
  const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
  return `${base}/auth/google-business?flowToken=${encodeURIComponent(flowToken)}`;
}

// Mint a one-shot token for a connect popup. Sends the session; the backend
// takes the client id from that and never from anything we pass here.
async function mintOAuthFlowToken(platform) {
  const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
  const res = await authedFetch(`${base}/api/oauth/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platform }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.flowToken) throw new Error(data.error || `Could not start the connection (${res.status})`);
  return data.flowToken;
}

function ConnectionsPanel() {
  const { clientId } = useClientId();
  // ── account data returned from OAuth ──
  const [igAccount, setIgAccount] = useState({
    connected: false,
    username: null,
    name: null,
    followers: null,
    type: 'Business Account',
    connectedDate: null,
    avatarInitials: null,
    accessToken: null,
    pageId: null,
    scopes: null,
  });
  const [fbAccount, setFbAccount] = useState({
    connected: false,
    username: null,
    name: null,
    followers: null,
    type: 'Facebook Page',
    connectedDate: null,
    avatarInitials: null,
    accessToken: null,
    pageId: null,
    scopes: null,
  });

  const [ttAccount, setTtAccount] = useState({
    connected: false,
    username: null,
    name: null,
    type: 'TikTok Creator',
    connectedDate: null,
    avatarInitials: null,
    pictureUrl: null,
    scopes: null,
  });

  // Whether Google Business is open to this client (admin-only until Google
  // verifies the app; server: gbp-access.js). False → the card sits in
  // "Coming soon" with no Connect button. Starts false so nothing flashes.
  const [gbpAvailable, setGbpAvailable] = useState(false);
  const [gbpAccount, setGbpAccount] = useState({
    connected: false,
    username: null,
    name: null,
    type: 'Business Profile',
    connectedDate: null,
    avatarInitials: null,
    pictureUrl: null,
    scopes: null,
  });

  const [igConnecting, setIgConnecting] = useState(false);
  const [fbConnecting, setFbConnecting] = useState(false);
  const [ttConnecting, setTtConnecting] = useState(false);
  const [gbpConnecting, setGbpConnecting] = useState(false);
  const [igError, setIgError] = useState(null);
  const [fbError, setFbError] = useState(null);
  const [ttError, setTtError] = useState(null);
  const [gbpError, setGbpError] = useState(null);
  const [apiError, setApiError] = useState(null);

  // FB Page picker — populated when /me/accounts returns >1 Page.
  // pages: [{ id, name, pictureUrl }]
  const [fbPagePicker, setFbPagePicker] = useState(null); // { flowToken, pages } | null
  const [fbPickerSubmitting, setFbPickerSubmitting] = useState(false);

  // Track all OAuth-flow timers in a ref so they can be cleaned up if the
  // user navigates away from the panel mid-flow (otherwise pollTimer/
  // safetyStop/closeTimer keep firing setState on an unmounted component
  // for up to 3 minutes — memory leak + console warnings).
  const oauthTimersRef = useRef({ pollTimer: null, safetyStop: null, closeTimer: null, ttPollTimer: null, ttSafetyStop: null, ttCloseTimer: null, gbpPollTimer: null, gbpSafetyStop: null, gbpCloseTimer: null });
  useEffect(() => () => {
    const t = oauthTimersRef.current;
    if (t.pollTimer)   clearInterval(t.pollTimer);
    if (t.safetyStop)  clearTimeout(t.safetyStop);
    if (t.closeTimer)  clearInterval(t.closeTimer);
    if (t.ttPollTimer)  clearInterval(t.ttPollTimer);
    if (t.ttSafetyStop) clearTimeout(t.ttSafetyStop);
    if (t.ttCloseTimer) clearInterval(t.ttCloseTimer);
    if (t.gbpPollTimer)  clearInterval(t.gbpPollTimer);
    if (t.gbpSafetyStop) clearTimeout(t.gbpSafetyStop);
    if (t.gbpCloseTimer) clearInterval(t.gbpCloseTimer);
  }, []);

  // ── Load real connection data from Supabase on mount ──
  useEffect(() => {
    if (!clientId) return;
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
    authedFetch(`${base}/api/meta-connections?client=${encodeURIComponent(clientId)}`)
      .then(r => r.json())
      .then(data => {
        if (data.instagram) setIgAccount(prev => ({ ...prev, ...data.instagram }));
        if (data.facebook)  setFbAccount(prev => ({ ...prev, ...data.facebook }));
        if (data.tiktok)    setTtAccount(prev => ({ ...prev, ...data.tiktok }));
        if (data.google_business) setGbpAccount(prev => ({ ...prev, ...data.google_business }));
        setGbpAvailable(data.google_business_available === true);
      })
      .catch(() => {});
  }, [clientId]);

  // ── Listen for postMessage from OAuth popup ──
  useEffect(() => {
    function onMessage(e) { console.log('MSG RECEIVED:', e.origin, JSON.stringify(e.data));
      // Security: only accept messages from our own origin
      // origin check disabled for local dev
      const { type, platform, account, error, errorCode, flowToken, pages } = e.data || {};

      // ── TikTok OAuth popup results ──
      if (type === 'TIKTOK_OAUTH_SUCCESS') {
        setTtConnecting(false);
        setTtError(null);
        setTtAccount(prev => ({
          ...prev,
          connected: true,
          connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }),
        }));
        const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
        authedFetch(`${base}/api/meta-connections?client=${encodeURIComponent(clientId)}`)
          .then(r => r.json())
          .then(d => { if (d.tiktok) setTtAccount(prev => ({ ...prev, ...d.tiktok })); })
          .catch(() => {});
        return;
      }
      if (type === 'TIKTOK_AUTH_ERROR') {
        setTtConnecting(false);
        setTtError(error || 'TikTok authorisation failed. Please try again.');
        return;
      }

      // ── Google Business OAuth popup results ──
      if (type === 'GOOGLE_BIZ_OAUTH_SUCCESS') {
        setGbpConnecting(false);
        setGbpError(null);
        setGbpAccount(prev => ({
          ...prev,
          connected: true,
          connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }),
        }));
        const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
        authedFetch(`${base}/api/meta-connections?client=${encodeURIComponent(clientId)}`)
          .then(r => r.json())
          .then(d => { if (d.google_business) setGbpAccount(prev => ({ ...prev, ...d.google_business })); })
          .catch(() => {});
        return;
      }
      if (type === 'GOOGLE_BIZ_AUTH_ERROR') {
        setGbpConnecting(false);
        setGbpError(error || 'Google Business authorisation failed. Please try again.');
        return;
      }

      if (type !== 'META_OAUTH_SUCCESS' && type !== 'META_OAUTH_ERROR' && type !== 'META_AUTH_ERROR' && type !== 'META_PAGES_CHOICE') return;

      // FB returned multiple Pages — open the picker.
      if (type === 'META_PAGES_CHOICE') {
        setFbConnecting(false);
        setFbError(null);
        setFbPagePicker({ flowToken, pages: Array.isArray(pages) ? pages : [] });
        return;
      }

      if (type === 'META_OAUTH_ERROR' || type === 'META_AUTH_ERROR') {
        if (errorCode === 'META_KEY_INVALID' || error === 'META_KEY_INVALID') {
          setIgConnecting(false); setFbConnecting(false); setApiError('META_KEY_INVALID'); return;
        }
        const msg = error || 'Authorisation failed. Please try again.';
        if (platform === 'ig') { setIgConnecting(false); setIgError(msg); }
        else                   { setFbConnecting(false); setFbError(msg); }
        return;
      }

      // Success
      if (platform === 'ig') {
        setIgConnecting(false);
        setIgError(null);
        setIgAccount(prev => ({
          ...prev,
          connected: true,
          username:       account?.username      || '@yourpage',
          name:           account?.name          || 'Your Business',
          followers:      account?.followers     || '—',
          type:           account?.type          || 'Business Account',
          connectedDate:  account?.connectedDate || new Date().toLocaleDateString('en-CA',{month:'short',year:'numeric'}),
          avatarInitials: (account?.name || 'Y').slice(0,2).toUpperCase(),
          accessToken:    account?.accessToken   || null,
          pageId:         account?.pageId        || null,
        }));
      } else {
        setFbConnecting(false);
        setFbError(null);
        setFbAccount(prev => ({
          ...prev,
          connected: true,
          username:       account?.pageName      || 'Your Facebook Page',
          name:           account?.pageName      || 'Your Facebook Page',
          followers:      account?.followers     || '—',
          type:           'Facebook Page',
          connectedDate:  account?.connectedDate || new Date().toLocaleDateString('en-CA',{month:'short',year:'numeric'}),
          avatarInitials: (account?.pageName || 'Y').slice(0,2).toUpperCase(),
          accessToken:    account?.accessToken   || null,
          pageId:         account?.pageId        || null,
        }));
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }); // no dependency array - re-register on every render

  // ── Open OAuth popup ──
  // The connect URL now needs a flowToken from an authenticated call, and that
  // await is the reason the popup is opened in two steps. Browsers only allow
  // window.open() while a click gesture is still being handled; opening it AFTER
  // an await gets it blocked as an unsolicited popup. So the window is opened
  // synchronously on about:blank while the gesture is live, and navigated once
  // the token arrives. Anything that fails in between closes it again.
  function handleConnect(platform) {
    if (platform === 'ig') {
      setIgError(null);
      setIgConnecting(true);
    } else {
      setFbError(null);
      setFbConnecting(true);
    }

    const fail = (msg) => {
      if (platform === 'ig') { setIgConnecting(false); setIgError(msg); }
      else                   { setFbConnecting(false); setFbError(msg); }
    };

    const name = 'MetaOAuth';
    const opts = 'width=540,height=680,scrollbars=yes,resizable=yes,status=yes';
    const popup = window.open('about:blank', name, opts); // synchronous — keep it that way

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      // Popup was blocked
      fail('Popup blocked. Please allow popups for this site and try again.');
      return;
    }

    mintOAuthFlowToken(platform === 'ig' ? 'instagram' : 'facebook')
      .then((flowToken) => { popup.location.href = metaOAuthUrl(platform, flowToken); })
      .catch((err) => {
        try { popup.close(); } catch (_) { /* already gone */ }
        // The poll/close timers below have already been started by the time this
        // runs. Stop them, or a failed mint leaves /api/meta/status being polled
        // every 2s for the next three minutes for a flow that never opened.
        const t = oauthTimersRef.current;
        if (t.pollTimer)  clearInterval(t.pollTimer);
        if (t.safetyStop) clearTimeout(t.safetyStop);
        if (t.closeTimer) clearInterval(t.closeTimer);
        fail(err.message || 'Could not start the connection. Please try again.');
      });

    // ── Poll /api/meta/status every 2s — handles new-tab case where window.opener is null ──
    const dbPlatform = platform === 'ig' ? 'instagram' : 'facebook';
    const POLL_INTERVAL = 2000;
    const POLL_TIMEOUT  = 3 * 60 * 1000; // 3 minutes

    // Cancel any timers from a previous incomplete connect attempt.
    if (oauthTimersRef.current.pollTimer)  clearInterval(oauthTimersRef.current.pollTimer);
    if (oauthTimersRef.current.safetyStop) clearTimeout(oauthTimersRef.current.safetyStop);
    if (oauthTimersRef.current.closeTimer) clearInterval(oauthTimersRef.current.closeTimer);

    const pollTimer = setInterval(async () => {
      try {
        const res = await authedFetch(`https://autopilot-backend-pn9r.onrender.com/api/meta/status?client_id=${encodeURIComponent(clientId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data[dbPlatform]) return;

        clearInterval(pollTimer);
        clearTimeout(safetyStop);

        if (platform === 'ig') {
          setIgConnecting(false);
          setIgError(null);
          setIgAccount(prev => ({
            ...prev,
            connected: true,
            connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }),
          }));
        } else {
          setFbConnecting(false);
          setFbError(null);
          setFbAccount(prev => ({
            ...prev,
            connected: true,
            connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }),
          }));
        }
      } catch (_) { /* network error — keep polling */ }
    }, POLL_INTERVAL);

    // Safety: stop polling after 3 minutes
    const safetyStop = setTimeout(() => {
      clearInterval(pollTimer);
      if (platform === 'ig') {
        setIgConnecting(prev => { if (prev) setIgError('Connection timed out. Please try again.'); return false; });
      } else {
        setFbConnecting(prev => { if (prev) setFbError('Connection timed out. Please try again.'); return false; });
      }
    }, POLL_TIMEOUT);

    oauthTimersRef.current.pollTimer  = pollTimer;
    oauthTimersRef.current.safetyStop = safetyStop;

    // Also watch for manual popup close (fallback for cancelled flow)
    const closeTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(closeTimer);
        // Final status check — user may have completed OAuth before closing the tab
        authedFetch(`https://autopilot-backend-pn9r.onrender.com/api/meta/status?client_id=${encodeURIComponent(clientId)}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data && data[dbPlatform]) return; // poll loop will handle the success update
            // Not connected — treat as cancelled
            if (platform === 'ig') {
              setIgConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setIgError('Connection cancelled.'); } return false; });
            } else {
              setFbConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setFbError('Connection cancelled.'); } return false; });
            }
          })
          .catch(() => {
            if (platform === 'ig') {
              setIgConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setIgError('Connection cancelled.'); } return false; });
            } else {
              setFbConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setFbError('Connection cancelled.'); } return false; });
            }
          });
      }
    }, 600);
    oauthTimersRef.current.closeTimer = closeTimer;
  }

  async function handleDisconnect(platform) {
    const setError = platform === 'ig' ? setIgError : setFbError;
    const apiPlatform = platform === 'ig' ? 'instagram' : 'facebook';
    // Delete the row server-side FIRST. If we only clear local state, the
    // status poll below re-reads the still-present meta_connections row and
    // flips the card back to "connected" — so a stale/invalid token can never
    // be replaced by reconnecting. (DELETE /api/meta-connections, server.js)
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
    try {
      const res = await authedFetch(
        `${base}/api/meta-connections?client=${encodeURIComponent(clientId)}&platform=${apiPlatform}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Could not disconnect. Please try again.');
        return;
      }
    } catch (err) {
      setError('Network error. Please try again.');
      return;
    }

    if (platform === 'ig') {
      setIgAccount(prev => ({ ...prev, connected:false, accessToken:null, pageId:null }));
      setIgError(null);
    } else {
      setFbAccount(prev => ({ ...prev, connected:false, accessToken:null, pageId:null }));
      setFbError(null);
    }
  }

  // ── TikTok connect (popup → /auth/tiktok, poll /api/meta/status) ──
  function handleTikTokConnect() {
    setTtError(null);
    setTtConnecting(true);
    // Still needed by the status/connection polls further down, even though the
    // popup URL is built by tiktokOAuthUrl() now.
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

    // Two-step open for the same reason as handleConnect: the flowToken mint is
    // async, and a window.open() after an await gets blocked as an unsolicited
    // popup. Open on about:blank while the click gesture is still live.
    const popup = window.open('about:blank', 'TikTokOAuth', 'width=540,height=680,scrollbars=yes,resizable=yes,status=yes');

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      setTtConnecting(false);
      setTtError('Popup blocked. Please allow popups for this site and try again.');
      return;
    }

    mintOAuthFlowToken('tiktok')
      .then((flowToken) => { popup.location.href = tiktokOAuthUrl(flowToken); })
      .catch((err) => {
        try { popup.close(); } catch (_) { /* already gone */ }
        const t = oauthTimersRef.current;
        if (t.ttPollTimer)  clearInterval(t.ttPollTimer);
        if (t.ttSafetyStop) clearTimeout(t.ttSafetyStop);
        if (t.ttCloseTimer) clearInterval(t.ttCloseTimer);
        setTtConnecting(false);
        setTtError(err.message || 'Could not start the connection. Please try again.');
      });

    const POLL_INTERVAL = 2000;
    const POLL_TIMEOUT  = 3 * 60 * 1000;
    if (oauthTimersRef.current.ttPollTimer)  clearInterval(oauthTimersRef.current.ttPollTimer);
    if (oauthTimersRef.current.ttSafetyStop) clearTimeout(oauthTimersRef.current.ttSafetyStop);
    if (oauthTimersRef.current.ttCloseTimer) clearInterval(oauthTimersRef.current.ttCloseTimer);

    const pollTimer = setInterval(async () => {
      try {
        const res = await authedFetch(`${base}/api/meta/status?client_id=${encodeURIComponent(clientId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.tiktok) return;
        clearInterval(pollTimer);
        clearTimeout(safetyStop);
        setTtConnecting(false);
        setTtError(null);
        setTtAccount(prev => ({ ...prev, connected: true, connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }) }));
        authedFetch(`${base}/api/meta-connections?client=${encodeURIComponent(clientId)}`)
          .then(r => r.json())
          .then(d => { if (d.tiktok) setTtAccount(prev => ({ ...prev, ...d.tiktok })); })
          .catch(() => {});
      } catch (_) { /* keep polling */ }
    }, POLL_INTERVAL);

    const safetyStop = setTimeout(() => {
      clearInterval(pollTimer);
      setTtConnecting(prev => { if (prev) setTtError('Connection timed out. Please try again.'); return false; });
    }, POLL_TIMEOUT);

    oauthTimersRef.current.ttPollTimer  = pollTimer;
    oauthTimersRef.current.ttSafetyStop = safetyStop;

    const closeTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(closeTimer);
        authedFetch(`${base}/api/meta/status?client_id=${encodeURIComponent(clientId)}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data && data.tiktok) return; // poll loop handles success
            setTtConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setTtError('Connection cancelled.'); } return false; });
          })
          .catch(() => setTtConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setTtError('Connection cancelled.'); } return false; }));
      }
    }, 600);
    oauthTimersRef.current.ttCloseTimer = closeTimer;
  }

  function handleGoogleBizConnect() {
    setGbpError(null);
    setGbpConnecting(true);
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

    // Two-step open for the same reason as handleConnect / handleTikTokConnect:
    // the flowToken mint is async and a window.open() after an await is blocked.
    const popup = window.open('about:blank', 'GoogleBizOAuth', 'width=540,height=680,scrollbars=yes,resizable=yes,status=yes');

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      setGbpConnecting(false);
      setGbpError('Popup blocked. Please allow popups for this site and try again.');
      return;
    }

    mintOAuthFlowToken('google_business')
      .then((flowToken) => { popup.location.href = googleBizOAuthUrl(flowToken); })
      .catch((err) => {
        try { popup.close(); } catch (_) { /* already gone */ }
        const t = oauthTimersRef.current;
        if (t.gbpPollTimer)  clearInterval(t.gbpPollTimer);
        if (t.gbpSafetyStop) clearTimeout(t.gbpSafetyStop);
        if (t.gbpCloseTimer) clearInterval(t.gbpCloseTimer);
        setGbpConnecting(false);
        setGbpError(err.message || 'Could not start the connection. Please try again.');
      });

    const POLL_INTERVAL = 2000;
    const POLL_TIMEOUT  = 3 * 60 * 1000;
    if (oauthTimersRef.current.gbpPollTimer)  clearInterval(oauthTimersRef.current.gbpPollTimer);
    if (oauthTimersRef.current.gbpSafetyStop) clearTimeout(oauthTimersRef.current.gbpSafetyStop);
    if (oauthTimersRef.current.gbpCloseTimer) clearInterval(oauthTimersRef.current.gbpCloseTimer);

    const pollTimer = setInterval(async () => {
      try {
        const res = await authedFetch(`${base}/api/meta/status?client_id=${encodeURIComponent(clientId)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.google_business) return;
        clearInterval(pollTimer);
        clearTimeout(safetyStop);
        setGbpConnecting(false);
        setGbpError(null);
        setGbpAccount(prev => ({ ...prev, connected: true, connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }) }));
        authedFetch(`${base}/api/meta-connections?client=${encodeURIComponent(clientId)}`)
          .then(r => r.json())
          .then(d => { if (d.google_business) setGbpAccount(prev => ({ ...prev, ...d.google_business })); })
          .catch(() => {});
      } catch (_) { /* keep polling */ }
    }, POLL_INTERVAL);

    const safetyStop = setTimeout(() => {
      clearInterval(pollTimer);
      setGbpConnecting(prev => { if (prev) setGbpError('Connection timed out. Please try again.'); return false; });
    }, POLL_TIMEOUT);

    oauthTimersRef.current.gbpPollTimer  = pollTimer;
    oauthTimersRef.current.gbpSafetyStop = safetyStop;

    const closeTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(closeTimer);
        authedFetch(`${base}/api/meta/status?client_id=${encodeURIComponent(clientId)}`)
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data && data.google_business) return; // poll loop handles success
            setGbpConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setGbpError('Connection cancelled.'); } return false; });
          })
          .catch(() => setGbpConnecting(prev => { if (prev) { clearInterval(pollTimer); clearTimeout(safetyStop); setGbpError('Connection cancelled.'); } return false; }));
      }
    }, 600);
    oauthTimersRef.current.gbpCloseTimer = closeTimer;
  }

  async function handleGoogleBizDisconnect() {
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
    try {
      const res = await authedFetch(`${base}/api/google-business-connections?client=${encodeURIComponent(clientId)}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setGbpError(data.error || 'Could not disconnect. Please try again.');
        return;
      }
    } catch (_) {
      setGbpError('Network error. Please try again.');
      return;
    }
    setGbpAccount(prev => ({ ...prev, connected: false, pictureUrl: null }));
    setGbpError(null);
  }

  async function handleTikTokDisconnect() {
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
    try {
      const res = await authedFetch(`${base}/api/tiktok-connections?client=${encodeURIComponent(clientId)}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setTtError(data.error || 'Could not disconnect. Please try again.');
        return;
      }
    } catch (_) {
      setTtError('Network error. Please try again.');
      return;
    }
    setTtAccount(prev => ({ ...prev, connected: false, pictureUrl: null }));
    setTtError(null);
  }

  // ── FB Page picker: user picked a Page → finalize via backend ──
  async function handleFbPagePick(pageId) {
    if (!fbPagePicker || !pageId) return;
    setFbPickerSubmitting(true);
    setFbError(null);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
      const res = await authedFetch(`${base}/auth/meta/fb-select-page`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flowToken: fbPagePicker.flowToken, pageId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setFbError(data.error || 'Could not connect that Page. Please try again.');
        setFbPagePicker(null);
        return;
      }
      // Connection saved — close modal; the status poll loop will pick it up.
      setFbPagePicker(null);
      setFbConnecting(false);
      setFbAccount(prev => ({
        ...prev,
        connected: true,
        connectedDate: new Date().toLocaleDateString('en-CA', { month: 'short', year: 'numeric' }),
      }));
      // Refresh full connection details from backend.
      authedFetch(`${base}/api/meta-connections?client=${encodeURIComponent(clientId)}`)
        .then(r => r.json())
        .then(d => { if (d.facebook) setFbAccount(prev => ({ ...prev, ...d.facebook })); })
        .catch(() => {});
    } catch (err) {
      setFbError('Network error. Please try again.');
      setFbPagePicker(null);
    } finally {
      setFbPickerSubmitting(false);
    }
  }

  function handleFbPickerCancel() {
    setFbPagePicker(null);
    setFbConnecting(false);
    setFbError('Facebook connection cancelled.');
  }

  const IG_SCOPES = ['instagram_basic', 'instagram_content_publish', 'instagram_manage_comments', 'pages_show_list', 'pages_read_engagement'];
  const FB_SCOPES = ['pages_manage_posts', 'pages_read_engagement', 'pages_manage_ads', 'ads_read', 'pages_show_list'];
  const igPerms = (igAccount.scopes || IG_SCOPES).map(s => ({ label: s, on: true }));
  const fbPerms = (fbAccount.scopes || FB_SCOPES).map(s => ({ label: s, on: true }));

  // ── Shared spinner element ──
  const Spinner = () => (
    <span style={{
      width:14, height:14,
      border:'2px solid rgba(255,255,255,0.35)',
      borderTopColor:'#fff',
      borderRadius:'50%',
      display:'inline-block',
      animation:'cx-spin 0.7s linear infinite',
      flexShrink:0,
    }}/>
  );

  // ── Error banner ──
  function ErrorBanner({ msg, onDismiss }) {
    return (
      <div style={{
        display:'flex', alignItems:'flex-start', gap:10,
        background:'rgba(239,68,68,0.08)',
        border:'1px solid rgba(239,68,68,0.3)',
        borderRadius:9, padding:'10px 14px',
        fontSize:11, color:'#f87171', lineHeight:1.5,
      }}>
        <span style={{fontSize:14, flexShrink:0, marginTop:1}}>⚠</span>
        <span style={{flex:1}}>{msg}</span>
        <button onClick={onDismiss} aria-label="Dismiss error" title="Dismiss" style={{
          background:'none', border:'none', color:'#f87171',
          fontSize:14, cursor:'pointer', padding:0, flexShrink:0, lineHeight:1,
        }}>×</button>
      </div>
    );
  }

  if (apiError === 'META_KEY_INVALID') {
    return <GlitchRobotCard errorCode="META_KEY_INVALID" />;
  }

  return (
    <div className="cx-wrap">

      {/* Inline keyframe for spinner — injected once */}
      <style>{`@keyframes cx-spin { to { transform: rotate(360deg); } }`}</style>

      {fbPagePicker && (
        <FbPagePickerModal
          pages={fbPagePicker.pages}
          submitting={fbPickerSubmitting}
          onPick={handleFbPagePick}
          onCancel={handleFbPickerCancel}
        />
      )}

      <div className="cx-page-header">
        <div className="cx-page-title">Connections</div>
        <div className="cx-page-sub">
          Connect your social accounts so Autopilot can post, schedule, and pull analytics on your behalf.
          Each platform is independent — connect one or both.
        </div>
      </div>

      {/* ── INSTAGRAM ── */}
      <div className={`cx-platform-card${igAccount.connected ? ' connected' : ''}`}>
        <div className="cx-platform-header">
          <div className="cx-platform-icon ig">
            <svg viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
          </div>
          <div className="cx-platform-info">
            <div className="cx-platform-name">Instagram Business</div>
            <div className="cx-platform-desc">Required for posting, scheduling, and engagement analytics</div>
          </div>
          <div className={`cx-status-badge ${igAccount.connected ? 'connected' : 'disconnected'}`}>
            <div className="cx-status-dot" />
            {igAccount.connected ? 'Connected' : 'Not Connected'}
          </div>
        </div>

        <div className="cx-platform-body">
          {igError && <ErrorBanner msg={igError} onDismiss={() => setIgError(null)} />}

          {igAccount.connected ? (
            <>
              <div className="cx-connected-info">
                <div className="cx-connected-avatar">{igAccount.avatarInitials}</div>
                <div className="cx-connected-detail">
                  <div className="cx-connected-name">{igAccount.name}</div>
                  <div className="cx-connected-handle">{igAccount.username}</div>
                  <div className="cx-connected-meta">
                    {igAccount.type} · {igAccount.followers} followers · Connected {igAccount.connectedDate}
                  </div>
                </div>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>Active Permissions</div>
              <div className="cx-permissions">
                {igPerms.map((p, i) => (
                  <div className="cx-permission" key={i}>
                    <div className={`cx-perm-check${p.on ? '' : ' off'}`}>{p.on ? '✓' : '×'}</div>
                    {p.label}
                  </div>
                ))}
              </div>
              <div className="cx-connect-actions">
                <div className="cx-connect-note">Connected as Owner. Autopilot can post and read analytics on your behalf.</div>
                <button className="cx-disconnect-btn" onClick={() => handleDisconnect('ig')}>Disconnect</button>
              </div>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
                Connecting Instagram lets Autopilot publish your approved posts, schedule content, and pull engagement metrics automatically. A Meta Business Account is required.
              </div>
              <div className="cx-permissions" style={{opacity:0.45}}>
                {igPerms.map((p, i) => (
                  <div className="cx-permission" key={i}>
                    <div className="cx-perm-check off">·</div>
                    {p.label}
                  </div>
                ))}
              </div>
              <button
                className="cx-connect-btn ig-btn"
                onClick={() => handleConnect('ig')}
                disabled={igConnecting}
                style={{opacity: igConnecting ? 0.8 : 1, gap:8}}
              >
                {igConnecting ? (
                  <><Spinner />Waiting for authorisation…</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{flexShrink:0}}>
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                    Connect Instagram
                  </>
                )}
              </button>
              {igConnecting && (
                <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6,textAlign:'center'}}>
                  Complete sign-in in the popup window. Don't see it? Check for a blocked popup notification in your browser.
                </div>
              )}
              {!igConnecting && (
                <div style={{fontSize:10,color:'var(--text-muted)',lineHeight:1.6}}>
                  A popup will open to authorise Autopilot Digital via Meta. We only request the permissions listed above — we can never read your messages or access private data.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── FACEBOOK ── */}
      <div className={`cx-platform-card${fbAccount.connected ? ' connected' : ''}`}>
        <div className="cx-platform-header">
          <div className="cx-platform-icon fb">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </div>
          <div className="cx-platform-info">
            <div className="cx-platform-name">Facebook Page</div>
            <div className="cx-platform-desc">Required for Facebook posting and running ads through the Ads Application</div>
          </div>
          <div className={`cx-status-badge ${fbAccount.connected ? 'connected' : 'disconnected'}`}>
            <div className="cx-status-dot" />
            {fbAccount.connected ? 'Connected' : 'Not Connected'}
          </div>
        </div>

        <div className="cx-platform-body">
          {fbError && <ErrorBanner msg={fbError} onDismiss={() => setFbError(null)} />}

          {fbAccount.connected ? (
            <>
              <div className="cx-connected-info">
                <div className="cx-connected-avatar" style={{background:'rgba(24,119,242,0.2)',border:'2px solid #1877f2',color:'#6399fa'}}>
                  {fbAccount.avatarInitials}
                </div>
                <div className="cx-connected-detail">
                  <div className="cx-connected-name">{fbAccount.name}</div>
                  <div className="cx-connected-handle" style={{color:'#6399fa'}}>
                    Facebook Page{fbAccount.followers ? ` · ${fbAccount.followers} followers` : ''}
                  </div>
                  <div className="cx-connected-meta">
                    Business Page{fbAccount.connectedDate ? ` · Connected ${fbAccount.connectedDate}` : ''}
                  </div>
                </div>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:2}}>Active Permissions</div>
              <div className="cx-permissions">
                {fbPerms.map((p, i) => (
                  <div className="cx-permission" key={i}>
                    <div className={`cx-perm-check${p.on ? '' : ' off'}`}>{p.on ? '✓' : '×'}</div>
                    {p.label}
                  </div>
                ))}
              </div>
              <div className="cx-connect-actions">
                <div className="cx-connect-note">Connected as Page Admin. Required for Ads Application to submit and manage campaigns.</div>
                <button className="cx-disconnect-btn" onClick={() => handleDisconnect('fb')}>Disconnect</button>
              </div>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
                Connecting your Facebook Page enables posting, ads through the Ads Application, and Page analytics. Facebook is also required to run Instagram ad campaigns.
              </div>
              <div className="cx-permissions" style={{opacity:0.45}}>
                {fbPerms.map((p, i) => (
                  <div className="cx-permission" key={i}>
                    <div className="cx-perm-check off">·</div>
                    {p.label}
                  </div>
                ))}
              </div>
              <button
                className="cx-connect-btn fb-btn"
                onClick={() => handleConnect('fb')}
                disabled={fbConnecting}
                style={{opacity: fbConnecting ? 0.8 : 1, gap:8}}
              >
                {fbConnecting ? (
                  <><Spinner />Waiting for authorisation…</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{flexShrink:0}}>
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Connect Facebook Page
                  </>
                )}
              </button>
              {fbConnecting && (
                <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6,textAlign:'center'}}>
                  Complete sign-in in the popup window. Don't see it? Check for a blocked popup notification in your browser.
                </div>
              )}
              {!fbConnecting && (
                <div style={{fontSize:10,color:'var(--text-muted)',lineHeight:1.6}}>
                  A popup will open to authorise Autopilot Digital via Meta. A Facebook Page is required — personal profiles are not supported.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── TIKTOK ── */}
      <div className={`cx-platform-card${ttAccount.connected ? ' connected' : ''}`}>
        <div className="cx-platform-header">
          <div className="cx-platform-icon" style={{background:'#010101'}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
            </svg>
          </div>
          <div className="cx-platform-info">
            <div className="cx-platform-name">TikTok</div>
            <div className="cx-platform-desc">Publish posts to TikTok directly from Autopilot</div>
          </div>
          <div className={`cx-status-badge ${ttAccount.connected ? 'connected' : 'disconnected'}`}>
            <div className="cx-status-dot" />
            {ttAccount.connected ? 'Connected' : 'Not Connected'}
          </div>
        </div>

        <div className="cx-platform-body">
          {ttError && <ErrorBanner msg={ttError} onDismiss={() => setTtError(null)} />}

          {ttAccount.connected ? (
            <>
              <div className="cx-connected-info">
                {ttAccount.pictureUrl
                  ? <img src={ttAccount.pictureUrl} alt="" className="cx-connected-avatar" style={{objectFit:'cover',padding:0}} />
                  : <div className="cx-connected-avatar" style={{background:'rgba(255,255,255,0.12)',border:'2px solid #555',color:'#ddd'}}>{ttAccount.avatarInitials}</div>}
                <div className="cx-connected-detail">
                  <div className="cx-connected-name">{ttAccount.name}</div>
                  <div className="cx-connected-handle">{ttAccount.username || 'TikTok Creator'}</div>
                  <div className="cx-connected-meta">
                    TikTok Creator{ttAccount.connectedDate ? ` · Connected ${ttAccount.connectedDate}` : ''}
                  </div>
                </div>
              </div>
              <div className="cx-connect-actions">
                <div className="cx-connect-note">Connected. Autopilot can publish approved posts to your TikTok.</div>
                <button className="cx-disconnect-btn" onClick={handleTikTokDisconnect}>Disconnect</button>
              </div>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
                Connecting TikTok lets Autopilot publish your approved posts. Background music is auto-added by TikTok and content is disclosed as AI-generated automatically.
              </div>
              <button
                className="cx-connect-btn"
                onClick={handleTikTokConnect}
                disabled={ttConnecting}
                style={{background:'#010101', color:'#fff', opacity: ttConnecting ? 0.8 : 1, gap:8}}
              >
                {ttConnecting ? (
                  <><Spinner />Waiting for authorisation…</>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="white" style={{flexShrink:0}}>
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                    Connect TikTok
                  </>
                )}
              </button>
              {ttConnecting && (
                <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6,textAlign:'center'}}>
                  Complete sign-in in the popup window. Don't see it? Check for a blocked popup notification in your browser.
                </div>
              )}
              {!ttConnecting && (
                <div style={{fontSize:10,color:'var(--text-muted)',lineHeight:1.6}}>
                  A popup will open to authorise Autopilot Digital via TikTok. Posts publish with the privacy level you select for each post.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── GOOGLE BUSINESS PROFILE ── admin-only until Google verification;
          everyone else sees it under Coming Soon below. */}
      {gbpAvailable && (
      <div className={`cx-platform-card${gbpAccount.connected ? ' connected' : ''}`} data-testid="gbp-connect-card">
        <div className="cx-platform-header">
          <div className="cx-platform-icon" style={{background:'#fff'}}>
            <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.4 5.4 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/>
              <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/>
              <path fill="#EA4335" d="M12 4.74c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.62l3.98 3.09C6.22 6.85 8.87 4.74 12 4.74z"/>
            </svg>
          </div>
          <div className="cx-platform-info">
            <div className="cx-platform-name">Google Business Profile</div>
            <div className="cx-platform-desc">Publish Google posts and sync your business listing from Autopilot</div>
          </div>
          <div className={`cx-status-badge ${gbpAccount.connected ? 'connected' : 'disconnected'}`}>
            <div className="cx-status-dot" />
            {gbpAccount.connected ? 'Connected' : 'Not Connected'}
          </div>
        </div>

        <div className="cx-platform-body">
          {gbpError && <ErrorBanner msg={gbpError} onDismiss={() => setGbpError(null)} />}

          {gbpAccount.connected ? (
            <>
              <div className="cx-connected-info">
                {gbpAccount.pictureUrl
                  ? <img src={gbpAccount.pictureUrl} alt="" className="cx-connected-avatar" style={{objectFit:'cover',padding:0}} />
                  : <div className="cx-connected-avatar" style={{background:'rgba(255,255,255,0.12)',border:'2px solid #555',color:'#ddd'}}>{gbpAccount.avatarInitials}</div>}
                <div className="cx-connected-detail">
                  <div className="cx-connected-name">{gbpAccount.name}</div>
                  <div className="cx-connected-handle">{gbpAccount.username || 'Business Profile'}</div>
                  <div className="cx-connected-meta">
                    Business Profile{gbpAccount.connectedDate ? ` \u00b7 Connected ${gbpAccount.connectedDate}` : ''}
                  </div>
                </div>
              </div>
              <div className="cx-connect-actions">
                <div className="cx-connect-note">Connected. Autopilot can publish approved posts to this Google Business location.</div>
                <button className="cx-disconnect-btn" onClick={handleGoogleBizDisconnect}>Disconnect</button>
              </div>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6}}>
                Connecting Google Business lets Autopilot publish your approved posts to your Google listing, where they show up in Search and Maps.
              </div>
              <button
                className="cx-connect-btn"
                onClick={handleGoogleBizConnect}
                disabled={gbpConnecting}
                style={{background:'#fff', color:'#3c4043', border:'1px solid #dadce0', opacity: gbpConnecting ? 0.8 : 1, gap:8}}
              >
                {gbpConnecting ? (
                  <><Spinner />Waiting for authorisation…</>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" style={{flexShrink:0}}>
                      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.4 5.4 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
                      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z"/>
                      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09z"/>
                      <path fill="#EA4335" d="M12 4.74c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.62l3.98 3.09C6.22 6.85 8.87 4.74 12 4.74z"/>
                    </svg>
                    Connect Google Business
                  </>
                )}
              </button>
              {gbpConnecting && (
                <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.6,textAlign:'center'}}>
                  Complete sign-in in the popup window. Don't see it? Check for a blocked popup notification in your browser.
                </div>
              )}
              {!gbpConnecting && (
                <div style={{fontSize:10,color:'var(--text-muted)',lineHeight:1.6}}>
                  A popup will open to authorise Autopilot Digital via Google. You'll be asked to grant access to the Business Profile you manage.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}

      {/* ── COMING SOON ── */}
      <div>
        <div className="cx-section-label">More Platforms — Coming Soon</div>
        <div className="cx-coming-grid">
          {[
            ...(gbpAvailable ? [] : [{ icon:'G', name:'Google Business', bg:'#fff', color:'#4285F4' }]),
            { icon:'💼', name:'LinkedIn', bg:'#0077b5' },
          ].map((p, i) => (
            <div className="cx-coming-card" key={i} data-testid={`coming-soon-${p.name}`}>
              <div className="cx-coming-icon" style={{background:p.bg}}>
                <span style={{fontSize:16, fontWeight:700, ...(p.color ? { color:p.color } : {})}}>{p.icon}</span>
              </div>
              <div className="cx-coming-name">{p.name}</div>
              <div className="cx-coming-soon-tag">Soon</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// ============================================================
// FB PAGE PICKER MODAL — used when one personal FB account is admin
// on multiple Pages. User picks which Page belongs to this client.
// ============================================================
function FbPagePickerModal({ pages, submitting, onPick, onCancel }) {
  return (
    <div
      onClick={submitting ? undefined : onCancel}
      style={{
        position:'fixed', inset:0, zIndex:1000,
        background:'rgba(0,0,0,0.6)',
        display:'flex', alignItems:'center', justifyContent:'center',
        padding:16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Choose Facebook Page"
        style={{
          background:'#171a23',
          border:'1px solid rgba(255,255,255,0.08)',
          borderRadius:14,
          width:'100%', maxWidth:460,
          padding:'22px 22px 18px',
          color:'#fff',
          boxShadow:'0 30px 80px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ fontSize:16, fontWeight:700, marginBottom:6 }}>Choose a Facebook Page</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', lineHeight:1.5, marginBottom:14 }}>
          Your Facebook account manages more than one Page. Pick the Page Autopilot should post to for this client.
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:340, overflowY:'auto' }}>
          {pages.map(p => (
            <button
              key={p.id}
              onClick={() => onPick(p.id)}
              disabled={submitting}
              style={{
                display:'flex', alignItems:'center', gap:12,
                padding:'10px 12px',
                background:'rgba(255,255,255,0.04)',
                border:'1px solid rgba(255,255,255,0.08)',
                borderRadius:10,
                cursor: submitting ? 'wait' : 'pointer',
                textAlign:'left',
                color:'#fff',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {p.pictureUrl ? (
                <img
                  src={p.pictureUrl}
                  alt=""
                  width={36} height={36}
                  style={{ borderRadius:'50%', flexShrink:0, objectFit:'cover', background:'#0d0f14' }}
                />
              ) : (
                <div style={{
                  width:36, height:36, borderRadius:'50%', flexShrink:0,
                  background:'#0d0f14',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.55)',
                }}>{(p.name || 'P').slice(0,2).toUpperCase()}</div>
              )}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.45)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>ID: {p.id}</div>
              </div>
            </button>
          ))}
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:16 }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              background:'none',
              border:'1px solid rgba(255,255,255,0.15)',
              color:'rgba(255,255,255,0.75)',
              padding:'8px 14px',
              borderRadius:8,
              fontSize:12,
              cursor: submitting ? 'wait' : 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ADS PORTAL PANEL — Session 10
// ============================================================
const DEMO_ADS = [
  {
    id:1, name:'Spring Menu Launch', emoji:'☕', platform:'Instagram + Facebook',
    status:'active', budget:'$15/day', duration:'Apr 1–Apr 21', totalSpend:'$195',
    impressions:'18,420', reach:'14,200', clicks:'342', ctr:'1.86%', objective:'Awareness',
  },
  {
    id:2, name:'Happy Hour Promo', emoji:'🌅', platform:'Instagram',
    status:'scheduled', budget:'$10/day', duration:'Apr 14–Apr 28', totalSpend:'$0',
    impressions:'—', reach:'—', clicks:'—', ctr:'—', objective:'Traffic',
  },
  {
    id:3, name:'Valentine\'s Day Special', emoji:'❤️', platform:'Facebook',
    status:'ended', budget:'$20/day', duration:'Feb 10–Feb 14', totalSpend:'$100',
    impressions:'9,840', reach:'7,600', clicks:'188', ctr:'1.91%', objective:'Awareness',
  },
];

const DEMO_ADS_BANK = [
  { id:1, emoji:'☕', name:'Spring-menu-hero.jpg', date:'Apr 1', usedIn:'Spring Menu Launch' },
  { id:2, emoji:'🌿', name:'Ethiopian-beans.jpg', date:'Mar 28', usedIn:null },
  { id:3, emoji:'🌅', name:'Patio-sunset.jpg', date:'Mar 22', usedIn:'Happy Hour Promo' },
  { id:4, emoji:'🏆', name:'10yr-anniversary.jpg', date:'Mar 18', usedIn:null },
  { id:5, emoji:'🫘', name:'Cold-brew-close.jpg', date:'Mar 14', usedIn:null },
  { id:6, emoji:'👥', name:'Team-behind-scenes.jpg', date:'Mar 10', usedIn:null },
];

function AdsPortalPanel({ isSelfServe }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [adName, setAdName] = useState('');
  const [platform, setPlatform] = useState({ ig: true, fb: false });
  const [budget, setBudget] = useState('10');
  const [duration, setDuration] = useState('7');
  const [objective, setObjective] = useState('Awareness');
  const [ageMin, setAgeMin] = useState('25');
  const [ageMax, setAgeMax] = useState('54');
  const [location, setLocation] = useState('Kelowna, BC (25km radius)');
  const [submitted, setSubmitted] = useState(false);
  const [bankImages, setBankImages] = useState(DEMO_ADS_BANK);

  const totalBudget = parseInt(budget || 0) * parseInt(duration || 0);
  const estReachMin = Math.round(totalBudget * 180);
  const estReachMax = Math.round(totalBudget * 380);
  const platformLabel = platform.ig && platform.fb ? 'Instagram + Facebook' : platform.ig ? 'Instagram' : platform.fb ? 'Facebook' : 'None selected';

  function handleSubmit() {
    if (!adName || !selectedCreative || (!platform.ig && !platform.fb)) return;
    setSubmitted(true);
  }

  if (submitted && activeTab === 'builder') {
    return (
      <div className="ads-wrap">
        <div className="ads-tabs">
          {['overview','builder','ads-bank'].map(t => (
            <button key={t} className={`ads-tab${activeTab===t?' active':''}`}
              onClick={() => { setActiveTab(t); setSubmitted(false); }}>
              {t === 'overview' ? 'Active Ads' : t === 'builder' ? 'Ad Builder' : 'Ads Bank'}
            </button>
          ))}
        </div>
        <div className="ads-submitted">
          <div className="ads-submitted-icon">🚀</div>
          <div className="ads-submitted-title">Ad Submitted for Review</div>
          <div className="ads-submitted-sub">
            <strong style={{color:'var(--accent)'}}>{adName}</strong> has been sent to Autopilot Digital for review. Once approved, your ad will go live on {platformLabel}.<br/><br/>
            Estimated budget: <strong style={{color:'var(--text-primary)'}}>${totalBudget}</strong> over {duration} days · Est. reach {estReachMin.toLocaleString()}–{estReachMax.toLocaleString()} people.
          </div>
          <button className="ob-btn ob-btn-primary" style={{marginTop:8}} onClick={() => { setSubmitted(false); setActiveTab('overview'); setAdName(''); setSelectedCreative(null); }}>
            Back to Active Ads →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-wrap">

      {/* Tabs */}
      <div className="ads-tabs">
        {['overview','builder','ads-bank'].map(t => (
          <button key={t} className={`ads-tab${activeTab===t?' active':''}`}
            onClick={() => setActiveTab(t)}>
            {t === 'overview' ? 'Active Ads' : t === 'builder' ? 'Ad Builder' : 'Ads Bank'}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <>
          <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:'36px 24px',textAlign:'center',display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
            <div style={{fontSize:32}}>📣</div>
            <div style={{fontSize:15,fontWeight:700,color:'var(--text-primary)'}}>No Active Campaigns</div>
            <div style={{fontSize:13,color:'var(--text-muted)',maxWidth:320}}>Your ad campaigns will appear here once they go live. Use the Ad Builder to create and submit your first campaign.</div>
            <button className="ads-submit-btn" style={{width:'auto',padding:'11px 32px',marginTop:4}}
              onClick={() => setActiveTab('builder')}>
              + Create New Ad
            </button>
          </div>
        </>
      )}

      {/* ── BUILDER TAB ── */}
      {activeTab === 'builder' && (
        <div className="ads-builder-wrap">

          {/* Form */}
          <div className="ads-builder-form">

            <div className="ads-builder-section">
              <div className="ads-builder-section-title">Campaign Details</div>
              <div className="ads-field">
                <div className="ads-label">Campaign Name</div>
                <input className="ads-input" placeholder="e.g. Spring Patio Launch" value={adName} onChange={e => setAdName(e.target.value)} />
              </div>
              <div className="ads-field">
                <div className="ads-label">Objective</div>
                <select className="ads-input ads-select" value={objective} onChange={e => setObjective(e.target.value)}>
                  <option>Awareness</option>
                  <option>Traffic</option>
                  <option>Engagement</option>
                  <option>Lead Generation</option>
                </select>
              </div>
            </div>

            <div className="ads-builder-section">
              <div className="ads-builder-section-title">Platform</div>
              <div className="ads-platform-toggle">
                <div className={`ads-platform-opt${platform.ig ? ' selected-ig' : ''}`}
                  onClick={() => setPlatform(p => ({...p, ig: !p.ig}))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  Instagram
                </div>
                <div className={`ads-platform-opt${platform.fb ? ' selected-fb' : ''}`}
                  onClick={() => setPlatform(p => ({...p, fb: !p.fb}))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </div>
              </div>
              {!platform.ig && !platform.fb && (
                <div style={{fontSize:11,color:'#f5a623',padding:'6px 10px',background:'rgba(245,166,35,0.08)',borderRadius:7,border:'1px solid rgba(245,166,35,0.2)'}}>
                  Select at least one platform
                </div>
              )}
            </div>

            <div className="ads-builder-section">
              <div className="ads-builder-section-title">Budget & Schedule</div>
              <div className="ads-budget-row">
                <div className="ads-field">
                  <div className="ads-label">Daily Budget ($)</div>
                  <input className="ads-input" type="number" min="5" value={budget} onChange={e => setBudget(e.target.value)} placeholder="10" />
                </div>
                <div className="ads-field">
                  <div className="ads-label">Duration (days)</div>
                  <input className="ads-input" type="number" min="1" value={duration} onChange={e => setDuration(e.target.value)} placeholder="7" />
                </div>
              </div>
              <div className="ads-budget-hint">
                <strong>Total: ${totalBudget}</strong> · Typical local CPM: $4–$9 · Est. reach: {estReachMin.toLocaleString()}–{estReachMax.toLocaleString()} people
              </div>
            </div>

            <div className="ads-builder-section">
              <div className="ads-builder-section-title">Audience</div>
              <div className="ads-field">
                <div className="ads-label">Location</div>
                <input className="ads-input" value={location} onChange={e => setLocation(e.target.value)} />
              </div>
              <div className="ads-audience-grid">
                <div className="ads-field">
                  <div className="ads-label">Age Min</div>
                  <input className="ads-input" type="number" value={ageMin} onChange={e => setAgeMin(e.target.value)} />
                </div>
                <div className="ads-field">
                  <div className="ads-label">Age Max</div>
                  <input className="ads-input" type="number" value={ageMax} onChange={e => setAgeMax(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="ads-builder-section">
              <div className="ads-builder-section-title">Ad Creative — from Ads Bank</div>
              {bankImages.length === 0 ? (
                <div className="ads-creative-empty">
                  No images in your Ads Bank yet.<br/>
                  Generate images and send them to the Ads Bank, or upload directly.
                </div>
              ) : (
                <div className="ads-creative-grid">
                  {bankImages.map(img => (
                    <div
                      key={img.id}
                      className={`ads-creative-thumb${selectedCreative === img.id ? ' selected' : ''}`}
                      onClick={() => setSelectedCreative(selectedCreative === img.id ? null : img.id)}
                    >
                      {img.emoji}
                      {selectedCreative === img.id && <div className="ads-creative-check">✓</div>}
                    </div>
                  ))}
                </div>
              )}
              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:6,lineHeight:1.5}}>
                Posts generated in the Post Generator can be sent to the Ads Bank. Recommended: 1080×1080px or 1080×1350px.
              </div>
            </div>

            <button
              className="ads-submit-btn"
              disabled={!adName || !selectedCreative || (!platform.ig && !platform.fb)}
              onClick={handleSubmit}
            >
              Submit for Review →
            </button>
            <div style={{fontSize:10,color:'var(--text-muted)',textAlign:'center',lineHeight:1.6}}>
              Autopilot Digital reviews and approves all ads before they go live. Ads run through Meta's native ad system.
            </div>

          </div>

          {/* Preview */}
          <div className="ads-preview-card">
            <div className="ads-preview-title">Ad Preview</div>
            <div className="ads-preview-phone">
              <div className="ads-preview-phone-bar">
                <div className="ads-preview-phone-bar-dot"/>
                <div className="ads-preview-phone-bar-dot"/>
                <div className="ads-preview-phone-bar-dot"/>
                <span style={{marginLeft:4}}>
                  {platform.ig ? 'Instagram' : platform.fb ? 'Facebook' : 'Select a platform'}
                </span>
              </div>
              <div className="ads-preview-img">
                {selectedCreative
                  ? bankImages.find(i => i.id === selectedCreative)?.emoji || '🖼️'
                  : <span style={{fontSize:14,color:'var(--text-muted)'}}>Select creative</span>}
              </div>
              <div className="ads-preview-caption">
                <div className="ads-preview-caption-label">Sponsored</div>
                <div className="ads-preview-caption-text">
                  {adName || 'Your ad name will appear here'}
                </div>
              </div>
              <div className="ads-preview-cta">Learn More</div>
            </div>
            <div className="ads-est-box">
              <div className="ads-est-row">
                <div className="ads-est-label">Platform</div>
                <div className="ads-est-val">{platformLabel}</div>
              </div>
              <div className="ads-est-row">
                <div className="ads-est-label">Total Budget</div>
                <div className="ads-est-val">${totalBudget}</div>
              </div>
              <div className="ads-est-row">
                <div className="ads-est-label">Est. Reach</div>
                <div className="ads-est-val">{estReachMin.toLocaleString()}–{estReachMax.toLocaleString()}</div>
              </div>
              <div className="ads-est-row">
                <div className="ads-est-label">Duration</div>
                <div className="ads-est-val">{duration} days</div>
              </div>
              <div className="ads-est-note">Estimates based on typical Kelowna-area CPM ($4–$9). Actual results vary. Final spend managed through Meta Ads.</div>
            </div>
          </div>

        </div>
      )}

      {/* ── ADS BANK TAB ── */}
      {activeTab === 'ads-bank' && (
        <>
          <div className="ads-bank-toolbar">
            <div className="ads-bank-title">Ads Bank</div>
            <div className="ads-bank-count">{bankImages.length} creatives</div>
            <div style={{marginLeft:'auto',display:'flex',gap:8}}>
              <button className="pl-upload-btn" onClick={() => {
                const newImg = { id: crypto.randomUUID(), emoji: ['🌿','🏆','☕','🌅','🫘'][Math.floor(Math.random()*5)], name:`Ad-creative-${bankImages.length+1}.jpg`, date:'Today', usedIn:null };
                setBankImages(b => [newImg, ...b]);
              }}>
                + Upload Creative
              </button>
            </div>
          </div>

          {bankImages.length === 0 ? (
            <div className="ads-bank-empty">
              <div className="ads-bank-empty-icon">🗂️</div>
              <div className="ads-bank-empty-title">Ads Bank is Empty</div>
              <div className="ads-bank-empty-sub">Generate posts in the Post Generator and send them here, or upload ad creatives directly.</div>
              <button className="ob-btn ob-btn-primary" onClick={() => {}}>Generate Images →</button>
            </div>
          ) : (
            <>
              <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:4,lineHeight:1.6}}>
                These creatives are exclusively for ads. To route images here from the Post Generator, select "Send to Ads Bank" when approving an image.
              </div>
              <div className="ads-bank-grid">
                {bankImages.map(img => (
                  <div key={img.id} className="ads-bank-card">
                    <div className="ads-bank-img">{img.emoji}</div>
                    <div className="ads-bank-info">
                      <div className="ads-bank-name">{img.name}</div>
                      <div className="ads-bank-meta">
                        {img.usedIn
                          ? <span style={{color:'var(--accent)'}}>Used in: {img.usedIn}</span>
                          : `Added ${img.date}`
                        }
                      </div>
                    </div>
                    <div className="ads-bank-overlay">
                      <button className="ads-bank-use-btn" onClick={() => { setSelectedCreative(img.id); setActiveTab('builder'); }}>
                        Use in Ad
                      </button>
                      <button className="ads-bank-del-btn" onClick={() => setBankImages(b => b.filter(i => i.id !== img.id))}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';

const KEY_LABELS = {
  claude: 'Claude API',
  fal: 'FAL Image Gen',
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  youtube: 'YouTube',
  twitter: 'Twitter / X',
  threads: 'Threads',
  google_biz: 'Google Business',
  google_analytics: 'Google Analytics',
  stripe: 'Stripe',
  resend: 'Resend',
  twilio: 'Twilio',
  blotato: 'Blotato',
};

export function AdminPanel() {
  const { clientId } = useClientId();
  // Which client, if any, this admin is currently viewing as. Read here only to
  // render the pill's own state ("View as" vs. "Viewing"); the exit control
  // lives in the root shell so it stays on screen after navigating away.
  const { viewingAsClientId } = useViewingAs();
  // Admin access is decided by the backend, not here. On mount we call an
  // admin-only endpoint with nothing but the signed-in Supabase session; the
  // response status is the answer. There is no password and no client-side
  // condition — ADMIN_USER_IDS on the backend is the whole gate.
  //
  // 'error' is deliberately distinct from 'denied': a backend that is down or
  // unreachable must not tell an admin they have lost access.
  const [access, setAccess] = React.useState('checking'); // checking | authorized | denied | error
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [restarting, setRestarting] = React.useState(false);
  const [restartMsg, setRestartMsg] = React.useState('');
  const [show2xBanner, setShow2xBanner] = React.useState(false);
  const [bannerClientName, setBannerClientName] = React.useState('');
  const [genClientId, setGenClientId] = React.useState(clientId);
  const [genTopic, setGenTopic]       = React.useState('');
  const [genLoading, setGenLoading]   = React.useState(false);
  const [genError, setGenError]       = React.useState('');
  const [toast, setToast]             = React.useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  // ── Managed clients overview (read-only) ──
  const [managed, setManaged]               = React.useState([]);
  const [managedRunway, setManagedRunway]   = React.useState(null); // { windowDays, threshold }
  const [managedLoading, setManagedLoading] = React.useState(false);
  const [managedError, setManagedError]     = React.useState('');

  // ── Managed upgrade applications ──
  const [upgradeApps, setUpgradeApps]               = React.useState([]);
  const [upgradeAppsLoading, setUpgradeAppsLoading] = React.useState(false);
  const [upgradeAppsError, setUpgradeAppsError]     = React.useState('');
  // Per-application: { loading } | { amountDue, currency } | { error }, keyed by app id.
  const [previews, setPreviews]                     = React.useState({});
  const [approvingId, setApprovingId]               = React.useState(null);

  const fmtMoney = (amount, currency) =>
    (amount / 100).toLocaleString('en-CA', { style: 'currency', currency: (currency || 'cad').toUpperCase() });

  const adminPost = async (path, body = {}) => {
    const res = await fetch(`${BACKEND}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
    return data;
  };

  const adminGet = async (path) => {
    const res = await fetch(`${BACKEND}${path}`, { headers: { ...(await authHeader()) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
    return data;
  };

  const fetchManagedClients = async () => {
    setManagedLoading(true);
    setManagedError('');
    try {
      const data = await adminGet('/api/admin/clients');
      setManaged(data.clients || []);
      setManagedRunway(data.runway || null);
    } catch (err) {
      setManagedError(err.message);
    }
    setManagedLoading(false);
  };

  // ── "View as" — enter the act-as-client context ──
  // Two separate things happen here and only one of them is a permission:
  //   1. setViewingAs writes in-memory UI state. It grants nothing on its own —
  //      requireOwnerOrAdmin re-checks ADMIN_USER_IDS on every request that
  //      carries the override, so this is a convenience, not a key.
  //   2. the audit POST records that an admin entered the context.
  //
  // The audit call is awaited but deliberately non-blocking on failure: the
  // backend answers 200 with { logged: false } when the row could not be
  // written, and refusing to switch views over a logging outage would trade a
  // real outage for a bookkeeping gap. A failure is surfaced as a toast rather
  // than swallowed, so it is visible without being obstructive.
  const [viewingAsBusy, setViewingAsBusy] = React.useState(null);

  const handleViewAs = async (client) => {
    setViewingAsBusy(client.id);
    setViewingAs(client.id, client.brand_name || client.id);
    try {
      const data = await adminPost('/api/admin/viewing-as', {
        action:     'enter',
        clientId:   client.id,
        clientName: client.brand_name || null,
      });
      showToast(data?.logged === false
        ? `Viewing as ${client.brand_name || client.id} — audit log write FAILED`
        : `Viewing as ${client.brand_name || client.id}`);
    } catch (err) {
      // The context is already set; say so plainly rather than implying the
      // switch did not happen.
      showToast(`Viewing as ${client.brand_name || client.id} — audit log unreachable (${err.message})`);
    }
    setViewingAsBusy(null);
  };

  // ── Find client by email ──
  // The Managed list above is the only other way to reach View As, and it
  // holds Managed clients only — this is how an admin gets to a Self-Serve
  // client. One exact address in, one client (or "No client found") out; the
  // backend refuses partial or wildcard input, and there is no list to page.
  // View As on the result is the same handleViewAs, audit POST included.
  const [lookupEmail, setLookupEmail]     = React.useState('');
  const [lookupBusy, setLookupBusy]       = React.useState(false);
  const [lookupResult, setLookupResult]   = React.useState(null); // null | { found, client? }
  const [lookupError, setLookupError]     = React.useState('');

  const findClientByEmail = async (e) => {
    e?.preventDefault();
    const email = lookupEmail.trim();
    if (!email) return;
    setLookupBusy(true);
    setLookupError('');
    setLookupResult(null);
    try {
      const data = await adminPost('/api/admin/client-by-email', { email });
      setLookupResult({ found: !!data.found, client: data.client || null });
    } catch (err) {
      setLookupError(err.message);
    }
    setLookupBusy(false);
  };

  const fetchUpgradeApps = async () => {
    setUpgradeAppsLoading(true);
    setUpgradeAppsError('');
    try {
      const data = await adminPost('/api/admin/plan-upgrade-applications');
      setUpgradeApps(data.applications || []);
    } catch (err) {
      setUpgradeAppsError(err.message);
    }
    setUpgradeAppsLoading(false);
  };

  const previewUpgrade = async (id) => {
    setPreviews(p => ({ ...p, [id]: { loading: true } }));
    try {
      const data = await adminPost('/api/admin/plan-upgrade-preview', { applicationId: id });
      setPreviews(p => ({ ...p, [id]: { amountDue: data.amountDue, currency: data.currency } }));
    } catch (err) {
      setPreviews(p => ({ ...p, [id]: { error: err.message } }));
    }
  };

  // Charges a real card. Preview is required first so the confirm can state the
  // exact amount rather than asking for a blind approval.
  const approveUpgrade = async (app) => {
    const preview = previews[app.id];
    if (!preview || preview.amountDue == null) {
      showToast('Preview the charge first.');
      return;
    }
    const amount = fmtMoney(preview.amountDue, preview.currency);
    if (!window.confirm(
      `Approve ${app.business_name} for Managed?\n\n` +
      `This charges ${amount} to their card immediately and switches the plan right away.\n\n` +
      `The $300 setup fee is NOT included — invoice that separately if onboarding work is needed.`
    )) return;

    setApprovingId(app.id);
    try {
      await adminPost('/api/admin/approve-plan-upgrade', { applicationId: app.id });
      showToast(`${app.business_name} upgraded to Managed.`);
      setPreviews(p => { const next = { ...p }; delete next[app.id]; return next; });
      await fetchUpgradeApps();
    } catch (err) {
      showToast(`Upgrade failed: ${err.message}`);
    }
    setApprovingId(null);
  };

  // DEPRECATED 2026-08-10 — pending removal, using dead brand-hub.json flat-file source. See if any errors/404s appear before final deletion
  /*
  const handleGeneratePost = async () => {
    setGenLoading(true);
    setGenError('');
    try {
      // Step 1: fetch brand hub data for this client
      const brandRes = await fetch(`${BACKEND}/api/brand-hub`);
      const brandData = brandRes.ok ? await brandRes.json() : {};

      // Step 2: POST to generate-post with full brand context merged in
      const res = await fetch(`${BACKEND}/api/generate-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...brandData,
          clientId: genClientId,
          topic: genTopic,
          platform: 'instagram',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Error ${res.status}`);
      }
      setGenLoading(false);
      showToast('Post generated successfully!');
    } catch (err) {
      setGenLoading(false);
      setGenError(err.message);
    }
  };
  */

  // Doubles as the access probe: /admin/status is admin-only server-side, so
  // its status code decides whether this panel renders at all.
  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/admin/status`, {
        headers: { ...(await authHeader()) },
      });
      if (res.status === 401 || res.status === 403) {
        setStatus(null);
        setAccess('denied');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus(await res.json());
      setAccess('authorized');
    } catch {
      setStatus(null);
      // Never downgrade an established session on a transient failure — a
      // refresh that times out is not a loss of admin rights.
      setAccess(prev => (prev === 'authorized' ? prev : 'error'));
    }
    setLoading(false);
  };

  const handleRestart = async () => {
    setRestarting(true);
    setRestartMsg('');
    try {
      const res = await fetch(`${BACKEND}/admin/restart-scheduler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      });
      const data = await res.json();
      setRestartMsg(data.message || 'Triggered.');
      fetchStatus();
    } catch {
      setRestartMsg('Failed to reach backend.');
    }
    setRestarting(false);
  };

  // Access probe. Runs once; fetchStatus sets `access` from the response.
  React.useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (access !== 'authorized') return;
    fetchManagedClients();
    fetchUpgradeApps();
    if (!clientId) return;
    if (sessionStorage.getItem('ap_2x_banner_shown')) return;
    // Phase 2: moved off direct Supabase, behaviour preserved exactly —
    // authedFetch, NOT clientScopedFetch, so this keeps reading the ADMIN's own
    // row as it always has.
    //
    // KNOWN BUG, deliberately not fixed here (backlogged 2026-09-08): the
    // banner reads "<name> is on 2x Growth Boost — 48 Generated Images Per
    // Month instead of 24", which is an operational note about a CLIENT, but
    // clientId is the signed-in admin's id. So it describes the admin's own
    // row, not any client's. Predates the Managed Clients pill list. Turning on the override
    // would make the wording true for the first time, but that is a behaviour
    // change and this phase is a migration.
    authedFetch(`${BACKEND}/api/brand-profile`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (d?.profile?.two_x_daily) {
          setBannerClientName(d.profile.brand_name || 'This client');
          setShow2xBanner(true);
          sessionStorage.setItem('ap_2x_banner_shown', '1');
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access, clientId]);

  if (access !== 'authorized') {
    return (
      <div style={adminStyles.gateWrap}>
        <div style={adminStyles.gateBox}>
          {access === 'checking' && (
            <>
              <h2 style={adminStyles.gateTitle}>Checking access…</h2>
              <p style={adminStyles.meta}>Confirming your account with the backend.</p>
            </>
          )}
          {access === 'denied' && (
            <>
              <h2 style={adminStyles.gateTitle}>Not authorized</h2>
              <p style={adminStyles.meta}>
                This area is limited to administrator accounts. You are signed in, but this
                account is not one of them.
              </p>
            </>
          )}
          {access === 'error' && (
            <>
              <h2 style={adminStyles.gateTitle}>Couldn't check access</h2>
              <p style={adminStyles.meta}>
                The backend didn't respond. This is not a permissions problem — try again.
              </p>
              <button onClick={fetchStatus} style={adminStyles.btnTeal}>Retry</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={adminStyles.wrap}>
      <h2 style={adminStyles.title}>Admin Dashboard</h2>

      {show2xBanner && (
        <div style={{display:'flex',alignItems:'flex-start',gap:12,background:'rgba(49,220,186,0.10)',border:'1px solid rgba(49,220,186,0.35)',borderRadius:10,padding:'14px 18px',marginBottom:20}}>
          <span style={{fontSize:16,lineHeight:1}}>⚡</span>
          <span style={{flex:1,fontSize:13,color:'#d0faf3',lineHeight:1.55}}>
            <strong style={{color:'#31DCBA'}}>{bannerClientName}</strong> is on 2x Growth Boost — 48 Generated Images Per Month instead of 24.
          </span>
          <button
            onClick={() => setShow2xBanner(false)}
            style={{background:'transparent',border:'none',color:'rgba(49,220,186,0.6)',fontSize:16,cursor:'pointer',lineHeight:1,padding:0,flexShrink:0}}
            aria-label="Dismiss"
          >✕</button>
        </div>
      )}

      <div style={adminStyles.card}>
        <h3 style={adminStyles.cardTitle}>Find Client by Email</h3>
        <form onSubmit={findClientByEmail} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <input
            type="email"
            aria-label="Client email"
            placeholder="client@example.com"
            value={lookupEmail}
            onChange={e => { setLookupEmail(e.target.value); setLookupResult(null); setLookupError(''); }}
            style={{ ...adminStyles.input, flex: '1 1 240px', minWidth: 0 }}
          />
          <button
            type="submit"
            disabled={lookupBusy || !lookupEmail.trim()}
            style={{
              background: '#31DCBA', color: '#000', border: 'none', borderRadius: 8,
              padding: '10px 18px', fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 13,
              cursor: (lookupBusy || !lookupEmail.trim()) ? 'default' : 'pointer',
              opacity: (lookupBusy || !lookupEmail.trim()) ? 0.6 : 1,
            }}
          >
            {lookupBusy ? 'Searching…' : 'Search'}
          </button>
        </form>

        {lookupError && <p style={adminStyles.errorText}>{lookupError}</p>}
        {lookupResult && !lookupResult.found && (
          <p style={adminStyles.meta}>No client found for that email.</p>
        )}
        {lookupResult?.found && lookupResult.client && (() => {
          const c = lookupResult.client;
          const viewing = viewingAsClientId === c.id;
          const busy    = viewingAsBusy === c.id;
          return (
            <div
              data-testid="client-lookup-result"
              style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                padding: '10px 18px', borderRadius: 999,
                background: 'rgba(255,255,255,0.04)', border: '1px solid #31DCBA',
                fontFamily: 'Barlow, sans-serif', fontSize: 13, color: '#fff',
              }}
            >
              <strong style={{ minWidth: 140 }}>{c.brand_name || c.id}</strong>
              <span style={{ color: '#aaa' }}>
                {c.plan === 'managed' ? 'Managed' : c.plan === 'self_serve' ? 'Self-Serve' : 'Plan not set'}
              </span>
              <button
                onClick={() => handleViewAs(c)}
                disabled={busy || viewing}
                style={{
                  marginLeft: 'auto',
                  background: viewing ? 'transparent' : '#31DCBA',
                  color:      viewing ? '#31DCBA' : '#000',
                  border:     viewing ? '1px solid #31DCBA' : 'none',
                  borderRadius: 999, padding: '4px 14px',
                  fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 12,
                  cursor: (busy || viewing) ? 'default' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                  flexShrink: 0,
                }}
              >
                {viewing ? 'Viewing' : busy ? '…' : 'View as'}
              </button>
            </div>
          );
        })()}
      </div>

      <StuckClientsCard
        adminGet={adminGet}
        adminPost={adminPost}
        onViewAs={handleViewAs}
        viewingAsClientId={viewingAsClientId}
        viewingAsBusy={viewingAsBusy}
        styles={adminStyles}
      />

      <div style={adminStyles.card}>
        <h3 style={adminStyles.cardTitle}>
          Managed Clients
          {managed.length > 0 && (
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px',
              borderRadius: 10, background: '#31DCBA22', border: '1px solid #31DCBA', color: '#31DCBA',
            }}>{managed.length}</span>
          )}
        </h3>

        {managedLoading && <p style={adminStyles.meta}>Loading clients…</p>}
        {managedError && <p style={adminStyles.errorText}>{managedError}</p>}
        {!managedLoading && !managedError && managed.length === 0 && (
          <p style={adminStyles.meta}>
            No clients on the Managed plan. If that looks wrong, brand_profile.plan
            may not have been backfilled yet — unclassified rows are excluded.
          </p>
        )}

        {managed.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {managed.map(c => {
              // Worst state decides the pill's border. Red is reserved for a
              // real problem (a permanently failed post, a lapsed payment);
              // amber is "needs looking at soon". A Stripe lookup that failed
              // reads 'unknown' and is deliberately neither.
              const overdue  = c.paidStatus === 'past_due' || c.paidStatus === 'unpaid';
              const bad      = overdue || c.failedPosts > 0;

              // `low` is unchanged as a field and unchanged in what it does to
              // the pill — only how the server decides it has changed, from a
              // post count to day coverage. The amber rule here is the same
              // rule it always was.
              const runwayLow   = !!c.runway?.low;
              const coveredDays = c.runway?.coveredPickedDays;
              const pickedDays  = c.runway?.pickedDaysInWindow;
              // Both numbers, or neither: a half-populated runway object means
              // a backend mid-deploy, and the old label is the honest render.
              const hasCoverage = typeof coveredDays === 'number' && typeof pickedDays === 'number';
              const runwayTitle = hasCoverage
                ? `Covered ${coveredDays} of ${pickedDays} posting ${pickedDays === 1 ? 'day' : 'days'}`
                  + ` in the next ${managedRunway?.windowDays ?? 7} days.`
                  + (typeof c.runway?.required === 'number' ? ` Flagged below ${c.runway.required}.` : '')
                : managedRunway
                  ? `Scheduled in the next ${managedRunway.windowDays} days. Flagged below ${managedRunway.threshold}.`
                  : undefined;

              const warn     = !bad && (runwayLow || c.connections?.needsAttention?.length > 0);
              const accent   = bad ? '#ff4d4d' : warn ? '#ffb020' : '#31DCBA';

              const paidLabel = {
                active:   'Active',
                trialing: 'Trialing',
                past_due: 'Past due',
                unpaid:   'Unpaid',
                canceled: 'Canceled',
                none:     'No subscription',
                unknown:  'Billing unknown',
              }[c.paidStatus] || c.paidStatus;

              const conn = c.connections || { connected: [], needsAttention: [], total: 0 };
              const connLabel = conn.total === 0
                ? 'No connections'
                : `${conn.total}/4 connected${conn.needsAttention.length ? ` · ${conn.needsAttention.length} expiring` : ''}`;

              return (
                <div
                  key={c.id}
                  style={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                    padding: '10px 18px', borderRadius: 999,
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${accent}`,
                    fontFamily: 'Barlow, sans-serif', fontSize: 13, color: '#fff',
                  }}
                >
                  <span style={{ ...adminStyles.dot, background: accent }} />
                  <strong style={{ minWidth: 140 }}>{c.brand_name || c.id}</strong>

                  <span style={{ color: overdue ? '#ff4d4d' : '#aaa' }}>{paidLabel}</span>
                  <span style={{ color: '#555' }}>|</span>

                  <span style={{ color: '#aaa' }}>{c.queuedPosts} queued</span>
                  <span style={{ color: '#555' }}>|</span>

                  <span style={{ color: c.failedPosts > 0 ? '#ff4d4d' : '#aaa' }}>
                    {c.failedPosts} failed
                  </span>
                  <span style={{ color: '#555' }}>|</span>

                  {/* DAYS COVERED, NOT POSTS COUNTED. "3 in 7d" could not tell
                      a client who posts every day from one who posts on
                      Mondays: the first was fine and the second could never
                      reach three, so the badge was permanently amber for them
                      and read as healthy for three posts stacked on one day.
                      The backend now answers how many of this client's own
                      posting days in the window have something going out.

                      The denominator is the client's PICKED days, not the
                      threshold, so the label never reads "5 of 3". The bar
                      itself lives in the tooltip, and `low` — computed
                      server-side against min(threshold, picked) — is what
                      actually turns the pill amber.

                      FALLS BACK TO THE OLD LABEL when the fields are absent,
                      because this panel can deploy before the backend does.
                      One deploy of "N in 7d" is better than a pill reading
                      "undefined of undefined". */}
                  <span
                    style={{ color: runwayLow ? '#ffb020' : '#aaa' }}
                    title={runwayTitle}
                  >
                    {hasCoverage
                      ? `${coveredDays} of ${pickedDays} ${pickedDays === 1 ? 'day' : 'days'} covered`
                      : `${c.runway?.scheduledNext7 ?? 0} in 7d`}
                    {runwayLow && <span style={{ marginLeft: 6 }}>⚠</span>}
                  </span>
                  <span style={{ color: '#555' }}>|</span>

                  <span
                    style={{ color: conn.needsAttention.length ? '#ffb020' : conn.total === 0 ? '#ff4d4d' : '#aaa' }}
                    title={conn.connected.length ? conn.connected.join(', ') : 'None connected'}
                  >
                    {connLabel}
                  </span>

                  {/* The picker IS the pill list — there is one admin looking at
                      one client at a time, so a separate chooser would be a
                      second list of the same rows. marginLeft:auto pins it to
                      the right edge as the row wraps on narrow screens. */}
                  <button
                    onClick={() => handleViewAs(c)}
                    disabled={viewingAsBusy === c.id || viewingAsClientId === c.id}
                    style={{
                      marginLeft: 'auto',
                      background: viewingAsClientId === c.id ? 'transparent' : '#31DCBA',
                      color:      viewingAsClientId === c.id ? '#31DCBA' : '#000',
                      border:     viewingAsClientId === c.id ? '1px solid #31DCBA' : 'none',
                      borderRadius: 999, padding: '4px 14px',
                      fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 12,
                      cursor: (viewingAsBusy === c.id || viewingAsClientId === c.id) ? 'default' : 'pointer',
                      opacity: viewingAsBusy === c.id ? 0.6 : 1,
                      flexShrink: 0,
                    }}
                  >
                    {viewingAsClientId === c.id ? 'Viewing' : viewingAsBusy === c.id ? '…' : 'View as'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <button onClick={fetchManagedClients} style={{ ...adminStyles.btnTeal, marginTop: 16 }}>↻ Refresh</button>
      </div>

      <div style={adminStyles.card}>
        <h3 style={adminStyles.cardTitle}>
          Managed Upgrade Applications
          {upgradeApps.length > 0 && (
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px',
              borderRadius: 10, background: '#31DCBA22', border: '1px solid #31DCBA', color: '#31DCBA',
            }}>{upgradeApps.length}</span>
          )}
        </h3>

        {upgradeAppsLoading && <p style={adminStyles.meta}>Loading applications…</p>}
        {upgradeAppsError && <p style={adminStyles.errorText}>{upgradeAppsError}</p>}
        {!upgradeAppsLoading && !upgradeAppsError && upgradeApps.length === 0 && (
          <p style={adminStyles.meta}>No pending applications.</p>
        )}

        {upgradeApps.map(app => {
          const preview = previews[app.id] || {};
          const approving = approvingId === app.id;
          return (
            <div key={app.id} style={{
              border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
              padding: '14px 16px', marginBottom: 12,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#e7ecf2', marginBottom: 2 }}>
                {app.business_name}
              </div>
              <p style={{ ...adminStyles.meta, marginBottom: 10 }}>
                {[app.contact_name, app.contact_email, app.phone].filter(Boolean).join(' · ')}
                {app.created_at && ` — ${new Date(app.created_at).toLocaleDateString()}`}
              </p>

              {[
                ['Not working', app.current_challenges],
                ['Goals',       app.goals],
                ['Notes',       app.notes],
              ].filter(([, v]) => v).map(([label, value]) => (
                <p key={label} style={{ ...adminStyles.meta, marginBottom: 6 }}>
                  <span style={{ color: '#8b95a5' }}>{label}:</span> {value}
                </p>
              ))}

              {preview.error && <p style={adminStyles.errorText}>{preview.error}</p>}
              {preview.amountDue != null && (
                <p style={{ ...adminStyles.meta, color: '#31DCBA', marginTop: 10 }}>
                  Charges <strong>{fmtMoney(preview.amountDue, preview.currency)}</strong> immediately (prorated).
                </p>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button
                  onClick={() => previewUpgrade(app.id)}
                  disabled={preview.loading || approving}
                  style={{ ...adminStyles.btnTeal, opacity: preview.loading ? 0.6 : 1 }}
                >
                  {preview.loading ? 'Checking…' : preview.amountDue != null ? '↻ Re-check charge' : 'Preview charge'}
                </button>
                <button
                  onClick={() => approveUpgrade(app)}
                  disabled={preview.amountDue == null || approving}
                  style={{
                    ...adminStyles.btnTeal,
                    background: 'transparent',
                    border: '1px solid rgba(245,166,35,0.5)',
                    color: '#f5a623',
                    opacity: preview.amountDue == null || approving ? 0.5 : 1,
                    cursor: preview.amountDue == null || approving ? 'default' : 'pointer',
                  }}
                >
                  {approving ? 'Approving…' : 'Approve & charge'}
                </button>
              </div>
            </div>
          );
        })}

        <button onClick={fetchUpgradeApps} style={{ ...adminStyles.btnTeal, marginTop: 4 }}>↻ Refresh</button>
      </div>

      <div style={adminStyles.card}>
        <h3 style={adminStyles.cardTitle}>Scheduler</h3>
        <p style={adminStyles.meta}>
          Last run: {status?.last_scheduler_run
            ? new Date(status.last_scheduler_run).toLocaleString()
            : 'No run recorded'}
        </p>
        <button onClick={handleRestart} disabled={restarting} style={adminStyles.btnTeal}>
          {restarting ? 'Restarting...' : '▶ Restart Scheduler'}
        </button>
        {restartMsg && <p style={adminStyles.successText}>{restartMsg}</p>}
      </div>
      <div style={adminStyles.card}>
        <h3 style={adminStyles.cardTitle}>API Key Status</h3>
        {loading && <p style={adminStyles.meta}>Loading...</p>}
        {status?.keys && (
          <div style={adminStyles.pillGrid}>
            {Object.entries(status.keys).map(([key, active]) => (
              <div key={key} style={adminStyles.pillRow}>
                <span style={{ ...adminStyles.pill, background: active ? '#31DCBA22' : 'rgba(255,77,77,0.1)', border: `1px solid ${active ? '#31DCBA' : '#ff4d4d'}` }}>
                  <span style={{ ...adminStyles.dot, background: active ? '#31DCBA' : '#ff4d4d' }} />
                  {KEY_LABELS[key] || key}
                </span>
              </div>
            ))}
          </div>
        )}
        <button onClick={fetchStatus} style={{ ...adminStyles.btnTeal, marginTop: 16 }}>↻ Refresh</button>
      </div>

      {/* DEPRECATED 2026-08-10 — pending removal, using dead brand-hub.json flat-file source. See if any errors/404s appear before final deletion
      <div style={adminStyles.card}>
        <h3 style={adminStyles.cardTitle}>Generate Post for Client</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <p style={{ ...adminStyles.meta, marginBottom: 4 }}>Client ID</p>
            <input
              value={genClientId}
              onChange={e => setGenClientId(e.target.value)}
              style={{ ...adminStyles.input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <p style={{ ...adminStyles.meta, marginBottom: 4 }}>Topic</p>
            <input
              value={genTopic}
              onChange={e => setGenTopic(e.target.value)}
              style={{ ...adminStyles.input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={handleGeneratePost} disabled={genLoading} style={adminStyles.btnTeal}>
              Generate Post
            </button>
            {genLoading && (
              <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(49,220,186,0.25)', borderTopColor: '#31DCBA', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            )}
          </div>
          {genError && <p style={adminStyles.errorText}>{genError}</p>}
        </div>
      </div>
      */}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#31DCBA', color: '#000', padding: '10px 20px', borderRadius: 8, fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 14, zIndex: 9999, whiteSpace: 'nowrap' }}>
          {toast}
        </div>
      )}
    </div>
  );
}

const adminStyles = {
  wrap: { padding: '32px 24px', color: '#fff', maxWidth: 700 },
  title: { fontSize: 24, fontFamily: 'Barlow Condensed, sans-serif', color: '#31DCBA', marginBottom: 24 },
  card: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 24, marginBottom: 20 },
  cardTitle: { fontSize: 16, fontFamily: 'Barlow Condensed, sans-serif', color: '#31DCBA', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 },
  meta: { fontSize: 13, color: '#aaa', marginBottom: 12 },
  pillGrid: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  pillRow: {},
  pill: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999, fontSize: 13, fontFamily: 'Barlow, sans-serif', color: '#fff' },
  dot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  btnTeal: { background: '#31DCBA', color: '#000', border: 'none', borderRadius: 8, padding: '10px 20px', fontFamily: 'Barlow, sans-serif', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  gateWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  gateBox: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 40, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 300 },
  gateTitle: { color: '#31DCBA', fontFamily: 'Barlow Condensed, sans-serif', fontSize: 22, margin: 0 },
  input: { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14, fontFamily: 'Barlow, sans-serif' },
  errorText: { color: '#ff4d4d', fontSize: 13, margin: 0 },
  successText: { color: '#31DCBA', fontSize: 13, marginTop: 8 },
};
const PANELS = {
  home:        HomePanel,
  onboarding:  OnboardingPanel,
  brand:       BrandHubPanel,
  calendar:    CalendarPanel,
  approval:    PostApprovalPanel,
  photos:      PhotoLibraryPanel,
  imagegen:    ImageGenerationPanel,
  stories:     StoriesPanel,
  performance: PostPerformancePanel,
  account:     AccountPanel,
  connections: ConnectionsPanel,
  ads:         AdsPortalPanel,
  admin:       AdminPanel,
};

// ============================================================
// SVG ICONS
// ============================================================
function NavIcon({ id }) {
  const icons = {
    'home':        <><path d="M1 6.5L8 1l7 5.5V15a1 1 0 01-1 1H2a1 1 0 01-1-1V6.5z"/><path d="M5.5 16V10h5v6"/></>,
    'check-circle':<><circle cx="8" cy="8" r="7"/><polyline points="5,8 7,10 11,6"/></>,
    'star':        <polygon points="8,1 10.5,5.5 15.5,6.3 11.75,9.9 12.6,15 8,12.4 3.4,15 4.25,9.9 0.5,6.3 5.5,5.5"/>,
    'calendar':    <><rect x="1" y="2" width="14" height="13" rx="2"/><line x1="1" y1="6.5" x2="15" y2="6.5"/><line x1="5" y1="1" x2="5" y2="4"/><line x1="11" y1="1" x2="11" y2="4"/></>,
    'file-check':  <><rect x="2" y="1" width="12" height="14" rx="2"/><line x1="5" y1="5" x2="11" y2="5"/><line x1="5" y1="8" x2="11" y2="8"/><polyline points="5,11 7,13 11,10"/></>,
    'image':       <><rect x="1" y="3" width="14" height="11" rx="2"/><circle cx="5.5" cy="7.5" r="1.5"/><polyline points="1,12 5,8 8,11 10.5,8.5 15,13"/></>,
    'video':       <><rect x="1" y="4" width="10" height="8" rx="2"/><polygon points="11,7 15,4.5 15,11.5 11,9"/></>,
    'sparkle':     <><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4"/><circle cx="8" cy="8" r="2.5"/></>,
    'phone':       <><rect x="4" y="1" width="8" height="14" rx="2"/><line x1="6" y1="4" x2="10" y2="4"/><line x1="6" y1="7" x2="10" y2="7"/></>,
    'line-chart':  <><polyline points="1,13 4,8 7,10 10,5 13,7 15,3"/><line x1="1" y1="15" x2="15" y2="15"/></>,
    'user':        <><circle cx="8" cy="5" r="3"/><path d="M1.5 14.5c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5"/></>,
    'megaphone':   <><path d="M13 3L3 7v2l10 4V3z"/><path d="M3 7v2"/><path d="M3 9c0 2.2 1.8 4 4 4"/></>,
    'link':        <><path d="M10 13a4 4 0 005.66-5.66l-2-2a4 4 0 00-5.66 5.66"/><path d="M6 3a4 4 0 00-5.66 5.66l2 2A4 4 0 007.66 4.34"/><line x1="5" y1="11" x2="11" y2="5"/></>,
'settings':    <><circle cx="8" cy="8" r="2.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4"/></>,
    'logout':      <><path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3"/><polyline points="11 11 14 8 11 5"/><line x1="14" y1="8" x2="6" y2="8"/></>,
    'store':       <><path d="M2 6l1.5-4h9L14 6"/><path d="M2 6h12v1.5a2 2 0 01-4 0 2 2 0 01-4 0 2 2 0 01-4 0z"/><path d="M3 9.5V15h10V9.5"/><rect x="6.5" y="11" width="3" height="4"/></>,
    'message':     <><path d="M2 3h12a1 1 0 011 1v7a1 1 0 01-1 1H6l-4 3V4a1 1 0 011-1z"/><line x1="5" y1="6.5" x2="11" y2="6.5"/><line x1="5" y1="9" x2="9" y2="9"/></>,
    'wand':        <><path d="M3 13l8-8"/><path d="M10.5 2.5l1 1M13.5 5.5l-1-1M13 9l.6.6M5 1.4l.6.6"/><circle cx="12.2" cy="4" r="0.4" fill="currentColor"/></>,
  };
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
      {icons[id]}
    </svg>
  );
}

// ============================================================
// JET LOGO SVG
// Replace path with final approved vector when ready
// ============================================================
function JetLogo() {
  return (
    <img src={require('./autopilot_logo.png')} className="ap-logo-icon" alt="Autopilot Digital" />
  );
}

// ============================================================
// MAIN APP
// ============================================================
// ============================================================
// MOBILE BOTTOM NAV — 5 pinned items + "More" drawer
// Pinned: Home, Calendar, Approval, Performance, More
// ============================================================
const MOBILE_NAV_PINNED = [
  { id: 'home',        label: 'Home',      icon: 'home' },
  { id: 'calendar',    label: 'Scheduler',  icon: 'calendar' },
  { id: 'approval',    label: 'Approval',  icon: 'file-check' },
  { id: 'performance', label: 'Stats',     icon: 'line-chart' },
];
// All panels not pinned go into "More" drawer
const MOBILE_MORE_IDS = ['onboarding','brand','photos','editor','video','imagegen','stories','ads','reviews','gbprofile','connections','account'];
export default function AutopilotDashboard() {
  const [active, setActive] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('panel');
    return p || 'home';
  });
  // signOut is the admin's OWN session and takes no viewing-as override — but
  // it must clear the context. The store is module-level and in-memory, and
  // sign-out → LoginScreen → sign-in happens without a page reload, so without
  // this a second user signing in on the same tab inherits the previous admin's
  // viewing-as client. requireOwnerOrAdmin 403s them, so it is not a data leak;
  // it just breaks their panels for no reason they can see. Cleared BEFORE
  // signOut so no request can be built with a stale override on the way out.
  const handleLogout = () => { clearViewingAs(); supabase.auth.signOut(); };
  const [collapsed, setCollapsed] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [barStats, setBarStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [plan, setPlan] = useState('managed'); // 'managed' | 'self-serve'
  // How much we trust `plan`:
  //   'loading'  — /api/customer is still in flight
  //   'resolved' — confirmed against the live Stripe subscription
  //   'failed'   — the request failed, or Stripe couldn't confirm the plan
  // `plan` keeps its 'managed' default in every state so the conservative view
  // (approval queue, no Build tab) is what renders when we don't know. Only the
  // copy changes on 'failed' — never what the client can access.
  const [planStatus, setPlanStatus] = useState('loading');
  const [planRetryToken, setPlanRetryToken] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  // Note: mobile/desktop switching is handled by CSS media queries (≤768px).
  // No JS state needed.

  // ── Brand Hub image gen modifiers — shared with ImageGenerationPanel ──
  // Start empty: BrandHubPanel's load fills them from the viewed client's
  // profile. Demo values here were shown to, saved for, and blocked autofill
  // on every client whose own fields were empty.
  const [imgBrandKeywords, setImgBrandKeywords] = useState([]);
  const [imgAvoidTerms, setImgAvoidTerms] = useState([]);
  const [imgLightingMood, setImgLightingMood] = useState([]);
  const [sidebarBizName, setSidebarBizName] = useState('');
  const [sidebarLogoUrl, setSidebarLogoUrl] = useState('');

  // ── Selected Brand Photo — bridges Photo Library → Post Generator ──
  // { id, url, filename, storage_path } | null
  const [selectedBrandPhoto, setSelectedBrandPhoto] = useState(null);

  // ── Selected Video Project — bridges Video Editor → Post Generator ──
  // A completed video_projects row | null (consumed + cleared by the Video tab)
  const [selectedVideoProject, setSelectedVideoProject] = useState(null);

  // ── Story preset — bridges Stories view → Post Generator ──
  // When true, the Post Generator mounts with IG/FB Story pre-selected at 9:16.
  // Consumed + cleared by ImageGenerationPanel on mount.
  const [imagegenStoryPreset, setImagegenStoryPreset] = useState(false);

  // ── Brand Hub autofill intent — bridges Onboarding's last step → Brand Hub ──
  // { mode: 'website', url } | { mode: 'screenshots' } | null. In memory only,
  // so a refresh never re-runs it; consumed + cleared by BrandHubPanel.
  const [brandAutofillIntent, setBrandAutofillIntent] = useState(null);
  const openBrandHub = (intent = null) => { setBrandAutofillIntent(intent); setActive('brand'); };

  // ── Getting started → Photo Library's auto-post switch ──
  // One-shot, like the bridges above: set by Home's "Turn on auto-post",
  // consumed + cleared by PhotoLibraryPanel once it has scrolled to the switch.
  const [photoLibraryFocusAutoPost, setPhotoLibraryFocusAutoPost] = useState(false);
  const clearPhotoLibraryFocus = React.useCallback(() => setPhotoLibraryFocusAutoPost(false), []);
  const homeNavigate = (id, opts) => {
    setPhotoLibraryFocusAutoPost(id === 'photos' && opts?.focus === 'autopost');
    setActive(id);
  };

  // ── Stripe customer ID — fetched from backend on load ──
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const { clientId, loading: authLoading } = useClientId();
  // Drives whether the Reviews screen is offered. Re-checks when an admin
  // switches which client they are viewing (the hook keys on viewing-as).
  const gbpConnected = useGoogleBizConnected(clientId);
  useEffect(() => blockStrayFileDrops(window), []);

  // New Google reviews the client hasn't opened the Reviews screen for yet
  // (the hourly check flags them; opening the screen clears them). Re-read on
  // a viewing-as switch, like every other shell read.
  const { viewingAsClientId: shellViewingAs } = useViewingAs();
  const [reviewsUnread, setReviewsUnread] = useState(0);
  useEffect(() => {
    if (!clientId || !gbpConnected) { setReviewsUnread(0); return undefined; }
    let cancelled = false;
    const base = process.env.REACT_APP_BACKEND_URL || 'https://autopilot-backend-pn9r.onrender.com';
    clientScopedFetch(`${base}/api/google-business/reviews/unread`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && typeof d.unread === 'number') setReviewsUnread(d.unread); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clientId, gbpConnected, shellViewingAs]);
  // ── Password-setup gate ──
  // Invite / signup / recovery email links create a session but leave the user
  // with no usable password. We must NOT drop them straight into the dashboard;
  // they set a password first. `type` is read from the URL hash at load
  // (invite/signup/recovery), and PASSWORD_RECOVERY covers reset links whose
  // hash may already be stripped by the time this mounts. Cleared once the user
  // successfully calls updateUser({ password }).
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(
    () => PASSWORD_SETUP_TYPES.includes(initialAuthType)
  );
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setNeedsPasswordSetup(true);
    });
    return () => subscription?.unsubscribe();
  }, []);
  // Backup for the reused-tab edge: if an invite/signup/recovery link lands in a
  // tab that already has the app mounted, only the hash changes (no reload), so
  // the module-load capture never re-runs. Re-read the type on hashchange.
  useEffect(() => {
    const onHashChange = () => {
      if (PASSWORD_SETUP_TYPES.includes(readAuthTypeFromHash())) setNeedsPasswordSetup(true);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  const [hasTwoX, setHasTwoX] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  // ── Growth Boost checkout return ──────────────────────────────────────────
  //
  // Read DURING THE FIRST RENDER, not in an effect, and that is load-bearing.
  // Both BrandHubPanel and CalendarPanel strip ?2xdaily from the URL in their
  // own mount effects, and child effects run before the parent's — so an
  // effect here would find the query string already gone. A lazy useState
  // initialiser runs while the shell renders, before any child exists.
  const [twoXReturn] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('2xdaily') !== 'success') return null;
      return { sessionId: params.get('session_id') || null };
    } catch { return null; }
  });

  // 'idle' | 'confirming' | 'active' | 'failed'
  const [twoXConfirm, setTwoXConfirm] = useState('idle');

  // Ask the server whether the checkout really was paid, and only then treat
  // Growth Boost as on. The webhook is the other path to the same write and
  // usually wins; this covers the seconds when it has not landed yet, and
  // replaces the browser simply asserting the entitlement to itself.
  //
  // RETRIES BECAUSE 'not paid yet' IS EXPECTED, not exceptional: the customer
  // is redirected the moment Stripe finishes, which can be before the session
  // reads as paid. A 202 means ask again; anything else is final either way.
  useEffect(() => {
    if (!twoXReturn) return;
    let cancelled = false;

    // No session id means a checkout started before success_url carried one,
    // or a hand-typed URL. Either way there is nothing to verify, so nothing
    // is granted — the webhook remains the path that unlocks it.
    if (!twoXReturn.sessionId) {
      console.warn('[2x] return without a session id — leaving it to the webhook');
      return;
    }

    setTwoXConfirm('confirming');

    const DELAYS_MS = [0, 1500, 3000, 5000, 8000];
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));

    (async () => {
      for (let attempt = 0; attempt < DELAYS_MS.length; attempt++) {
        if (cancelled) return;
        if (DELAYS_MS[attempt]) await sleep(DELAYS_MS[attempt]);
        if (cancelled) return;
        try {
          const res = await clientScopedFetch(`${BACKEND}/api/2x/confirm`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ sessionId: twoXReturn.sessionId }),
          });
          if (res.status === 202) continue;           // not paid yet — ask again
          const body = await res.json().catch(() => ({}));
          if (cancelled) return;
          if (res.ok && body.ok) {
            setHasTwoX(true);
            setTwoXConfirm('active');
            // Internal heads-up, on a CONFIRMED purchase only. This used to
            // fire from the query string alone, so it could be triggered by
            // anyone typing the URL.
            if (!body.alreadyActive) {
              authedFetch('/api/send-2x-notification', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ clientName: 'Growth Boost purchase' }),
              }).catch(() => {});
            }
            return;
          }
          setTwoXConfirm('failed');
          return;
        } catch {
          // Network blip: fall through to the next attempt.
        }
      }
      if (!cancelled) setTwoXConfirm('failed');
    })();

    return () => { cancelled = true; };
  }, [twoXReturn]);

  // Strip the session id once. The panels strip 2xdaily and panel themselves;
  // neither touches this, and leaving a Stripe session id sitting in the
  // address bar to be copied into a bug report is worth one more line.
  useEffect(() => {
    if (!twoXReturn?.sessionId) return;
    try {
      const cleaned = window.location.search.replace(/[?&]session_id=[^&]*/, '').replace(/^&/, '?');
      window.history.replaceState({}, '', window.location.pathname + (cleaned === '?' ? '' : cleaned));
    } catch { /* history is not available in every embedding */ }
  }, [twoXReturn]);
  // ── Admin/Client view toggle ──
  // Secure default: client view unless ap_is_admin is explicitly 'true'.
  // A fresh session (key absent) must NOT default to admin — that would expose
  // the Ads/Video/Admin tabs to every new client. Admins flip the sidebar
  // toggle (writes 'true'); the toggle is always rendered so admins aren't stranded.
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('ap_is_admin') === 'true');
  const toggleAdmin = () => setIsAdmin(prev => { const next = !prev; localStorage.setItem('ap_is_admin', next ? 'true' : 'false'); return next; });

  // ── Viewing-as (admin act-as-client) ──
  // Read in the shell rather than only in AdminPanel so the exit control stays
  // on screen wherever the admin navigates after setting it.
  const { viewingAsClientId, viewingAsClientName } = useViewingAs();

  // Exit is local-first and unconditional: the context is cleared before the
  // audit call, so a backend that is down cannot strand an admin in a view they
  // asked to leave. The log is then best-effort — a missing exit row is a gap
  // in the record, but refusing to exit would be a gap in control.
  const handleBackToAdmin = async () => {
    const leaving = viewingAsClientId;
    clearViewingAs();
    try {
      await authedFetch(`${BACKEND}/api/admin/viewing-as`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ action: 'exit', clientId: leaving }),
      });
    } catch (err) {
      console.error('[viewingAs] exit audit log failed:', err?.message || err);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setPlanStatus('loading');

    // One automatic retry before giving up. Both failure modes here are
    // transient-capable: a cold Render start behind the /api/customer rewrite,
    // or a Stripe blip that makes resolvePlan report an unconfirmed plan
    // (200 with plan: null). A second attempt clears either one.
    const attempt = async (retriesLeft) => {
      let failure = null;
      try {
        const res = await authedFetch(`/api/customer?client=${encodeURIComponent(clientId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        if (data?.stripeCustomerId) setStripeCustomerId(data.stripeCustomerId);

        if (data?.plan) {
          setPlan(data.plan === 'selfserve' ? 'self-serve' : 'managed');
          setPlanStatus('resolved');
          return;
        }
        failure = 'plan could not be confirmed against Stripe';
      } catch (err) {
        if (cancelled) return;
        failure = err.message;
      }

      if (retriesLeft > 0) {
        setTimeout(() => { if (!cancelled) attempt(retriesLeft - 1); }, 1500);
        return;
      }
      console.warn(`[Autopilot] /api/customer — ${failure}`);
      setPlanStatus('failed');
    };

    attempt(1);
    return () => { cancelled = true; };
  }, [clientId, planRetryToken]);

  // Phase 3: two direct brand_profile reads (two_x_daily, and brand_name +
  // logo_url) became one round trip. Both wanted columns the /api/brand-profile
  // allowlist already serves, and they always rendered together, so they move as
  // one fetch rather than two — same reasoning as Phase 2's Home merge.
  //
  // WHY THE DEPS INCLUDE viewingAsClientId. `clientId` is useClientId's value,
  // which is always the SIGNED-IN user and never changes when an admin enters or
  // leaves a client view. The root shell also never unmounts. Keyed on
  // [clientId] alone these would not re-run on enter (the override would be
  // ignored) and would strand the viewed client's brand on screen after exit,
  // permanently. The id is not sent anywhere — clientScopedFetch reads the store
  // itself — it is here purely to make the effect re-run.
  //
  // EVERY FIELD IS NOW SET UNCONDITIONALLY, where the reads it replaces only
  // ever set on a truthy value. That was safe when the shell served one client
  // for the life of the page; once these re-run it is not. A client with
  // two_x_daily would otherwise leave hasTwoX stuck on for whoever is viewed
  // next, and a client with no logo would keep displaying the previous one's.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/brand-profile`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled) return;
        const p = d?.profile;
        // A failed or empty read clears rather than keeps: showing the
        // previously-viewed client's brand over another client's dashboard is
        // the exact confusion this phase exists to remove. Same posture as
        // HomePanel's cards in Phase 2.
        setHasTwoX(Boolean(p?.two_x_daily));
        setSidebarBizName(p?.brand_name || '');
        setSidebarLogoUrl(p?.logo_url || '');
      })
      .catch(() => {
        if (cancelled) return;
        setHasTwoX(false);
        setSidebarBizName('');
        setSidebarLogoUrl('');
      });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId]);

  // First-login routing: a new self-serve client lands on Onboarding rather
  // than an empty Home. "New" = plan confirmed self-serve by Stripe and neither
  // Instagram nor Facebook connected. brand_name is NOT a signal: signup writes
  // it from the form (plan-sync.js signupProfileRow), so it is never empty for
  // a real signup. Never for an admin (sidebar toggle on) or anyone in View As,
  // never over an explicit ?panel=, at most once per page load (ref) and once
  // per browser session per client (sessionStorage), so the client can leave
  // Onboarding and stay gone.
  const firstLoginCheckedRef = useRef(false);
  useEffect(() => {
    if (firstLoginCheckedRef.current) return;
    if (!clientId || planStatus !== 'resolved') return;
    firstLoginCheckedRef.current = true;
    if (plan !== 'self-serve' || isAdmin || viewingAsClientId) return;
    if (new URLSearchParams(window.location.search).get('panel')) return;
    const seenKey = `ap_onboarding_redirected_${clientId}`;
    try { if (sessionStorage.getItem(seenKey)) return; } catch (_) { /* storage blocked — the ref still limits it to once per load */ }
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/meta/status`)
      .then(r => (r.ok ? r.json() : null))
      .then(meta => {
        // A failed read means we cannot tell — stay on Home.
        if (cancelled || !meta) return;
        if (meta.instagram || meta.facebook) return;
        try { sessionStorage.setItem(seenKey, '1'); } catch (_) { /* best-effort */ }
        setActive(a => (a === 'home' ? 'onboarding' : a));
      })
      .catch(() => { /* best-effort: Home is the safe default */ });
    return () => { cancelled = true; };
  }, [clientId, planStatus, plan, isAdmin, viewingAsClientId]);

  // Pending-approvals count for the Content Queue nav badge.
  //
  // Its own endpoint, not /api/home-summary — that returns this same number but
  // runs eight queries plus a paginated post_insights scan to do it, and this
  // fires on every dashboard load to render one integer.
  //
  // KNOWN, DELIBERATE RESIDUE UNTIL PHASE 5: PostApprovalPanel still reads
  // posts directly, so it reports the ADMIN's pending list through
  // onPendingChange and overwrites this value the moment the Content Queue tab
  // is opened while viewing as a client. Migrating the badge is still right —
  // it now agrees with HomePanel's pending card, which Phase 2 already made the
  // viewed client's, instead of both being the admin's. Not special-cased here:
  // Phase 5 moves that panel and would only have to unwind the special case.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    clientScopedFetch(`${BACKEND}/api/pending-count`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) setPendingApprovals(d?.pendingCount ?? 0); })
      .catch(() => { if (!cancelled) setPendingApprovals(0); });
    return () => { cancelled = true; };
  }, [clientId, viewingAsClientId]);

  const isSelfServe = plan === 'self-serve';

  // Nav label and badge change based on plan
  const navApprovalLabel = 'Content Queue';
  const TITLES_PLAN = {
    ...TITLES,
    approval: 'Content Queue',
  };

  const ActivePanel = PANELS[active] || PANELS.home;

  // Helper: navigate (closes More drawer)
  const navigate = (id) => {
    setActive(id);
    setMoreOpen(false);
  };

  // Mobile "More" drawer items
  const moreItems = [
    ...NAV.filter(n => n.id && MOBILE_MORE_IDS.includes(n.id) && !(n.id === 'ads' && !isAdmin) && !(n.id === 'video' && !(isAdmin || isSelfServe)) && !((n.id === 'reviews' || n.id === 'gbprofile') && !gbpConnected)),
    ...FOOTER_NAV,
  ];
if (authLoading) return (
  <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'#0d0f14',color:'rgba(255,255,255,0.4)',fontFamily:'Barlow, sans-serif',fontSize:14}}>
    Loading…
  </div>
);
if (!clientId) return <LoginScreen />;
// Session exists but arrived via an invite/signup/recovery link — force a
// password before exposing the dashboard. Normal logins skip this (no `type`).
if (needsPasswordSetup) return <SetPasswordScreen onDone={() => setNeedsPasswordSetup(false)} />;
return (
    <>
      <style>{CSS}</style>
      <div className="ap-root">

        {/* SIDEBAR — desktop only (hidden on mobile via CSS) */}
        <aside className={`ap-sidebar${collapsed ? ' collapsed' : ''}`}>
          <div className="ap-logo-area">
            <JetLogo />
            {!collapsed && <div className="ap-logo-text">Autopilot <span>Digital</span></div>}
            <button
              className="ap-cbtn"
              onClick={() => setCollapsed(c => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? '›' : '‹'}
            </button>
          </div>

          {!collapsed && <div className="ap-divider" />}

          {!collapsed && (
            <div className="ap-client">
              {sidebarLogoUrl
                ? <img src={sidebarLogoUrl} alt="Brand logo" style={{ width:32, height:32, borderRadius:7, objectFit:'contain', flexShrink:0, background:'var(--bg-card2)' }} />
                : <div className="ap-avatar">
                    {(sidebarBizName || '').split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase()}
                  </div>
              }
              <div>
                <div className="ap-cname">{sidebarBizName || 'Loading…'}</div>
                <div className="ap-cplan">{isSelfServe ? 'Self-Serve Plan' : 'Managed Plan'}</div>
              </div>
            </div>
          )}

          {/* Admin-only. This flips plan UI locally without touching Stripe, so
              it must never reach clients — it would unlock Self-Serve-only tabs
              and contradict what they're actually paying for. */}
          {!collapsed && isAdmin && (
            <div className="ap-plan-switch">
              <div className="ap-plan-switch-label">Demo: Switch Plan</div>
              <div className="ap-plan-opts">
                <button className={`ap-plan-opt${!isSelfServe ? ' active' : ''}`} onClick={() => setPlan('managed')}>Managed</button>
                <button className={`ap-plan-opt${isSelfServe ? ' active' : ''}`} onClick={() => setPlan('self-serve')}>Self-Serve</button>
              </div>
            </div>
          )}

          <nav className="ap-nav">
            {NAV.map((item, i) => {
              if (item.id === 'ads' && !isAdmin) return null;
              if (item.id === 'video' && !(isAdmin || isSelfServe)) return null;
              if (item.id === 'admin' && !isAdmin) return null;
              if ((item.id === 'reviews' || item.id === 'gbprofile') && !gbpConnected) return null;
              if (item.section) {
                return collapsed ? null : <div key={i} className="ap-ns">{item.section}</div>;
              }
              const label = item.id === 'approval' ? navApprovalLabel : item.label;
              return (
                <div
                  key={item.id}
                  className={`ap-ni${active === item.id ? ' active' : ''}`}
                  onClick={() => setActive(item.id)}
                  title={collapsed ? label : undefined}
                >
                  <NavIcon id={item.icon} />
                  {!collapsed && <span>{label}</span>}
                  {!collapsed && item.id === 'approval' && !isSelfServe && pendingApprovals > 0 && <span className="ap-badge">{pendingApprovals}</span>}
                  {!collapsed && item.id === 'reviews' && reviewsUnread > 0 && <span className="ap-badge" title="New Google reviews">{reviewsUnread}</span>}
                  {!collapsed && item.comingSoon && <span className="ap-soon">Soon</span>}
                </div>
              );
            })}
          </nav>

          <div className="ap-sfooter">
            {FOOTER_NAV.map(item => (
              <div
                key={item.id}
                className={`ap-ni${active === item.id ? ' active' : ''}`}
                onClick={() => setActive(item.id)}
                title={collapsed ? item.label : undefined}
              >
                <NavIcon id={item.icon} />
                {!collapsed && <span>{item.label}</span>}
              </div>
            ))}
            {!collapsed && (
              <div onClick={toggleAdmin} className="ap-ni" style={{cursor:'pointer', marginBottom:4}}>
                <span style={{fontSize:11, color: isAdmin ? 'var(--accent)' : 'var(--text-muted)', fontFamily:'var(--font-head)', letterSpacing:1}}>
                  {isAdmin ? '⚙ ADMIN VIEW' : '👤 CLIENT VIEW'}
                </span>
              </div>
            )}
            <div
              className="ap-ni"
              onClick={handleLogout}
              title={collapsed ? 'Log Out' : undefined}
              style={{ color: 'var(--text-muted)' }}
            >
              <NavIcon id="logout" />
              {!collapsed && <span>Log Out</span>}
            </div>
          </div>
        </aside>

        {/* RIGHT SIDE */}
        <div className="ap-right">

          {/* VIEWING-AS BANNER — the exit control.
              Deliberately a real, always-visible element rather than internal
              state plus a memory: the context is invisible otherwise (the admin
              is still signed in as themselves, so nothing else on the page
              changes), and an admin who forgets they set it is the one failure
              mode this whole flow has. Sits above the topbar so it survives
              navigating away from the Admin panel, which is where it was set.

              No confirmation on exit: clearing costs nothing and is instantly
              reversible from the pill list. */}
          {viewingAsClientId && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '9px 20px', flexShrink: 0,
              background: 'rgba(245,166,35,0.12)',
              borderBottom: '1px solid rgba(245,166,35,0.45)',
              fontFamily: 'Barlow, sans-serif', fontSize: 13, color: '#f5a623',
            }}>
              <span style={{ fontWeight: 700, letterSpacing: 0.4 }}>⚠ VIEWING AS</span>
              <strong style={{ color: '#fff' }}>{viewingAsClientName || viewingAsClientId}</strong>
              <span style={{ color: 'rgba(245,166,35,0.75)' }}>
                — you are still signed in as yourself
              </span>
              <button
                onClick={handleBackToAdmin}
                style={{
                  marginLeft: 'auto', flexShrink: 0,
                  background: '#f5a623', color: '#0d0f14', border: 'none',
                  borderRadius: 999, padding: '5px 16px',
                  fontFamily: 'Barlow, sans-serif', fontWeight: 700, fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Back to admin
              </button>
            </div>
          )}

          {/* Growth Boost checkout return. Without this the customer lands on
              a dashboard that looks exactly as it did before they paid, for as
              long as the webhook takes — and if it never lands, forever, with
              no indication anything is wrong. */}
          {twoXConfirm !== 'idle' && (
            <div
              role="status"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 18px',
                background: twoXConfirm === 'failed' ? 'rgba(245,166,35,0.12)' : 'rgba(49,220,186,0.10)',
                borderBottom: `1px solid ${twoXConfirm === 'failed' ? 'rgba(245,166,35,0.45)' : 'rgba(49,220,186,0.35)'}`,
                fontFamily: 'Barlow, sans-serif', fontSize: 13,
                color: twoXConfirm === 'failed' ? '#f5a623' : 'var(--accent)',
              }}
            >
              {twoXConfirm === 'confirming' && <>Confirming your payment…</>}
              {twoXConfirm === 'active'     && <><strong>Growth Boost is on.</strong> Pick your second posting time in the Scheduler.</>}
              {twoXConfirm === 'failed'     && <>We could not confirm your payment yet. If you were charged, it will switch on shortly — contact support if it does not.</>}
            </div>
          )}

          {/* TOP BAR */}
          <div className="ap-topbar">
            <div className="ap-topbar-title">{TITLES_PLAN[active] || active}</div>
            <div className="ap-sdot" />
            <div className="ap-slabel">Posting Active</div>
          </div>

          {/* MOBILE: client strip with plan toggle — shown below topbar on phones */}
          <div className="ap-mobile-header-client">
            {sidebarLogoUrl
              ? <img src={sidebarLogoUrl} alt="Brand logo" style={{width:22,height:22,borderRadius:5,objectFit:'contain',flexShrink:0,background:'var(--bg-card2)'}} />
              : <div className="ap-avatar" style={{width:22,height:22,fontSize:8}}>
                  {(sidebarBizName || '').split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase()}
                </div>
            }
            <span style={{fontSize:12,fontWeight:600,color:'var(--text-primary)'}}>{sidebarBizName || 'Loading…'}</span>
            <span className="ap-mobile-header-plan">{isSelfServe ? 'Self-Serve' : 'Managed'}</span>
            {isAdmin && (
              <button
                className="ap-mobile-plan-toggle"
                onClick={() => setPlan(p => p === 'managed' ? 'self-serve' : 'managed')}
              >
                Switch
              </button>
            )}
          </div>

          {/* WORKSPACE */}
          <div className="ap-workspace">
            {active === 'home'
              ? <HomePanel clientId={clientId} clientName={sidebarBizName} onStatsLoaded={setBarStats} hasTwoX={hasTwoX} isSelfServe={isSelfServe} onNavigate={homeNavigate} />
              : active === 'approval'
                ? <PostApprovalPanel isSelfServe={isSelfServe} planStatus={planStatus} onRetryPlan={() => setPlanRetryToken(t => t + 1)} clientId={clientId} stripeCustomerId={stripeCustomerId} onPendingChange={setPendingApprovals} onApprove={() => setCalendarRefreshKey(k => k + 1)} />
                : active === 'brand'
                  ? <BrandHubPanel
                      isSelfServe={isSelfServe}
                      isAdmin={isAdmin}
                      clientId={clientId}
                      stripeCustomerId={stripeCustomerId}
                      hasTwoX={hasTwoX}
                      imgBrandKeywords={imgBrandKeywords} setImgBrandKeywords={setImgBrandKeywords}
                      imgAvoidTerms={imgAvoidTerms}       setImgAvoidTerms={setImgAvoidTerms}
                      lightingMood={imgLightingMood}      setLightingMood={setImgLightingMood}
                      onBizNameChange={setSidebarBizName}
                      onLogoChange={setSidebarLogoUrl}
                      autofillIntent={brandAutofillIntent}
                      onAutofillIntentConsumed={() => setBrandAutofillIntent(null)}
                      onNavigate={setActive}
                    />
                  : active === 'imagegen'
                    ? <ImageGenerationPanel
                        isSelfServe={isSelfServe}
                        clientId={clientId}
                        stripeCustomerId={stripeCustomerId}
                        brandKeywords={imgBrandKeywords}
                        avoidTerms={imgAvoidTerms}
                        lightingMood={imgLightingMood}
                        onNavigate={setActive}
                        selectedBrandPhoto={selectedBrandPhoto}
                        setSelectedBrandPhoto={setSelectedBrandPhoto}
                        selectedVideoProject={selectedVideoProject}
                        setSelectedVideoProject={setSelectedVideoProject}
                        storyPreset={imagegenStoryPreset}
                        onStoryPresetConsumed={() => setImagegenStoryPreset(false)}
                      />
                    : active === 'photos'
                      ? <PhotoLibraryPanel
                          onNavigate={setActive}
                          setSelectedBrandPhoto={setSelectedBrandPhoto}
                          focusAutoPost={photoLibraryFocusAutoPost}
                          onFocusAutoPostConsumed={clearPhotoLibraryFocus}
                        />
                      : active === 'editor'
                        ? <PhotoEditorPanel clientId={clientId} />
                      : active === 'video'
                        ? ((isAdmin || isSelfServe) ? <VideoEditorPanel clientId={clientId} onNavigate={setActive} setSelectedVideoProject={setSelectedVideoProject} /> : <HomePanel clientId={clientId} clientName={sidebarBizName} onStatsLoaded={setBarStats} hasTwoX={hasTwoX} />)
                      : active === 'performance'
                        ? <PostPerformancePanel clientId={clientId} isSelfServe={isSelfServe} />
                      : active === 'gbprofile'
                        ? <GoogleProfilePanel clientId={clientId} />
                      : active === 'reviews'
                        ? <ReviewsPanel clientId={clientId} onUnreadChange={setReviewsUnread} />
                      : active === 'stories'
                        ? <StoriesPanel onStartStory={() => { setImagegenStoryPreset(true); setActive('imagegen'); }} />
                      : active === 'onboarding'
                        ? <OnboardingPanel onOpenBrandHub={openBrandHub} isSelfServe={isSelfServe} onCreatePost={() => setActive('imagegen')} />
                        : <ActivePanel isSelfServe={isSelfServe} planStatus={planStatus} onRetryPlan={() => setPlanRetryToken(t => t + 1)} clientId={clientId} stripeCustomerId={stripeCustomerId} hasDoubleSlot={hasTwoX} calendarRefreshKey={calendarRefreshKey} />
            }
          </div>

          {/* DASHBOARD BAR — hidden on Home, collapsible elsewhere, hidden on mobile via CSS */}
          {active !== 'home' && (
          <div className="ap-dashboard-bar">
            <div className="ap-db-handle" onClick={() => setDashboardOpen(o => !o)}>
              <span className="ap-db-label">Dashboard</span>
              {dashboardOpen && <span className="ap-db-hint">Click to hide dashboard</span>}
              <span className="ap-db-arrow" style={{ transform: dashboardOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
            </div>
            <div className={`ap-db-inner${dashboardOpen ? '' : ' hidden'}`}>
              {(() => {
     const tz = getDisplayTimeZone();
     const fmtBarDate = (iso) => {
       if (!iso) return '—';
       const d = new Date(iso);
       const now = new Date();
       if (d.toDateString() === now.toDateString()) return 'Today';
       if (d.toDateString() === new Date(now.getTime() + 86400000).toDateString()) return 'Tomorrow';
       return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
     };
     const fmtBarTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz }) : '';
     const trunc = (s, n) => s && s.length > n ? s.slice(0, n) + '…' : (s || '');
     const np = barStats?.nextPost;
     return (
       <>
         <div className="ap-stat-grid">
           <div className="ap-stat-card">
             <div className="ap-stat-label">Posts This Month</div>
             <div className="ap-stat-value">{barStats ? barStats.postsThisMonth : '—'}</div>
             <div className="ap-stat-sub">This calendar month</div>
           </div>
           <div className="ap-stat-card">
             <div className="ap-stat-label">Avg. Engagement</div>
             <div className="ap-stat-value">{barStats?.avgEngagement != null ? barStats.avgEngagement.toFixed(1) + '%' : '—'}</div>
             <div className="ap-stat-sub">{barStats?.avgEngagement != null ? 'From post insights' : 'No data yet'}</div>
           </div>
           <div className="ap-stat-card">
             <div className="ap-stat-label">{isSelfServe ? 'Drafts' : 'Pending Approval'}</div>
             <div className="ap-stat-value">{pendingApprovals}</div>
             <div className="ap-stat-sub">{pendingApprovals === 0 ? 'All reviewed' : 'Needs your review'}</div>
           </div>
           <div className="ap-stat-card">
             <div className="ap-stat-label">Next Post</div>
             <div className="ap-stat-value" style={{ fontSize:17 }}>{np ? fmtBarDate(np.scheduled_at) : '—'}</div>
             <div className="ap-stat-sub">{np ? `${fmtBarTime(np.scheduled_at)}${np.post_type ? ' — ' + np.post_type : ''}` : 'Nothing scheduled'}</div>
           </div>
         </div>
         <div className="ap-two-col">
           <div className="ap-card">
             <div className="ap-card-header">
               <span className="ap-card-title">Recent Posts</span>
               <span className="ap-card-action">View all →</span>
             </div>
             {(barStats?.recentPosts ?? []).length === 0
               ? <div className="ap-post-item" style={{color:'var(--text-muted)',fontSize:13}}>No recent posts yet</div>
               : (barStats?.recentPosts ?? []).map(p => (
                 <div key={p.id} className="ap-post-item">
                   <div className="ap-thumb" style={{backgroundImage: p.image_url ? `url(${p.image_url})` : 'none', backgroundSize:'cover', backgroundPosition:'center'}}>{!p.image_url ? '📄' : ''}</div>
                   <div style={{flex:1,minWidth:0}}>
                     <div className="ap-post-caption">{trunc(p.caption, 40)}</div>
                     <div className="ap-post-meta">{fmtBarDate(p.scheduled_at)}{p.platforms?.[0] ? ' · ' + p.platforms[0] : ''}</div>
                   </div>
                   <span className="ap-pill s-posted">Posted</span>
                 </div>
               ))
             }
           </div>
           <div className="ap-card">
             <div className="ap-card-header">
               <span className="ap-card-title">{isSelfServe ? 'My Drafts' : 'Pending Approval'}</span>
               <span className="ap-card-action">{isSelfServe ? 'Open Builder →' : 'Review →'}</span>
             </div>
             {(barStats?.pendingList ?? []).length === 0
               ? <div className="ap-post-item" style={{color:'var(--text-muted)',fontSize:13}}>Nothing pending</div>
               : (barStats?.pendingList ?? []).map(p => (
                 <div key={p.id} className="ap-post-item">
                   <div className="ap-thumb" style={{backgroundImage: p.image_url ? `url(${p.image_url})` : 'none', backgroundSize:'cover', backgroundPosition:'center'}}>{!p.image_url ? '📄' : ''}</div>
                   <div style={{flex:1,minWidth:0}}>
                     <div className="ap-post-caption">{trunc(p.caption, 40)}</div>
                     <div className="ap-post-meta">{fmtBarDate(p.scheduled_at)}{p.post_type ? ' · ' + p.post_type : ''}</div>
                   </div>
                 </div>
               ))
             }
           </div>
         </div>
       </>
     );
   })()}
            </div>
          </div>
          )}

        </div>

        {/* ============================================================
            MOBILE BOTTOM NAV BAR
            Only visible on ≤768px via CSS.
            4 pinned items + "More" that opens a slide-up drawer.
        ============================================================ */}
        <nav className="ap-mobile-nav">
          <div className="ap-mobile-nav-inner">
            {MOBILE_NAV_PINNED.map(item => {
              const label = item.id === 'approval' ? 'Queue' : item.label;
              const isActive = active === item.id;
              return (
                <div
                  key={item.id}
                  className={`ap-mobile-nav-item${isActive ? ' active' : ''}`}
                  onClick={() => navigate(item.id)}
                >
                  {item.id === 'approval' && !isSelfServe && pendingApprovals > 0 && (
                    <span className="ap-mobile-nav-badge">{pendingApprovals}</span>
                  )}
                  <NavIcon id={item.icon} />
                  <span>{label}</span>
                </div>
              );
            })}

            {/* More button */}
            <div
              className={`ap-mobile-nav-item${moreOpen || MOBILE_MORE_IDS.includes(active) ? ' active' : ''}`}
              onClick={() => setMoreOpen(o => !o)}
            >
              {/* Hamburger/grid icon */}
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="4" cy="4" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="8" cy="4" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="12" cy="4" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="4" cy="8" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="12" cy="8" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="4" cy="12" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="8" cy="12" r="1.2" fill="currentColor" stroke="none"/>
                <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/>
              </svg>
              <span>More</span>
            </div>
          </div>
        </nav>

        {/* MOBILE MORE DRAWER */}
        <div className={`ap-mobile-more-overlay${moreOpen ? ' open' : ''}`}>
          <div className="ap-mobile-more-backdrop" onClick={() => setMoreOpen(false)} />
          <div className="ap-mobile-more-sheet">
            <div className="ap-mobile-more-handle" />
            <div className="ap-mobile-more-title">All Sections</div>
            {moreItems.map(item => {
              const label = item.id === 'approval' ? navApprovalLabel : item.label;
              const isActive = active === item.id;
              return (
                <div
                  key={item.id}
                  className={`ap-mobile-more-item${isActive ? ' active' : ''}`}
                  onClick={() => navigate(item.id)}
                >
                  <NavIcon id={item.icon} />
                  <span className="ap-mobile-more-label">{label}</span>
                  {item.id === 'approval' && !isSelfServe && pendingApprovals > 0 && (
                    <span className="ap-mobile-more-badge">{pendingApprovals}</span>
                  )}
                  {item.id === 'reviews' && reviewsUnread > 0 && (
                    <span className="ap-mobile-more-badge">{reviewsUnread}</span>
                  )}
                  {item.comingSoon && <span className="ap-mobile-more-soon">Soon</span>}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      <ChatWidget context="dashboard" avatarType="captain" />
    </>
  );
}
